// The Storefront page (Shopping assistant v1): Setup, Widget, Install and Conversations.
// Namespace `storefront`; owner: the Storefront UI lane (ui/workspace/storefront).
// Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  pageTitle: 'Storefront',
  assistantTitle: 'Shopping assistant',
  designUnconfirmed:
    'Stopped before Chat could confirm the design was saved. The assistant’s canvas shows its newest version.',
  errorNewerVersion:
    'A newer version was saved since you opened this. Look at it first, then publish.',
  canvasSelectHint: 'Click a part of the assistant to change it in chat · Esc to stop',
  canvasDraft: 'Version {version} · not published yet',
  canvasDraftOverLive: 'Version {version} · version {live} is published',
  canvasLive: 'Version {version} · published',
  canvasNoVersion: 'The first version comes when reading the store is finished',
  canvasPublish: 'Publish on the Storefront page',
  canvasFrameTitle: '{name} shopping assistant',
  viewLabel: 'View',
  photosGood: '{count} product photos: whole and sharp',
  photosGoodOne: 'The product photo is whole and sharp',
  canvasMissingTitle: 'This shopping assistant is not in this workspace',
  canvasMissingBody: 'It belongs to another workspace, or it was removed.',
  canvasMissingLink: 'See this workspace’s assistants',
  photosLabel: 'Product photos',
  photosSmall: '{count} small in their tiles',
  photosCut: '{count} cut off',
  photosSoft: '{count} enlarged, so soft',
  photosMissing: '{count} without a photo',
  photosFix: 'Fix with Chat',
  photosPrompt:
    'Make the product photos in the cards look right: each one whole, large enough in its tile, and sharp.',
  photosFacts:
    '{total} photos: {small} small in their tiles, {cut} cut, {soft} enlarged, {missing} without a photo. Sizes: {sizes}',
  viewDesign: 'Design',
  viewVersions: 'Versions',
  versionsLoading: 'Loading versions…',
  versionsFailed: 'The versions could not be loaded.',
  versionTitle: 'Version {version}',
  versionLive: 'Published',
  versionChecked: 'Checked',
  versionToday: 'Today',
  versionYesterday: 'Yesterday',
  versionEarlier: 'Earlier',
  versionByPerson: 'Asked by {name}',
  versionByChat: 'Asked in chat',
  versionFromReading: 'From reading {domain}',
  versionPublishedBy: 'published by {name}, {at}',
  versionPublish: 'Publish',
  versionRestore: 'Restore…',
  versionRestoreTitle: 'Restore version {version}?',
  versionRestoreBody:
    'It becomes version {next}, a new draft. Your site doesn’t change until you publish.',
  versionRestoreConfirm: 'Restore',
  versionRestoreCancel: 'Cancel',
  versionRestored: 'Restored as version {version}',
  versionRestoreFailed: 'The version could not be restored.',
  compareTitle: 'Compare version {to} with version {from}',
  compareTitleLive: 'Compare version {to} with version {from}, published',
  compareHint: 'Drag to compare',
  compareLabel: 'Compare version {from} and version {to}',
  compareTagLive: 'v{version} · Published',
  compareTag: 'v{version}',
  changesTitle: 'What changed from version {from} to {to}',
  changesNone: 'The designs are the same. Only the notes or the store’s data changed.',
  changeAdded: 'Added',
  changeRemoved: 'Removed',
  changeChanged: 'Changed',
  changeDrawn: 'a drawn logo',
  changeCss: 'its own CSS',
  changeYes: 'yes',
  changeNo: 'no',
  'field.colors.brand': 'Brand colour',
  'field.colors.actions': 'Button colour',
  'field.colors.panel': 'Panel colour',
  'field.colors.surface': 'Surface colour',
  'field.colors.ink': 'Text colour',
  'field.colors.muted': 'Quiet text colour',
  'field.colors.line': 'Line colour',
  'field.colors.bubble': 'Shopper message colour',
  'field.colors.bubbleInk': 'Shopper message text',
  'field.colors.header': 'Header colour',
  'field.colors.headerInk': 'Header text colour',
  'field.colors.tints': 'Soft tints',
  'field.copy.assistantName': 'Name',
  'field.copy.greeting': 'Greeting',
  'field.copy.placeholder': 'Question box',
  'field.copy.starters': 'Starters',
  'field.copy.launcherLabel': 'Launcher words',
  'field.copy.aiLabel': 'AI label',
  'field.copy.personLabel': 'Link to a person',
  'field.type.body': 'Text font',
  'field.type.display': 'Heading font',
  'field.type.fontCss': 'Font source',
  'field.type.scale': 'Text size',
  'field.shape.radius': 'Corners',
  'field.shape.shadow': 'Shadow',
  'field.shape.density': 'Spacing',
  'field.launcher.style': 'Launcher on computers',
  'field.launcher.phoneStyle': 'Launcher on phones',
  'field.launcher.side': 'Launcher side',
  'field.launcher.icon': 'Launcher icon',
  'field.panel.header': 'Header size',
  'field.panel.opening': 'First screen',
  'field.products.layout': 'Products',
  'field.products.imageFit': 'Product photos',
  'field.products.imageRatio': 'Photo tile shape',
  'field.products.imagePadding': 'Room around photos',
  'field.products.imageBackground': 'Photo tile colour',
  'field.products.picker': 'Size choice',
  'field.products.showStock': 'Stock shown',
  'field.cart.landing': 'Cart animation',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Logo shape',
  'field.logo.plate': 'Logo background',
  'field.css': 'Custom CSS',
  'value.ink': 'ink',
  'value.brand': 'brand colour',
  'value.small': 'small',
  'value.medium': 'medium',
  'value.large': 'large',
  'value.square': 'square',
  'value.soft': 'soft',
  'value.round': 'round',
  'value.flat': 'flat',
  'value.deep': 'deep',
  'value.compact': 'compact',
  'value.comfortable': 'comfortable',
  'value.pill': 'pill with words',
  'value.circle': 'round',
  'value.right': 'right',
  'value.left': 'left',
  'value.logo': 'logo',
  'value.chat': 'chat bubble',
  'value.sparkle': 'sparkle',
  'value.bag': 'bag',
  'value.full': 'full',
  'value.middle': 'in the middle',
  'value.bottom': 'at the bottom',
  'value.rows': 'rows',
  'value.grid': 'grid',
  'value.carousel': 'a row you swipe',
  'value.contain': 'whole photo',
  'value.cover': 'filled tile',
  'value.portrait': 'portrait',
  'value.landscape': 'landscape',
  'value.none': 'none',
  'value.pop': '+1 that rises',
  'value.ring': 'ripple',
  'value.blink': 'blink',
  'value.chips': 'buttons in a row',
  'value.menu': 'a list',
  'value.rounded': 'rounded',
  designCardTitle: '{name} shopping assistant',
  designCardSaved: 'Version {version} saved · not published yet',
  designCardSavedFirst: 'Design saved · the first version will carry it',
  designSaving: 'Saving the design…',
  selectedAssistantPart: 'Selected: a part of the assistant',
  loading: 'Loading Storefront…',
  disabledTitle: 'Storefront is not turned on in this workspace.',
  disabledBody:
    'It is in early access and arrives in stages. Ask your Poneva contact when it reaches you.',
  loadFailed: 'Storefront could not be loaded.',
  startTitle: 'A shopping assistant for your store',
  startBody:
    'Poneva reads your store the way a shopper uses it: search, products, the cart and the chat you already have. It takes about 10 to 30 minutes and uses your company’s credits, and you can keep working.',
  urlLabel: 'Your store’s address',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Read my store',
  starting: 'Starting…',
  startFailed: 'That store could not be read. Check the address and try again.',
  tabConversations: 'Conversations',
  tabOverview: 'Overview',
  allAssistants: 'All assistants',
  allAssistantsNote: '{assistants} assistants · {sites} sites',
  allAssistantsMenuNote: '{count} conversations this week',
  marketLabel: 'Market',
  allMarkets: 'All markets',
  allMarketsNote: '{count} sites',
  scopeIn: '{assistant} in {market}',
  askAbout: 'Ask Chat about {scope}',
  overviewAssistantsTitle: 'Assistants and markets',
  overviewMarketsTitle: 'Markets',
  overviewAssistantsOnly: 'Assistants',
  overviewIdle: '{count} assistants with no conversations yet',
  allAssistantsCount: '{assistants} assistants',
  overviewFailed: 'The overview could not be loaded.',
  colConversations: 'Conversations',
  colCart: 'Added to cart',
  colCheckout: 'Checkout',
  colPerson: 'Person',
  colCost: 'Credits',
  rowMarkets: '{count} markets',
  rowVersion: 'version {version}',
  rowAria:
    '{name}, {sub}: {conversations} conversations, {cart} added to cart, {checkout} reached checkout, {person} asked for a person, credits {cost}',
  versionPublishedAt: 'published {at}',
  designColorHeader: 'Header',
  designColorHeaderInk: 'Header text',
  designGroupTranslations: 'Words in other languages',
  topicsTitle: 'What shoppers ask',
  topicsNote: 'Topics Chat found in first questions',
  startPagesTitle: 'Where conversations start',
  startPagesOne: 'Every conversation started on one kind of page: {page}.',
  'startPage.product': 'Product pages',
  'startPage.search': 'Search results',
  'startPage.start': 'Start page',
  'startPage.cart': 'Cart',
  'startPage.other': 'Other pages',
  addMarketInChat: 'Add a market, in chat',
  marketsTitle: 'Markets',
  marketsBody:
    'One assistant and one design. Each market has its own language, currency, products, cart and chat for a person.',
  marketChecked: 'checked {when}',
  'marketStatus.ready': 'Ready',
  'marketStatus.found': 'Not added',
  'marketStatus.checking': 'Checking',
  'marketStatus.failed': 'Needs a fix',
  addMarketPrompt:
    'Add another market to the shopping assistant for {name} ({domain}): find the store’s other countries, languages or sites and ask me which to add.',
  tabWidget: 'Widget',
  tabInstall: 'Install',
  tabSetup: 'Setup',
  statusReading: 'Reading your store',
  readingProducts: 'Reading the products again',
  readingCart: 'Checking the cart again',
  readingChat: 'Checking the way to your own chat',
  readingMarket: 'Adding the {market} market',
  readingMarketAny: 'Adding a market',
  statusReady: 'Ready to install',
  statusFailed: 'Reading stopped',
  statusLive: 'Live · version {version} on {domain}',
  statusPublished: 'Published · version {version} · not on your site yet',
  statusPublishedUnverified: 'Published · version {version} · could not check your site',
  stepOpen: 'Opening the site',
  stepSearch: 'Search',
  stepProducts: 'Products',
  stepCart: 'Cart',
  stepChat: 'Your existing chat',
  stepLook: 'Your look and logo',
  stepChecks: 'Checking it works',
  stepReady: 'Ready',
  stepDetailOpen: 'Opening {domain}',
  stepDetailSearch: 'Finding the store’s products',
  stepDetailProducts: 'Reading products, prices, photos, colours and sizes',
  stepDetailCart: 'Trying the cart: add, read back, remove and empty',
  stepDetailChat: 'Looking for your existing chat',
  stepDetailLook: 'Reading the store’s logo and colours',
  stepDetailChecks: 'Trying shopper searches, products, browsing and the cart',
  stepDetailChecksProducts: 'Checking product searches, browsing, photos, colours and sizes',
  stepDetailChecksCart: 'Checking the cart’s add, read back, remove and empty, and product reads',
  stepDetailChecksChat: 'Checking the handoff to your existing chat, and product reads',
  stepDetailChecksMarket: 'Checking this market’s products, prices and cart',
  nowRunning: 'Now: {step}',
  nowProgress: 'Step {n} of {total} · {minutes} min so far',
  nowReady: 'Your store is ready for an assistant',
  nowReadyBody: 'Search, products and the cart work.',
  nowLastRead: 'Last read {when}',
  nowUsually: 'usually {min}–{max}',
  stepsRead: 'Read {when} · Show the steps',
  nowFailed: 'Reading stopped',
  factAnswers: 'Answers shoppers with',
  costCounting: 'Counting…',
  factPaid: 'Paid from',
  paidCompany: 'Company credits',
  creditsAmount: '{amount} credits',
  creditsOne: '{amount} credit',
  creditsUnder: 'under {amount} credits',
  stopRun: 'Stop the run',
  stopping: 'Stopping…',
  closeNote: 'You can leave this page. The run keeps going.',
  readyNote: 'Next: try your widget, then install it.',
  buildWidget: 'Try the widget',
  readAgain: 'Read the store again',
  foundTitle: 'What it found',
  foundProducts: 'Products and prices read',
  foundSearch: 'Search works',
  foundCart: 'Add to cart works',
  foundChat: 'Your {vendor} chat',
  foundNoChat: 'No chat of your own',
  foundLook: 'Your colours and logo',
  widgetTitle: 'Your widget',
  widgetBody:
    'Your assistant, in your colours. Try it here: it answers with your real products. Nothing is added to a real cart.',
  widgetLoading: 'Loading the preview…',
  widgetFailed: 'The preview did not load.',
  widgetNotReady: 'The widget is ready when the Setup steps are complete.',
  installLineTitle: 'Add this line to your site',
  installLineBody:
    'It works on any platform: in the head of your pages, on Shopify in theme.liquid just before </head>. It shows nothing until you publish.',
  installLineBodyLive:
    'It works on any platform: in the head of your pages, on Shopify in theme.liquid just before </head>. Shoppers then meet version {version}.',
  installPolicyAsk: 'Does your site set a Content Security Policy?',
  installPolicy:
    'If your site sets a Content Security Policy, also allow {origin} in {directives}, or the assistant cannot start.',
  installPolicyRefuses:
    "Your site's Content Security Policy refuses the assistant: allow {origin} in {directives}, or nothing will start.",
  installPolicyNonce:
    'Your policy runs only scripts it trusts, so add the line with your pages’ nonce.',
  installPolicyEval: "The assistant also needs 'unsafe-eval' in script-src.",
  copy: 'Copy',
  copied: 'Copied',
  domainsTitle: 'Sites it answers on',
  domainsBody: 'It stays silent on any other site, even one that copies the line.',
  lineFound: 'Line found',
  lineNotSeen: 'Not on the page yet',
  lineUnverified:
    'Could not check: the site blocks automated visits. The assistant still works for shoppers.',
  lineChecked: 'checked {when}',
  lineCheckAgain: 'Check again',
  lineChecking: 'Checking the page…',
  installCheckBusy: 'The check is busy right now. Try again in a moment.',
  publishTitle: 'Publish',
  publishBody: 'Shoppers on {domain} see it right away. You can unpublish at any time.',
  publishButton: 'Publish version {version}',
  publishConfirm: 'Publish version {version} of {name}?',
  publishNow: 'Publish',
  notNow: 'Not now',
  publishing: 'Publishing…',
  liveTitle: 'Published: version {version}',
  liveSince: 'Since {since}',
  unpublish: 'Unpublish',
  unpublishConfirm:
    'Take version {version} off your site? Shoppers no longer see the assistant until you publish again.',
  publishedLine: 'Published now: version {version}, since {since}',
  usageTitle: 'Usage',
  usageBody: 'It uses your company’s credits, like the rest of Chat.',
  usageDiscovery: 'Reading the store',
  usageChats: 'Shopper chats',
  usageChatsValue: '{count} chats · {cost}',
  last7: 'Last 7 days',
  askWeek: 'Ask Chat about this week',
  kpiConversations: 'Conversations',
  kpiVsPrevious: '{change} vs the week before',
  kpiCart: 'Added to cart',
  kpiCheckout: 'Reached checkout',
  kpiPerson: 'Asked for a person',
  kpiCost: 'Credits used',
  ofConversations: '{percent} of conversations',
  checkoutNote: '{percent} of conversations',
  personNote: '{percent} opened your chat',
  costNote: 'About {amount} per conversation',
  costNoteUnder: 'Under {amount} per conversation',
  sparkLabel: 'Conversations per day, last {days} days',
  sparkDay: '{day}: {count} conversations',
  filterAll: 'All',
  filterCart: 'Added to cart',
  filterCheckout: 'Checkout',
  filterPerson: 'Person',
  outcomeCart: 'Added to cart',
  outcomeCheckout: 'Reached checkout',
  outcomePerson: 'Asked for a person',
  outcomeAnswered: 'Answered',
  devicePhone: 'Phone',
  deviceComputer: 'Computer',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Started on {page}',
  anonymous: 'The shopper is anonymous.',
  askConversation: 'Ask Chat about this conversation',
  showOlderVersions: 'Show earlier versions',
  showOlder: 'Show older conversations',
  filterCountsWeek: 'Counts: last 7 days',
  readAlreadyHere: 'This shop is already here, so nothing new started and no credits were used.',
  readOpenShop: 'Open it',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'What can we learn from this conversation?',
  askWeekMessage: 'What can we learn from this week’s conversations?',
  conversationsChipOne: '{name} · conversation from {when}',
  conversationsChipWeek: '{name} · this week',
  conversationsChipWeekIn: '{name} · {market} · this week',
  removeConversations: 'Remove the conversations',
  noConversations: 'No conversations yet',
  noConversationsBody: 'When shoppers use the assistant, their conversations show up here.',
  pickConversation: 'Pick a conversation to read it.',
  eventCheckout: 'Went to checkout',
  eventPerson: 'Opened your chat',
  showMore: 'Show more',
  urlInvalid: 'Use your store’s https address, like https://www.yourstore.com.',
  errorNoCompany: 'Storefront needs a company workspace. Ask your admin to add you.',
  errorPublishPermission:
    'Only people who can publish for your company can do this. Ask your admin.',
  actionFailed: 'That did not work. Try again.',
  copyFailed: 'Could not copy. Select the line and copy it yourself.',
  reasonCancelled: 'You stopped the run.',
  reasonFailed: 'The read could not finish on your shop, so the assistant is not ready.',
  reasonFailedLive:
    'The read could not finish on your shop. The published assistant keeps working as it is.',
  reasonFailedKept:
    'The read could not finish on your shop, so nothing changed: version {version} stays as it was.',
  statusReadStopped: 'Ready · the last read stopped',
  reasonStepMarked: 'The step that stopped is marked below.',
  reasonReadAgain: 'Reading again starts from what this read learned.',
  reasonNotAStore:
    'This address does not look like a web shop: there were no products to search. Check the address, or try the shop’s main domain.',
  reasonTooSlow:
    'Your shop’s search answers too slowly for a shopping assistant: it took over 12 seconds. A faster search on the shop, or reading it again later, may help.',
  reasonOurSide:
    'The read stopped because of a problem on our side, not with your shop, so the assistant is not ready yet.',
  reasonOurSideLive:
    'The read stopped because of a problem on our side, not with your shop. The published assistant keeps working as it is.',
  reasonOurSideKept:
    'The read stopped because of a problem on our side, not with your shop, so nothing changed: version {version} stays as it was.',
  reasonTryLater: 'Try reading it again in a little while.',
  errorNoCredits:
    'There are no credits for this right now. A company owner can check the plan and limits in Usage.',
  errorReadUnavailable: 'This shop can’t be read right now. Try again in a few minutes.',
  reasonTimeout:
    'The read reached its time limit before it finished. Reading again continues from what it already verified.',
  retrying: 'Starting again…',
  readTitleChecks: 'Check the saved read again',
  checksScopeLabel: 'Checks',
  readScopeChecks:
    'Shopper searches, products, browsing and the cart, on what the last read saved. The shop is not read again.',
  readScopeChecksProducts:
    'Product searches, browsing, photos, colours and sizes, on what the last read saved. The shop is not read again.',
  readScopeChecksCart:
    'The cart’s add, read back, remove and empty, and product reads, on what the last read saved. The shop is not read again.',
  readScopeChecksChat:
    'The handoff to your existing chat, and product reads, on what the last read saved. The shop is not read again.',
  readScopeChecksMarket:
    'The market’s products, prices and cart, on what the last read saved. The shop is not read again.',
  checksCost: 'Only the new checks use company credits',
  checksAfter: 'Saves a new draft if the checks pass. Nothing reaches shoppers until you publish.',
  checksAfterLive:
    'Saves a new draft if the checks pass. The published assistant keeps working, and shoppers see the change only after you publish.',
  checksStarted: 'The checks have started. When the new draft is ready, ask Chat to check it.',
  checkingSavedWork: 'Checking the saved work again',
  checkAgain: 'Check again',
  checkingAgain: 'Checking again…',
  reasonCheckAgain:
    'Check again tests the saved work once more, without reading the store again. Only the new checks use company credits.',
  fixWithChat: 'Fix with Chat',
  fixReadPrompt:
    'The read of {name} ({domain}) stopped. Look at what happened and help me get it working: tell me what you find, suggest what I could tell the read, then check it again or read it again with that hint, reading as little of the store as you can.',
  fixReadPromptStep:
    'The read of {name} ({domain}) stopped at “{step}”. Look at what happened and help me get it working: tell me what you find, suggest what I could tell the read, then check it again or read it again with that hint, reading as little of the store as you can.',
  stepStateDone: 'done',
  stepStateRunning: 'in progress',
  stepStateWaiting: 'not started',
  stepStateFailed: 'stopped',
  newVersionTitle: 'Version {version} is ready',
  newVersionBody: 'Publish it to replace version {live}.',
  newVersionBodyLive: 'Publish it to replace version {live} on {domain}.',
  previewSize: 'Preview size',
  designWithChat: 'Open design',
  designReviewBody: 'Review the current design, then tell Chat what you’d like to adjust.',
  designWhileTitle: 'Design it while we read your store',
  designWhileBody:
    'Chat can shape the assistant’s look, words and logo with you now. The design is ready when the read is.',
  designPreviewNote: 'A preview with example products until your store is read.',
  proposedDesign: 'Proposed design',
  proposedDesignNote:
    'The widget with this change, shown with example products. Approving saves it as a new draft; publishing stays on the Storefront page.',
  lineSources: 'From the store’s pages:',
  readAgainConfirm: 'Read {name} again? It takes about 10 to 30 minutes and uses company credits.',
  readAgainLive: 'The published assistant keeps working until you publish the new version.',
  readAgainNow: 'Read again',
  switchStore: 'Switch store or add one',
  storesLabel: 'Your stores',
  addStore: 'Add another store',
  backToStores: 'Back to your stores',
  storeLive: 'Published',
  storeReady: 'Ready',
  storeReading: 'Reading',
  storeStopped: 'Stopped',
  actionDesignTitle: 'Change the shopping assistant’s design',
  actionKnowledgeTitle: 'Change what the shopping assistant knows',
  readTitleProducts: 'Read the products again',
  readTitleCart: 'Check the cart again',
  readTitleTheirChat: 'Check the way to your own chat again',
  readTitleAll: 'Read the whole shop again',
  readTitleStore: 'Read a new shop',
  readTitleMarket: 'Add a market',
  readShopLabel: 'Shop',
  readScopeLabel: 'Reads',
  readTimeLabel: 'Takes',
  readCostLabel: 'Cost',
  readAfterLabel: 'After',
  readScopeProducts: 'Product search, sorting, photos, colours and sizes',
  readScopeCart: 'Adding to the cart and removing from it',
  readScopeTheirChat: 'How shoppers reach your own chat',
  readScopeAll: 'Products, the cart, your own chat and your markets',
  readScopeStore: 'Products, prices and sizes, the cart, your own chat, your look and your markets',
  readScopeMarket: '{market}: its products, prices and cart',
  readTime: 'Usually {min}–{max} minutes',
  readCost: 'Uses company credits',
  readCostLast: 'Uses company credits · the last read of this shop used {credits}',
  readCostLastPartial:
    'Uses company credits · the last read of this shop, stopped early, used at least {credits}',
  readAfter: 'Saves a new draft. Nothing reaches shoppers until you publish.',
  readAfterLive:
    'Saves a new draft. The published assistant keeps working, and shoppers see the change only after you publish.',
  readAfterNew: 'Saves a first draft for you to try. Nothing is published.',
  readCartNote: 'To check the cart, it adds a test item and removes it again. It never checks out.',
  readFocusLabel: 'What Chat asked the read to fix',
  readFooter: 'Nothing starts until you approve. You can stop it in the Setup tab.',
  readStartedAt:
    'Started {time} · usually {min}–{max} minutes. When the new draft is ready, ask Chat to check it.',
  readFocusAll: 'Show all',
  readFocusLess: 'Show less',
  readStart: 'Start the read',
  addToSite: 'Add it to your site',
  readTab: 'Request',
  readStarted: 'The read has started. When the new draft is ready, ask Chat to check it.',
  readFollow: 'Follow it in the Setup tab',
  proposedDesignLive:
    'Your store’s real preview in this design. Ask it something: nothing is added to a real cart. Approving saves it as a new draft; publishing stays on the Storefront page.',
  designChanges: 'What changes',
  designReset: 'back to the default',
  designGroupColors: 'Colours',
  designGroupType: 'Fonts',
  designGroupShape: 'Shape',
  designGroupLauncher: 'Button on the page',
  designGroupPanel: 'Header',
  designGroupProducts: 'Products',
  designGroupCart: "Shop's cart",
  designGroupCopy: 'Words',
  designGroupLogo: 'Logo',
  designGroupCss: 'Custom CSS',
  designColorBrand: 'Brand',
  designColorActions: 'Add and Send',
  designColorPanel: 'Panel',
  designColorSurface: 'Surfaces',
  designColorInk: 'Text',
  designColorMuted: 'Secondary text',
  designColorLine: 'Lines',
  designColorBubble: 'Shopper’s messages',
  designColorBubbleInk: 'Shopper’s text',
  designActionsBrand: 'Brand colour',
  designActionsInk: 'Text colour',
  designWordsName: 'Name',
  designWordsGreeting: 'Greeting',
  designWordsStarters: 'Starter questions',
  designWordsLauncher: 'Button label',
  designWordsPlaceholder: 'Placeholder',
  designWordsAi: 'AI label',
  designWordsPerson: 'Link to a person',
  noteNew: 'New',
  noteChanged: 'Changed',
  noteRemoved: 'Removed',
  noteSourceKnowledge: 'Knowledge page: {title}',
  noteSourceFile: 'File: {name}',
  noteSourceChat: 'From the chat',
  knowledgeApprovalNote:
    'Shoppers can read everything in these notes. Approving saves a new draft: ask it in the Widget tab, then publish it in the Install tab.',
  profileTitle: 'About this store',
  profileChecked: 'Checked {date}',
  profileSources: 'Written from',
  profileSample: '{count} products sampled for the price level',
  profileSampleOne: '{count} product sampled for the price level',
  profileAssistant: 'What the assistant can do here',
  profileTeam: 'Written by your team',
  profileEmpty:
    'Not written yet. Ask Chat to run a knowledge pass: it writes this from your own pages and what it saw on your site, and you can add your own lines.',
  profileWithChat: 'Add or change with Chat',
  profilePrompt:
    'Help me improve what the shopping assistant for {name} ({domain}) knows about our shop as a whole: what we sell, what we are known for, who we are for and what shoppers can do here. Read the About this store profile first and tell me what is missing or wrong. Anything I tell you about the shop that no page says goes in as a line from our team.',
  profilePartNavigation: 'Departments',
  profilePartWelcome: 'Greeting',
  profilePartMarkets: 'Markets',
  profilePartFunctions: 'What the assistant can do',
  profilePartProducts: 'Products',
  profilePartPages: 'Service pages',
  profileCleared: 'Clears the written profile',
  knowledgeTitle: 'What it knows',
  knowledgeBody:
    'Besides your products, prices and stock, which it always reads live from your store.',
  knowledgeEmpty:
    'Only your catalogue so far. Add delivery, returns or payment information with Chat, from your own pages or Knowledge.',
  knowledgeWithChat: 'Add with Chat',
  knowledgeChangeWithChat: 'Change with Chat',
  knowledgePrompt:
    'Help me choose what the shopping assistant for {name} ({domain}) tells shoppers besides our products, such as delivery, returns and payment. Find it on our own pages or in our Knowledge, and propose notes I can approve.',
  pagesTitle: 'Pages it reads',
  pagesBody:
    'The pages your notes come from. Poneva checks them every day, and Chat tells you when one changes.',
  pagesEmpty:
    'No pages yet. The pages your shop’s read finds, and any you name in Chat, show here.',
  pageFound: 'found by Poneva',
  pageLinkedFrom: 'linked from {page}',
  pageAddedByYou: 'added by you',
  pageFromNote: 'named in a note',
  pageNoteOne: '1 note',
  pageNotesMany: '{count} notes',
  pageStatusOk: 'Up to date',
  pageStatusChanged: 'Changed',
  pageStatusUnreadable: 'Cannot be read',
  pageStatusUnchecked: 'Not checked yet',
  pagesChangeWithChat: 'Change with Chat',
  pagesUpdateWithChat: 'Update notes with Chat',
  pagesPrompt:
    'Help me change which pages the shopping assistant of {name} ({domain}) reads for its notes. Show me the pages it uses now and ask me which to add or remove.',
  pagesUpdatePrompt:
    'Some pages of {name} ({domain}) changed or can no longer be read since their notes were made. Show me which, and make the notes again from them as a draft I can look at.',
  viewCompare: 'Compare',
  lookDeviceLabel: 'Compare on',
  lookShop: 'Your shop',
  lookShopCaption: 'Home page as read {when}',
  lookWidget: 'The assistant',
  lookWidgetCaption: 'Version {version}',
  lookLoading: 'Setting the assistant beside your shop…',
  lookFailed: 'The comparison could not be made right now.',
  lookNoShopPicture: 'The read did not keep a picture of your shop at this width.',
  lookNoneTitle: 'Your shop’s own look was not kept',
  lookNoneBody:
    'This read did not record it. Read the store again from Setup, and its logo, colours and pages are kept for comparing.',
  lookColoursShop: 'Colours your shop uses',
  lookColoursAssistant: 'Colours of the assistant',
  lookRoleHeader: 'Header',
  lookRoleBrand: 'Brand',
  lookSame: 'The same as your shop’s {colour} ({source})',
  lookOff: 'Close to your shop’s {colour} ({source}) but not the same, difference {delta}',
  lookOwn: 'Its own colour: your shop does not use it',
  lookNeutral: 'Your shop was not loaded for this: only what the read kept is shown.',
  lookFromLogo: 'logo',
  lookFromTheme: 'theme colour',
  lookFromTile: 'tile colour',
  lookFromMask: 'pinned icon',
  lookFromManifest: 'app manifest',
  lookFromHeader: 'header',
  lookFromButton: 'buttons',
  lookFromVariable: 'style colour',
  lookFix: 'Fix with Chat',
  lookFixPrompt:
    'The assistant’s {role} colour is {colour}, close to your shop’s {exact} ({source}) but not the same. Please use your shop’s exact colour {exact}.',
  lookSwatch: 'Colour {colour}',
  errorDesignChecks: 'This version did not pass the design checks.',
  designSamplesBlocked:
    'Card layouts can’t be proven here: your site blocks automated visits. Colours, fonts and the logo still work.',
  designCheckLabel: 'An AI label is too faint to read.',
  designCheckReach: 'A button is out of reach.',
  designCheckSize: 'Some text is too small.',
  designCheckOverflow: 'The panel scrolls sideways.',
  designCheckRender: 'The checks could not run. Try again in a moment.',
  fixDesignPrompt:
    'I tried to publish the newest draft of {name} ({domain}) and it did not pass the design checks: {facts}. Please fix the design in this chat and check it on phone and computer. Do not publish it.',
} as const;
export type StorefrontCopyKey = keyof typeof en;

const sv: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Shoppingassistent',
  designUnconfirmed:
    'Stoppades innan Chat kunde bekräfta att designen sparades. Assistentens canvas visar den nyaste versionen.',
  errorNewerVersion:
    'En nyare version har sparats sedan du öppnade det här. Titta på den först och publicera sedan.',
  canvasSelectHint:
    'Klicka på en del av assistenten för att ändra den i chatten · Esc för att sluta',
  canvasDraft: 'Version {version} · inte publicerad än',
  canvasDraftOverLive: 'Version {version} · version {live} är publicerad',
  canvasLive: 'Version {version} · publicerad',
  canvasNoVersion: 'Den första versionen kommer när läsningen av butiken är klar',
  canvasPublish: 'Publicera på Storefront-sidan',
  canvasFrameTitle: 'Shoppingassistent för {name}',
  viewLabel: 'Vy',
  photosGood: '{count} produktbilder: hela och skarpa',
  photosGoodOne: 'Produktbilden är hel och skarp',
  canvasMissingTitle: 'Den här shoppingassistenten finns inte i den här arbetsytan',
  canvasMissingBody: 'Den hör till en annan arbetsyta, eller så har den tagits bort.',
  canvasMissingLink: 'Se arbetsytans assistenter',
  photosLabel: 'Produktbilder',
  photosSmall: '{count} små i sina rutor',
  photosCut: '{count} avskurna',
  photosSoft: '{count} förstorade, så de blir oskarpa',
  photosMissing: '{count} utan bild',
  photosFix: 'Åtgärda med Chat',
  photosPrompt:
    'Få produktbilderna i korten att se rätt ut: varje bild hel, tillräckligt stor i sin ruta och skarp.',
  photosFacts:
    '{total} bilder: {small} små i sina rutor, {cut} avskurna, {soft} förstorade, {missing} utan bild. Storlekar: {sizes}',
  viewDesign: 'Design',
  viewVersions: 'Versioner',
  versionsLoading: 'Laddar versioner…',
  versionsFailed: 'Versionerna kunde inte laddas.',
  versionTitle: 'Version {version}',
  versionLive: 'Publicerad',
  versionChecked: 'Kontrollerad',
  versionToday: 'Idag',
  versionYesterday: 'Igår',
  versionEarlier: 'Tidigare',
  versionByPerson: 'Ändrad av {name}',
  versionByChat: 'Från chatten',
  versionFromReading: 'Från läsningen av {domain}',
  versionPublishedBy: 'publicerad av {name}, {at}',
  versionPublish: 'Publicera',
  versionRestore: 'Återställ…',
  versionRestoreTitle: 'Återställa version {version}?',
  versionRestoreBody:
    'Den blir version {next}, ett nytt utkast. Din webbplats ändras inte förrän du publicerar.',
  versionRestoreConfirm: 'Återställ',
  versionRestoreCancel: 'Avbryt',
  versionRestored: 'Återställd som version {version}',
  versionRestoreFailed: 'Versionen kunde inte återställas.',
  compareTitle: 'Jämför version {to} med version {from}',
  compareTitleLive: 'Jämför version {to} med version {from}, publicerad',
  compareHint: 'Dra för att jämföra',
  compareLabel: 'Jämför version {from} och version {to}',
  compareTagLive: 'v{version} · Publicerad',
  compareTag: 'v{version}',
  changesTitle: 'Vad som ändrades från version {from} till {to}',
  changesNone: 'Designen är densamma. Bara anteckningarna eller butikens data ändrades.',
  changeAdded: 'Tillagd',
  changeRemoved: 'Borttagen',
  changeChanged: 'Ändrad',
  changeDrawn: 'en ritad logga',
  changeCss: 'egen CSS',
  changeYes: 'ja',
  changeNo: 'nej',
  'field.colors.brand': 'Varumärkesfärg',
  'field.colors.actions': 'Knappfärg',
  'field.colors.panel': 'Panelens färg',
  'field.colors.surface': 'Ytfärg',
  'field.colors.ink': 'Textfärg',
  'field.colors.muted': 'Dämpad textfärg',
  'field.colors.line': 'Linjefärg',
  'field.colors.bubble': 'Kundens meddelanden',
  'field.colors.bubbleInk': 'Text i kundens meddelanden',
  'field.colors.header': 'Sidhuvudets färg',
  'field.colors.headerInk': 'Sidhuvudets textfärg',
  'field.colors.tints': 'Mjuka nyanser',
  'field.copy.assistantName': 'Namn',
  'field.copy.greeting': 'Hälsning',
  'field.copy.placeholder': 'Frågerutan',
  'field.copy.starters': 'Förslag',
  'field.copy.launcherLabel': 'Ord på knappen',
  'field.copy.aiLabel': 'AI-märkning',
  'field.copy.personLabel': 'Länk till en person',
  'field.type.body': 'Brödtextens typsnitt',
  'field.type.display': 'Rubrikernas typsnitt',
  'field.type.fontCss': 'Typsnittets källa',
  'field.type.scale': 'Textstorlek',
  'field.shape.radius': 'Hörn',
  'field.shape.shadow': 'Skugga',
  'field.shape.density': 'Luft',
  'field.launcher.style': 'Knappen på datorer',
  'field.launcher.phoneStyle': 'Knappen på mobiler',
  'field.launcher.side': 'Knappens sida',
  'field.launcher.icon': 'Knappens ikon',
  'field.panel.header': 'Sidhuvudets storlek',
  'field.panel.opening': 'Första skärmen',
  'field.products.layout': 'Produkter',
  'field.products.imageFit': 'Produktbilder',
  'field.products.imageRatio': 'Bildrutans form',
  'field.products.imagePadding': 'Luft runt bilderna',
  'field.products.imageBackground': 'Bildrutans färg',
  'field.products.picker': 'Storleksval',
  'field.products.showStock': 'Lagerstatus visas',
  'field.cart.landing': 'Animation i varukorgen',
  'field.logo.svg': 'Logga',
  'field.logo.shape': 'Loggans form',
  'field.logo.plate': 'Loggans platta',
  'field.css': 'Egen CSS',
  'value.ink': 'bläck',
  'value.brand': 'varumärkesfärgen',
  'value.small': 'liten',
  'value.medium': 'mellan',
  'value.large': 'stor',
  'value.square': 'fyrkantig',
  'value.soft': 'mjuk',
  'value.round': 'rund',
  'value.flat': 'platt',
  'value.deep': 'djup',
  'value.compact': 'tät',
  'value.comfortable': 'luftig',
  'value.pill': 'avlång med ord',
  'value.circle': 'rund',
  'value.right': 'höger',
  'value.left': 'vänster',
  'value.logo': 'logga',
  'value.chat': 'pratbubbla',
  'value.sparkle': 'gnistra',
  'value.bag': 'påse',
  'value.full': 'hel',
  'value.middle': 'i mitten',
  'value.bottom': 'längst ner',
  'value.rows': 'rader',
  'value.grid': 'rutnät',
  'value.carousel': 'en rad att svepa',
  'value.contain': 'hela bilden',
  'value.cover': 'fylld ruta',
  'value.portrait': 'stående',
  'value.landscape': 'liggande',
  'value.none': 'ingen',
  'value.pop': '+1 som stiger',
  'value.ring': 'ring som sprids',
  'value.blink': 'blinkar',
  'value.chips': 'knappar på rad',
  'value.menu': 'en lista',
  'value.rounded': 'rundad',
  designCardTitle: 'Shoppingassistent för {name}',
  designCardSaved: 'Version {version} sparad · inte publicerad än',
  designCardSavedFirst: 'Designen är sparad · den första versionen får den',
  designSaving: 'Sparar designen…',
  selectedAssistantPart: 'Valt: en del av assistenten',
  loading: 'Laddar Storefront…',
  disabledTitle: 'Storefront är inte påslaget i den här arbetsytan.',
  disabledBody:
    'Det är i tidig åtkomst och kommer i omgångar. Fråga din kontakt på Poneva när det når er.',
  loadFailed: 'Storefront kunde inte laddas.',
  startTitle: 'En shoppingassistent för din butik',
  startBody:
    'Poneva läser din butik som en kund använder den: sökningen, produkterna, varukorgen och chatten ni redan har. Det tar ungefär 10 till 30 minuter och använder företagets krediter, och du kan jobba vidare under tiden.',
  urlLabel: 'Din butiks adress',
  urlPlaceholder: 'https://www.dinbutik.se',
  startButton: 'Läs min butik',
  starting: 'Startar…',
  startFailed: 'Butiken kunde inte läsas. Kontrollera adressen och försök igen.',
  tabConversations: 'Samtal',
  tabOverview: 'Översikt',
  allAssistants: 'Alla assistenter',
  allAssistantsNote: '{assistants} assistenter · {sites} webbplatser',
  allAssistantsMenuNote: '{count} samtal den här veckan',
  marketLabel: 'Marknad',
  allMarkets: 'Alla marknader',
  allMarketsNote: '{count} webbplatser',
  scopeIn: '{assistant} i {market}',
  askAbout: 'Fråga Chat om {scope}',
  overviewAssistantsTitle: 'Assistenter och marknader',
  overviewMarketsTitle: 'Marknader',
  overviewAssistantsOnly: 'Assistenter',
  overviewIdle: '{count} assistenter utan samtal än',
  allAssistantsCount: '{assistants} assistenter',
  overviewFailed: 'Översikten kunde inte laddas.',
  colConversations: 'Samtal',
  colCart: 'I varukorgen',
  colCheckout: 'Kassan',
  colPerson: 'Person',
  colCost: 'Krediter',
  rowMarkets: '{count} marknader',
  rowVersion: 'version {version}',
  rowAria:
    '{name}, {sub}: {conversations} samtal, {cart} till varukorgen, {checkout} till kassan, {person} bad om en person, krediter {cost}',
  versionPublishedAt: 'publicerad {at}',
  designColorHeader: 'Sidhuvud',
  designColorHeaderInk: 'Sidhuvudets text',
  designGroupTranslations: 'Ord på andra språk',
  topicsTitle: 'Vad kunderna frågar',
  topicsNote: 'Ämnen som Chat hittade i de första frågorna',
  startPagesTitle: 'Var samtalen börjar',
  startPagesOne: 'Alla samtal började på samma slags sida: {page}.',
  'startPage.product': 'Produktsidor',
  'startPage.search': 'Sökresultat',
  'startPage.start': 'Startsidan',
  'startPage.cart': 'Varukorgen',
  'startPage.other': 'Andra sidor',
  addMarketInChat: 'Lägg till en marknad i chatten',
  marketsTitle: 'Marknader',
  marketsBody:
    'En assistent och en design. Varje marknad har sitt eget språk, sin valuta, sina produkter, sin varukorg och sin chatt med en person.',
  marketChecked: 'kontrollerad {when}',
  'marketStatus.ready': 'Klar',
  'marketStatus.found': 'Inte tillagd',
  'marketStatus.checking': 'Kontrolleras',
  'marketStatus.failed': 'Behöver åtgärdas',
  addMarketPrompt:
    'Lägg till en marknad till shoppingassistenten för {name} ({domain}): hitta butikens andra länder, språk eller webbplatser och fråga mig vilka som ska läggas till.',
  tabWidget: 'Widget',
  tabInstall: 'Installera',
  tabSetup: 'Kom igång',
  statusReading: 'Läser din butik',
  readingProducts: 'Läser produkterna igen',
  readingCart: 'Kontrollerar varukorgen igen',
  readingChat: 'Kontrollerar vägen till er egen chatt',
  readingMarket: 'Lägger till marknaden {market}',
  readingMarketAny: 'Lägger till en marknad',
  statusReady: 'Redo att installera',
  statusFailed: 'Läsningen stoppades',
  statusLive: 'Live · version {version} på {domain}',
  statusPublished: 'Publicerad · version {version} · inte på din webbplats än',
  statusPublishedUnverified:
    'Publicerad · version {version} · kunde inte kontrollera din webbplats',
  stepOpen: 'Öppnar sajten',
  stepSearch: 'Sökning',
  stepProducts: 'Produkter',
  stepCart: 'Varukorg',
  stepChat: 'Er befintliga chatt',
  stepLook: 'Ert utseende och logotyp',
  stepChecks: 'Kontrollerar att allt fungerar',
  stepReady: 'Klart',
  stepDetailOpen: 'Öppnar {domain}',
  stepDetailSearch: 'Hittar butikens produkter',
  stepDetailProducts: 'Läser produkter, priser, bilder, färger och storlekar',
  stepDetailCart: 'Provar varukorgen: lägga till, läsa av, ta bort och tömma',
  stepDetailChat: 'Letar efter er befintliga chatt',
  stepDetailLook: 'Läser butikens logotyp och färger',
  stepDetailChecks: 'Provar kunders sökningar, produkter, bläddring och varukorgen',
  stepDetailChecksProducts:
    'Kontrollerar produktsökningar, bläddring, bilder, färger och storlekar',
  stepDetailChecksCart:
    'Kontrollerar att lägga till, läsa av, ta bort och tömma varukorgen, och produktläsningar',
  stepDetailChecksChat: 'Kontrollerar överlämningen till er befintliga chatt, och produktläsningar',
  stepDetailChecksMarket: 'Kontrollerar marknadens produkter, priser och varukorg',
  nowRunning: 'Nu: {step}',
  nowProgress: 'Steg {n} av {total} · {minutes} min hittills',
  nowReady: 'Din butik är redo för en assistent',
  nowReadyBody: 'Sökning, produkter och varukorg fungerar.',
  nowLastRead: 'Senast läst {when}',
  nowUsually: 'oftast {min}–{max}',
  stepsRead: 'Läst {when} · Visa stegen',
  nowFailed: 'Läsningen stoppades',
  factAnswers: 'Svarar kunder med',
  costCounting: 'Räknas…',
  factPaid: 'Betalas från',
  paidCompany: 'Företagets krediter',
  creditsAmount: '{amount} krediter',
  creditsOne: '{amount} kredit',
  creditsUnder: 'under {amount} krediter',
  stopRun: 'Stoppa körningen',
  stopping: 'Stoppar…',
  closeNote: 'Du kan lämna sidan. Körningen fortsätter.',
  readyNote: 'Nästa steg: prova din widget och installera den.',
  buildWidget: 'Prova widgeten',
  readAgain: 'Läs butiken igen',
  foundTitle: 'Det här hittades',
  foundProducts: 'Produkter och priser lästa',
  foundSearch: 'Sökningen fungerar',
  foundCart: 'Lägg i varukorgen fungerar',
  foundChat: 'Er {vendor}-chatt',
  foundNoChat: 'Ingen egen chatt',
  foundLook: 'Era färger och er logotyp',
  widgetTitle: 'Din widget',
  widgetBody:
    'Din assistent, i era färger. Prova den här: den svarar med era riktiga produkter. Inget läggs i en riktig varukorg.',
  widgetLoading: 'Laddar förhandsvisningen…',
  widgetFailed: 'Förhandsvisningen kunde inte laddas.',
  widgetNotReady: 'Widgeten blir klar när stegen under Kom igång är färdiga.',
  installLineTitle: 'Lägg till den här raden på din sajt',
  installLineBody:
    'Den fungerar på alla plattformar: i sidornas head, i Shopify i theme.liquid precis före </head>. Inget syns förrän du publicerar.',
  installLineBodyLive:
    'Den fungerar på alla plattformar: i sidornas head, i Shopify i theme.liquid precis före </head>. Sedan möter kunderna version {version}.',
  installPolicyAsk: 'Har din sajt en Content Security Policy?',
  installPolicy:
    'Om din sajt har en Content Security Policy behöver du också tillåta {origin} i {directives}, annars kan assistenten inte starta.',
  installPolicyRefuses:
    'Din sajts Content Security Policy stoppar assistenten: tillåt {origin} i {directives}, annars startar ingenting.',
  installPolicyNonce:
    'Din policy kör bara skript den litar på, så lägg till raden med sidornas nonce.',
  installPolicyEval: "Assistenten behöver också 'unsafe-eval' i script-src.",
  copy: 'Kopiera',
  copied: 'Kopierat',
  domainsTitle: 'Sajter där den svarar',
  domainsBody: 'På alla andra sajter är den tyst, även om de kopierar raden.',
  lineFound: 'Raden hittad',
  lineNotSeen: 'Inte på sidan än',
  lineUnverified:
    'Kunde inte kontrollera: sidan blockerar automatiska besök. Assistenten fungerar ändå för kunderna.',
  lineChecked: 'kontrollerad {when}',
  lineCheckAgain: 'Kontrollera igen',
  lineChecking: 'Kontrollerar sidan…',
  installCheckBusy: 'Kontrollen är upptagen just nu. Försök igen om en stund.',
  publishTitle: 'Publicera',
  publishBody: 'Kunder på {domain} ser den direkt. Du kan avpublicera när som helst.',
  publishButton: 'Publicera version {version}',
  publishConfirm: 'Publicera version {version} av {name}?',
  publishNow: 'Publicera',
  notNow: 'Inte nu',
  publishing: 'Publicerar…',
  liveTitle: 'Publicerad: version {version}',
  liveSince: 'Sedan {since}',
  unpublish: 'Avpublicera',
  unpublishConfirm:
    'Ta bort version {version} från din sajt? Kunderna ser inte assistenten förrän du publicerar igen.',
  publishedLine: 'Publicerad nu: version {version}, sedan {since}',
  usageTitle: 'Användning',
  usageBody: 'Den använder företagets krediter, precis som resten av Chat.',
  usageDiscovery: 'Läsning av butiken',
  usageChats: 'Kundchattar',
  usageChatsValue: '{count} chattar · {cost}',
  last7: 'Senaste 7 dagarna',
  askWeek: 'Fråga Chat om veckan',
  kpiConversations: 'Samtal',
  kpiVsPrevious: '{change} jämfört med veckan innan',
  kpiCart: 'Lagt i varukorgen',
  kpiCheckout: 'Gick till kassan',
  kpiPerson: 'Bad om en person',
  kpiCost: 'Använda krediter',
  ofConversations: '{percent} av samtalen',
  checkoutNote: '{percent} av samtalen',
  personNote: '{percent} öppnade er chatt',
  costNote: 'Ungefär {amount} per samtal',
  costNoteUnder: 'Under {amount} per samtal',
  sparkLabel: 'Samtal per dag, senaste {days} dagarna',
  sparkDay: '{day}: {count} samtal',
  filterAll: 'Alla',
  filterCart: 'Lagt i varukorgen',
  filterCheckout: 'Kassan',
  filterPerson: 'Person',
  outcomeCart: 'Lagt i varukorgen',
  outcomeCheckout: 'Gick till kassan',
  outcomePerson: 'Bad om en person',
  outcomeAnswered: 'Besvarad',
  devicePhone: 'Mobil',
  deviceComputer: 'Dator',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Började på {page}',
  anonymous: 'Kunden är anonym.',
  askConversation: 'Fråga Chat om det här samtalet',
  showOlderVersions: 'Visa tidigare versioner',
  showOlder: 'Visa äldre samtal',
  filterCountsWeek: 'Antal: senaste 7 dagarna',
  readAlreadyHere: 'Butiken finns redan här, så inget nytt startade och inga krediter användes.',
  readOpenShop: 'Öppna den',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Vad kan vi lära oss av det här samtalet?',
  askWeekMessage: 'Vad kan vi lära oss av veckans samtal?',
  conversationsChipOne: '{name} · samtal från {when}',
  conversationsChipWeek: '{name} · den här veckan',
  conversationsChipWeekIn: '{name} · {market} · den här veckan',
  removeConversations: 'Ta bort samtalen',
  noConversations: 'Inga samtal än',
  noConversationsBody: 'När kunder använder assistenten visas deras samtal här.',
  pickConversation: 'Välj ett samtal för att läsa det.',
  eventCheckout: 'Gick till kassan',
  eventPerson: 'Öppnade er chatt',
  showMore: 'Visa fler',
  urlInvalid: 'Använd butikens https-adress, till exempel https://www.dinbutik.se.',
  errorNoCompany: 'Storefront kräver en företagsarbetsyta. Be din administratör lägga till dig.',
  errorPublishPermission:
    'Bara de som får publicera för ert företag kan göra det här. Fråga din administratör.',
  actionFailed: 'Det gick inte. Försök igen.',
  copyFailed: 'Kunde inte kopiera. Markera raden och kopiera den själv.',
  reasonCancelled: 'Du stoppade körningen.',
  reasonFailed: 'Läsningen kunde inte bli klar i din butik, så assistenten är inte redo.',
  reasonFailedLive:
    'Läsningen kunde inte bli klar i din butik. Den publicerade assistenten fortsätter fungera som den är.',
  reasonFailedKept:
    'Läsningen kunde inte bli klar i din butik, så inget ändrades: version {version} är kvar som den var.',
  statusReadStopped: 'Klar · senaste läsningen stoppades',
  reasonStepMarked: 'Steget som stoppade är markerat nedan.',
  reasonReadAgain: 'Läser du igen börjar den från det den här läsningen lärde sig.',
  reasonNotAStore:
    'Adressen ser inte ut som en webbutik: det fanns inga produkter att söka bland. Kontrollera adressen, eller prova butikens huvuddomän.',
  reasonTooSlow:
    'Butikens sökning svarar för långsamt för en shoppingassistent: den tog över 12 sekunder. En snabbare sökning i butiken, eller att läsa den igen senare, kan hjälpa.',
  reasonOurSide:
    'Läsningen stoppades av ett fel hos oss, inte i din butik, så assistenten är inte redo än.',
  reasonOurSideLive:
    'Läsningen stoppades av ett fel hos oss, inte i din butik. Den publicerade assistenten fortsätter fungera som den är.',
  reasonOurSideKept:
    'Läsningen stoppades av ett fel hos oss, inte i din butik, så inget ändrades: version {version} är kvar som den var.',
  reasonTryLater: 'Försök läsa igen om en stund.',
  errorNoCredits:
    'Det finns inga krediter för det här just nu. En ägare av företaget kan se över planen och gränserna under Usage.',
  errorReadUnavailable: 'Butiken kan inte läsas just nu. Försök igen om några minuter.',
  reasonTimeout:
    'Läsningen nådde sin tidsgräns innan den blev klar. Läser du igen fortsätter den från det som redan är verifierat.',
  retrying: 'Startar igen…',
  readTitleChecks: 'Kontrollera den sparade läsningen igen',
  checksScopeLabel: 'Kontrollerar',
  readScopeChecks:
    'Kundsökningar, produkter, bläddring och varukorgen, på det som den senaste läsningen sparade. Butiken läses inte igen.',
  readScopeChecksProducts:
    'Produktsökningar, bläddring, bilder, färger och storlekar, på det som den senaste läsningen sparade. Butiken läses inte igen.',
  readScopeChecksCart:
    'Att lägga till, läsa av, ta bort och tömma varukorgen, och produktläsningar, på det som den senaste läsningen sparade. Butiken läses inte igen.',
  readScopeChecksChat:
    'Överlämningen till er befintliga chatt, och produktläsningar, på det som den senaste läsningen sparade. Butiken läses inte igen.',
  readScopeChecksMarket:
    'Marknadens produkter, priser och varukorg, på det som den senaste läsningen sparade. Butiken läses inte igen.',
  checksCost: 'Bara de nya kontrollerna använder företagets krediter',
  checksAfter:
    'Sparar ett nytt utkast om kontrollerna går igenom. Inget når kunderna förrän du publicerar.',
  checksAfterLive:
    'Sparar ett nytt utkast om kontrollerna går igenom. Den publicerade assistenten fortsätter fungera, och kunderna ser ändringen först när du publicerar.',
  checksStarted:
    'Kontrollerna har startat. När det nya utkastet är klart kan du be Chat kontrollera det.',
  checkingSavedWork: 'Kontrollerar det sparade arbetet igen',
  checkAgain: 'Kontrollera igen',
  checkingAgain: 'Kontrollerar igen…',
  reasonCheckAgain:
    'Kontrollera igen testar det sparade arbetet en gång till, utan att läsa butiken igen. Bara de nya kontrollerna använder företagets krediter.',
  fixWithChat: 'Åtgärda med Chat',
  fixReadPrompt:
    'Läsningen av {name} ({domain}) stoppade. Titta på vad som hände och hjälp mig att få det att fungera: berätta vad du hittar, föreslå vad jag kan säga till läsningen och kontrollera den sedan igen eller läs om den med det tipset, så lite av butiken som möjligt.',
  fixReadPromptStep:
    'Läsningen av {name} ({domain}) stoppade vid ”{step}”. Titta på vad som hände och hjälp mig att få det att fungera: berätta vad du hittar, föreslå vad jag kan säga till läsningen och kontrollera den sedan igen eller läs om den med det tipset, så lite av butiken som möjligt.',
  stepStateDone: 'klar',
  stepStateRunning: 'pågår',
  stepStateWaiting: 'inte påbörjad',
  stepStateFailed: 'stoppad',
  newVersionTitle: 'Version {version} är klar',
  newVersionBody: 'Publicera den för att ersätta version {live}.',
  newVersionBodyLive: 'Publicera den för att ersätta version {live} på {domain}.',
  previewSize: 'Förhandsvisningens storlek',
  designWithChat: 'Öppna design',
  designReviewBody:
    'Titta på den nuvarande designen och berätta sedan för Chat vad du vill justera.',
  designWhileTitle: 'Designa den medan vi läser din butik',
  designWhileBody:
    'Chat kan forma assistentens utseende, ord och logotyp med dig redan nu. Designen är klar när läsningen är det.',
  designPreviewNote: 'En förhandsvisning med exempelprodukter tills din butik är läst.',
  proposedDesign: 'Föreslagen design',
  proposedDesignNote:
    'Widgeten med ändringen, visad med exempelprodukter. Godkänner du sparas den som ett nytt utkast; publiceringen sker på Storefront-sidan.',
  lineSources: 'Från butikens sidor:',
  readAgainConfirm:
    'Läsa {name} igen? Det tar ungefär 10 till 30 minuter och använder företagets krediter.',
  readAgainLive:
    'Den publicerade assistenten fortsätter fungera tills du publicerar den nya versionen.',
  readAgainNow: 'Läs igen',
  switchStore: 'Byt butik eller lägg till en',
  storesLabel: 'Dina butiker',
  addStore: 'Lägg till en butik till',
  backToStores: 'Tillbaka till dina butiker',
  storeLive: 'Publicerad',
  storeReady: 'Klar',
  storeReading: 'Läses',
  storeStopped: 'Stoppad',
  actionDesignTitle: 'Ändra shoppingassistentens design',
  actionKnowledgeTitle: 'Ändra vad shoppingassistenten vet',
  readTitleProducts: 'Läs produkterna igen',
  readTitleCart: 'Kontrollera varukorgen igen',
  readTitleTheirChat: 'Kontrollera vägen till er egen chatt igen',
  readTitleAll: 'Läs hela butiken igen',
  readTitleStore: 'Läs en ny butik',
  readTitleMarket: 'Lägg till en marknad',
  readShopLabel: 'Butik',
  readScopeLabel: 'Läser',
  readTimeLabel: 'Tar',
  readCostLabel: 'Kostnad',
  readAfterLabel: 'Efteråt',
  readScopeProducts: 'Produktsökning, sortering, produktbilder, färger och storlekar',
  readScopeCart: 'Att lägga i varukorgen och ta bort ur den',
  readScopeTheirChat: 'Hur kunderna når er egen chatt',
  readScopeAll: 'Produkter, varukorgen, er egen chatt och era marknader',
  readScopeStore:
    'Produkter, priser och storlekar, varukorgen, er egen chatt, ert utseende och era marknader',
  readScopeMarket: '{market}: dess produkter, priser och varukorg',
  readTime: 'Oftast {min}–{max} minuter',
  readCost: 'Använder företagets krediter',
  readCostLast: 'Använder företagets krediter · förra läsningen av butiken använde {credits}',
  readCostLastPartial:
    'Använder företagets krediter · förra läsningen av butiken, som stoppades tidigt, använde minst {credits}',
  readAfter: 'Sparar ett nytt utkast. Inget når kunderna förrän du publicerar.',
  readAfterLive:
    'Sparar ett nytt utkast. Den publicerade assistenten fortsätter fungera, och kunderna ser ändringen först när du publicerar.',
  readAfterNew: 'Sparar ett första utkast som du kan prova. Inget publiceras.',
  readCartNote:
    'För att kontrollera varukorgen lägger den i en testvara och tar bort den igen. Den går aldrig till kassan.',
  readFocusLabel: 'Vad Chat bad läsningen att rätta',
  readFooter: 'Inget startar innan du godkänner. Du kan stoppa den under Kom igång.',
  readStartedAt:
    'Startade {time} · oftast {min}–{max} minuter. När det nya utkastet är klart kan du be Chat kontrollera det.',
  readFocusAll: 'Visa allt',
  readFocusLess: 'Visa mindre',
  readStart: 'Starta läsningen',
  addToSite: 'Lägg in den på din sajt',
  readTab: 'Begäran',
  readStarted:
    'Läsningen har startat. När det nya utkastet är klart kan du be Chat kontrollera det.',
  readFollow: 'Följ den under Kom igång',
  proposedDesignLive:
    'Din butiks riktiga förhandsvisning i den här designen. Fråga den något: inget läggs i en riktig varukorg. Godkänner du sparas den som ett nytt utkast; publiceringen sker på Storefront-sidan.',
  designChanges: 'Det här ändras',
  designReset: 'tillbaka till standard',
  designGroupColors: 'Färger',
  designGroupType: 'Typsnitt',
  designGroupShape: 'Form',
  designGroupLauncher: 'Knappen på sidan',
  designGroupPanel: 'Sidhuvud',
  designGroupProducts: 'Produkter',
  designGroupCart: 'Butikens varukorg',
  designGroupCopy: 'Texter',
  designGroupLogo: 'Logotyp',
  designGroupCss: 'Egen CSS',
  designColorBrand: 'Varumärke',
  designColorActions: 'Lägg till och Skicka',
  designColorPanel: 'Panel',
  designColorSurface: 'Ytor',
  designColorInk: 'Text',
  designColorMuted: 'Sekundär text',
  designColorLine: 'Linjer',
  designColorBubble: 'Kundens meddelanden',
  designColorBubbleInk: 'Kundens text',
  designActionsBrand: 'Varumärkesfärgen',
  designActionsInk: 'Textfärgen',
  designWordsName: 'Namn',
  designWordsGreeting: 'Hälsning',
  designWordsStarters: 'Startfrågor',
  designWordsLauncher: 'Knappens text',
  designWordsPlaceholder: 'Platshållare',
  designWordsAi: 'AI-etikett',
  designWordsPerson: 'Länk till en person',
  noteNew: 'Ny',
  noteChanged: 'Ändrad',
  noteRemoved: 'Tas bort',
  noteSourceKnowledge: 'Kunskapssida: {title}',
  noteSourceFile: 'Fil: {name}',
  noteSourceChat: 'Från chatten',
  knowledgeApprovalNote:
    'Kunderna kan läsa allt i de här anteckningarna. Godkänner du sparas ett nytt utkast: fråga den under Widget och publicera den under Installera.',
  profileTitle: 'Om butiken',
  profileChecked: 'Kontrollerad {date}',
  profileSources: 'Skriven utifrån',
  profileSample: '{count} produkter provade för prisnivån',
  profileSampleOne: '{count} produkt provad för prisnivån',
  profileAssistant: 'Vad assistenten kan göra här',
  profileTeam: 'Skrivet av ert team',
  profileEmpty:
    'Inte skriven än. Be Chat köra en kunskapsrunda: den skriver detta utifrån era egna sidor och det den såg på er webbplats, och ni kan lägga till egna rader.',
  profileWithChat: 'Lägg till eller ändra med Chat',
  profilePrompt:
    'Hjälp mig förbättra vad shoppingassistenten för {name} ({domain}) vet om vår butik som helhet: vad vi säljer, vad vi är kända för, vilka vi är till för och vad kunder kan göra här. Läs profilen Om butiken först och berätta vad som saknas eller är fel. Det jag berättar om butiken som ingen sida säger läggs in som en rad från vårt team.',
  profilePartNavigation: 'Avdelningar',
  profilePartWelcome: 'Hälsning',
  profilePartMarkets: 'Marknader',
  profilePartFunctions: 'Vad assistenten kan göra',
  profilePartProducts: 'Produkter',
  profilePartPages: 'Servicesidor',
  profileCleared: 'Rensar den skrivna profilen',
  knowledgeTitle: 'Vad den vet',
  knowledgeBody:
    'Utöver dina produkter, priser och lagersaldo, som den alltid läser direkt från din butik.',
  knowledgeEmpty:
    'Bara din katalog än så länge. Lägg till leverans, returer eller betalning med Chat, från dina egna sidor eller från Kunskap.',
  knowledgeWithChat: 'Lägg till med Chat',
  knowledgeChangeWithChat: 'Ändra med Chat',
  knowledgePrompt:
    'Hjälp mig att välja vad shoppingassistenten för {name} ({domain}) berättar för kunderna utöver våra produkter, till exempel leverans, returer och betalning. Hitta det på våra egna sidor eller i vår Kunskap och föreslå anteckningar som jag kan godkänna.',
  pagesTitle: 'Sidor den läser',
  pagesBody:
    'Sidorna som anteckningarna kommer från. Poneva kontrollerar dem varje dag, och Chat berättar när en av dem ändras.',
  pagesEmpty:
    'Inga sidor än. Här visas sidorna som butikens genomsökning hittar och de du nämner i Chat.',
  pageFound: 'hittad av Poneva',
  pageLinkedFrom: 'länkad från {page}',
  pageAddedByYou: 'tillagd av dig',
  pageFromNote: 'nämnd i en anteckning',
  pageNoteOne: '1 anteckning',
  pageNotesMany: '{count} anteckningar',
  pageStatusOk: 'Uppdaterad',
  pageStatusChanged: 'Ändrad',
  pageStatusUnreadable: 'Kan inte läsas',
  pageStatusUnchecked: 'Ännu inte kontrollerad',
  pagesChangeWithChat: 'Ändra med Chat',
  pagesUpdateWithChat: 'Uppdatera anteckningar med Chat',
  pagesPrompt:
    'Hjälp mig ändra vilka sidor shoppingassistenten för {name} ({domain}) läser för sina anteckningar. Visa mig vilka sidor den använder nu och fråga mig vilka jag ska lägga till eller ta bort.',
  pagesUpdatePrompt:
    'Några sidor för {name} ({domain}) har ändrats eller går inte längre att läsa sedan anteckningarna gjordes. Visa mig vilka, och gör anteckningarna på nytt från dem som ett utkast jag kan granska.',
  viewCompare: 'Jämförelse',
  lookDeviceLabel: 'Jämför på',
  lookShop: 'Din butik',
  lookShopCaption: 'Startsidan, läst {when}',
  lookWidget: 'Assistenten',
  lookWidgetCaption: 'Version {version}',
  lookLoading: 'Placerar assistenten bredvid din butik…',
  lookFailed: 'Jämförelsen kunde inte göras just nu.',
  lookNoShopPicture: 'Läsningen sparade ingen bild av din butik i den här bredden.',
  lookNoneTitle: 'Din butiks eget utseende sparades inte',
  lookNoneBody:
    'Den här läsningen registrerade det inte. Läs butiken igen från Kom igång, så sparas dess logotyp, färger och sidor för jämförelse.',
  lookColoursShop: 'Färger som din butik använder',
  lookColoursAssistant: 'Assistentens färger',
  lookRoleHeader: 'Sidhuvud',
  lookRoleBrand: 'Varumärke',
  lookSame: 'Samma som din butiks {colour} ({source})',
  lookOff: 'Nära din butiks {colour} ({source}) men inte samma, skillnad {delta}',
  lookOwn: 'Egen färg: din butik använder den inte',
  lookNeutral: 'Din butik laddades inte för detta: bara det som läsningen sparade visas.',
  lookFromLogo: 'logotyp',
  lookFromTheme: 'temafärg',
  lookFromTile: 'rutfärg',
  lookFromMask: 'fäst ikon',
  lookFromManifest: 'appmanifest',
  lookFromHeader: 'sidhuvud',
  lookFromButton: 'knappar',
  lookFromVariable: 'stilfärg',
  lookFix: 'Åtgärda med Chat',
  lookFixPrompt:
    'Assistentens färg för {role} är {colour}, nära din butiks {exact} ({source}) men inte samma. Använd gärna din butiks exakta färg {exact}.',
  lookSwatch: 'Färg {colour}',
  errorDesignChecks: 'Den här versionen klarade inte designkontrollerna.',
  designSamplesBlocked:
    'Kortlayouter kan inte bevisas här: din webbplats blockerar automatiska besök. Färger, typsnitt och logotyp fungerar ändå.',
  designCheckLabel: 'En AI-etikett är för blek för att läsas.',
  designCheckReach: 'En knapp går inte att nå.',
  designCheckSize: 'Viss text är för liten.',
  designCheckOverflow: 'Panelen scrollar i sidled.',
  designCheckRender: 'Kontrollerna kunde inte köras. Försök igen om en stund.',
  fixDesignPrompt:
    'Jag försökte publicera det senaste utkastet av {name} ({domain}) och det klarade inte designkontrollerna: {facts}. Åtgärda designen i den här chatten och kontrollera den på mobil och dator. Publicera den inte.',
};

const bg: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Асистент за пазаруване',
  designUnconfirmed:
    'Спряно преди Chat да потвърди, че дизайнът е запазен. Платното на асистента показва най-новата му версия.',
  errorNewerVersion:
    'След като сте отворили страницата, е запазена по-нова версия. Първо я прегледайте, после я публикувайте.',
  canvasSelectHint: 'Щракнете върху част от асистента, за да я промените в чата · Esc за спиране',
  canvasDraft: 'Версия {version} · още не е публикувана',
  canvasDraftOverLive: 'Версия {version} · версия {live} е публикувана',
  canvasLive: 'Версия {version} · публикувана',
  canvasNoVersion: 'Първата версия ще се появи, когато прочитането на магазина завърши',
  canvasPublish: 'Публикувай на страницата Storefront',
  canvasFrameTitle: 'Асистент за пазаруване на {name}',
  viewLabel: 'Преглед',
  photosGood: '{count} снимки на продукти: цели и ясни',
  photosGoodOne: 'Снимката на продукта е цяла и ясна',
  canvasMissingTitle: 'Този асистент за пазаруване не е в това работно пространство',
  canvasMissingBody: 'Той принадлежи на друго работно пространство или е премахнат.',
  canvasMissingLink: 'Вижте асистентите в това работно пространство',
  photosLabel: 'Снимки на продукти',
  photosSmall: '{count} малки в своите плочки',
  photosCut: '{count} изрязани',
  photosSoft: '{count} увеличени и неясни',
  photosMissing: '{count} без снимка',
  photosFix: 'Поправи с Chat',
  photosPrompt:
    'Направете снимките на продуктите в картите правилни: всяка да е цяла, достатъчно голяма в плочката си и ясна.',
  photosFacts:
    '{total} снимки: {small} малки в плочките си, {cut} изрязани, {soft} увеличени, {missing} без снимка. Размери: {sizes}',
  viewDesign: 'Дизайн',
  viewVersions: 'Версии',
  versionsLoading: 'Зареждане на версии…',
  versionsFailed: 'Версиите не можаха да се заредят.',
  versionTitle: 'Версия {version}',
  versionLive: 'Публикувана',
  versionChecked: 'Проверена',
  versionToday: 'Днес',
  versionYesterday: 'Вчера',
  versionEarlier: 'По-рано',
  versionByPerson: 'Поискана от {name}',
  versionByChat: 'Поискана в чата',
  versionFromReading: 'От прочитане на {domain}',
  versionPublishedBy: 'публикувана от {name}, {at}',
  versionPublish: 'Публикувай',
  versionRestore: 'Възстанови…',
  versionRestoreTitle: 'Да се възстанови ли версия {version}?',
  versionRestoreBody:
    'Тя става версия {next}, нова чернова. Сайтът ви няма да се промени, докато не я публикувате.',
  versionRestoreConfirm: 'Възстанови',
  versionRestoreCancel: 'Отказ',
  versionRestored: 'Възстановена като версия {version}',
  versionRestoreFailed: 'Версията не можа да се възстанови.',
  compareTitle: 'Сравни версия {to} с версия {from}',
  compareTitleLive: 'Сравни версия {to} с версия {from}, публикувана',
  compareHint: 'Плъзнете, за да сравните',
  compareLabel: 'Сравни версии {from} и {to}',
  compareTagLive: 'v{version} · Публикувана',
  compareTag: 'v{version}',
  changesTitle: 'Какво се промени от версия {from} до {to}',
  changesNone: 'Дизайните са еднакви. Променени са само бележките или данните на магазина.',
  changeAdded: 'Добавено',
  changeRemoved: 'Премахнато',
  changeChanged: 'Променено',
  changeDrawn: 'нарисувано лого',
  changeCss: 'собствен CSS',
  changeYes: 'да',
  changeNo: 'не',
  'field.colors.brand': 'Цвят на марката',
  'field.colors.actions': 'Цвят на бутоните',
  'field.colors.panel': 'Цвят на панела',
  'field.colors.surface': 'Цвят на повърхността',
  'field.colors.ink': 'Цвят на текста',
  'field.colors.muted': 'Цвят на вторичния текст',
  'field.colors.line': 'Цвят на линиите',
  'field.colors.bubble': 'Цвят на съобщенията от купувача',
  'field.colors.bubbleInk': 'Текст на съобщенията от купувача',
  'field.colors.header': 'Цвят на заглавката',
  'field.colors.headerInk': 'Цвят на текста в заглавката',
  'field.colors.tints': 'Меки нюанси',
  'field.copy.assistantName': 'Име',
  'field.copy.greeting': 'Поздрав',
  'field.copy.placeholder': 'Поле за въпрос',
  'field.copy.starters': 'Начални въпроси',
  'field.copy.launcherLabel': 'Текст на бутона за отваряне',
  'field.copy.aiLabel': 'Етикет AI',
  'field.copy.personLabel': 'Връзка към човек',
  'field.type.body': 'Шрифт на текста',
  'field.type.display': 'Шрифт на заглавията',
  'field.type.fontCss': 'Източник на шрифта',
  'field.type.scale': 'Размер на текста',
  'field.shape.radius': 'Заобляне на ъглите',
  'field.shape.shadow': 'Сянка',
  'field.shape.density': 'Разстояние',
  'field.launcher.style': 'Бутон на компютри',
  'field.launcher.phoneStyle': 'Бутон на телефони',
  'field.launcher.side': 'Страна на бутона',
  'field.launcher.icon': 'Икона на бутона',
  'field.panel.header': 'Размер на заглавката',
  'field.panel.opening': 'Първи екран',
  'field.products.layout': 'Продукти',
  'field.products.imageFit': 'Снимки на продукти',
  'field.products.imageRatio': 'Форма на плочката за снимки',
  'field.products.imagePadding': 'Свободно място около снимките',
  'field.products.imageBackground': 'Цвят на плочката за снимки',
  'field.products.picker': 'Избор на размер',
  'field.products.showStock': 'Показване на наличност',
  'field.cart.landing': 'Анимация на количката',
  'field.logo.svg': 'Лого',
  'field.logo.shape': 'Форма на логото',
  'field.logo.plate': 'Фон на логото',
  'field.css': 'Персонализиран CSS',
  'value.ink': 'тъмен цвят',
  'value.brand': 'цвят на марката',
  'value.small': 'малък',
  'value.medium': 'среден',
  'value.large': 'голям',
  'value.square': 'квадратен',
  'value.soft': 'мек',
  'value.round': 'кръгъл',
  'value.flat': 'плосък',
  'value.deep': 'дълбок',
  'value.compact': 'компактен',
  'value.comfortable': 'удобен',
  'value.pill': 'бутон с текст във формата на хапче',
  'value.circle': 'кръг',
  'value.right': 'дясно',
  'value.left': 'ляво',
  'value.logo': 'лого',
  'value.chat': 'балонче за чат',
  'value.sparkle': 'искра',
  'value.bag': 'чанта',
  'value.full': 'пълен',
  'value.middle': 'в средата',
  'value.bottom': 'най-долу',
  'value.rows': 'редове',
  'value.grid': 'мрежа',
  'value.carousel': 'ред, който се плъзга',
  'value.contain': 'цялата снимка',
  'value.cover': 'запълнена плочка',
  'value.portrait': 'портретен',
  'value.landscape': 'пейзажен',
  'value.none': 'няма',
  'value.pop': '+1, което изплува',
  'value.ring': 'вълна',
  'value.blink': 'примигване',
  'value.chips': 'бутони в ред',
  'value.menu': 'списък',
  'value.rounded': 'заоблен',
  designCardTitle: 'Асистент за пазаруване на {name}',
  designCardSaved: 'Версия {version} е запазена · още не е публикувана',
  designCardSavedFirst: 'Дизайнът е запазен · първата версия ще го използва',
  designSaving: 'Дизайнът се запазва…',
  selectedAssistantPart: 'Избрано: част от асистента',
  loading: 'Storefront се зарежда…',
  disabledTitle: 'Storefront не е включен в това работно пространство.',
  disabledBody:
    'Услугата е в ранен достъп и се въвежда на етапи. Попитайте своя контакт в Poneva кога ще стигне до вас.',
  loadFailed: 'Storefront не можа да се зареди.',
  startTitle: 'Асистент за пазаруване за вашия магазин',
  startBody:
    'Poneva прочита вашия магазин по начина, по който го използва клиентът: търсене, продукти, количка и чата, който вече имате. Това отнема около 10 до 30 минути и използва кредитите на вашата компания, а вие можете да продължите да работите.',
  urlLabel: 'Адресът на вашия магазин',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Прочети магазина ми',
  starting: 'Стартира…',
  startFailed: 'Този магазин не можа да бъде прочетен. Проверете адреса и опитайте отново.',
  tabConversations: 'Разговори',
  tabOverview: 'Общ преглед',
  allAssistants: 'Всички асистенти',
  allAssistantsNote: '{assistants} асистента · {sites} сайта',
  allAssistantsMenuNote: '{count} разговора тази седмица',
  marketLabel: 'Пазар',
  allMarkets: 'Всички пазари',
  allMarketsNote: '{count} сайта',
  scopeIn: '{assistant} в {market}',
  askAbout: 'Попитай Chat за {scope}',
  overviewAssistantsTitle: 'Асистенти и пазари',
  overviewMarketsTitle: 'Пазари',
  overviewAssistantsOnly: 'Асистенти',
  overviewIdle: '{count} асистента все още нямат разговори',
  allAssistantsCount: '{assistants} асистента',
  overviewFailed: 'Общият преглед не можа да се зареди.',
  colConversations: 'Разговори',
  colCart: 'Добавени в количката',
  colCheckout: 'Плащане',
  colPerson: 'Човек',
  colCost: 'Кредити',
  rowMarkets: '{count} пазара',
  rowVersion: 'версия {version}',
  rowAria:
    '{name}, {sub}: {conversations} разговора, {cart} добавени в количката, {checkout} стигнали до плащане, {person} поискали човек, кредити {cost}',
  versionPublishedAt: 'публикувана {at}',
  designColorHeader: 'Заглавка',
  designColorHeaderInk: 'Текст на заглавката',
  designGroupTranslations: 'Думи на други езици',
  topicsTitle: 'За какво питат клиентите',
  topicsNote: 'Теми, открити от Chat в първите въпроси',
  startPagesTitle: 'Къде започват разговорите',
  startPagesOne: 'Всеки разговор е започнал на един вид страница: {page}.',
  'startPage.product': 'Страници на продукти',
  'startPage.search': 'Резултати от търсене',
  'startPage.start': 'Начална страница',
  'startPage.cart': 'Количка',
  'startPage.other': 'Други страници',
  addMarketInChat: 'Добави пазар в чата',
  marketsTitle: 'Пазари',
  marketsBody:
    'Един асистент и един дизайн. Всеки пазар има свой език, валута, продукти, количка и чат за връзка с човек.',
  marketChecked: 'проверено {when}',
  'marketStatus.ready': 'Готово',
  'marketStatus.found': 'Не е добавен',
  'marketStatus.checking': 'Проверява се',
  'marketStatus.failed': 'Нужна е поправка',
  addMarketPrompt:
    'Добавете друг пазар към асистента за пазаруване на {name} ({domain}): намерете другите държави, езици или сайтове на магазина и ме попитайте кой да добавя.',
  tabWidget: 'Widget',
  tabInstall: 'Инсталиране',
  tabSetup: 'Настройка',
  statusReading: 'Прочитане на магазина ви',
  readingProducts: 'Продуктите се прочитат отново',
  readingCart: 'Количката се проверява отново',
  readingChat: 'Проверка на достъпа до вашия чат',
  readingMarket: 'Пазарът {market} се добавя',
  readingMarketAny: 'Добавя се пазар',
  statusReady: 'Готово за инсталиране',
  statusFailed: 'Прочитането спря',
  statusLive: 'Активна · версия {version} на {domain}',
  statusPublished: 'Публикувано · версия {version} · още не е на сайта ви',
  statusPublishedUnverified: 'Публикувано · версия {version} · сайтът ви не можа да се провери',
  stepOpen: 'Сайтът се отваря',
  stepSearch: 'Търсене',
  stepProducts: 'Продукти',
  stepCart: 'Количка',
  stepChat: 'Съществуващият ви чат',
  stepLook: 'Вашата визия и лого',
  stepChecks: 'Проверява се дали работи',
  stepReady: 'Готово',
  stepDetailOpen: 'Отваря се {domain}',
  stepDetailSearch: 'Намират се продуктите на магазина',
  stepDetailProducts: 'Четат се продукти, цени, снимки, цветове и размери',
  stepDetailCart: 'Количката се изпробва: добавяне, повторно прочитане, премахване и изпразване',
  stepDetailChat: 'Търси се съществуващият ви чат',
  stepDetailLook: 'Четат се логото и цветовете на магазина',
  stepDetailChecks: 'Изпробват се търсенията на клиентите, продуктите, разглеждането и количката',
  stepDetailChecksProducts:
    'Проверяват се търсенето на продукти, разглеждането, снимките, цветовете и размерите',
  stepDetailChecksCart:
    'Проверяват се добавянето, повторното прочитане, премахването и изпразването на количката и прочитането на продукти',
  stepDetailChecksChat:
    'Проверява се предаването към съществуващия ви чат и прочитането на продукти',
  stepDetailChecksMarket: 'Проверяват се продуктите, цените и количката на този пазар',
  nowRunning: 'Сега: {step}',
  nowProgress: 'Стъпка {n} от {total} · {minutes} мин досега',
  nowReady: 'Вашият магазин е готов за асистент',
  nowReadyBody: 'Търсенето, продуктите и количката работят.',
  nowLastRead: 'Последно прочитане {when}',
  nowUsually: 'обикновено {min}–{max}',
  stepsRead: 'Прочетено {when} · Покажи стъпките',
  nowFailed: 'Прочитането спря',
  factAnswers: 'Отговаря на клиентите с',
  costCounting: 'Брои се…',
  factPaid: 'Платено от',
  paidCompany: 'Кредитите на компанията',
  creditsAmount: '{amount} кредита',
  creditsOne: '{amount} кредит',
  creditsUnder: 'под {amount} кредита',
  stopRun: 'Спри изпълнението',
  stopping: 'Спира…',
  closeNote: 'Можете да напуснете тази страница. Прочитането продължава.',
  readyNote: 'Следващо: изпробвайте своя widget и после го инсталирайте.',
  buildWidget: 'Изпробвайте widget',
  readAgain: 'Прочетете магазина отново',
  foundTitle: 'Какво е намерено',
  foundProducts: 'Продуктите и цените са прочетени',
  foundSearch: 'Търсенето работи',
  foundCart: 'Добавянето в количката работи',
  foundChat: 'Вашият чат {vendor}',
  foundNoChat: 'Нямаш собствен чат',
  foundLook: 'Вашите цветове и лого',
  widgetTitle: 'Вашият Widget',
  widgetBody:
    'Вашият асистент във вашите цветове. Изпробвайте го тук: отговаря с истинските ви продукти. Нищо не се добавя в истинска количка.',
  widgetLoading: 'Прегледът се зарежда…',
  widgetFailed: 'Прегледът не се зареди.',
  widgetNotReady: 'Widget ще е готов, когато завършат стъпките в раздел „Настройка“.',
  installLineTitle: 'Добави този ред към сайта си',
  installLineBody:
    'Работи на всяка платформа: в head на страниците ви, а в Shopify — в theme.liquid точно преди </head>. Нищо не се показва, докато не публикувате.',
  installLineBodyLive:
    'Работи на всяка платформа: в head на страниците ви, а в Shopify — в theme.liquid точно преди </head>. Тогава клиентите ще видят версия {version}.',
  installPolicyAsk: 'Сайтът ви задава ли Content Security Policy?',
  installPolicy:
    'Ако сайтът ви задава Content Security Policy, разрешете и {origin} в {directives}, иначе асистентът не може да стартира.',
  installPolicyRefuses:
    'Content Security Policy на сайта ви отказва асистента: разрешете {origin} в {directives}, иначе нищо няма да стартира.',
  installPolicyNonce:
    'Политиката ви изпълнява само скриптове, на които има доверие, затова добавете реда с nonce от страниците си.',
  installPolicyEval: "Асистентът има нужда и от 'unsafe-eval' в script-src.",
  copy: 'Копирай',
  copied: 'Копирано',
  domainsTitle: 'Сайтове, на които отговаря',
  domainsBody: 'На всеки друг сайт остава безмълвен, дори ако сайтът копира този ред.',
  lineFound: 'Редът е намерен',
  lineNotSeen: 'Още не е на страницата',
  lineUnverified:
    'Не може да се провери: сайтът блокира автоматични посещения. Асистентът работи за купувачите.',
  lineChecked: 'проверено {when}',
  lineCheckAgain: 'Провери отново',
  lineChecking: 'Страницата се проверява…',
  installCheckBusy: 'Проверката е заета в момента. Опитайте отново след малко.',
  publishTitle: 'Публикувай',
  publishBody:
    'Клиентите на {domain} го виждат веднага. Можете да го свалите от сайта по всяко време.',
  publishButton: 'Публикувай версия {version}',
  publishConfirm: 'Да се публикува ли версия {version} на {name}?',
  publishNow: 'Публикувай',
  notNow: 'Не сега',
  publishing: 'Публикува се…',
  liveTitle: 'Публикувана: версия {version}',
  liveSince: 'От {since}',
  unpublish: 'Спри публикуването',
  unpublishConfirm:
    'Да се премахне ли версия {version} от сайта ви? Клиентите няма да виждат асистента, докато не го публикувате отново.',
  publishedLine: 'Публикувана сега: версия {version}, от {since}',
  usageTitle: 'Използване',
  usageBody: 'Използва кредитите на компанията ви, както останалата част от Chat.',
  usageDiscovery: 'Прочитане на магазина',
  usageChats: 'Чатове с клиенти',
  usageChatsValue: '{count} чата · {cost}',
  last7: 'Последните 7 дни',
  askWeek: 'Попитай Chat за тази седмица',
  kpiConversations: 'Разговори',
  kpiVsPrevious: '{change} спрямо предходната седмица',
  kpiCart: 'Добавили в количката',
  kpiCheckout: 'Стигнали до плащане',
  kpiPerson: 'Поискали човек',
  kpiCost: 'Използвани кредити',
  ofConversations: '{percent} от разговорите',
  checkoutNote: '{percent} от разговорите',
  personNote: '{percent} са отворили чата ви',
  costNote: 'Около {amount} на разговор',
  costNoteUnder: 'Под {amount} на разговор',
  sparkLabel: 'Разговори на ден за последните {days} дни',
  sparkDay: '{day}: {count} разговора',
  filterAll: 'Всички',
  filterCart: 'Добавили в количката',
  filterCheckout: 'Плащане',
  filterPerson: 'Човек',
  outcomeCart: 'Добавили в количката',
  outcomeCheckout: 'Стигнали до плащане',
  outcomePerson: 'Поискали човек',
  outcomeAnswered: 'Получили отговор',
  devicePhone: 'Телефон',
  deviceComputer: 'Компютър',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} мин · Започнато на {page}',
  anonymous: 'Клиентът е анонимен.',
  askConversation: 'Попитай Chat за този разговор',
  showOlderVersions: 'Покажи по-стари версии',
  showOlder: 'Покажи по-стари разговори',
  filterCountsWeek: 'Брой: последните 7 дни',
  readAlreadyHere:
    'Магазинът вече е тук, затова не започна нищо ново и не бяха използвани кредити.',
  readOpenShop: 'Отвори го',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Какво можем да научим от този разговор?',
  askWeekMessage: 'Какво можем да научим от разговорите тази седмица?',
  conversationsChipOne: '{name} · разговор от {when}',
  conversationsChipWeek: '{name} · тази седмица',
  conversationsChipWeekIn: '{name} · {market} · тази седмица',
  removeConversations: 'Премахване на разговорите',
  noConversations: 'Все още няма разговори',
  noConversationsBody: 'Когато клиентите използват асистента, разговорите им ще се показват тук.',
  pickConversation: 'Изберете разговор, за да го прочетете.',
  eventCheckout: 'Отишъл до плащане',
  eventPerson: 'Отворил чата ви',
  showMore: 'Покажи още',
  urlInvalid: 'Използвай https адреса на магазина си, например https://www.yourstore.com.',
  errorNoCompany:
    'Storefront изисква фирмено работно пространство. Помоли администратора си да те добави.',
  errorPublishPermission:
    'Това могат да правят само хора с право да публикуват за компанията ви. Попитайте администратора си.',
  actionFailed: 'Не се получи. Опитайте отново.',
  copyFailed: 'Копирането не се получи. Изберете реда и го копирайте.',
  reasonCancelled: 'Спряхте прочитането.',
  reasonFailed: 'Прочитането на магазина ви не завърши, затова асистентът не е готов.',
  reasonFailedLive:
    'Прочитането на магазина ви не завърши. Публикуваният асистент продължава да работи както досега.',
  reasonFailedKept:
    'Прочитането на магазина ви не завърши, затова нищо не се промени: версия {version} остава същата.',
  statusReadStopped: 'Готово · последното прочитане спря',
  reasonStepMarked: 'Стъпката, на която спря, е отбелязана по-долу.',
  reasonReadAgain: 'При ново прочитане се започва от наученото при това прочитане.',
  reasonNotAStore:
    'Този адрес не изглежда като онлайн магазин: нямаше продукти за търсене. Проверете адреса или опитайте основния домейн на магазина.',
  reasonTooSlow:
    'Търсенето в магазина ви е твърде бавно за асистент за пазаруване: отне над 12 секунди. По-бързо търсене или ново прочитане по-късно може да помогне.',
  reasonOurSide:
    'Прочитането спря заради проблем при нас, а не в магазина ви, затова асистентът още не е готов.',
  reasonOurSideLive:
    'Прочитането спря заради проблем при нас, а не в магазина ви. Публикуваният асистент продължава да работи както досега.',
  reasonOurSideKept:
    'Прочитането спря заради проблем при нас, а не в магазина ви, затова нищо не се промени: версия {version} остава същата.',
  reasonTryLater: 'Опитайте да прочетете магазина отново след малко.',
  errorNoCredits:
    'В момента няма кредити за това. Собственик на компанията може да провери плана и лимитите в Usage.',
  errorReadUnavailable:
    'Магазинът не може да бъде прочетен в момента. Опитайте отново след няколко минути.',
  reasonTimeout:
    'Прочитането достигна времевия си лимит, преди да завърши. При ново прочитане се продължава от вече провереното.',
  retrying: 'Започва отново…',
  readTitleChecks: 'Провери отново запазеното прочитане',
  checksScopeLabel: 'Какво се проверява',
  readScopeChecks:
    'Търсения на клиенти, продукти, разглеждане и количката – върху запазеното от последното прочитане. Магазинът не се чете отново.',
  readScopeChecksProducts:
    'Търсенето на продукти, разглеждането, снимките, цветовете и размерите – върху запазеното от последното прочитане. Магазинът не се чете отново.',
  readScopeChecksCart:
    'Добавянето, повторното прочитане, премахването и изпразването на количката и прочитането на продукти – върху запазеното от последното прочитане. Магазинът не се чете отново.',
  readScopeChecksChat:
    'Предаването към съществуващия ви чат и прочитането на продукти – върху запазеното от последното прочитане. Магазинът не се чете отново.',
  readScopeChecksMarket:
    'Продуктите, цените и количката на пазара – върху запазеното от последното прочитане. Магазинът не се чете отново.',
  checksCost: 'Само новите проверки използват фирмени кредити',
  checksAfter:
    'Запазва нова чернова, ако проверките минат. Клиентите не виждат нищо, докато не публикувате.',
  checksAfterLive:
    'Запазва нова чернова, ако проверките минат. Публикуваният асистент продължава да работи, а клиентите ще видят промяната едва след публикуване.',
  checksStarted: 'Проверките започнаха. Когато новата чернова е готова, помоли Chat да я провери.',
  checkingSavedWork: 'Запазената работа се проверява отново',
  checkAgain: 'Проверете отново',
  checkingAgain: 'Проверява се отново…',
  reasonCheckAgain:
    '„Проверете отново“ тества запазената работа още веднъж, без магазинът да се чете отново. Само новите проверки използват фирмени кредити.',
  fixWithChat: 'Поправете с Chat',
  fixReadPrompt:
    'Прочитането на {name} ({domain}) спря. Вижте какво се е случило и ми помогнете да проработи: кажете ми какво откривате, предложете какво мога да кажа на прочитането и след това го проверете отново или го прочетете отново с тази подсказка, като четете възможно най-малка част от магазина.',
  fixReadPromptStep:
    'Прочитането на {name} ({domain}) спря на стъпка „{step}“. Вижте какво се е случило и ми помогнете да проработи: кажете ми какво откривате, предложете какво мога да кажа на прочитането и след това го проверете отново или го прочетете отново с тази подсказка, като четете възможно най-малка част от магазина.',
  stepStateDone: 'завършено',
  stepStateRunning: 'в процес',
  stepStateWaiting: 'не е започнато',
  stepStateFailed: 'спряно',
  newVersionTitle: 'Версия {version} е готова',
  newVersionBody: 'Публикувайте я, за да замени версия {live}.',
  newVersionBodyLive: 'Публикувайте я, за да замени версия {live} на {domain}.',
  previewSize: 'Размер на предварителния преглед',
  designWithChat: 'Отвори дизайна',
  designReviewBody: 'Разгледайте текущия дизайн и кажете на Chat какво искате да промените.',
  designWhileTitle: 'Проектирайте го, докато четем магазина ви',
  designWhileBody:
    'Chat може да оформи заедно с вас визията, текста и логото на асистента още сега. Дизайнът ще е готов, когато приключи прочитането.',
  designPreviewNote:
    'Предварителен преглед с примерни продукти, докато магазинът ви бъде прочетен.',
  proposedDesign: 'Предложен дизайн',
  proposedDesignNote:
    'Widget с тази промяна, показан с примерни продукти. Одобрението го запазва като нова чернова; публикуването остава на страницата Storefront.',
  lineSources: 'От страниците на магазина:',
  readAgainConfirm:
    'Да прочета ли {name} отново? Това отнема около 10 до 30 минути и използва фирмени кредити.',
  readAgainLive:
    'Публикуваният асистент продължава да работи, докато не публикувате новата версия.',
  readAgainNow: 'Прочети отново',
  switchStore: 'Смени магазина или добави нов',
  storesLabel: 'Вашите магазини',
  addStore: 'Добави още един магазин',
  backToStores: 'Назад към вашите магазини',
  storeLive: 'Публикуван',
  storeReady: 'Готов',
  storeReading: 'Чете се',
  storeStopped: 'Спрян',
  actionDesignTitle: 'Промени дизайна на асистента за пазаруване',
  actionKnowledgeTitle: 'Промени какво знае асистентът за пазаруване',
  readTitleProducts: 'Прочети продуктите отново',
  readTitleCart: 'Провери количката отново',
  readTitleTheirChat: 'Провери отново пътя към вашия собствен чат',
  readTitleAll: 'Прочети целия магазин отново',
  readTitleStore: 'Прочети нов магазин',
  readTitleMarket: 'Добави пазар',
  readShopLabel: 'Магазин',
  readScopeLabel: 'Какво се чете',
  readTimeLabel: 'Време',
  readCostLabel: 'Цена',
  readAfterLabel: 'След това',
  readScopeProducts: 'Търсене и сортиране на продукти, снимки на продукти, цветове и размери',
  readScopeCart: 'Добавяне в количката и премахване от нея',
  readScopeTheirChat: 'Как клиентите стигат до вашия собствен чат',
  readScopeAll: 'Продукти, количка, ваш собствен чат и вашите пазари',
  readScopeStore:
    'Продукти, цени и размери, количка, ваш собствен чат, вашата визия и вашите пазари',
  readScopeMarket: '{market}: неговите продукти, цени и количка',
  readTime: 'Обикновено {min}–{max} минути',
  readCost: 'Използва фирмени кредити',
  readCostLast:
    'Използва фирмени кредити · последното прочитане на този магазин използва {credits}',
  readCostLastPartial:
    'Използва фирмени кредити · последното прочитане на този магазин, спряно по-рано, използва поне {credits}',
  readAfter: 'Запазва нова чернова. Клиентите не виждат нищо, докато не публикувате.',
  readAfterLive:
    'Запазва нова чернова. Публикуваният асистент продължава да работи, а клиентите ще видят промяната едва след публикуване.',
  readAfterNew: 'Запазва първа чернова, която да изпробваш. Нищо не се публикува.',
  readCartNote:
    'За да провери количката, добавя тестов продукт и после го премахва. Никога не стига до плащане.',
  readFocusLabel: 'Какво Chat помоли прочитането да поправи',
  readFooter:
    'Нищо не започва, преди да одобрите. Можете да спрете прочитането в раздел „Настройка“.',
  readStartedAt:
    'Започна {time} · обикновено {min}–{max} минути. Когато новата чернова е готова, помоли Chat да я провери.',
  readFocusAll: 'Покажи всичко',
  readFocusLess: 'Покажи по-малко',
  readStart: 'Започни прочитането',
  addToSite: 'Добави го към сайта си',
  readTab: 'Заявка',
  readStarted: 'Прочитането започна. Когато новата чернова е готова, помоли Chat да я провери.',
  readFollow: 'Проследете го в раздел „Настройка“',
  proposedDesignLive:
    'Реалният преглед на магазина ви с този дизайн. Задайте въпрос: нищо не се добавя в реална количка. Одобрението запазва нова чернова; публикуването е на страницата Storefront.',
  designChanges: 'Какво се променя',
  designReset: 'обратно към стандартните настройки',
  designGroupColors: 'Цветове',
  designGroupType: 'Шрифтове',
  designGroupShape: 'Форма',
  designGroupLauncher: 'Бутон на страницата',
  designGroupPanel: 'Заглавка',
  designGroupProducts: 'Продукти',
  designGroupCart: 'Количката на магазина',
  designGroupCopy: 'Текстове',
  designGroupLogo: 'Лого',
  designGroupCss: 'Персонализиран CSS',
  designColorBrand: 'Марка',
  designColorActions: 'Добавяне и изпращане',
  designColorPanel: 'Панел',
  designColorSurface: 'Повърхности',
  designColorInk: 'Текст',
  designColorMuted: 'Вторичен текст',
  designColorLine: 'Линии',
  designColorBubble: 'Съобщения на клиента',
  designColorBubbleInk: 'Текст на клиента',
  designActionsBrand: 'Цвят на марката',
  designActionsInk: 'Цвят на текста',
  designWordsName: 'Име',
  designWordsGreeting: 'Поздрав',
  designWordsStarters: 'Начални въпроси',
  designWordsLauncher: 'Текст на бутона',
  designWordsPlaceholder: 'Заместващ текст',
  designWordsAi: 'Етикет на AI',
  designWordsPerson: 'Връзка към човек',
  noteNew: 'Ново',
  noteChanged: 'Променено',
  noteRemoved: 'Премахнато',
  noteSourceKnowledge: 'Страница със знания: {title}',
  noteSourceFile: 'Файл: {name}',
  noteSourceChat: 'От чата',
  knowledgeApprovalNote:
    'Клиентите могат да прочетат всичко в тези бележки. Одобрението запазва нова чернова: задайте въпроси в Widget, после публикувайте в раздел „Инсталиране“.',
  profileTitle: 'За магазина',
  profileChecked: 'Проверено {date}',
  profileSources: 'Написано въз основа на',
  profileSample: '{count} продукта са прегледани за ценовото ниво',
  profileSampleOne: '{count} продукт е прегледан за ценовото ниво',
  profileAssistant: 'Какво може асистентът тук',
  profileTeam: 'Написано от вашия екип',
  profileEmpty:
    'Още не е написано. Помолете Chat да пусне преглед на знанията: той го пише от вашите страници и от това, което е видял в сайта ви, а вие можете да добавите свои редове.',
  profileWithChat: 'Добави или промени с Chat',
  profilePrompt:
    'Помогни ми да подобря това, което асистентът за пазаруване на {name} ({domain}) знае за магазина ни като цяло: какво продаваме, с какво сме известни, за кого сме и какво могат да правят купувачите тук. Първо прочети профила „За магазина“ и ми кажи какво липсва или е грешно. Всичко, което ти казвам за магазина и което никоя страница не казва, влиза като ред от нашия екип.',
  profilePartNavigation: 'Отдели',
  profilePartWelcome: 'Поздрав',
  profilePartMarkets: 'Пазари',
  profilePartFunctions: 'Какво може асистентът',
  profilePartProducts: 'Продукти',
  profilePartPages: 'Страници за обслужване',
  profileCleared: 'Изчиства написания профил',
  knowledgeTitle: 'Какво знае',
  knowledgeBody:
    'Освен продуктите, цените и наличностите ви, които винаги прочита директно от магазина ви.',
  knowledgeEmpty:
    'Засега само каталогът ви. Добавете информация за доставка, връщане или плащане с Chat, от собствените си страници или от Знания.',
  knowledgeWithChat: 'Добави с Chat',
  knowledgeChangeWithChat: 'Промени с Chat',
  knowledgePrompt:
    'Помогни ми да избера какво асистентът за пазаруване на {name} ({domain}) да разказва на клиентите освен за продуктите ни, например за доставка, връщане и плащане. Намери информацията на собствените ни страници или в Знания и предложи бележки, които мога да одобря.',
  pagesTitle: 'Страници, които чете',
  pagesBody:
    'Страниците, от които идват бележките. Poneva ги проверява всеки ден, а Chat показва, когато някоя се промени.',
  pagesEmpty:
    'Все още няма страници. Тук се показват страниците, които открива четенето на магазина, и тези, които са посочени в Chat.',
  pageFound: 'открита от Poneva',
  pageLinkedFrom: 'свързана от {page}',
  pageAddedByYou: 'добавена ръчно',
  pageFromNote: 'посочена в бележка',
  pageNoteOne: '1 бележка',
  pageNotesMany: '{count} бележки',
  pageStatusOk: 'Актуално',
  pageStatusChanged: 'Променено',
  pageStatusUnreadable: 'Не може да се прочете',
  pageStatusUnchecked: 'Още не е проверено',
  pagesChangeWithChat: 'Промени с Chat',
  pagesUpdateWithChat: 'Актуализирай бележките с Chat',
  pagesPrompt:
    'Помогни ми да променя кои страници асистентът за пазаруване на {name} ({domain}) чете за бележките си. Покажи ми страниците, които използва сега, и ме попитай кои да добавя или премахна.',
  pagesUpdatePrompt:
    'Някои страници на {name} ({domain}) са се променили или вече не могат да се прочетат, след като са били направени бележките. Покажи ми кои са и направи бележките отново от тях като чернова, която мога да прегледам.',
  viewCompare: 'Сравнение',
  lookDeviceLabel: 'Сравнение на',
  lookShop: 'Вашият магазин',
  lookShopCaption: 'Начална страница, прочетена {when}',
  lookWidget: 'Асистентът',
  lookWidgetCaption: 'Версия {version}',
  lookLoading: 'Асистентът се поставя до магазина ви…',
  lookFailed: 'Сравнението не можа да бъде направено в момента.',
  lookNoShopPicture: 'При прочитането не е запазена снимка на магазина ви с тази ширина.',
  lookNoneTitle: 'Собствената визия на магазина ви не е запазена',
  lookNoneBody:
    'Това прочитане не я е записало. Прочетете магазина отново от Настройка и логото, цветовете и страниците му ще се запазят за сравнение.',
  lookColoursShop: 'Цветове, които използва магазинът ви',
  lookColoursAssistant: 'Цветове на асистента',
  lookRoleHeader: 'Заглавка',
  lookRoleBrand: 'Марка',
  lookSame: 'Същият като {colour} на магазина ви ({source})',
  lookOff: 'Близък до {colour} на магазина ви ({source}), но не същият, разлика {delta}',
  lookOwn: 'Собствен цвят: магазинът ви не го използва',
  lookNeutral:
    'Магазинът ви не е зареден за това: показва се само това, което е запазило прочитането.',
  lookFromLogo: 'лого',
  lookFromTheme: 'цвят на темата',
  lookFromTile: 'цвят на плочката',
  lookFromMask: 'закачена икона',
  lookFromManifest: 'манифест на приложението',
  lookFromHeader: 'заглавка',
  lookFromButton: 'бутони',
  lookFromVariable: 'цвят от стила',
  lookFix: 'Поправи с Chat',
  lookFixPrompt:
    'Цветът на асистента за {role} е {colour}, близък до {exact} на магазина ви ({source}), но не същият. Моля, използвайте точния цвят на магазина ви {exact}.',
  lookSwatch: 'Цвят {colour}',
  errorDesignChecks: 'Тази версия не премина проверките на дизайна.',
  designSamplesBlocked:
    'Оформлението на картите не може да се докаже тук: сайтът ви блокира автоматични посещения. Цветовете, шрифтовете и логото работят.',
  designCheckLabel: 'Етикет за AI е твърде блед, за да се чете.',
  designCheckReach: 'Бутон е недостъпен.',
  designCheckSize: 'Част от текста е твърде малка.',
  designCheckOverflow: 'Панелът се превърта настрани.',
  designCheckRender: 'Проверките не можаха да се изпълнят. Опитайте отново след малко.',
  fixDesignPrompt:
    'Опитах да публикувам най-новата чернова на {name} ({domain}), но тя не премина проверките на дизайна: {facts}. Поправете дизайна в този чат и го проверете на телефон и компютър. Не го публикувайте.',
};

const hr: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Asistent za kupnju',
  designUnconfirmed:
    'Zaustavljeno prije nego što je Chat mogao potvrditi da je dizajn spremljen. Platno asistenta prikazuje najnoviju verziju.',
  errorNewerVersion:
    'Od vašeg otvaranja spremljena je novija verzija. Najprije je pregledajte, a zatim objavite.',
  canvasSelectHint: 'Kliknite dio asistenta da biste ga promijenili u chatu · Esc za zaustavljanje',
  canvasDraft: 'Verzija {version} · još nije objavljena',
  canvasDraftOverLive: 'Verzija {version} · objavljena je verzija {live}',
  canvasLive: 'Verzija {version} · objavljena',
  canvasNoVersion: 'Prva verzija bit će dostupna nakon završetka čitanja trgovine',
  canvasPublish: 'Objavite na stranici Storefront',
  canvasFrameTitle: 'Asistent za kupnju trgovine {name}',
  viewLabel: 'Prikaz',
  photosGood: '{count} fotografija proizvoda: cijele i oštre',
  photosGoodOne: 'Fotografija proizvoda je cijela i oštra',
  canvasMissingTitle: 'Ovaj asistent za kupnju nije u ovom radnom prostoru',
  canvasMissingBody: 'Pripada drugom radnom prostoru ili je uklonjen.',
  canvasMissingLink: 'Pogledajte asistente ovog radnog prostora',
  photosLabel: 'Fotografije proizvoda',
  photosSmall: '{count} premalih u svojim pločicama',
  photosCut: '{count} odrezanih',
  photosSoft: '{count} uvećanih pa su mutne',
  photosMissing: '{count} bez fotografije',
  photosFix: 'Popravi s Chatom',
  photosPrompt:
    'Učini da fotografije proizvoda na karticama izgledaju ispravno: neka svaka bude cijela, dovoljno velika u svojoj pločici i oštra.',
  photosFacts:
    '{total} fotografija: {small} premalih u pločicama, {cut} odrezanih, {soft} uvećanih, {missing} bez fotografije. Veličine: {sizes}',
  viewDesign: 'Dizajn',
  viewVersions: 'Verzije',
  versionsLoading: 'Učitavanje verzija…',
  versionsFailed: 'Verzije nije moguće učitati.',
  versionTitle: 'Verzija {version}',
  versionLive: 'Objavljeno',
  versionChecked: 'Provjereno',
  versionToday: 'Danas',
  versionYesterday: 'Jučer',
  versionEarlier: 'Ranije',
  versionByPerson: 'Zatražio/la {name}',
  versionByChat: 'Zatraženo u chatu',
  versionFromReading: 'Iz čitanja {domain}',
  versionPublishedBy: 'objavio/la {name}, {at}',
  versionPublish: 'Objavi',
  versionRestore: 'Vrati…',
  versionRestoreTitle: 'Vratiti verziju {version}?',
  versionRestoreBody:
    'Postat će verzija {next}, nova skica. Vaša se stranica ne mijenja dok je ne objavite.',
  versionRestoreConfirm: 'Vrati',
  versionRestoreCancel: 'Odustani',
  versionRestored: 'Vraćeno kao verzija {version}',
  versionRestoreFailed: 'Verziju nije moguće vratiti.',
  compareTitle: 'Usporedite verziju {to} s verzijom {from}',
  compareTitleLive: 'Usporedite verziju {to} s objavljenom verzijom {from}',
  compareHint: 'Povucite za usporedbu',
  compareLabel: 'Usporedi verziju {from} i verziju {to}',
  compareTagLive: 'v{version} · Objavljeno',
  compareTag: 'v{version}',
  changesTitle: 'Što se promijenilo iz verzije {from} u {to}',
  changesNone: 'Dizajni su isti. Promijenile su se samo bilješke ili podaci trgovine.',
  changeAdded: 'Dodano',
  changeRemoved: 'Uklonjeno',
  changeChanged: 'Promijenjeno',
  changeDrawn: 'nacrtani logotip',
  changeCss: 'vlastiti CSS',
  changeYes: 'da',
  changeNo: 'ne',
  'field.colors.brand': 'Boja robne marke',
  'field.colors.actions': 'Boja gumba',
  'field.colors.panel': 'Boja ploče',
  'field.colors.surface': 'Boja površine',
  'field.colors.ink': 'Boja teksta',
  'field.colors.muted': 'Boja prigušenog teksta',
  'field.colors.line': 'Boja linija',
  'field.colors.bubble': 'Boja poruke kupca',
  'field.colors.bubbleInk': 'Tekst poruke kupca',
  'field.colors.header': 'Boja zaglavlja',
  'field.colors.headerInk': 'Boja teksta zaglavlja',
  'field.colors.tints': 'Meke nijanse',
  'field.copy.assistantName': 'Naziv',
  'field.copy.greeting': 'Pozdrav',
  'field.copy.placeholder': 'Polje za pitanje',
  'field.copy.starters': 'Početna pitanja',
  'field.copy.launcherLabel': 'Tekst pokretača',
  'field.copy.aiLabel': 'AI oznaka',
  'field.copy.personLabel': 'Veza s osobom',
  'field.type.body': 'Font teksta',
  'field.type.display': 'Font naslova',
  'field.type.fontCss': 'Izvor fonta',
  'field.type.scale': 'Veličina teksta',
  'field.shape.radius': 'Kutovi',
  'field.shape.shadow': 'Sjena',
  'field.shape.density': 'Razmak',
  'field.launcher.style': 'Pokretač na računalima',
  'field.launcher.phoneStyle': 'Pokretač na telefonima',
  'field.launcher.side': 'Strana pokretača',
  'field.launcher.icon': 'Ikona pokretača',
  'field.panel.header': 'Veličina zaglavlja',
  'field.panel.opening': 'Prvi zaslon',
  'field.products.layout': 'Proizvodi',
  'field.products.imageFit': 'Fotografije proizvoda',
  'field.products.imageRatio': 'Oblik pločice fotografije',
  'field.products.imagePadding': 'Prostor oko fotografija',
  'field.products.imageBackground': 'Boja pločice fotografije',
  'field.products.picker': 'Odabir veličine',
  'field.products.showStock': 'Prikazane zalihe',
  'field.cart.landing': 'Animacija košarice',
  'field.logo.svg': 'Logotip',
  'field.logo.shape': 'Oblik logotipa',
  'field.logo.plate': 'Podloga logotipa',
  'field.css': 'Prilagođeni CSS',
  'value.ink': 'tinta',
  'value.brand': 'boja robne marke',
  'value.small': 'malo',
  'value.medium': 'srednje',
  'value.large': 'veliko',
  'value.square': 'kvadratno',
  'value.soft': 'mekano',
  'value.round': 'okruglo',
  'value.flat': 'ravno',
  'value.deep': 'duboko',
  'value.compact': 'zbijeno',
  'value.comfortable': 'prostrano',
  'value.pill': 'gumb u obliku kapsule s tekstom',
  'value.circle': 'krug',
  'value.right': 'desno',
  'value.left': 'lijevo',
  'value.logo': 'logotip',
  'value.chat': 'oblačić za chat',
  'value.sparkle': 'iskrica',
  'value.bag': 'torba',
  'value.full': 'puno',
  'value.middle': 'u sredini',
  'value.bottom': 'na dnu',
  'value.rows': 'retci',
  'value.grid': 'mreža',
  'value.carousel': 'redak koji povlačite',
  'value.contain': 'cijela fotografija',
  'value.cover': 'ispunjena pločica',
  'value.portrait': 'uspravno',
  'value.landscape': 'vodoravno',
  'value.none': 'ništa',
  'value.pop': '+1 koji se diže',
  'value.ring': 'val',
  'value.blink': 'treptanje',
  'value.chips': 'gumbi u retku',
  'value.menu': 'popis',
  'value.rounded': 'zaobljeno',
  designCardTitle: 'Asistent za kupnju trgovine {name}',
  designCardSaved: 'Verzija {version} spremljena · još nije objavljena',
  designCardSavedFirst: 'Dizajn spremljen · nosit će ga prva verzija',
  designSaving: 'Spremanje dizajna…',
  selectedAssistantPart: 'Odabrano: dio asistenta',
  loading: 'Učitavanje Storefronta…',
  disabledTitle: 'Storefront nije uključen u ovom radnom prostoru.',
  disabledBody:
    'U ranom je pristupu i uvodi se postupno. Pitajte svoju kontakt-osobu u Ponevi kada će stići do vas.',
  loadFailed: 'Storefront nije moguće učitati.',
  startTitle: 'Asistent za kupnju vaše trgovine',
  startBody:
    'Poneva čita vašu trgovinu onako kako je koristi kupac: pretraživanje, proizvode, košaricu i chat koji već imate. To traje oko 10 do 30 minuta i koristi kredite vaše tvrtke, a vi možete nastaviti raditi.',
  urlLabel: 'Adresa vaše trgovine',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Pročitaj moju trgovinu',
  starting: 'Pokretanje…',
  startFailed: 'Tu trgovinu nije moguće pročitati. Provjerite adresu i pokušajte ponovno.',
  tabConversations: 'Razgovori',
  tabOverview: 'Pregled',
  allAssistants: 'Svi asistenti',
  allAssistantsNote: '{assistants} asistenata · {sites} web-mjesta',
  allAssistantsMenuNote: '{count} razgovora ovaj tjedan',
  marketLabel: 'Tržište',
  allMarkets: 'Sva tržišta',
  allMarketsNote: '{count} web-mjesta',
  scopeIn: '{assistant} u {market}',
  askAbout: 'Pitaj Chat o {scope}',
  overviewAssistantsTitle: 'Asistenti i tržišta',
  overviewMarketsTitle: 'Tržišta',
  overviewAssistantsOnly: 'Asistenti',
  overviewIdle: '{count} asistenata još nema razgovora',
  allAssistantsCount: '{assistants} asistenata',
  overviewFailed: 'Pregled nije moguće učitati.',
  colConversations: 'Razgovori',
  colCart: 'Dodano u košaricu',
  colCheckout: 'Plaćanje',
  colPerson: 'Osoba',
  colCost: 'Krediti',
  rowMarkets: '{count} tržišta',
  rowVersion: 'verzija {version}',
  rowAria:
    '{name}, {sub}: {conversations} razgovora, {cart} dodano u košaricu, {checkout} stiglo do plaćanja, {person} zatražilo osobu, krediti {cost}',
  versionPublishedAt: 'objavljeno {at}',
  designColorHeader: 'Zaglavlje',
  designColorHeaderInk: 'Tekst zaglavlja',
  designGroupTranslations: 'Riječi na drugim jezicima',
  topicsTitle: 'O čemu kupci pitaju',
  topicsNote: 'Teme koje je Chat pronašao u prvim pitanjima',
  startPagesTitle: 'Gdje razgovori počinju',
  startPagesOne: 'Svaki je razgovor počeo na jednoj vrsti stranice: {page}.',
  'startPage.product': 'Stranice proizvoda',
  'startPage.search': 'Rezultati pretraživanja',
  'startPage.start': 'Početna stranica',
  'startPage.cart': 'Košarica',
  'startPage.other': 'Druge stranice',
  addMarketInChat: 'Dodajte tržište u chatu',
  marketsTitle: 'Tržišta',
  marketsBody:
    'Jedan asistent i jedan dizajn. Svako tržište ima vlastiti jezik, valutu, proizvode, košaricu i chat za osobu.',
  marketChecked: 'provjereno {when}',
  'marketStatus.ready': 'Spremno',
  'marketStatus.found': 'Nije dodano',
  'marketStatus.checking': 'Provjera',
  'marketStatus.failed': 'Potrebna je ispravka',
  addMarketPrompt:
    'Dodaj drugo tržište asistentu za kupnju trgovine {name} ({domain}): pronađi druge države, jezike ili web-mjesta trgovine i pitaj me koje da dodam.',
  tabWidget: 'Widget',
  tabInstall: 'Instalacija',
  tabSetup: 'Postavljanje',
  statusReading: 'Čitanje vaše trgovine',
  readingProducts: 'Ponovno čitanje proizvoda',
  readingCart: 'Ponovna provjera košarice',
  readingChat: 'Ponovna provjera puta do vašeg chata',
  readingMarket: 'Dodavanje tržišta {market}',
  readingMarketAny: 'Dodavanje tržišta',
  statusReady: 'Spremno za instalaciju',
  statusFailed: 'Čitanje zaustavljeno',
  statusLive: 'Aktivno · verzija {version} na {domain}',
  statusPublished: 'Objavljeno · verzija {version} · još nije na vašoj stranici',
  statusPublishedUnverified:
    'Objavljeno · verzija {version} · vaše web-mjesto nije moguće provjeriti',
  stepOpen: 'Otvaranje web-mjesta',
  stepSearch: 'Pretraživanje',
  stepProducts: 'Proizvodi',
  stepCart: 'Košarica',
  stepChat: 'Vaš postojeći chat',
  stepLook: 'Vaš izgled i logotip',
  stepChecks: 'Provjera rada',
  stepReady: 'Spremno',
  stepDetailOpen: 'Otvaranje {domain}',
  stepDetailSearch: 'Pronalaženje proizvoda trgovine',
  stepDetailProducts: 'Čitanje proizvoda, cijena, fotografija, boja i veličina',
  stepDetailCart: 'Isprobavanje košarice: dodavanje, ponovno čitanje, uklanjanje i pražnjenje',
  stepDetailChat: 'Traženje vašeg postojećeg chata',
  stepDetailLook: 'Čitanje logotipa i boja trgovine',
  stepDetailChecks: 'Isprobavanje pretraživanja kupaca, proizvoda, pregledavanja i košarice',
  stepDetailChecksProducts:
    'Provjera pretraživanja proizvoda, pregledavanja, fotografija, boja i veličina',
  stepDetailChecksCart:
    'Provjera dodavanja, ponovnog čitanja, uklanjanja i pražnjenja košarice te čitanja proizvoda',
  stepDetailChecksChat: 'Provjera predaje vašem postojećem chatu i čitanja proizvoda',
  stepDetailChecksMarket: 'Provjera proizvoda, cijena i košarice ovog tržišta',
  nowRunning: 'Sada: {step}',
  nowProgress: 'Korak {n} od {total} · {minutes} min dosad',
  nowReady: 'Vaša je trgovina spremna za asistenta',
  nowReadyBody: 'Pretraživanje, proizvodi i košarica rade.',
  nowLastRead: 'Zadnje čitanje {when}',
  nowUsually: 'obično {min}–{max}',
  stepsRead: 'Pročitano {when} · Prikaži korake',
  nowFailed: 'Čitanje zaustavljeno',
  factAnswers: 'Odgovara kupcima pomoću',
  costCounting: 'Prebrojavanje…',
  factPaid: 'Plaća se iz',
  paidCompany: 'Kredita tvrtke',
  creditsAmount: '{amount} kredita',
  creditsOne: '{amount} kredit',
  creditsUnder: 'manje od {amount} kredita',
  stopRun: 'Zaustavi pokretanje',
  stopping: 'Zaustavljanje…',
  closeNote: 'Možete napustiti ovu stranicu. Pokretanje će se nastaviti.',
  readyNote: 'Sljedeće: isprobajte svoj widget, a zatim ga instalirajte.',
  buildWidget: 'Isprobaj widget',
  readAgain: 'Ponovno pročitaj trgovinu',
  foundTitle: 'Što je pronađeno',
  foundProducts: 'Proizvodi i cijene pročitani',
  foundSearch: 'Pretraživanje radi',
  foundCart: 'Dodavanje u košaricu radi',
  foundChat: 'Vaš chat {vendor}',
  foundNoChat: 'Nemate vlastiti chat',
  foundLook: 'Vaše boje i logotip',
  widgetTitle: 'Vaš widget',
  widgetBody:
    'Vaš asistent u vašim bojama. Isprobajte ga ovdje: odgovara vašim stvarnim proizvodima. Ništa se ne dodaje u stvarnu košaricu.',
  widgetLoading: 'Učitavanje pregleda…',
  widgetFailed: 'Pregled se nije učitao.',
  widgetNotReady: 'Widget će biti spreman kad završe koraci na kartici Postavljanje.',
  installLineTitle: 'Dodajte ovaj redak na svoju stranicu',
  installLineBody:
    'Radi na svakoj platformi: u zaglavlju stranica, a na Shopifyju u theme.liquid neposredno prije </head>. Ništa se ne prikazuje dok ne objavite.',
  installLineBodyLive:
    'Radi na svakoj platformi: u zaglavlju stranica, a na Shopifyju u theme.liquid neposredno prije </head>. Kupci će tada vidjeti verziju {version}.',
  installPolicyAsk: 'Postavlja li vaša stranica Content Security Policy?',
  installPolicy:
    'Ako vaša stranica postavlja Content Security Policy, dopustite i {origin} u {directives}, inače se asistent ne može pokrenuti.',
  installPolicyRefuses:
    'Content Security Policy vaše stranice odbija asistenta: dopustite {origin} u {directives}, inače se ništa neće pokrenuti.',
  installPolicyNonce:
    'Vaša pravila pokreću samo skripte kojima vjeruju, zato dodajte redak s nonceom svojih stranica.',
  installPolicyEval: "Asistent također treba 'unsafe-eval' u script-src.",
  copy: 'Kopiraj',
  copied: 'Kopirano',
  domainsTitle: 'Web-mjesta na kojima odgovara',
  domainsBody: 'Ostaje tih na svakom drugom web-mjestu, čak i onome koje kopira ovaj redak.',
  lineFound: 'Redak pronađen',
  lineNotSeen: 'Još nije na stranici',
  lineUnverified:
    'Nije moguće provjeriti: web-mjesto blokira automatizirane posjete. Asistent i dalje radi za kupce.',
  lineChecked: 'provjereno {when}',
  lineCheckAgain: 'Provjeri ponovno',
  lineChecking: 'Provjera stranice…',
  installCheckBusy: 'Provjera je trenutno zauzeta. Pokušajte ponovno za trenutak.',
  publishTitle: 'Objavi',
  publishBody:
    'Kupci na {domain} odmah će ga vidjeti. Možete poništiti objavu u bilo kojem trenutku.',
  publishButton: 'Objavi verziju {version}',
  publishConfirm: 'Objaviti verziju {version} trgovine {name}?',
  publishNow: 'Objavi',
  notNow: 'Ne sada',
  publishing: 'Objavljivanje…',
  liveTitle: 'Objavljeno: verzija {version}',
  liveSince: 'Od {since}',
  unpublish: 'Poništi objavu',
  unpublishConfirm:
    'Ukloniti verziju {version} s vaše stranice? Kupci više neće vidjeti asistenta dok ga ponovno ne objavite.',
  publishedLine: 'Sada objavljeno: verzija {version}, od {since}',
  usageTitle: 'Upotreba',
  usageBody: 'Koristi kredite vaše tvrtke, kao i ostatak Chata.',
  usageDiscovery: 'Čitanje trgovine',
  usageChats: 'Razgovori kupaca',
  usageChatsValue: '{count} razgovora · {cost}',
  last7: 'Posljednjih 7 dana',
  askWeek: 'Pitaj Chat o ovom tjednu',
  kpiConversations: 'Razgovori',
  kpiVsPrevious: '{change} u odnosu na prethodni tjedan',
  kpiCart: 'Dodano u košaricu',
  kpiCheckout: 'Stiglo do plaćanja',
  kpiPerson: 'Zatražena osoba',
  kpiCost: 'Iskorišteni krediti',
  ofConversations: '{percent} razgovora',
  checkoutNote: '{percent} razgovora',
  personNote: '{percent} otvorilo je vaš chat',
  costNote: 'Oko {amount} po razgovoru',
  costNoteUnder: 'Manje od {amount} po razgovoru',
  sparkLabel: 'Razgovori dnevno, posljednjih {days} dana',
  sparkDay: '{day}: {count} razgovora',
  filterAll: 'Sve',
  filterCart: 'Dodano u košaricu',
  filterCheckout: 'Plaćanje',
  filterPerson: 'Osoba',
  outcomeCart: 'Dodano u košaricu',
  outcomeCheckout: 'Stiglo do plaćanja',
  outcomePerson: 'Zatražena osoba',
  outcomeAnswered: 'Odgovoreno',
  devicePhone: 'Telefon',
  deviceComputer: 'Računalo',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Započeto na {page}',
  anonymous: 'Kupac je anoniman.',
  askConversation: 'Pitaj Chat o ovom razgovoru',
  showOlderVersions: 'Prikaži starije verzije',
  showOlder: 'Prikaži starije razgovore',
  filterCountsWeek: 'Broj: zadnjih 7 dana',
  readAlreadyHere:
    'Ova je trgovina već ovdje pa ništa novo nije pokrenuto i nisu potrošeni krediti.',
  readOpenShop: 'Otvori je',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Što možemo naučiti iz ovog razgovora?',
  askWeekMessage: 'Što možemo naučiti iz ovotjednih razgovora?',
  conversationsChipOne: '{name} · razgovor od {when}',
  conversationsChipWeek: '{name} · ovaj tjedan',
  conversationsChipWeekIn: '{name} · {market} · ovaj tjedan',
  removeConversations: 'Ukloni razgovore',
  noConversations: 'Još nema razgovora',
  noConversationsBody: 'Kada kupci upotrijebe asistenta, njihovi će se razgovori prikazati ovdje.',
  pickConversation: 'Odaberite razgovor da biste ga pročitali.',
  eventCheckout: 'Otišao/la na plaćanje',
  eventPerson: 'Otvorio/la vaš chat',
  showMore: 'Prikaži više',
  urlInvalid: 'Upotrijebite https adresu svoje trgovine, primjerice https://www.yourstore.com.',
  errorNoCompany: 'Storefront treba radni prostor tvrtke. Zamolite administratora da vas doda.',
  errorPublishPermission:
    'To mogu učiniti samo osobe koje smiju objavljivati za vašu tvrtku. Obratite se administratoru.',
  actionFailed: 'To nije uspjelo. Pokušajte ponovno.',
  copyFailed: 'Kopiranje nije uspjelo. Odaberite redak i kopirajte ga sami.',
  reasonCancelled: 'Zaustavili ste pokretanje.',
  reasonFailed: 'Čitanje vaše trgovine nije završilo pa asistent nije spreman.',
  reasonFailedLive:
    'Čitanje vaše trgovine nije završilo. Objavljeni asistent nastavlja raditi kao i prije.',
  reasonFailedKept:
    'Čitanje vaše trgovine nije završilo pa se ništa nije promijenilo: verzija {version} ostaje ista.',
  statusReadStopped: 'Spremno · zadnje čitanje zaustavljeno',
  reasonStepMarked: 'Korak na kojem je zaustavljeno označen je u nastavku.',
  reasonReadAgain: 'Novo čitanje počinje onime što je ovo čitanje naučilo.',
  reasonNotAStore:
    'Ova adresa ne izgleda kao web-trgovina: nije bilo proizvoda za pretraživanje. Provjerite adresu ili pokušajte s glavnom domenom trgovine.',
  reasonTooSlow:
    'Pretraživanje vaše trgovine odgovara presporo za asistenta za kupnju: trajalo je više od 12 sekundi. Brže pretraživanje trgovine ili ponovno čitanje kasnije moglo bi pomoći.',
  reasonOurSide:
    'Čitanje je zaustavljeno zbog problema na našoj strani, a ne u vašoj trgovini, pa asistent još nije spreman.',
  reasonOurSideLive:
    'Čitanje je zaustavljeno zbog problema na našoj strani, a ne u vašoj trgovini. Objavljeni asistent nastavlja raditi kao i prije.',
  reasonOurSideKept:
    'Čitanje je zaustavljeno zbog problema na našoj strani, a ne u vašoj trgovini, pa se ništa nije promijenilo: verzija {version} ostaje ista.',
  reasonTryLater: 'Pokušajte ponovno pročitati trgovinu malo kasnije.',
  errorNoCredits:
    'Trenutačno nema kredita za ovo. Vlasnik tvrtke može provjeriti plan i ograničenja u odjeljku Usage.',
  errorReadUnavailable:
    'Trgovinu trenutačno nije moguće pročitati. Pokušajte ponovno za nekoliko minuta.',
  reasonTimeout:
    'Čitanje je dosegnulo vremensko ograničenje prije završetka. Novo čitanje nastavlja od onoga što je već provjereno.',
  retrying: 'Ponovno pokretanje…',
  readTitleChecks: 'Ponovno provjeri spremljeno čitanje',
  checksScopeLabel: 'Provjerava se',
  readScopeChecks:
    'Pretraživanja kupaca, proizvodi, pregledavanje i košarica, na onome što je zadnje čitanje spremilo. Trgovina se ne čita ponovno.',
  readScopeChecksProducts:
    'Pretraživanje proizvoda, pregledavanje, fotografije, boje i veličine, na onome što je zadnje čitanje spremilo. Trgovina se ne čita ponovno.',
  readScopeChecksCart:
    'Dodavanje, ponovno čitanje, uklanjanje i pražnjenje košarice te čitanje proizvoda, na onome što je zadnje čitanje spremilo. Trgovina se ne čita ponovno.',
  readScopeChecksChat:
    'Predaja vašem postojećem chatu i čitanje proizvoda, na onome što je zadnje čitanje spremilo. Trgovina se ne čita ponovno.',
  readScopeChecksMarket:
    'Proizvodi, cijene i košarica tržišta, na onome što je zadnje čitanje spremilo. Trgovina se ne čita ponovno.',
  checksCost: 'Samo nove provjere koriste kredite tvrtke',
  checksAfter: 'Sprema novu skicu ako provjere prođu. Ništa ne stiže do kupaca dok ne objavite.',
  checksAfterLive:
    'Sprema novu skicu ako provjere prođu. Objavljeni asistent nastavlja raditi, a kupci će promjenu vidjeti tek nakon objave.',
  checksStarted:
    'Provjere su započele. Kada je nova skica spremna, zatražite od Chata da je provjeri.',
  checkingSavedWork: 'Ponovna provjera spremljenog rada',
  checkAgain: 'Provjeri ponovno',
  checkingAgain: 'Ponovna provjera…',
  reasonCheckAgain:
    '„Provjeri ponovno” još jednom testira spremljeni rad, bez ponovnog čitanja trgovine. Samo nove provjere koriste kredite tvrtke.',
  fixWithChat: 'Popravi s Chatom',
  fixReadPrompt:
    'Čitanje trgovine {name} ({domain}) je zaustavljeno. Pogledaj što se dogodilo i pomozi mi da proradi: reci mi što pronalaziš, predloži što mogu reći čitanju, a zatim ga ponovno provjeri ili ga ponovi s tim savjetom, čitajući što manji dio trgovine.',
  fixReadPromptStep:
    'Čitanje trgovine {name} ({domain}) je zaustavljeno na koraku „{step}”. Pogledaj što se dogodilo i pomozi mi da proradi: reci mi što pronalaziš, predloži što mogu reći čitanju, a zatim ga ponovno provjeri ili ga ponovi s tim savjetom, čitajući što manji dio trgovine.',
  stepStateDone: 'završeno',
  stepStateRunning: 'u tijeku',
  stepStateWaiting: 'nije započeto',
  stepStateFailed: 'zaustavljeno',
  newVersionTitle: 'Verzija {version} je spremna',
  newVersionBody: 'Objavite je da biste zamijenili verziju {live}.',
  newVersionBodyLive: 'Objavite je da biste zamijenili verziju {live} na {domain}.',
  previewSize: 'Veličina pregleda',
  designWithChat: 'Otvori dizajn',
  designReviewBody: 'Pregledajte trenutačni dizajn, a zatim recite Chatu što želite prilagoditi.',
  designWhileTitle: 'Dizajnirajte ga dok čitamo vašu trgovinu',
  designWhileBody:
    'Chat sada može s vama oblikovati izgled, riječi i logotip asistenta. Dizajn će biti spreman kada čitanje završi.',
  designPreviewNote: 'Pregled s primjerima proizvoda dok se vaša trgovina ne pročita.',
  proposedDesign: 'Predloženi dizajn',
  proposedDesignNote:
    'Widget s ovom promjenom, prikazan s primjerima proizvoda. Odobrenjem se sprema kao nova skica; objavljivanje ostaje na stranici Storefront.',
  lineSources: 'Sa stranica trgovine:',
  readAgainConfirm:
    'Ponovno pročitati {name}? To traje oko 10 do 30 minuta i koristi kredite tvrtke.',
  readAgainLive: 'Objavljeni asistent nastavlja raditi dok ne objavite novu verziju.',
  readAgainNow: 'Pročitaj ponovno',
  switchStore: 'Promijeni trgovinu ili dodaj novu',
  storesLabel: 'Vaše trgovine',
  addStore: 'Dodaj još jednu trgovinu',
  backToStores: 'Natrag na vaše trgovine',
  storeLive: 'Objavljeno',
  storeReady: 'Spremno',
  storeReading: 'Čitanje',
  storeStopped: 'Zaustavljeno',
  actionDesignTitle: 'Promijeni dizajn asistenta za kupnju',
  actionKnowledgeTitle: 'Promijeni što asistent za kupnju zna',
  readTitleProducts: 'Ponovno pročitaj proizvode',
  readTitleCart: 'Ponovno provjeri košaricu',
  readTitleTheirChat: 'Ponovno provjeri put do vlastitog chata',
  readTitleAll: 'Ponovno pročitaj cijelu trgovinu',
  readTitleStore: 'Pročitaj novu trgovinu',
  readTitleMarket: 'Dodaj tržište',
  readShopLabel: 'Trgovina',
  readScopeLabel: 'Čita se',
  readTimeLabel: 'Trajanje',
  readCostLabel: 'Trošak',
  readAfterLabel: 'Nakon toga',
  readScopeProducts: 'Pretraga i sortiranje proizvoda, fotografije proizvoda, boje i veličine',
  readScopeCart: 'Dodavanje u košaricu i uklanjanje iz nje',
  readScopeTheirChat: 'Kako kupci dolaze do vašeg vlastitog chata',
  readScopeAll: 'Proizvodi, košarica, vaš vlastiti chat i vaša tržišta',
  readScopeStore:
    'Proizvodi, cijene i veličine, košarica, vaš vlastiti chat, vaš izgled i vaša tržišta',
  readScopeMarket: '{market}: njegovi proizvodi, cijene i košarica',
  readTime: 'Obično {min}–{max} minuta',
  readCost: 'Koristi kredite tvrtke',
  readCostLast: 'Koristi kredite tvrtke · zadnje čitanje ove trgovine koristilo je {credits}',
  readCostLastPartial:
    'Koristi kredite tvrtke · zadnje čitanje ove trgovine, zaustavljeno ranije, koristilo je najmanje {credits}',
  readAfter: 'Sprema novu skicu. Ništa ne stiže do kupaca dok ne objavite.',
  readAfterLive:
    'Sprema novu skicu. Objavljeni asistent nastavlja raditi, a kupci će promjenu vidjeti tek nakon objave.',
  readAfterNew: 'Sprema prvu skicu za isprobavanje. Ništa se ne objavljuje.',
  readCartNote:
    'Za provjeru košarice dodaje testni artikl i ponovno ga uklanja. Nikad ne ide na plaćanje.',
  readFocusLabel: 'Što je Chat zatražio da čitanje popravi',
  readFooter:
    'Ništa ne počinje dok ne odobrite. Čitanje možete zaustaviti na kartici Postavljanje.',
  readStartedAt:
    'Započeto {time} · obično {min}–{max} minuta. Kada je nova skica spremna, zatražite od Chata da je provjeri.',
  readFocusAll: 'Prikaži sve',
  readFocusLess: 'Prikaži manje',
  readStart: 'Započni čitanje',
  addToSite: 'Dodaj na svoju stranicu',
  readTab: 'Zahtjev',
  readStarted:
    'Čitanje je započelo. Kada je nova skica spremna, zatražite od Chata da je provjeri.',
  readFollow: 'Pratite ga na kartici Postavljanje',
  proposedDesignLive:
    'Stvarni pregled vaše trgovine u ovom dizajnu. Pitajte ga nešto: ništa se ne dodaje u stvarnu košaricu. Odobrenjem se sprema kao nova skica; objavljivanje ostaje na stranici Storefront.',
  designChanges: 'Što se mijenja',
  designReset: 'natrag na zadano',
  designGroupColors: 'Boje',
  designGroupType: 'Fontovi',
  designGroupShape: 'Oblik',
  designGroupLauncher: 'Gumb na stranici',
  designGroupPanel: 'Zaglavlje',
  designGroupProducts: 'Proizvodi',
  designGroupCart: 'Košarica trgovine',
  designGroupCopy: 'Tekstovi',
  designGroupLogo: 'Logotip',
  designGroupCss: 'Prilagođeni CSS',
  designColorBrand: 'Robna marka',
  designColorActions: 'Dodaj i pošalji',
  designColorPanel: 'Ploča',
  designColorSurface: 'Površine',
  designColorInk: 'Tekst',
  designColorMuted: 'Sekundarni tekst',
  designColorLine: 'Linije',
  designColorBubble: 'Poruke kupca',
  designColorBubbleInk: 'Tekst kupca',
  designActionsBrand: 'Boja robne marke',
  designActionsInk: 'Boja teksta',
  designWordsName: 'Naziv',
  designWordsGreeting: 'Pozdrav',
  designWordsStarters: 'Početna pitanja',
  designWordsLauncher: 'Oznaka gumba',
  designWordsPlaceholder: 'Zamjenski tekst',
  designWordsAi: 'AI oznaka',
  designWordsPerson: 'Veza s osobom',
  noteNew: 'Novo',
  noteChanged: 'Promijenjeno',
  noteRemoved: 'Uklonjeno',
  noteSourceKnowledge: 'Stranica znanja: {title}',
  noteSourceFile: 'Datoteka: {name}',
  noteSourceChat: 'Iz chata',
  knowledgeApprovalNote:
    'Kupci mogu pročitati sve u ovim bilješkama. Odobrenjem se sprema nova skica: postavite mu pitanja na kartici Widget, a zatim ga objavite na kartici Instalacija.',
  profileTitle: 'O trgovini',
  profileChecked: 'Provjereno {date}',
  profileSources: 'Napisano na temelju',
  profileSample: '{count} proizvoda uzorkovano za razinu cijena',
  profileSampleOne: '{count} proizvod uzorkovan za razinu cijena',
  profileAssistant: 'Što asistent može ovdje',
  profileTeam: 'Napisao vaš tim',
  profileEmpty:
    'Još nije napisano. Zamolite Chat da pokrene prolaz znanja: piše ovo iz vaših stranica i onoga što je vidio na vašoj web-stranici, a možete dodati i vlastite retke.',
  profileWithChat: 'Dodaj ili promijeni s Chatom',
  profilePrompt:
    'Pomozi mi poboljšati ono što shopping asistent za {name} ({domain}) zna o našoj trgovini u cjelini: što prodajemo, po čemu smo poznati, za koga smo i što kupci ovdje mogu učiniti. Prvo pročitaj profil „O trgovini“ i reci mi što nedostaje ili je pogrešno. Sve što ti kažem o trgovini, a što nijedna stranica ne navodi, ulazi kao redak našeg tima.',
  profilePartNavigation: 'Odjeli',
  profilePartWelcome: 'Pozdrav',
  profilePartMarkets: 'Tržišta',
  profilePartFunctions: 'Što asistent može',
  profilePartProducts: 'Proizvodi',
  profilePartPages: 'Stranice usluga',
  profileCleared: 'Briše napisani profil',
  knowledgeTitle: 'Što zna',
  knowledgeBody:
    'Osim vaših proizvoda, cijena i zaliha, koje uvijek čita izravno iz vaše trgovine.',
  knowledgeEmpty:
    'Zasad samo vaš katalog. Dodajte informacije o dostavi, povratima ili plaćanju uz Chat, sa svojih stranica ili iz Znanja.',
  knowledgeWithChat: 'Dodaj s Chatom',
  knowledgeChangeWithChat: 'Promijeni s Chatom',
  knowledgePrompt:
    'Pomogni mi odabrati što asistent za kupnju trgovine {name} ({domain}) govori kupcima osim o našim proizvodima, primjerice o dostavi, povratima i plaćanju. Pronađi to na našim stranicama ili u Znanju i predloži bilješke koje mogu odobriti.',
  pagesTitle: 'Stranice koje čita',
  pagesBody:
    'Stranice iz kojih dolaze bilješke. Poneva ih provjerava svaki dan, a Chat javlja kad se neka promijeni.',
  pagesEmpty:
    'Još nema stranica. Ovdje se prikazuju stranice koje pronađe čitanje trgovine i one koje navedete u Chatu.',
  pageFound: 'Ponevin nalaz',
  pageLinkedFrom: 'povezano s {page}',
  pageAddedByYou: 'dodano ručno',
  pageFromNote: 'navedena u bilješci',
  pageNoteOne: '1 bilješka',
  pageNotesMany: '{count} bilješki',
  pageStatusOk: 'Ažurno',
  pageStatusChanged: 'Promijenjeno',
  pageStatusUnreadable: 'Nije moguće pročitati',
  pageStatusUnchecked: 'Još nije provjereno',
  pagesChangeWithChat: 'Promijeni s Chatom',
  pagesUpdateWithChat: 'Ažuriraj bilješke s Chatom',
  pagesPrompt:
    'Pomozi mi promijeniti koje stranice asistent za kupovinu za {name} ({domain}) čita za svoje bilješke. Pokaži mi stranice koje sada koristi i pitaj me koje dodati ili ukloniti.',
  pagesUpdatePrompt:
    'Neke stranice za {name} ({domain}) promijenile su se ili se više ne mogu pročitati otkako su bilješke napravljene. Pokaži mi koje su i ponovno izradi bilješke iz njih kao nacrt koji mogu pregledati.',
  viewCompare: 'Usporedba',
  lookDeviceLabel: 'Usporedi na',
  lookShop: 'Vaša trgovina',
  lookShopCaption: 'Početna stranica, pročitana {when}',
  lookWidget: 'Asistent',
  lookWidgetCaption: 'Verzija {version}',
  lookLoading: 'Postavljanje asistenta pokraj vaše trgovine…',
  lookFailed: 'Usporedbu trenutačno nije bilo moguće napraviti.',
  lookNoShopPicture: 'Čitanje nije spremilo sliku vaše trgovine pri ovoj širini.',
  lookNoneTitle: 'Vlastiti izgled vaše trgovine nije spremljen',
  lookNoneBody:
    'Ovo čitanje to nije zabilježilo. Ponovno pročitajte trgovinu iz Postavljanja i njezin će se logotip, boje i stranice spremiti za usporedbu.',
  lookColoursShop: 'Boje koje koristi vaša trgovina',
  lookColoursAssistant: 'Boje asistenta',
  lookRoleHeader: 'Zaglavlje',
  lookRoleBrand: 'Robna marka',
  lookSame: 'Ista kao boja {colour} vaše trgovine ({source})',
  lookOff: 'Blizu boje {colour} vaše trgovine ({source}), ali nije ista, razlika {delta}',
  lookOwn: 'Vlastita boja: vaša trgovina ne koristi tu boju',
  lookNeutral: 'Vaša trgovina nije učitana za ovo: prikazano je samo ono što je čitanje spremilo.',
  lookFromLogo: 'logotip',
  lookFromTheme: 'boja teme',
  lookFromTile: 'boja pločice',
  lookFromMask: 'prikvačena ikona',
  lookFromManifest: 'manifest aplikacije',
  lookFromHeader: 'zaglavlje',
  lookFromButton: 'gumbi',
  lookFromVariable: 'boja stila',
  lookFix: 'Popravi s Chatom',
  lookFixPrompt:
    'Boja asistenta za {role} je {colour}, blizu boje {exact} vaše trgovine ({source}), ali nije ista. Upotrijebite točnu boju vaše trgovine, {exact}.',
  lookSwatch: 'Boja {colour}',
  errorDesignChecks: 'Ova verzija nije prošla provjere dizajna.',
  designSamplesBlocked:
    'Rasporedi kartica ovdje se ne mogu provjeriti: vaše web-mjesto blokira automatizirane posjete. Boje, fontovi i logotip i dalje rade.',
  designCheckLabel: 'Oznaka umjetne inteligencije preblijeda je za čitanje.',
  designCheckReach: 'Gumb je nedostupan.',
  designCheckSize: 'Dio teksta je premalen.',
  designCheckOverflow: 'Ploča se pomiče u stranu.',
  designCheckRender: 'Provjere se nisu mogle pokrenuti. Pokušajte ponovno za trenutak.',
  fixDesignPrompt:
    'Pokušao sam objaviti najnoviji nacrt za {name} ({domain}), ali nije prošao provjere dizajna: {facts}. Popravite dizajn u ovom razgovoru i provjerite ga na mobitelu i računalu. Nemojte ga objaviti.',
};

const cs: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Nákupní asistent',
  designUnconfirmed:
    'Chat skončil dříve, než potvrdil uložení návrhu. Plátno asistenta zobrazuje jeho nejnovější verzi.',
  errorNewerVersion:
    'Od vašeho otevření byla uložena novější verze. Nejprve si ji prohlédněte a potom ji publikujte.',
  canvasSelectHint: 'Kliknutím na část asistenta ji změníte v chatu · Esc ukončí výběr',
  canvasDraft: 'Verze {version} · zatím nepublikováno',
  canvasDraftOverLive: 'Verze {version} · publikována je verze {live}',
  canvasLive: 'Verze {version} · publikováno',
  canvasNoVersion: 'První verze vznikne po dokončení načtení obchodu',
  canvasPublish: 'Publikovat na stránce Storefront',
  canvasFrameTitle: 'Nákupní asistent {name}',
  viewLabel: 'Zobrazení',
  photosGood: '{count} produktových fotografií: celé a ostré',
  photosGoodOne: 'Produktová fotografie je celá a ostrá',
  canvasMissingTitle: 'Tento nákupní asistent není v tomto pracovním prostoru',
  canvasMissingBody: 'Patří jinému pracovnímu prostoru nebo byl odstraněn.',
  canvasMissingLink: 'Zobrazit asistenty tohoto pracovního prostoru',
  photosLabel: 'Produktové fotografie',
  photosSmall: '{count} malých ve svých dlaždicích',
  photosCut: '{count} oříznutých',
  photosSoft: '{count} zvětšených, a proto neostrých',
  photosMissing: '{count} bez fotografie',
  photosFix: 'Opravit pomocí Chatu',
  photosPrompt:
    'Uprav produktové fotografie na kartách tak, aby vypadaly správně: každá celá, dostatečně velká ve své dlaždici a ostrá.',
  photosFacts:
    '{total} fotografií: {small} malých ve dlaždicích, {cut} oříznutých, {soft} zvětšených, {missing} bez fotografie. Rozměry: {sizes}',
  viewDesign: 'Návrh',
  viewVersions: 'Verze',
  versionsLoading: 'Načítání verzí…',
  versionsFailed: 'Verze se nepodařilo načíst.',
  versionTitle: 'Verze {version}',
  versionLive: 'Publikováno',
  versionChecked: 'Zkontrolováno',
  versionToday: 'Dnes',
  versionYesterday: 'Včera',
  versionEarlier: 'Dříve',
  versionByPerson: 'Požádal(a) {name}',
  versionByChat: 'Požádáno v chatu',
  versionFromReading: 'Z načtení {domain}',
  versionPublishedBy: 'publikoval(a) {name}, {at}',
  versionPublish: 'Publikovat',
  versionRestore: 'Obnovit…',
  versionRestoreTitle: 'Obnovit verzi {version}?',
  versionRestoreBody:
    'Stane se z ní verze {next}, nový koncept. Váš web se nezmění, dokud ji nepublikujete.',
  versionRestoreConfirm: 'Obnovit',
  versionRestoreCancel: 'Zrušit',
  versionRestored: 'Obnoveno jako verze {version}',
  versionRestoreFailed: 'Verzi se nepodařilo obnovit.',
  compareTitle: 'Porovnat verzi {to} s verzí {from}',
  compareTitleLive: 'Porovnat verzi {to} s publikovanou verzí {from}',
  compareHint: 'Přetažením porovnáte',
  compareLabel: 'Porovnat verzi {from} a verzi {to}',
  compareTagLive: 'v{version} · Publikováno',
  compareTag: 'v{version}',
  changesTitle: 'Co se změnilo z verze {from} na {to}',
  changesNone: 'Návrhy jsou stejné. Změnily se jen poznámky nebo data obchodu.',
  changeAdded: 'Přidáno',
  changeRemoved: 'Odstraněno',
  changeChanged: 'Změněno',
  changeDrawn: 'kreslené logo',
  changeCss: 'vlastní CSS',
  changeYes: 'ano',
  changeNo: 'ne',
  'field.colors.brand': 'Barva značky',
  'field.colors.actions': 'Barva tlačítek',
  'field.colors.panel': 'Barva panelu',
  'field.colors.surface': 'Barva plochy',
  'field.colors.ink': 'Barva textu',
  'field.colors.muted': 'Barva vedlejšího textu',
  'field.colors.line': 'Barva čáry',
  'field.colors.bubble': 'Barva zprávy nakupujícího',
  'field.colors.bubbleInk': 'Text zprávy nakupujícího',
  'field.colors.header': 'Barva záhlaví',
  'field.colors.headerInk': 'Barva textu záhlaví',
  'field.colors.tints': 'Jemné odstíny',
  'field.copy.assistantName': 'Název',
  'field.copy.greeting': 'Pozdrav',
  'field.copy.placeholder': 'Pole pro otázku',
  'field.copy.starters': 'Úvodní otázky',
  'field.copy.launcherLabel': 'Text spouštěče',
  'field.copy.aiLabel': 'Štítek AI',
  'field.copy.personLabel': 'Odkaz na člověka',
  'field.type.body': 'Písmo textu',
  'field.type.display': 'Písmo nadpisů',
  'field.type.fontCss': 'Zdroj písma',
  'field.type.scale': 'Velikost textu',
  'field.shape.radius': 'Rohy',
  'field.shape.shadow': 'Stín',
  'field.shape.density': 'Rozestupy',
  'field.launcher.style': 'Spouštěč na počítačích',
  'field.launcher.phoneStyle': 'Spouštěč na telefonech',
  'field.launcher.side': 'Strana spouštěče',
  'field.launcher.icon': 'Ikona spouštěče',
  'field.panel.header': 'Velikost záhlaví',
  'field.panel.opening': 'První obrazovka',
  'field.products.layout': 'Produkty',
  'field.products.imageFit': 'Produktové fotografie',
  'field.products.imageRatio': 'Tvar dlaždice fotografie',
  'field.products.imagePadding': 'Prostor kolem fotografií',
  'field.products.imageBackground': 'Barva dlaždice fotografie',
  'field.products.picker': 'Volba velikosti',
  'field.products.showStock': 'Zobrazení dostupnosti',
  'field.cart.landing': 'Animace košíku',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Tvar loga',
  'field.logo.plate': 'Podklad loga',
  'field.css': 'Vlastní CSS',
  'value.ink': 'inkoustová',
  'value.brand': 'barva značky',
  'value.small': 'malá',
  'value.medium': 'střední',
  'value.large': 'velká',
  'value.square': 'čtvercová',
  'value.soft': 'měkká',
  'value.round': 'kulatá',
  'value.flat': 'plochá',
  'value.deep': 'hluboká',
  'value.compact': 'úsporné',
  'value.comfortable': 'pohodlné',
  'value.pill': 'pilulka se slovy',
  'value.circle': 'kruh',
  'value.right': 'vpravo',
  'value.left': 'vlevo',
  'value.logo': 'logo',
  'value.chat': 'bublina chatu',
  'value.sparkle': 'jiskra',
  'value.bag': 'taška',
  'value.full': 'celé',
  'value.middle': 'uprostřed',
  'value.bottom': 'dole',
  'value.rows': 'řádky',
  'value.grid': 'mřížka',
  'value.carousel': 'řádek pro posouvání',
  'value.contain': 'celá fotografie',
  'value.cover': 'vyplněná dlaždice',
  'value.portrait': 'na výšku',
  'value.landscape': 'na šířku',
  'value.none': 'žádné',
  'value.pop': 'stoupající +1',
  'value.ring': 'vlnka',
  'value.blink': 'bliknutí',
  'value.chips': 'tlačítka v řádku',
  'value.menu': 'seznam',
  'value.rounded': 'zaoblené',
  designCardTitle: 'Nákupní asistent {name}',
  designCardSaved: 'Verze {version} uložena · zatím nepublikováno',
  designCardSavedFirst: 'Návrh uložen · ponese ho první verze',
  designSaving: 'Ukládání návrhu…',
  selectedAssistantPart: 'Vybráno: část asistenta',
  loading: 'Načítání Storefront…',
  disabledTitle: 'Storefront není v tomto pracovním prostoru zapnutý.',
  disabledBody:
    'Je v předběžném přístupu a zavádí se postupně. Zeptejte se své kontaktní osoby v Ponevě, kdy bude dostupný i pro vás.',
  loadFailed: 'Storefront se nepodařilo načíst.',
  startTitle: 'Nákupní asistent pro váš obchod',
  startBody:
    'Poneva načte váš obchod tak, jak ho používá nakupující: vyhledávání, produkty, košík a váš stávající chat. Trvá to přibližně 10 až 30 minut, využije kredity vaší společnosti a vy můžete dál pracovat.',
  urlLabel: 'Adresa vašeho obchodu',
  urlPlaceholder: 'https://www.vasobchod.cz',
  startButton: 'Načíst můj obchod',
  starting: 'Spouštění…',
  startFailed: 'Tento obchod se nepodařilo načíst. Zkontrolujte adresu a zkuste to znovu.',
  tabConversations: 'Konverzace',
  tabOverview: 'Přehled',
  allAssistants: 'Všichni asistenti',
  allAssistantsNote: '{assistants} asistentů · {sites} webů',
  allAssistantsMenuNote: '{count} konverzací tento týden',
  marketLabel: 'Trh',
  allMarkets: 'Všechny trhy',
  allMarketsNote: '{count} webů',
  scopeIn: '{assistant} v {market}',
  askAbout: 'Zeptat se Chatu na {scope}',
  overviewAssistantsTitle: 'Asistenti a trhy',
  overviewMarketsTitle: 'Trhy',
  overviewAssistantsOnly: 'Asistenti',
  overviewIdle: '{count} asistentů zatím bez konverzací',
  allAssistantsCount: '{assistants} asistentů',
  overviewFailed: 'Přehled se nepodařilo načíst.',
  colConversations: 'Konverzace',
  colCart: 'Přidáno do košíku',
  colCheckout: 'Pokladna',
  colPerson: 'Člověk',
  colCost: 'Kredity',
  rowMarkets: '{count} trhů',
  rowVersion: 'verze {version}',
  rowAria:
    '{name}, {sub}: {conversations} konverzací, {cart} přidání do košíku, {checkout} návštěv pokladny, {person} žádostí o člověka, kredity {cost}',
  versionPublishedAt: 'publikováno {at}',
  designColorHeader: 'Záhlaví',
  designColorHeaderInk: 'Text záhlaví',
  designGroupTranslations: 'Slova v jiných jazycích',
  topicsTitle: 'Na co se nakupující ptají',
  topicsNote: 'Témata, která Chat našel v prvních otázkách',
  startPagesTitle: 'Kde konverzace začínají',
  startPagesOne: 'Každá konverzace začala na jednom typu stránky: {page}.',
  'startPage.product': 'Stránky produktů',
  'startPage.search': 'Výsledky vyhledávání',
  'startPage.start': 'Úvodní stránka',
  'startPage.cart': 'Košík',
  'startPage.other': 'Jiné stránky',
  addMarketInChat: 'Přidat trh v chatu',
  marketsTitle: 'Trhy',
  marketsBody:
    'Jeden asistent a jeden návrh. Každý trh má vlastní jazyk, měnu, produkty, košík a chat s člověkem.',
  marketChecked: 'zkontrolováno {when}',
  'marketStatus.ready': 'Připraveno',
  'marketStatus.found': 'Nepřidáno',
  'marketStatus.checking': 'Kontroluje se',
  'marketStatus.failed': 'Vyžaduje opravu',
  addMarketPrompt:
    'Přidej k nákupnímu asistentovi pro {name} ({domain}) další trh: najdi další země, jazyky nebo weby obchodu a zeptej se mě, který mám přidat.',
  tabWidget: 'Widget',
  tabInstall: 'Instalace',
  tabSetup: 'Nastavení',
  statusReading: 'Načítání vašeho obchodu',
  readingProducts: 'Opětovné načítání produktů',
  readingCart: 'Opětovná kontrola košíku',
  readingChat: 'Kontrola cesty k vašemu chatu',
  readingMarket: 'Přidávání trhu {market}',
  readingMarketAny: 'Přidávání trhu',
  statusReady: 'Připraveno k instalaci',
  statusFailed: 'Načítání zastaveno',
  statusLive: 'Živé · verze {version} na {domain}',
  statusPublished: 'Publikováno · verze {version} · zatím není na vašem webu',
  statusPublishedUnverified: 'Publikováno · verze {version} · váš web nelze ověřit',
  stepOpen: 'Otevírání webu',
  stepSearch: 'Vyhledávání',
  stepProducts: 'Produkty',
  stepCart: 'Košík',
  stepChat: 'Váš stávající chat',
  stepLook: 'Váš vzhled a logo',
  stepChecks: 'Kontrola funkčnosti',
  stepReady: 'Připraveno',
  stepDetailOpen: 'Otevírání {domain}',
  stepDetailSearch: 'Hledání produktů obchodu',
  stepDetailProducts: 'Načítání produktů, cen, fotografií, barev a velikostí',
  stepDetailCart: 'Zkoušení košíku: přidání, načtení, odebrání a vyprázdnění',
  stepDetailChat: 'Hledání vašeho stávajícího chatu',
  stepDetailLook: 'Načítání loga a barev obchodu',
  stepDetailChecks: 'Zkoušení vyhledávání nakupujících, produktů, procházení a košíku',
  stepDetailChecksProducts:
    'Kontrola vyhledávání produktů, procházení, fotografií, barev a velikostí',
  stepDetailChecksCart:
    'Kontrola přidání, načtení, odebrání a vyprázdnění košíku a načtení produktů',
  stepDetailChecksChat: 'Kontrola předání do vašeho stávajícího chatu a načtení produktů',
  stepDetailChecksMarket: 'Kontrola produktů, cen a košíku tohoto trhu',
  nowRunning: 'Nyní: {step}',
  nowProgress: 'Krok {n} z {total} · zatím {minutes} min',
  nowReady: 'Váš obchod je připraven pro asistenta',
  nowReadyBody: 'Vyhledávání, produkty a košík fungují.',
  nowLastRead: 'Naposledy načteno {when}',
  nowUsually: 'obvykle {min}–{max}',
  stepsRead: 'Načteno {when} · Zobrazit kroky',
  nowFailed: 'Načítání zastaveno',
  factAnswers: 'Nakupujícím odpovídá pomocí',
  costCounting: 'Počítání…',
  factPaid: 'Placeno z',
  paidCompany: 'Kreditů společnosti',
  creditsAmount: '{amount} kreditů',
  creditsOne: '{amount} kredit',
  creditsUnder: 'méně než {amount} kreditů',
  stopRun: 'Zastavit běh',
  stopping: 'Zastavování…',
  closeNote: 'Tuto stránku můžete opustit. Běh bude pokračovat.',
  readyNote: 'Další krok: vyzkoušejte widget a potom ho nainstalujte.',
  buildWidget: 'Vyzkoušet widget',
  readAgain: 'Načíst obchod znovu',
  foundTitle: 'Co našel',
  foundProducts: 'Produkty a ceny načteny',
  foundSearch: 'Vyhledávání funguje',
  foundCart: 'Přidání do košíku funguje',
  foundChat: 'Váš chat {vendor}',
  foundNoChat: 'Nemáte vlastní chat',
  foundLook: 'Vaše barvy a logo',
  widgetTitle: 'Váš widget',
  widgetBody:
    'Váš asistent ve vašich barvách. Vyzkoušejte ho zde: odpovídá pomocí skutečných produktů. Do skutečného košíku se nic nepřidá.',
  widgetLoading: 'Načítání náhledu…',
  widgetFailed: 'Náhled se nepodařilo načíst.',
  widgetNotReady: 'Widget bude připraven po dokončení kroků na kartě Nastavení.',
  installLineTitle: 'Přidejte tento řádek na svůj web',
  installLineBody:
    'Funguje na jakékoli platformě: do hlavičky stránek, na Shopify v theme.liquid těsně před </head>. Dokud ho nepublikujete, nic se nezobrazí.',
  installLineBodyLive:
    'Funguje na jakékoli platformě: do hlavičky stránek, na Shopify v theme.liquid těsně před </head>. Nakupující pak uvidí verzi {version}.',
  installPolicyAsk: 'Nastavuje váš web zásady Content Security Policy?',
  installPolicy:
    'Pokud váš web nastavuje Content Security Policy, povolte také {origin} v {directives}, jinak se asistent nespustí.',
  installPolicyRefuses:
    'Content Security Policy vašeho webu asistenta odmítá: povolte {origin} v {directives}, jinak se nic nespustí.',
  installPolicyNonce:
    'Vaše zásady spouštějí jen důvěryhodné skripty, proto přidejte řádek s nonce vašich stránek.',
  installPolicyEval: "Asistent také potřebuje 'unsafe-eval' v script-src.",
  copy: 'Kopírovat',
  copied: 'Zkopírováno',
  domainsTitle: 'Weby, na kterých odpovídá',
  domainsBody: 'Na žádném jiném webu nic nezobrazuje, ani na webu, který tento řádek zkopíruje.',
  lineFound: 'Řádek nalezen',
  lineNotSeen: 'Na stránce zatím není',
  lineUnverified:
    'Nelze ověřit: web blokuje automatické návštěvy. Asistent přesto funguje pro zákazníky.',
  lineChecked: 'zkontrolováno {when}',
  lineCheckAgain: 'Zkontrolovat znovu',
  lineChecking: 'Kontrola stránky…',
  installCheckBusy: 'Kontrola je teď zaneprázdněná. Zkuste to znovu za chvíli.',
  publishTitle: 'Publikovat',
  publishBody: 'Nakupující na {domain} ho uvidí okamžitě. Kdykoli ho můžete znepublikovat.',
  publishButton: 'Publikovat verzi {version}',
  publishConfirm: 'Publikovat verzi {version} nákupního asistenta {name}?',
  publishNow: 'Publikovat',
  notNow: 'Teď ne',
  publishing: 'Publikování…',
  liveTitle: 'Publikováno: verze {version}',
  liveSince: 'Od {since}',
  unpublish: 'Znepublikovat',
  unpublishConfirm:
    'Odebrat verzi {version} z vašeho webu? Nakupující asistenta neuvidí, dokud ho znovu nepublikujete.',
  publishedLine: 'Nyní publikováno: verze {version}, od {since}',
  usageTitle: 'Využití',
  usageBody: 'Využívá kredity vaší společnosti stejně jako zbytek Chatu.',
  usageDiscovery: 'Načítání obchodu',
  usageChats: 'Chaty nakupujících',
  usageChatsValue: '{count} chatů · {cost}',
  last7: 'Posledních 7 dní',
  askWeek: 'Zeptat se Chatu na tento týden',
  kpiConversations: 'Konverzace',
  kpiVsPrevious: '{change} oproti předchozímu týdnu',
  kpiCart: 'Přidáno do košíku',
  kpiCheckout: 'Dosažena pokladna',
  kpiPerson: 'Požádáno o člověka',
  kpiCost: 'Využité kredity',
  ofConversations: '{percent} konverzací',
  checkoutNote: '{percent} konverzací',
  personNote: '{percent} otevřelo váš chat',
  costNote: 'Přibližně {amount} na konverzaci',
  costNoteUnder: 'Méně než {amount} na konverzaci',
  sparkLabel: 'Konverzace za den, posledních {days} dní',
  sparkDay: '{day}: {count} konverzací',
  filterAll: 'Vše',
  filterCart: 'Přidáno do košíku',
  filterCheckout: 'Pokladna',
  filterPerson: 'Člověk',
  outcomeCart: 'Přidáno do košíku',
  outcomeCheckout: 'Dosažena pokladna',
  outcomePerson: 'Požádáno o člověka',
  outcomeAnswered: 'Zodpovězeno',
  devicePhone: 'Telefon',
  deviceComputer: 'Počítač',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Začátek na stránce {page}',
  anonymous: 'Nakupující je anonymní.',
  askConversation: 'Zeptat se Chatu na tuto konverzaci',
  showOlderVersions: 'Zobrazit starší verze',
  showOlder: 'Zobrazit starší konverzace',
  filterCountsWeek: 'Počty: posledních 7 dní',
  readAlreadyHere:
    'Tento obchod už tu je, takže se nic nového nespustilo a žádné kredity se nespotřebovaly.',
  readOpenShop: 'Otevřít obchod',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Co se můžeme naučit z této konverzace?',
  askWeekMessage: 'Co se můžeme naučit z konverzací tohoto týdne?',
  conversationsChipOne: '{name} · konverzace z {when}',
  conversationsChipWeek: '{name} · tento týden',
  conversationsChipWeekIn: '{name} · {market} · tento týden',
  removeConversations: 'Odebrat konverzace',
  noConversations: 'Zatím žádné konverzace',
  noConversationsBody: 'Až nakupující asistenta použijí, jejich konverzace se zobrazí zde.',
  pickConversation: 'Vyberte konverzaci ke čtení.',
  eventCheckout: 'Přešel(a) k pokladně',
  eventPerson: 'Otevřel(a) váš chat',
  showMore: 'Zobrazit více',
  urlInvalid: 'Použijte adresu svého obchodu s https, například https://www.vasobchod.cz.',
  errorNoCompany:
    'Storefront potřebuje firemní pracovní prostor. Požádejte správce, aby vás přidal.',
  errorPublishPermission:
    'Toto mohou provést jen lidé, kteří mohou publikovat za vaši společnost. Požádejte správce.',
  actionFailed: 'To se nepodařilo. Zkuste to znovu.',
  copyFailed: 'Kopírování se nepodařilo. Vyberte řádek a zkopírujte ho sami.',
  reasonCancelled: 'Běh jste zastavili.',
  reasonFailed: 'Načítání vašeho obchodu nebylo dokončeno, takže asistent není připraven.',
  reasonFailedLive:
    'Načítání vašeho obchodu nebylo dokončeno. Publikovaný asistent dál funguje beze změny.',
  reasonFailedKept:
    'Načítání vašeho obchodu nebylo dokončeno, takže se nic nezměnilo: verze {version} zůstává.',
  statusReadStopped: 'Připraveno · poslední načítání zastaveno',
  reasonStepMarked: 'Krok, při kterém se načítání zastavilo, je níže označen.',
  reasonReadAgain: 'Opětovné načtení začne tím, co se toto načtení dozvědělo.',
  reasonNotAStore:
    'Tato adresa nevypadá jako internetový obchod: nebyly nalezeny žádné produkty k vyhledávání. Zkontrolujte adresu nebo zkuste hlavní doménu obchodu.',
  reasonTooSlow:
    'Vyhledávání vašeho obchodu odpovídá pro nákupního asistenta příliš pomalu: trvalo déle než 12 sekund. Může pomoci rychlejší vyhledávání v obchodu nebo pozdější opětovné načtení.',
  reasonOurSide:
    'Načítání se zastavilo kvůli problému na naší straně, ne ve vašem obchodě, takže asistent ještě není připravený.',
  reasonOurSideLive:
    'Načítání se zastavilo kvůli problému na naší straně, ne ve vašem obchodě. Publikovaný asistent dál funguje beze změny.',
  reasonOurSideKept:
    'Načítání se zastavilo kvůli problému na naší straně, ne ve vašem obchodě, takže se nic nezměnilo: verze {version} zůstává.',
  reasonTryLater: 'Zkuste obchod načíst znovu za chvíli.',
  errorNoCredits:
    'Na tuto akci teď nejsou k dispozici žádné kredity. Vlastník společnosti může zkontrolovat plán a limity v části Usage.',
  errorReadUnavailable: 'Obchod teď nelze načíst. Zkuste to znovu za pár minut.',
  reasonTimeout:
    'Načítání dosáhlo časového limitu, než se dokončilo. Opětovné načtení pokračuje od toho, co už bylo ověřeno.',
  retrying: 'Spouštění znovu…',
  readTitleChecks: 'Znovu zkontrolovat uložené načtení',
  checksScopeLabel: 'Kontroluje',
  readScopeChecks:
    'Vyhledávání nakupujících, produkty, procházení a košík, na tom, co uložilo poslední načtení. Obchod se znovu nenačítá.',
  readScopeChecksProducts:
    'Vyhledávání produktů, procházení, fotografie, barvy a velikosti, na tom, co uložilo poslední načtení. Obchod se znovu nenačítá.',
  readScopeChecksCart:
    'Přidání, načtení, odebrání a vyprázdnění košíku a načtení produktů, na tom, co uložilo poslední načtení. Obchod se znovu nenačítá.',
  readScopeChecksChat:
    'Předání do vašeho stávajícího chatu a načtení produktů, na tom, co uložilo poslední načtení. Obchod se znovu nenačítá.',
  readScopeChecksMarket:
    'Produkty, ceny a košík trhu, na tom, co uložilo poslední načtení. Obchod se znovu nenačítá.',
  checksCost: 'Jen nové kontroly využijí kredity společnosti',
  checksAfter:
    'Uloží nový koncept, pokud kontroly projdou. K nakupujícím se nic nedostane, dokud to nepublikujete.',
  checksAfterLive:
    'Uloží nový koncept, pokud kontroly projdou. Publikovaný asistent dál funguje a nakupující změnu uvidí až po publikování.',
  checksStarted:
    'Kontroly byly spuštěny. Až bude nový koncept připraven, požádejte Chat o kontrolu.',
  checkingSavedWork: 'Znovu se kontroluje uložená práce',
  checkAgain: 'Zkontrolovat znovu',
  checkingAgain: 'Kontroluje se znovu…',
  reasonCheckAgain:
    'Zkontrolovat znovu otestuje uloženou práci ještě jednou, aniž by se obchod načítal znovu. Jen nové kontroly využijí kredity společnosti.',
  fixWithChat: 'Opravit pomocí Chatu',
  fixReadPrompt:
    'Načítání obchodu {name} ({domain}) se zastavilo. Podívej se, co se stalo, a pomoz mi to zprovoznit: řekni mi, co zjistíš, navrhni, co mohu načítání sdělit, a pak ho zkontroluj znovu nebo ho s tou radou spusť znovu a načti přitom co nejmenší část obchodu.',
  fixReadPromptStep:
    'Načítání obchodu {name} ({domain}) se zastavilo u kroku „{step}“. Podívej se, co se stalo, a pomoz mi to zprovoznit: řekni mi, co zjistíš, navrhni, co mohu načítání sdělit, a pak ho zkontroluj znovu nebo ho s tou radou spusť znovu a načti přitom co nejmenší část obchodu.',
  stepStateDone: 'hotovo',
  stepStateRunning: 'probíhá',
  stepStateWaiting: 'nespuštěno',
  stepStateFailed: 'zastaveno',
  newVersionTitle: 'Verze {version} je připravena',
  newVersionBody: 'Publikujte ji a nahraďte verzi {live}.',
  newVersionBodyLive: 'Publikujte ji a nahraďte verzi {live} na {domain}.',
  previewSize: 'Velikost náhledu',
  designWithChat: 'Otevřít design',
  designReviewBody: 'Prohlédněte si současný design a řekněte Chatu, co chcete upravit.',
  designWhileTitle: 'Navrhujte ho během načítání obchodu',
  designWhileBody:
    'Chat s vámi může už nyní upravit vzhled, slova i logo asistenta. Návrh bude připraven, až bude připravené načtení.',
  designPreviewNote: 'Náhled s ukázkovými produkty, dokud nebude obchod načten.',
  proposedDesign: 'Navrhovaný návrh',
  proposedDesignNote:
    'Widget s touto změnou, zobrazený s ukázkovými produkty. Schválením ho uložíte jako nový koncept; publikování zůstává na stránce Storefront.',
  lineSources: 'Ze stránek obchodu:',
  readAgainConfirm:
    'Načíst {name} znovu? Trvá to přibližně 10 až 30 minut a využívá kredity společnosti.',
  readAgainLive: 'Publikovaný asistent bude fungovat, dokud nepublikujete novou verzi.',
  readAgainNow: 'Načíst znovu',
  switchStore: 'Přepnout obchod nebo přidat další',
  storesLabel: 'Vaše obchody',
  addStore: 'Přidat další obchod',
  backToStores: 'Zpět k vašim obchodům',
  storeLive: 'Publikováno',
  storeReady: 'Připraveno',
  storeReading: 'Načítání',
  storeStopped: 'Zastaveno',
  actionDesignTitle: 'Změnit návrh nákupního asistenta',
  actionKnowledgeTitle: 'Změnit, co nákupní asistent ví',
  readTitleProducts: 'Načíst produkty znovu',
  readTitleCart: 'Zkontrolovat košík znovu',
  readTitleTheirChat: 'Zkontrolovat cestu k vašemu chatu znovu',
  readTitleAll: 'Načíst celý obchod znovu',
  readTitleStore: 'Načíst nový obchod',
  readTitleMarket: 'Přidat trh',
  readShopLabel: 'Obchod',
  readScopeLabel: 'Načte',
  readTimeLabel: 'Trvá',
  readCostLabel: 'Cena',
  readAfterLabel: 'Poté',
  readScopeProducts: 'Vyhledávání a řazení produktů, produktové fotografie, barvy a velikosti',
  readScopeCart: 'Přidávání do košíku a odebírání z něj',
  readScopeTheirChat: 'Jak se nakupující dostanou k vašemu chatu',
  readScopeAll: 'Produkty, košík, váš chat a vaše trhy',
  readScopeStore: 'Produkty, ceny a velikosti, košík, váš chat, váš vzhled a vaše trhy',
  readScopeMarket: '{market}: jeho produkty, ceny a košík',
  readTime: 'Obvykle {min}–{max} minut',
  readCost: 'Využívá kredity společnosti',
  readCostLast: 'Využívá kredity společnosti · poslední načtení tohoto obchodu využilo {credits}',
  readCostLastPartial:
    'Využívá kredity společnosti · poslední načtení tohoto obchodu, zastavené předčasně, využilo nejméně {credits}',
  readAfter: 'Uloží nový koncept. K nakupujícím se nic nedostane, dokud to nepublikujete.',
  readAfterLive:
    'Uloží nový koncept. Publikovaný asistent dál funguje a nakupující změnu uvidí až po publikování.',
  readAfterNew: 'Uloží první koncept k vyzkoušení. Nic nebude publikováno.',
  readCartNote:
    'Pro kontrolu košíku přidá testovací položku a znovu ji odebere. Nikdy nepřejde k pokladně.',
  readFocusLabel: 'Co Chat požádal načtení opravit',
  readFooter: 'Nic se nespustí, dokud to neschválíte. Načítání můžete zastavit na kartě Nastavení.',
  readStartedAt:
    'Spuštěno {time} · obvykle {min}–{max} minut. Až bude nový koncept připraven, požádejte Chat o kontrolu.',
  readFocusAll: 'Zobrazit vše',
  readFocusLess: 'Zobrazit méně',
  readStart: 'Spustit načítání',
  addToSite: 'Přidat na váš web',
  readTab: 'Požadavek',
  readStarted: 'Načítání bylo spuštěno. Až bude nový koncept připraven, požádejte Chat o kontrolu.',
  readFollow: 'Sledovat na kartě Nastavení',
  proposedDesignLive:
    'Skutečný náhled vašeho obchodu v tomto návrhu. Na něco se zeptejte: do skutečného košíku se nic nepřidá. Schválením ho uložíte jako nový koncept; publikování zůstává na stránce Storefront.',
  designChanges: 'Co se mění',
  designReset: 'zpět na výchozí',
  designGroupColors: 'Barvy',
  designGroupType: 'Písma',
  designGroupShape: 'Tvar',
  designGroupLauncher: 'Tlačítko na stránce',
  designGroupPanel: 'Záhlaví',
  designGroupProducts: 'Produkty',
  designGroupCart: 'Košík obchodu',
  designGroupCopy: 'Slova',
  designGroupLogo: 'Logo',
  designGroupCss: 'Vlastní CSS',
  designColorBrand: 'Značka',
  designColorActions: 'Přidat a odeslat',
  designColorPanel: 'Panel',
  designColorSurface: 'Plochy',
  designColorInk: 'Text',
  designColorMuted: 'Doplňkový text',
  designColorLine: 'Čáry',
  designColorBubble: 'Zprávy nakupujícího',
  designColorBubbleInk: 'Text nakupujícího',
  designActionsBrand: 'Barva značky',
  designActionsInk: 'Barva textu',
  designWordsName: 'Název',
  designWordsGreeting: 'Pozdrav',
  designWordsStarters: 'Úvodní otázky',
  designWordsLauncher: 'Popisek tlačítka',
  designWordsPlaceholder: 'Zástupný text',
  designWordsAi: 'Štítek AI',
  designWordsPerson: 'Odkaz na člověka',
  noteNew: 'Nové',
  noteChanged: 'Změněno',
  noteRemoved: 'Odstraněno',
  noteSourceKnowledge: 'Stránka znalostí: {title}',
  noteSourceFile: 'Soubor: {name}',
  noteSourceChat: 'Z chatu',
  knowledgeApprovalNote:
    'Nakupující si mohou přečíst vše v těchto poznámkách. Schválením uložíte nový koncept: položte mu otázky na kartě Widget a potom ho publikujte na kartě Instalace.',
  profileTitle: 'O obchodě',
  profileChecked: 'Zkontrolováno {date}',
  profileSources: 'Napsáno z',
  profileSample: '{count} produktů vzorkováno pro cenovou hladinu',
  profileSampleOne: '{count} produkt vzorkován pro cenovou hladinu',
  profileAssistant: 'Co tu asistent umí',
  profileTeam: 'Napsal váš tým',
  profileEmpty:
    'Zatím nenapsáno. Požádejte Chat, aby spustil průchod znalostí: napíše to z vašich vlastních stránek a z toho, co viděl na vašem webu, a můžete přidat vlastní řádky.',
  profileWithChat: 'Přidat nebo změnit pomocí Chatu',
  profilePrompt:
    'Pomoz mi zlepšit, co nákupní asistent pro {name} ({domain}) ví o našem obchodě jako celku: co prodáváme, čím jsme známí, pro koho jsme a co tu mohou zákazníci dělat. Nejprve si přečti profil „O obchodě“ a řekni mi, co chybí nebo je špatně. Cokoli ti o obchodě řeknu a žádná stránka to neuvádí, se vloží jako řádek od našeho týmu.',
  profilePartNavigation: 'Oddělení',
  profilePartWelcome: 'Pozdrav',
  profilePartMarkets: 'Trhy',
  profilePartFunctions: 'Co asistent umí',
  profilePartProducts: 'Produkty',
  profilePartPages: 'Servisní stránky',
  profileCleared: 'Vymaže napsaný profil',
  knowledgeTitle: 'Co ví',
  knowledgeBody: 'Kromě vašich produktů, cen a skladu, které vždy načítá živě z vašeho obchodu.',
  knowledgeEmpty:
    'Zatím jen váš katalog. Přidejte informace o doručení, vrácení nebo platbě pomocí Chatu, z vlastních stránek nebo ze Znalostí.',
  knowledgeWithChat: 'Přidat pomocí Chatu',
  knowledgeChangeWithChat: 'Změnit pomocí Chatu',
  knowledgePrompt:
    'Pomoz mi vybrat, co má nákupní asistent pro {name} ({domain}) říkat nakupujícím kromě našich produktů, například o doručení, vrácení a platbě. Najdi to na našich stránkách nebo ve Znalostech a navrhni poznámky, které mohu schválit.',
  pagesTitle: 'Stránky, které čte',
  pagesBody:
    'Stránky, ze kterých poznámky vycházejí. Poneva je kontroluje každý den a Chat upozorní, když se některá změní.',
  pagesEmpty:
    'Zatím žádné stránky. Zobrazí se zde stránky, které najde čtení obchodu, a ty, které uvedete pomocí Chatu.',
  pageFound: 'Ponevou nalezeno',
  pageLinkedFrom: 'odkazováno z {page}',
  pageAddedByYou: 'přidáno ručně',
  pageFromNote: 'uvedena v poznámce',
  pageNoteOne: '1 poznámka',
  pageNotesMany: '{count} poznámek',
  pageStatusOk: 'Aktuální',
  pageStatusChanged: 'Změněno',
  pageStatusUnreadable: 'Nelze přečíst',
  pageStatusUnchecked: 'Zatím nezkontrolováno',
  pagesChangeWithChat: 'Změnit pomocí Chatu',
  pagesUpdateWithChat: 'Aktualizovat poznámky pomocí Chatu',
  pagesPrompt:
    'Pomozte mi změnit, které stránky nákupní asistent pro {name} ({domain}) čte pro své poznámky. Ukažte mi stránky, které nyní používá, a zeptejte se mě, které přidat nebo odebrat.',
  pagesUpdatePrompt:
    'Některé stránky {name} ({domain}) se od vzniku poznámek změnily nebo už nejdou přečíst. Ukažte mi, které to jsou, a vytvořte z nich poznámky znovu jako koncept, který si mohu prohlédnout.',
  viewCompare: 'Porovnání',
  lookDeviceLabel: 'Porovnat na',
  lookShop: 'Váš obchod',
  lookShopCaption: 'Úvodní stránka, načtená {when}',
  lookWidget: 'Asistent',
  lookWidgetCaption: 'Verze {version}',
  lookLoading: 'Asistent se umisťuje vedle vašeho obchodu…',
  lookFailed: 'Porovnání se teď nepodařilo vytvořit.',
  lookNoShopPicture: 'Načtení neuložilo snímek vašeho obchodu v této šířce.',
  lookNoneTitle: 'Vlastní vzhled vašeho obchodu nebyl uložen',
  lookNoneBody:
    'Toto načtení jej nezaznamenalo. Načtěte obchod znovu v části Nastavení a jeho logo, barvy a stránky se uloží pro porovnání.',
  lookColoursShop: 'Barvy, které váš obchod používá',
  lookColoursAssistant: 'Barvy asistenta',
  lookRoleHeader: 'Záhlaví',
  lookRoleBrand: 'Značka',
  lookSame: 'Stejná jako barva {colour} vašeho obchodu ({source})',
  lookOff: 'Blízká barvě {colour} vašeho obchodu ({source}), ale ne stejná, rozdíl {delta}',
  lookOwn: 'Vlastní barva: váš obchod ji nepoužívá',
  lookNeutral: 'Váš obchod se pro to nenačetl: zobrazuje se jen to, co si načtení uložilo.',
  lookFromLogo: 'logo',
  lookFromTheme: 'barva motivu',
  lookFromTile: 'barva dlaždice',
  lookFromMask: 'připnutá ikona',
  lookFromManifest: 'manifest aplikace',
  lookFromHeader: 'záhlaví',
  lookFromButton: 'tlačítka',
  lookFromVariable: 'barva stylu',
  lookFix: 'Opravit pomocí Chatu',
  lookFixPrompt:
    'Barva asistenta pro {role} je {colour}, blízká barvě {exact} vašeho obchodu ({source}), ale ne stejná. Použijte prosím přesnou barvu vašeho obchodu, {exact}.',
  lookSwatch: 'Barva {colour}',
  errorDesignChecks: 'Tato verze nevyhověla kontrolám designu.',
  designSamplesBlocked:
    'Rozvržení karet zde nelze ověřit: váš web blokuje automatické návštěvy. Barvy, písma a logo fungují dál.',
  designCheckLabel: 'Označení AI je příliš nevýrazné na čtení.',
  designCheckReach: 'Tlačítko je nedostupné.',
  designCheckSize: 'Část textu je příliš malá.',
  designCheckOverflow: 'Panel se posouvá do strany.',
  designCheckRender: 'Kontroly se nepodařilo spustit. Zkuste to za chvíli znovu.',
  fixDesignPrompt:
    'Pokusil jsem se zveřejnit nejnovější koncept {name} ({domain}), ale nevyhověl kontrolám designu: {facts}. Opravte design v tomto chatu a zkontrolujte ho na telefonu i počítači. Nezveřejňujte ho.',
};

const da: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Shoppingassistent',
  designUnconfirmed:
    'Stoppede, før Chat kunne bekræfte, at designet blev gemt. Assistentens lærred viser den nyeste version.',
  errorNewerVersion:
    'En nyere version blev gemt, siden du åbnede denne. Se den først, og udgiv derefter.',
  canvasSelectHint: 'Klik på en del af assistenten for at ændre den i chatten · Esc for at stoppe',
  canvasDraft: 'Version {version} · ikke udgivet endnu',
  canvasDraftOverLive: 'Version {version} · version {live} er udgivet',
  canvasLive: 'Version {version} · udgivet',
  canvasNoVersion: 'Den første version oprettes, når butikken er færdig med at blive læst',
  canvasPublish: 'Udgiv på Storefront-siden',
  canvasFrameTitle: '{name} shoppingassistent',
  viewLabel: 'Vis',
  photosGood: '{count} produktbilleder: hele og skarpe',
  photosGoodOne: 'Produktbilledet er helt og skarpt',
  canvasMissingTitle: 'Denne shoppingassistent findes ikke i dette arbejdsområde',
  canvasMissingBody: 'Den tilhører et andet arbejdsområde eller er blevet fjernet.',
  canvasMissingLink: 'Se dette arbejdsområdes assistenter',
  photosLabel: 'Produktbilleder',
  photosSmall: '{count} små i deres felter',
  photosCut: '{count} beskåret',
  photosSoft: '{count} forstørrede og uskarpe',
  photosMissing: '{count} uden billede',
  photosFix: 'Ret med Chat',
  photosPrompt:
    'Få produktbillederne i kortene til at se rigtige ud: hvert billede skal være helt, stort nok til sit felt og skarpt.',
  photosFacts:
    '{total} billeder: {small} små i deres felter, {cut} beskåret, {soft} forstørrede, {missing} uden billede. Størrelser: {sizes}',
  viewDesign: 'Design',
  viewVersions: 'Versioner',
  versionsLoading: 'Indlæser versioner…',
  versionsFailed: 'Versionerne kunne ikke indlæses.',
  versionTitle: 'Version {version}',
  versionLive: 'Udgivet',
  versionChecked: 'Kontrolleret',
  versionToday: 'I dag',
  versionYesterday: 'I går',
  versionEarlier: 'Tidligere',
  versionByPerson: 'Anmodet af {name}',
  versionByChat: 'Anmodet i chatten',
  versionFromReading: 'Fra læsning af {domain}',
  versionPublishedBy: 'udgivet af {name}, {at}',
  versionPublish: 'Udgiv',
  versionRestore: 'Gendan…',
  versionRestoreTitle: 'Gendan version {version}?',
  versionRestoreBody:
    'Den bliver til version {next}, et nyt udkast. Dit websted ændres ikke, før du udgiver.',
  versionRestoreConfirm: 'Gendan',
  versionRestoreCancel: 'Annuller',
  versionRestored: 'Gendannet som version {version}',
  versionRestoreFailed: 'Versionen kunne ikke gendannes.',
  compareTitle: 'Sammenlign version {to} med version {from}',
  compareTitleLive: 'Sammenlign version {to} med version {from}, udgivet',
  compareHint: 'Træk for at sammenligne',
  compareLabel: 'Sammenlign version {from} og version {to}',
  compareTagLive: 'v{version} · Udgivet',
  compareTag: 'v{version}',
  changesTitle: 'Hvad ændrede sig fra version {from} til {to}',
  changesNone: 'Designene er ens. Kun noterne eller butikkens data blev ændret.',
  changeAdded: 'Tilføjet',
  changeRemoved: 'Fjernet',
  changeChanged: 'Ændret',
  changeDrawn: 'et tegnet logo',
  changeCss: 'sin egen CSS',
  changeYes: 'ja',
  changeNo: 'nej',
  'field.colors.brand': 'Brandfarve',
  'field.colors.actions': 'Knapfarve',
  'field.colors.panel': 'Panelfarve',
  'field.colors.surface': 'Overfladefarve',
  'field.colors.ink': 'Tekstfarve',
  'field.colors.muted': 'Dæmpet tekstfarve',
  'field.colors.line': 'Linjefarve',
  'field.colors.bubble': 'Shopperens beskedfarve',
  'field.colors.bubbleInk': 'Shopperens beskedtekst',
  'field.colors.header': 'Headerfarve',
  'field.colors.headerInk': 'Sidehovedets tekstfarve',
  'field.colors.tints': 'Bløde nuancer',
  'field.copy.assistantName': 'Navn',
  'field.copy.greeting': 'Hilsen',
  'field.copy.placeholder': 'Spørgefelt',
  'field.copy.starters': 'Startspørgsmål',
  'field.copy.launcherLabel': 'Launcher-tekst',
  'field.copy.aiLabel': 'AI-label',
  'field.copy.personLabel': 'Link til en person',
  'field.type.body': 'Skrifttype til tekst',
  'field.type.display': 'Skrifttype til overskrifter',
  'field.type.fontCss': 'Skrifttypekilde',
  'field.type.scale': 'Tekststørrelse',
  'field.shape.radius': 'Hjørner',
  'field.shape.shadow': 'Skygge',
  'field.shape.density': 'Afstand',
  'field.launcher.style': 'Launcher på computere',
  'field.launcher.phoneStyle': 'Launcher på telefoner',
  'field.launcher.side': 'Launcher-side',
  'field.launcher.icon': 'Launcher-ikon',
  'field.panel.header': 'Headerstørrelse',
  'field.panel.opening': 'Første skærm',
  'field.products.layout': 'Produkter',
  'field.products.imageFit': 'Produktbilleder',
  'field.products.imageRatio': 'Billedfeltets form',
  'field.products.imagePadding': 'Plads omkring billeder',
  'field.products.imageBackground': 'Billedfeltets farve',
  'field.products.picker': 'Størrelsesvalg',
  'field.products.showStock': 'Vis lager',
  'field.cart.landing': 'Animation i kurven',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Logoform',
  'field.logo.plate': 'Logoets baggrund',
  'field.css': 'Tilpasset CSS',
  'value.ink': 'blæk',
  'value.brand': 'brandfarve',
  'value.small': 'lille',
  'value.medium': 'mellem',
  'value.large': 'stor',
  'value.square': 'firkantet',
  'value.soft': 'blød',
  'value.round': 'rund',
  'value.flat': 'flad',
  'value.deep': 'dyb',
  'value.compact': 'kompakt',
  'value.comfortable': 'luftig',
  'value.pill': 'pille med tekst',
  'value.circle': 'rund',
  'value.right': 'højre',
  'value.left': 'venstre',
  'value.logo': 'logo',
  'value.chat': 'chatboble',
  'value.sparkle': 'glimt',
  'value.bag': 'taske',
  'value.full': 'fuld',
  'value.middle': 'i midten',
  'value.bottom': 'nederst',
  'value.rows': 'rækker',
  'value.grid': 'gitter',
  'value.carousel': 'en række, du kan stryge',
  'value.contain': 'hele billedet',
  'value.cover': 'udfyldt felt',
  'value.portrait': 'portræt',
  'value.landscape': 'landskab',
  'value.none': 'ingen',
  'value.pop': '+1 der stiger op',
  'value.ring': 'ring der breder sig',
  'value.blink': 'blink',
  'value.chips': 'knapper på en række',
  'value.menu': 'en liste',
  'value.rounded': 'afrundet',
  designCardTitle: '{name} shoppingassistent',
  designCardSaved: 'Version {version} gemt · ikke udgivet endnu',
  designCardSavedFirst: 'Design gemt · den første version får det',
  designSaving: 'Gemmer designet…',
  selectedAssistantPart: 'Valgt: en del af assistenten',
  loading: 'Indlæser Storefront…',
  disabledTitle: 'Storefront er ikke slået til i dette arbejdsområde.',
  disabledBody:
    'Det er i tidlig adgang og kommer i etaper. Spørg din Poneva-kontakt, hvornår det når jer.',
  loadFailed: 'Storefront kunne ikke indlæses.',
  startTitle: 'En shoppingassistent til din butik',
  startBody:
    'Poneva læser din butik, som en kunde bruger den: søgning, produkter, kurven og den chat, du allerede har. Det tager cirka 10 til 30 minutter, bruger virksomhedens kreditter, og du kan fortsætte med at arbejde.',
  urlLabel: 'Din butiks adresse',
  urlPlaceholder: 'https://www.dinbutik.dk',
  startButton: 'Læs min butik',
  starting: 'Starter…',
  startFailed: 'Butikken kunne ikke læses. Kontrollér adressen, og prøv igen.',
  tabConversations: 'Samtaler',
  tabOverview: 'Oversigt',
  allAssistants: 'Alle assistenter',
  allAssistantsNote: '{assistants} assistenter · {sites} websteder',
  allAssistantsMenuNote: '{count} samtaler denne uge',
  marketLabel: 'Marked',
  allMarkets: 'Alle markeder',
  allMarketsNote: '{count} websteder',
  scopeIn: '{assistant} i {market}',
  askAbout: 'Spørg Chat om {scope}',
  overviewAssistantsTitle: 'Assistenter og markeder',
  overviewMarketsTitle: 'Markeder',
  overviewAssistantsOnly: 'Assistenter',
  overviewIdle: '{count} assistenter uden samtaler endnu',
  allAssistantsCount: '{assistants} assistenter',
  overviewFailed: 'Oversigten kunne ikke indlæses.',
  colConversations: 'Samtaler',
  colCart: 'Lagt i kurv',
  colCheckout: 'Gået til betaling',
  colPerson: 'Person',
  colCost: 'Kreditter',
  rowMarkets: '{count} markeder',
  rowVersion: 'version {version}',
  rowAria:
    '{name}, {sub}: {conversations} samtaler, {cart} lagt i kurv, {checkout} nåede betaling, {person} bad om en person, {cost} kreditter',
  versionPublishedAt: 'udgivet {at}',
  designColorHeader: 'Sidehoved',
  designColorHeaderInk: 'Headertekst',
  designGroupTranslations: 'Ord på andre sprog',
  topicsTitle: 'Hvad kunderne spørger om',
  topicsNote: 'Emner, Chat fandt i de første spørgsmål',
  startPagesTitle: 'Hvor samtaler starter',
  startPagesOne: 'Alle samtaler startede på én slags side: {page}.',
  'startPage.product': 'Produktsider',
  'startPage.search': 'Søgeresultater',
  'startPage.start': 'Startsiden',
  'startPage.cart': 'Kurv',
  'startPage.other': 'Andre sider',
  addMarketInChat: 'Tilføj et marked i chatten',
  marketsTitle: 'Markeder',
  marketsBody:
    'Én assistent og ét design. Hvert marked har sit eget sprog, sin egen valuta, sine produkter, sin kurv og sin chat til en person.',
  marketChecked: 'kontrolleret {when}',
  'marketStatus.ready': 'Klar',
  'marketStatus.found': 'Ikke tilføjet',
  'marketStatus.checking': 'Kontrollerer',
  'marketStatus.failed': 'Skal rettes',
  addMarketPrompt:
    'Tilføj endnu et marked til shoppingassistenten for {name} ({domain}): find butikkens andre lande, sprog eller websteder, og spørg mig, hvilket der skal tilføjes.',
  tabWidget: 'Widget',
  tabInstall: 'Installer',
  tabSetup: 'Kom i gang',
  statusReading: 'Læser din butik',
  readingProducts: 'Læser produkterne igen',
  readingCart: 'Kontrollerer kurven igen',
  readingChat: 'Kontrollerer vejen til din egen chat',
  readingMarket: 'Tilføjer markedet {market}',
  readingMarketAny: 'Tilføjer et marked',
  statusReady: 'Klar til installation',
  statusFailed: 'Læsning stoppet',
  statusLive: 'Aktiv · version {version} på {domain}',
  statusPublished: 'Udgivet · version {version} · endnu ikke på dit websted',
  statusPublishedUnverified: 'Udgivet · version {version} · kunne ikke kontrollere dit websted',
  stepOpen: 'Åbner webstedet',
  stepSearch: 'Søgning',
  stepProducts: 'Produkter',
  stepCart: 'Kurv',
  stepChat: 'Din eksisterende chat',
  stepLook: 'Dit udtryk og logo',
  stepChecks: 'Kontrollerer, at det virker',
  stepReady: 'Klar',
  stepDetailOpen: 'Åbner {domain}',
  stepDetailSearch: 'Finder butikkens produkter',
  stepDetailProducts: 'Læser produkter, priser, billeder, farver og størrelser',
  stepDetailCart: 'Prøver kurven: tilføjer, læser tilbage, fjerner og tømmer',
  stepDetailChat: 'Leder efter din eksisterende chat',
  stepDetailLook: 'Læser butikkens logo og farver',
  stepDetailChecks: 'Prøver kundesøgninger, produkter, browsing og kurven',
  stepDetailChecksProducts:
    'Kontrollerer produktsøgninger, browsing, billeder, farver og størrelser',
  stepDetailChecksCart:
    'Kontrollerer kurvens tilføjelse, tilbagelæsning, fjernelse og tømning samt produktlæsning',
  stepDetailChecksChat: 'Kontrollerer overdragelsen til din eksisterende chat samt produktlæsning',
  stepDetailChecksMarket: 'Kontrollerer dette markeds produkter, priser og kurv',
  nowRunning: 'Nu: {step}',
  nowProgress: 'Trin {n} af {total} · {minutes} min indtil videre',
  nowReady: 'Din butik er klar til en assistent',
  nowReadyBody: 'Søgning, produkter og kurven virker.',
  nowLastRead: 'Sidst læst {when}',
  nowUsually: 'normalt {min}–{max}',
  stepsRead: 'Læst {when} · Vis trinene',
  nowFailed: 'Læsning stoppet',
  factAnswers: 'Besvarer kunderne med',
  costCounting: 'Tæller…',
  factPaid: 'Betales med',
  paidCompany: 'Virksomhedens kreditter',
  creditsAmount: '{amount} kreditter',
  creditsOne: '{amount} kredit',
  creditsUnder: 'under {amount} kreditter',
  stopRun: 'Stop kørslen',
  stopping: 'Stopper…',
  closeNote: 'Du kan forlade siden. Kørslen fortsætter.',
  readyNote: 'Næste trin: prøv din widget, og installér den derefter.',
  buildWidget: 'Prøv widgetten',
  readAgain: 'Læs butikken igen',
  foundTitle: 'Det blev fundet',
  foundProducts: 'Produkter og priser læst',
  foundSearch: 'Søgning virker',
  foundCart: 'Tilføjelse til kurven virker',
  foundChat: 'Din {vendor}-chat',
  foundNoChat: 'Ingen egen chat',
  foundLook: 'Dine farver og dit logo',
  widgetTitle: 'Din widget',
  widgetBody:
    'Din assistent i dine farver. Prøv den her: den svarer med dine rigtige produkter. Intet bliver lagt i en rigtig kurv.',
  widgetLoading: 'Indlæser forhåndsvisningen…',
  widgetFailed: 'Forhåndsvisningen kunne ikke indlæses.',
  widgetNotReady: 'Widget er klar, når trinnene under Kom i gang er færdige.',
  installLineTitle: 'Tilføj denne linje til dit websted',
  installLineBody:
    'Den virker på alle platforme: i sidernes head, på Shopify i theme.liquid lige før </head>. Den viser intet, før du udgiver.',
  installLineBodyLive:
    'Den virker på alle platforme: i sidernes head, på Shopify i theme.liquid lige før </head>. Kunderne møder derefter version {version}.',
  installPolicyAsk: 'Bruger dit websted en Content Security Policy?',
  installPolicy:
    'Hvis dit websted bruger en Content Security Policy, skal du også tillade {origin} i {directives}, ellers kan assistenten ikke starte.',
  installPolicyRefuses:
    'Dit websteds Content Security Policy afviser assistenten: tillad {origin} i {directives}, ellers starter intet.',
  installPolicyNonce:
    'Din politik kører kun scripts, den har tillid til, så tilføj linjen med dine siders nonce.',
  installPolicyEval: "Assistenten kræver også 'unsafe-eval' i script-src.",
  copy: 'Kopiér',
  copied: 'Kopieret',
  domainsTitle: 'Websteder, den svarer på',
  domainsBody: 'Den er stille på alle andre websteder, også et der kopierer linjen.',
  lineFound: 'Linje fundet',
  lineNotSeen: 'Ikke på siden endnu',
  lineUnverified:
    'Kunne ikke kontrollere: siden blokerer automatiske besøg. Assistenten virker stadig for kunderne.',
  lineChecked: 'kontrolleret {when}',
  lineCheckAgain: 'Kontrollér igen',
  lineChecking: 'Kontrollerer siden…',
  installCheckBusy: 'Kontrollen er optaget lige nu. Prøv igen om lidt.',
  publishTitle: 'Udgiv',
  publishBody:
    'Shoppere på {domain} ser den med det samme. Du kan til enhver tid fjerne udgivelsen.',
  publishButton: 'Udgiv version {version}',
  publishConfirm: 'Udgiv version {version} af {name}?',
  publishNow: 'Udgiv',
  notNow: 'Ikke nu',
  publishing: 'Udgiver…',
  liveTitle: 'Udgivet: version {version}',
  liveSince: 'Siden {since}',
  unpublish: 'Fjern udgivelse',
  unpublishConfirm:
    'Fjern version {version} fra dit websted? Kunderne kan ikke længere se assistenten, før du udgiver igen.',
  publishedLine: 'Udgivet nu: version {version}, siden {since}',
  usageTitle: 'Forbrug',
  usageBody: 'Den bruger virksomhedens kreditter ligesom resten af Chat.',
  usageDiscovery: 'Læsning af butikken',
  usageChats: 'Shopperchats',
  usageChatsValue: '{count} chats · {cost}',
  last7: 'Sidste 7 dage',
  askWeek: 'Spørg Chat om denne uge',
  kpiConversations: 'Samtaler',
  kpiVsPrevious: '{change} sammenlignet med ugen før',
  kpiCart: 'Lagt i kurv',
  kpiCheckout: 'Nåede betaling',
  kpiPerson: 'Bad om en person',
  kpiCost: 'Brugte kreditter',
  ofConversations: '{percent} af samtalerne',
  checkoutNote: '{percent} af samtalerne',
  personNote: '{percent} åbnede din chat',
  costNote: 'Cirka {amount} pr. samtale',
  costNoteUnder: 'Under {amount} pr. samtale',
  sparkLabel: 'Samtaler pr. dag, seneste {days} dage',
  sparkDay: '{day}: {count} samtaler',
  filterAll: 'Alle',
  filterCart: 'Lagt i kurv',
  filterCheckout: 'Betaling',
  filterPerson: 'Person',
  outcomeCart: 'Lagt i kurv',
  outcomeCheckout: 'Nåede betaling',
  outcomePerson: 'Bad om en person',
  outcomeAnswered: 'Besvaret',
  devicePhone: 'Telefon',
  deviceComputer: 'Computer',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Startede på {page}',
  anonymous: 'Shopperen er anonym.',
  askConversation: 'Spørg Chat om denne samtale',
  showOlderVersions: 'Vis tidligere versioner',
  showOlder: 'Vis ældre samtaler',
  filterCountsWeek: 'Antal: de seneste 7 dage',
  readAlreadyHere:
    'Butikken er allerede her, så intet nyt blev startet, og der blev ikke brugt kreditter.',
  readOpenShop: 'Åbn den',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Hvad kan vi lære af denne samtale?',
  askWeekMessage: 'Hvad kan vi lære af denne uges samtaler?',
  conversationsChipOne: '{name} · samtale fra {when}',
  conversationsChipWeek: '{name} · denne uge',
  conversationsChipWeekIn: '{name} · {market} · denne uge',
  removeConversations: 'Fjern samtalerne',
  noConversations: 'Ingen samtaler endnu',
  noConversationsBody: 'Når kunderne bruger assistenten, vises deres samtaler her.',
  pickConversation: 'Vælg en samtale for at læse den.',
  eventCheckout: 'Gik til betaling',
  eventPerson: 'Åbnede din chat',
  showMore: 'Vis mere',
  urlInvalid: 'Brug din butiks https-adresse, for eksempel https://www.dinbutik.dk.',
  errorNoCompany:
    'Storefront kræver et virksomhedsarbejdsområde. Bed din administrator om at tilføje dig.',
  errorPublishPermission:
    'Kun personer, der kan udgive for din virksomhed, kan gøre dette. Bed din administrator.',
  actionFailed: 'Det virkede ikke. Prøv igen.',
  copyFailed: 'Kunne ikke kopiere. Markér linjen, og kopiér den selv.',
  reasonCancelled: 'Du stoppede kørslen.',
  reasonFailed: 'Læsningen kunne ikke gennemføres i din butik, så assistenten er ikke klar.',
  reasonFailedLive:
    'Læsningen kunne ikke gennemføres i din butik. Den udgivne assistent fortsætter med at virke som hidtil.',
  reasonFailedKept:
    'Læsningen kunne ikke gennemføres i din butik, så intet blev ændret: version {version} forbliver som den var.',
  statusReadStopped: 'Klar · den seneste læsning stoppede',
  reasonStepMarked: 'Trinnet, hvor det stoppede, er markeret nedenfor.',
  reasonReadAgain: 'En ny læsning starter med det, denne læsning allerede lærte.',
  reasonNotAStore:
    'Denne adresse ligner ikke en webshop: der var ingen produkter at søge efter. Kontrollér adressen, eller prøv butikkens hoveddomæne.',
  reasonTooSlow:
    'Din butiks søgning svarer for langsomt til en shoppingassistent: det tog over 12 sekunder. En hurtigere søgning i butikken eller en ny læsning senere kan hjælpe.',
  reasonOurSide:
    'Læsningen stoppede på grund af en fejl hos os, ikke i din butik, så assistenten er ikke klar endnu.',
  reasonOurSideLive:
    'Læsningen stoppede på grund af en fejl hos os, ikke i din butik. Den udgivne assistent fortsætter med at virke som hidtil.',
  reasonOurSideKept:
    'Læsningen stoppede på grund af en fejl hos os, ikke i din butik, så intet blev ændret: version {version} forbliver som den var.',
  reasonTryLater: 'Prøv at læse butikken igen om lidt.',
  errorNoCredits:
    'Der er ingen kreditter til dette lige nu. En ejer af virksomheden kan tjekke planen og grænserne under Usage.',
  errorReadUnavailable: 'Butikken kan ikke læses lige nu. Prøv igen om et par minutter.',
  reasonTimeout:
    'Læsningen nåede sin tidsgrænse, før den blev færdig. En ny læsning fortsætter fra det, der allerede er kontrolleret.',
  retrying: 'Starter igen…',
  readTitleChecks: 'Kontrollér den gemte læsning igen',
  checksScopeLabel: 'Kontrollerer',
  readScopeChecks:
    'Kundesøgninger, produkter, browsing og kurven, på det, den sidste læsning gemte. Butikken læses ikke igen.',
  readScopeChecksProducts:
    'Produktsøgninger, browsing, billeder, farver og størrelser, på det, den sidste læsning gemte. Butikken læses ikke igen.',
  readScopeChecksCart:
    'Kurvens tilføjelse, tilbagelæsning, fjernelse og tømning samt produktlæsning, på det, den sidste læsning gemte. Butikken læses ikke igen.',
  readScopeChecksChat:
    'Overdragelsen til din eksisterende chat samt produktlæsning, på det, den sidste læsning gemte. Butikken læses ikke igen.',
  readScopeChecksMarket:
    'Markedets produkter, priser og kurv, på det, den sidste læsning gemte. Butikken læses ikke igen.',
  checksCost: 'Kun de nye kontroller bruger virksomhedens kreditter',
  checksAfter:
    'Gemmer et nyt udkast, hvis kontrollerne går igennem. Intet når kunderne, før du udgiver.',
  checksAfterLive:
    'Gemmer et nyt udkast, hvis kontrollerne går igennem. Den udgivne assistent fortsætter med at virke, og kunderne ser først ændringen, når du udgiver.',
  checksStarted:
    'Kontrollerne er startet. Når det nye udkast er klar, kan du bede Chat om at kontrollere det.',
  checkingSavedWork: 'Kontrollerer det gemte arbejde igen',
  checkAgain: 'Kontrollér igen',
  checkingAgain: 'Kontrollerer igen…',
  reasonCheckAgain:
    'Kontrollér igen tester det gemte arbejde én gang til uden at læse butikken igen. Kun de nye kontroller bruger virksomhedens kreditter.',
  fixWithChat: 'Ret med Chat',
  fixReadPrompt:
    'Læsningen af {name} ({domain}) stoppede. Se på, hvad der skete, og hjælp mig med at få det til at virke: fortæl mig, hvad du finder, foreslå, hvad jeg kan fortælle læsningen, og kontrollér den så igen eller læs igen med det tip, så lidt af butikken som muligt.',
  fixReadPromptStep:
    'Læsningen af {name} ({domain}) stoppede ved »{step}«. Se på, hvad der skete, og hjælp mig med at få det til at virke: fortæl mig, hvad du finder, foreslå, hvad jeg kan fortælle læsningen, og kontrollér den så igen eller læs igen med det tip, så lidt af butikken som muligt.',
  stepStateDone: 'færdig',
  stepStateRunning: 'i gang',
  stepStateWaiting: 'ikke startet',
  stepStateFailed: 'stoppet',
  newVersionTitle: 'Version {version} er klar',
  newVersionBody: 'Udgiv den for at erstatte version {live}.',
  newVersionBodyLive: 'Udgiv den for at erstatte version {live} på {domain}.',
  previewSize: 'Forhåndsvisningens størrelse',
  designWithChat: 'Åbn design',
  designReviewBody: 'Se det nuværende design, og fortæl så Chat, hvad du vil justere.',
  designWhileTitle: 'Design den, mens vi læser din butik',
  designWhileBody:
    'Chat kan forme assistentens udseende, ord og logo sammen med dig nu. Designet er klar, når læsningen er færdig.',
  designPreviewNote: 'En forhåndsvisning med eksempelprodukter, indtil din butik er læst.',
  proposedDesign: 'Foreslået design',
  proposedDesignNote:
    'Widgetten med denne ændring, vist med eksempelprodukter. Når du godkender, gemmes den som et nyt udkast; udgivelse sker på Storefront-siden.',
  lineSources: 'Fra butikkens sider:',
  readAgainConfirm:
    'Læs {name} igen? Det tager cirka 10 til 30 minutter og bruger virksomhedens kreditter.',
  readAgainLive:
    'Den udgivne assistent fortsætter med at virke, indtil du udgiver den nye version.',
  readAgainNow: 'Læs igen',
  switchStore: 'Skift butik eller tilføj en',
  storesLabel: 'Dine butikker',
  addStore: 'Tilføj endnu en butik',
  backToStores: 'Tilbage til dine butikker',
  storeLive: 'Udgivet',
  storeReady: 'Klar',
  storeReading: 'Læser',
  storeStopped: 'Stoppet',
  actionDesignTitle: 'Skift shoppingassistentens design',
  actionKnowledgeTitle: 'Skift, hvad shoppingassistenten ved',
  readTitleProducts: 'Læs produkterne igen',
  readTitleCart: 'Kontrollér kurven igen',
  readTitleTheirChat: 'Kontrollér vejen til din egen chat igen',
  readTitleAll: 'Læs hele butikken igen',
  readTitleStore: 'Læs en ny butik',
  readTitleMarket: 'Tilføj et marked',
  readShopLabel: 'Butik',
  readScopeLabel: 'Læser',
  readTimeLabel: 'Tager',
  readCostLabel: 'Pris',
  readAfterLabel: 'Bagefter',
  readScopeProducts: 'Produktsøgning, sortering, produktbilleder, farver og størrelser',
  readScopeCart: 'Tilføjelse til og fjernelse fra kurven',
  readScopeTheirChat: 'Hvordan kunderne når din egen chat',
  readScopeAll: 'Produkter, kurven, din egen chat og dine markeder',
  readScopeStore:
    'Produkter, priser og størrelser, kurven, din egen chat, dit udtryk og dine markeder',
  readScopeMarket: '{market}: produkter, priser og kurv',
  readTime: 'Normalt {min}–{max} minutter',
  readCost: 'Bruger virksomhedens kreditter',
  readCostLast:
    'Bruger virksomhedens kreditter · den seneste læsning af denne butik brugte {credits}',
  readCostLastPartial:
    'Bruger virksomhedens kreditter · den seneste læsning af denne butik stoppede tidligt og brugte mindst {credits}',
  readAfter: 'Gemmer et nyt udkast. Intet når kunderne, før du udgiver.',
  readAfterLive:
    'Gemmer et nyt udkast. Den udgivne assistent fortsætter med at virke, og kunderne ser først ændringen, når du udgiver.',
  readAfterNew: 'Gemmer et første udkast, du kan prøve. Intet er udgivet.',
  readCartNote:
    'For at kontrollere kurven tilføjer den en testvare og fjerner den igen. Den gennemfører aldrig et køb.',
  readFocusLabel: 'Det, Chat bad læsningen om at rette',
  readFooter: 'Intet starter, før du godkender. Du kan stoppe læsningen under Kom i gang.',
  readStartedAt:
    'Startede {time} · normalt {min}–{max} minutter. Når det nye udkast er klar, kan du bede Chat om at kontrollere det.',
  readFocusAll: 'Vis alle',
  readFocusLess: 'Vis færre',
  readStart: 'Start læsningen',
  addToSite: 'Tilføj til dit websted',
  readTab: 'Anmodning',
  readStarted:
    'Læsningen er startet. Når det nye udkast er klar, kan du bede Chat om at kontrollere det.',
  readFollow: 'Følg den under Kom i gang',
  proposedDesignLive:
    'Din butiks rigtige forhåndsvisning i dette design. Spørg den om noget: intet bliver lagt i en rigtig kurv. Når du godkender, gemmes den som et nyt udkast; udgivelse sker på Storefront-siden.',
  designChanges: 'Hvad ændres',
  designReset: 'tilbage til standarden',
  designGroupColors: 'Farver',
  designGroupType: 'Skrifttyper',
  designGroupShape: 'Form',
  designGroupLauncher: 'Knap på siden',
  designGroupPanel: 'Sidehoved',
  designGroupProducts: 'Produkter',
  designGroupCart: 'Butikkens kurv',
  designGroupCopy: 'Ord',
  designGroupLogo: 'Logo',
  designGroupCss: 'Tilpasset CSS',
  designColorBrand: 'Mærke',
  designColorActions: 'Tilføj og send',
  designColorPanel: 'Panel',
  designColorSurface: 'Overflader',
  designColorInk: 'Tekst',
  designColorMuted: 'Sekundær tekst',
  designColorLine: 'Linjer',
  designColorBubble: 'Shopperens beskeder',
  designColorBubbleInk: 'Shopperens tekst',
  designActionsBrand: 'Brandfarve',
  designActionsInk: 'Tekstfarve',
  designWordsName: 'Navn',
  designWordsGreeting: 'Hilsen',
  designWordsStarters: 'Startspørgsmål',
  designWordsLauncher: 'Knaptekst',
  designWordsPlaceholder: 'Pladsholder',
  designWordsAi: 'AI-label',
  designWordsPerson: 'Link til en person',
  noteNew: 'Ny',
  noteChanged: 'Ændret',
  noteRemoved: 'Fjernet',
  noteSourceKnowledge: 'Vidensside: {title}',
  noteSourceFile: 'Fil: {name}',
  noteSourceChat: 'Fra chatten',
  knowledgeApprovalNote:
    'Kunderne kan læse alt i disse noter. Når du godkender, gemmes et nyt udkast: spørg den under Widget, og udgiv den derefter under Installer.',
  profileTitle: 'Om butikken',
  profileChecked: 'Tjekket {date}',
  profileSources: 'Skrevet ud fra',
  profileSample: '{count} produkter er prøvet for prisniveauet',
  profileSampleOne: '{count} produkt er prøvet for prisniveauet',
  profileAssistant: 'Hvad assistenten kan her',
  profileTeam: 'Skrevet af jeres team',
  profileEmpty:
    'Ikke skrevet endnu. Bed Chat køre en vidensrunde: den skriver dette ud fra jeres egne sider og det, den så på jeres websted, og I kan tilføje egne linjer.',
  profileWithChat: 'Tilføj eller skift med Chat',
  profilePrompt:
    'Hjælp mig med at forbedre, hvad shoppingassistenten for {name} ({domain}) ved om vores butik som helhed: hvad vi sælger, hvad vi er kendt for, hvem vi er til for, og hvad kunder kan gøre her. Læs profilen Om butikken først, og fortæl mig, hvad der mangler eller er forkert. Alt, jeg fortæller om butikken, som ingen side siger, kommer ind som en linje fra vores team.',
  profilePartNavigation: 'Afdelinger',
  profilePartWelcome: 'Hilsen',
  profilePartMarkets: 'Markeder',
  profilePartFunctions: 'Hvad assistenten kan',
  profilePartProducts: 'Produkter',
  profilePartPages: 'Servicesider',
  profileCleared: 'Rydder den skrevne profil',
  knowledgeTitle: 'Det ved den',
  knowledgeBody:
    'Ud over dine produkter, priser og lager, som den altid læser direkte fra din butik.',
  knowledgeEmpty:
    'Kun dit katalog indtil videre. Tilføj oplysninger om levering, retur eller betaling med Chat fra dine egne sider eller Viden.',
  knowledgeWithChat: 'Tilføj med Chat',
  knowledgeChangeWithChat: 'Skift med Chat',
  knowledgePrompt:
    'Hjælp mig med at vælge, hvad shoppingassistenten for {name} ({domain}) skal fortælle kunderne ud over vores produkter, for eksempel om levering, retur og betaling. Find det på vores egne sider eller i Viden, og foreslå noter, jeg kan godkende.',
  pagesTitle: 'Sider den læser',
  pagesBody:
    'De sider dine noter kommer fra. Poneva tjekker dem hver dag, og Chat fortæller dig, når en af dem ændrer sig.',
  pagesEmpty:
    'Ingen sider endnu. Sider, som butikkens læsning finder, og dem du nævner i Chat, vises her.',
  pageFound: 'fundet af Poneva',
  pageLinkedFrom: 'linket fra {page}',
  pageAddedByYou: 'tilføjet af dig',
  pageFromNote: 'nævnt i en note',
  pageNoteOne: '1 note',
  pageNotesMany: '{count} noter',
  pageStatusOk: 'Opdateret',
  pageStatusChanged: 'Ændret',
  pageStatusUnreadable: 'Kan ikke læses',
  pageStatusUnchecked: 'Ikke tjekket endnu',
  pagesChangeWithChat: 'Skift med Chat',
  pagesUpdateWithChat: 'Opdater noter med Chat',
  pagesPrompt:
    'Hjælp mig med at ændre, hvilke sider shoppingassistenten for {name} ({domain}) læser til sine noter. Vis mig de sider, den bruger nu, og spørg mig, hvilke der skal tilføjes eller fjernes.',
  pagesUpdatePrompt:
    'Nogle sider for {name} ({domain}) er blevet ændret, eller de kan ikke længere læses, siden noterne blev lavet. Vis mig hvilke, og lav noterne igen ud fra dem som et udkast, jeg kan gennemse.',
  viewCompare: 'Sammenligning',
  lookDeviceLabel: 'Sammenlign på',
  lookShop: 'Din butik',
  lookShopCaption: 'Forsiden, læst {when}',
  lookWidget: 'Assistenten',
  lookWidgetCaption: 'Version {version}',
  lookLoading: 'Placerer assistenten ved siden af din butik…',
  lookFailed: 'Sammenligningen kunne ikke laves lige nu.',
  lookNoShopPicture: 'Læsningen gemte ikke et billede af din butik i denne bredde.',
  lookNoneTitle: 'Din butiks eget udtryk blev ikke gemt',
  lookNoneBody:
    'Denne læsning registrerede det ikke. Læs butikken igen fra Kom i gang, så gemmes dens logo, farver og sider til sammenligning.',
  lookColoursShop: 'Farver, som din butik bruger',
  lookColoursAssistant: 'Assistentens farver',
  lookRoleHeader: 'Sidehoved',
  lookRoleBrand: 'Mærke',
  lookSame: 'Samme som din butiks {colour} ({source})',
  lookOff: 'Tæt på din butiks {colour} ({source}) men ikke den samme, forskel {delta}',
  lookOwn: 'Egen farve: din butik bruger den ikke',
  lookNeutral: 'Din butik blev ikke indlæst til dette: kun det, læsningen gemte, vises.',
  lookFromLogo: 'logo',
  lookFromTheme: 'temafarve',
  lookFromTile: 'feltfarve',
  lookFromMask: 'fastgjort ikon',
  lookFromManifest: 'app-manifest',
  lookFromHeader: 'sidehoved',
  lookFromButton: 'knapper',
  lookFromVariable: 'stilfarve',
  lookFix: 'Ret med Chat',
  lookFixPrompt:
    'Assistentens farve for {role} er {colour}, tæt på din butiks {exact} ({source}) men ikke den samme. Brug din butiks præcise farve {exact}.',
  lookSwatch: 'Farve {colour}',
  errorDesignChecks: 'Denne version bestod ikke designkontrollerne.',
  designSamplesBlocked:
    'Kortlayout kan ikke bevises her: dit websted blokerer automatiske besøg. Farver, skrifttyper og logo virker stadig.',
  designCheckLabel: 'En AI-etiket er for svag til at kunne læses.',
  designCheckReach: 'En knap kan ikke nås.',
  designCheckSize: 'Noget tekst er for lille.',
  designCheckOverflow: 'Panelet ruller sidelæns.',
  designCheckRender: 'Kontrollerne kunne ikke køres. Prøv igen om lidt.',
  fixDesignPrompt:
    'Jeg forsøgte at udgive det nyeste udkast af {name} ({domain}), og det bestod ikke designkontrollerne: {facts}. Ret designet i denne chat og tjek det på mobil og computer. Udgiv det ikke.',
};

const nl: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Winkelassistent',
  designUnconfirmed:
    'Gestopt voordat Chat kon bevestigen dat het ontwerp was opgeslagen. Op het canvas van de assistent staat de nieuwste versie.',
  errorNewerVersion:
    'Sinds je dit hebt geopend, is een nieuwere versie opgeslagen. Bekijk die eerst en publiceer haar daarna.',
  canvasSelectHint:
    'Klik op een onderdeel van de assistent om het in de chat te wijzigen · Esc om te stoppen',
  canvasDraft: 'Versie {version} · nog niet gepubliceerd',
  canvasDraftOverLive: 'Versie {version} · versie {live} is gepubliceerd',
  canvasLive: 'Versie {version} · gepubliceerd',
  canvasNoVersion: 'De eerste versie komt zodra het uitlezen van de winkel is voltooid',
  canvasPublish: 'Publiceer op de Storefront-pagina',
  canvasFrameTitle: 'Winkelassistent voor {name}',
  viewLabel: 'Bekijken',
  photosGood: '{count} productfoto’s: volledig en scherp',
  photosGoodOne: 'De productfoto is volledig en scherp',
  canvasMissingTitle: 'Deze winkelassistent staat niet in deze werkruimte',
  canvasMissingBody: 'Hij hoort bij een andere werkruimte of is verwijderd.',
  canvasMissingLink: 'De assistenten in deze werkruimte bekijken',
  photosLabel: 'Productfoto’s',
  photosSmall: '{count} te klein in hun tegels',
  photosCut: '{count} afgesneden',
  photosSoft: '{count} vergroot en daardoor onscherp',
  photosMissing: '{count} zonder foto',
  photosFix: 'Met Chat oplossen',
  photosPrompt:
    'Zorg dat de productfoto’s in de kaarten er goed uitzien: elke foto volledig, groot genoeg in de tegel en scherp.',
  photosFacts:
    '{total} foto’s: {small} te klein in hun tegels, {cut} afgesneden, {soft} vergroot, {missing} zonder foto. Afmetingen: {sizes}',
  viewDesign: 'Ontwerp',
  viewVersions: 'Versies',
  versionsLoading: 'Versies worden geladen…',
  versionsFailed: 'De versies konden niet worden geladen.',
  versionTitle: 'Versie {version}',
  versionLive: 'Gepubliceerd',
  versionChecked: 'Gecontroleerd',
  versionToday: 'Vandaag',
  versionYesterday: 'Gisteren',
  versionEarlier: 'Eerder',
  versionByPerson: 'Gevraagd door {name}',
  versionByChat: 'Gevraagd in de chat',
  versionFromReading: 'Uitgelezen van {domain}',
  versionPublishedBy: 'gepubliceerd door {name}, {at}',
  versionPublish: 'Publiceren',
  versionRestore: 'Herstellen…',
  versionRestoreTitle: 'Versie {version} herstellen?',
  versionRestoreBody:
    'Dit wordt versie {next}, een nieuw concept. Je site verandert pas wanneer je publiceert.',
  versionRestoreConfirm: 'Herstellen',
  versionRestoreCancel: 'Annuleren',
  versionRestored: 'Hersteld als versie {version}',
  versionRestoreFailed: 'De versie kon niet worden hersteld.',
  compareTitle: 'Versie {to} vergelijken met versie {from}',
  compareTitleLive: 'Versie {to} vergelijken met versie {from}, gepubliceerd',
  compareHint: 'Sleep om te vergelijken',
  compareLabel: 'Versie {from} en versie {to} vergelijken',
  compareTagLive: 'v{version} · Gepubliceerd',
  compareTag: 'v{version}',
  changesTitle: 'Wat er veranderde van versie {from} naar {to}',
  changesNone:
    'De ontwerpen zijn hetzelfde. Alleen de notities of de gegevens van de winkel zijn veranderd.',
  changeAdded: 'Toegevoegd',
  changeRemoved: 'Verwijderd',
  changeChanged: 'Gewijzigd',
  changeDrawn: 'een getekend logo',
  changeCss: 'eigen CSS',
  changeYes: 'ja',
  changeNo: 'nee',
  'field.colors.brand': 'Merkleur',
  'field.colors.actions': 'Knopkleur',
  'field.colors.panel': 'Paneelkleur',
  'field.colors.surface': 'Achtergrondkleur',
  'field.colors.ink': 'Tekstkleur',
  'field.colors.muted': 'Kleur van gedempte tekst',
  'field.colors.line': 'Lijnkleur',
  'field.colors.bubble': 'Kleur van klantberichten',
  'field.colors.bubbleInk': 'Tekst van klantberichten',
  'field.colors.header': 'Kleur van de koptekst',
  'field.colors.headerInk': 'Tekstkleur van de koptekst',
  'field.colors.tints': 'Zachte tinten',
  'field.copy.assistantName': 'Naam',
  'field.copy.greeting': 'Begroeting',
  'field.copy.placeholder': 'Vraagvak',
  'field.copy.starters': 'Starters',
  'field.copy.launcherLabel': 'Tekst van de opener',
  'field.copy.aiLabel': 'AI-label',
  'field.copy.personLabel': 'Link naar een persoon',
  'field.type.body': 'Tekstlettertype',
  'field.type.display': 'Lettertype voor koppen',
  'field.type.fontCss': 'Bron van het lettertype',
  'field.type.scale': 'Tekstgrootte',
  'field.shape.radius': 'Hoeken',
  'field.shape.shadow': 'Schaduw',
  'field.shape.density': 'Afstand',
  'field.launcher.style': 'Opener op computers',
  'field.launcher.phoneStyle': 'Opener op telefoons',
  'field.launcher.side': 'Kant van de opener',
  'field.launcher.icon': 'Pictogram van de opener',
  'field.panel.header': 'Grootte van de koptekst',
  'field.panel.opening': 'Eerste scherm',
  'field.products.layout': 'Producten',
  'field.products.imageFit': 'Productfoto’s',
  'field.products.imageRatio': 'Vorm van de fototegel',
  'field.products.imagePadding': 'Ruimte rond foto’s',
  'field.products.imageBackground': 'Kleur van de fototegel',
  'field.products.picker': 'Maatkeuze',
  'field.products.showStock': 'Voorraad tonen',
  'field.cart.landing': 'Animatie bij de winkelwagen',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Logovorm',
  'field.logo.plate': 'Achtergrond van het logo',
  'field.css': 'Aangepaste CSS',
  'value.ink': 'inkt',
  'value.brand': 'merkkleur',
  'value.small': 'klein',
  'value.medium': 'gemiddeld',
  'value.large': 'groot',
  'value.square': 'vierkant',
  'value.soft': 'zacht',
  'value.round': 'rond',
  'value.flat': 'vlak',
  'value.deep': 'diep',
  'value.compact': 'compact',
  'value.comfortable': 'ruim',
  'value.pill': 'pil met tekst',
  'value.circle': 'rond',
  'value.right': 'rechts',
  'value.left': 'links',
  'value.logo': 'logo',
  'value.chat': 'chatballon',
  'value.sparkle': 'glinstering',
  'value.bag': 'tas',
  'value.full': 'volledig',
  'value.middle': 'in het midden',
  'value.bottom': 'onderaan',
  'value.rows': 'rijen',
  'value.grid': 'raster',
  'value.carousel': 'een rij waar je doorheen veegt',
  'value.contain': 'volledige foto',
  'value.cover': 'gevulde tegel',
  'value.portrait': 'staand',
  'value.landscape': 'liggend',
  'value.none': 'geen',
  'value.pop': '+1 die opstijgt',
  'value.ring': 'rimpeling',
  'value.blink': 'knipperen',
  'value.chips': 'knoppen op een rij',
  'value.menu': 'een lijst',
  'value.rounded': 'afgerond',
  designCardTitle: 'Winkelassistent voor {name}',
  designCardSaved: 'Versie {version} opgeslagen · nog niet gepubliceerd',
  designCardSavedFirst: 'Ontwerp opgeslagen · de eerste versie krijgt dit ontwerp',
  designSaving: 'Ontwerp wordt opgeslagen…',
  selectedAssistantPart: 'Geselecteerd: een onderdeel van de assistent',
  loading: 'Storefront wordt geladen…',
  disabledTitle: 'Storefront is niet ingeschakeld in deze werkruimte.',
  disabledBody:
    'Het is in vroege toegang en komt gefaseerd beschikbaar. Vraag je Poneva-contactpersoon wanneer het voor jullie beschikbaar komt.',
  loadFailed: 'Storefront kon niet worden geladen.',
  startTitle: 'Een winkelassistent voor je winkel',
  startBody:
    'Poneva leest je winkel zoals een klant die gebruikt: zoeken, producten, de winkelwagen en de chat die je al hebt. Dit duurt ongeveer 10 tot 30 minuten en gebruikt credits van je bedrijf; ondertussen kun je gewoon doorwerken.',
  urlLabel: 'Adres van je winkel',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Mijn winkel uitlezen',
  starting: 'Wordt gestart…',
  startFailed:
    'Deze winkel kon niet worden uitgelezen. Controleer het adres en probeer het opnieuw.',
  tabConversations: 'Gesprekken',
  tabOverview: 'Overzicht',
  allAssistants: 'Alle assistenten',
  allAssistantsNote: '{assistants} assistenten · {sites} sites',
  allAssistantsMenuNote: '{count} gesprekken deze week',
  marketLabel: 'Markt',
  allMarkets: 'Alle markten',
  allMarketsNote: '{count} sites',
  scopeIn: '{assistant} in {market}',
  askAbout: 'Chat vragen over {scope}',
  overviewAssistantsTitle: 'Assistenten en markten',
  overviewMarketsTitle: 'Markten',
  overviewAssistantsOnly: 'Assistenten',
  overviewIdle: '{count} assistenten hebben nog geen gesprekken',
  allAssistantsCount: '{assistants} assistenten',
  overviewFailed: 'Het overzicht kon niet worden geladen.',
  colConversations: 'Gesprekken',
  colCart: 'In winkelwagen',
  colCheckout: 'Afrekenen',
  colPerson: 'Persoon',
  colCost: 'Credits',
  rowMarkets: '{count} markten',
  rowVersion: 'versie {version}',
  rowAria:
    '{name}, {sub}: {conversations} gesprekken, {cart} in winkelwagen, {checkout} naar afrekenen, {person} naar een persoon gevraagd, credits {cost}',
  versionPublishedAt: 'gepubliceerd {at}',
  designColorHeader: 'Koptekst',
  designColorHeaderInk: 'Tekst van de koptekst',
  designGroupTranslations: 'Teksten in andere talen',
  topicsTitle: 'Waar klanten naar vragen',
  topicsNote: 'Onderwerpen die Chat in de eerste vragen vond',
  startPagesTitle: 'Waar gesprekken beginnen',
  startPagesOne: 'Elk gesprek begon op één soort pagina: {page}.',
  'startPage.product': 'Productpagina’s',
  'startPage.search': 'Zoekresultaten',
  'startPage.start': 'Startpagina',
  'startPage.cart': 'Winkelwagen',
  'startPage.other': 'Andere pagina’s',
  addMarketInChat: 'Een markt toevoegen in de chat',
  marketsTitle: 'Markten',
  marketsBody:
    'Eén assistent en één ontwerp. Elke markt heeft een eigen taal, valuta, producten, winkelwagen en chat met een medewerker.',
  marketChecked: 'gecontroleerd {when}',
  'marketStatus.ready': 'Gereed',
  'marketStatus.found': 'Niet toegevoegd',
  'marketStatus.checking': 'Wordt gecontroleerd',
  'marketStatus.failed': 'Moet worden opgelost',
  addMarketPrompt:
    'Voeg een andere markt toe aan de winkelassistent voor {name} ({domain}): zoek de andere landen, talen of sites van de winkel en vraag me welke ik moet toevoegen.',
  tabWidget: 'Widget',
  tabInstall: 'Installeren',
  tabSetup: 'Aan de slag',
  statusReading: 'Je winkel wordt uitgelezen',
  readingProducts: 'Producten opnieuw uitlezen',
  readingCart: 'Winkelwagen opnieuw controleren',
  readingChat: 'De route naar je eigen chat controleren',
  readingMarket: 'De markt {market} toevoegen',
  readingMarketAny: 'Een markt toevoegen',
  statusReady: 'Gereed om te installeren',
  statusFailed: 'Uitlezen gestopt',
  statusLive: 'Live · versie {version} op {domain}',
  statusPublished: 'Gepubliceerd · versie {version} · nog niet op je site',
  statusPublishedUnverified: 'Gepubliceerd · versie {version} · kon je site niet controleren',
  stepOpen: 'Site openen',
  stepSearch: 'Zoeken',
  stepProducts: 'Producten',
  stepCart: 'Winkelwagen',
  stepChat: 'Je bestaande chat',
  stepLook: 'Je stijl en logo',
  stepChecks: 'Controleren of het werkt',
  stepReady: 'Gereed',
  stepDetailOpen: '{domain} openen',
  stepDetailSearch: 'Producten van de winkel zoeken',
  stepDetailProducts: 'Producten, prijzen, foto’s, kleuren en maten uitlezen',
  stepDetailCart: 'Winkelwagen proberen: toevoegen, teruglezen, verwijderen en leegmaken',
  stepDetailChat: 'Je bestaande chat zoeken',
  stepDetailLook: 'Logo en kleuren van de winkel uitlezen',
  stepDetailChecks:
    'Zoeken, producten, browsen en de winkelwagen uitproberen zoals klanten dat doen',
  stepDetailChecksProducts: 'Productzoekopdrachten, browsen, foto’s, kleuren en maten controleren',
  stepDetailChecksCart:
    'Toevoegen, teruglezen, verwijderen en leegmaken van de winkelwagen en het uitlezen van producten controleren',
  stepDetailChecksChat:
    'De overdracht naar je bestaande chat en het uitlezen van producten controleren',
  stepDetailChecksMarket: 'Producten, prijzen en winkelwagen van deze markt controleren',
  nowRunning: 'Nu: {step}',
  nowProgress: 'Stap {n} van {total} · tot nu toe {minutes} min.',
  nowReady: 'Je winkel is klaar voor een assistent',
  nowReadyBody: 'Zoeken, producten en de winkelwagen werken.',
  nowLastRead: 'Laatst uitgelezen {when}',
  nowUsually: 'meestal {min}–{max}',
  stepsRead: 'Uitgelezen {when} · Stappen tonen',
  nowFailed: 'Uitlezen gestopt',
  factAnswers: 'Beantwoordt klanten met',
  costCounting: 'Wordt geteld…',
  factPaid: 'Betaald uit',
  paidCompany: 'Bedrijfscredits',
  creditsAmount: '{amount} credits',
  creditsOne: '{amount} credit',
  creditsUnder: 'minder dan {amount} credits',
  stopRun: 'Uitvoering stoppen',
  stopping: 'Wordt gestopt…',
  closeNote: 'Je kunt deze pagina verlaten. De uitvoering gaat door.',
  readyNote: 'Volgende stap: probeer je widget en installeer hem daarna.',
  buildWidget: 'Widget proberen',
  readAgain: 'Winkel opnieuw uitlezen',
  foundTitle: 'Wat er is gevonden',
  foundProducts: 'Producten en prijzen uitgelezen',
  foundSearch: 'Zoeken werkt',
  foundCart: 'Toevoegen aan winkelwagen werkt',
  foundChat: 'Je {vendor}-chat',
  foundNoChat: 'Geen eigen chat',
  foundLook: 'Je kleuren en logo',
  widgetTitle: 'Je widget',
  widgetBody:
    'Je assistent in jouw kleuren. Probeer hem hier: hij antwoordt met je echte producten. Er wordt niets aan een echte winkelwagen toegevoegd.',
  widgetLoading: 'Voorbeeld wordt geladen…',
  widgetFailed: 'Het voorbeeld is niet geladen.',
  widgetNotReady: 'De widget is klaar zodra de stappen onder Aan de slag zijn afgerond.',
  installLineTitle: 'Voeg deze regel toe aan je site',
  installLineBody:
    'Dit werkt op elk platform: in de head van je pagina’s, op Shopify in theme.liquid direct vóór </head>. Er wordt niets getoond totdat je publiceert.',
  installLineBodyLive:
    'Dit werkt op elk platform: in de head van je pagina’s, op Shopify in theme.liquid direct vóór </head>. Klanten krijgen daarna versie {version} te zien.',
  installPolicyAsk: 'Stelt je site een Content Security Policy in?',
  installPolicy:
    'Als je site een Content Security Policy instelt, moet je ook {origin} toestaan in {directives}, anders kan de assistent niet starten.',
  installPolicyRefuses:
    'De Content Security Policy van je site weigert de assistent: sta {origin} toe in {directives}, anders start er niets.',
  installPolicyNonce:
    'Je beleid voert alleen scripts uit die het vertrouwt. Voeg daarom de regel toe met de nonce van je pagina’s.',
  installPolicyEval: "De assistent heeft ook 'unsafe-eval' nodig in script-src.",
  copy: 'Kopiëren',
  copied: 'Gekopieerd',
  domainsTitle: 'Sites waarop hij antwoordt',
  domainsBody: 'Op elke andere site blijft hij stil, zelfs als die de regel kopieert.',
  lineFound: 'Regel gevonden',
  lineNotSeen: 'Nog niet op de pagina',
  lineUnverified:
    'Kon niet controleren: de site blokkeert geautomatiseerde bezoeken. De assistent werkt nog steeds voor klanten.',
  lineChecked: 'gecontroleerd {when}',
  lineCheckAgain: 'Opnieuw controleren',
  lineChecking: 'Pagina wordt gecontroleerd…',
  installCheckBusy: 'De controle is nu bezig. Probeer het zo meteen opnieuw.',
  publishTitle: 'Publiceren',
  publishBody: 'Klanten op {domain} zien hem meteen. Je kunt op elk moment depubliceren.',
  publishButton: 'Versie {version} publiceren',
  publishConfirm: 'Versie {version} van {name} publiceren?',
  publishNow: 'Publiceren',
  notNow: 'Niet nu',
  publishing: 'Wordt gepubliceerd…',
  liveTitle: 'Gepubliceerd: versie {version}',
  liveSince: 'Sinds {since}',
  unpublish: 'Depubliceren',
  unpublishConfirm:
    'Versie {version} van je site halen? Klanten zien de assistent niet meer totdat je opnieuw publiceert.',
  publishedLine: 'Nu gepubliceerd: versie {version}, sinds {since}',
  usageTitle: 'Gebruik',
  usageBody: 'Hij gebruikt credits van je bedrijf, net als de rest van Chat.',
  usageDiscovery: 'Winkel uitlezen',
  usageChats: 'Chats met klanten',
  usageChatsValue: '{count} chats · {cost}',
  last7: 'Afgelopen 7 dagen',
  askWeek: 'Chat vragen naar deze week',
  kpiConversations: 'Gesprekken',
  kpiVsPrevious: '{change} ten opzichte van de week ervoor',
  kpiCart: 'In winkelwagen',
  kpiCheckout: 'Naar afrekenen',
  kpiPerson: 'Naar een persoon gevraagd',
  kpiCost: 'Gebruikte credits',
  ofConversations: '{percent} van de gesprekken',
  checkoutNote: '{percent} van de gesprekken',
  personNote: '{percent} opende je chat',
  costNote: 'Ongeveer {amount} per gesprek',
  costNoteUnder: 'Minder dan {amount} per gesprek',
  sparkLabel: 'Gesprekken per dag, afgelopen {days} dagen',
  sparkDay: '{day}: {count} gesprekken',
  filterAll: 'Alle',
  filterCart: 'In winkelwagen',
  filterCheckout: 'Afrekenen',
  filterPerson: 'Persoon',
  outcomeCart: 'In winkelwagen',
  outcomeCheckout: 'Naar afrekenen',
  outcomePerson: 'Naar een persoon gevraagd',
  outcomeAnswered: 'Beantwoord',
  devicePhone: 'Telefoon',
  deviceComputer: 'Computer',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min. · Gestart op {page}',
  anonymous: 'De klant is anoniem.',
  askConversation: 'Chat vragen over dit gesprek',
  showOlderVersions: 'Eerdere versies tonen',
  showOlder: 'Oudere gesprekken tonen',
  filterCountsWeek: 'Aantallen: afgelopen 7 dagen',
  readAlreadyHere:
    'Deze winkel staat hier al, dus er is niets nieuws gestart en er zijn geen credits gebruikt.',
  readOpenShop: 'Openen',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Wat kunnen we leren van dit gesprek?',
  askWeekMessage: 'Wat kunnen we leren van de gesprekken van deze week?',
  conversationsChipOne: '{name} · gesprek van {when}',
  conversationsChipWeek: '{name} · deze week',
  conversationsChipWeekIn: '{name} · {market} · deze week',
  removeConversations: 'Gesprekken verwijderen',
  noConversations: 'Nog geen gesprekken',
  noConversationsBody: 'Wanneer klanten de assistent gebruiken, verschijnen hun gesprekken hier.',
  pickConversation: 'Kies een gesprek om te lezen.',
  eventCheckout: 'Naar de checkout gegaan',
  eventPerson: 'Je chat geopend',
  showMore: 'Meer tonen',
  urlInvalid: 'Gebruik het HTTPS-adres van je winkel, bijvoorbeeld https://www.jouwshop.nl.',
  errorNoCompany:
    'Storefront heeft een bedrijfswerkruimte nodig. Vraag je beheerder om je toe te voegen.',
  errorPublishPermission:
    'Alleen mensen die voor je bedrijf mogen publiceren kunnen dit doen. Vraag je beheerder.',
  actionFailed: 'Dat is niet gelukt. Probeer het opnieuw.',
  copyFailed: 'Kopiëren is niet gelukt. Selecteer de regel en kopieer hem zelf.',
  reasonCancelled: 'Je hebt de uitvoering gestopt.',
  reasonFailed:
    'Het uitlezen van je winkel kon niet worden voltooid, dus de assistent is nog niet klaar.',
  reasonFailedLive:
    'Het uitlezen van je winkel kon niet worden voltooid. De gepubliceerde assistent blijft werken zoals hij is.',
  reasonFailedKept:
    'Het uitlezen van je winkel kon niet worden voltooid. Daarom is er niets veranderd: versie {version} blijft zoals ze was.',
  statusReadStopped: 'Gereed · het laatste uitlezen is gestopt',
  reasonStepMarked: 'De gestopte stap staat hieronder gemarkeerd.',
  reasonReadAgain: 'Opnieuw uitlezen begint met wat deze uitlezing heeft geleerd.',
  reasonNotAStore:
    'Dit adres lijkt niet op een webshop: er waren geen producten om te zoeken. Controleer het adres of probeer het hoofddomein van de winkel.',
  reasonTooSlow:
    'De zoekfunctie van je winkel antwoordt te langzaam voor een winkelassistent: het duurde meer dan 12 seconden. Een snellere zoekfunctie of later opnieuw uitlezen kan helpen.',
  reasonOurSide:
    'Het uitlezen stopte door een probleem bij ons, niet in je winkel, dus de assistent is nog niet klaar.',
  reasonOurSideLive:
    'Het uitlezen stopte door een probleem bij ons, niet in je winkel. De gepubliceerde assistent blijft werken zoals hij is.',
  reasonOurSideKept:
    'Het uitlezen stopte door een probleem bij ons, niet in je winkel. Daarom is er niets veranderd: versie {version} blijft zoals ze was.',
  reasonTryLater: 'Probeer de winkel over een tijdje opnieuw uit te lezen.',
  errorNoCredits:
    'Er zijn nu geen credits hiervoor. Een eigenaar van het bedrijf kan het abonnement en de limieten bekijken onder Usage.',
  errorReadUnavailable:
    'Deze winkel kan nu niet worden uitgelezen. Probeer het over een paar minuten opnieuw.',
  reasonTimeout:
    'Het uitlezen bereikte de tijdslimiet voordat het klaar was. Opnieuw uitlezen gaat verder met wat al is gecontroleerd.',
  retrying: 'Wordt opnieuw gestart…',
  readTitleChecks: 'Opgeslagen uitlezing opnieuw controleren',
  checksScopeLabel: 'Wordt gecontroleerd',
  readScopeChecks:
    'Zoekopdrachten van klanten, producten, browsen en de winkelwagen, op wat de laatste uitlezing opsloeg. De winkel wordt niet opnieuw uitgelezen.',
  readScopeChecksProducts:
    'Productzoekopdrachten, browsen, foto’s, kleuren en maten, op wat de laatste uitlezing opsloeg. De winkel wordt niet opnieuw uitgelezen.',
  readScopeChecksCart:
    'Toevoegen, teruglezen, verwijderen en leegmaken van de winkelwagen en het uitlezen van producten, op wat de laatste uitlezing opsloeg. De winkel wordt niet opnieuw uitgelezen.',
  readScopeChecksChat:
    'De overdracht naar je bestaande chat en het uitlezen van producten, op wat de laatste uitlezing opsloeg. De winkel wordt niet opnieuw uitgelezen.',
  readScopeChecksMarket:
    'Producten, prijzen en winkelwagen van de markt, op wat de laatste uitlezing opsloeg. De winkel wordt niet opnieuw uitgelezen.',
  checksCost: 'Alleen de nieuwe controles gebruiken bedrijfscredits',
  checksAfter:
    'Slaat een nieuw concept op als de controles slagen. Klanten zien niets totdat je publiceert.',
  checksAfterLive:
    'Slaat een nieuw concept op als de controles slagen. De gepubliceerde assistent blijft werken en klanten zien de wijziging pas nadat je publiceert.',
  checksStarted:
    'De controles zijn gestart. Vraag Chat om het nieuwe concept te controleren zodra het klaar is.',
  checkingSavedWork: 'Het opgeslagen werk wordt opnieuw gecontroleerd',
  checkAgain: 'Opnieuw controleren',
  checkingAgain: 'Wordt opnieuw gecontroleerd…',
  reasonCheckAgain:
    'Opnieuw controleren test het opgeslagen werk nog een keer, zonder de winkel opnieuw uit te lezen. Alleen de nieuwe controles gebruiken bedrijfscredits.',
  fixWithChat: 'Met Chat oplossen',
  fixReadPrompt:
    'Het uitlezen van {name} ({domain}) is gestopt. Kijk wat er is gebeurd en help me het werkend te krijgen: vertel me wat je vindt, stel voor wat ik het uitlezen kan meegeven en controleer het daarna opnieuw of lees opnieuw uit met die tip, zo weinig van de winkel als mogelijk.',
  fixReadPromptStep:
    'Het uitlezen van {name} ({domain}) is gestopt bij ‘{step}’. Kijk wat er is gebeurd en help me het werkend te krijgen: vertel me wat je vindt, stel voor wat ik het uitlezen kan meegeven en controleer het daarna opnieuw of lees opnieuw uit met die tip, zo weinig van de winkel als mogelijk.',
  stepStateDone: 'klaar',
  stepStateRunning: 'bezig',
  stepStateWaiting: 'nog niet gestart',
  stepStateFailed: 'gestopt',
  newVersionTitle: 'Versie {version} is klaar',
  newVersionBody: 'Publiceer haar om versie {live} te vervangen.',
  newVersionBodyLive: 'Publiceer haar om versie {live} op {domain} te vervangen.',
  previewSize: 'Voorbeeldgrootte',
  designWithChat: 'Ontwerp openen',
  designReviewBody: 'Bekijk het huidige ontwerp en vertel Chat wat je wilt aanpassen.',
  designWhileTitle: 'Ontwerp hem terwijl we je winkel uitlezen',
  designWhileBody:
    'Chat kan samen met jou het uiterlijk, de teksten en het logo van de assistent vormgeven. Het ontwerp is klaar zodra het uitlezen klaar is.',
  designPreviewNote: 'Een voorbeeld met voorbeeldproducten totdat je winkel is uitgelezen.',
  proposedDesign: 'Voorgesteld ontwerp',
  proposedDesignNote:
    'De widget met deze wijziging en voorbeeldproducten. Goedkeuren slaat hem op als nieuw concept; publiceren blijft op de Storefront-pagina.',
  lineSources: 'Uit de pagina’s van de winkel:',
  readAgainConfirm:
    '{name} opnieuw uitlezen? Dat duurt ongeveer 10 tot 30 minuten en gebruikt bedrijfscredits.',
  readAgainLive: 'De gepubliceerde assistent blijft werken totdat je de nieuwe versie publiceert.',
  readAgainNow: 'Opnieuw uitlezen',
  switchStore: 'Winkel wisselen of toevoegen',
  storesLabel: 'Je winkels',
  addStore: 'Nog een winkel toevoegen',
  backToStores: 'Terug naar je winkels',
  storeLive: 'Gepubliceerd',
  storeReady: 'Gereed',
  storeReading: 'Wordt uitgelezen',
  storeStopped: 'Gestopt',
  actionDesignTitle: 'Ontwerp van de winkelassistent wijzigen',
  actionKnowledgeTitle: 'Wat de winkelassistent weet wijzigen',
  readTitleProducts: 'Producten opnieuw uitlezen',
  readTitleCart: 'Winkelwagen opnieuw controleren',
  readTitleTheirChat: 'De route naar je eigen chat opnieuw controleren',
  readTitleAll: 'De hele winkel opnieuw uitlezen',
  readTitleStore: 'Een nieuwe winkel uitlezen',
  readTitleMarket: 'Een markt toevoegen',
  readShopLabel: 'Winkel',
  readScopeLabel: 'Wordt uitgelezen',
  readTimeLabel: 'Duur',
  readCostLabel: 'Kosten',
  readAfterLabel: 'Daarna',
  readScopeProducts: 'Producten zoeken en sorteren, productfoto’s, kleuren en maten',
  readScopeCart: 'Producten aan de winkelwagen toevoegen en eruit verwijderen',
  readScopeTheirChat: 'Hoe klanten je eigen chat bereiken',
  readScopeAll: 'Producten, winkelwagen, eigen chat en markten',
  readScopeStore: 'Producten, prijzen en maten, winkelwagen, eigen chat, stijl en markten',
  readScopeMarket: '{market}: producten, prijzen en winkelwagen',
  readTime: 'Meestal {min}–{max} minuten',
  readCost: 'Gebruikt bedrijfscredits',
  readCostLast:
    'Gebruikt bedrijfscredits · de laatste uitlezing van deze winkel gebruikte {credits}',
  readCostLastPartial:
    'Gebruikt bedrijfscredits · de laatste uitlezing van deze winkel is vroeg gestopt en gebruikte minstens {credits}',
  readAfter: 'Slaat een nieuw concept op. Klanten zien niets totdat je publiceert.',
  readAfterLive:
    'Slaat een nieuw concept op. De gepubliceerde assistent blijft werken en klanten zien de wijziging pas nadat je publiceert.',
  readAfterNew: 'Slaat een eerste concept op om uit te proberen. Er wordt niets gepubliceerd.',
  readCartNote:
    'Om de winkelwagen te controleren wordt een testartikel toegevoegd en weer verwijderd. Er wordt nooit afgerekend.',
  readFocusLabel: 'Wat Chat het uitlezen liet oplossen',
  readFooter: 'Er start niets totdat je goedkeurt. Je kunt het lezen onder Aan de slag stoppen.',
  readStartedAt:
    'Gestart {time} · meestal {min}–{max} minuten. Vraag Chat om het nieuwe concept te controleren zodra het klaar is.',
  readFocusAll: 'Alles tonen',
  readFocusLess: 'Minder tonen',
  readStart: 'Uitlezen starten',
  addToSite: 'Aan je site toevoegen',
  readTab: 'Aanvraag',
  readStarted:
    'Het uitlezen is gestart. Vraag Chat om het nieuwe concept te controleren zodra het klaar is.',
  readFollow: 'Volgen onder Aan de slag',
  proposedDesignLive:
    'De echte voorbeeldweergave van je winkel in dit ontwerp. Stel een vraag: er wordt niets aan een echte winkelwagen toegevoegd. Goedkeuren slaat het op als nieuw concept; publiceren blijft op de Storefront-pagina.',
  designChanges: 'Wijzigingen',
  designReset: 'terug naar de standaard',
  designGroupColors: 'Kleuren',
  designGroupType: 'Lettertypen',
  designGroupShape: 'Vorm',
  designGroupLauncher: 'Knop op de pagina',
  designGroupPanel: 'Koptekst',
  designGroupProducts: 'Producten',
  designGroupCart: 'Winkelwagen van de winkel',
  designGroupCopy: 'Teksten',
  designGroupLogo: 'Logo',
  designGroupCss: 'Aangepaste CSS',
  designColorBrand: 'Merk',
  designColorActions: 'Toevoegen en verzenden',
  designColorPanel: 'Paneel',
  designColorSurface: 'Oppervlakken',
  designColorInk: 'Tekst',
  designColorMuted: 'Secundaire tekst',
  designColorLine: 'Lijnen',
  designColorBubble: 'Berichten van klanten',
  designColorBubbleInk: 'Tekst van klanten',
  designActionsBrand: 'Merkleur',
  designActionsInk: 'Tekstkleur',
  designWordsName: 'Naam',
  designWordsGreeting: 'Begroeting',
  designWordsStarters: 'Startvragen',
  designWordsLauncher: 'Label van de knop',
  designWordsPlaceholder: 'Plaatsaanduiding',
  designWordsAi: 'AI-label',
  designWordsPerson: 'Link naar een persoon',
  noteNew: 'Nieuw',
  noteChanged: 'Gewijzigd',
  noteRemoved: 'Verwijderd',
  noteSourceKnowledge: 'Kennispagina: {title}',
  noteSourceFile: 'Bestand: {name}',
  noteSourceChat: 'Uit de chat',
  knowledgeApprovalNote:
    'Klanten kunnen alles in deze notities lezen. Goedkeuren slaat een nieuw concept op: stel vragen onder Widget en publiceer het daarna onder Installeren.',
  profileTitle: 'Over deze winkel',
  profileChecked: 'Gecontroleerd {date}',
  profileSources: 'Geschreven op basis van',
  profileSample: '{count} producten bekeken voor het prijsniveau',
  profileSampleOne: '{count} product bekeken voor het prijsniveau',
  profileAssistant: 'Wat de assistent hier kan',
  profileTeam: 'Geschreven door jullie team',
  profileEmpty:
    'Nog niet geschreven. Vraag Chat een kennisronde te doen: die schrijft dit op basis van jullie eigen pagina’s en wat hij op jullie site zag, en jullie kunnen eigen regels toevoegen.',
  profileWithChat: 'Toevoegen of wijzigen met Chat',
  profilePrompt:
    'Help me verbeteren wat de winkelassistent voor {name} ({domain}) over onze winkel als geheel weet: wat we verkopen, waar we om bekendstaan, voor wie we er zijn en wat klanten hier kunnen doen. Lees eerst het profiel Over deze winkel en vertel me wat er ontbreekt of niet klopt. Alles wat ik je over de winkel vertel dat op geen enkele pagina staat, komt erin als regel van ons team.',
  profilePartNavigation: 'Afdelingen',
  profilePartWelcome: 'Begroeting',
  profilePartMarkets: 'Markten',
  profilePartFunctions: 'Wat de assistent kan',
  profilePartProducts: 'Producten',
  profilePartPages: 'Servicepagina’s',
  profileCleared: 'Wist het geschreven profiel',
  knowledgeTitle: 'Wat hij weet',
  knowledgeBody:
    'Naast je producten, prijzen en voorraad, die hij altijd live uit je winkel leest.',
  knowledgeEmpty:
    'Tot nu toe alleen je catalogus. Voeg met Chat informatie over levering, retourneren of betalen toe vanuit je eigen pagina’s of Kennis.',
  knowledgeWithChat: 'Toevoegen met Chat',
  knowledgeChangeWithChat: 'Wijzigen met Chat',
  knowledgePrompt:
    'Help me kiezen wat de winkelassistent voor {name} ({domain}) klanten vertelt naast onze producten, zoals levering, retourneren en betalen. Zoek het op onze eigen pagina’s of in Kennis en stel notities voor die ik kan goedkeuren.',
  pagesTitle: 'Pagina’s die hij leest',
  pagesBody:
    'De pagina’s waar de notities uit komen. Poneva controleert ze elke dag en Chat laat je weten wanneer er een verandert.',
  pagesEmpty:
    'Nog geen pagina’s. Hier verschijnen de pagina’s die het lezen van de winkel vindt en de pagina’s die je in Chat noemt.',
  pageFound: 'gevonden door Poneva',
  pageLinkedFrom: 'gelinkt vanaf {page}',
  pageAddedByYou: 'door jou toegevoegd',
  pageFromNote: 'genoemd in een notitie',
  pageNoteOne: '1 notitie',
  pageNotesMany: '{count} notities',
  pageStatusOk: 'Actueel',
  pageStatusChanged: 'Gewijzigd',
  pageStatusUnreadable: 'Kan niet worden gelezen',
  pageStatusUnchecked: 'Nog niet gecontroleerd',
  pagesChangeWithChat: 'Wijzigen met Chat',
  pagesUpdateWithChat: 'Notities bijwerken met Chat',
  pagesPrompt:
    'Help me om te wijzigen welke pagina’s de shoppingassistent van {name} ({domain}) leest voor de notities. Laat me zien welke pagina’s de assistent nu gebruikt en vraag me welke ik moet toevoegen of verwijderen.',
  pagesUpdatePrompt:
    'Sommige pagina’s van {name} ({domain}) zijn gewijzigd of kunnen niet meer worden gelezen sinds de notities zijn gemaakt. Laat me zien welke, en maak de notities opnieuw op basis daarvan als concept dat ik kan bekijken.',
  viewCompare: 'Vergelijking',
  lookDeviceLabel: 'Vergelijken op',
  lookShop: 'Je winkel',
  lookShopCaption: 'Startpagina, uitgelezen {when}',
  lookWidget: 'De assistent',
  lookWidgetCaption: 'Versie {version}',
  lookLoading: 'De assistent naast je winkel plaatsen…',
  lookFailed: 'De vergelijking kon nu niet worden gemaakt.',
  lookNoShopPicture: 'Bij het uitlezen is geen afbeelding van je winkel op deze breedte bewaard.',
  lookNoneTitle: 'De eigen stijl van je winkel is niet bewaard',
  lookNoneBody:
    'Dit uitlezen heeft die niet vastgelegd. Lees de winkel opnieuw uit via Aan de slag, dan worden het logo, de kleuren en de pagina’s ervan bewaard om te vergelijken.',
  lookColoursShop: 'Kleuren die je winkel gebruikt',
  lookColoursAssistant: 'Kleuren van de assistent',
  lookRoleHeader: 'Koptekst',
  lookRoleBrand: 'Merk',
  lookSame: 'Hetzelfde als de kleur {colour} van je winkel ({source})',
  lookOff:
    'Dicht bij de kleur {colour} van je winkel ({source}) maar niet hetzelfde, verschil {delta}',
  lookOwn: 'Eigen kleur: je winkel gebruikt die niet',
  lookNeutral:
    'Je winkel is hiervoor niet geladen: alleen wat het uitlezen heeft bewaard, wordt getoond.',
  lookFromLogo: 'logo',
  lookFromTheme: 'themakleur',
  lookFromTile: 'tegelkleur',
  lookFromMask: 'vastgezet pictogram',
  lookFromManifest: 'app-manifest',
  lookFromHeader: 'koptekst',
  lookFromButton: 'knoppen',
  lookFromVariable: 'stijlkleur',
  lookFix: 'Met Chat oplossen',
  lookFixPrompt:
    'De kleur van de assistent voor {role} is {colour}, dicht bij de kleur {exact} van je winkel ({source}) maar niet hetzelfde. Gebruik de exacte kleur van je winkel, {exact}.',
  lookSwatch: 'Kleur {colour}',
  errorDesignChecks: 'Deze versie kwam niet door de ontwerpcontroles.',
  designSamplesBlocked:
    'Kaartindelingen kunnen hier niet worden bewezen: je site blokkeert geautomatiseerde bezoeken. Kleuren, lettertypen en het logo werken nog steeds.',
  designCheckLabel: 'Een AI-label is te vaag om te lezen.',
  designCheckReach: 'Een knop is onbereikbaar.',
  designCheckSize: 'Een deel van de tekst is te klein.',
  designCheckOverflow: 'Het paneel scrolt zijwaarts.',
  designCheckRender: 'De controles konden niet worden uitgevoerd. Probeer het zo opnieuw.',
  fixDesignPrompt:
    'Ik probeerde de nieuwste versie van {name} ({domain}) te publiceren, maar die kwam niet door de ontwerpcontroles: {facts}. Herstel het ontwerp in deze chat en controleer het op telefoon en computer. Publiceer het niet.',
};

const et: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Ostuabiline',
  designUnconfirmed:
    'Chat peatus enne kujunduse salvestamise kinnitamist. Assistendi lõuendil kuvatakse uusim versioon.',
  errorNewerVersion:
    'Pärast selle avamist salvestati uuem versioon. Vaata seda esmalt ja avalda siis.',
  canvasSelectHint: 'Assistendi muutmiseks vestluses klõpsa selle osal · peatamiseks vajuta Esc',
  canvasDraft: 'Versioon {version} · veel avaldamata',
  canvasDraftOverLive: 'Versioon {version} · versioon {live} on avaldatud',
  canvasLive: 'Versioon {version} · avaldatud',
  canvasNoVersion: 'Esimene versioon luuakse pärast poe lugemise lõpetamist',
  canvasPublish: 'Avalda Storefronti lehel',
  canvasFrameTitle: '{name} ostuabiline',
  viewLabel: 'Vaade',
  photosGood: '{count} tootefotot: terved ja teravad',
  photosGoodOne: 'Tootefoto on terve ja terav',
  canvasMissingTitle: 'See ostuabiline ei ole selles tööruumis',
  canvasMissingBody: 'See kuulub teise tööruumi või on eemaldatud.',
  canvasMissingLink: 'Vaata selle tööruumi abilisi',
  photosLabel: 'Tootefotod',
  photosSmall: '{count} on oma paanis liiga väikesed',
  photosCut: '{count} on ära lõigatud',
  photosSoft: '{count} on suurendatud ja seetõttu udused',
  photosMissing: '{count} ilma fotota',
  photosFix: 'Paranda Chatiga',
  photosPrompt:
    'Muuda kaartidel olevad tootefotod õigeks: iga foto peab olema terviklik, oma paanis piisavalt suur ja terav.',
  photosFacts:
    '{total} fotot: {small} oma paanis liiga väikesed, {cut} ära lõigatud, {soft} suurendatud ja udused, {missing} fotota. Suurused: {sizes}',
  viewDesign: 'Kujundus',
  viewVersions: 'Versioonid',
  versionsLoading: 'Versioonide laadimine…',
  versionsFailed: 'Versioone ei saanud laadida.',
  versionTitle: 'Versioon {version}',
  versionLive: 'Avaldatud',
  versionChecked: 'Kontrollitud',
  versionToday: 'Täna',
  versionYesterday: 'Eile',
  versionEarlier: 'Varem',
  versionByPerson: 'Küsis {name}',
  versionByChat: 'Küsitud vestluses',
  versionFromReading: 'Lugemisest {domain}',
  versionPublishedBy: 'avaldas {name}, {at}',
  versionPublish: 'Avalda',
  versionRestore: 'Taasta…',
  versionRestoreTitle: 'Kas taastada versioon {version}?',
  versionRestoreBody: 'Sellest saab versioon {next}, uus mustand. Sait ei muutu enne avaldamist.',
  versionRestoreConfirm: 'Taasta',
  versionRestoreCancel: 'Tühista',
  versionRestored: 'Taastatud versioonina {version}',
  versionRestoreFailed: 'Versiooni ei saanud taastada.',
  compareTitle: 'Võrdle versiooni {to} versiooniga {from}',
  compareTitleLive: 'Võrdle versiooni {to} avaldatud versiooniga {from}',
  compareHint: 'Võrdlemiseks lohista',
  compareLabel: 'Võrdle versioone {from} ja {to}',
  compareTagLive: 'v{version} · avaldatud',
  compareTag: 'v{version}',
  changesTitle: 'Mis muutus versioonist {from} versiooniks {to}',
  changesNone: 'Kujundused on samad. Muutusid ainult märkmed või poe andmed.',
  changeAdded: 'Lisatud',
  changeRemoved: 'Eemaldatud',
  changeChanged: 'Muudetud',
  changeDrawn: 'joonistatud logo',
  changeCss: 'oma CSS',
  changeYes: 'jah',
  changeNo: 'ei',
  'field.colors.brand': 'Brändi värv',
  'field.colors.actions': 'Nupu värv',
  'field.colors.panel': 'Paneeli värv',
  'field.colors.surface': 'Tausta värv',
  'field.colors.ink': 'Teksti värv',
  'field.colors.muted': 'Vaiksema teksti värv',
  'field.colors.line': 'Joone värv',
  'field.colors.bubble': 'Ostleja sõnumi värv',
  'field.colors.bubbleInk': 'Ostleja sõnumi tekst',
  'field.colors.header': 'Päise värv',
  'field.colors.headerInk': 'Päise teksti värv',
  'field.colors.tints': 'Pehmed toonid',
  'field.copy.assistantName': 'Nimi',
  'field.copy.greeting': 'Tervitus',
  'field.copy.placeholder': 'Küsimuse väli',
  'field.copy.starters': 'Alustusküsimused',
  'field.copy.launcherLabel': 'Avamisnupu tekst',
  'field.copy.aiLabel': 'Tehisintellekti silt',
  'field.copy.personLabel': 'Viide inimesele',
  'field.type.body': 'Teksti font',
  'field.type.display': 'Pealkirja font',
  'field.type.fontCss': 'Fondi allikas',
  'field.type.scale': 'Teksti suurus',
  'field.shape.radius': 'Nurgad',
  'field.shape.shadow': 'Vari',
  'field.shape.density': 'Vahed',
  'field.launcher.style': 'Avamisnupp arvutis',
  'field.launcher.phoneStyle': 'Avamisnupp telefonis',
  'field.launcher.side': 'Avamisnupu külg',
  'field.launcher.icon': 'Avamisnupu ikoon',
  'field.panel.header': 'Päise suurus',
  'field.panel.opening': 'Esimene ekraan',
  'field.products.layout': 'Tooted',
  'field.products.imageFit': 'Tootefotod',
  'field.products.imageRatio': 'Fotopaani kuju',
  'field.products.imagePadding': 'Fotode ümber jääv ruum',
  'field.products.imageBackground': 'Fotopaani värv',
  'field.products.picker': 'Suuruse valik',
  'field.products.showStock': 'Laoseisu näitamine',
  'field.cart.landing': 'Ostukorvi animatsioon',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Logo kuju',
  'field.logo.plate': 'Logo taust',
  'field.css': 'Kohandatud CSS',
  'value.ink': 'tint',
  'value.brand': 'brändi värv',
  'value.small': 'väike',
  'value.medium': 'keskmine',
  'value.large': 'suur',
  'value.square': 'ruudukujuline',
  'value.soft': 'pehme',
  'value.round': 'ümar',
  'value.flat': 'tasane',
  'value.deep': 'sügav',
  'value.compact': 'tihe',
  'value.comfortable': 'avar',
  'value.pill': 'sõnadega pill',
  'value.circle': 'ring',
  'value.right': 'paremal',
  'value.left': 'vasakul',
  'value.logo': 'logo',
  'value.chat': 'vestlusmull',
  'value.sparkle': 'säde',
  'value.bag': 'kott',
  'value.full': 'täislaius',
  'value.middle': 'keskel',
  'value.bottom': 'all',
  'value.rows': 'read',
  'value.grid': 'ruudustik',
  'value.carousel': 'sõrmega keritav rida',
  'value.contain': 'terviklik foto',
  'value.cover': 'täidetud paan',
  'value.portrait': 'püstine',
  'value.landscape': 'rõhtne',
  'value.none': 'puudub',
  'value.pop': 'tõusev +1',
  'value.ring': 'lainetus',
  'value.blink': 'vilkumine',
  'value.chips': 'kõrvuti nupud',
  'value.menu': 'loend',
  'value.rounded': 'ümardatud',
  designCardTitle: '{name} ostuabiline',
  designCardSaved: 'Versioon {version} salvestatud · veel avaldamata',
  designCardSavedFirst: 'Kujundus salvestatud · esimene versioon kasutab seda',
  designSaving: 'Kujunduse salvestamine…',
  selectedAssistantPart: 'Valitud: assistendi osa',
  loading: 'Storefronti laadimine…',
  disabledTitle: 'Storefront ei ole selles tööruumis sisse lülitatud.',
  disabledBody:
    'See on varajases kasutuses ja jõuab kasutajateni järk-järgult. Küsi oma Poneva kontaktisikult, millal see teieni jõuab.',
  loadFailed: 'Storefronti ei saanud laadida.',
  startTitle: 'Ostuabiline sinu poele',
  startBody:
    'Poneva loeb sinu poodi nii, nagu ostleja seda kasutab: otsing, tooted, ostukorv ja juba olemasolev vestlus. Selleks kulub umbes 10–30 minutit ja kasutatakse ettevõtte krediite, samal ajal saad edasi töötada.',
  urlLabel: 'Sinu poe aadress',
  urlPlaceholder: 'https://www.sinupood.ee',
  startButton: 'Loe minu poodi',
  starting: 'Alustamine…',
  startFailed: 'Seda poodi ei saanud lugeda. Kontrolli aadressi ja proovi uuesti.',
  tabConversations: 'Vestlused',
  tabOverview: 'Ülevaade',
  allAssistants: 'Kõik abilised',
  allAssistantsNote: '{assistants} abilist · {sites} saiti',
  allAssistantsMenuNote: '{count} vestlust sel nädalal',
  marketLabel: 'Turg',
  allMarkets: 'Kõik turud',
  allMarketsNote: '{count} saiti',
  scopeIn: '{assistant} turul {market}',
  askAbout: 'Küsi Chatilt teema {scope} kohta',
  overviewAssistantsTitle: 'Abilised ja turud',
  overviewMarketsTitle: 'Turud',
  overviewAssistantsOnly: 'Abilised',
  overviewIdle: '{count} abilisel pole veel vestlusi',
  allAssistantsCount: '{assistants} abilist',
  overviewFailed: 'Ülevaadet ei saanud laadida.',
  colConversations: 'Vestlused',
  colCart: 'Ostukorvi lisatud',
  colCheckout: 'Kassasse jõudnud',
  colPerson: 'Inimene',
  colCost: 'Krediidid',
  rowMarkets: '{count} turgu',
  rowVersion: 'versioon {version}',
  rowAria:
    '{name}, {sub}: {conversations} vestlust, {cart} ostukorvi lisamist, {checkout} kassasse jõudmist, {person} inimese poole pöördumist, {cost} krediiti',
  versionPublishedAt: 'avaldatud {at}',
  designColorHeader: 'Päis',
  designColorHeaderInk: 'Päise tekst',
  designGroupTranslations: 'Sõnad teistes keeltes',
  topicsTitle: 'Mida ostlejad küsivad',
  topicsNote: 'Teemad, mille Chat leidis esimestest küsimustest',
  startPagesTitle: 'Kus vestlused algavad',
  startPagesOne: 'Iga vestlus algas üht tüüpi lehel: {page}.',
  'startPage.product': 'Tootelehed',
  'startPage.search': 'Otsingutulemused',
  'startPage.start': 'Avaleht',
  'startPage.cart': 'Ostukorv',
  'startPage.other': 'Muud lehed',
  addMarketInChat: 'Lisa vestluses turg',
  marketsTitle: 'Turud',
  marketsBody:
    'Üks abiline ja üks kujundus. Igal turul on oma keel, valuuta, tooted, ostukorv ja vestlus inimesega.',
  marketChecked: 'kontrollitud {when}',
  'marketStatus.ready': 'Valmis',
  'marketStatus.found': 'Lisamata',
  'marketStatus.checking': 'Kontrollimine',
  'marketStatus.failed': 'Vajab parandamist',
  addMarketPrompt:
    'Lisa ostuabilisele {name} jaoks veel üks turg ({domain}): leia poe teised riigid, keeled või saidid ja küsi minult, millise neist lisan.',
  tabWidget: 'Widget',
  tabInstall: 'Paigaldamine',
  tabSetup: 'Seadistamine',
  statusReading: 'Poe lugemine',
  readingProducts: 'Toodete uuesti lugemine',
  readingCart: 'Ostukorvi uuesti kontrollimine',
  readingChat: 'Olemasoleva vestluseni jõudmise kontrollimine',
  readingMarket: 'Turu {market} lisamine',
  readingMarketAny: 'Turu lisamine',
  statusReady: 'Installimiseks valmis',
  statusFailed: 'Lugemine peatatud',
  statusLive: 'Aktiivne · versioon {version} saidil {domain}',
  statusPublished: 'Avaldatud · versioon {version} · pole veel sinu saidil',
  statusPublishedUnverified: 'Avaldatud · versioon {version} · sinu saiti ei saanud kontrollida',
  stepOpen: 'Saidi avamine',
  stepSearch: 'Otsing',
  stepProducts: 'Tooted',
  stepCart: 'Ostukorv',
  stepChat: 'Sinu olemasolev vestlus',
  stepLook: 'Sinu ilme ja logo',
  stepChecks: 'Toimivuse kontroll',
  stepReady: 'Valmis',
  stepDetailOpen: 'Saidi {domain} avamine',
  stepDetailSearch: 'Poe toodete leidmine',
  stepDetailProducts: 'Toodete, hindade, fotode, värvide ja suuruste lugemine',
  stepDetailCart: 'Ostukorvi proovimine: lisamine, kontrollimine, eemaldamine ja tühjendamine',
  stepDetailChat: 'Olemasoleva vestluse otsimine',
  stepDetailLook: 'Poe logo ja värvide lugemine',
  stepDetailChecks: 'Ostleja otsingute, toodete, sirvimise ja ostukorvi proovimine',
  stepDetailChecksProducts: 'Tooteotsingute, sirvimise, fotode, värvide ja suuruste kontrollimine',
  stepDetailChecksCart:
    'Ostukorvi lisamise, kontrollimise, eemaldamise ja tühjendamise ning toodete lugemise kontrollimine',
  stepDetailChecksChat: 'Olemasolevale vestlusele suunamise ja toodete lugemise kontrollimine',
  stepDetailChecksMarket: 'Selle turu toodete, hindade ja ostukorvi kontrollimine',
  nowRunning: 'Praegu: {step}',
  nowProgress: 'Samm {n} / {total} · kulunud {minutes} min',
  nowReady: 'Sinu pood on assistendi jaoks valmis',
  nowReadyBody: 'Otsing, tooted ja ostukorv toimivad.',
  nowLastRead: 'Viimati loetud {when}',
  nowUsually: 'tavaliselt {min}–{max}',
  stepsRead: 'Loetud {when} · Kuva sammud',
  nowFailed: 'Lugemine peatatud',
  factAnswers: 'Ostlejatele vastab mudel',
  costCounting: 'Loendamine…',
  factPaid: 'Tasub',
  paidCompany: 'Ettevõtte krediidid',
  creditsAmount: '{amount} krediiti',
  creditsOne: '{amount} krediit',
  creditsUnder: 'alla {amount} krediidi',
  stopRun: 'Peata käitamine',
  stopping: 'Peatamine…',
  closeNote: 'Võid selle lehe sulgeda. Käitamine jätkub.',
  readyNote: 'Järgmisena proovi oma widgetit ja installi see.',
  buildWidget: 'Proovi widgetit',
  readAgain: 'Loe poodi uuesti',
  foundTitle: 'Leitud',
  foundProducts: 'Loetud tooted ja hinnad',
  foundSearch: 'Otsing toimib',
  foundCart: 'Ostukorvi lisamine toimib',
  foundChat: 'Sinu {vendor} vestlus',
  foundNoChat: 'Oma vestlust ei leitud',
  foundLook: 'Sinu värvid ja logo',
  widgetTitle: 'Sinu widget',
  widgetBody:
    'Sinu värvides ostuabiline. Proovi seda siin: see vastab sinu päristoodete põhjal. Päris ostukorvi midagi ei lisata.',
  widgetLoading: 'Eelvaate laadimine…',
  widgetFailed: 'Eelvaadet ei saanud laadida.',
  widgetNotReady: 'Widget on valmis, kui vahekaardi „Seadistamine“ sammud on lõpetatud.',
  installLineTitle: 'Lisa see rida oma saidile',
  installLineBody:
    'See töötab igal platvormil: sinu lehtede päises, Shopify puhul failis theme.liquid vahetult enne </head>. Enne avaldamist ei kuvata midagi.',
  installLineBodyLive:
    'See töötab igal platvormil: sinu lehtede päises, Shopify puhul failis theme.liquid vahetult enne </head>. Seejärel näevad ostlejad versiooni {version}.',
  installPolicyAsk: 'Kas sinu sait määrab Content Security Policy?',
  installPolicy:
    'Kui sinu sait määrab Content Security Policy, luba ka {origin} jaotises {directives}, muidu ei saa assistent käivituda.',
  installPolicyRefuses:
    'Sinu saidi Content Security Policy ei luba assistenti: luba {origin} jaotises {directives}, muidu ei käivitu miski.',
  installPolicyNonce:
    'Sinu poliitika käivitab ainult usaldatud skripte, seega lisa rida koos oma lehtede nonce-väärtusega.',
  installPolicyEval: "Assistent vajab script-src-is ka väärtust 'unsafe-eval'.",
  copy: 'Kopeeri',
  copied: 'Kopeeritud',
  domainsTitle: 'Saidid, kus see vastab',
  domainsBody: 'Teistel saitidel jääb see vaikseks, isegi kui keegi rea kopeerib.',
  lineFound: 'Rida leitud',
  lineNotSeen: 'Pole veel lehel nähtav',
  lineUnverified:
    'Kontrollimine ebaõnnestus: sait blokeerib automaatsed külastused. Assistent töötab ostjatele siiski.',
  lineChecked: 'kontrollitud {when}',
  lineCheckAgain: 'Kontrolli uuesti',
  lineChecking: 'Lehe kontrollimine…',
  installCheckBusy: 'Kontroll on praegu hõivatud. Proovi hetke pärast uuesti.',
  publishTitle: 'Avaldamine',
  publishBody:
    'Saidil {domain} olevad ostlejad näevad seda kohe. Võid avaldamise igal ajal tühistada.',
  publishButton: 'Avalda versioon {version}',
  publishConfirm: 'Kas avaldada {name} versioon {version}?',
  publishNow: 'Avalda',
  notNow: 'Mitte praegu',
  publishing: 'Avaldamine…',
  liveTitle: 'Avaldatud: versioon {version}',
  liveSince: 'Alates {since}',
  unpublish: 'Tühista avaldamine',
  unpublishConfirm:
    'Kas eemaldada versioon {version} saidilt? Ostlejad ei näe assistenti enne, kui selle uuesti avaldad.',
  publishedLine: 'Praegu avaldatud: versioon {version}, alates {since}',
  usageTitle: 'Kasutus',
  usageBody: 'See kasutab ettevõtte krediite nagu Chat muu kasutuski.',
  usageDiscovery: 'Poe lugemine',
  usageChats: 'Ostlejate vestlused',
  usageChatsValue: '{count} vestlust · {cost}',
  last7: 'Viimased 7 päeva',
  askWeek: 'Küsi Chatilt selle nädala kohta',
  kpiConversations: 'Vestlused',
  kpiVsPrevious: '{change} võrreldes eelmise nädalaga',
  kpiCart: 'Ostukorvi lisatud',
  kpiCheckout: 'Kassasse jõudnud',
  kpiPerson: 'Inimese poole pöördunud',
  kpiCost: 'Kasutatud krediidid',
  ofConversations: '{percent} vestlustest',
  checkoutNote: '{percent} vestlustest',
  personNote: '{percent} avas sinu vestluse',
  costNote: 'Umbes {amount} vestluse kohta',
  costNoteUnder: 'Alla {amount} vestluse kohta',
  sparkLabel: 'Vestlused päevas, viimased {days} päeva',
  sparkDay: '{day}: {count} vestlust',
  filterAll: 'Kõik',
  filterCart: 'Ostukorvi lisatud',
  filterCheckout: 'Kassa',
  filterPerson: 'Inimene',
  outcomeCart: 'Ostukorvi lisatud',
  outcomeCheckout: 'Kassasse jõudnud',
  outcomePerson: 'Inimese poole pöördunud',
  outcomeAnswered: 'Vastatud',
  devicePhone: 'Telefon',
  deviceComputer: 'Arvuti',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Algas lehel {page}',
  anonymous: 'Ostleja on anonüümne.',
  askConversation: 'Küsi Chatilt selle vestluse kohta',
  showOlderVersions: 'Näita varasemaid versioone',
  showOlder: 'Näita vanemaid vestlusi',
  filterCountsWeek: 'Arvud: viimased 7 päeva',
  readAlreadyHere: 'See pood on juba siin, seega ei käivitatud midagi uut ega kasutatud krediite.',
  readOpenShop: 'Ava pood',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Mida saame sellest vestlusest õppida?',
  askWeekMessage: 'Mida saame selle nädala vestlustest õppida?',
  conversationsChipOne: '{name} · vestlus, {when}',
  conversationsChipWeek: '{name} · see nädal',
  conversationsChipWeekIn: '{name} · {market} · see nädal',
  removeConversations: 'Eemalda vestlused',
  noConversations: 'Vestlusi veel pole',
  noConversationsBody: 'Kui ostlejad assistenti kasutavad, kuvatakse nende vestlused siin.',
  pickConversation: 'Vestluse lugemiseks vali see.',
  eventCheckout: 'Liikus kassasse',
  eventPerson: 'Avati sinu vestlus',
  showMore: 'Näita rohkem',
  urlInvalid: 'Kasuta oma poe https-aadressi, näiteks https://www.sinupood.ee.',
  errorNoCompany: 'Storefront vajab ettevõtte tööruumi. Palu administraatoril sind lisada.',
  errorPublishPermission:
    'Seda saavad teha ainult inimesed, kes võivad ettevõtte nimel avaldada. Küsi administraatorilt.',
  actionFailed: 'See ei õnnestunud. Proovi uuesti.',
  copyFailed: 'Kopeerimine ei õnnestunud. Vali rida ja kopeeri see ise.',
  reasonCancelled: 'Peatasid käitamise.',
  reasonFailed: 'Sinu poe lugemist ei saanud lõpetada, seega pole assistent valmis.',
  reasonFailedLive:
    'Sinu poe lugemist ei saanud lõpetada. Avaldatud assistent töötab edasi endisel kujul.',
  reasonFailedKept:
    'Sinu poe lugemist ei saanud lõpetada, seega midagi ei muutunud: versioon {version} jäi samaks.',
  statusReadStopped: 'Valmis · viimane lugemine peatati',
  reasonStepMarked: 'Peatunud samm on all märgitud.',
  reasonReadAgain: 'Uuesti lugemine jätkub sellest, mida käesolev lugemine teada sai.',
  reasonNotAStore:
    'See aadress ei näi olevat veebipood: otsitavaid tooteid ei leitud. Kontrolli aadressi või proovi poe peamist domeeni.',
  reasonTooSlow:
    'Sinu poe otsing vastab ostuabilise jaoks liiga aeglaselt: selleks kulus üle 12 sekundi. Poe kiirem otsing või hiljem uuesti lugemine võib aidata.',
  reasonOurSide:
    'Lugemine peatus meie poolse probleemi tõttu, mitte sinu poe pärast, seega assistent pole veel valmis.',
  reasonOurSideLive:
    'Lugemine peatus meie poolse probleemi tõttu, mitte sinu poe pärast. Avaldatud assistent töötab edasi endisel kujul.',
  reasonOurSideKept:
    'Lugemine peatus meie poolse probleemi tõttu, mitte sinu poe pärast, seega midagi ei muutunud: versioon {version} jäi samaks.',
  reasonTryLater: 'Proovi poodi mõne aja pärast uuesti lugeda.',
  errorNoCredits:
    'Selle jaoks pole praegu krediite. Ettevõtte omanik saab paketti ja limiite kontrollida jaotises Usage.',
  errorReadUnavailable: 'Seda poodi ei saa praegu lugeda. Proovi mõne minuti pärast uuesti.',
  reasonTimeout:
    'Lugemine jõudis enne lõpetamist ajalimiidini. Uuesti lugemine jätkub juba kontrollitud kohast.',
  retrying: 'Uuesti alustamine…',
  readTitleChecks: 'Kontrolli salvestatud lugemist uuesti',
  checksScopeLabel: 'Kontrollib',
  readScopeChecks:
    'Ostlejate otsingud, tooted, sirvimine ja ostukorv – sellel, mille viimane lugemine salvestas. Poodi uuesti ei loeta.',
  readScopeChecksProducts:
    'Tooteotsingud, sirvimine, fotod, värvid ja suurused – sellel, mille viimane lugemine salvestas. Poodi uuesti ei loeta.',
  readScopeChecksCart:
    'Ostukorvi lisamine, kontrollimine, eemaldamine ja tühjendamine ning toodete lugemine – sellel, mille viimane lugemine salvestas. Poodi uuesti ei loeta.',
  readScopeChecksChat:
    'Olemasolevale vestlusele suunamine ja toodete lugemine – sellel, mille viimane lugemine salvestas. Poodi uuesti ei loeta.',
  readScopeChecksMarket:
    'Turu tooted, hinnad ja ostukorv – sellel, mille viimane lugemine salvestas. Poodi uuesti ei loeta.',
  checksCost: 'Ettevõtte krediite kasutavad ainult uued kontrollid',
  checksAfter:
    'Salvestab uue mustandi, kui kontrollid õnnestuvad. Ostlejad ei näe midagi enne avaldamist.',
  checksAfterLive:
    'Salvestab uue mustandi, kui kontrollid õnnestuvad. Avaldatud assistent töötab edasi ja ostlejad näevad muudatust alles pärast avaldamist.',
  checksStarted: 'Kontrollid on alanud. Kui uus mustand on valmis, palu Chatil seda kontrollida.',
  checkingSavedWork: 'Salvestatud tööd kontrollitakse uuesti',
  checkAgain: 'Kontrolli uuesti',
  checkingAgain: 'Kontrollitakse uuesti…',
  reasonCheckAgain:
    '„Kontrolli uuesti“ testib salvestatud tööd veel kord, ilma poodi uuesti lugemata. Ettevõtte krediite kasutavad ainult uued kontrollid.',
  fixWithChat: 'Paranda Chatiga',
  fixReadPrompt:
    'Poe {name} ({domain}) lugemine peatus. Vaata, mis juhtus, ja aita mul see tööle saada: ütle, mida leiad, soovita, mida võiksin lugemisele öelda, ning kontrolli seda siis uuesti või loe selle vihjega uuesti, lugedes poest võimalikult vähe.',
  fixReadPromptStep:
    'Poe {name} ({domain}) lugemine peatus sammul „{step}“. Vaata, mis juhtus, ja aita mul see tööle saada: ütle, mida leiad, soovita, mida võiksin lugemisele öelda, ning kontrolli seda siis uuesti või loe selle vihjega uuesti, lugedes poest võimalikult vähe.',
  stepStateDone: 'valmis',
  stepStateRunning: 'pooleli',
  stepStateWaiting: 'alustamata',
  stepStateFailed: 'peatatud',
  newVersionTitle: 'Versioon {version} on valmis',
  newVersionBody: 'Avalda see versiooni {live} asendamiseks.',
  newVersionBodyLive: 'Avalda see saidil {domain} oleva versiooni {live} asendamiseks.',
  previewSize: 'Eelvaate suurus',
  designWithChat: 'Ava kujundus',
  designReviewBody: 'Vaata praegust kujundust ja ütle Chatile, mida soovid kohandada.',
  designWhileTitle: 'Kujunda seda poe lugemise ajal',
  designWhileBody:
    'Chat saab sinuga kohe assistendi ilmet, sõnu ja logo kujundada. Kujundus on valmis, kui lugemine lõpeb.',
  designPreviewNote: 'Eelvaade näidistoodetega seni, kuni sinu pood on loetud.',
  proposedDesign: 'Pakutud kujundus',
  proposedDesignNote:
    'Selle muudatusega widget näidistoodetega. Heakskiit salvestab selle uue mustandina; avaldamine toimub Storefronti lehel.',
  lineSources: 'Poe lehtedelt:',
  readAgainConfirm:
    'Kas lugeda {name} uuesti? Selleks kulub umbes 10–30 minutit ja kasutatakse ettevõtte krediite.',
  readAgainLive: 'Avaldatud assistent töötab edasi, kuni avaldad uue versiooni.',
  readAgainNow: 'Loe uuesti',
  switchStore: 'Vaheta poodi või lisa pood',
  storesLabel: 'Sinu poed',
  addStore: 'Lisa veel üks pood',
  backToStores: 'Tagasi oma poodide juurde',
  storeLive: 'Avaldatud',
  storeReady: 'Valmis',
  storeReading: 'Lugemine',
  storeStopped: 'Peatatud',
  actionDesignTitle: 'Muuda ostuabilise kujundust',
  actionKnowledgeTitle: 'Muuda, mida ostuabiline teab',
  readTitleProducts: 'Loe tooteid uuesti',
  readTitleCart: 'Kontrolli ostukorvi uuesti',
  readTitleTheirChat: 'Kontrolli uuesti teed oma vestluseni',
  readTitleAll: 'Loe kogu poodi uuesti',
  readTitleStore: 'Loe uut poodi',
  readTitleMarket: 'Lisa turg',
  readShopLabel: 'Pood',
  readScopeLabel: 'Loeb',
  readTimeLabel: 'Kestus',
  readCostLabel: 'Kulu',
  readAfterLabel: 'Pärast',
  readScopeProducts: 'Tooteotsing, sortimine, tootefotod, värvid ja suurused',
  readScopeCart: 'Ostukorvi lisamine ja sealt eemaldamine',
  readScopeTheirChat: 'Kuidas ostlejad sinu vestluseni jõuavad',
  readScopeAll: 'Tooted, ostukorv, sinu vestlus ja turud',
  readScopeStore: 'Tooted, hinnad ja suurused, ostukorv, sinu vestlus, ilme ja turud',
  readScopeMarket: '{market}: selle tooted, hinnad ja ostukorv',
  readTime: 'Tavaliselt {min}–{max} minutit',
  readCost: 'Kasutab ettevõtte krediite',
  readCostLast: 'Kasutab ettevõtte krediite · selle poe viimane lugemine kasutas {credits}',
  readCostLastPartial:
    'Kasutab ettevõtte krediite · selle poe viimasena peatunud lugemine kasutas vähemalt {credits}',
  readAfter: 'Salvestab uue mustandi. Ostlejad ei näe midagi enne avaldamist.',
  readAfterLive:
    'Salvestab uue mustandi. Avaldatud assistent töötab edasi ja ostlejad näevad muudatust alles pärast avaldamist.',
  readAfterNew: 'Salvestab sulle proovimiseks esimese mustandi. Midagi ei avaldata.',
  readCartNote:
    'Ostukorvi kontrollimiseks lisab see testtoote ja eemaldab selle kohe. Kassasse ei minda kunagi.',
  readFocusLabel: 'Mida Chat palus lugemisel parandada',
  readFooter:
    'Miski ei käivitu enne sinu heakskiitu. Saad lugemise peatada vahekaardil „Seadistamine“.',
  readStartedAt:
    'Alustati {time} · tavaliselt {min}–{max} minutit. Kui uus mustand on valmis, palu Chatil seda kontrollida.',
  readFocusAll: 'Näita kõike',
  readFocusLess: 'Näita vähem',
  readStart: 'Alusta lugemist',
  addToSite: 'Lisa see oma saidile',
  readTab: 'Taotlus',
  readStarted: 'Lugemine on alanud. Kui uus mustand on valmis, palu Chatil seda kontrollida.',
  readFollow: 'Jälgi seda vahekaardil „Seadistamine“',
  proposedDesignLive:
    'Sinu poe päris eelvaade selles kujunduses. Küsi sellelt midagi: päris ostukorvi midagi ei lisata. Heakskiit salvestab selle uue mustandina; avaldamine toimub Storefronti lehel.',
  designChanges: 'Mis muutub',
  designReset: 'tagasi vaikeväärtusele',
  designGroupColors: 'Värvid',
  designGroupType: 'Fondid',
  designGroupShape: 'Kuju',
  designGroupLauncher: 'Lehel olev nupp',
  designGroupPanel: 'Päis',
  designGroupProducts: 'Tooted',
  designGroupCart: 'Poe ostukorv',
  designGroupCopy: 'Sõnad',
  designGroupLogo: 'Logo',
  designGroupCss: 'Kohandatud CSS',
  designColorBrand: 'Bränd',
  designColorActions: 'Lisa ja saada',
  designColorPanel: 'Paneel',
  designColorSurface: 'Pinnad',
  designColorInk: 'Tekst',
  designColorMuted: 'Teisene tekst',
  designColorLine: 'Jooned',
  designColorBubble: 'Ostleja sõnumid',
  designColorBubbleInk: 'Ostleja tekst',
  designActionsBrand: 'Brändi värv',
  designActionsInk: 'Teksti värv',
  designWordsName: 'Nimi',
  designWordsGreeting: 'Tervitus',
  designWordsStarters: 'Alustusküsimused',
  designWordsLauncher: 'Nupu silt',
  designWordsPlaceholder: 'Kohatäide',
  designWordsAi: 'Tehisintellekti silt',
  designWordsPerson: 'Viide inimesele',
  noteNew: 'Uus',
  noteChanged: 'Muudetud',
  noteRemoved: 'Eemaldatud',
  noteSourceKnowledge: 'Teadmiste leht: {title}',
  noteSourceFile: 'Fail: {name}',
  noteSourceChat: 'Vestlusest',
  knowledgeApprovalNote:
    'Ostlejad saavad lugeda kõike nendes märkmetes. Heakskiit salvestab uue mustandi: esita küsimusi vahekaardil Widget ja avalda see vahekaardil „Paigaldamine“.',
  profileTitle: 'Poe kohta',
  profileChecked: 'Kontrollitud {date}',
  profileSources: 'Kirjutatud allikatest',
  profileSample: '{count} toodet valiti hinnataseme jaoks',
  profileSampleOne: '{count} toode valiti hinnataseme jaoks',
  profileAssistant: 'Mida assistent siin teha oskab',
  profileTeam: 'Kirjutanud teie meeskond',
  profileEmpty:
    'Pole veel kirjutatud. Paluge Chatil teha teadmiste läbimine: see kirjutab selle teie enda lehtedest ja sellest, mida ta teie saidil nägi, ning saate lisada oma ridu.',
  profileWithChat: 'Lisa või muuda Chatiga',
  profilePrompt:
    'Aita mul parandada, mida ostuassistent poele {name} ({domain}) meie poest tervikuna teab: mida müüme, millega oleme tuntud, kellele me oleme ja mida ostjad siin teha saavad. Loe esmalt profiili „Poe kohta“ ja ütle mulle, mis puudub või on vale. Kõik, mida poe kohta ütlen ja mida ükski leht ei ütle, lisandub meie meeskonna reana.',
  profilePartNavigation: 'Osakonnad',
  profilePartWelcome: 'Tervitus',
  profilePartMarkets: 'Turud',
  profilePartFunctions: 'Mida assistent oskab',
  profilePartProducts: 'Tooted',
  profilePartPages: 'Teenindusleheküljed',
  profileCleared: 'Kustutab kirjutatud profiili',
  knowledgeTitle: 'Mida see teab',
  knowledgeBody: 'Lisaks toodetele, hindadele ja laoseisule, mida see loeb alati otse sinu poest.',
  knowledgeEmpty:
    'Praegu ainult sinu kataloog. Lisa Chatiga tarne-, tagastus- või makseteave oma lehtedelt või Teadmistest.',
  knowledgeWithChat: 'Lisa Chatiga',
  knowledgeChangeWithChat: 'Muuda Chatiga',
  knowledgePrompt:
    'Aita mul valida, mida poe {name} ({domain}) ostuabiline ostlejatele meie toodete kõrval räägib, näiteks tarne, tagastused ja maksed. Leia see meie enda lehtedelt või Teadmistest ning paku märkmeid, mille saan heaks kiita.',
  pagesTitle: 'Lehed, mida ta loeb',
  pagesBody:
    'Lehed, millest märkmed pärinevad. Poneva kontrollib neid iga päev ja Chat teatab, kui mõni muutub.',
  pagesEmpty:
    'Lehti veel pole. Siin kuvatakse poe lugemise leitud lehed ja need, mille nimetad Chatis.',
  pageFound: 'Poneva leitud',
  pageLinkedFrom: 'lingitud lehelt {page}',
  pageAddedByYou: 'lisatud käsitsi',
  pageFromNote: 'mainitud märkmes',
  pageNoteOne: '1 märkus',
  pageNotesMany: '{count} märkust',
  pageStatusOk: 'Ajakohane',
  pageStatusChanged: 'Muudetud',
  pageStatusUnreadable: 'Ei saa lugeda',
  pageStatusUnchecked: 'Pole veel kontrollitud',
  pagesChangeWithChat: 'Muuda Chatiga',
  pagesUpdateWithChat: 'Uuenda märkmeid Chatiga',
  pagesPrompt:
    'Aita mul muuta, milliseid lehti {name} ({domain}) poe ostuassistent märkmete jaoks loeb. Näita mulle lehti, mida ta praegu kasutab, ja küsi, millised lisada või eemaldada.',
  pagesUpdatePrompt:
    'Mõned {name} ({domain}) lehed on pärast märkmete tegemist muutunud või neid ei saa enam lugeda. Näita mulle, millised need on, ja tee märkmed nende põhjal uuesti mustandina, mida saan vaadata.',
  viewCompare: 'Võrdlus',
  lookDeviceLabel: 'Võrdle seadmel',
  lookShop: 'Sinu pood',
  lookShopCaption: 'Avaleht, loetud {when}',
  lookWidget: 'Ostuabiline',
  lookWidgetCaption: 'Versioon {version}',
  lookLoading: 'Ostuabilist asetatakse sinu poe kõrvale…',
  lookFailed: 'Võrdlust ei õnnestunud praegu teha.',
  lookNoShopPicture: 'Lugemisel ei salvestatud sinu poest pilti selle laiusega.',
  lookNoneTitle: 'Sinu poe enda ilmet ei salvestatud',
  lookNoneBody:
    'See lugemine ei salvestanud seda. Loe poodi jaotises Seadistamine uuesti ning selle logo, värvid ja lehed salvestatakse võrdlemiseks.',
  lookColoursShop: 'Värvid, mida sinu pood kasutab',
  lookColoursAssistant: 'Ostuabilise värvid',
  lookRoleHeader: 'Päis',
  lookRoleBrand: 'Bränd',
  lookSame: 'Sama mis sinu poe {colour} ({source})',
  lookOff: 'Sinu poe {colour} ({source}) lähedal, kuid mitte sama, erinevus {delta}',
  lookOwn: 'Oma värv: sinu pood seda ei kasuta',
  lookNeutral:
    'Sinu poodi selle jaoks ei laaditud: kuvatakse ainult seda, mille lugemine salvestas.',
  lookFromLogo: 'logo',
  lookFromTheme: 'teemavärv',
  lookFromTile: 'paanivärv',
  lookFromMask: 'kinnitatud ikoon',
  lookFromManifest: 'rakenduse manifest',
  lookFromHeader: 'päis',
  lookFromButton: 'nupud',
  lookFromVariable: 'stiilivärv',
  lookFix: 'Paranda Chatiga',
  lookFixPrompt:
    'Ostuabilise värv ({role}) on {colour}, sinu poe värvi {exact} ({source}) lähedal, kuid mitte sama. Palun kasuta sinu poe täpset värvi {exact}.',
  lookSwatch: 'Värv {colour}',
  errorDesignChecks: 'See versioon ei läbinud kujunduskontrolle.',
  designSamplesBlocked:
    'Kaartide paigutust ei saa siin tõendada: sinu sait blokeerib automaatsed külastused. Värvid, fondid ja logo töötavad siiski.',
  designCheckLabel: 'Tehisintellekti silt on lugemiseks liiga hele.',
  designCheckReach: 'Nupuni ei pääse.',
  designCheckSize: 'Osa teksti on liiga väike.',
  designCheckOverflow: 'Paneel kerib külgsuunas.',
  designCheckRender: 'Kontrolle ei saanud käivitada. Proovige hetke pärast uuesti.',
  fixDesignPrompt:
    'Püüdsin avaldada ettevõtte {name} ({domain}) uusimat mustandit, kuid see ei läbinud kujunduskontrolle: {facts}. Parandage kujundus selles vestluses ja kontrollige seda telefonis ja arvutis. Ärge seda avaldage.',
};

const fi: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Ostoavustaja',
  designUnconfirmed:
    'Pysäytettiin ennen kuin Chat ehti vahvistaa ulkoasun tallennuksen. Avustajan canvas näyttää uusimman version.',
  errorNewerVersion:
    'Sen jälkeen kun avasit tämän, on tallennettu uudempi versio. Tarkista se ensin ja julkaise sitten.',
  canvasSelectHint: 'Muuta avustajan osaa keskustelussa napsauttamalla sitä · Esc lopettaa',
  canvasDraft: 'Versio {version} · ei vielä julkaistu',
  canvasDraftOverLive: 'Versio {version} · versio {live} on julkaistu',
  canvasLive: 'Versio {version} · julkaistu',
  canvasNoVersion: 'Ensimmäinen versio syntyy, kun kaupan lukeminen on valmis',
  canvasPublish: 'Julkaise Storefront-sivulla',
  canvasFrameTitle: '{name}-ostoavustaja',
  viewLabel: 'Näkymä',
  photosGood: '{count} tuotekuvaa: kokonaisia ja tarkkoja',
  photosGoodOne: 'Tuotekuva on kokonainen ja tarkka',
  canvasMissingTitle: 'Tämä ostoavustaja ei ole tässä työtilassa',
  canvasMissingBody: 'Se kuuluu toiseen työtilaan tai se on poistettu.',
  canvasMissingLink: 'Katso tämän työtilan avustajat',
  photosLabel: 'Tuotekuvat',
  photosSmall: '{count} on liian pieniä ruuduissaan',
  photosCut: '{count} on rajattu pois',
  photosSoft: '{count} on suurennettuina epätarkkoja',
  photosMissing: '{count} ilman kuvaa',
  photosFix: 'Korjaa Chatilla',
  photosPrompt:
    'Korjaa korttien tuotekuvat: jokaisen tulee näkyä kokonaan, olla riittävän suuri ruudussaan ja terävä.',
  photosFacts:
    '{total} kuvaa: {small} liian pieniä ruuduissaan, {cut} rajattuja, {soft} suurennettuina epätarkkoja, {missing} ilman kuvaa. Koot: {sizes}',
  viewDesign: 'Ulkoasu',
  viewVersions: 'Versiot',
  versionsLoading: 'Ladataan versioita…',
  versionsFailed: 'Versioita ei voitu ladata.',
  versionTitle: 'Versio {version}',
  versionLive: 'Julkaistu',
  versionChecked: 'Tarkistettu',
  versionToday: 'Tänään',
  versionYesterday: 'Eilen',
  versionEarlier: 'Aiemmin',
  versionByPerson: 'Pyytäjä: {name}',
  versionByChat: 'Pyydetty keskustelussa',
  versionFromReading: 'Luettu kohteesta {domain}',
  versionPublishedBy: 'julkaisija {name}, {at}',
  versionPublish: 'Julkaise',
  versionRestore: 'Palauta…',
  versionRestoreTitle: 'Palautetaanko versio {version}?',
  versionRestoreBody:
    'Siitä tulee versio {next}, uusi luonnos. Sivustosi ei muutu ennen julkaisua.',
  versionRestoreConfirm: 'Palauta',
  versionRestoreCancel: 'Peruuta',
  versionRestored: 'Palautettu versioksi {version}',
  versionRestoreFailed: 'Versiota ei voitu palauttaa.',
  compareTitle: 'Vertaa versiota {to} versioon {from}',
  compareTitleLive: 'Vertaa versiota {to} julkaistuun versioon {from}',
  compareHint: 'Vertaa vetämällä',
  compareLabel: 'Vertaa versioita {from} ja {to}',
  compareTagLive: 'v{version} · Julkaistu',
  compareTag: 'v{version}',
  changesTitle: 'Mitä muuttui versiosta {from} versioon {to}',
  changesNone: 'Ulkoasut ovat samat. Vain muistiinpanot tai kaupan tiedot muuttuivat.',
  changeAdded: 'Lisätty',
  changeRemoved: 'Poistettu',
  changeChanged: 'Muutettu',
  changeDrawn: 'piirretty logo',
  changeCss: 'oma CSS',
  changeYes: 'kyllä',
  changeNo: 'ei',
  'field.colors.brand': 'Brändin väri',
  'field.colors.actions': 'Painikkeen väri',
  'field.colors.panel': 'Paneelin väri',
  'field.colors.surface': 'Pinnan väri',
  'field.colors.ink': 'Tekstin väri',
  'field.colors.muted': 'Himmeän tekstin väri',
  'field.colors.line': 'Viivan väri',
  'field.colors.bubble': 'Ostajan viestin väri',
  'field.colors.bubbleInk': 'Ostajan viestin teksti',
  'field.colors.header': 'Ylätunnisteen väri',
  'field.colors.headerInk': 'Ylätunnisteen tekstin väri',
  'field.colors.tints': 'Pehmeät sävyt',
  'field.copy.assistantName': 'Nimi',
  'field.copy.greeting': 'Tervehdys',
  'field.copy.placeholder': 'Kysymyskenttä',
  'field.copy.starters': 'Aloituskysymykset',
  'field.copy.launcherLabel': 'Avauspainikkeen teksti',
  'field.copy.aiLabel': 'Tekoälytunniste',
  'field.copy.personLabel': 'Linkki henkilöön',
  'field.type.body': 'Tekstin fontti',
  'field.type.display': 'Otsikkofontti',
  'field.type.fontCss': 'Fontin lähde',
  'field.type.scale': 'Tekstin koko',
  'field.shape.radius': 'Kulmat',
  'field.shape.shadow': 'Varjo',
  'field.shape.density': 'Väljyys',
  'field.launcher.style': 'Avauspainike tietokoneilla',
  'field.launcher.phoneStyle': 'Avauspainike puhelimissa',
  'field.launcher.side': 'Avauspainikkeen puoli',
  'field.launcher.icon': 'Avauspainikkeen kuvake',
  'field.panel.header': 'Ylätunnisteen koko',
  'field.panel.opening': 'Ensimmäinen näkymä',
  'field.products.layout': 'Tuotteet',
  'field.products.imageFit': 'Tuotekuvat',
  'field.products.imageRatio': 'Kuvaruudun muoto',
  'field.products.imagePadding': 'Kuvien ympärillä oleva tila',
  'field.products.imageBackground': 'Kuvaruudun väri',
  'field.products.picker': 'Koon valinta',
  'field.products.showStock': 'Saatavuuden näyttäminen',
  'field.cart.landing': 'Ostoskorin animaatio',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Logon muoto',
  'field.logo.plate': 'Logon tausta',
  'field.css': 'Mukautettu CSS',
  'value.ink': 'muste',
  'value.brand': 'brändin väri',
  'value.small': 'pieni',
  'value.medium': 'keskikokoinen',
  'value.large': 'suuri',
  'value.square': 'neliö',
  'value.soft': 'pehmeä',
  'value.round': 'pyöreä',
  'value.flat': 'tasainen',
  'value.deep': 'syvä',
  'value.compact': 'tiivis',
  'value.comfortable': 'väljä',
  'value.pill': 'tekstillinen pilleri',
  'value.circle': 'ympyrä',
  'value.right': 'oikea',
  'value.left': 'vasen',
  'value.logo': 'logo',
  'value.chat': 'keskustelukupla',
  'value.sparkle': 'säihke',
  'value.bag': 'kassi',
  'value.full': 'täysi',
  'value.middle': 'keskellä',
  'value.bottom': 'alhaalla',
  'value.rows': 'rivit',
  'value.grid': 'ruudukko',
  'value.carousel': 'pyyhkäistävä rivi',
  'value.contain': 'koko kuva',
  'value.cover': 'täytetty ruutu',
  'value.portrait': 'pysty',
  'value.landscape': 'vaaka',
  'value.none': 'ei mitään',
  'value.pop': 'nouseva +1',
  'value.ring': 'aalto',
  'value.blink': 'vilkutus',
  'value.chips': 'painikkeet rivissä',
  'value.menu': 'luettelo',
  'value.rounded': 'pyöristetty',
  designCardTitle: '{name}-ostoavustaja',
  designCardSaved: 'Versio {version} tallennettu · ei vielä julkaistu',
  designCardSavedFirst: 'Ulkoasu tallennettu · ensimmäinen versio sisältää sen',
  designSaving: 'Tallennetaan ulkoasua…',
  selectedAssistantPart: 'Valittu: avustajan osa',
  loading: 'Ladataan Storefrontia…',
  disabledTitle: 'Storefront ei ole käytössä tässä työtilassa.',
  disabledBody:
    'Se on ennakkokäytössä ja otetaan käyttöön vaiheittain. Kysy Poneva-yhteyshenkilöltäsi, milloin se tulee teille.',
  loadFailed: 'Storefrontia ei voitu ladata.',
  startTitle: 'Ostoavustaja kaupallesi',
  startBody:
    'Poneva lukee kauppaasi kuten ostaja käyttää sitä: haun, tuotteet, ostoskorin ja nykyisen keskustelusi. Se kestää noin 10–30 minuuttia, käyttää yrityksesi krediittejä ja voit jatkaa työskentelyä.',
  urlLabel: 'Kauppasi osoite',
  urlPlaceholder: 'https://www.kauppasi.fi',
  startButton: 'Lue kauppani',
  starting: 'Aloitetaan…',
  startFailed: 'Kauppaa ei voitu lukea. Tarkista osoite ja yritä uudelleen.',
  tabConversations: 'Keskustelut',
  tabOverview: 'Yleiskatsaus',
  allAssistants: 'Kaikki avustajat',
  allAssistantsNote: '{assistants} avustajaa · {sites} sivustoa',
  allAssistantsMenuNote: '{count} keskustelua tällä viikolla',
  marketLabel: 'Markkina',
  allMarkets: 'Kaikki markkinat',
  allMarketsNote: '{count} sivustoa',
  scopeIn: '{assistant} · {market}',
  askAbout: 'Kysy Chatilta aiheesta {scope}',
  overviewAssistantsTitle: 'Avustajat ja markkinat',
  overviewMarketsTitle: 'Markkinat',
  overviewAssistantsOnly: 'Avustajat',
  overviewIdle: '{count} avustajalla ei vielä ole keskusteluja',
  allAssistantsCount: '{assistants} avustajaa',
  overviewFailed: 'Yleiskatsausta ei voitu ladata.',
  colConversations: 'Keskustelut',
  colCart: 'Lisätty ostoskoriin',
  colCheckout: 'Kassalle',
  colPerson: 'Henkilö',
  colCost: 'Krediitit',
  rowMarkets: '{count} markkinaa',
  rowVersion: 'versio {version}',
  rowAria:
    '{name}, {sub}: {conversations} keskustelua, {cart} lisätty ostoskoriin, {checkout} edennyt kassalle, {person} pyytänyt henkilöä, {cost} krediittiä',
  versionPublishedAt: 'julkaistu {at}',
  designColorHeader: 'Ylätunniste',
  designColorHeaderInk: 'Ylätunnisteen teksti',
  designGroupTranslations: 'Sanat muilla kielillä',
  topicsTitle: 'Mistä ostajat kysyvät',
  topicsNote: 'Chatin ensimmäisistä kysymyksistä löytämät aiheet',
  startPagesTitle: 'Mistä keskustelut alkavat',
  startPagesOne: 'Jokainen keskustelu alkoi yhdenlaiselta sivulta: {page}.',
  'startPage.product': 'Tuotesivut',
  'startPage.search': 'Hakutulokset',
  'startPage.start': 'Aloitussivu',
  'startPage.cart': 'Ostoskori',
  'startPage.other': 'Muut sivut',
  addMarketInChat: 'Lisää markkina keskustelussa',
  marketsTitle: 'Markkinat',
  marketsBody:
    'Yksi avustaja ja yksi ulkoasu. Jokaisella markkinalla on oma kieli, valuutta, tuotteet, ostoskori ja yhteys asiakaspalveluun.',
  marketChecked: 'tarkistettu {when}',
  'marketStatus.ready': 'Valmis',
  'marketStatus.found': 'Ei lisätty',
  'marketStatus.checking': 'Tarkistetaan',
  'marketStatus.failed': 'Vaatii korjauksen',
  addMarketPrompt:
    'Lisää ostoavustajalle toinen markkina: {name} ({domain}). Etsi kaupan muut maat, kielet tai sivustot ja kysy minulta, minkä lisään.',
  tabWidget: 'Widget',
  tabInstall: 'Asennus',
  tabSetup: 'Aloitus',
  statusReading: 'Kauppaasi luetaan',
  readingProducts: 'Tuotteita luetaan uudelleen',
  readingCart: 'Ostoskoria tarkistetaan uudelleen',
  readingChat: 'Yhteyttä omaan keskusteluusi tarkistetaan',
  readingMarket: 'Markkinaa {market} lisätään',
  readingMarketAny: 'Markkinaa lisätään',
  statusReady: 'Valmis asennettavaksi',
  statusFailed: 'Lukeminen pysähtyi',
  statusLive: 'Käytössä · versio {version} osoitteessa {domain}',
  statusPublished: 'Julkaistu · versio {version} · ei vielä sivustollasi',
  statusPublishedUnverified: 'Julkaistu · versio {version} · sivustoasi ei voitu tarkistaa',
  stepOpen: 'Sivustoa avataan',
  stepSearch: 'Haku',
  stepProducts: 'Tuotteet',
  stepCart: 'Ostoskori',
  stepChat: 'Nykyinen keskustelusi',
  stepLook: 'Ulkoasu ja logo',
  stepChecks: 'Toimivuutta tarkistetaan',
  stepReady: 'Valmis',
  stepDetailOpen: 'Avataan {domain}',
  stepDetailSearch: 'Kaupan tuotteita etsitään',
  stepDetailProducts: 'Tuotteita, hintoja, kuvia, värejä ja kokoja luetaan',
  stepDetailCart: 'Ostoskoria kokeillaan: lisätään, luetaan uudelleen, poistetaan ja tyhjennetään',
  stepDetailChat: 'Nykyistä keskusteluasi etsitään',
  stepDetailLook: 'Kaupan logoa ja värejä luetaan',
  stepDetailChecks: 'Ostajien hakuja, tuotteita, selausta ja ostoskoria kokeillaan',
  stepDetailChecksProducts: 'Tuotehakuja, selausta, kuvia, värejä ja kokoja tarkistetaan',
  stepDetailChecksCart:
    'Ostoskorin lisäämistä, uudelleenlukua, poistamista ja tyhjentämistä sekä tuotetietoja tarkistetaan',
  stepDetailChecksChat: 'Yhteyttä nykyiseen keskusteluusi ja tuotetietoja tarkistetaan',
  stepDetailChecksMarket: 'Tämän markkinan tuotteita, hintoja ja ostoskoria tarkistetaan',
  nowRunning: 'Nyt: {step}',
  nowProgress: 'Vaihe {n}/{total} · kulunut {minutes} min',
  nowReady: 'Kauppasi on valmis avustajalle',
  nowReadyBody: 'Haku, tuotteet ja ostoskori toimivat.',
  nowLastRead: 'Luettu viimeksi {when}',
  nowUsually: 'yleensä {min}–{max}',
  stepsRead: 'Luettu {when} · Näytä vaiheet',
  nowFailed: 'Lukeminen pysähtyi',
  factAnswers: 'Vastaa ostajille käyttäen',
  costCounting: 'Lasketaan…',
  factPaid: 'Maksetaan lähteestä',
  paidCompany: 'Yrityksen krediitit',
  creditsAmount: '{amount} krediittiä',
  creditsOne: '{amount} krediitti',
  creditsUnder: 'alle {amount} krediittiä',
  stopRun: 'Pysäytä ajo',
  stopping: 'Pysäytetään…',
  closeNote: 'Voit poistua tältä sivulta. Ajo jatkuu.',
  readyNote: 'Seuraavaksi: kokeile pienoisohjelmaa ja asenna se sitten.',
  buildWidget: 'Kokeile pienoisohjelmaa',
  readAgain: 'Lue kauppa uudelleen',
  foundTitle: 'Mitä löytyi',
  foundProducts: 'Tuotteet ja hinnat luettu',
  foundSearch: 'Haku toimii',
  foundCart: 'Ostoskoriin lisääminen toimii',
  foundChat: '{vendor}-keskustelusi',
  foundNoChat: 'Omaa keskustelua ei löytynyt',
  foundLook: 'Omat värit ja logo',
  widgetTitle: 'Widgetisi',
  widgetBody:
    'Ostoavustajasi omilla väreilläsi. Kokeile sitä tässä: se vastaa oikeilla tuotteillasi. Mitään ei lisätä oikeaan ostoskoriin.',
  widgetLoading: 'Esikatselua ladataan…',
  widgetFailed: 'Esikatselua ei voitu ladata.',
  widgetNotReady: 'Widget on valmis, kun Aloitus-välilehden vaiheet on tehty.',
  installLineTitle: 'Lisää tämä rivi sivustollesi',
  installLineBody:
    'Se toimii millä tahansa alustalla: sivujesi head-osiossa tai Shopifyssa theme.liquid-tiedostossa juuri ennen </head>-tagia. Se ei näytä mitään ennen julkaisua.',
  installLineBodyLive:
    'Se toimii millä tahansa alustalla: sivujesi head-osiossa tai Shopifyssa theme.liquid-tiedostossa juuri ennen </head>-tagia. Ostajat näkevät silloin version {version}.',
  installPolicyAsk: 'Asettaako sivustosi Content Security Policyn?',
  installPolicy:
    'Jos sivustosi asettaa Content Security Policyn, salli myös {origin} kohdassa {directives}, tai avustaja ei voi käynnistyä.',
  installPolicyRefuses:
    'Sivustosi Content Security Policy estää avustajan: salli {origin} kohdassa {directives}, tai mikään ei käynnisty.',
  installPolicyNonce:
    'Käytäntösi sallii vain luottamasi skriptit, joten lisää rivi sivujesi noncella.',
  installPolicyEval: "Avustaja tarvitsee myös arvon 'unsafe-eval' kohtaan script-src.",
  copy: 'Kopioi',
  copied: 'Kopioitu',
  domainsTitle: 'Sivustot, joilla se vastaa',
  domainsBody: 'Se pysyy hiljaa kaikilla muilla sivustoilla, vaikka joku kopioisi rivin.',
  lineFound: 'Rivi löytyi',
  lineNotSeen: 'Ei vielä sivulla',
  lineUnverified:
    'Tarkistus ei onnistunut: sivusto estää automaattiset vierailut. Avustaja toimii silti asiakkaille.',
  lineChecked: 'tarkistettu {when}',
  lineCheckAgain: 'Tarkista uudelleen',
  lineChecking: 'Sivua tarkistetaan…',
  installCheckBusy: 'Tarkistus on juuri nyt kiireinen. Yritä hetken kuluttua uudelleen.',
  publishTitle: 'Julkaise',
  publishBody:
    'Osoitteessa {domain} olevat ostajat näkevät sen heti. Voit perua julkaisun milloin tahansa.',
  publishButton: 'Julkaise versio {version}',
  publishConfirm: 'Julkaistaanko {name}-avustajan versio {version}?',
  publishNow: 'Julkaise',
  notNow: 'Ei nyt',
  publishing: 'Julkaistaan…',
  liveTitle: 'Julkaistu: versio {version}',
  liveSince: 'Alkaen {since}',
  unpublish: 'Peru julkaisu',
  unpublishConfirm:
    'Poistetaanko versio {version} sivustoltasi? Ostajat eivät enää näe avustajaa, ennen kuin julkaiset uudelleen.',
  publishedLine: 'Julkaistu nyt: versio {version}, alkaen {since}',
  usageTitle: 'Käyttö',
  usageBody: 'Se käyttää yrityksesi krediittejä kuten muukin Chat.',
  usageDiscovery: 'Kaupan lukeminen',
  usageChats: 'Ostajien keskustelut',
  usageChatsValue: '{count} keskustelua · {cost}',
  last7: 'Viimeiset 7 päivää',
  askWeek: 'Kysy Chatilta tästä viikosta',
  kpiConversations: 'Keskustelut',
  kpiVsPrevious: '{change} edelliseen viikkoon verrattuna',
  kpiCart: 'Lisätty ostoskoriin',
  kpiCheckout: 'Edetty kassalle',
  kpiPerson: 'Pyysi henkilöä',
  kpiCost: 'Käytetyt krediitit',
  ofConversations: '{percent} keskusteluista',
  checkoutNote: '{percent} keskusteluista',
  personNote: '{percent} avasi keskustelusi',
  costNote: 'Noin {amount} keskustelua kohden',
  costNoteUnder: 'Alle {amount} keskustelua kohden',
  sparkLabel: 'Keskusteluja päivässä, viimeiset {days} päivää',
  sparkDay: '{day}: {count} keskustelua',
  filterAll: 'Kaikki',
  filterCart: 'Lisätty ostoskoriin',
  filterCheckout: 'Kassa',
  filterPerson: 'Henkilö',
  outcomeCart: 'Lisätty ostoskoriin',
  outcomeCheckout: 'Edetty kassalle',
  outcomePerson: 'Pyydetty henkilöä',
  outcomeAnswered: 'Vastattu',
  devicePhone: 'Puhelin',
  deviceComputer: 'Tietokone',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Aloitussivu: {page}',
  anonymous: 'Ostaja on anonyymi.',
  askConversation: 'Kysy Chatilta tästä keskustelusta',
  showOlderVersions: 'Näytä aiemmat versiot',
  showOlder: 'Näytä vanhemmat keskustelut',
  filterCountsWeek: 'Määrät: viimeiset 7 päivää',
  readAlreadyHere:
    'Tämä kauppa on jo täällä, joten mitään uutta ei aloitettu eikä krediittejä käytetty.',
  readOpenShop: 'Avaa kauppa',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Mitä voimme oppia tästä keskustelusta?',
  askWeekMessage: 'Mitä voimme oppia tämän viikon keskusteluista?',
  conversationsChipOne: '{name} · keskustelu {when}',
  conversationsChipWeek: '{name} · tämä viikko',
  conversationsChipWeekIn: '{name} · {market} · tämä viikko',
  removeConversations: 'Poista keskustelut',
  noConversations: 'Ei vielä keskusteluja',
  noConversationsBody: 'Kun ostajat käyttävät avustajaa, heidän keskustelunsa näkyvät täällä.',
  pickConversation: 'Valitse luettava keskustelu.',
  eventCheckout: 'Siirtyi kassalle',
  eventPerson: 'Avasi keskustelusi',
  showMore: 'Näytä lisää',
  urlInvalid: 'Käytä kauppasi https-osoitetta, kuten https://www.kauppasi.fi.',
  errorNoCompany: 'Storefront tarvitsee yrityksen työtilan. Pyydä ylläpitäjää lisäämään sinut.',
  errorPublishPermission:
    'Vain yrityksesi julkaisuoikeuden omaavat henkilöt voivat tehdä tämän. Pyydä ylläpitäjää.',
  actionFailed: 'Se ei onnistunut. Yritä uudelleen.',
  copyFailed: 'Kopiointi ei onnistunut. Valitse rivi ja kopioi se itse.',
  reasonCancelled: 'Pysäytit ajon.',
  reasonFailed: 'Lukeminen ei valmistunut kaupassasi, joten avustaja ei ole valmis.',
  reasonFailedLive:
    'Lukeminen ei valmistunut kaupassasi. Julkaistu avustaja jatkaa toimintaansa ennallaan.',
  reasonFailedKept:
    'Lukeminen ei valmistunut kaupassasi, joten mikään ei muuttunut: versio {version} säilyy ennallaan.',
  statusReadStopped: 'Valmis · viimeinen lukeminen pysähtyi',
  reasonStepMarked: 'Pysähtynyt vaihe on merkitty alle.',
  reasonReadAgain: 'Uudelleenlukeminen jatkaa siitä, mitä tämä lukeminen oppi.',
  reasonNotAStore:
    'Tämä osoite ei vaikuta verkkokaupalta: haettavia tuotteita ei löytynyt. Tarkista osoite tai kokeile kaupan pääverkkotunnusta.',
  reasonTooSlow:
    'Kauppasi haku vastaa ostoavustajalle liian hitaasti: siihen kului yli 12 sekuntia. Kaupan nopeampi haku tai lukeminen myöhemmin voi auttaa.',
  reasonOurSide:
    'Lukeminen keskeytyi meidän päässämme olleen ongelman vuoksi, ei kauppasi takia, joten avustaja ei ole vielä valmis.',
  reasonOurSideLive:
    'Lukeminen keskeytyi meidän päässämme olleen ongelman vuoksi, ei kauppasi takia. Julkaistu avustaja jatkaa toimintaansa ennallaan.',
  reasonOurSideKept:
    'Lukeminen keskeytyi meidän päässämme olleen ongelman vuoksi, ei kauppasi takia, joten mikään ei muuttunut: versio {version} säilyy ennallaan.',
  reasonTryLater: 'Yritä lukea kauppa uudelleen hetken kuluttua.',
  errorNoCredits:
    'Tähän ei ole juuri nyt krediittejä. Yrityksen omistaja voi tarkistaa paketin ja rajat kohdasta Usage.',
  errorReadUnavailable:
    'Kauppaa ei voi lukea juuri nyt. Yritä uudelleen muutaman minuutin kuluttua.',
  reasonTimeout:
    'Lukeminen saavutti aikarajansa ennen valmistumista. Uudelleenlukeminen jatkaa jo tarkistetusta.',
  retrying: 'Aloitetaan uudelleen…',
  readTitleChecks: 'Tarkista tallennettu lukeminen uudelleen',
  checksScopeLabel: 'Tarkistetaan',
  readScopeChecks:
    'Ostajien haut, tuotteet, selaus ja ostoskori sen pohjalta, mitä edellinen lukeminen tallensi. Kauppaa ei lueta uudelleen.',
  readScopeChecksProducts:
    'Tuotehaut, selaus, kuvat, värit ja koot sen pohjalta, mitä edellinen lukeminen tallensi. Kauppaa ei lueta uudelleen.',
  readScopeChecksCart:
    'Ostoskorin lisääminen, uudelleenluku, poistaminen ja tyhjentäminen sekä tuotetiedot sen pohjalta, mitä edellinen lukeminen tallensi. Kauppaa ei lueta uudelleen.',
  readScopeChecksChat:
    'Yhteys nykyiseen keskusteluusi ja tuotetiedot sen pohjalta, mitä edellinen lukeminen tallensi. Kauppaa ei lueta uudelleen.',
  readScopeChecksMarket:
    'Markkinan tuotteet, hinnat ja ostoskori sen pohjalta, mitä edellinen lukeminen tallensi. Kauppaa ei lueta uudelleen.',
  checksCost: 'Vain uudet tarkistukset käyttävät yrityksen krediittejä',
  checksAfter:
    'Tallentaa uuden luonnoksen, jos tarkistukset menevät läpi. Mikään ei saavuta ostajia ennen julkaisua.',
  checksAfterLive:
    'Tallentaa uuden luonnoksen, jos tarkistukset menevät läpi. Julkaistu avustaja jatkaa toimintaansa, ja ostajat näkevät muutoksen vasta julkaisun jälkeen.',
  checksStarted:
    'Tarkistukset on aloitettu. Kun uusi luonnos on valmis, pyydä Chatia tarkistamaan se.',
  checkingSavedWork: 'Tallennettua työtä tarkistetaan uudelleen',
  checkAgain: 'Tarkista uudelleen',
  checkingAgain: 'Tarkistetaan uudelleen…',
  reasonCheckAgain:
    'Tarkista uudelleen testaa tallennetun työn vielä kerran lukematta kauppaa uudelleen. Vain uudet tarkistukset käyttävät yrityksen krediittejä.',
  fixWithChat: 'Korjaa Chatilla',
  fixReadPrompt:
    'Kaupan {name} ({domain}) lukeminen pysähtyi. Katso, mitä tapahtui, ja auta minua saamaan se toimimaan: kerro, mitä löydät, ehdota, mitä voisin kertoa lukemiselle, ja tarkista se sitten uudelleen tai lue se uudelleen tuon vihjeen avulla, lukien kaupasta mahdollisimman vähän.',
  fixReadPromptStep:
    'Kaupan {name} ({domain}) lukeminen pysähtyi vaiheessa ”{step}”. Katso, mitä tapahtui, ja auta minua saamaan se toimimaan: kerro, mitä löydät, ehdota, mitä voisin kertoa lukemiselle, ja tarkista se sitten uudelleen tai lue se uudelleen tuon vihjeen avulla, lukien kaupasta mahdollisimman vähän.',
  stepStateDone: 'valmis',
  stepStateRunning: 'käynnissä',
  stepStateWaiting: 'ei aloitettu',
  stepStateFailed: 'pysähtynyt',
  newVersionTitle: 'Versio {version} on valmis',
  newVersionBody: 'Julkaise se korvataksesi version {live}.',
  newVersionBodyLive: 'Julkaise se korvataksesi version {live} osoitteessa {domain}.',
  previewSize: 'Esikatselun koko',
  designWithChat: 'Avaa ulkoasu',
  designReviewBody: 'Tarkastele nykyistä ulkoasua ja kerro sitten Chatille, mitä haluat muuttaa.',
  designWhileTitle: 'Suunnittele samalla kun luemme kauppaasi',
  designWhileBody:
    'Chat voi muotoilla avustajan ulkoasua, sanoja ja logoa kanssasi jo nyt. Ulkoasu on valmis, kun lukeminen valmistuu.',
  designPreviewNote: 'Esikatselu esimerkkituotteilla, kunnes kauppasi on luettu.',
  proposedDesign: 'Ehdotettu ulkoasu',
  proposedDesignNote:
    'Pienoisohjelma tällä muutoksella ja esimerkkituotteilla. Hyväksyminen tallentaa sen uutena luonnoksena; julkaisu tehdään Storefront-sivulla.',
  lineSources: 'Kaupan sivuilta:',
  readAgainConfirm:
    'Luetaanko {name} uudelleen? Se kestää noin 10–30 minuuttia ja käyttää yrityksen krediittejä.',
  readAgainLive: 'Julkaistu avustaja jatkaa toimintaansa, kunnes julkaiset uuden version.',
  readAgainNow: 'Lue uudelleen',
  switchStore: 'Vaihda kauppaa tai lisää kauppa',
  storesLabel: 'Kauppasi',
  addStore: 'Lisää toinen kauppa',
  backToStores: 'Takaisin kauppoihisi',
  storeLive: 'Julkaistu',
  storeReady: 'Valmis',
  storeReading: 'Luetaan',
  storeStopped: 'Pysäytetty',
  actionDesignTitle: 'Muuta ostoavustajan ulkoasua',
  actionKnowledgeTitle: 'Muuta ostoavustajan tietoja',
  readTitleProducts: 'Lue tuotteet uudelleen',
  readTitleCart: 'Tarkista ostoskori uudelleen',
  readTitleTheirChat: 'Tarkista yhteys omaan keskusteluusi uudelleen',
  readTitleAll: 'Lue koko kauppa uudelleen',
  readTitleStore: 'Lue uusi kauppa',
  readTitleMarket: 'Lisää markkina',
  readShopLabel: 'Kauppa',
  readScopeLabel: 'Luetaan',
  readTimeLabel: 'Kesto',
  readCostLabel: 'Hinta',
  readAfterLabel: 'Sen jälkeen',
  readScopeProducts: 'Tuotehaku, lajittelu, tuotekuvat, värit ja koot',
  readScopeCart: 'Ostoskoriin lisääminen ja poistaminen',
  readScopeTheirChat: 'Miten ostajat pääsevät omaan keskusteluusi',
  readScopeAll: 'Tuotteet, ostoskori, oma keskustelusi ja markkinasi',
  readScopeStore: 'Tuotteet, hinnat ja koot, ostoskori, oma keskustelusi, ulkoasu ja markkinasi',
  readScopeMarket: '{market}: sen tuotteet, hinnat ja ostoskori',
  readTime: 'Yleensä {min}–{max} minuuttia',
  readCost: 'Käyttää yrityksen krediittejä',
  readCostLast: 'Käyttää yrityksen krediittejä · tämän kaupan viimeinen lukeminen käytti {credits}',
  readCostLastPartial:
    'Käyttää yrityksen krediittejä · tämän kaupan viimeinen lukeminen pysähtyi aikaisin ja käytti vähintään {credits}',
  readAfter: 'Tallentaa uuden luonnoksen. Mikään ei saavuta ostajia ennen julkaisua.',
  readAfterLive:
    'Tallentaa uuden luonnoksen. Julkaistu avustaja jatkaa toimintaansa, ja ostajat näkevät muutoksen vasta julkaisun jälkeen.',
  readAfterNew: 'Tallentaa ensimmäisen luonnoksen kokeiltavaksesi. Mitään ei julkaista.',
  readCartNote:
    'Ostoskorin tarkistamiseksi lisätään testituote ja poistetaan se uudelleen. Kassalle ei koskaan siirrytä.',
  readFocusLabel: 'Mitä Chat pyysi lukemista korjaamaan',
  readFooter: 'Mikään ei ala ennen hyväksyntääsi. Voit pysäyttää lukemisen Aloitus-välilehdellä.',
  readStartedAt:
    'Aloitettu {time} · yleensä {min}–{max} minuuttia. Kun uusi luonnos on valmis, pyydä Chatia tarkistamaan se.',
  readFocusAll: 'Näytä kaikki',
  readFocusLess: 'Näytä vähemmän',
  readStart: 'Aloita lukeminen',
  addToSite: 'Lisää sivustollesi',
  readTab: 'Pyyntö',
  readStarted: 'Lukeminen on aloitettu. Kun uusi luonnos on valmis, pyydä Chatia tarkistamaan se.',
  readFollow: 'Seuraa lukemista Aloitus-välilehdellä',
  proposedDesignLive:
    'Kauppasi oikea esikatselu tässä ulkoasussa. Kysy siltä jotain: mitään ei lisätä oikeaan ostoskoriin. Hyväksyminen tallentaa uuden luonnoksen; julkaisu tehdään Storefront-sivulla.',
  designChanges: 'Muutokset',
  designReset: 'takaisin oletukseen',
  designGroupColors: 'Värit',
  designGroupType: 'Fontit',
  designGroupShape: 'Muoto',
  designGroupLauncher: 'Sivun painike',
  designGroupPanel: 'Ylätunniste',
  designGroupProducts: 'Tuotteet',
  designGroupCart: 'Kaupan ostoskori',
  designGroupCopy: 'Sanat',
  designGroupLogo: 'Logo',
  designGroupCss: 'Mukautettu CSS',
  designColorBrand: 'Brändi',
  designColorActions: 'Lisää ja lähetä',
  designColorPanel: 'Paneeli',
  designColorSurface: 'Pinnat',
  designColorInk: 'Teksti',
  designColorMuted: 'Toissijainen teksti',
  designColorLine: 'Viivat',
  designColorBubble: 'Ostajan viestit',
  designColorBubbleInk: 'Ostajan teksti',
  designActionsBrand: 'Brändin väri',
  designActionsInk: 'Tekstin väri',
  designWordsName: 'Nimi',
  designWordsGreeting: 'Tervehdys',
  designWordsStarters: 'Aloituskysymykset',
  designWordsLauncher: 'Painikkeen teksti',
  designWordsPlaceholder: 'Paikkamerkki',
  designWordsAi: 'Tekoälytunniste',
  designWordsPerson: 'Linkki henkilöön',
  noteNew: 'Uusi',
  noteChanged: 'Muutettu',
  noteRemoved: 'Poistettu',
  noteSourceKnowledge: 'Tietosivu: {title}',
  noteSourceFile: 'Tiedosto: {name}',
  noteSourceChat: 'Keskustelusta',
  knowledgeApprovalNote:
    'Ostajat voivat lukea kaiken näissä muistiinpanoissa. Hyväksyminen tallentaa uuden luonnoksen: kysy siltä Widget-välilehdellä ja julkaise se sitten Asennus-välilehdellä.',
  profileTitle: 'Tietoja kaupasta',
  profileChecked: 'Tarkistettu {date}',
  profileSources: 'Kirjoitettu lähteistä',
  profileSample: '{count} tuotetta otettiin hintatason otokseen',
  profileSampleOne: '{count} tuote otettiin hintatason otokseen',
  profileAssistant: 'Mitä avustaja osaa täällä',
  profileTeam: 'Tiimisi kirjoittama',
  profileEmpty:
    'Ei vielä kirjoitettu. Pyydä Chatia ajamaan tietokierros: se kirjoittaa tämän omista sivuistanne ja siitä, mitä se näki sivustollanne, ja voitte lisätä omia rivejä.',
  profileWithChat: 'Lisää tai muuta Chatilla',
  profilePrompt:
    'Auta minua parantamaan sitä, mitä kauppa-avustaja kaupalle {name} ({domain}) tietää kaupastamme kokonaisuutena: mitä myymme, mistä meidät tunnetaan, keitä varten olemme ja mitä ostajat voivat täällä tehdä. Lue ensin Tietoja kaupasta -profiili ja kerro, mikä puuttuu tai on väärin. Kaikki, mitä kerron kaupasta ja mitä mikään sivu ei kerro, lisätään tiimimme rivinä.',
  profilePartNavigation: 'Osastot',
  profilePartWelcome: 'Tervehdys',
  profilePartMarkets: 'Markkinat',
  profilePartFunctions: 'Mitä avustaja osaa',
  profilePartProducts: 'Tuotteet',
  profilePartPages: 'Palvelusivut',
  profileCleared: 'Tyhjentää kirjoitetun profiilin',
  knowledgeTitle: 'Mitä se tietää',
  knowledgeBody: 'Näiden lisäksi se lukee tuotteet, hinnat ja saatavuuden aina suoraan kaupastasi.',
  knowledgeEmpty:
    'Toistaiseksi vain luettelosi. Lisää toimitus-, palautus- tai maksutietoja Chatilla omilta sivuiltasi tai Tiedot-osiosta.',
  knowledgeWithChat: 'Lisää Chatilla',
  knowledgeChangeWithChat: 'Muuta Chatilla',
  knowledgePrompt:
    'Auta minua valitsemaan, mitä {name}-ostoavustaja ({domain}) kertoo ostajille tuotteidemme lisäksi, kuten toimituksista, palautuksista ja maksuista. Etsi tiedot omilta sivuiltamme tai Tiedoista ja ehdota muistiinpanoja, jotka voin hyväksyä.',
  pagesTitle: 'Sivut, joita se lukee',
  pagesBody:
    'Sivut, joista muistiinpanot ovat peräisin. Poneva tarkistaa ne joka päivä, ja Chat kertoo, kun jokin niistä muuttuu.',
  pagesEmpty:
    'Ei vielä sivuja. Tässä näkyvät kaupan lukemisen löytämät sivut sekä Chatissa nimetyt sivut.',
  pageFound: 'Ponevan löytämä',
  pageLinkedFrom: 'linkitetty sivulta {page}',
  pageAddedByYou: 'lisätty käsin',
  pageFromNote: 'mainittu muistiinpanossa',
  pageNoteOne: '1 muistiinpano',
  pageNotesMany: '{count} muistiinpanoa',
  pageStatusOk: 'Ajan tasalla',
  pageStatusChanged: 'Muuttunut',
  pageStatusUnreadable: 'Ei voi lukea',
  pageStatusUnchecked: 'Ei vielä tarkistettu',
  pagesChangeWithChat: 'Muuta Chatilla',
  pagesUpdateWithChat: 'Päivitä muistiinpanot Chatilla',
  pagesPrompt:
    'Auta minua muuttamaan, mitä sivuja {name} ({domain}) ostosavustaja lukee muistiinpanoihinsa. Näytä minulle sivut, joita se käyttää nyt, ja kysy, mitkä lisätään tai poistetaan.',
  pagesUpdatePrompt:
    'Osa sivuista {name} ({domain}) on muuttunut tai niitä ei voi enää lukea muistiinpanojen tekemisen jälkeen. Näytä minulle, mitkä ne ovat, ja tee muistiinpanot niistä uudelleen luonnoksena, jonka voin tarkistaa.',
  viewCompare: 'Vertailu',
  lookDeviceLabel: 'Vertaa laitteella',
  lookShop: 'Kauppasi',
  lookShopCaption: 'Etusivu, luettu {when}',
  lookWidget: 'Avustaja',
  lookWidgetCaption: 'Versio {version}',
  lookLoading: 'Avustajaa asetetaan kauppasi viereen…',
  lookFailed: 'Vertailua ei voitu tehdä juuri nyt.',
  lookNoShopPicture: 'Lukeminen ei tallentanut kuvaa kaupastasi tällä leveydellä.',
  lookNoneTitle: 'Kauppasi omaa ulkoasua ei tallennettu',
  lookNoneBody:
    'Tämä lukeminen ei tallentanut sitä. Lue kauppa uudelleen Aloituksesta, niin sen logo, värit ja sivut tallennetaan vertailua varten.',
  lookColoursShop: 'Kauppasi käyttämät värit',
  lookColoursAssistant: 'Avustajan värit',
  lookRoleHeader: 'Ylätunniste',
  lookRoleBrand: 'Brändi',
  lookSame: 'Sama kuin kauppasi väri {colour} ({source})',
  lookOff: 'Lähellä kauppasi väriä {colour} ({source}) mutta ei sama, ero {delta}',
  lookOwn: 'Oma väri: kauppasi ei käytä sitä',
  lookNeutral: 'Kauppaasi ei ladattu tätä varten: näytetään vain se, minkä lukeminen tallensi.',
  lookFromLogo: 'logo',
  lookFromTheme: 'teemaväri',
  lookFromTile: 'ruutuväri',
  lookFromMask: 'kiinnitetty kuvake',
  lookFromManifest: 'sovelluksen manifesti',
  lookFromHeader: 'ylätunniste',
  lookFromButton: 'painikkeet',
  lookFromVariable: 'tyyliväri',
  lookFix: 'Korjaa Chatilla',
  lookFixPrompt:
    'Avustajan väri ({role}) on {colour}, lähellä kauppasi väriä {exact} ({source}) mutta ei sama. Käytä kauppasi tarkkaa väriä {exact}.',
  lookSwatch: 'Väri {colour}',
  errorDesignChecks: 'Tämä versio ei läpäissyt suunnittelutarkistuksia.',
  designSamplesBlocked:
    'Korttien asettelua ei voi todentaa tässä: sivustosi estää automaattiset vierailut. Värit, fontit ja logo toimivat silti.',
  designCheckLabel: 'Tekoälytunniste on liian haalea luettavaksi.',
  designCheckReach: 'Painike on tavoittamattomissa.',
  designCheckSize: 'Osa tekstistä on liian pientä.',
  designCheckOverflow: 'Paneeli vierii sivuttain.',
  designCheckRender: 'Tarkistuksia ei voitu suorittaa. Yritä hetken kuluttua uudelleen.',
  fixDesignPrompt:
    'Yritin julkaista kohteen {name} ({domain}) uusimman luonnoksen, mutta se ei läpäissyt suunnittelutarkistuksia: {facts}. Korjaa suunnittelu tässä keskustelussa ja tarkista se puhelimella ja tietokoneella. Älä julkaise sitä.',
};

const fr: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Assistant shopping',
  designUnconfirmed:
    'La procédure s’est arrêtée avant que Chat puisse confirmer l’enregistrement du design. Le canevas de l’assistant affiche sa version la plus récente.',
  errorNewerVersion:
    'Une version plus récente a été enregistrée depuis votre ouverture. Consultez-la d’abord, puis publiez-la.',
  canvasSelectHint:
    'Cliquez sur une partie de l’assistant pour la modifier dans le chat · Échap pour arrêter',
  canvasDraft: 'Version {version} · pas encore publiée',
  canvasDraftOverLive: 'Version {version} · la version {live} est publiée',
  canvasLive: 'Version {version} · publiée',
  canvasNoVersion: 'La première version sera créée une fois la lecture de la boutique terminée',
  canvasPublish: 'Publier sur la page Storefront',
  canvasFrameTitle: 'Assistant shopping de {name}',
  viewLabel: 'Afficher',
  photosGood: '{count} photos de produits : entières et nettes',
  photosGoodOne: 'La photo du produit est entière et nette',
  canvasMissingTitle: 'Cet assistant shopping ne se trouve pas dans cet espace de travail',
  canvasMissingBody: 'Il appartient à un autre espace de travail ou a été supprimé.',
  canvasMissingLink: 'Voir les assistants de cet espace de travail',
  photosLabel: 'Photos de produits',
  photosSmall: '{count} trop petites dans leur vignette',
  photosCut: '{count} recadrées',
  photosSoft: '{count} agrandies et floues',
  photosMissing: '{count} sans photo',
  photosFix: 'Corriger avec Chat',
  photosPrompt:
    'Faites en sorte que les photos de produits dans les cartes s’affichent correctement : entières, assez grandes dans leur vignette et nettes.',
  photosFacts:
    '{total} photos : {small} trop petites dans leur vignette, {cut} recadrées, {soft} agrandies, {missing} sans photo. Dimensions : {sizes}',
  viewDesign: 'Design',
  viewVersions: 'Versions',
  versionsLoading: 'Chargement des versions…',
  versionsFailed: 'Les versions n’ont pas pu être chargées.',
  versionTitle: 'Version {version}',
  versionLive: 'Publiée',
  versionChecked: 'Vérifiée',
  versionToday: 'Aujourd’hui',
  versionYesterday: 'Hier',
  versionEarlier: 'Plus tôt',
  versionByPerson: 'Demandée par {name}',
  versionByChat: 'Demandée dans le chat',
  versionFromReading: 'Issue de la lecture de {domain}',
  versionPublishedBy: 'publiée par {name}, {at}',
  versionPublish: 'Publier',
  versionRestore: 'Restaurer…',
  versionRestoreTitle: 'Restaurer la version {version} ?',
  versionRestoreBody:
    'Elle deviendra la version {next}, un nouveau brouillon. Votre site ne changera pas tant que vous ne l’aurez pas publiée.',
  versionRestoreConfirm: 'Restaurer',
  versionRestoreCancel: 'Annuler',
  versionRestored: 'Restaurée comme version {version}',
  versionRestoreFailed: 'La version n’a pas pu être restaurée.',
  compareTitle: 'Comparer la version {to} avec la version {from}',
  compareTitleLive: 'Comparer la version {to} avec la version {from}, publiée',
  compareHint: 'Faites glisser pour comparer',
  compareLabel: 'Comparer les versions {from} et {to}',
  compareTagLive: 'v{version} · Publiée',
  compareTag: 'v{version}',
  changesTitle: 'Qu’est-ce qui a changé entre les versions {from} et {to} ?',
  changesNone:
    'Les designs sont identiques. Seules les notes ou les données de la boutique ont changé.',
  changeAdded: 'Ajouté',
  changeRemoved: 'Supprimé',
  changeChanged: 'Modifié',
  changeDrawn: 'un logo dessiné',
  changeCss: 'son propre CSS',
  changeYes: 'oui',
  changeNo: 'non',
  'field.colors.brand': 'Couleur de la marque',
  'field.colors.actions': 'Couleur des boutons',
  'field.colors.panel': 'Couleur du panneau',
  'field.colors.surface': 'Couleur de la surface',
  'field.colors.ink': 'Couleur du texte',
  'field.colors.muted': 'Couleur du texte discret',
  'field.colors.line': 'Couleur des lignes',
  'field.colors.bubble': 'Couleur du message du client',
  'field.colors.bubbleInk': 'Texte du message du client',
  'field.colors.header': 'Couleur de l’en-tête',
  'field.colors.headerInk': 'Couleur du texte de l’en-tête',
  'field.colors.tints': 'Teintes douces',
  'field.copy.assistantName': 'Nom',
  'field.copy.greeting': 'Message d’accueil',
  'field.copy.placeholder': 'Champ de question',
  'field.copy.starters': 'Questions proposées',
  'field.copy.launcherLabel': 'Texte du bouton d’ouverture',
  'field.copy.aiLabel': 'Libellé de l’IA',
  'field.copy.personLabel': 'Lien vers une personne',
  'field.type.body': 'Police du texte',
  'field.type.display': 'Police des titres',
  'field.type.fontCss': 'Source de la police',
  'field.type.scale': 'Taille du texte',
  'field.shape.radius': 'Coins',
  'field.shape.shadow': 'Ombre',
  'field.shape.density': 'Espacement',
  'field.launcher.style': 'Bouton sur ordinateur',
  'field.launcher.phoneStyle': 'Bouton sur téléphone',
  'field.launcher.side': 'Côté du bouton',
  'field.launcher.icon': 'Icône du bouton',
  'field.panel.header': 'Taille de l’en-tête',
  'field.panel.opening': 'Premier écran',
  'field.products.layout': 'Produits',
  'field.products.imageFit': 'Photos de produits',
  'field.products.imageRatio': 'Forme de la vignette photo',
  'field.products.imagePadding': 'Espace autour des photos',
  'field.products.imageBackground': 'Couleur de la vignette photo',
  'field.products.picker': 'Choix de la taille',
  'field.products.showStock': 'Stock affiché',
  'field.cart.landing': 'Animation du panier',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Forme du logo',
  'field.logo.plate': 'Fond du logo',
  'field.css': 'CSS personnalisé',
  'value.ink': 'encre',
  'value.brand': 'couleur de la marque',
  'value.small': 'petit',
  'value.medium': 'moyen',
  'value.large': 'grand',
  'value.square': 'carré',
  'value.soft': 'doux',
  'value.round': 'rond',
  'value.flat': 'plat',
  'value.deep': 'profond',
  'value.compact': 'compact',
  'value.comfortable': 'confortable',
  'value.pill': 'pastille avec texte',
  'value.circle': 'rond',
  'value.right': 'droite',
  'value.left': 'gauche',
  'value.logo': 'logo',
  'value.chat': 'bulle de chat',
  'value.sparkle': 'étincelle',
  'value.bag': 'sac',
  'value.full': 'pleine largeur',
  'value.middle': 'au milieu',
  'value.bottom': 'en bas',
  'value.rows': 'lignes',
  'value.grid': 'grille',
  'value.carousel': 'une ligne à faire défiler',
  'value.contain': 'photo entière',
  'value.cover': 'vignette remplie',
  'value.portrait': 'portrait',
  'value.landscape': 'paysage',
  'value.none': 'aucun',
  'value.pop': "+1 qui s'élève",
  'value.ring': 'onde',
  'value.blink': 'clignotement',
  'value.chips': 'boutons sur une ligne',
  'value.menu': 'une liste',
  'value.rounded': 'arrondi',
  designCardTitle: 'Assistant shopping de {name}',
  designCardSaved: 'Version {version} enregistrée · pas encore publiée',
  designCardSavedFirst: 'Design enregistré · la première version l’utilisera',
  designSaving: 'Enregistrement du design…',
  selectedAssistantPart: 'Sélection : une partie de l’assistant',
  loading: 'Chargement de Storefront…',
  disabledTitle: 'Storefront n’est pas activé dans cet espace de travail.',
  disabledBody:
    'Storefront est en accès anticipé et arrive par étapes. Demandez à votre contact Poneva quand il sera disponible pour vous.',
  loadFailed: 'Storefront n’a pas pu être chargé.',
  startTitle: 'Un assistant shopping pour votre boutique',
  startBody:
    'Poneva lit votre boutique comme le ferait un client : recherche, produits, panier et chat que vous utilisez déjà. La lecture prend environ 10 à 30 minutes et utilise les crédits de votre entreprise ; vous pouvez continuer à travailler.',
  urlLabel: 'Adresse de votre boutique',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Lire ma boutique',
  starting: 'Démarrage…',
  startFailed: 'Cette boutique n’a pas pu être lue. Vérifiez l’adresse et réessayez.',
  tabConversations: 'Conversations',
  tabOverview: 'Vue d’ensemble',
  allAssistants: 'Tous les assistants',
  allAssistantsNote: '{assistants} assistants · {sites} sites',
  allAssistantsMenuNote: '{count} conversations cette semaine',
  marketLabel: 'Marché',
  allMarkets: 'Tous les marchés',
  allMarketsNote: '{count} sites',
  scopeIn: '{assistant} sur {market}',
  askAbout: 'Demander à Chat des informations sur {scope}',
  overviewAssistantsTitle: 'Assistants et marchés',
  overviewMarketsTitle: 'Marchés',
  overviewAssistantsOnly: 'Assistants',
  overviewIdle: '{count} assistants sans conversation pour le moment',
  allAssistantsCount: '{assistants} assistants',
  overviewFailed: 'La vue d’ensemble n’a pas pu être chargée.',
  colConversations: 'Conversations',
  colCart: 'Ajouts au panier',
  colCheckout: 'Passages au paiement',
  colPerson: 'Personne',
  colCost: 'Crédits',
  rowMarkets: '{count} marchés',
  rowVersion: 'version {version}',
  rowAria:
    '{name}, {sub} : {conversations} conversations, {cart} ajouts au panier, {checkout} passages au paiement, {person} demandes de contact, {cost} crédits',
  versionPublishedAt: 'publiée {at}',
  designColorHeader: 'En-tête',
  designColorHeaderInk: 'Texte de l’en-tête',
  designGroupTranslations: 'Mots dans d’autres langues',
  topicsTitle: 'Questions des clients',
  topicsNote: 'Sujets trouvés par Chat dans les premières questions',
  startPagesTitle: 'Pages où commencent les conversations',
  startPagesOne: 'Chaque conversation a commencé sur un type de page : {page}.',
  'startPage.product': 'Pages de produits',
  'startPage.search': 'Résultats de recherche',
  'startPage.start': 'Page d’accueil',
  'startPage.cart': 'Panier',
  'startPage.other': 'Autres pages',
  addMarketInChat: 'Ajouter un marché dans le chat',
  marketsTitle: 'Marchés',
  marketsBody:
    'Un assistant et un design. Chaque marché possède sa propre langue, sa devise, ses produits, son panier et son chat pour contacter une personne.',
  marketChecked: 'vérifié {when}',
  'marketStatus.ready': 'Prêt',
  'marketStatus.found': 'Non ajouté',
  'marketStatus.checking': 'Vérification',
  'marketStatus.failed': 'Correction nécessaire',
  addMarketPrompt:
    'Ajoutez un autre marché à l’assistant shopping de {name} ({domain}) : trouvez les autres pays, langues ou sites de la boutique et demandez-moi lequel ajouter.',
  tabWidget: 'Widget',
  tabInstall: 'Installation',
  tabSetup: 'Configuration',
  statusReading: 'Lecture de votre boutique',
  readingProducts: 'Nouvelle lecture des produits',
  readingCart: 'Nouvelle vérification du panier',
  readingChat: 'Vérification de l’accès à votre propre chat',
  readingMarket: 'Ajout du marché {market}',
  readingMarketAny: 'Ajout d’un marché',
  statusReady: 'Prêt à installer',
  statusFailed: 'Lecture arrêtée',
  statusLive: 'En ligne · version {version} sur {domain}',
  statusPublished: 'Publiée · version {version} · pas encore sur votre site',
  statusPublishedUnverified: 'Publiée · version {version} · votre site n’a pas pu être vérifié',
  stepOpen: 'Ouverture du site',
  stepSearch: 'Recherche',
  stepProducts: 'Produits',
  stepCart: 'Panier',
  stepChat: 'Votre chat existant',
  stepLook: 'Votre style et votre logo',
  stepChecks: 'Vérification du fonctionnement',
  stepReady: 'Prêt',
  stepDetailOpen: 'Ouverture de {domain}',
  stepDetailSearch: 'Recherche des produits de la boutique',
  stepDetailProducts: 'Lecture des produits, prix, photos, couleurs et tailles',
  stepDetailCart: 'Test du panier : ajout, relecture, retrait et vidage',
  stepDetailChat: 'Recherche de votre chat existant',
  stepDetailLook: 'Lecture du logo et des couleurs de la boutique',
  stepDetailChecks: 'Test des recherches clients, des produits, de la navigation et du panier',
  stepDetailChecksProducts:
    'Vérification des recherches de produits, de la navigation, des photos, des couleurs et des tailles',
  stepDetailChecksCart:
    'Vérification de l’ajout, de la relecture, du retrait et du vidage du panier, ainsi que de la lecture des produits',
  stepDetailChecksChat:
    'Vérification du transfert vers votre chat existant et de la lecture des produits',
  stepDetailChecksMarket: 'Vérification des produits, prix et panier de ce marché',
  nowRunning: 'En cours : {step}',
  nowProgress: 'Étape {n} sur {total} · {minutes} min écoulées',
  nowReady: 'Votre boutique est prête pour un assistant',
  nowReadyBody: 'La recherche, les produits et le panier fonctionnent.',
  nowLastRead: 'Dernière lecture {when}',
  nowUsually: 'généralement {min}–{max}',
  stepsRead: 'Lecture {when} · Afficher les étapes',
  nowFailed: 'Lecture arrêtée',
  factAnswers: 'Répond aux clients avec',
  costCounting: 'Calcul en cours…',
  factPaid: 'Financé par',
  paidCompany: 'Crédits de l’entreprise',
  creditsAmount: '{amount} crédits',
  creditsOne: '{amount} crédit',
  creditsUnder: 'moins de {amount} crédits',
  stopRun: 'Arrêter l’exécution',
  stopping: 'Arrêt en cours…',
  closeNote: 'Vous pouvez quitter cette page. L’exécution continue.',
  readyNote: 'Ensuite : essayez votre widget, puis installez-le.',
  buildWidget: 'Essayer le widget',
  readAgain: 'Relire la boutique',
  foundTitle: 'Résultats trouvés',
  foundProducts: 'Produits et prix lus',
  foundSearch: 'La recherche fonctionne',
  foundCart: 'L’ajout au panier fonctionne',
  foundChat: 'Votre chat {vendor}',
  foundNoChat: 'Aucun chat à vous',
  foundLook: 'Vos couleurs et votre logo',
  widgetTitle: 'Votre widget',
  widgetBody:
    'Votre assistant, dans vos couleurs. Essayez-le ici : il répond avec vos vrais produits. Rien n’est ajouté à un vrai panier.',
  widgetLoading: 'Chargement de l’aperçu…',
  widgetFailed: 'L’aperçu n’a pas pu être chargé.',
  widgetNotReady: 'Le widget sera prêt une fois les étapes de l’onglet Configuration terminées.',
  installLineTitle: 'Ajoutez cette ligne à votre site',
  installLineBody:
    'Elle fonctionne sur toutes les plateformes : dans l’en-tête de vos pages, ou sur Shopify dans theme.liquid juste avant </head>. Rien ne s’affiche tant que vous ne publiez pas.',
  installLineBodyLive:
    'Elle fonctionne sur toutes les plateformes : dans l’en-tête de vos pages, ou sur Shopify dans theme.liquid juste avant </head>. Les clients verront alors la version {version}.',
  installPolicyAsk: 'Votre site définit-il une Content Security Policy ?',
  installPolicy:
    'Si votre site définit une Content Security Policy, autorisez également {origin} dans {directives}, sinon l’assistant ne pourra pas démarrer.',
  installPolicyRefuses:
    'La Content Security Policy de votre site refuse l’assistant : autorisez {origin} dans {directives}, sinon rien ne démarrera.',
  installPolicyNonce:
    'Votre politique n’exécute que les scripts approuvés ; ajoutez donc la ligne avec le nonce de vos pages.',
  installPolicyEval: "L’assistant a également besoin de 'unsafe-eval' dans script-src.",
  copy: 'Copier',
  copied: 'Copié',
  domainsTitle: 'Sites auxquels il répond',
  domainsBody: 'Il reste silencieux sur tout autre site, même s’il reprend cette ligne.',
  lineFound: 'Ligne trouvée',
  lineNotSeen: 'Pas encore présente sur la page',
  lineUnverified:
    'Vérification impossible : le site bloque les visites automatisées. L’assistant fonctionne quand même pour les clients.',
  lineChecked: 'vérifiée {when}',
  lineCheckAgain: 'Vérifier à nouveau',
  lineChecking: 'Vérification de la page…',
  installCheckBusy: 'La vérification est occupée pour le moment. Réessayez dans un instant.',
  publishTitle: 'Publier',
  publishBody:
    'Les clients sur {domain} le verront immédiatement. Vous pouvez le dépublier à tout moment.',
  publishButton: 'Publier la version {version}',
  publishConfirm: 'Publier la version {version} de {name} ?',
  publishNow: 'Publier',
  notNow: 'Pas maintenant',
  publishing: 'Publication en cours…',
  liveTitle: 'Publiée : version {version}',
  liveSince: 'Depuis {since}',
  unpublish: 'Dépublier',
  unpublishConfirm:
    'Retirer la version {version} de votre site ? Les clients ne verront plus l’assistant jusqu’à une nouvelle publication.',
  publishedLine: 'Publiée maintenant : version {version}, depuis {since}',
  usageTitle: 'Utilisation',
  usageBody: 'Elle utilise les crédits de votre entreprise, comme le reste de Chat.',
  usageDiscovery: 'Lecture de la boutique',
  usageChats: 'Chats des clients',
  usageChatsValue: '{count} chats · {cost}',
  last7: 'Sept derniers jours',
  askWeek: 'Demander à Chat pour cette semaine',
  kpiConversations: 'Conversations',
  kpiVsPrevious: '{change} par rapport à la semaine précédente',
  kpiCart: 'Ajouté au panier',
  kpiCheckout: 'Passage à la caisse',
  kpiPerson: 'Demande de contact humain',
  kpiCost: 'Crédits utilisés',
  ofConversations: '{percent} des conversations',
  checkoutNote: '{percent} des conversations',
  personNote: '{percent} ont ouvert votre chat',
  costNote: 'Environ {amount} par conversation',
  costNoteUnder: 'Moins de {amount} par conversation',
  sparkLabel: 'Conversations par jour, sur les {days} derniers jours',
  sparkDay: '{day} : {count} conversations',
  filterAll: 'Tout',
  filterCart: 'Ajouté au panier',
  filterCheckout: 'Caisse',
  filterPerson: 'Contact humain',
  outcomeCart: 'Ajouté au panier',
  outcomeCheckout: 'Passage à la caisse',
  outcomePerson: 'Demande de contact humain',
  outcomeAnswered: 'Répondu',
  devicePhone: 'Téléphone',
  deviceComputer: 'Ordinateur',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Commencée sur {page}',
  anonymous: 'Le client est anonyme.',
  askConversation: 'Interroger Chat sur cette conversation',
  showOlderVersions: 'Afficher les versions précédentes',
  showOlder: 'Afficher les conversations plus anciennes',
  filterCountsWeek: 'Totaux : 7 derniers jours',
  readAlreadyHere:
    'Cette boutique est déjà présente : rien de nouveau n’a été lancé et aucun crédit n’a été utilisé.',
  readOpenShop: 'L’ouvrir',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Que pouvons-nous apprendre de cette conversation ?',
  askWeekMessage: 'Que pouvons-nous apprendre des conversations de cette semaine ?',
  conversationsChipOne: '{name} · conversation du {when}',
  conversationsChipWeek: '{name} · cette semaine',
  conversationsChipWeekIn: '{name} · {market} · cette semaine',
  removeConversations: 'Retirer les conversations',
  noConversations: 'Aucune conversation pour le moment',
  noConversationsBody:
    'Lorsque des clients utilisent l’assistant, leurs conversations apparaissent ici.',
  pickConversation: 'Choisissez une conversation à lire.',
  eventCheckout: 'Est allé à la caisse',
  eventPerson: 'A ouvert votre chat',
  showMore: 'Afficher plus',
  urlInvalid:
    'Utilisez l’adresse https de votre boutique, par exemple https://www.votreboutique.com.',
  errorNoCompany:
    'Storefront a besoin d’un espace de travail d’entreprise. Demandez à votre administrateur de vous ajouter.',
  errorPublishPermission:
    'Seules les personnes autorisées à publier pour votre entreprise peuvent faire cela. Demandez à votre administrateur.',
  actionFailed: 'Cela n’a pas fonctionné. Réessayez.',
  copyFailed: 'Impossible de copier. Sélectionnez la ligne et copiez-la vous-même.',
  reasonCancelled: 'Vous avez arrêté l’exécution.',
  reasonFailed:
    'La lecture n’a pas pu se terminer sur votre boutique ; l’assistant n’est donc pas prêt.',
  reasonFailedLive:
    'La lecture n’a pas pu se terminer sur votre boutique. L’assistant publié continue de fonctionner comme avant.',
  reasonFailedKept:
    'La lecture n’a pas pu se terminer sur votre boutique ; rien n’a donc changé : la version {version} reste telle quelle.',
  statusReadStopped: 'Prêt · la dernière lecture a été arrêtée',
  reasonStepMarked: 'L’étape qui s’est arrêtée est indiquée ci-dessous.',
  reasonReadAgain: 'Une nouvelle lecture commence à partir de ce que celle-ci a appris.',
  reasonNotAStore:
    'Cette adresse ne ressemble pas à une boutique en ligne : aucun produit n’était disponible pour la recherche. Vérifiez l’adresse ou essayez le domaine principal de la boutique.',
  reasonTooSlow:
    'La recherche de votre boutique répond trop lentement pour un assistant shopping : elle a pris plus de 12 secondes. Une recherche plus rapide sur la boutique, ou une nouvelle lecture plus tard, peut aider.',
  reasonOurSide:
    'La lecture s’est arrêtée à cause d’un problème de notre côté, pas de votre boutique ; l’assistant n’est donc pas encore prêt.',
  reasonOurSideLive:
    'La lecture s’est arrêtée à cause d’un problème de notre côté, pas de votre boutique. L’assistant publié continue de fonctionner comme avant.',
  reasonOurSideKept:
    'La lecture s’est arrêtée à cause d’un problème de notre côté, pas de votre boutique ; rien n’a donc changé : la version {version} reste telle quelle.',
  reasonTryLater: 'Réessayez de lire la boutique d’ici un moment.',
  errorNoCredits:
    'Aucun crédit n’est disponible pour cela pour le moment. Un propriétaire de l’entreprise peut vérifier l’offre et les limites dans Usage.',
  errorReadUnavailable:
    'Cette boutique ne peut pas être lue pour le moment. Réessayez dans quelques minutes.',
  reasonTimeout:
    'La lecture a atteint sa limite de temps avant de se terminer. Une nouvelle lecture reprend à partir de ce qui a déjà été vérifié.',
  retrying: 'Redémarrage…',
  readTitleChecks: 'Vérifier à nouveau la lecture enregistrée',
  checksScopeLabel: 'Vérifications',
  readScopeChecks:
    'Recherches des clients, produits, navigation et panier, sur ce que la dernière lecture a enregistré. La boutique n’est pas relue.',
  readScopeChecksProducts:
    'Recherches de produits, navigation, photos, couleurs et tailles, sur ce que la dernière lecture a enregistré. La boutique n’est pas relue.',
  readScopeChecksCart:
    'Ajout, relecture, retrait et vidage du panier, ainsi que lecture des produits, sur ce que la dernière lecture a enregistré. La boutique n’est pas relue.',
  readScopeChecksChat:
    'Transfert vers votre chat existant et lecture des produits, sur ce que la dernière lecture a enregistré. La boutique n’est pas relue.',
  readScopeChecksMarket:
    'Produits, prix et panier du marché, sur ce que la dernière lecture a enregistré. La boutique n’est pas relue.',
  checksCost: 'Seules les nouvelles vérifications utilisent les crédits de l’entreprise',
  checksAfter:
    'Enregistre un nouveau brouillon si les vérifications réussissent. Rien n’est visible par les clients avant la publication.',
  checksAfterLive:
    'Enregistre un nouveau brouillon si les vérifications réussissent. L’assistant publié continue de fonctionner et les clients ne voient la modification qu’après sa publication.',
  checksStarted:
    'Les vérifications ont démarré. Lorsque le nouveau brouillon est prêt, demandez à Chat de le vérifier.',
  checkingSavedWork: 'Nouvelle vérification du travail enregistré',
  checkAgain: 'Vérifier à nouveau',
  checkingAgain: 'Nouvelle vérification…',
  reasonCheckAgain:
    '« Vérifier à nouveau » teste une nouvelle fois le travail enregistré, sans relire la boutique. Seules les nouvelles vérifications utilisent les crédits de l’entreprise.',
  fixWithChat: 'Corriger avec Chat',
  fixReadPrompt:
    'La lecture de {name} ({domain}) s’est arrêtée. Regardez ce qui s’est passé et aidez-moi à la faire fonctionner : dites-moi ce que vous trouvez, proposez ce que je pourrais indiquer à la lecture, puis vérifiez-la à nouveau ou relancez-la avec cette indication, en relisant le moins possible de la boutique.',
  fixReadPromptStep:
    'La lecture de {name} ({domain}) s’est arrêtée à l’étape « {step} ». Regardez ce qui s’est passé et aidez-moi à la faire fonctionner : dites-moi ce que vous trouvez, proposez ce que je pourrais indiquer à la lecture, puis vérifiez-la à nouveau ou relancez-la avec cette indication, en relisant le moins possible de la boutique.',
  stepStateDone: 'terminée',
  stepStateRunning: 'en cours',
  stepStateWaiting: 'pas commencée',
  stepStateFailed: 'arrêtée',
  newVersionTitle: 'La version {version} est prête',
  newVersionBody: 'Publiez-la pour remplacer la version {live}.',
  newVersionBodyLive: 'Publiez-la pour remplacer la version {live} sur {domain}.',
  previewSize: 'Taille de l’aperçu',
  designWithChat: 'Ouvrir le design',
  designReviewBody: 'Regardez le design actuel, puis dites à Chat ce que vous souhaitez ajuster.',
  designWhileTitle: 'Concevez-le pendant que nous lisons votre boutique',
  designWhileBody:
    'Chat peut dès maintenant façonner avec vous l’apparence, les textes et le logo de l’assistant. Le design sera prêt lorsque la lecture le sera.',
  designPreviewNote:
    'Un aperçu avec des produits d’exemple en attendant la lecture de votre boutique.',
  proposedDesign: 'Design proposé',
  proposedDesignNote:
    'Le widget avec cette modification, affiché avec des produits d’exemple. L’approbation l’enregistre comme nouveau brouillon ; la publication se fait sur la page Storefront.',
  lineSources: 'D’après les pages de la boutique :',
  readAgainConfirm:
    'Relire {name} ? Cela prend environ 10 à 30 minutes et utilise les crédits de l’entreprise.',
  readAgainLive:
    'L’assistant publié continue de fonctionner jusqu’à la publication de la nouvelle version.',
  readAgainNow: 'Relire',
  switchStore: 'Changer de boutique ou en ajouter une',
  storesLabel: 'Vos boutiques',
  addStore: 'Ajouter une autre boutique',
  backToStores: 'Retour à vos boutiques',
  storeLive: 'Publiée',
  storeReady: 'Prête',
  storeReading: 'Lecture en cours',
  storeStopped: 'Arrêtée',
  actionDesignTitle: 'Modifier le design de l’assistant shopping',
  actionKnowledgeTitle: 'Modifier ce que l’assistant shopping sait',
  readTitleProducts: 'Relire les produits',
  readTitleCart: 'Vérifier à nouveau le panier',
  readTitleTheirChat: 'Vérifier à nouveau l’accès à votre propre chat',
  readTitleAll: 'Relire toute la boutique',
  readTitleStore: 'Lire une nouvelle boutique',
  readTitleMarket: 'Ajouter un marché',
  readShopLabel: 'Boutique',
  readScopeLabel: 'Lecture',
  readTimeLabel: 'Durée',
  readCostLabel: 'Coût',
  readAfterLabel: 'Après',
  readScopeProducts: 'Recherche et tri des produits, photos, couleurs et tailles des produits',
  readScopeCart: 'Ajout et retrait d’articles du panier',
  readScopeTheirChat: 'Comment les clients accèdent à votre propre chat',
  readScopeAll: 'Produits, panier, votre propre chat et vos marchés',
  readScopeStore:
    'Produits, prix et tailles, panier, votre propre chat, votre identité visuelle et vos marchés',
  readScopeMarket: '{market} : ses produits, ses prix et son panier',
  readTime: 'Généralement {min}–{max} minutes',
  readCost: 'Utilise les crédits de l’entreprise',
  readCostLast:
    'Utilise les crédits de l’entreprise · la dernière lecture de cette boutique a utilisé {credits}',
  readCostLastPartial:
    'Utilise les crédits de l’entreprise · la dernière lecture de cette boutique, arrêtée prématurément, a utilisé au moins {credits}',
  readAfter:
    'Enregistre un nouveau brouillon. Rien n’est visible par les clients avant la publication.',
  readAfterLive:
    'Enregistre un nouveau brouillon. L’assistant publié continue de fonctionner et les clients ne voient la modification qu’après sa publication.',
  readAfterNew: 'Enregistre un premier brouillon à essayer. Rien n’est publié.',
  readCartNote:
    'Pour vérifier le panier, un article test y est ajouté puis retiré. Aucun achat n’est jamais effectué.',
  readFocusLabel: 'Ce que Chat a demandé à la lecture de corriger',
  readFooter:
    'Rien ne démarre avant votre approbation. Vous pouvez arrêter la lecture dans l’onglet Configuration.',
  readStartedAt:
    'Démarrée à {time} · généralement {min}–{max} minutes. Lorsque le nouveau brouillon est prêt, demandez à Chat de le vérifier.',
  readFocusAll: 'Tout afficher',
  readFocusLess: 'Afficher moins',
  readStart: 'Démarrer la lecture',
  addToSite: 'L’ajouter à votre site',
  readTab: 'Demande',
  readStarted:
    'La lecture a démarré. Lorsque le nouveau brouillon est prêt, demandez à Chat de le vérifier.',
  readFollow: 'Suivre la lecture dans l’onglet Configuration',
  proposedDesignLive:
    'L’aperçu réel de votre boutique dans ce design. Posez-lui une question : rien n’est ajouté à un vrai panier. L’approbation l’enregistre comme nouveau brouillon ; la publication se fait sur la page Storefront.',
  designChanges: 'Modifications',
  designReset: 'revenir aux valeurs par défaut',
  designGroupColors: 'Couleurs',
  designGroupType: 'Polices',
  designGroupShape: 'Forme',
  designGroupLauncher: 'Bouton sur la page',
  designGroupPanel: 'En-tête',
  designGroupProducts: 'Produits',
  designGroupCart: 'Panier de la boutique',
  designGroupCopy: 'Textes',
  designGroupLogo: 'Logo',
  designGroupCss: 'CSS personnalisé',
  designColorBrand: 'Marque',
  designColorActions: 'Ajouter et envoyer',
  designColorPanel: 'Panneau',
  designColorSurface: 'Surfaces',
  designColorInk: 'Texte',
  designColorMuted: 'Texte secondaire',
  designColorLine: 'Lignes',
  designColorBubble: 'Messages du client',
  designColorBubbleInk: 'Texte du client',
  designActionsBrand: 'Couleur de la marque',
  designActionsInk: 'Couleur du texte',
  designWordsName: 'Nom',
  designWordsGreeting: 'Message d’accueil',
  designWordsStarters: 'Questions suggérées',
  designWordsLauncher: 'Libellé du bouton',
  designWordsPlaceholder: 'Texte indicatif',
  designWordsAi: 'Libellé IA',
  designWordsPerson: 'Lien vers une personne',
  noteNew: 'Nouveau',
  noteChanged: 'Modifié',
  noteRemoved: 'Supprimé',
  noteSourceKnowledge: 'Page de connaissances : {title}',
  noteSourceFile: 'Fichier : {name}',
  noteSourceChat: 'Depuis le chat',
  knowledgeApprovalNote:
    'Les clients peuvent lire tout le contenu de ces notes. L’approbation enregistre un nouveau brouillon : interrogez-le dans l’onglet Widget, puis publiez-le dans l’onglet Installation.',
  profileTitle: 'À propos de la boutique',
  profileChecked: 'Vérifié le {date}',
  profileSources: 'Rédigé à partir de',
  profileSample: '{count} produits échantillonnés pour le niveau de prix',
  profileSampleOne: '{count} produit échantillonné pour le niveau de prix',
  profileAssistant: 'Ce que l’assistant peut faire ici',
  profileTeam: 'Rédigé par votre équipe',
  profileEmpty:
    'Pas encore rédigé. Demandez à Chat de lancer une passe de connaissances : il l’écrit à partir de vos propres pages et de ce qu’il a vu sur votre site, et vous pouvez ajouter vos propres lignes.',
  profileWithChat: 'Ajouter ou modifier avec Chat',
  profilePrompt:
    'Aide-moi à améliorer ce que l’assistant d’achat de {name} ({domain}) sait de notre boutique dans son ensemble : ce que nous vendons, ce qui nous distingue, pour qui nous sommes là et ce que les clients peuvent faire ici. Lis d’abord le profil « À propos de la boutique » et dis-moi ce qui manque ou est faux. Tout ce que je te dis sur la boutique et qu’aucune page n’indique est ajouté comme ligne de notre équipe.',
  profilePartNavigation: 'Rayons',
  profilePartWelcome: 'Message d’accueil',
  profilePartMarkets: 'Marchés',
  profilePartFunctions: 'Ce que l’assistant peut faire',
  profilePartProducts: 'Produits',
  profilePartPages: 'Pages de service',
  profileCleared: 'Efface le profil rédigé',
  knowledgeTitle: 'Ce qu’il sait',
  knowledgeBody:
    'En plus de vos produits, de vos prix et de vos stocks, qu’il lit toujours en direct depuis votre boutique.',
  knowledgeEmpty:
    'Pour l’instant, uniquement votre catalogue. Ajoutez des informations sur la livraison, les retours ou le paiement avec Chat, depuis vos propres pages ou Connaissances.',
  knowledgeWithChat: 'Ajouter avec Chat',
  knowledgeChangeWithChat: 'Modifier avec Chat',
  knowledgePrompt:
    'Aidez-moi à choisir ce que l’assistant shopping de {name} ({domain}) doit dire aux clients en plus de nos produits, comme la livraison, les retours et le paiement. Trouvez ces informations sur nos pages ou dans Connaissances, puis proposez des notes que je pourrai approuver.',
  pagesTitle: 'Pages qu’il lit',
  pagesBody:
    'Les pages d’où viennent les notes. Poneva les vérifie chaque jour, et Chat vous prévient quand l’une change.',
  pagesEmpty:
    'Aucune page pour l’instant. Les pages trouvées par la lecture de votre boutique, et celles que vous nommez dans Chat, s’affichent ici.',
  pageFound: 'trouvée par Poneva',
  pageLinkedFrom: 'lié depuis {page}',
  pageAddedByYou: 'ajoutée par vous',
  pageFromNote: 'citée dans une note',
  pageNoteOne: '1 note',
  pageNotesMany: '{count} notes',
  pageStatusOk: 'À jour',
  pageStatusChanged: 'Modifiée',
  pageStatusUnreadable: 'Impossible à lire',
  pageStatusUnchecked: 'Pas encore vérifiée',
  pagesChangeWithChat: 'Modifier avec Chat',
  pagesUpdateWithChat: 'Mettre à jour les notes avec Chat',
  pagesPrompt:
    'Aidez-moi à changer les pages que l’assistant d’achat de {name} ({domain}) lit pour ses notes. Montrez-moi les pages qu’il utilise actuellement et demandez-moi lesquelles ajouter ou retirer.',
  pagesUpdatePrompt:
    'Certaines pages de {name} ({domain}) ont changé ou ne peuvent plus être lues depuis la création des notes. Montrez-moi lesquelles, et refaites les notes à partir d’elles sous forme de brouillon que je peux consulter.',
  viewCompare: 'Comparaison',
  lookDeviceLabel: 'Comparer sur',
  lookShop: 'Votre boutique',
  lookShopCaption: 'Page d’accueil, lue {when}',
  lookWidget: 'L’assistant',
  lookWidgetCaption: 'Version {version}',
  lookLoading: 'Mise en place de l’assistant à côté de votre boutique…',
  lookFailed: 'La comparaison n’a pas pu être réalisée pour le moment.',
  lookNoShopPicture: 'La lecture n’a pas conservé d’image de votre boutique à cette largeur.',
  lookNoneTitle: 'Le style propre à votre boutique n’a pas été conservé',
  lookNoneBody:
    'Cette lecture ne l’a pas enregistré. Relisez la boutique depuis Configuration, et son logo, ses couleurs et ses pages seront conservés pour la comparaison.',
  lookColoursShop: 'Couleurs utilisées par votre boutique',
  lookColoursAssistant: 'Couleurs de l’assistant',
  lookRoleHeader: 'En-tête',
  lookRoleBrand: 'Marque',
  lookSame: 'Identique à la couleur {colour} de votre boutique ({source})',
  lookOff:
    'Proche de la couleur {colour} de votre boutique ({source}) mais pas identique, écart {delta}',
  lookOwn: 'Couleur propre : votre boutique ne l’utilise pas',
  lookNeutral:
    'Votre boutique n’a pas été chargée pour cela : seul ce que la lecture a conservé est affiché.',
  lookFromLogo: 'logo',
  lookFromTheme: 'couleur du thème',
  lookFromTile: 'couleur de la vignette',
  lookFromMask: 'icône épinglée',
  lookFromManifest: 'manifeste de l’application',
  lookFromHeader: 'en-tête',
  lookFromButton: 'boutons',
  lookFromVariable: 'couleur du style',
  lookFix: 'Corriger avec Chat',
  lookFixPrompt:
    'La couleur de l’assistant pour {role} est {colour}, proche de la couleur {exact} de votre boutique ({source}) mais pas identique. Utilisez la couleur exacte de votre boutique, {exact}.',
  lookSwatch: 'Couleur {colour}',
  errorDesignChecks: 'Cette version n’a pas passé les contrôles de conception.',
  designSamplesBlocked:
    'Les mises en page des cartes ne peuvent pas être prouvées ici : votre site bloque les visites automatisées. Les couleurs, les polices et le logo fonctionnent quand même.',
  designCheckLabel: 'Une étiquette IA est trop pâle pour être lue.',
  designCheckReach: 'Un bouton est inaccessible.',
  designCheckSize: 'Une partie du texte est trop petite.',
  designCheckOverflow: 'Le panneau défile latéralement.',
  designCheckRender: 'Les contrôles n’ont pas pu s’exécuter. Réessayez dans un instant.',
  fixDesignPrompt:
    'J’ai essayé de publier le dernier brouillon de {name} ({domain}), mais il n’a pas passé les contrôles de conception : {facts}. Corrigez la conception dans ce chat et vérifiez-la sur téléphone et sur ordinateur. Ne la publiez pas.',
};

const de: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Einkaufsassistent',
  designUnconfirmed:
    'Beendet, bevor Chat bestätigen konnte, dass das Design gespeichert wurde. Das Canvas des Assistenten zeigt die neueste Version.',
  errorNewerVersion:
    'Seit dem Öffnen dieser Version wurde eine neuere Version gespeichert. Sehen Sie sie sich zuerst an und veröffentlichen Sie sie anschließend.',
  canvasSelectHint:
    'Klicken Sie auf einen Teil des Assistenten, um ihn im Chat zu ändern · Esc zum Beenden',
  canvasDraft: 'Version {version} · noch nicht veröffentlicht',
  canvasDraftOverLive: 'Version {version} · Version {live} ist veröffentlicht',
  canvasLive: 'Version {version} · veröffentlicht',
  canvasNoVersion: 'Die erste Version entsteht, wenn das Lesen des Shops abgeschlossen ist',
  canvasPublish: 'Auf der Storefront-Seite veröffentlichen',
  canvasFrameTitle: 'Einkaufsassistent für {name}',
  viewLabel: 'Ansicht',
  photosGood: '{count} Produktfotos: vollständig und scharf',
  photosGoodOne: 'Das Produktfoto ist vollständig und scharf',
  canvasMissingTitle: 'Dieser Einkaufsassistent ist nicht in diesem Arbeitsbereich',
  canvasMissingBody: 'Er gehört zu einem anderen Arbeitsbereich oder wurde entfernt.',
  canvasMissingLink: 'Assistenten dieses Arbeitsbereichs anzeigen',
  photosLabel: 'Produktfotos',
  photosSmall: '{count} zu klein in ihren Feldern',
  photosCut: '{count} abgeschnitten',
  photosSoft: '{count} vergrößert und deshalb unscharf',
  photosMissing: '{count} ohne Foto',
  photosFix: 'Mit Chat korrigieren',
  photosPrompt:
    'Sorge dafür, dass die Produktfotos in den Karten richtig aussehen: vollständig, groß genug für ihr Feld und scharf.',
  photosFacts:
    '{total} Fotos: {small} zu klein im Feld, {cut} abgeschnitten, {soft} vergrößert und unscharf, {missing} ohne Foto. Größen: {sizes}',
  viewDesign: 'Design',
  viewVersions: 'Versionen',
  versionsLoading: 'Versionen werden geladen…',
  versionsFailed: 'Die Versionen konnten nicht geladen werden.',
  versionTitle: 'Version {version}',
  versionLive: 'Veröffentlicht',
  versionChecked: 'Geprüft',
  versionToday: 'Heute',
  versionYesterday: 'Gestern',
  versionEarlier: 'Früher',
  versionByPerson: 'Angefragt von {name}',
  versionByChat: 'Im Chat angefragt',
  versionFromReading: 'Aus dem Lesen von {domain}',
  versionPublishedBy: 'veröffentlicht von {name}, {at}',
  versionPublish: 'Veröffentlichen',
  versionRestore: 'Wiederherstellen…',
  versionRestoreTitle: 'Version {version} wiederherstellen?',
  versionRestoreBody:
    'Sie wird zu Version {next}, einem neuen Entwurf. Ihre Website ändert sich erst, wenn Sie veröffentlichen.',
  versionRestoreConfirm: 'Wiederherstellen',
  versionRestoreCancel: 'Abbrechen',
  versionRestored: 'Als Version {version} wiederhergestellt',
  versionRestoreFailed: 'Die Version konnte nicht wiederhergestellt werden.',
  compareTitle: 'Version {to} mit Version {from} vergleichen',
  compareTitleLive: 'Version {to} mit der veröffentlichten Version {from} vergleichen',
  compareHint: 'Zum Vergleichen ziehen',
  compareLabel: 'Version {from} und Version {to} vergleichen',
  compareTagLive: 'v{version} · Veröffentlicht',
  compareTag: 'v{version}',
  changesTitle: 'Was sich von Version {from} zu {to} geändert hat',
  changesNone: 'Die Designs sind gleich. Nur die Notizen oder die Daten des Shops wurden geändert.',
  changeAdded: 'Hinzugefügt',
  changeRemoved: 'Entfernt',
  changeChanged: 'Geändert',
  changeDrawn: 'ein gezeichnetes Logo',
  changeCss: 'eigenes CSS',
  changeYes: 'ja',
  changeNo: 'nein',
  'field.colors.brand': 'Markenfarbe',
  'field.colors.actions': 'Farbe der Schaltflächen',
  'field.colors.panel': 'Farbe des Bedienfelds',
  'field.colors.surface': 'Oberflächenfarbe',
  'field.colors.ink': 'Textfarbe',
  'field.colors.muted': 'Farbe des dezenten Texts',
  'field.colors.line': 'Linienfarbe',
  'field.colors.bubble': 'Farbe der Kundennachricht',
  'field.colors.bubbleInk': 'Text der Kundennachricht',
  'field.colors.header': 'Farbe der Kopfzeile',
  'field.colors.headerInk': 'Textfarbe der Kopfzeile',
  'field.colors.tints': 'Weiche Farbtöne',
  'field.copy.assistantName': 'Name',
  'field.copy.greeting': 'Begrüßung',
  'field.copy.placeholder': 'Fragefeld',
  'field.copy.starters': 'Startfragen',
  'field.copy.launcherLabel': 'Text der Startschaltfläche',
  'field.copy.aiLabel': 'KI-Bezeichnung',
  'field.copy.personLabel': 'Link zu einer Person',
  'field.type.body': 'Textschrift',
  'field.type.display': 'Überschriftenschrift',
  'field.type.fontCss': 'Quelle der Schrift',
  'field.type.scale': 'Textgröße',
  'field.shape.radius': 'Ecken',
  'field.shape.shadow': 'Schatten',
  'field.shape.density': 'Abstände',
  'field.launcher.style': 'Startschaltfläche auf Computern',
  'field.launcher.phoneStyle': 'Startschaltfläche auf Smartphones',
  'field.launcher.side': 'Seite der Startschaltfläche',
  'field.launcher.icon': 'Symbol der Startschaltfläche',
  'field.panel.header': 'Größe der Kopfzeile',
  'field.panel.opening': 'Erster Bildschirm',
  'field.products.layout': 'Produkte',
  'field.products.imageFit': 'Produktfotos',
  'field.products.imageRatio': 'Form des Fotofelds',
  'field.products.imagePadding': 'Abstand um die Fotos',
  'field.products.imageBackground': 'Farbe des Fotofelds',
  'field.products.picker': 'Größenauswahl',
  'field.products.showStock': 'Bestand anzeigen',
  'field.cart.landing': 'Warenkorb-Animation',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Form des Logos',
  'field.logo.plate': 'Logo-Hintergrund',
  'field.css': 'Benutzerdefiniertes CSS',
  'value.ink': 'Tinte',
  'value.brand': 'Markenfarbe',
  'value.small': 'klein',
  'value.medium': 'mittel',
  'value.large': 'groß',
  'value.square': 'quadratisch',
  'value.soft': 'weich',
  'value.round': 'rund',
  'value.flat': 'flach',
  'value.deep': 'tief',
  'value.compact': 'kompakt',
  'value.comfortable': 'luftig',
  'value.pill': 'abgerundete Schaltfläche mit Text',
  'value.circle': 'rund',
  'value.right': 'rechts',
  'value.left': 'links',
  'value.logo': 'Logo',
  'value.chat': 'Chatblase',
  'value.sparkle': 'Funkeln',
  'value.bag': 'Tasche',
  'value.full': 'vollständig',
  'value.middle': 'in der Mitte',
  'value.bottom': 'unten',
  'value.rows': 'Zeilen',
  'value.grid': 'Raster',
  'value.carousel': 'eine Zeile zum Wischen',
  'value.contain': 'vollständiges Foto',
  'value.cover': 'gefülltes Feld',
  'value.portrait': 'Hochformat',
  'value.landscape': 'Querformat',
  'value.none': 'keine',
  'value.pop': 'aufsteigendes +1',
  'value.ring': 'Welle',
  'value.blink': 'Blinken',
  'value.chips': 'Schaltflächen in einer Zeile',
  'value.menu': 'eine Liste',
  'value.rounded': 'abgerundet',
  designCardTitle: 'Einkaufsassistent für {name}',
  designCardSaved: 'Version {version} gespeichert · noch nicht veröffentlicht',
  designCardSavedFirst: 'Design gespeichert · die erste Version erhält es',
  designSaving: 'Design wird gespeichert…',
  selectedAssistantPart: 'Ausgewählt: ein Teil des Assistenten',
  loading: 'Storefront wird geladen…',
  disabledTitle: 'Storefront ist in diesem Arbeitsbereich nicht aktiviert.',
  disabledBody:
    'Die Funktion befindet sich im Early Access und wird schrittweise bereitgestellt. Fragen Sie Ihren Poneva-Kontakt, wann sie für Sie verfügbar ist.',
  loadFailed: 'Storefront konnte nicht geladen werden.',
  startTitle: 'Ein Einkaufsassistent für Ihren Shop',
  startBody:
    'Poneva liest Ihren Shop so, wie ihn ein Käufer nutzt: Suche, Produkte, Warenkorb und den bereits vorhandenen Chat. Das dauert etwa 10 bis 30 Minuten, verwendet die Credits Ihres Unternehmens, und Sie können weiterarbeiten.',
  urlLabel: 'Adresse Ihres Shops',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Meinen Shop lesen',
  starting: 'Wird gestartet…',
  startFailed:
    'Dieser Shop konnte nicht gelesen werden. Überprüfen Sie die Adresse und versuchen Sie es erneut.',
  tabConversations: 'Gespräche',
  tabOverview: 'Übersicht',
  allAssistants: 'Alle Assistenten',
  allAssistantsNote: '{assistants} Assistenten · {sites} Websites',
  allAssistantsMenuNote: '{count} Gespräche diese Woche',
  marketLabel: 'Markt',
  allMarkets: 'Alle Märkte',
  allMarketsNote: '{count} Websites',
  scopeIn: '{assistant} in {market}',
  askAbout: 'Chat zu {scope} fragen',
  overviewAssistantsTitle: 'Assistenten und Märkte',
  overviewMarketsTitle: 'Märkte',
  overviewAssistantsOnly: 'Assistenten',
  overviewIdle: '{count} Assistenten ohne bisherige Gespräche',
  allAssistantsCount: '{assistants} Assistenten',
  overviewFailed: 'Die Übersicht konnte nicht geladen werden.',
  colConversations: 'Gespräche',
  colCart: 'In den Warenkorb gelegt',
  colCheckout: 'Kasse',
  colPerson: 'Person',
  colCost: 'Credits',
  rowMarkets: '{count} Märkte',
  rowVersion: 'Version {version}',
  rowAria:
    '{name}, {sub}: {conversations} Gespräche, {cart} in den Warenkorb gelegt, {checkout} Kasse erreicht, {person} nach einer Person gefragt, {cost} Credits',
  versionPublishedAt: 'veröffentlicht {at}',
  designColorHeader: 'Kopfzeile',
  designColorHeaderInk: 'Text der Kopfzeile',
  designGroupTranslations: 'Texte in anderen Sprachen',
  topicsTitle: 'Wonach Kunden fragen',
  topicsNote: 'Themen, die Chat in den ersten Fragen gefunden hat',
  startPagesTitle: 'Wo Gespräche beginnen',
  startPagesOne: 'Jedes Gespräch begann auf einer bestimmten Seitenart: {page}.',
  'startPage.product': 'Produktseiten',
  'startPage.search': 'Suchergebnisse',
  'startPage.start': 'Startseite',
  'startPage.cart': 'Warenkorb',
  'startPage.other': 'Andere Seiten',
  addMarketInChat: 'Im Chat einen Markt hinzufügen',
  marketsTitle: 'Märkte',
  marketsBody:
    'Ein Assistent und ein Design. Jeder Markt hat seine eigene Sprache, Währung, Produkte, seinen Warenkorb und einen Chat mit einem Menschen.',
  marketChecked: 'geprüft {when}',
  'marketStatus.ready': 'Bereit',
  'marketStatus.found': 'Nicht hinzugefügt',
  'marketStatus.checking': 'Wird geprüft',
  'marketStatus.failed': 'Benötigt eine Korrektur',
  addMarketPrompt:
    'Füge dem Einkaufsassistenten für {name} ({domain}) einen weiteren Markt hinzu: Finde die anderen Länder, Sprachen oder Websites des Shops und frage mich, welche davon hinzugefügt werden sollen.',
  tabWidget: 'Widget',
  tabInstall: 'Installation',
  tabSetup: 'Einrichtung',
  statusReading: 'Shop wird gelesen',
  readingProducts: 'Produkte werden erneut gelesen',
  readingCart: 'Warenkorb wird erneut geprüft',
  readingChat: 'Die Verbindung zu Ihrem eigenen Chat wird überprüft',
  readingMarket: 'Markt {market} wird hinzugefügt',
  readingMarketAny: 'Ein Markt wird hinzugefügt',
  statusReady: 'Bereit zur Installation',
  statusFailed: 'Lesen gestoppt',
  statusLive: 'Live · Version {version} auf {domain}',
  statusPublished: 'Veröffentlicht · Version {version} · noch nicht auf Ihrer Website',
  statusPublishedUnverified:
    'Veröffentlicht · Version {version} · Ihre Website konnte nicht überprüft werden',
  stepOpen: 'Website wird geöffnet',
  stepSearch: 'Suche',
  stepProducts: 'Produkte',
  stepCart: 'Warenkorb',
  stepChat: 'Ihr vorhandener Chat',
  stepLook: 'Ihr Erscheinungsbild und Logo',
  stepChecks: 'Funktion wird geprüft',
  stepReady: 'Bereit',
  stepDetailOpen: '{domain} wird geöffnet',
  stepDetailSearch: 'Produkte des Shops werden gesucht',
  stepDetailProducts: 'Produkte, Preise, Fotos, Farben und Größen werden gelesen',
  stepDetailCart: 'Warenkorb wird getestet: hinzufügen, auslesen, entfernen und leeren',
  stepDetailChat: 'Ihr vorhandener Chat wird gesucht',
  stepDetailLook: 'Logo und Farben des Shops werden gelesen',
  stepDetailChecks: 'Kundensuchen, Produkte, Navigation und Warenkorb werden getestet',
  stepDetailChecksProducts: 'Produktsuche, Navigation, Fotos, Farben und Größen werden geprüft',
  stepDetailChecksCart:
    'Hinzufügen, Auslesen, Entfernen und Leeren des Warenkorbs sowie das Lesen von Produkten werden geprüft',
  stepDetailChecksChat:
    'Die Übergabe an Ihren vorhandenen Chat und das Auslesen von Produkten werden überprüft',
  stepDetailChecksMarket: 'Produkte, Preise und Warenkorb dieses Marktes werden geprüft',
  nowRunning: 'Jetzt: {step}',
  nowProgress: 'Schritt {n} von {total} · bisher {minutes} Min.',
  nowReady: 'Ihr Shop ist für einen Assistenten bereit',
  nowReadyBody: 'Suche, Produkte und Warenkorb funktionieren.',
  nowLastRead: 'Zuletzt gelesen {when}',
  nowUsually: 'normalerweise {min}–{max}',
  stepsRead: 'Gelesen {when} · Schritte anzeigen',
  nowFailed: 'Lesen gestoppt',
  factAnswers: 'Beantwortet Kunden mit',
  costCounting: 'Wird gezählt…',
  factPaid: 'Bezahlt aus',
  paidCompany: 'Credits des Unternehmens',
  creditsAmount: '{amount} Credits',
  creditsOne: '{amount} Credit',
  creditsUnder: 'unter {amount} Credits',
  stopRun: 'Lauf stoppen',
  stopping: 'Wird gestoppt…',
  closeNote: 'Sie können diese Seite verlassen. Der Vorgang läuft weiter.',
  readyNote: 'Als Nächstes: Testen Sie Ihr Widget und installieren Sie es anschließend.',
  buildWidget: 'Widget ausprobieren',
  readAgain: 'Shop erneut lesen',
  foundTitle: 'Das wurde gefunden',
  foundProducts: 'Produkte und Preise gelesen',
  foundSearch: 'Suche funktioniert',
  foundCart: 'In den Warenkorb legen funktioniert',
  foundChat: 'Ihr {vendor}-Chat',
  foundNoChat: 'Kein eigener Chat',
  foundLook: 'Ihre Farben und Ihr Logo',
  widgetTitle: 'Ihr Widget',
  widgetBody:
    'Ihr Assistent in Ihren Farben. Testen Sie ihn hier: Er antwortet mit Ihren echten Produkten. Es wird nichts zu einem echten Warenkorb hinzugefügt.',
  widgetLoading: 'Vorschau wird geladen…',
  widgetFailed: 'Die Vorschau konnte nicht geladen werden.',
  widgetNotReady:
    'Das Widget ist bereit, sobald die Schritte unter „Einrichtung“ abgeschlossen sind.',
  installLineTitle: 'Fügen Sie diese Zeile zu Ihrer Website hinzu',
  installLineBody:
    'Die Zeile funktioniert auf jeder Plattform: im head Ihrer Seiten, bei Shopify in theme.liquid direkt vor </head>. Sie zeigt erst etwas an, wenn Sie veröffentlichen.',
  installLineBodyLive:
    'Die Zeile funktioniert auf jeder Plattform: im head Ihrer Seiten, bei Shopify in theme.liquid direkt vor </head>. Besucher sehen dann Version {version}.',
  installPolicyAsk: 'Legt Ihre Website eine Content Security Policy fest?',
  installPolicy:
    'Wenn Ihre Website eine Content Security Policy festlegt, erlauben Sie außerdem {origin} in {directives}, sonst kann der Assistent nicht gestartet werden.',
  installPolicyRefuses:
    'Die Content Security Policy Ihrer Website blockiert den Assistenten: Erlauben Sie {origin} in {directives}, sonst kann nichts gestartet werden.',
  installPolicyNonce:
    'Ihre Policy führt nur vertrauenswürdige Skripte aus. Fügen Sie daher die Zeile mit dem Nonce Ihrer Seiten hinzu.',
  installPolicyEval: "Der Assistent benötigt außerdem 'unsafe-eval' in script-src.",
  copy: 'Kopieren',
  copied: 'Kopiert',
  domainsTitle: 'Websites, auf denen er antwortet',
  domainsBody: 'Auf jeder anderen Website bleibt er stumm, auch wenn sie die Zeile kopiert.',
  lineFound: 'Zeile gefunden',
  lineNotSeen: 'Noch nicht auf der Seite',
  lineUnverified:
    'Prüfung nicht möglich: Die Website blockiert automatisierte Besuche. Der Assistent funktioniert für Kundinnen und Kunden trotzdem.',
  lineChecked: 'geprüft {when}',
  lineCheckAgain: 'Erneut prüfen',
  lineChecking: 'Seite wird geprüft…',
  installCheckBusy:
    'Die Überprüfung ist momentan ausgelastet. Versuchen Sie es gleich noch einmal.',
  publishTitle: 'Veröffentlichen',
  publishBody:
    'Besucher auf {domain} sehen den Assistenten sofort. Sie können ihn jederzeit wieder zurückziehen.',
  publishButton: 'Version {version} veröffentlichen',
  publishConfirm: 'Version {version} von {name} veröffentlichen?',
  publishNow: 'Veröffentlichen',
  notNow: 'Nicht jetzt',
  publishing: 'Wird veröffentlicht…',
  liveTitle: 'Veröffentlicht: Version {version}',
  liveSince: 'Seit {since}',
  unpublish: 'Veröffentlichung aufheben',
  unpublishConfirm:
    'Version {version} von Ihrer Website entfernen? Besucher sehen den Assistenten erst wieder, wenn Sie ihn erneut veröffentlichen.',
  publishedLine: 'Jetzt veröffentlicht: Version {version}, seit {since}',
  usageTitle: 'Nutzung',
  usageBody: 'Der Assistent verwendet die Credits Ihres Unternehmens, wie auch der übrige Chat.',
  usageDiscovery: 'Shop wird gelesen',
  usageChats: 'Kundenchats',
  usageChatsValue: '{count} Chats · {cost}',
  last7: 'Letzte 7 Tage',
  askWeek: 'Chat zu dieser Woche fragen',
  kpiConversations: 'Unterhaltungen',
  kpiVsPrevious: '{change} im Vergleich zur Vorwoche',
  kpiCart: 'In den Warenkorb gelegt',
  kpiCheckout: 'Checkout erreicht',
  kpiPerson: 'Nach einer Person gefragt',
  kpiCost: 'Verbrauchte Credits',
  ofConversations: '{percent} der Unterhaltungen',
  checkoutNote: '{percent} der Unterhaltungen',
  personNote: '{percent} haben Ihren Chat geöffnet',
  costNote: 'Etwa {amount} pro Unterhaltung',
  costNoteUnder: 'Weniger als {amount} pro Unterhaltung',
  sparkLabel: 'Unterhaltungen pro Tag, letzte {days} Tage',
  sparkDay: '{day}: {count} Unterhaltungen',
  filterAll: 'Alle',
  filterCart: 'In den Warenkorb gelegt',
  filterCheckout: 'Kasse',
  filterPerson: 'Person',
  outcomeCart: 'In den Warenkorb gelegt',
  outcomeCheckout: 'Checkout erreicht',
  outcomePerson: 'Nach einer Person gefragt',
  outcomeAnswered: 'Beantwortet',
  devicePhone: 'Telefon',
  deviceComputer: 'Computer',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} Min. · Begonnen auf {page}',
  anonymous: 'Die kaufende Person ist anonym.',
  askConversation: 'Chat zu dieser Unterhaltung fragen',
  showOlderVersions: 'Frühere Versionen anzeigen',
  showOlder: 'Ältere Gespräche anzeigen',
  filterCountsWeek: 'Anzahl: letzte 7 Tage',
  readAlreadyHere:
    'Dieser Shop ist schon hier. Es wurde nichts Neues gestartet und keine Credits verbraucht.',
  readOpenShop: 'Shop öffnen',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Was können wir aus dieser Unterhaltung lernen?',
  askWeekMessage: 'Was können wir aus den Unterhaltungen dieser Woche lernen?',
  conversationsChipOne: '{name} · Unterhaltung vom {when}',
  conversationsChipWeek: '{name} · diese Woche',
  conversationsChipWeekIn: '{name} · {market} · diese Woche',
  removeConversations: 'Unterhaltungen entfernen',
  noConversations: 'Noch keine Unterhaltungen',
  noConversationsBody:
    'Wenn Kundinnen und Kunden den Assistenten nutzen, werden ihre Unterhaltungen hier angezeigt.',
  pickConversation: 'Wählen Sie eine Unterhaltung aus, um sie zu lesen.',
  eventCheckout: 'Ist zum Checkout gegangen',
  eventPerson: 'Hat Ihren Chat geöffnet',
  showMore: 'Mehr anzeigen',
  urlInvalid:
    'Verwenden Sie die HTTPS-Adresse Ihres Shops, zum Beispiel https://www.yourstore.com.',
  errorNoCompany:
    'Storefront benötigt einen Unternehmens-Workspace. Bitten Sie Ihre Administration, Sie hinzuzufügen.',
  errorPublishPermission:
    'Nur Personen mit Veröffentlichungsberechtigung für Ihr Unternehmen können dies tun. Bitten Sie Ihre Administration.',
  actionFailed: 'Das hat nicht funktioniert. Versuchen Sie es noch einmal.',
  copyFailed: 'Kopieren nicht möglich. Markieren Sie die Zeile und kopieren Sie sie selbst.',
  reasonCancelled: 'Sie haben den Vorgang beendet.',
  reasonFailed:
    'Der Shop konnte nicht vollständig gelesen werden. Der Assistent ist daher noch nicht bereit.',
  reasonFailedLive:
    'Der Shop konnte nicht vollständig gelesen werden. Der veröffentlichte Assistent funktioniert weiterhin unverändert.',
  reasonFailedKept:
    'Der Shop konnte nicht vollständig gelesen werden. Daher wurde nichts geändert: Version {version} bleibt bestehen.',
  statusReadStopped: 'Bereit · das letzte Auslesen wurde angehalten',
  reasonStepMarked: 'Der angehaltene Schritt ist unten markiert.',
  reasonReadAgain: 'Beim erneuten Auslesen wird mit den Erkenntnissen dieses Laufs begonnen.',
  reasonNotAStore:
    'Diese Adresse sieht nicht wie ein Onlineshop aus: Es wurden keine Produkte gefunden. Prüfen Sie die Adresse oder versuchen Sie es mit der Hauptdomain des Shops.',
  reasonTooSlow:
    'Die Suchantworten Ihres Shops sind für einen Shopping-Assistenten zu langsam: Die Suche dauerte über 12 Sekunden. Eine schnellere Suche auf dem Shop oder ein erneutes Einlesen zu einem späteren Zeitpunkt kann helfen.',
  reasonOurSide:
    'Das Einlesen wurde aufgrund eines Problems auf unserer Seite beendet, nicht aufgrund eines Problems mit Ihrem Shop. Der Assistent ist daher noch nicht bereit.',
  reasonOurSideLive:
    'Das Einlesen wurde aufgrund eines Problems auf unserer Seite beendet, nicht aufgrund eines Problems mit Ihrem Shop. Der veröffentlichte Assistent funktioniert weiterhin unverändert.',
  reasonOurSideKept:
    'Das Einlesen wurde aufgrund eines Problems auf unserer Seite beendet, nicht aufgrund eines Problems mit Ihrem Shop. Daher wurde nichts geändert: Version {version} bleibt bestehen.',
  reasonTryLater: 'Versuchen Sie, den Shop nach einiger Zeit erneut einzulesen.',
  errorNoCredits:
    'Dafür sind gerade keine Credits verfügbar. Eine Inhaberin oder ein Inhaber des Unternehmens kann Tarif und Limits unter Usage prüfen.',
  errorReadUnavailable:
    'Dieser Shop kann derzeit nicht gelesen werden. Versuchen Sie es in einigen Minuten erneut.',
  reasonTimeout:
    'Das Auslesen hat sein Zeitlimit erreicht, bevor es fertig war. Beim erneuten Auslesen wird mit dem fortgefahren, was bereits überprüft wurde.',
  retrying: 'Wird erneut gestartet …',
  readTitleChecks: 'Gespeichertes Auslesen erneut prüfen',
  checksScopeLabel: 'Geprüft wird',
  readScopeChecks:
    'Suchen von Kundinnen und Kunden, Produkte, Navigation und der Warenkorb – auf dem, was das letzte Auslesen gespeichert hat. Der Shop wird nicht erneut ausgelesen.',
  readScopeChecksProducts:
    'Produktsuche, Navigation, Fotos, Farben und Größen – auf dem, was das letzte Auslesen gespeichert hat. Der Shop wird nicht erneut ausgelesen.',
  readScopeChecksCart:
    'Hinzufügen, Auslesen, Entfernen und Leeren des Warenkorbs sowie das Lesen von Produkten – auf dem, was das letzte Auslesen gespeichert hat. Der Shop wird nicht erneut ausgelesen.',
  readScopeChecksChat:
    'Die Weiterleitung zu Ihrem bestehenden Chat und das Lesen von Produkten basieren auf den beim letzten Einlesen gespeicherten Daten. Der Shop wird nicht erneut gelesen.',
  readScopeChecksMarket:
    'Produkte, Preise und Warenkorb des Marktes – auf dem, was das letzte Auslesen gespeichert hat. Der Shop wird nicht erneut ausgelesen.',
  checksCost: 'Nur die neuen Prüfungen verbrauchen Unternehmens-Credits',
  checksAfter:
    'Wenn die Prüfungen erfolgreich sind, wird ein neuer Entwurf gespeichert. Bevor Sie ihn veröffentlichen, erreicht nichts davon die Kunden.',
  checksAfterLive:
    'Wenn die Prüfungen erfolgreich sind, wird ein neuer Entwurf gespeichert. Der veröffentlichte Assistent funktioniert weiterhin, und Kunden sehen die Änderung erst nach Ihrer Veröffentlichung.',
  checksStarted:
    'Die Prüfungen wurden gestartet. Wenn der neue Entwurf bereit ist, bitte Chat um eine Prüfung.',
  checkingSavedWork: 'Gespeicherte Arbeit wird erneut geprüft',
  checkAgain: 'Erneut prüfen',
  checkingAgain: 'Wird erneut geprüft …',
  reasonCheckAgain:
    '„Erneut prüfen“ testet die gespeicherte Arbeit noch einmal, ohne den Shop erneut auszulesen. Nur die neuen Prüfungen verbrauchen Unternehmens-Credits.',
  fixWithChat: 'Mit Chat korrigieren',
  fixReadPrompt:
    'Das Einlesen von {name} ({domain}) wurde beendet. Sehen Sie sich an, was passiert ist, und helfen Sie mir, es wieder zum Laufen zu bringen: Teilen Sie mir mit, was Sie herausfinden, schlagen Sie vor, was ich dem Einlesevorgang mitteilen könnte, und prüfen Sie ihn anschließend erneut oder lesen Sie ihn mit diesem Hinweis erneut ein. Lesen Sie dabei so wenig wie möglich vom Shop ein.',
  fixReadPromptStep:
    'Das Einlesen von {name} ({domain}) wurde bei „{step}“ beendet. Sehen Sie sich an, was passiert ist, und helfen Sie mir, es wieder zum Laufen zu bringen: Teilen Sie mir mit, was Sie herausfinden, schlagen Sie vor, was ich dem Einlesevorgang mitteilen könnte, und prüfen Sie ihn anschließend erneut oder lesen Sie ihn mit diesem Hinweis erneut ein. Lesen Sie dabei so wenig wie möglich vom Shop ein.',
  stepStateDone: 'fertig',
  stepStateRunning: 'in Bearbeitung',
  stepStateWaiting: 'noch nicht gestartet',
  stepStateFailed: 'angehalten',
  newVersionTitle: 'Version {version} ist bereit',
  newVersionBody: 'Veröffentlichen Sie sie, um Version {live} zu ersetzen.',
  newVersionBodyLive: 'Veröffentlichen Sie sie, um Version {live} auf {domain} zu ersetzen.',
  previewSize: 'Vorschaugröße',
  designWithChat: 'Design öffnen',
  designReviewBody:
    'Sehen Sie sich das aktuelle Design an und sagen Sie Chat, was Sie anpassen möchten.',
  designWhileTitle: 'Gestalten Sie ihn, während wir Ihren Shop einlesen',
  designWhileBody:
    'Im Chat können Sie jetzt gemeinsam mit uns das Erscheinungsbild, die Texte und das Logo des Assistenten gestalten. Das Design ist fertig, sobald das Einlesen abgeschlossen ist.',
  designPreviewNote: 'Eine Vorschau mit Beispielprodukten, bis Ihr Shop eingelesen ist.',
  proposedDesign: 'Vorgeschlagenes Design',
  proposedDesignNote:
    'Das Widget mit dieser Änderung und Beispielprodukten. Durch die Genehmigung wird es als neuer Entwurf gespeichert; die Veröffentlichung erfolgt weiterhin auf der Storefront-Seite.',
  lineSources: 'Aus den Seiten des Shops:',
  readAgainConfirm:
    '{name} erneut auslesen? Das dauert etwa 10 bis 30 Minuten und verbraucht Unternehmens-Credits.',
  readAgainLive:
    'Der veröffentlichte Assistent funktioniert weiter, bis Sie die neue Version veröffentlichen.',
  readAgainNow: 'Erneut auslesen',
  switchStore: 'Shop wechseln oder hinzufügen',
  storesLabel: 'Ihre Shops',
  addStore: 'Weiteren Shop hinzufügen',
  backToStores: 'Zurück zu Ihren Shops',
  storeLive: 'Veröffentlicht',
  storeReady: 'Bereit',
  storeReading: 'Wird ausgelesen',
  storeStopped: 'Angehalten',
  actionDesignTitle: 'Design des Einkaufsassistenten ändern',
  actionKnowledgeTitle: 'Wissen des Einkaufsassistenten ändern',
  readTitleProducts: 'Produkte erneut auslesen',
  readTitleCart: 'Warenkorb erneut prüfen',
  readTitleTheirChat: 'Den Weg zu Ihrem eigenen Chat erneut prüfen',
  readTitleAll: 'Ganzen Shop erneut auslesen',
  readTitleStore: 'Neuen Shop auslesen',
  readTitleMarket: 'Markt hinzufügen',
  readShopLabel: 'Shop',
  readScopeLabel: 'Ausgelesen werden',
  readTimeLabel: 'Dauer',
  readCostLabel: 'Kosten',
  readAfterLabel: 'Danach',
  readScopeProducts: 'Produktsuche, Sortierung, Produktfotos, Farben und Größen',
  readScopeCart: 'Produkte in den Warenkorb legen und daraus entfernen',
  readScopeTheirChat: 'So erreichen Kunden Ihren eigenen Chat',
  readScopeAll: 'Produkte, Warenkorb, eigener Chat und Märkte',
  readScopeStore:
    'Produkte, Preise und Größen, Warenkorb, eigener Chat, Erscheinungsbild und Märkte',
  readScopeMarket: '{market}: Produkte, Preise und Warenkorb',
  readTime: 'Normalerweise {min}–{max} Minuten',
  readCost: 'Verbraucht Unternehmens-Credits',
  readCostLast:
    'Verbraucht Unternehmens-Credits · das letzte Auslesen dieses Shops verbrauchte {credits}',
  readCostLastPartial:
    'Verbraucht Unternehmens-Credits · das letzte Auslesen dieses Shops wurde früh angehalten und verbrauchte mindestens {credits}',
  readAfter:
    'Ein neuer Entwurf wird gespeichert. Bevor Sie ihn veröffentlichen, erreicht nichts davon die Kunden.',
  readAfterLive:
    'Ein neuer Entwurf wird gespeichert. Der veröffentlichte Assistent funktioniert weiterhin, und Kunden sehen die Änderung erst nach Ihrer Veröffentlichung.',
  readAfterNew: 'Speichert einen ersten Entwurf zum Ausprobieren. Es wird nichts veröffentlicht.',
  readCartNote:
    'Um den Warenkorb zu prüfen, legt der Lauf einen Testartikel hinein und entfernt ihn wieder. Ein Checkout wird nie durchgeführt.',
  readFocusLabel: 'Was Chat den Lauf beheben lassen soll',
  readFooter:
    'Es beginnt erst, wenn Sie die Genehmigung erteilen. Sie können den Vorgang im Tab „Einrichtung“ stoppen.',
  readStartedAt:
    'Gestartet {time} · normalerweise {min}–{max} Minuten. Wenn der neue Entwurf bereit ist, bitte Chat um eine Prüfung.',
  readFocusAll: 'Alle anzeigen',
  readFocusLess: 'Weniger anzeigen',
  readStart: 'Auslesen starten',
  addToSite: 'Zu Ihrer Website hinzufügen',
  readTab: 'Anfrage',
  readStarted:
    'Das Auslesen wurde gestartet. Wenn der neue Entwurf bereit ist, bitte Chat um eine Prüfung.',
  readFollow: 'Unter „Einrichtung“ verfolgen',
  proposedDesignLive:
    'Die echte Vorschau Ihres Shops in diesem Design. Stellen Sie eine Frage: Es wird nichts zu einem echten Warenkorb hinzugefügt. Durch die Genehmigung wird es als neuer Entwurf gespeichert; die Veröffentlichung erfolgt weiterhin auf der Storefront-Seite.',
  designChanges: 'Änderungen',
  designReset: 'auf die Standardeinstellungen zurücksetzen',
  designGroupColors: 'Farben',
  designGroupType: 'Schriftarten',
  designGroupShape: 'Form',
  designGroupLauncher: 'Schaltfläche auf der Seite',
  designGroupPanel: 'Kopfzeile',
  designGroupProducts: 'Produkte',
  designGroupCart: 'Warenkorb des Shops',
  designGroupCopy: 'Texte',
  designGroupLogo: 'Logo',
  designGroupCss: 'Eigenes CSS',
  designColorBrand: 'Marke',
  designColorActions: 'Hinzufügen und Senden',
  designColorPanel: 'Panel',
  designColorSurface: 'Flächen',
  designColorInk: 'Text',
  designColorMuted: 'Sekundärtext',
  designColorLine: 'Linien',
  designColorBubble: 'Nachrichten der kaufenden Person',
  designColorBubbleInk: 'Text der kaufenden Person',
  designActionsBrand: 'Markenfarbe',
  designActionsInk: 'Textfarbe',
  designWordsName: 'Name',
  designWordsGreeting: 'Begrüßung',
  designWordsStarters: 'Einstiegsfragen',
  designWordsLauncher: 'Beschriftung der Schaltfläche',
  designWordsPlaceholder: 'Platzhalter',
  designWordsAi: 'KI-Bezeichnung',
  designWordsPerson: 'Link zu einer Person',
  noteNew: 'Neu',
  noteChanged: 'Geändert',
  noteRemoved: 'Entfernt',
  noteSourceKnowledge: 'Wissensseite: {title}',
  noteSourceFile: 'Datei: {name}',
  noteSourceChat: 'Aus dem Chat',
  knowledgeApprovalNote:
    'Kunden können alles in diesen Notizen lesen. Durch die Genehmigung wird ein neuer Entwurf gespeichert: Stellen Sie Fragen dazu im Tab „Widget“ und veröffentlichen Sie ihn anschließend im Tab „Installation“.',
  profileTitle: 'Über diesen Shop',
  profileChecked: 'Geprüft {date}',
  profileSources: 'Geschrieben aus',
  profileSample: '{count} Produkte als Stichprobe für das Preisniveau',
  profileSampleOne: '{count} Produkt als Stichprobe für das Preisniveau',
  profileAssistant: 'Was der Assistent hier kann',
  profileTeam: 'Von Ihrem Team geschrieben',
  profileEmpty:
    'Noch nicht geschrieben. Bitten Sie Chat, einen Wissensdurchlauf zu starten: Er schreibt dies aus Ihren eigenen Seiten und dem, was er auf Ihrer Website gesehen hat, und Sie können eigene Zeilen hinzufügen.',
  profileWithChat: 'Mit Chat hinzufügen oder ändern',
  profilePrompt:
    'Helfen Sie mir zu verbessern, was der Shopping-Assistent für {name} ({domain}) über unseren Shop als Ganzes weiß: was wir verkaufen, wofür wir bekannt sind, für wen wir da sind und was Kundinnen und Kunden hier tun können. Lesen Sie zuerst das Profil „Über diesen Shop“ und sagen Sie mir, was fehlt oder falsch ist. Alles, was ich Ihnen über den Shop sage und keine Seite nennt, kommt als Zeile unseres Teams hinein.',
  profilePartNavigation: 'Abteilungen',
  profilePartWelcome: 'Begrüßung',
  profilePartMarkets: 'Märkte',
  profilePartFunctions: 'Was der Assistent kann',
  profilePartProducts: 'Produkte',
  profilePartPages: 'Serviceseiten',
  profileCleared: 'Löscht das geschriebene Profil',
  knowledgeTitle: 'Was er weiß',
  knowledgeBody:
    'Zusätzlich zu Ihren Produkten, Preisen und Lagerbeständen, die immer live aus Ihrem Shop eingelesen werden.',
  knowledgeEmpty:
    'Bisher ist nur Ihr Katalog vorhanden. Fügen Sie mit dem Chat Informationen zu Lieferung, Rückgaben oder Zahlungsarten hinzu – von Ihren eigenen Seiten oder aus der Wissensdatenbank.',
  knowledgeWithChat: 'Mit Chat hinzufügen',
  knowledgeChangeWithChat: 'Mit Chat ändern',
  knowledgePrompt:
    'Hilf mir auszuwählen, was der Einkaufsassistent für {name} ({domain}) Kunden zusätzlich zu unseren Produkten mitteilt, zum Beispiel über Lieferung, Rückgabe und Zahlung. Finde die Informationen auf unseren eigenen Seiten oder in unserem Wissen und schlage Notizen vor, die ich freigeben kann.',
  pagesTitle: 'Seiten, die er liest',
  pagesBody:
    'Die Seiten, aus denen die Notizen stammen. Poneva prüft sie jeden Tag, und Chat meldet Ihnen, wenn sich eine ändert.',
  pagesEmpty:
    'Noch keine Seiten. Hier erscheinen die Seiten, die das Lesen Ihres Shops findet, und die, die Sie im Chat nennen.',
  pageFound: 'von Poneva gefunden',
  pageLinkedFrom: 'verlinkt von {page}',
  pageAddedByYou: 'von Ihnen hinzugefügt',
  pageFromNote: 'in einer Notiz genannt',
  pageNoteOne: '1 Notiz',
  pageNotesMany: '{count} Notizen',
  pageStatusOk: 'Aktuell',
  pageStatusChanged: 'Geändert',
  pageStatusUnreadable: 'Nicht lesbar',
  pageStatusUnchecked: 'Noch nicht geprüft',
  pagesChangeWithChat: 'Mit Chat ändern',
  pagesUpdateWithChat: 'Notizen mit Chat aktualisieren',
  pagesPrompt:
    'Helfen Sie mir, zu ändern, welche Seiten der Shopping-Assistent von {name} ({domain}) für seine Notizen liest. Zeigen Sie mir die Seiten, die er jetzt verwendet, und fragen Sie mich, welche ich hinzufügen oder entfernen soll.',
  pagesUpdatePrompt:
    'Einige Seiten von {name} ({domain}) haben sich geändert oder können seit der Erstellung der Notizen nicht mehr gelesen werden. Zeigen Sie mir, welche das sind, und erstellen Sie die Notizen daraus neu als Entwurf, den ich prüfen kann.',
  viewCompare: 'Vergleich',
  lookDeviceLabel: 'Vergleichen auf',
  lookShop: 'Ihr Shop',
  lookShopCaption: 'Startseite, gelesen {when}',
  lookWidget: 'Der Assistent',
  lookWidgetCaption: 'Version {version}',
  lookLoading: 'Der Assistent wird neben Ihrem Shop eingerichtet…',
  lookFailed: 'Der Vergleich konnte gerade nicht erstellt werden.',
  lookNoShopPicture: 'Beim Einlesen wurde kein Bild Ihres Shops in dieser Breite gespeichert.',
  lookNoneTitle: 'Das Erscheinungsbild Ihres Shops wurde nicht übernommen',
  lookNoneBody:
    'Dieses Lesen hat es nicht erfasst. Lesen Sie den Shop unter „Einrichtung“ erneut, dann werden sein Logo, seine Farben und seine Seiten zum Vergleichen gespeichert.',
  lookColoursShop: 'Von Ihrem Shop verwendete Farben',
  lookColoursAssistant: 'Farben des Assistenten',
  lookRoleHeader: 'Kopfzeile',
  lookRoleBrand: 'Marke',
  lookSame: 'Identisch mit der Farbe {colour} Ihres Shops ({source})',
  lookOff:
    'Ähnlich wie die Farbe {colour} Ihres Shops ({source}), aber nicht identisch; Abweichung {delta}',
  lookOwn: 'Eigene Farbe: Ihr Shop verwendet sie nicht',
  lookNeutral:
    'Ihr Shop wurde hierfür nicht geladen; angezeigt wird nur, was beim Einlesen gespeichert wurde.',
  lookFromLogo: 'Logo',
  lookFromTheme: 'Themenfarbe',
  lookFromTile: 'Kachelfarbe',
  lookFromMask: 'angeheftetes Symbol',
  lookFromManifest: 'App-Manifest',
  lookFromHeader: 'Kopfzeile',
  lookFromButton: 'Schaltflächen',
  lookFromVariable: 'Stilfarbe',
  lookFix: 'Mit Chat korrigieren',
  lookFixPrompt:
    'Die Farbe des Assistenten für {role} ist {colour} und ähnelt der Farbe {exact} Ihres Shops ({source}), ist aber nicht identisch. Verwenden Sie bitte exakt die Farbe {exact} Ihres Shops.',
  lookSwatch: 'Farbe {colour}',
  errorDesignChecks: 'Diese Version hat die Designprüfungen nicht bestanden.',
  designSamplesBlocked:
    'Kartenlayouts können hier nicht überprüft werden: Ihre Website blockiert automatisierte Besuche. Farben, Schriftarten und das Logo funktionieren weiterhin.',
  designCheckLabel: 'Ein KI-Hinweis ist zu blass zum Lesen.',
  designCheckReach: 'Eine Schaltfläche ist nicht erreichbar.',
  designCheckSize: 'Ein Teil des Textes ist zu klein.',
  designCheckOverflow: 'Das Panel scrollt seitlich.',
  designCheckRender:
    'Die Prüfungen konnten nicht ausgeführt werden. Versuchen Sie es gleich noch einmal.',
  fixDesignPrompt:
    'Ich habe versucht, den neuesten Entwurf von {name} ({domain}) zu veröffentlichen, aber er hat die Designprüfungen nicht bestanden: {facts}. Beheben Sie das Design in diesem Chat und prüfen Sie es auf Smartphone und Computer. Veröffentlichen Sie es nicht.',
};

const el: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Βοηθός αγορών',
  designUnconfirmed:
    'Η διαδικασία σταμάτησε πριν το Chat επιβεβαιώσει ότι ο σχεδιασμός αποθηκεύτηκε. Ο καμβάς του βοηθού δείχνει την πιο πρόσφατη έκδοση.',
  errorNewerVersion:
    'Αποθηκεύτηκε νεότερη έκδοση από τότε που ανοίξατε αυτή τη σελίδα. Ελέγξτε την πρώτα και μετά δημοσιεύστε.',
  canvasSelectHint:
    'Κάντε κλικ σε ένα μέρος του βοηθού για να το αλλάξετε στο chat · Esc για διακοπή',
  canvasDraft: 'Έκδοση {version} · δεν έχει δημοσιευτεί ακόμη',
  canvasDraftOverLive: 'Έκδοση {version} · η έκδοση {live} είναι δημοσιευμένη',
  canvasLive: 'Έκδοση {version} · δημοσιευμένη',
  canvasNoVersion: 'Η πρώτη έκδοση θα είναι διαθέσιμη όταν ολοκληρωθεί η ανάγνωση του καταστήματος',
  canvasPublish: 'Δημοσίευση στη σελίδα Storefront',
  canvasFrameTitle: 'Βοηθός αγορών {name}',
  viewLabel: 'Προβολή',
  photosGood: '{count} φωτογραφίες προϊόντων: ολόκληρες και καθαρές',
  photosGoodOne: 'Η φωτογραφία του προϊόντος είναι ολόκληρη και καθαρή',
  canvasMissingTitle: 'Αυτός ο βοηθός αγορών δεν βρίσκεται σε αυτόν τον χώρο εργασίας',
  canvasMissingBody: 'Ανήκει σε άλλο χώρο εργασίας ή έχει αφαιρεθεί.',
  canvasMissingLink: 'Δείτε τους βοηθούς αυτού του χώρου εργασίας',
  photosLabel: 'Φωτογραφίες προϊόντων',
  photosSmall: '{count} μικρές στις κάρτες τους',
  photosCut: '{count} κομμένες',
  photosSoft: '{count} μεγεθυσμένες και θολές',
  photosMissing: '{count} χωρίς φωτογραφία',
  photosFix: 'Διόρθωση με Chat',
  photosPrompt:
    'Κάντε τις φωτογραφίες προϊόντων στις κάρτες σωστές: ολόκληρες, αρκετά μεγάλες στην κάρτα τους και καθαρές.',
  photosFacts:
    '{total} φωτογραφίες: {small} μικρές στις κάρτες, {cut} κομμένες, {soft} μεγεθυσμένες, {missing} χωρίς φωτογραφία. Μεγέθη: {sizes}',
  viewDesign: 'Σχεδιασμός',
  viewVersions: 'Εκδόσεις',
  versionsLoading: 'Φόρτωση εκδόσεων…',
  versionsFailed: 'Δεν ήταν δυνατή η φόρτωση των εκδόσεων.',
  versionTitle: 'Έκδοση {version}',
  versionLive: 'Δημοσιευμένη',
  versionChecked: 'Ελεγμένη',
  versionToday: 'Σήμερα',
  versionYesterday: 'Χθες',
  versionEarlier: 'Νωρίτερα',
  versionByPerson: 'Ζητήθηκε από {name}',
  versionByChat: 'Ζητήθηκε στο chat',
  versionFromReading: 'Από ανάγνωση του {domain}',
  versionPublishedBy: 'δημοσιεύτηκε από {name}, {at}',
  versionPublish: 'Δημοσίευση',
  versionRestore: 'Επαναφορά…',
  versionRestoreTitle: 'Επαναφορά της έκδοσης {version};',
  versionRestoreBody:
    'Θα γίνει η έκδοση {next}, ένα νέο προσχέδιο. Ο ιστότοπός σας δεν αλλάζει μέχρι να δημοσιεύσετε.',
  versionRestoreConfirm: 'Επαναφορά',
  versionRestoreCancel: 'Ακύρωση',
  versionRestored: 'Επαναφέρθηκε ως έκδοση {version}',
  versionRestoreFailed: 'Δεν ήταν δυνατή η επαναφορά της έκδοσης.',
  compareTitle: 'Σύγκριση της έκδοσης {to} με την {from}',
  compareTitleLive: 'Σύγκριση της έκδοσης {to} με την {from}, δημοσιευμένης',
  compareHint: 'Σύρετε για σύγκριση',
  compareLabel: 'Σύγκριση των εκδόσεων {from} και {to}',
  compareTagLive: 'v{version} · Δημοσιευμένη',
  compareTag: 'v{version}',
  changesTitle: 'Τι άλλαξε από την έκδοση {from} στην {to}',
  changesNone:
    'Οι σχεδιασμοί είναι ίδιοι. Άλλαξαν μόνο οι σημειώσεις ή τα δεδομένα του καταστήματος.',
  changeAdded: 'Προστέθηκε',
  changeRemoved: 'Αφαιρέθηκε',
  changeChanged: 'Άλλαξε',
  changeDrawn: 'ένα σχεδιασμένο λογότυπο',
  changeCss: 'το δικό του CSS',
  changeYes: 'ναι',
  changeNo: 'όχι',
  'field.colors.brand': 'Χρώμα μάρκας',
  'field.colors.actions': 'Χρώμα κουμπιού',
  'field.colors.panel': 'Χρώμα πίνακα',
  'field.colors.surface': 'Χρώμα επιφάνειας',
  'field.colors.ink': 'Χρώμα κειμένου',
  'field.colors.muted': 'Χρώμα δευτερεύοντος κειμένου',
  'field.colors.line': 'Χρώμα γραμμής',
  'field.colors.bubble': 'Χρώμα μηνύματος πελάτη',
  'field.colors.bubbleInk': 'Κείμενο μηνύματος πελάτη',
  'field.colors.header': 'Χρώμα κεφαλίδας',
  'field.colors.headerInk': 'Χρώμα κειμένου κεφαλίδας',
  'field.colors.tints': 'Απαλές αποχρώσεις',
  'field.copy.assistantName': 'Όνομα',
  'field.copy.greeting': 'Χαιρετισμός',
  'field.copy.placeholder': 'Πλαίσιο ερώτησης',
  'field.copy.starters': 'Προτεινόμενες ερωτήσεις',
  'field.copy.launcherLabel': 'Λέξεις κουμπιού',
  'field.copy.aiLabel': 'Ετικέτα AI',
  'field.copy.personLabel': 'Σύνδεσμος προς άτομο',
  'field.type.body': 'Γραμματοσειρά κειμένου',
  'field.type.display': 'Γραμματοσειρά επικεφαλίδων',
  'field.type.fontCss': 'Πηγή γραμματοσειράς',
  'field.type.scale': 'Μέγεθος κειμένου',
  'field.shape.radius': 'Γωνίες',
  'field.shape.shadow': 'Σκιά',
  'field.shape.density': 'Αποστάσεις',
  'field.launcher.style': 'Κουμπί σε υπολογιστές',
  'field.launcher.phoneStyle': 'Κουμπί σε τηλέφωνα',
  'field.launcher.side': 'Πλευρά κουμπιού',
  'field.launcher.icon': 'Εικονίδιο κουμπιού',
  'field.panel.header': 'Μέγεθος κεφαλίδας',
  'field.panel.opening': 'Πρώτη οθόνη',
  'field.products.layout': 'Προϊόντα',
  'field.products.imageFit': 'Φωτογραφίες προϊόντων',
  'field.products.imageRatio': 'Σχήμα κάρτας φωτογραφίας',
  'field.products.imagePadding': 'Χώρος γύρω από φωτογραφίες',
  'field.products.imageBackground': 'Χρώμα κάρτας φωτογραφίας',
  'field.products.picker': 'Επιλογή μεγέθους',
  'field.products.showStock': 'Εμφάνιση αποθέματος',
  'field.cart.landing': 'Κίνηση καλαθιού',
  'field.logo.svg': 'Λογότυπο',
  'field.logo.shape': 'Σχήμα λογοτύπου',
  'field.logo.plate': 'Φόντο λογοτύπου',
  'field.css': 'Προσαρμοσμένο CSS',
  'value.ink': 'μελάνι',
  'value.brand': 'χρώμα μάρκας',
  'value.small': 'μικρό',
  'value.medium': 'μεσαίο',
  'value.large': 'μεγάλο',
  'value.square': 'τετράγωνο',
  'value.soft': 'απαλό',
  'value.round': 'στρογγυλό',
  'value.flat': 'επίπεδο',
  'value.deep': 'βαθύ',
  'value.compact': 'συμπαγές',
  'value.comfortable': 'άνετο',
  'value.pill': 'χάπι με λέξεις',
  'value.circle': 'κύκλος',
  'value.right': 'δεξιά',
  'value.left': 'αριστερά',
  'value.logo': 'λογότυπο',
  'value.chat': 'φούσκα chat',
  'value.sparkle': 'λάμψη',
  'value.bag': 'τσάντα',
  'value.full': 'πλήρες',
  'value.middle': 'στη μέση',
  'value.bottom': 'στο κάτω μέρος',
  'value.rows': 'γραμμές',
  'value.grid': 'πλέγμα',
  'value.carousel': 'σειρά με οριζόντια κύλιση',
  'value.contain': 'ολόκληρη φωτογραφία',
  'value.cover': 'γεμάτη κάρτα',
  'value.portrait': 'κατακόρυφο',
  'value.landscape': 'οριζόντιο',
  'value.none': 'κανένα',
  'value.pop': '+1 που ανεβαίνει',
  'value.ring': 'κυματισμός',
  'value.blink': 'αναβόσβημα',
  'value.chips': 'κουμπιά σε σειρά',
  'value.menu': 'λίστα',
  'value.rounded': 'με στρογγυλεμένες γωνίες',
  designCardTitle: 'Βοηθός αγορών {name}',
  designCardSaved: 'Αποθηκεύτηκε η έκδοση {version} · δεν έχει δημοσιευτεί ακόμη',
  designCardSavedFirst: 'Ο σχεδιασμός αποθηκεύτηκε · η πρώτη έκδοση θα τον χρησιμοποιεί',
  designSaving: 'Αποθήκευση σχεδιασμού…',
  selectedAssistantPart: 'Επιλεγμένο: μέρος του βοηθού',
  loading: 'Φόρτωση Storefront…',
  disabledTitle: 'Το Storefront δεν είναι ενεργοποιημένο σε αυτόν τον χώρο εργασίας.',
  disabledBody:
    'Βρίσκεται σε πρώιμη πρόσβαση και διατίθεται σταδιακά. Ρωτήστε την επαφή σας στην Poneva πότε θα είναι διαθέσιμο για εσάς.',
  loadFailed: 'Δεν ήταν δυνατή η φόρτωση του Storefront.',
  startTitle: 'Ένας βοηθός αγορών για το κατάστημά σας',
  startBody:
    'Η Poneva διαβάζει το κατάστημά σας όπως το χρησιμοποιεί ένας πελάτης: αναζήτηση, προϊόντα, καλάθι και το chat που ήδη έχετε. Χρειάζονται περίπου 10 έως 30 λεπτά και χρησιμοποιούνται εταιρικές πιστώσεις, ενώ μπορείτε να συνεχίσετε να εργάζεστε.',
  urlLabel: 'Διεύθυνση καταστήματος',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Ανάγνωση του καταστήματός μου',
  starting: 'Έναρξη…',
  startFailed:
    'Δεν ήταν δυνατή η ανάγνωση αυτού του καταστήματος. Ελέγξτε τη διεύθυνση και δοκιμάστε ξανά.',
  tabConversations: 'Συνομιλίες',
  tabOverview: 'Επισκόπηση',
  allAssistants: 'Όλοι οι βοηθοί',
  allAssistantsNote: '{assistants} βοηθοί · {sites} ιστότοποι',
  allAssistantsMenuNote: '{count} συνομιλίες αυτή την εβδομάδα',
  marketLabel: 'Αγορά',
  allMarkets: 'Όλες οι αγορές',
  allMarketsNote: '{count} ιστότοποι',
  scopeIn: '{assistant} σε {market}',
  askAbout: 'Ρωτήστε το Chat για {scope}',
  overviewAssistantsTitle: 'Βοηθοί και αγορές',
  overviewMarketsTitle: 'Αγορές',
  overviewAssistantsOnly: 'Βοηθοί',
  overviewIdle: '{count} βοηθοί χωρίς συνομιλίες ακόμη',
  allAssistantsCount: '{assistants} βοηθοί',
  overviewFailed: 'Δεν ήταν δυνατή η φόρτωση της επισκόπησης.',
  colConversations: 'Συνομιλίες',
  colCart: 'Προστέθηκε στο καλάθι',
  colCheckout: 'Ταμείο',
  colPerson: 'Άτομο',
  colCost: 'Πιστώσεις',
  rowMarkets: '{count} αγορές',
  rowVersion: 'έκδοση {version}',
  rowAria:
    '{name}, {sub}: {conversations} συνομιλίες, {cart} προσθήκες στο καλάθι, {checkout} επισκέψεις στο ταμείο, {person} αιτήματα για άτομο, {cost} πιστώσεις',
  versionPublishedAt: 'δημοσιεύτηκε {at}',
  designColorHeader: 'Κεφαλίδα',
  designColorHeaderInk: 'Κείμενο κεφαλίδας',
  designGroupTranslations: 'Λέξεις σε άλλες γλώσσες',
  topicsTitle: 'Τι ρωτούν οι πελάτες',
  topicsNote: 'Θέματα που βρήκε το Chat στις πρώτες ερωτήσεις',
  startPagesTitle: 'Πού ξεκινούν οι συνομιλίες',
  startPagesOne: 'Κάθε συνομιλία ξεκίνησε σε έναν τύπο σελίδας: {page}.',
  'startPage.product': 'Σελίδες προϊόντων',
  'startPage.search': 'Αποτελέσματα αναζήτησης',
  'startPage.start': 'Αρχική σελίδα',
  'startPage.cart': 'Καλάθι',
  'startPage.other': 'Άλλες σελίδες',
  addMarketInChat: 'Προσθήκη αγοράς, στο chat',
  marketsTitle: 'Αγορές',
  marketsBody:
    'Ένας βοηθός και ένας σχεδιασμός. Κάθε αγορά έχει τη δική της γλώσσα, νόμισμα, προϊόντα, καλάθι και chat για επικοινωνία με άτομο.',
  marketChecked: 'ελέγχθηκε {when}',
  'marketStatus.ready': 'Έτοιμη',
  'marketStatus.found': 'Δεν προστέθηκε',
  'marketStatus.checking': 'Έλεγχος',
  'marketStatus.failed': 'Χρειάζεται διόρθωση',
  addMarketPrompt:
    'Προσθέστε άλλη αγορά στον βοηθό αγορών για {name} ({domain}): βρείτε τις άλλες χώρες, γλώσσες ή ιστοτόπους του καταστήματος και ρωτήστε με ποιον να προσθέσουμε.',
  tabWidget: 'Widget',
  tabInstall: 'Εγκατάσταση',
  tabSetup: 'Ρύθμιση',
  statusReading: 'Ανάγνωση του καταστήματός σας',
  readingProducts: 'Νέα ανάγνωση προϊόντων',
  readingCart: 'Νέος έλεγχος καλαθιού',
  readingChat: 'Έλεγχος πρόσβασης στο δικό σας chat',
  readingMarket: 'Προσθήκη αγοράς {market}',
  readingMarketAny: 'Προσθήκη αγοράς',
  statusReady: 'Έτοιμο για εγκατάσταση',
  statusFailed: 'Η ανάγνωση σταμάτησε',
  statusLive: 'Σε λειτουργία · έκδοση {version} στο {domain}',
  statusPublished: 'Δημοσιευμένο · έκδοση {version} · δεν είναι ακόμη στον ιστότοπό σας',
  statusPublishedUnverified:
    'Δημοσιευμένο · έκδοση {version} · δεν ήταν δυνατός ο έλεγχος του ιστότοπού σας',
  stepOpen: 'Άνοιγμα ιστοτόπου',
  stepSearch: 'Αναζήτηση',
  stepProducts: 'Προϊόντα',
  stepCart: 'Καλάθι',
  stepChat: 'Το υπάρχον chat σας',
  stepLook: 'Η εμφάνιση και το λογότυπό σας',
  stepChecks: 'Έλεγχος λειτουργίας',
  stepReady: 'Έτοιμο',
  stepDetailOpen: 'Άνοιγμα {domain}',
  stepDetailSearch: 'Εύρεση προϊόντων του καταστήματος',
  stepDetailProducts: 'Ανάγνωση προϊόντων, τιμών, φωτογραφιών, χρωμάτων και μεγεθών',
  stepDetailCart: 'Δοκιμή καλαθιού: προσθήκη, ανάγνωση, αφαίρεση και άδειασμα',
  stepDetailChat: 'Αναζήτηση του υπάρχοντος chat σας',
  stepDetailLook: 'Ανάγνωση λογοτύπου και χρωμάτων του καταστήματος',
  stepDetailChecks: 'Δοκιμή αναζητήσεων πελατών, προϊόντων, περιήγησης και καλαθιού',
  stepDetailChecksProducts:
    'Έλεγχος αναζητήσεων προϊόντων, περιήγησης, φωτογραφιών, χρωμάτων και μεγεθών',
  stepDetailChecksCart:
    'Έλεγχος προσθήκης, ανάγνωσης, αφαίρεσης και αδειάσματος καλαθιού, καθώς και ανάγνωσης προϊόντων',
  stepDetailChecksChat: 'Έλεγχος μεταφοράς στο υπάρχον chat σας και ανάγνωσης προϊόντων',
  stepDetailChecksMarket: 'Έλεγχος προϊόντων, τιμών και καλαθιού αυτής της αγοράς',
  nowRunning: 'Τώρα: {step}',
  nowProgress: 'Βήμα {n} από {total} · {minutes} λεπτά μέχρι τώρα',
  nowReady: 'Το κατάστημά σας είναι έτοιμο για βοηθό',
  nowReadyBody: 'Η αναζήτηση, τα προϊόντα και το καλάθι λειτουργούν.',
  nowLastRead: 'Τελευταία ανάγνωση {when}',
  nowUsually: 'συνήθως {min}–{max}',
  stepsRead: 'Ανάγνωση {when} · Εμφάνιση βημάτων',
  nowFailed: 'Η ανάγνωση σταμάτησε',
  factAnswers: 'Απαντά στους πελάτες με',
  costCounting: 'Καταμέτρηση…',
  factPaid: 'Πληρώνεται από',
  paidCompany: 'Εταιρικές πιστώσεις',
  creditsAmount: '{amount} πιστώσεις',
  creditsOne: '{amount} πίστωση',
  creditsUnder: 'κάτω από {amount} πιστώσεις',
  stopRun: 'Διακοπή εκτέλεσης',
  stopping: 'Διακοπή…',
  closeNote: 'Μπορείτε να αφήσετε τη σελίδα. Η εκτέλεση συνεχίζεται.',
  readyNote: 'Επόμενο: δοκιμάστε το widget και μετά εγκαταστήστε το.',
  buildWidget: 'Δοκιμή widget',
  readAgain: 'Νέα ανάγνωση καταστήματος',
  foundTitle: 'Τι βρέθηκε',
  foundProducts: 'Έγινε ανάγνωση προϊόντων και τιμών',
  foundSearch: 'Η αναζήτηση λειτουργεί',
  foundCart: 'Η προσθήκη στο καλάθι λειτουργεί',
  foundChat: 'Το chat σας στο {vendor}',
  foundNoChat: 'Δεν βρέθηκε δικό σας chat',
  foundLook: 'Τα χρώματα και το λογότυπό σας',
  widgetTitle: 'Το widget σας',
  widgetBody:
    'Ο βοηθός σας, με τα χρώματά σας. Δοκιμάστε τον εδώ: απαντά με τα πραγματικά προϊόντα σας. Δεν προστίθεται τίποτα σε πραγματικό καλάθι.',
  widgetLoading: 'Φόρτωση προεπισκόπησης…',
  widgetFailed: 'Δεν ήταν δυνατή η φόρτωση της προεπισκόπησης.',
  widgetNotReady: 'Το widget θα είναι έτοιμο όταν ολοκληρωθούν τα βήματα στην καρτέλα «Ρύθμιση».',
  installLineTitle: 'Προσθέστε αυτή τη γραμμή στον ιστότοπό σας',
  installLineBody:
    'Λειτουργεί σε κάθε πλατφόρμα: στην κεφαλίδα των σελίδων σας, στο Shopify μέσα στο theme.liquid ακριβώς πριν από το </head>. Δεν εμφανίζεται τίποτα μέχρι να δημοσιεύσετε.',
  installLineBodyLive:
    'Λειτουργεί σε κάθε πλατφόρμα: στην κεφαλίδα των σελίδων σας, στο Shopify μέσα στο theme.liquid ακριβώς πριν από το </head>. Οι πελάτες θα βλέπουν τότε την έκδοση {version}.',
  installPolicyAsk: 'Ο ιστότοπός σας ορίζει Πολιτική Ασφάλειας Περιεχομένου;',
  installPolicy:
    'Αν ο ιστότοπός σας ορίζει Πολιτική Ασφάλειας Περιεχομένου, επιτρέψτε επίσης το {origin} στο {directives}, αλλιώς ο βοηθός δεν μπορεί να ξεκινήσει.',
  installPolicyRefuses:
    'Η Πολιτική Ασφάλειας Περιεχομένου του ιστοτόπου σας απορρίπτει τον βοηθό: επιτρέψτε το {origin} στο {directives}, αλλιώς τίποτα δεν θα ξεκινήσει.',
  installPolicyNonce:
    'Η πολιτική σας εκτελεί μόνο έμπιστα script, γι’ αυτό προσθέστε τη γραμμή με το nonce των σελίδων σας.',
  installPolicyEval: "Ο βοηθός χρειάζεται επίσης το 'unsafe-eval' στο script-src.",
  copy: 'Αντιγραφή',
  copied: 'Αντιγράφηκε',
  domainsTitle: 'Ιστότοποι στους οποίους απαντά',
  domainsBody: 'Παραμένει αδρανής σε κάθε άλλο ιστότοπο, ακόμη κι αν αντιγράψει τη γραμμή.',
  lineFound: 'Η γραμμή βρέθηκε',
  lineNotSeen: 'Δεν υπάρχει ακόμη στη σελίδα',
  lineUnverified:
    'Δεν ήταν δυνατός ο έλεγχος: ο ιστότοπος μπλοκάρει τις αυτοματοποιημένες επισκέψεις. Ο βοηθός λειτουργεί κανονικά για τους αγοραστές.',
  lineChecked: 'ελέγχθηκε {when}',
  lineCheckAgain: 'Νέος έλεγχος',
  lineChecking: 'Έλεγχος σελίδας…',
  installCheckBusy: 'Ο έλεγχος είναι απασχολημένος αυτή τη στιγμή. Δοκιμάστε ξανά σε λίγο.',
  publishTitle: 'Δημοσίευση',
  publishBody:
    'Οι πελάτες στο {domain} θα το δουν αμέσως. Μπορείτε να αναιρέσετε τη δημοσίευση οποιαδήποτε στιγμή.',
  publishButton: 'Δημοσίευση έκδοσης {version}',
  publishConfirm: 'Δημοσίευση της έκδοσης {version} του {name};',
  publishNow: 'Δημοσίευση',
  notNow: 'Όχι τώρα',
  publishing: 'Δημοσίευση…',
  liveTitle: 'Δημοσιευμένο: έκδοση {version}',
  liveSince: 'Από {since}',
  unpublish: 'Αναίρεση δημοσίευσης',
  unpublishConfirm:
    'Να αφαιρεθεί η έκδοση {version} από τον ιστότοπό σας; Οι πελάτες δεν θα βλέπουν πλέον τον βοηθό μέχρι να δημοσιεύσετε ξανά.',
  publishedLine: 'Δημοσιευμένο τώρα: έκδοση {version}, από {since}',
  usageTitle: 'Χρήση',
  usageBody: 'Χρησιμοποιεί τις εταιρικές σας πιστώσεις, όπως και το υπόλοιπο Chat.',
  usageDiscovery: 'Ανάγνωση καταστήματος',
  usageChats: 'Συνομιλίες πελατών',
  usageChatsValue: '{count} συνομιλίες · {cost}',
  last7: 'Τελευταίες 7 ημέρες',
  askWeek: 'Ρωτήστε το Chat για αυτή την εβδομάδα',
  kpiConversations: 'Συνομιλίες',
  kpiVsPrevious: '{change} σε σχέση με την προηγούμενη εβδομάδα',
  kpiCart: 'Προσθήκες στο καλάθι',
  kpiCheckout: 'Έφτασαν στο ταμείο',
  kpiPerson: 'Ζήτησαν άτομο',
  kpiCost: 'Χρησιμοποιημένες πιστώσεις',
  ofConversations: '{percent} των συνομιλιών',
  checkoutNote: '{percent} των συνομιλιών',
  personNote: '{percent} άνοιξαν το chat σας',
  costNote: 'Περίπου {amount} ανά συνομιλία',
  costNoteUnder: 'Κάτω από {amount} ανά συνομιλία',
  sparkLabel: 'Συνομιλίες ανά ημέρα, τελευταίες {days} ημέρες',
  sparkDay: '{day}: {count} συνομιλίες',
  filterAll: 'Όλες',
  filterCart: 'Προσθήκη στο καλάθι',
  filterCheckout: 'Ταμείο',
  filterPerson: 'Άτομο',
  outcomeCart: 'Προσθήκη στο καλάθι',
  outcomeCheckout: 'Έφτασε στο ταμείο',
  outcomePerson: 'Ζήτησε άτομο',
  outcomeAnswered: 'Απαντήθηκε',
  devicePhone: 'Τηλέφωνο',
  deviceComputer: 'Υπολογιστής',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} λεπτά · Ξεκίνησε στη σελίδα {page}',
  anonymous: 'Ο πελάτης είναι ανώνυμος.',
  askConversation: 'Ρωτήστε το Chat για αυτή τη συνομιλία',
  showOlderVersions: 'Εμφάνιση παλαιότερων εκδόσεων',
  showOlder: 'Εμφάνιση παλαιότερων συνομιλιών',
  filterCountsWeek: 'Πλήθος: τελευταίες 7 ημέρες',
  readAlreadyHere:
    'Αυτό το κατάστημα υπάρχει ήδη εδώ, οπότε δεν ξεκίνησε κάτι νέο και δεν χρησιμοποιήθηκαν πιστώσεις.',
  readOpenShop: 'Άνοιγμα καταστήματος',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Τι μπορούμε να μάθουμε από αυτή τη συνομιλία;',
  askWeekMessage: 'Τι μπορούμε να μάθουμε από τις συνομιλίες αυτής της εβδομάδας;',
  conversationsChipOne: '{name} · συνομιλία από {when}',
  conversationsChipWeek: '{name} · αυτή την εβδομάδα',
  conversationsChipWeekIn: '{name} · {market} · αυτή την εβδομάδα',
  removeConversations: 'Κατάργηση των συνομιλιών',
  noConversations: 'Δεν υπάρχουν ακόμη συνομιλίες',
  noConversationsBody:
    'Όταν οι πελάτες χρησιμοποιούν τον βοηθό, οι συνομιλίες τους εμφανίζονται εδώ.',
  pickConversation: 'Επιλέξτε μια συνομιλία για να τη διαβάσετε.',
  eventCheckout: 'Πήγε στο ταμείο',
  eventPerson: 'Άνοιξε το chat σας',
  showMore: 'Εμφάνιση περισσότερων',
  urlInvalid:
    'Χρησιμοποιήστε τη διεύθυνση https του καταστήματός σας, όπως https://www.yourstore.com.',
  errorNoCompany:
    'Το Storefront χρειάζεται εταιρικό χώρο εργασίας. Ζητήστε από τον διαχειριστή σας να σας προσθέσει.',
  errorPublishPermission:
    'Μόνο όσοι μπορούν να δημοσιεύουν για την εταιρεία σας μπορούν να το κάνουν. Ρωτήστε τον διαχειριστή σας.',
  actionFailed: 'Δεν λειτούργησε. Δοκιμάστε ξανά.',
  copyFailed: 'Δεν ήταν δυνατή η αντιγραφή. Επιλέξτε τη γραμμή και αντιγράψτε την μόνοι σας.',
  reasonCancelled: 'Σταματήσατε την εκτέλεση.',
  reasonFailed:
    'Η ανάγνωση δεν ολοκληρώθηκε στο κατάστημά σας, επομένως ο βοηθός δεν είναι έτοιμος.',
  reasonFailedLive:
    'Η ανάγνωση δεν ολοκληρώθηκε στο κατάστημά σας. Ο δημοσιευμένος βοηθός συνεχίζει να λειτουργεί όπως πριν.',
  reasonFailedKept:
    'Η ανάγνωση δεν ολοκληρώθηκε στο κατάστημά σας, επομένως δεν άλλαξε τίποτα: η έκδοση {version} παραμένει όπως ήταν.',
  statusReadStopped: 'Έτοιμο · η τελευταία ανάγνωση σταμάτησε',
  reasonStepMarked: 'Το βήμα στο οποίο σταμάτησε σημειώνεται παρακάτω.',
  reasonReadAgain: 'Η νέα ανάγνωση ξεκινά από όσα έμαθε αυτή η ανάγνωση.',
  reasonNotAStore:
    'Αυτή η διεύθυνση δεν μοιάζει με ηλεκτρονικό κατάστημα: δεν υπήρχαν προϊόντα για αναζήτηση. Ελέγξτε τη διεύθυνση ή δοκιμάστε το κύριο domain του καταστήματος.',
  reasonTooSlow:
    'Η αναζήτηση του καταστήματός σας απαντά πολύ αργά για βοηθό αγορών: χρειάστηκε πάνω από 12 δευτερόλεπτα. Μια ταχύτερη αναζήτηση στο κατάστημα ή νέα ανάγνωση αργότερα μπορεί να βοηθήσει.',
  reasonOurSide:
    'Η ανάγνωση σταμάτησε λόγω προβλήματος από τη δική μας πλευρά, όχι στο κατάστημά σας, οπότε ο βοηθός δεν είναι ακόμη έτοιμος.',
  reasonOurSideLive:
    'Η ανάγνωση σταμάτησε λόγω προβλήματος από τη δική μας πλευρά, όχι στο κατάστημά σας. Ο δημοσιευμένος βοηθός συνεχίζει να λειτουργεί όπως πριν.',
  reasonOurSideKept:
    'Η ανάγνωση σταμάτησε λόγω προβλήματος από τη δική μας πλευρά, όχι στο κατάστημά σας, επομένως δεν άλλαξε τίποτα: η έκδοση {version} παραμένει όπως ήταν.',
  reasonTryLater: 'Δοκιμάστε να διαβάσετε ξανά το κατάστημα σε λίγο.',
  errorNoCredits:
    'Δεν υπάρχουν διαθέσιμες πιστώσεις για αυτό αυτή τη στιγμή. Ένας ιδιοκτήτης της εταιρείας μπορεί να ελέγξει το πρόγραμμα και τα όρια στο Usage.',
  errorReadUnavailable:
    'Αυτό το κατάστημα δεν μπορεί να διαβαστεί αυτή τη στιγμή. Δοκιμάστε ξανά σε λίγα λεπτά.',
  reasonTimeout:
    'Η ανάγνωση έφτασε στο χρονικό της όριο πριν ολοκληρωθεί. Η νέα ανάγνωση συνεχίζει από όσα έχουν ήδη επαληθευτεί.',
  retrying: 'Νέα προσπάθεια…',
  readTitleChecks: 'Νέος έλεγχος της αποθηκευμένης ανάγνωσης',
  checksScopeLabel: 'Έλεγχοι',
  readScopeChecks:
    'Αναζητήσεις πελατών, προϊόντα, περιήγηση και καλάθι, πάνω σε ό,τι αποθήκευσε η τελευταία ανάγνωση. Το κατάστημα δεν διαβάζεται ξανά.',
  readScopeChecksProducts:
    'Αναζητήσεις προϊόντων, περιήγηση, φωτογραφίες, χρώματα και μεγέθη, πάνω σε ό,τι αποθήκευσε η τελευταία ανάγνωση. Το κατάστημα δεν διαβάζεται ξανά.',
  readScopeChecksCart:
    'Προσθήκη, ανάγνωση, αφαίρεση και άδειασμα καλαθιού, καθώς και ανάγνωση προϊόντων, πάνω σε ό,τι αποθήκευσε η τελευταία ανάγνωση. Το κατάστημα δεν διαβάζεται ξανά.',
  readScopeChecksChat:
    'Μεταφορά στο υπάρχον chat σας και ανάγνωση προϊόντων, πάνω σε ό,τι αποθήκευσε η τελευταία ανάγνωση. Το κατάστημα δεν διαβάζεται ξανά.',
  readScopeChecksMarket:
    'Προϊόντα, τιμές και καλάθι της αγοράς, πάνω σε ό,τι αποθήκευσε η τελευταία ανάγνωση. Το κατάστημα δεν διαβάζεται ξανά.',
  checksCost: 'Μόνο οι νέοι έλεγχοι χρησιμοποιούν εταιρικές πιστώσεις',
  checksAfter:
    'Αποθηκεύει νέο προσχέδιο αν περάσουν οι έλεγχοι. Τίποτα δεν φτάνει στους πελάτες μέχρι να δημοσιεύσετε.',
  checksAfterLive:
    'Αποθηκεύει νέο προσχέδιο αν περάσουν οι έλεγχοι. Ο δημοσιευμένος βοηθός συνεχίζει να λειτουργεί και οι πελάτες βλέπουν την αλλαγή μόνο μετά τη δημοσίευση.',
  checksStarted:
    'Οι έλεγχοι ξεκίνησαν. Όταν είναι έτοιμο το νέο προσχέδιο, ζητήστε από το Chat να το ελέγξει.',
  checkingSavedWork: 'Νέος έλεγχος της αποθηκευμένης εργασίας',
  checkAgain: 'Νέος έλεγχος',
  checkingAgain: 'Γίνεται νέος έλεγχος…',
  reasonCheckAgain:
    'Ο «Νέος έλεγχος» δοκιμάζει ξανά την αποθηκευμένη εργασία, χωρίς νέα ανάγνωση του καταστήματος. Μόνο οι νέοι έλεγχοι χρησιμοποιούν εταιρικές πιστώσεις.',
  fixWithChat: 'Διόρθωση με Chat',
  fixReadPrompt:
    'Η ανάγνωση του {name} ({domain}) σταμάτησε. Δείτε τι συνέβη και βοηθήστε με να λειτουργήσει: πείτε μου τι βρίσκετε, προτείνετε τι θα μπορούσα να πω στην ανάγνωση και μετά ελέγξτε την ξανά ή διαβάστε ξανά με αυτή την υπόδειξη, όσο το δυνατόν μικρότερο μέρος του καταστήματος.',
  fixReadPromptStep:
    'Η ανάγνωση του {name} ({domain}) σταμάτησε στο βήμα «{step}». Δείτε τι συνέβη και βοηθήστε με να λειτουργήσει: πείτε μου τι βρίσκετε, προτείνετε τι θα μπορούσα να πω στην ανάγνωση και μετά ελέγξτε την ξανά ή διαβάστε ξανά με αυτή την υπόδειξη, όσο το δυνατόν μικρότερο μέρος του καταστήματος.',
  stepStateDone: 'ολοκληρώθηκε',
  stepStateRunning: 'σε εξέλιξη',
  stepStateWaiting: 'δεν ξεκίνησε',
  stepStateFailed: 'σταμάτησε',
  newVersionTitle: 'Η έκδοση {version} είναι έτοιμη',
  newVersionBody: 'Δημοσιεύστε την για να αντικαταστήσει την έκδοση {live}.',
  newVersionBodyLive: 'Δημοσιεύστε την για να αντικαταστήσει την έκδοση {live} στο {domain}.',
  previewSize: 'Μέγεθος προεπισκόπησης',
  designWithChat: 'Άνοιγμα σχεδίου',
  designReviewBody: 'Δείτε το τρέχον σχέδιο και πείτε στο Chat τι θέλετε να προσαρμόσετε.',
  designWhileTitle: 'Σχεδιάστε τον ενώ διαβάζουμε το κατάστημά σας',
  designWhileBody:
    'Το Chat μπορεί να διαμορφώσει μαζί σας τώρα την εμφάνιση, τα λόγια και το λογότυπο του βοηθού. Ο σχεδιασμός θα είναι έτοιμος όταν ολοκληρωθεί η ανάγνωση.',
  designPreviewNote: 'Προεπισκόπηση με ενδεικτικά προϊόντα μέχρι να διαβαστεί το κατάστημά σας.',
  proposedDesign: 'Προτεινόμενος σχεδιασμός',
  proposedDesignNote:
    'Το widget με αυτή την αλλαγή, εμφανισμένο με ενδεικτικά προϊόντα. Η έγκριση το αποθηκεύει ως νέο προσχέδιο· η δημοσίευση παραμένει στη σελίδα Storefront.',
  lineSources: 'Από τις σελίδες του καταστήματος:',
  readAgainConfirm:
    'Να διαβαστεί ξανά το {name}; Χρειάζονται περίπου 10 έως 30 λεπτά και χρησιμοποιούνται εταιρικές πιστώσεις.',
  readAgainLive:
    'Ο δημοσιευμένος βοηθός συνεχίζει να λειτουργεί μέχρι να δημοσιεύσετε τη νέα έκδοση.',
  readAgainNow: 'Νέα ανάγνωση',
  switchStore: 'Αλλαγή καταστήματος ή προσθήκη',
  storesLabel: 'Τα καταστήματά σας',
  addStore: 'Προσθήκη άλλου καταστήματος',
  backToStores: 'Επιστροφή στα καταστήματά σας',
  storeLive: 'Δημοσιευμένο',
  storeReady: 'Έτοιμο',
  storeReading: 'Σε ανάγνωση',
  storeStopped: 'Σταμάτησε',
  actionDesignTitle: 'Αλλαγή σχεδιασμού του βοηθού αγορών',
  actionKnowledgeTitle: 'Αλλαγή όσων γνωρίζει ο βοηθός αγορών',
  readTitleProducts: 'Νέα ανάγνωση προϊόντων',
  readTitleCart: 'Νέος έλεγχος καλαθιού',
  readTitleTheirChat: 'Νέος έλεγχος πρόσβασης στο δικό σας chat',
  readTitleAll: 'Νέα ανάγνωση ολόκληρου του καταστήματος',
  readTitleStore: 'Ανάγνωση νέου καταστήματος',
  readTitleMarket: 'Προσθήκη αγοράς',
  readShopLabel: 'Κατάστημα',
  readScopeLabel: 'Αναγνώσεις',
  readTimeLabel: 'Διάρκεια',
  readCostLabel: 'Κόστος',
  readAfterLabel: 'Μετά',
  readScopeProducts:
    'Αναζήτηση και ταξινόμηση προϊόντων, φωτογραφίες, χρώματα και μεγέθη προϊόντων',
  readScopeCart: 'Προσθήκη στο καλάθι και αφαίρεση από αυτό',
  readScopeTheirChat: 'Πώς φτάνουν οι πελάτες στο δικό σας chat',
  readScopeAll: 'Προϊόντα, καλάθι, δικό σας chat και αγορές',
  readScopeStore: 'Προϊόντα, τιμές και μεγέθη, καλάθι, δικό σας chat, εμφάνιση και αγορές',
  readScopeMarket: '{market}: προϊόντα, τιμές και καλάθι',
  readTime: 'Συνήθως {min}–{max} λεπτά',
  readCost: 'Χρησιμοποιεί εταιρικές πιστώσεις',
  readCostLast:
    'Χρησιμοποιεί εταιρικές πιστώσεις · η τελευταία ανάγνωση αυτού του καταστήματος χρησιμοποίησε {credits}',
  readCostLastPartial:
    'Χρησιμοποιεί εταιρικές πιστώσεις · η τελευταία ανάγνωση αυτού του καταστήματος σταμάτησε νωρίς και χρησιμοποίησε τουλάχιστον {credits}',
  readAfter: 'Αποθηκεύει νέο προσχέδιο. Τίποτα δεν φτάνει στους πελάτες μέχρι να δημοσιεύσετε.',
  readAfterLive:
    'Αποθηκεύει νέο προσχέδιο. Ο δημοσιευμένος βοηθός συνεχίζει να λειτουργεί και οι πελάτες βλέπουν την αλλαγή μόνο μετά τη δημοσίευση.',
  readAfterNew: 'Αποθηκεύει πρώτο προσχέδιο για δοκιμή. Τίποτα δεν δημοσιεύεται.',
  readCartNote:
    'Για να ελέγξει το καλάθι, προσθέτει ένα δοκιμαστικό προϊόν και το αφαιρεί ξανά. Δεν ολοκληρώνει ποτέ αγορά.',
  readFocusLabel: 'Τι ζήτησε το Chat από την ανάγνωση να διορθώσει',
  readFooter:
    'Τίποτα δεν ξεκινά μέχρι να εγκρίνετε. Μπορείτε να σταματήσετε την ανάγνωση στην καρτέλα «Ρύθμιση».',
  readStartedAt:
    'Ξεκίνησε {time} · συνήθως {min}–{max} λεπτά. Όταν είναι έτοιμο το νέο προσχέδιο, ζητήστε από το Chat να το ελέγξει.',
  readFocusAll: 'Εμφάνιση όλων',
  readFocusLess: 'Εμφάνιση λιγότερων',
  readStart: 'Έναρξη ανάγνωσης',
  addToSite: 'Προσθήκη στον ιστότοπό σας',
  readTab: 'Αίτημα',
  readStarted:
    'Η ανάγνωση ξεκίνησε. Όταν είναι έτοιμο το νέο προσχέδιο, ζητήστε από το Chat να το ελέγξει.',
  readFollow: 'Παρακολούθηση στην καρτέλα «Ρύθμιση»',
  proposedDesignLive:
    'Η πραγματική προεπισκόπηση του καταστήματός σας με αυτόν τον σχεδιασμό. Ρωτήστε τον κάτι: τίποτα δεν προστίθεται σε πραγματικό καλάθι. Η έγκριση το αποθηκεύει ως νέο προσχέδιο· η δημοσίευση παραμένει στη σελίδα Storefront.',
  designChanges: 'Τι αλλάζει',
  designReset: 'επιστροφή στις προεπιλογές',
  designGroupColors: 'Χρώματα',
  designGroupType: 'Γραμματοσειρές',
  designGroupShape: 'Σχήμα',
  designGroupLauncher: 'Κουμπί στη σελίδα',
  designGroupPanel: 'Κεφαλίδα',
  designGroupProducts: 'Προϊόντα',
  designGroupCart: 'Καλάθι του καταστήματος',
  designGroupCopy: 'Λέξεις',
  designGroupLogo: 'Λογότυπο',
  designGroupCss: 'Προσαρμοσμένο CSS',
  designColorBrand: 'Μάρκα',
  designColorActions: 'Προσθήκη και αποστολή',
  designColorPanel: 'Πίνακας',
  designColorSurface: 'Επιφάνειες',
  designColorInk: 'Κείμενο',
  designColorMuted: 'Δευτερεύον κείμενο',
  designColorLine: 'Γραμμές',
  designColorBubble: 'Μηνύματα πελατών',
  designColorBubbleInk: 'Κείμενο πελάτη',
  designActionsBrand: 'Χρώμα μάρκας',
  designActionsInk: 'Χρώμα κειμένου',
  designWordsName: 'Όνομα',
  designWordsGreeting: 'Χαιρετισμός',
  designWordsStarters: 'Προτεινόμενες ερωτήσεις',
  designWordsLauncher: 'Ετικέτα κουμπιού',
  designWordsPlaceholder: 'Κείμενο υπόδειξης',
  designWordsAi: 'Ετικέτα AI',
  designWordsPerson: 'Σύνδεσμος προς άτομο',
  noteNew: 'Νέο',
  noteChanged: 'Τροποποιήθηκε',
  noteRemoved: 'Αφαιρέθηκε',
  noteSourceKnowledge: 'Σελίδα γνώσεων: {title}',
  noteSourceFile: 'Αρχείο: {name}',
  noteSourceChat: 'Από το chat',
  knowledgeApprovalNote:
    'Οι πελάτες μπορούν να διαβάσουν όλο το περιεχόμενο αυτών των σημειώσεων. Η έγκριση αποθηκεύει νέο προσχέδιο: ρωτήστε τον βοηθό στην καρτέλα Widget και μετά δημοσιεύστε τον στην καρτέλα «Εγκατάσταση».',
  profileTitle: 'Σχετικά με το κατάστημα',
  profileChecked: 'Ελέγχθηκε {date}',
  profileSources: 'Γράφτηκε από',
  profileSample: '{count} προϊόντα δειγματοληπτήθηκαν για το επίπεδο τιμών',
  profileSampleOne: '{count} προϊόν δειγματοληπτήθηκε για το επίπεδο τιμών',
  profileAssistant: 'Τι μπορεί να κάνει εδώ ο βοηθός',
  profileTeam: 'Γράφτηκε από την ομάδα σας',
  profileEmpty:
    'Δεν έχει γραφτεί ακόμη. Ζητήστε από το Chat να τρέξει μια διαδικασία γνώσης: το γράφει από τις δικές σας σελίδες και όσα είδε στον ιστότοπό σας, και μπορείτε να προσθέσετε δικές σας γραμμές.',
  profileWithChat: 'Προσθήκη ή αλλαγή με το Chat',
  profilePrompt:
    'Βοήθησέ με να βελτιώσω όσα γνωρίζει ο βοηθός αγορών για το {name} ({domain}) για το κατάστημά μας συνολικά: τι πουλάμε, για τι είμαστε γνωστοί, για ποιον είμαστε και τι μπορούν να κάνουν εδώ οι αγοραστές. Διάβασε πρώτα το προφίλ «Σχετικά με το κατάστημα» και πες μου τι λείπει ή είναι λάθος. Ό,τι σου λέω για το κατάστημα και δεν το αναφέρει καμία σελίδα μπαίνει ως γραμμή της ομάδας μας.',
  profilePartNavigation: 'Τμήματα',
  profilePartWelcome: 'Χαιρετισμός',
  profilePartMarkets: 'Αγορές',
  profilePartFunctions: 'Τι μπορεί να κάνει ο βοηθός',
  profilePartProducts: 'Προϊόντα',
  profilePartPages: 'Σελίδες εξυπηρέτησης',
  profileCleared: 'Διαγράφει το γραμμένο προφίλ',
  knowledgeTitle: 'Τι γνωρίζει',
  knowledgeBody:
    'Εκτός από τα προϊόντα, τις τιμές και το απόθεμά σας, τα οποία διαβάζει πάντα ζωντανά από το κατάστημά σας.',
  knowledgeEmpty:
    'Μέχρι στιγμής μόνο ο κατάλογός σας. Προσθέστε πληροφορίες για παράδοση, επιστροφές ή πληρωμές με το Chat, από τις δικές σας σελίδες ή τη Γνώση.',
  knowledgeWithChat: 'Προσθήκη με Chat',
  knowledgeChangeWithChat: 'Αλλαγή με Chat',
  knowledgePrompt:
    'Βοηθήστε με να επιλέξω τι θα λέει στους πελάτες ο βοηθός αγορών για {name} ({domain}) εκτός από τα προϊόντα μας, όπως για παράδοση, επιστροφές και πληρωμές. Βρείτε το στις δικές μας σελίδες ή στη Γνώση και προτείνετε σημειώσεις που μπορώ να εγκρίνω.',
  pagesTitle: 'Σελίδες που διαβάζει',
  pagesBody:
    'Οι σελίδες από τις οποίες προέρχονται οι σημειώσεις. Η Poneva τις ελέγχει κάθε μέρα και το Chat σάς ενημερώνει όταν αλλάζει κάποια.',
  pagesEmpty:
    'Δεν υπάρχουν σελίδες ακόμα. Εδώ εμφανίζονται οι σελίδες που βρίσκει η ανάγνωση του καταστήματός σας και όσες ονομάσετε στο Chat.',
  pageFound: 'βρέθηκε από την Poneva',
  pageLinkedFrom: 'συνδέεται από {page}',
  pageAddedByYou: 'προστέθηκε από εσάς',
  pageFromNote: 'αναφέρεται σε σημείωση',
  pageNoteOne: '1 σημείωση',
  pageNotesMany: '{count} σημειώσεις',
  pageStatusOk: 'Ενημερωμένη',
  pageStatusChanged: 'Τροποποιήθηκε',
  pageStatusUnreadable: 'Δεν είναι δυνατή η ανάγνωση',
  pageStatusUnchecked: 'Δεν έχει ελεγχθεί ακόμα',
  pagesChangeWithChat: 'Αλλαγή με Chat',
  pagesUpdateWithChat: 'Ενημέρωση σημειώσεων με Chat',
  pagesPrompt:
    'Βοηθήστε με να αλλάξω ποιες σελίδες διαβάζει ο βοηθός αγορών του {name} ({domain}) για τις σημειώσεις του. Δείξτε μου τις σελίδες που χρησιμοποιεί τώρα και ρωτήστε με ποιες να προσθέσω ή να αφαιρέσω.',
  pagesUpdatePrompt:
    'Μερικές σελίδες του {name} ({domain}) άλλαξαν ή δεν μπορούν πλέον να διαβαστούν από τη δημιουργία των σημειώσεων. Δείξτε μου ποιες είναι και ξαναφτιάξτε τις σημειώσεις από αυτές ως πρόχειρο που μπορώ να δω.',
  viewCompare: 'Σύγκριση',
  lookDeviceLabel: 'Σύγκριση σε',
  lookShop: 'Το κατάστημά σας',
  lookShopCaption: 'Αρχική σελίδα όπως διαβάστηκε {when}',
  lookWidget: 'Ο βοηθός',
  lookWidgetCaption: 'Έκδοση {version}',
  lookLoading: 'Τοποθέτηση του βοηθού δίπλα στο κατάστημά σας…',
  lookFailed: 'Η σύγκριση δεν μπόρεσε να γίνει αυτή τη στιγμή.',
  lookNoShopPicture: 'Η ανάγνωση δεν κράτησε εικόνα του καταστήματός σας σε αυτό το πλάτος.',
  lookNoneTitle: 'Η ίδια η εμφάνιση του καταστήματός σας δεν αποθηκεύτηκε',
  lookNoneBody:
    'Αυτή η ανάγνωση δεν την κατέγραψε. Διαβάστε ξανά το κατάστημα από τη Ρύθμιση και το λογότυπο, τα χρώματα και οι σελίδες του θα αποθηκευτούν για σύγκριση.',
  lookColoursShop: 'Χρώματα που χρησιμοποιεί το κατάστημά σας',
  lookColoursAssistant: 'Χρώματα του βοηθού',
  lookRoleHeader: 'Κεφαλίδα',
  lookRoleBrand: 'Μάρκα',
  lookSame: 'Ίδιο με το χρώμα {colour} του καταστήματός σας ({source})',
  lookOff:
    'Κοντά στο χρώμα {colour} του καταστήματός σας ({source}) αλλά όχι ίδιο, διαφορά {delta}',
  lookOwn: 'Δικό του χρώμα: το κατάστημά σας δεν το χρησιμοποιεί',
  lookNeutral: 'Το κατάστημά σας δεν φορτώθηκε για αυτό: εμφανίζεται μόνο όσα κράτησε η ανάγνωση.',
  lookFromLogo: 'λογότυπο',
  lookFromTheme: 'χρώμα θέματος',
  lookFromTile: 'χρώμα κάρτας',
  lookFromMask: 'καρφιτσωμένο εικονίδιο',
  lookFromManifest: 'μανιφέστο εφαρμογής',
  lookFromHeader: 'κεφαλίδα',
  lookFromButton: 'κουμπιά',
  lookFromVariable: 'χρώμα στυλ',
  lookFix: 'Διόρθωση με Chat',
  lookFixPrompt:
    'Το χρώμα του βοηθού για {role} είναι {colour}, κοντά στο χρώμα {exact} του καταστήματός σας ({source}) αλλά όχι ίδιο. Χρησιμοποιήστε το ακριβές χρώμα του καταστήματός σας, {exact}.',
  lookSwatch: 'Χρώμα {colour}',
  errorDesignChecks: 'Αυτή η έκδοση δεν πέρασε τους ελέγχους σχεδίασης.',
  designSamplesBlocked:
    'Οι διατάξεις καρτών δεν μπορούν να αποδειχθούν εδώ: ο ιστότοπός σας μπλοκάρει τις αυτοματοποιημένες επισκέψεις. Τα χρώματα, οι γραμματοσειρές και το λογότυπο λειτουργούν κανονικά.',
  designCheckLabel: 'Μια ετικέτα AI είναι πολύ αχνή για να διαβαστεί.',
  designCheckReach: 'Ένα κουμπί δεν είναι προσβάσιμο.',
  designCheckSize: 'Κάποιο κείμενο είναι πολύ μικρό.',
  designCheckOverflow: 'Ο πίνακας κυλά πλάγια.',
  designCheckRender: 'Οι έλεγχοι δεν μπόρεσαν να εκτελεστούν. Δοκιμάστε ξανά σε λίγο.',
  fixDesignPrompt:
    'Προσπάθησα να δημοσιεύσω την πιο πρόσφατη πρόχειρη έκδοση του {name} ({domain}), αλλά δεν πέρασε τους ελέγχους σχεδίασης: {facts}. Διορθώστε τη σχεδίαση σε αυτή τη συνομιλία και ελέγξτε την σε κινητό και υπολογιστή. Μην τη δημοσιεύσετε.',
};

const hu: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Vásárlási asszisztens',
  designUnconfirmed:
    'Leállt, mielőtt a Chat megerősíthette volna, hogy a dizájn mentése megtörtént. Az asszisztens vászna a legújabb verziót mutatja.',
  errorNewerVersion: 'A megnyitásod óta újabb verzió lett mentve. Előbb nézd meg, majd tedd közzé.',
  canvasSelectHint:
    'Kattints az asszisztens egy részére, hogy a chatben módosítsd · Esc a leállításhoz',
  canvasDraft: '{version}. verzió · még nincs közzétéve',
  canvasDraftOverLive: '{version}. verzió · a {live}. verzió van közzétéve',
  canvasLive: '{version}. verzió · közzétéve',
  canvasNoVersion: 'Az első verzió az áruház olvasásának befejezésekor készül el',
  canvasPublish: 'Közzététel a Storefront oldalon',
  canvasFrameTitle: '{name} vásárlási asszisztense',
  viewLabel: 'Nézet',
  photosGood: '{count} termékfotó: teljes és éles',
  photosGoodOne: 'A termékfotó teljes és éles',
  canvasMissingTitle: 'Ez a vásárlási asszisztens nincs ebben a munkatérben',
  canvasMissingBody: 'Másik munkatérhez tartozik, vagy eltávolították.',
  canvasMissingLink: 'A munkatér asszisztenseinek megtekintése',
  photosLabel: 'Termékfotók',
  photosSmall: '{count} túl kicsi a csempéjén',
  photosCut: '{count} le van vágva',
  photosSoft: '{count} fel van nagyítva, ezért homályos',
  photosMissing: '{count} fotó nélkül',
  photosFix: 'Javítás a Chattel',
  photosPrompt:
    'Tedd rendbe a kártyák termékfotóit: mindegyik legyen teljes, elég nagy a csempéjén és éles.',
  photosFacts:
    '{total} fotó: {small} túl kicsi a csempéjén, {cut} levágva, {soft} felnagyítva, {missing} fotó nélkül. Méretek: {sizes}',
  viewDesign: 'Dizájn',
  viewVersions: 'Verziók',
  versionsLoading: 'Verziók betöltése…',
  versionsFailed: 'A verziókat nem sikerült betölteni.',
  versionTitle: '{version}. verzió',
  versionLive: 'Közzétéve',
  versionChecked: 'Ellenőrizve',
  versionToday: 'Ma',
  versionYesterday: 'Tegnap',
  versionEarlier: 'Korábban',
  versionByPerson: '{name} kérte',
  versionByChat: 'A chatben kérve',
  versionFromReading: 'A(z) {domain} olvasásából',
  versionPublishedBy: 'közzétette: {name}, {at}',
  versionPublish: 'Közzététel',
  versionRestore: 'Visszaállítás…',
  versionRestoreTitle: 'Visszaállítod a(z) {version}. verziót?',
  versionRestoreBody:
    'Ez lesz a(z) {next}. verzió, új piszkozatként. A webhelyed addig nem változik, amíg közzé nem teszed.',
  versionRestoreConfirm: 'Visszaállítás',
  versionRestoreCancel: 'Mégse',
  versionRestored: 'Visszaállítva a(z) {version}. verzióként',
  versionRestoreFailed: 'A verziót nem sikerült visszaállítani.',
  compareTitle: 'A(z) {to}. és a(z) {from}. verzió összehasonlítása',
  compareTitleLive: 'A(z) {to}. verzió összehasonlítása a közzétett {from}. verzióval',
  compareHint: 'Húzd el az összehasonlításhoz',
  compareLabel: 'A(z) {from}. és a(z) {to}. verzió összehasonlítása',
  compareTagLive: 'v{version} · Közzétéve',
  compareTag: 'v{version}',
  changesTitle: 'Mi változott a(z) {from}. verzióhoz képest a(z) {to}. verzióban',
  changesNone: 'A dizájnok azonosak. Csak a megjegyzések vagy az áruház adatai változtak.',
  changeAdded: 'Hozzáadva',
  changeRemoved: 'Eltávolítva',
  changeChanged: 'Módosítva',
  changeDrawn: 'rajzolt logó',
  changeCss: 'saját CSS',
  changeYes: 'igen',
  changeNo: 'nem',
  'field.colors.brand': 'Márkaszín',
  'field.colors.actions': 'Gombszín',
  'field.colors.panel': 'Panel színe',
  'field.colors.surface': 'Felület színe',
  'field.colors.ink': 'Szövegszín',
  'field.colors.muted': 'Halvány szöveg színe',
  'field.colors.line': 'Vonal színe',
  'field.colors.bubble': 'Vásárlói üzenet színe',
  'field.colors.bubbleInk': 'Vásárlói üzenet szövege',
  'field.colors.header': 'Fejléc színe',
  'field.colors.headerInk': 'Fejlécszöveg színe',
  'field.colors.tints': 'Lágy árnyalatok',
  'field.copy.assistantName': 'Név',
  'field.copy.greeting': 'Üdvözlés',
  'field.copy.placeholder': 'Kérdésmező',
  'field.copy.starters': 'Kezdőkérdések',
  'field.copy.launcherLabel': 'Indítógomb szövege',
  'field.copy.aiLabel': 'MI-címke',
  'field.copy.personLabel': 'Kapcsolat egy munkatárssal',
  'field.type.body': 'Szöveg betűtípusa',
  'field.type.display': 'Cím betűtípusa',
  'field.type.fontCss': 'Betűtípus forrása',
  'field.type.scale': 'Szövegméret',
  'field.shape.radius': 'Sarkok',
  'field.shape.shadow': 'Árnyék',
  'field.shape.density': 'Térköz',
  'field.launcher.style': 'Indítógomb számítógépen',
  'field.launcher.phoneStyle': 'Indítógomb telefonon',
  'field.launcher.side': 'Indítógomb oldala',
  'field.launcher.icon': 'Indítógomb ikonja',
  'field.panel.header': 'Fejléc mérete',
  'field.panel.opening': 'Első képernyő',
  'field.products.layout': 'Termékek',
  'field.products.imageFit': 'Termékfotók',
  'field.products.imageRatio': 'Fotócsempe alakja',
  'field.products.imagePadding': 'Fotók körüli hely',
  'field.products.imageBackground': 'Fotócsempe színe',
  'field.products.picker': 'Méretválasztó',
  'field.products.showStock': 'Készlet megjelenítése',
  'field.cart.landing': 'Kosáranimáció',
  'field.logo.svg': 'Logó',
  'field.logo.shape': 'Logó alakja',
  'field.logo.plate': 'Logó háttere',
  'field.css': 'Egyéni CSS',
  'value.ink': 'tinta',
  'value.brand': 'márkaszín',
  'value.small': 'kicsi',
  'value.medium': 'közepes',
  'value.large': 'nagy',
  'value.square': 'négyzetes',
  'value.soft': 'lágy',
  'value.round': 'kerek',
  'value.flat': 'lapos',
  'value.deep': 'mély',
  'value.compact': 'tömör',
  'value.comfortable': 'kényelmes',
  'value.pill': 'szöveges kapszula',
  'value.circle': 'kör',
  'value.right': 'jobb',
  'value.left': 'bal',
  'value.logo': 'logó',
  'value.chat': 'chatbuborék',
  'value.sparkle': 'szikra',
  'value.bag': 'táska',
  'value.full': 'teljes',
  'value.middle': 'középen',
  'value.bottom': 'alul',
  'value.rows': 'sorok',
  'value.grid': 'rács',
  'value.carousel': 'lapozható sor',
  'value.contain': 'teljes fotó',
  'value.cover': 'kitöltött csempe',
  'value.portrait': 'álló',
  'value.landscape': 'fekvő',
  'value.none': 'nincs',
  'value.pop': 'felszálló +1',
  'value.ring': 'hullám',
  'value.blink': 'villanás',
  'value.chips': 'egymás melletti gombok',
  'value.menu': 'lista',
  'value.rounded': 'lekerekített',
  designCardTitle: '{name} vásárlási asszisztense',
  designCardSaved: '{version}. verzió mentve · még nincs közzétéve',
  designCardSavedFirst: 'Dizájn mentve · ez lesz az első verzió',
  designSaving: 'Dizájn mentése…',
  selectedAssistantPart: 'Kiválasztva: az asszisztens egy része',
  loading: 'Storefront betöltése…',
  disabledTitle: 'A Storefront nincs bekapcsolva ebben a munkatérben.',
  disabledBody:
    'Korai hozzáférésben van, és fokozatosan érkezik. Kérdezd meg a Poneva-kapcsolattartódat, mikor lesz elérhető nálatok.',
  loadFailed: 'A Storefrontot nem sikerült betölteni.',
  startTitle: 'Vásárlási asszisztens az áruházadhoz',
  startBody:
    'A Poneva úgy olvassa az áruházadat, ahogy a vásárló használja: keresés, termékek, kosár és a már meglévő chat. Ez körülbelül 10–30 percet vesz igénybe, a céged kreditjeit használja, te pedig közben dolgozhatsz.',
  urlLabel: 'Az áruházad címe',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Áruházam olvasása',
  starting: 'Indítás…',
  startFailed: 'Ezt az áruházat nem sikerült beolvasni. Ellenőrizd a címet, majd próbáld újra.',
  tabConversations: 'Beszélgetések',
  tabOverview: 'Áttekintés',
  allAssistants: 'Összes asszisztens',
  allAssistantsNote: '{assistants} asszisztens · {sites} webhely',
  allAssistantsMenuNote: '{count} beszélgetés ezen a héten',
  marketLabel: 'Piac',
  allMarkets: 'Összes piac',
  allMarketsNote: '{count} webhely',
  scopeIn: '{assistant} itt: {market}',
  askAbout: 'Kérdezd a Chatet erről: {scope}',
  overviewAssistantsTitle: 'Asszisztensek és piacok',
  overviewMarketsTitle: 'Piacok',
  overviewAssistantsOnly: 'Asszisztensek',
  overviewIdle: '{count} asszisztens, még nincs beszélgetés',
  allAssistantsCount: '{assistants} asszisztens',
  overviewFailed: 'Az áttekintést nem sikerült betölteni.',
  colConversations: 'Beszélgetések',
  colCart: 'Kosárba téve',
  colCheckout: 'Pénztár',
  colPerson: 'Munkatárs',
  colCost: 'Kreditek',
  rowMarkets: '{count} piac',
  rowVersion: '{version}. verzió',
  rowAria:
    '{name}, {sub}: {conversations} beszélgetés, {cart} kosárba téve, {checkout} eljutott a pénztárig, {person} munkatársat kért, {cost} kredit',
  versionPublishedAt: 'közzétéve: {at}',
  designColorHeader: 'Fejléc',
  designColorHeaderInk: 'Fejléc szövege',
  designGroupTranslations: 'Szavak más nyelveken',
  topicsTitle: 'Amire a vásárlók rákérdeznek',
  topicsNote: 'A Chat által az első kérdésekben talált témák',
  startPagesTitle: 'Hol kezdődnek a beszélgetések',
  startPagesOne: 'Minden beszélgetés egyféle oldalon kezdődött: {page}.',
  'startPage.product': 'Termékoldalak',
  'startPage.search': 'Keresési eredmények',
  'startPage.start': 'Kezdőoldal',
  'startPage.cart': 'Kosár',
  'startPage.other': 'Más oldalak',
  addMarketInChat: 'Piac hozzáadása a chatben',
  marketsTitle: 'Piacok',
  marketsBody:
    'Egy asszisztens és egy dizájn. Minden piacnak saját nyelve, pénzneme, termékei, kosara és munkatársi chatje van.',
  marketChecked: 'ellenőrizve: {when}',
  'marketStatus.ready': 'Kész',
  'marketStatus.found': 'Nincs hozzáadva',
  'marketStatus.checking': 'Ellenőrzés',
  'marketStatus.failed': 'Javítás szükséges',
  addMarketPrompt:
    'Adj hozzá egy másik piacot a(z) {name} vásárlási asszisztenséhez ({domain}): keresd meg az áruház más országait, nyelveit vagy webhelyeit, és kérdezd meg, melyiket adjam hozzá.',
  tabWidget: 'Widget',
  tabInstall: 'Telepítés',
  tabSetup: 'Beállítás',
  statusReading: 'Áruházad olvasása',
  readingProducts: 'Termékek újbóli olvasása',
  readingCart: 'Kosár újbóli ellenőrzése',
  readingChat: 'A saját chathez vezető út ellenőrzése',
  readingMarket: 'A(z) {market} piac hozzáadása',
  readingMarketAny: 'Piac hozzáadása',
  statusReady: 'Telepítésre kész',
  statusFailed: 'Az olvasás leállt',
  statusLive: 'Élő · {version}. verzió a(z) {domain} oldalon',
  statusPublished: 'Közzétéve · {version}. verzió · még nincs a webhelyeden',
  statusPublishedUnverified: 'Közzétéve · {version}. verzió · a webhelyed nem ellenőrizhető',
  stepOpen: 'Webhely megnyitása',
  stepSearch: 'Keresés',
  stepProducts: 'Termékek',
  stepCart: 'Kosár',
  stepChat: 'Meglévő chat',
  stepLook: 'Megjelenés és logó',
  stepChecks: 'Működés ellenőrzése',
  stepReady: 'Kész',
  stepDetailOpen: '{domain} megnyitása',
  stepDetailSearch: 'Az áruház termékeinek keresése',
  stepDetailProducts: 'Termékek, árak, fotók, színek és méretek olvasása',
  stepDetailCart: 'Kosár kipróbálása: hozzáadás, visszaolvasás, eltávolítás és ürítés',
  stepDetailChat: 'Meglévő chat keresése',
  stepDetailLook: 'Az áruház logójának és színeinek olvasása',
  stepDetailChecks: 'Vásárlói keresések, termékek, böngészés és kosár kipróbálása',
  stepDetailChecksProducts: 'Termékkeresések, böngészés, fotók, színek és méretek ellenőrzése',
  stepDetailChecksCart:
    'A kosár hozzáadásának, visszaolvasásának, eltávolításának és ürítésének, valamint a termékek olvasásának ellenőrzése',
  stepDetailChecksChat: 'A meglévő chathez való átadás és a termékek olvasásának ellenőrzése',
  stepDetailChecksMarket: 'A piac termékeinek, árainak és kosarának ellenőrzése',
  nowRunning: 'Most: {step}',
  nowProgress: '{n}. lépés a(z) {total} közül · eddig {minutes} perc',
  nowReady: 'Az áruházad készen áll az asszisztenshez',
  nowReadyBody: 'A keresés, a termékek és a kosár működik.',
  nowLastRead: 'Utoljára olvasva: {when}',
  nowUsually: 'általában {min}–{max}',
  stepsRead: 'Olvasva: {when} · Lépések megjelenítése',
  nowFailed: 'Az olvasás leállt',
  factAnswers: 'Ezekkel válaszol a vásárlóknak:',
  costCounting: 'Számolás…',
  factPaid: 'Fizetve innen:',
  paidCompany: 'Céges kreditek',
  creditsAmount: '{amount} kredit',
  creditsOne: '{amount} kredit',
  creditsUnder: '{amount} kredit alatt',
  stopRun: 'Folyamat leállítása',
  stopping: 'Leállítás…',
  closeNote: 'Elhagyhatod ezt az oldalt. A folyamat tovább fut.',
  readyNote: 'Következő: próbáld ki a widgetet, majd telepítsd.',
  buildWidget: 'Widget kipróbálása',
  readAgain: 'Áruház újbóli olvasása',
  foundTitle: 'Amit talált',
  foundProducts: 'Beolvasott termékek és árak',
  foundSearch: 'A keresés működik',
  foundCart: 'A kosárba helyezés működik',
  foundChat: 'A(z) {vendor} chated',
  foundNoChat: 'Nincs saját chat',
  foundLook: 'Színek és logó',
  widgetTitle: 'A widgeted',
  widgetBody:
    'A te színeidben megjelenő asszisztens. Próbáld ki itt: valódi termékeiddel válaszol. Valódi kosárba semmi nem kerül.',
  widgetLoading: 'Előnézet betöltése…',
  widgetFailed: 'Az előnézetet nem sikerült betölteni.',
  widgetNotReady: 'A Widget akkor lesz kész, amikor a Beállítás lap lépései befejeződnek.',
  installLineTitle: 'Add ezt a sort a webhelyedhez',
  installLineBody:
    'Bármely platformon működik: az oldalaid fejlécében, Shopify esetén a theme.liquid fájlban, közvetlenül a </head> elé. Közzétételig semmit nem jelenít meg.',
  installLineBodyLive:
    'Bármely platformon működik: az oldalaid fejlécében, Shopify esetén a theme.liquid fájlban, közvetlenül a </head> elé. Ezután a vásárlók a(z) {version}. verzióval találkoznak.',
  installPolicyAsk: 'Beállít a webhelyed Content Security Policy-t?',
  installPolicy:
    'Ha a webhelyed Content Security Policy-t állít be, engedélyezd a(z) {origin} címet a(z) {directives} részben is, különben az asszisztens nem indul el.',
  installPolicyRefuses:
    'A webhelyed Content Security Policy-je elutasítja az asszisztenst: engedélyezd a(z) {origin} címet a(z) {directives} részben, különben semmi nem indul el.',
  installPolicyNonce:
    'A házirended csak a megbízható szkripteket futtatja, ezért add hozzá a sort az oldalaid nonce értékével.',
  installPolicyEval:
    "Az asszisztensnek az 'unsafe-eval' értékre is szüksége van a script-src részben.",
  copy: 'Másolás',
  copied: 'Kimásolva',
  domainsTitle: 'Webhelyek, ahol válaszol',
  domainsBody: 'Minden más webhelyen csendben marad, még akkor is, ha bemásolják a sort.',
  lineFound: 'A sor megtalálva',
  lineNotSeen: 'Még nincs az oldalon',
  lineUnverified:
    'Az ellenőrzés nem sikerült: az oldal blokkolja az automatikus látogatásokat. Az asszisztens így is működik a vásárlóknak.',
  lineChecked: 'ellenőrizve: {when}',
  lineCheckAgain: 'Ellenőrzés újra',
  lineChecking: 'Oldal ellenőrzése…',
  installCheckBusy: 'Az ellenőrzés most foglalt. Próbáld újra egy pillanat múlva.',
  publishTitle: 'Közzététel',
  publishBody: 'A(z) {domain} vásárlói azonnal látják. Bármikor visszavonhatod a közzétételt.',
  publishButton: '{version}. verzió közzététele',
  publishConfirm: 'Közzéteszed a(z) {name} {version}. verzióját?',
  publishNow: 'Közzététel',
  notNow: 'Most nem',
  publishing: 'Közzététel…',
  liveTitle: 'Közzétéve: {version}. verzió',
  liveSince: 'Ettől: {since}',
  unpublish: 'Közzététel visszavonása',
  unpublishConfirm:
    'Eltávolítod a(z) {version}. verziót a webhelyedről? A vásárlók addig nem látják az asszisztenst, amíg újra közzé nem teszed.',
  publishedLine: 'Most közzétéve: {version}. verzió, ekkortól: {since}',
  usageTitle: 'Használat',
  usageBody: 'A céged kreditjeit használja, akárcsak a Chat többi része.',
  usageDiscovery: 'Áruház olvasása',
  usageChats: 'Vásárlói chatek',
  usageChatsValue: '{count} chat · {cost}',
  last7: 'Utolsó 7 nap',
  askWeek: 'Kérdezd a Chatet erről a hétről',
  kpiConversations: 'Beszélgetések',
  kpiVsPrevious: '{change} az előző héthez képest',
  kpiCart: 'Kosárba téve',
  kpiCheckout: 'Eljutott a pénztárig',
  kpiPerson: 'Munkatársat kért',
  kpiCost: 'Felhasznált kreditek',
  ofConversations: 'A beszélgetések {percent} része',
  checkoutNote: 'A beszélgetések {percent} része',
  personNote: 'A beszélgetések {percent} részében nyitották meg a chatet',
  costNote: 'Körülbelül {amount} beszélgetésenként',
  costNoteUnder: '{amount} alatt beszélgetésenként',
  sparkLabel: 'Napi beszélgetések az utolsó {days} napban',
  sparkDay: '{day}: {count} beszélgetés',
  filterAll: 'Összes',
  filterCart: 'Kosárba téve',
  filterCheckout: 'Pénztár',
  filterPerson: 'Munkatárs',
  outcomeCart: 'Kosárba téve',
  outcomeCheckout: 'Eljutott a pénztárig',
  outcomePerson: 'Munkatársat kért',
  outcomeAnswered: 'Megválaszolva',
  devicePhone: 'Telefon',
  deviceComputer: 'Számítógép',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} perc · Kezdőoldal: {page}',
  anonymous: 'A vásárló névtelen.',
  askConversation: 'Kérdezd a Chatet erről a beszélgetésről',
  showOlderVersions: 'Korábbi verziók megjelenítése',
  showOlder: 'Régebbi beszélgetések megjelenítése',
  filterCountsWeek: 'Darabszámok: az elmúlt 7 nap',
  readAlreadyHere:
    'Ez az áruház már itt van, így nem indult el semmi új, és nem használtunk fel kreditet.',
  readOpenShop: 'Megnyitás',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Mit tanulhatunk ebből a beszélgetésből?',
  askWeekMessage: 'Mit tanulhatunk a heti beszélgetésekből?',
  conversationsChipOne: '{name} · beszélgetés: {when}',
  conversationsChipWeek: '{name} · ezen a héten',
  conversationsChipWeekIn: '{name} · {market} · ezen a héten',
  removeConversations: 'Beszélgetések eltávolítása',
  noConversations: 'Még nincsenek beszélgetések',
  noConversationsBody:
    'Amikor a vásárlók használják az asszisztenst, a beszélgetéseik itt jelennek meg.',
  pickConversation: 'Válassz ki egy beszélgetést az olvasáshoz.',
  eventCheckout: 'A pénztárhoz lépett',
  eventPerson: 'Megnyitotta a chatedet',
  showMore: 'Több megjelenítése',
  urlInvalid: 'Használd az áruházad https-címét, például: https://www.yourstore.com.',
  errorNoCompany:
    'A Storefronthoz céges munkatér szükséges. Kérd meg a rendszergazdádat, hogy adjon hozzá.',
  errorPublishPermission:
    'Ezt csak a cégedben közzétételi jogosultsággal rendelkezők tehetik meg. Kérd meg a rendszergazdádat.',
  actionFailed: 'Ez nem sikerült. Próbáld újra.',
  copyFailed: 'Nem sikerült másolni. Jelöld ki a sort, és másold ki magad.',
  reasonCancelled: 'Leállítottad a folyamatot.',
  reasonFailed:
    'Az olvasás nem tudott befejeződni az áruházadban, ezért az asszisztens még nem kész.',
  reasonFailedLive:
    'Az olvasás nem tudott befejeződni az áruházadban. A közzétett asszisztens változatlanul működik.',
  reasonFailedKept:
    'Az olvasás nem tudott befejeződni az áruházadban, ezért semmi sem változott: a(z) {version}. verzió megmarad.',
  statusReadStopped: 'Kész · az utolsó olvasás leállt',
  reasonStepMarked: 'A leállt lépés alább meg van jelölve.',
  reasonReadAgain: 'Az újbóli olvasás abból indul, amit ez az olvasás már megtanult.',
  reasonNotAStore:
    'Ez a cím nem tűnik webáruháznak: nem voltak kereshető termékek. Ellenőrizd a címet, vagy próbáld ki az áruház fődomainjét.',
  reasonTooSlow:
    'Az áruházad keresése túl lassan válaszol egy vásárlási asszisztenshez: több mint 12 másodpercig tartott. Az áruház gyorsabb keresése vagy egy későbbi újraolvasás segíthet.',
  reasonOurSide:
    'Az olvasás nálunk felmerült hiba miatt állt le, nem az áruházad miatt, ezért az asszisztens még nem kész.',
  reasonOurSideLive:
    'Az olvasás nálunk felmerült hiba miatt állt le, nem az áruházad miatt. A közzétett asszisztens változatlanul működik.',
  reasonOurSideKept:
    'Az olvasás nálunk felmerült hiba miatt állt le, nem az áruházad miatt, ezért semmi sem változott: a(z) {version}. verzió megmarad.',
  reasonTryLater: 'Próbáld újra beolvasni az áruházat egy kis idő múlva.',
  errorNoCredits:
    'Erre most nincs elérhető kredit. A cég tulajdonosa a Usage részben ellenőrizheti a csomagot és a kereteket.',
  errorReadUnavailable: 'Ezt az áruházat most nem lehet beolvasni. Próbáld újra néhány perc múlva.',
  reasonTimeout:
    'Az olvasás elérte az időkorlátot, mielőtt befejeződött volna. Az újbóli olvasás abból folytatja, amit már ellenőrzött.',
  retrying: 'Újrakezdés…',
  readTitleChecks: 'A mentett olvasás újraellenőrzése',
  checksScopeLabel: 'Ellenőrzés tárgya',
  readScopeChecks:
    'Vásárlói keresések, termékek, böngészés és a kosár azon, amit a legutóbbi olvasás elmentett. Az áruházat nem olvassuk újra.',
  readScopeChecksProducts:
    'Termékkeresések, böngészés, fotók, színek és méretek azon, amit a legutóbbi olvasás elmentett. Az áruházat nem olvassuk újra.',
  readScopeChecksCart:
    'A kosárba helyezés, visszaolvasás, eltávolítás és ürítés, valamint a termékek olvasása azon, amit a legutóbbi olvasás elmentett. Az áruházat nem olvassuk újra.',
  readScopeChecksChat:
    'A meglévő chathez való átadás és a termékek olvasása azon, amit a legutóbbi olvasás elmentett. Az áruházat nem olvassuk újra.',
  readScopeChecksMarket:
    'A piac termékei, árai és kosara azon, amit a legutóbbi olvasás elmentett. Az áruházat nem olvassuk újra.',
  checksCost: 'Csak az új ellenőrzések használnak céges krediteket',
  checksAfter:
    'Új piszkozatot ment, ha az ellenőrzések sikeresek. Semmi nem jut el a vásárlókhoz, amíg közzé nem teszed.',
  checksAfterLive:
    'Új piszkozatot ment, ha az ellenőrzések sikeresek. A közzétett asszisztens tovább működik, a vásárlók pedig csak közzététel után látják a változást.',
  checksStarted:
    'Az ellenőrzések elindultak. Amikor elkészül az új piszkozat, kérd meg a Chatet az ellenőrzésére.',
  checkingSavedWork: 'A mentett munka újraellenőrzése',
  checkAgain: 'Újraellenőrzés',
  checkingAgain: 'Újraellenőrzés folyamatban…',
  reasonCheckAgain:
    'Az Újraellenőrzés még egyszer teszteli a mentett munkát, az áruház újraolvasása nélkül. Csak az új ellenőrzések használnak céges krediteket.',
  fixWithChat: 'Javítás a Chattel',
  fixReadPrompt:
    'A(z) {name} ({domain}) olvasása leállt. Nézd meg, mi történt, és segíts működésre bírni: mondd el, mit találsz, javasold, mit mondhatnék az olvasásnak, aztán ellenőrizd újra, vagy olvasd újra ezzel a tippel, az áruháznak a lehető legkisebb részét.',
  fixReadPromptStep:
    'A(z) {name} ({domain}) olvasása a(z) „{step}” lépésnél leállt. Nézd meg, mi történt, és segíts működésre bírni: mondd el, mit találsz, javasold, mit mondhatnék az olvasásnak, aztán ellenőrizd újra, vagy olvasd újra ezzel a tippel, az áruháznak a lehető legkisebb részét.',
  stepStateDone: 'kész',
  stepStateRunning: 'folyamatban',
  stepStateWaiting: 'még nem indult el',
  stepStateFailed: 'leállt',
  newVersionTitle: 'A(z) {version}. verzió elkészült',
  newVersionBody: 'Tedd közzé a(z) {live}. verzió lecseréléséhez.',
  newVersionBodyLive: 'Tedd közzé a(z) {live}. verzió lecseréléséhez a(z) {domain} oldalon.',
  previewSize: 'Előnézet mérete',
  designWithChat: 'Megjelenés megnyitása',
  designReviewBody:
    'Nézd meg a jelenlegi megjelenést, majd mondd el a Chatnek, min szeretnél változtatni.',
  designWhileTitle: 'Tervezd meg, amíg az áruházat olvassuk',
  designWhileBody:
    'A Chat most együtt alakíthatja veled az asszisztens megjelenését, szövegeit és logóját. A dizájn akkor kész, amikor az olvasás befejeződik.',
  designPreviewNote: 'Előnézet példatermékekkel az áruház olvasásáig.',
  proposedDesign: 'Javasolt dizájn',
  proposedDesignNote:
    'A widget ezzel a módosítással, példatermékekkel. Jóváhagyáskor új piszkozatként mentjük; a közzététel a Storefront oldalon marad.',
  lineSources: 'Az áruház oldalairól:',
  readAgainConfirm:
    'Újraolvasod a(z) {name} áruházat? Körülbelül 10–30 percet vesz igénybe, és céges krediteket használ.',
  readAgainLive: 'A közzétett asszisztens tovább működik, amíg közzé nem teszed az új verziót.',
  readAgainNow: 'Újraolvasás',
  switchStore: 'Áruház váltása vagy hozzáadása',
  storesLabel: 'Áruházaid',
  addStore: 'Másik áruház hozzáadása',
  backToStores: 'Vissza az áruházakhoz',
  storeLive: 'Közzétéve',
  storeReady: 'Kész',
  storeReading: 'Olvasás',
  storeStopped: 'Leállt',
  actionDesignTitle: 'A vásárlási asszisztens dizájnjának módosítása',
  actionKnowledgeTitle: 'A vásárlási asszisztens tudásának módosítása',
  readTitleProducts: 'Termékek újbóli olvasása',
  readTitleCart: 'Kosár újbóli ellenőrzése',
  readTitleTheirChat: 'A saját chathez vezető út újbóli ellenőrzése',
  readTitleAll: 'A teljes áruház újbóli olvasása',
  readTitleStore: 'Új áruház olvasása',
  readTitleMarket: 'Piac hozzáadása',
  readShopLabel: 'Áruház',
  readScopeLabel: 'Olvasás tárgya',
  readTimeLabel: 'Időtartam',
  readCostLabel: 'Költség',
  readAfterLabel: 'Utána',
  readScopeProducts: 'Termékkeresés, rendezés, termékfotók, színek és méretek',
  readScopeCart: 'Kosárba helyezés és eltávolítás',
  readScopeTheirChat: 'Ahogy a vásárlók elérik a saját chatet',
  readScopeAll: 'Termékek, kosár, saját chat és piacok',
  readScopeStore: 'Termékek, árak és méretek, kosár, saját chat, megjelenés és piacok',
  readScopeMarket: '{market}: termékei, árai és kosara',
  readTime: 'Általában {min}–{max} perc',
  readCost: 'Céges krediteket használ',
  readCostLast: 'Céges krediteket használ · az áruház utolsó olvasása {credits} kreditet használt',
  readCostLastPartial:
    'Céges krediteket használ · az áruház utolsó, korán leállt olvasása legalább {credits} kreditet használt',
  readAfter: 'Új piszkozatot ment. Semmi nem jut el a vásárlókhoz, amíg közzé nem teszed.',
  readAfterLive:
    'Új piszkozatot ment. A közzétett asszisztens tovább működik, a vásárlók pedig csak közzététel után látják a változást.',
  readAfterNew: 'Első piszkozatot ment neked kipróbálásra. Semmi nincs közzétéve.',
  readCartNote:
    'A kosár ellenőrzéséhez hozzáad egy tesztterméket, majd eltávolítja. Soha nem hajt végre vásárlást.',
  readFocusLabel: 'Amit a Chat kért az olvasástól, hogy javítsa',
  readFooter:
    'Semmi nem indul el, amíg jóvá nem hagyod. Az olvasást a Beállítás lapon leállíthatod.',
  readStartedAt:
    'Elindítva: {time} · általában {min}–{max} perc. Amikor elkészül az új piszkozat, kérd meg a Chatet az ellenőrzésére.',
  readFocusAll: 'Összes megjelenítése',
  readFocusLess: 'Kevesebb megjelenítése',
  readStart: 'Olvasás indítása',
  addToSite: 'Hozzáadás a webhelyedhez',
  readTab: 'Kérés',
  readStarted:
    'Az olvasás elindult. Amikor elkészül az új piszkozat, kérd meg a Chatet az ellenőrzésére.',
  readFollow: 'Követés a Beállítás lapon',
  proposedDesignLive:
    'Az áruházad valódi előnézete ebben a dizájnban. Kérdezz tőle valamit: valódi kosárba semmi nem kerül. Jóváhagyáskor új piszkozatként mentjük; a közzététel a Storefront oldalon marad.',
  designChanges: 'Milyen változások történtek',
  designReset: 'vissza az alapértelmezéshez',
  designGroupColors: 'Színek',
  designGroupType: 'Betűtípusok',
  designGroupShape: 'Alak',
  designGroupLauncher: 'Oldalon lévő gomb',
  designGroupPanel: 'Fejléc',
  designGroupProducts: 'Termékek',
  designGroupCart: 'Az üzlet kosara',
  designGroupCopy: 'Szövegek',
  designGroupLogo: 'Logó',
  designGroupCss: 'Egyéni CSS',
  designColorBrand: 'Márka',
  designColorActions: 'Hozzáadás és küldés',
  designColorPanel: 'Panel',
  designColorSurface: 'Felületek',
  designColorInk: 'Szöveg',
  designColorMuted: 'Másodlagos szöveg',
  designColorLine: 'Vonalak',
  designColorBubble: 'Vásárlói üzenetek',
  designColorBubbleInk: 'Vásárlói szöveg',
  designActionsBrand: 'Márkaszín',
  designActionsInk: 'Szövegszín',
  designWordsName: 'Név',
  designWordsGreeting: 'Üdvözlés',
  designWordsStarters: 'Kezdőkérdések',
  designWordsLauncher: 'Gombfelirat',
  designWordsPlaceholder: 'Helyőrző',
  designWordsAi: 'MI-címke',
  designWordsPerson: 'Kapcsolat egy munkatárssal',
  noteNew: 'Új',
  noteChanged: 'Módosítva',
  noteRemoved: 'Eltávolítva',
  noteSourceKnowledge: 'Tudásoldal: {title}',
  noteSourceFile: 'Fájl: {name}',
  noteSourceChat: 'A chatből',
  knowledgeApprovalNote:
    'A vásárlók mindent elolvashatnak ezekben a jegyzetekben. Jóváhagyáskor új piszkozatot mentünk: kérdezd az asszisztenst a Widget lapon, majd tedd közzé a Telepítés lapon.',
  profileTitle: 'A boltról',
  profileChecked: 'Ellenőrizve: {date}',
  profileSources: 'Ezekből készült',
  profileSample: '{count} termék mintája az árszinthez',
  profileSampleOne: '{count} termék mintája az árszinthez',
  profileAssistant: 'Mit tud itt az asszisztens',
  profileTeam: 'A csapatod írta',
  profileEmpty:
    'Még nincs megírva. Kérd meg a Chatet, hogy futtasson tudásfelmérést: a saját oldalaitokból és abból írja meg, amit az oldalatokon látott, és saját sorokat is hozzáadhattok.',
  profileWithChat: 'Hozzáadás vagy módosítás a Chattel',
  profilePrompt:
    'Segíts javítani, mit tud a(z) {name} ({domain}) vásárlási asszisztense a boltunkról egészében: mit árulunk, miről vagyunk ismertek, kinek szól, és mit tehetnek itt a vásárlók. Először olvasd el „A boltról“ profilt, és mondd el, mi hiányzik vagy téves. Amit a boltról mondok, és egyetlen oldal sem állít, az a csapatunk sora lesz.',
  profilePartNavigation: 'Részlegek',
  profilePartWelcome: 'Üdvözlés',
  profilePartMarkets: 'Piacok',
  profilePartFunctions: 'Mit tud az asszisztens',
  profilePartProducts: 'Termékek',
  profilePartPages: 'Szolgáltatási oldalak',
  profileCleared: 'Törli a megírt profilt',
  knowledgeTitle: 'Amit tud',
  knowledgeBody:
    'A termékeken, árakon és készleten kívül, amelyeket mindig élőben olvas az áruházadból.',
  knowledgeEmpty:
    'Egyelőre csak a katalógusod. A Chattel, saját oldalaidról vagy a Tudásból adj hozzá szállítási, visszaküldési vagy fizetési információkat.',
  knowledgeWithChat: 'Hozzáadás a Chattel',
  knowledgeChangeWithChat: 'Módosítás a Chattel',
  knowledgePrompt:
    'Segíts kiválasztani, mit mondjon a(z) {name} vásárlási asszisztense ({domain}) a vásárlóknak a termékeinken kívül, például a szállításról, visszaküldésről és fizetésről. Keresd meg a saját oldalainkon vagy a Tudásban, és javasolj jóváhagyható jegyzeteket.',
  pagesTitle: 'Oldalak, amelyeket olvas',
  pagesBody:
    'Azok az oldalak, ahonnan a jegyzetek származnak. A Poneva naponta ellenőrzi őket, a Chat pedig szól, ha valamelyik megváltozik.',
  pagesEmpty:
    'Még nincsenek oldalak. Itt jelennek meg a bolt olvasása által talált oldalak, valamint a Chatben megnevezett oldalak.',
  pageFound: 'a Poneva találta',
  pageLinkedFrom: 'innen hivatkozva: {page}',
  pageAddedByYou: 'kézzel hozzáadva',
  pageFromNote: 'jegyzetben szerepel',
  pageNoteOne: '1 jegyzet',
  pageNotesMany: '{count} jegyzet',
  pageStatusOk: 'Naprakész',
  pageStatusChanged: 'Megváltozott',
  pageStatusUnreadable: 'Nem olvasható',
  pageStatusUnchecked: 'Még nincs ellenőrizve',
  pagesChangeWithChat: 'Módosítás a Chattel',
  pagesUpdateWithChat: 'Jegyzetek frissítése a Chattel',
  pagesPrompt:
    'Segíts megváltoztatni, hogy a(z) {name} ({domain}) vásárlóasszisztense a jegyzeteihez mely oldalakat olvassa. Mutasd meg, melyeket használ most, és kérdezd meg, melyeket adjam hozzá vagy távolítsam el.',
  pagesUpdatePrompt:
    'A(z) {name} ({domain}) néhány oldala megváltozott, vagy a jegyzetek készítése óta már nem olvasható. Mutasd meg, melyek ezek, és készítsd el belőlük újra a jegyzeteket vázlatként, amelyet át tudok nézni.',
  viewCompare: 'Összehasonlítás',
  lookDeviceLabel: 'Összehasonlítás eszközön',
  lookShop: 'Az áruházad',
  lookShopCaption: 'Kezdőoldal, olvasva: {when}',
  lookWidget: 'Az asszisztens',
  lookWidgetCaption: '{version}. verzió',
  lookLoading: 'Az asszisztens elhelyezése az áruházad mellett…',
  lookFailed: 'Az összehasonlítást most nem sikerült elkészíteni.',
  lookNoShopPicture: 'Az olvasás nem őrzött meg képet az áruházadról ezen a szélességen.',
  lookNoneTitle: 'Az áruházad saját megjelenése nem lett mentve',
  lookNoneBody:
    'Ez az olvasás nem rögzítette. Olvasd újra az áruházat a Beállításban, és a logója, színei és oldalai el lesznek mentve az összehasonlításhoz.',
  lookColoursShop: 'Az áruházad által használt színek',
  lookColoursAssistant: 'Az asszisztens színei',
  lookRoleHeader: 'Fejléc',
  lookRoleBrand: 'Márka',
  lookSame: 'Megegyezik az áruházad {colour} színével ({source})',
  lookOff: 'Közel van az áruházad {colour} színéhez ({source}), de nem azonos, eltérés: {delta}',
  lookOwn: 'Saját szín: az áruházad nem használja',
  lookNeutral: 'Az áruházad nem lett betöltve ehhez: csak az látszik, amit az olvasás megőrzött.',
  lookFromLogo: 'logó',
  lookFromTheme: 'témaszín',
  lookFromTile: 'csempeszín',
  lookFromMask: 'kitűzött ikon',
  lookFromManifest: 'alkalmazásjegyzék',
  lookFromHeader: 'fejléc',
  lookFromButton: 'gombok',
  lookFromVariable: 'stílusszín',
  lookFix: 'Javítás a Chattel',
  lookFixPrompt:
    'Az asszisztens színe ({role}): {colour}, közel van az áruházad {exact} színéhez ({source}), de nem azonos. Kérlek, használd az áruházad pontos színét: {exact}.',
  lookSwatch: '{colour} szín',
  errorDesignChecks: 'Ez a verzió nem ment át a dizájnellenőrzéseken.',
  designSamplesBlocked:
    'A kártyaelrendezések itt nem igazolhatók: az oldalad blokkolja az automatikus látogatásokat. A színek, a betűtípusok és a logó így is működnek.',
  designCheckLabel: 'Egy MI-címke túl halvány az olvasáshoz.',
  designCheckReach: 'Egy gomb elérhetetlen.',
  designCheckSize: 'A szöveg egy része túl kicsi.',
  designCheckOverflow: 'A panel oldalra görget.',
  designCheckRender: 'Az ellenőrzéseket nem sikerült lefuttatni. Próbálja újra egy pillanat múlva.',
  fixDesignPrompt:
    'Megpróbáltam közzétenni a(z) {name} ({domain}) legújabb vázlatát, de nem ment át a dizájnellenőrzéseken: {facts}. Javítsa ki a dizájnt ebben a csevegésben, és ellenőrizze telefonon és számítógépen. Ne tegye közzé.',
};

const ga: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Cúntóir siopadóireachta',
  designUnconfirmed:
    'Stopadh é sula bhféadfadh Chat a dheimhniú gur sábháladh an dearadh. Taispeánann canbhás an chúntóra an leagan is nuaí.',
  errorNewerVersion:
    'Sábháladh leagan níos nuaí ó d’oscail tú é. Féach air ar dtús, ansin foilsigh é.',
  canvasSelectHint: 'Cliceáil ar chuid den chúntóir chun é a athrú sa chomhrá · Esc chun stopadh',
  canvasDraft: 'Leagan {version} · níl sé foilsithe fós',
  canvasDraftOverLive: 'Leagan {version} · tá leagan {live} foilsithe',
  canvasLive: 'Leagan {version} · foilsithe',
  canvasNoVersion: 'Tiocfaidh an chéad leagan nuair a bheidh léamh an tsiopa críochnaithe',
  canvasPublish: 'Foilsigh ar leathanach Storefront',
  canvasFrameTitle: 'Cúntóir siopadóireachta {name}',
  viewLabel: 'Amharc',
  photosGood: '{count} ghrianghraf táirge: iomlán agus géar',
  photosGoodOne: 'Tá grianghraf an táirge iomlán agus géar',
  canvasMissingTitle: 'Níl an cúntóir siopadóireachta seo sa spás oibre seo',
  canvasMissingBody: 'Baineann sé le spás oibre eile, nó baineadh é.',
  canvasMissingLink: 'Féach ar chúntóirí an spáis oibre seo',
  photosLabel: 'Grianghraif táirgí',
  photosSmall: '{count} beag ina tíleanna',
  photosCut: '{count} gearrtha amach',
  photosSoft: '{count} méadaithe agus doiléir',
  photosMissing: '{count} gan ghrianghraf',
  photosFix: 'Deisigh le Chat',
  photosPrompt:
    'Déan cuma cheart ar ghrianghraif na dtáirgí sna cártaí: gach ceann iomlán, mór go leor ina thíl agus géar.',
  photosFacts:
    '{total} ghrianghraf: {small} beag ina tíleanna, {cut} gearrtha, {soft} méadaithe, {missing} gan ghrianghraf. Méideanna: {sizes}',
  viewDesign: 'Dearadh',
  viewVersions: 'Leaganacha',
  versionsLoading: 'Leaganacha á lódáil…',
  versionsFailed: 'Níorbh fhéidir na leaganacha a lódáil.',
  versionTitle: 'Leagan {version}',
  versionLive: 'Foilsithe',
  versionChecked: 'Seiceáilte',
  versionToday: 'Inniu',
  versionYesterday: 'Inné',
  versionEarlier: 'Níos luaithe',
  versionByPerson: 'Iarrtha ag {name}',
  versionByChat: 'Iarrtha sa chomhrá',
  versionFromReading: 'Ó léamh {domain}',
  versionPublishedBy: 'foilsithe ag {name}, {at}',
  versionPublish: 'Foilsigh',
  versionRestore: 'Athchóirigh…',
  versionRestoreTitle: 'Athchóirigh leagan {version}?',
  versionRestoreBody:
    'Beidh sé ina leagan {next}, dréacht nua. Ní athróidh do shuíomh go dtí go bhfoilseoidh tú é.',
  versionRestoreConfirm: 'Athchóirigh',
  versionRestoreCancel: 'Cealaigh',
  versionRestored: 'Athchóiríodh mar leagan {version}',
  versionRestoreFailed: 'Níorbh fhéidir an leagan a athchóiriú.',
  compareTitle: 'Cuir leagan {to} i gcomparáid le leagan {from}',
  compareTitleLive: 'Cuir leagan {to} i gcomparáid leis an leagan foilsithe {from}',
  compareHint: 'Tarraing chun comparáid a dhéanamh',
  compareLabel: 'Cuir leagan {from} agus leagan {to} i gcomparáid',
  compareTagLive: 'v{version} · Foilsithe',
  compareTag: 'v{version}',
  changesTitle: 'Cad a d’athraigh ó leagan {from} go {to}',
  changesNone: 'Tá na dearaí mar an gcéanna. Níor athraigh ach na nótaí nó sonraí an tsiopa.',
  changeAdded: 'Curtha leis',
  changeRemoved: 'Bainte',
  changeChanged: 'Athraithe',
  changeDrawn: 'lógó tarraingthe',
  changeCss: 'a CSS féin',
  changeYes: 'tá',
  changeNo: 'níl',
  'field.colors.brand': 'Dath an bhranda',
  'field.colors.actions': 'Dath na gcnaipí',
  'field.colors.panel': 'Dath an phainéil',
  'field.colors.surface': 'Dath an dromchla',
  'field.colors.ink': 'Dath an téacs',
  'field.colors.muted': 'Dath an téacs thánaistigh',
  'field.colors.line': 'Dath na líne',
  'field.colors.bubble': 'Dath theachtaireacht an tsiopadóra',
  'field.colors.bubbleInk': 'Téacs theachtaireacht an tsiopadóra',
  'field.colors.header': 'Dath an cheanntáisc',
  'field.colors.headerInk': 'Dath théacs an cheanntáisc',
  'field.colors.tints': 'Scáileanna boga',
  'field.copy.assistantName': 'Ainm',
  'field.copy.greeting': 'Beannú',
  'field.copy.placeholder': 'Bosca ceiste',
  'field.copy.starters': 'Túscheisteanna',
  'field.copy.launcherLabel': 'Focail an lainseálaí',
  'field.copy.aiLabel': 'Lipéad AI',
  'field.copy.personLabel': 'Nasc chuig duine',
  'field.type.body': 'Cló an téacs',
  'field.type.display': 'Cló na gceannteideal',
  'field.type.fontCss': 'Foinse an chló',
  'field.type.scale': 'Méid an téacs',
  'field.shape.radius': 'Coirnéil',
  'field.shape.shadow': 'Scáth',
  'field.shape.density': 'Spásáil',
  'field.launcher.style': 'Lainseálaí ar ríomhairí',
  'field.launcher.phoneStyle': 'Lainseálaí ar fhóin',
  'field.launcher.side': 'Taobh an lainseálaí',
  'field.launcher.icon': 'Deilbhín an lainseálaí',
  'field.panel.header': 'Méid an cheanntáisc',
  'field.panel.opening': 'An chéad scáileán',
  'field.products.layout': 'Táirgí',
  'field.products.imageFit': 'Grianghraif táirgí',
  'field.products.imageRatio': 'Cruth thíl an ghrianghraif',
  'field.products.imagePadding': 'Spás timpeall na ngrianghraf',
  'field.products.imageBackground': 'Dath thíl an ghrianghraif',
  'field.products.picker': 'Rogha méide',
  'field.products.showStock': 'Stoc le feiceáil',
  'field.cart.landing': 'Beochan na cairte',
  'field.logo.svg': 'Lógó',
  'field.logo.shape': 'Cruth an lógó',
  'field.logo.plate': 'Cúlra an lógó',
  'field.css': 'CSS saincheaptha',
  'value.ink': 'dúch',
  'value.brand': 'dath branda',
  'value.small': 'beag',
  'value.medium': 'meánach',
  'value.large': 'mór',
  'value.square': 'cearnógach',
  'value.soft': 'bog',
  'value.round': 'cruinn',
  'value.flat': 'réidh',
  'value.deep': 'domhain',
  'value.compact': 'dlúth',
  'value.comfortable': 'compordach',
  'value.pill': 'pill le focail',
  'value.circle': 'ciorclach',
  'value.right': 'ar dheis',
  'value.left': 'ar chlé',
  'value.logo': 'lógó',
  'value.chat': 'bolgán comhrá',
  'value.sparkle': 'spréach',
  'value.bag': 'mála',
  'value.full': 'lán',
  'value.middle': 'sa lár',
  'value.bottom': 'ag an mbun',
  'value.rows': 'sraitheanna',
  'value.grid': 'eangach',
  'value.carousel': 'sraith a shleamhnaíonn tú',
  'value.contain': 'grianghraf iomlán',
  'value.cover': 'tíl líonta',
  'value.portrait': 'portráid',
  'value.landscape': 'tírdhreach',
  'value.none': 'gan cheann',
  'value.pop': '+1 a éiríonn',
  'value.ring': 'cuilithín',
  'value.blink': 'caochadh',
  'value.chips': 'cnaipí i sraith',
  'value.menu': 'liosta',
  'value.rounded': 'cruinnithe',
  designCardTitle: 'Cúntóir siopadóireachta {name}',
  designCardSaved: 'Sábháladh leagan {version} · níl sé foilsithe fós',
  designCardSavedFirst: 'Sábháladh an dearadh · beidh sé sa chéad leagan',
  designSaving: 'Dearadh á shábháil…',
  selectedAssistantPart: 'Roghnaithe: cuid den chúntóir',
  loading: 'Storefront á lódáil…',
  disabledTitle: 'Níl Storefront casta air sa spás oibre seo.',
  disabledBody:
    'Tá sé i rochtain luath agus á thabhairt isteach de réir a chéile. Fiafraigh de do theagmhálaí Poneva cathain a bheidh sé agaibh.',
  loadFailed: 'Níorbh fhéidir Storefront a lódáil.',
  startTitle: 'Cúntóir siopadóireachta do do stór',
  startBody:
    'Léann Poneva do stór mar a úsáideann siopadóir é: cuardach, táirgí, an tralaí agus an comhrá atá agat cheana. Tógann sé thart ar 10 go 30 nóiméad, úsáideann sé creidmheasanna do chuideachta, agus is féidir leat leanúint ar aghaidh ag obair.',
  urlLabel: 'Seoladh do shiopa',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Léigh mo stór',
  starting: 'Ag tosú…',
  startFailed: 'Níorbh fhéidir an stór sin a léamh. Seiceáil an seoladh agus bain triail eile as.',
  tabConversations: 'Comhráite',
  tabOverview: 'Forléargas',
  allAssistants: 'Gach cúntóir',
  allAssistantsNote: '{assistants} cúntóir · {sites} suíomh',
  allAssistantsMenuNote: '{count} comhrá an tseachtain seo',
  marketLabel: 'Margadh',
  allMarkets: 'Gach margadh',
  allMarketsNote: '{count} suíomh',
  scopeIn: '{assistant} i {market}',
  askAbout: 'Cuir ceist ar Chat faoi {scope}',
  overviewAssistantsTitle: 'Cúntóirí agus margaí',
  overviewMarketsTitle: 'Margaí',
  overviewAssistantsOnly: 'Cúntóirí',
  overviewIdle: '{count} cúntóir gan chomhrá fós',
  allAssistantsCount: '{assistants} cúntóir',
  overviewFailed: 'Níorbh fhéidir an forléargas a lódáil.',
  colConversations: 'Comhráite',
  colCart: 'Curtha sa tralaí',
  colCheckout: 'Íocaíocht',
  colPerson: 'Duine',
  colCost: 'Creidmheasanna',
  rowMarkets: '{count} margadh',
  rowVersion: 'leagan {version}',
  rowAria:
    '{name}, {sub}: {conversations} comhrá, {cart} curtha sa tralaí, {checkout} a shroich an íocaíocht, {person} iarratas ar dhuine, {cost} creidmheas',
  versionPublishedAt: 'foilsithe {at}',
  designColorHeader: 'Ceanntásc',
  designColorHeaderInk: 'Téacs an cheanntáisc',
  designGroupTranslations: 'Focail i dteangacha eile',
  topicsTitle: 'Cad a iarrann siopadóirí',
  topicsNote: 'Ábhair a fuair Chat sna chéad cheisteanna',
  startPagesTitle: 'Cá dtosaíonn comhráite',
  startPagesOne: 'Thosaigh gach comhrá ar chineál amháin leathanaigh: {page}.',
  'startPage.product': 'Leathanaigh táirge',
  'startPage.search': 'Torthaí cuardaigh',
  'startPage.start': 'Leathanach tosaigh',
  'startPage.cart': 'Tralaí',
  'startPage.other': 'Leathanaigh eile',
  addMarketInChat: 'Cuir margadh leis sa chomhrá',
  marketsTitle: 'Margaí',
  marketsBody:
    'Cúntóir amháin agus dearadh amháin. Tá a theanga, airgeadra, táirgí, tralaí agus comhrá le duine féin ag gach margadh.',
  marketChecked: 'seiceáilte {when}',
  'marketStatus.ready': 'Réidh',
  'marketStatus.found': 'Gan cur leis',
  'marketStatus.checking': 'Á sheiceáil',
  'marketStatus.failed': 'Teastaíonn deisiú',
  addMarketPrompt:
    'Cuir margadh eile leis an gcúntóir siopadóireachta do {name} ({domain}): aimsigh tíortha, teangacha nó suíomhanna eile an tsiopa agus fiafraigh díom cé acu ba cheart a chur leis.',
  tabWidget: 'Widget',
  tabInstall: 'Suiteáil',
  tabSetup: 'Socrú',
  statusReading: 'Do stór á léamh',
  readingProducts: 'Na táirgí á léamh arís',
  readingCart: 'An tralaí á sheiceáil arís',
  readingChat: 'An bealach chuig do chomhrá féin á sheiceáil',
  readingMarket: 'Margadh {market} á chur leis',
  readingMarketAny: 'Margadh á chur leis',
  statusReady: 'Réidh le suiteáil',
  statusFailed: 'Stopadh an léamh',
  statusLive: 'Beo · leagan {version} ar {domain}',
  statusPublished: 'Foilsithe · leagan {version} · níl sé ar do shuíomh fós',
  statusPublishedUnverified: 'Foilsithe · leagan {version} · níorbh fhéidir do shuíomh a sheiceáil',
  stepOpen: 'An suíomh á oscailt',
  stepSearch: 'Cuardach',
  stepProducts: 'Táirgí',
  stepCart: 'Tralaí',
  stepChat: 'Do chomhrá atá ann cheana',
  stepLook: 'Do chuma agus do lógó',
  stepChecks: 'Ag seiceáil go n-oibríonn sé',
  stepReady: 'Réidh',
  stepDetailOpen: '{domain} á oscailt',
  stepDetailSearch: 'Táirgí an tsiopa á aimsiú',
  stepDetailProducts: 'Táirgí, praghsanna, grianghraif, dathanna agus méideanna á léamh',
  stepDetailCart: 'An tralaí á thriail: cuir leis, léigh siar, bain agus folmhaigh',
  stepDetailChat: 'Do chomhrá atá ann cheana á lorg',
  stepDetailLook: 'Lógó agus dathanna an tsiopa á léamh',
  stepDetailChecks: 'Cuardaigh siopadóra, táirgí, brabhsáil agus an tralaí á dtriail',
  stepDetailChecksProducts:
    'Cuardaigh táirgí, brabhsáil, grianghraif, dathanna agus méideanna á seiceáil',
  stepDetailChecksCart:
    'Cuir leis an tralaí, léamh siar, baint agus folmhú, agus léamh táirgí á seiceáil',
  stepDetailChecksChat: 'Aistriú chuig do chomhrá atá ann cheana agus léamh táirgí á sheiceáil',
  stepDetailChecksMarket: 'Táirgí, praghsanna agus tralaí an mhargaidh seo á seiceáil',
  nowRunning: 'Anois: {step}',
  nowProgress: 'Céim {n} as {total} · {minutes} nóiméad go dtí seo',
  nowReady: 'Tá do stór réidh do chúntóir',
  nowReadyBody: 'Oibríonn cuardach, táirgí agus an tralaí.',
  nowLastRead: 'Léite an uair dheireanach {when}',
  nowUsually: 'de ghnáth {min}–{max}',
  stepsRead: 'Léite {when} · Taispeáin na céimeanna',
  nowFailed: 'Stopadh an léamh',
  factAnswers: 'Freagraíonn sé do shiopadóirí le',
  costCounting: 'Á chomhaireamh…',
  factPaid: 'Íoctha as',
  paidCompany: 'Creidmheasanna na cuideachta',
  creditsAmount: '{amount} creidmheas',
  creditsOne: '{amount} creidmheas',
  creditsUnder: 'faoi bhun {amount} creidmheas',
  stopRun: 'Stop an rith',
  stopping: 'Á stopadh…',
  closeNote: 'Is féidir leat an leathanach seo a fhágáil. Leanfaidh an rith ar aghaidh.',
  readyNote: 'Ar aghaidh: bain triail as do ghiúirléid, ansin suiteáil é.',
  buildWidget: 'Bain triail as an ngiúirléid',
  readAgain: 'Léigh an stór arís',
  foundTitle: 'An méid a fuair sé',
  foundProducts: 'Táirgí agus praghsanna léite',
  foundSearch: 'Oibríonn an cuardach',
  foundCart: 'Oibríonn cur leis an tralaí',
  foundChat: 'Do chomhrá {vendor}',
  foundNoChat: 'Níl comhrá féin agat',
  foundLook: 'Do dhathanna agus do lógó',
  widgetTitle: 'Do ghiúirléid',
  widgetBody:
    'Do chúntóir, i do dhathanna. Bain triail as anseo: freagraíonn sé le do tháirgí féin. Ní chuirtear aon rud i bhfíor-thralaí.',
  widgetLoading: 'Réamhamharc á lódáil…',
  widgetFailed: 'Níor lódáladh an réamhamharc.',
  widgetNotReady:
    'Beidh an ghiúirléid réidh nuair a bheidh na céimeanna sa chluaisín Socrú críochnaithe.',
  installLineTitle: 'Cuir an líne seo le do shuíomh',
  installLineBody:
    'Oibríonn sé ar ardán ar bith: i gceann do leathanaigh, ar Shopify in theme.liquid díreach roimh </head>. Ní thaispeánann sé tada go dtí go bhfoilsíonn tú é.',
  installLineBodyLive:
    'Oibríonn sé ar ardán ar bith: i gceann do leathanaigh, ar Shopify in theme.liquid díreach roimh </head>. Feicfidh siopadóirí leagan {version} ansin.',
  installPolicyAsk: 'An socraíonn do shuíomh Beartas Slándála Ábhair?',
  installPolicy:
    'Má shocraíonn do shuíomh Beartas Slándála Ábhair, ceadaigh {origin} in {directives} freisin, nó ní féidir leis an gcúntóir tosú.',
  installPolicyRefuses:
    'Diúltaíonn Beartas Slándála Ábhair do shuímh don chúntóir: ceadaigh {origin} in {directives}, nó ní thosóidh tada.',
  installPolicyNonce:
    'Ní ritheann do bheartas ach scripteanna a bhfuil muinín aige astu, mar sin cuir an líne leis an nonce ó do leathanaigh.',
  installPolicyEval: "Teastaíonn 'unsafe-eval' ón gcúntóir in script-src freisin.",
  copy: 'Cóipeáil',
  copied: 'Cóipeáilte',
  domainsTitle: 'Suíomhanna a bhfreagraíonn sé orthu',
  domainsBody: 'Fanann sé ciúin ar aon suíomh eile, fiú ceann a chóipeálann an líne.',
  lineFound: 'Aimsíodh an líne',
  lineNotSeen: 'Níl ar an leathanach fós',
  lineUnverified:
    'Níorbh fhéidir seiceáil: cuireann an suíomh bac ar chuairteanna uathoibrithe. Oibríonn an cúntóir fós do chustaiméirí.',
  lineChecked: 'seiceáilte {when}',
  lineCheckAgain: 'Seiceáil arís',
  lineChecking: 'An leathanach á sheiceáil…',
  installCheckBusy: 'Tá an seiceáil gnóthach faoi láthair. Bain triail eile as i gceann tamaill.',
  publishTitle: 'Foilsigh',
  publishBody:
    'Feicfidh siopadóirí ar {domain} é láithreach. Is féidir leat é a dhífhoilsiú am ar bith.',
  publishButton: 'Foilsigh leagan {version}',
  publishConfirm: 'Foilsigh leagan {version} de {name}?',
  publishNow: 'Foilsigh',
  notNow: 'Ní anois',
  publishing: 'Á fhoilsiú…',
  liveTitle: 'Foilsithe: leagan {version}',
  liveSince: 'Ó {since}',
  unpublish: 'Dífhoilsigh',
  unpublishConfirm:
    'Bain leagan {version} de do shuíomh? Ní fheicfidh siopadóirí an cúntóir go dtí go bhfoilseoidh tú arís.',
  publishedLine: 'Foilsithe anois: leagan {version}, ó {since}',
  usageTitle: 'Úsáid',
  usageBody: 'Úsáideann sé creidmheasanna do chuideachta, cosúil leis an gcuid eile de Chat.',
  usageDiscovery: 'An stór á léamh',
  usageChats: 'Comhráite siopadóirí',
  usageChatsValue: '{count} comhrá · {cost}',
  last7: 'Na 7 lá dheireanacha',
  askWeek: 'Cuir ceist ar Chat faoin tseachtain seo',
  kpiConversations: 'Comhráite',
  kpiVsPrevious: '{change} i gcomparáid leis an tseachtain roimhe',
  kpiCart: 'Curtha sa tralaí',
  kpiCheckout: 'Shroich siad an íocaíocht',
  kpiPerson: 'Iarradh duine',
  kpiCost: 'Creidmheasanna úsáidte',
  ofConversations: '{percent} de na comhráite',
  checkoutNote: '{percent} de na comhráite',
  personNote: 'D’oscail {percent} do chomhrá',
  costNote: 'Thart ar {amount} in aghaidh an chomhrá',
  costNoteUnder: 'Faoi bhun {amount} in aghaidh an chomhrá',
  sparkLabel: 'Comhráite in aghaidh an lae, na {days} lá dheireanacha',
  sparkDay: '{day}: {count} comhrá',
  filterAll: 'Gach rud',
  filterCart: 'Curtha sa tralaí',
  filterCheckout: 'Íocaíocht',
  filterPerson: 'Duine',
  outcomeCart: 'Curtha sa tralaí',
  outcomeCheckout: 'Shroich siad an íocaíocht',
  outcomePerson: 'Iarradh duine',
  outcomeAnswered: 'Freagra tugtha',
  devicePhone: 'Fón',
  deviceComputer: 'Ríomhaire',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} nóiméad · Tosaíodh ar {page}',
  anonymous: 'Tá an siopadóir gan ainm.',
  askConversation: 'Cuir ceist ar Chat faoin gcomhrá seo',
  showOlderVersions: 'Taispeáin leaganacha níos luaithe',
  showOlder: 'Taispeáin comhráite níos sine',
  filterCountsWeek: 'Líon: na 7 lá seo caite',
  readAlreadyHere:
    'Tá an siopa seo anseo cheana, mar sin níor tosaíodh aon rud nua agus níor úsáideadh aon chreidmheasanna.',
  readOpenShop: 'Oscail é',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Cad is féidir linn a fhoghlaim ón gcomhrá seo?',
  askWeekMessage: 'Cad is féidir linn a fhoghlaim ó chomhráite na seachtaine seo?',
  conversationsChipOne: '{name} · comhrá ó {when}',
  conversationsChipWeek: '{name} · an tseachtain seo',
  conversationsChipWeekIn: '{name} · {market} · an tseachtain seo',
  removeConversations: 'Bain na comhráite',
  noConversations: 'Níl comhráite fós',
  noConversationsBody: 'Nuair a úsáideann siopadóirí an cúntóir, taispeánfar a gcomhráite anseo.',
  pickConversation: 'Roghnaigh comhrá le léamh.',
  eventCheckout: 'Chuaigh chuig an leathanach íocaíochta',
  eventPerson: 'D’oscail do chomhrá',
  showMore: 'Taispeáin tuilleadh',
  urlInvalid: 'Úsáid seoladh https do shiopa, mar https://www.yourstore.com.',
  errorNoCompany:
    'Teastaíonn spás oibre cuideachta ó Storefront. Iarr ar do riarthóir thú a chur leis.',
  errorPublishPermission:
    'Ní féidir ach le daoine atá in ann foilsiú do do chuideachta é seo a dhéanamh. Iarr ar do riarthóir.',
  actionFailed: 'Níor oibrigh sé sin. Bain triail eile as.',
  copyFailed: 'Níorbh fhéidir é a chóipeáil. Roghnaigh an líne agus cóipeáil í tú féin.',
  reasonCancelled: 'Stop tú an rith.',
  reasonFailed: 'Níorbh fhéidir an léamh a chríochnú ar do shiopa, mar sin níl an cúntóir réidh.',
  reasonFailedLive:
    'Níorbh fhéidir an léamh a chríochnú ar do shiopa. Leanann an cúntóir foilsithe ag obair mar atá.',
  reasonFailedKept:
    'Níorbh fhéidir an léamh a chríochnú ar do shiopa, mar sin níor athraigh tada: fanann leagan {version} mar a bhí.',
  statusReadStopped: 'Réidh · stopadh an léamh deireanach',
  reasonStepMarked: 'Tá an chéim ar stopadh í marcáilte thíos.',
  reasonReadAgain: 'Tosaíonn léamh eile ón méid a d’fhoghlaim an léamh seo.',
  reasonNotAStore:
    'Ní cosúil gur siopa gréasáin é an seoladh seo: ní raibh táirgí le cuardach. Seiceáil an seoladh, nó bain triail as príomhdhómain an tsiopa.',
  reasonTooSlow:
    'Freagraíonn cuardach do shiopa ró-mhall do chúntóir siopadóireachta: thóg sé breis agus 12 soicind. B’fhéidir go gcabhródh cuardach níos tapúla ar an siopa, nó é a léamh arís níos déanaí.',
  reasonOurSide:
    'Stop an léamh mar gheall ar fhadhb ar ár dtaobhna, ní le do shiopa, mar sin níl an cúntóir réidh fós.',
  reasonOurSideLive:
    'Stop an léamh mar gheall ar fhadhb ar ár dtaobhna, ní le do shiopa. Leanann an cúntóir foilsithe ag obair mar atá.',
  reasonOurSideKept:
    'Stop an léamh mar gheall ar fhadhb ar ár dtaobhna, ní le do shiopa, mar sin níor athraigh tada: fanann leagan {version} mar a bhí.',
  reasonTryLater: 'Bain triail as an siopa a léamh arís i gceann tamaill.',
  errorNoCredits:
    'Níl aon chreidmheas ar fáil dó seo faoi láthair. Is féidir le húinéir na cuideachta an plean agus na teorainneacha a sheiceáil in Usage.',
  errorReadUnavailable:
    'Ní féidir an siopa seo a léamh faoi láthair. Bain triail eile as i gceann cúpla nóiméad.',
  reasonTimeout:
    'Shroich an léamh a theorainn ama sular chríochnaigh sé. Leanann léamh eile ón méid a fíoraíodh cheana.',
  retrying: 'Ag tosú arís…',
  readTitleChecks: 'Seiceáil an léamh sábháilte arís',
  checksScopeLabel: 'Seiceálacha',
  readScopeChecks:
    'Cuardaigh siopadóirí, táirgí, brabhsáil agus an tralaí, ar an méid a shábháil an léamh deireanach. Ní léitear an siopa arís.',
  readScopeChecksProducts:
    'Cuardaigh táirgí, brabhsáil, grianghraif, dathanna agus méideanna, ar an méid a shábháil an léamh deireanach. Ní léitear an siopa arís.',
  readScopeChecksCart:
    'Cur leis an tralaí, léamh siar, baint agus folmhú, agus léamh táirgí, ar an méid a shábháil an léamh deireanach. Ní léitear an siopa arís.',
  readScopeChecksChat:
    'Aistriú chuig do chomhrá atá ann cheana agus léamh táirgí, ar an méid a shábháil an léamh deireanach. Ní léitear an siopa arís.',
  readScopeChecksMarket:
    'Táirgí, praghsanna agus tralaí an mhargaidh, ar an méid a shábháil an léamh deireanach. Ní léitear an siopa arís.',
  checksCost: 'Ní úsáideann ach na seiceálacha nua creidmheasanna na cuideachta',
  checksAfter:
    'Sábhálann sé dréacht nua má éiríonn leis na seiceálacha. Ní shroichfidh tada siopadóirí go dtí go bhfoilsíonn tú é.',
  checksAfterLive:
    'Sábhálann sé dréacht nua má éiríonn leis na seiceálacha. Leanann an cúntóir foilsithe ag obair, agus ní fheicfidh siopadóirí an t-athrú go dtí go bhfoilsíonn tú é.',
  checksStarted:
    'Tá na seiceálacha tosaithe. Nuair a bheidh an dréacht nua réidh, iarr ar Chat é a sheiceáil.',
  checkingSavedWork: 'An obair shábháilte á seiceáil arís',
  checkAgain: 'Seiceáil arís',
  checkingAgain: 'Á sheiceáil arís…',
  reasonCheckAgain:
    'Déanann Seiceáil arís tástáil ar an obair shábháilte uair amháin eile, gan an siopa a léamh arís. Ní úsáideann ach na seiceálacha nua creidmheasanna na cuideachta.',
  fixWithChat: 'Deisigh le Chat',
  fixReadPrompt:
    'Stad léamh {name} ({domain}). Féach ar cad a tharla agus cabhraigh liom é a chur ag obair: inis dom cad a aimsíonn tú, mol cad a d’fhéadfainn a rá leis an léamh, agus ansin seiceáil arís é nó léigh arís é leis an leid sin, an méid is lú den siopa is féidir.',
  fixReadPromptStep:
    'Stad léamh {name} ({domain}) ag an gcéim “{step}”. Féach ar cad a tharla agus cabhraigh liom é a chur ag obair: inis dom cad a aimsíonn tú, mol cad a d’fhéadfainn a rá leis an léamh, agus ansin seiceáil arís é nó léigh arís é leis an leid sin, an méid is lú den siopa is féidir.',
  stepStateDone: 'críochnaithe',
  stepStateRunning: 'ar siúl',
  stepStateWaiting: 'gan tosú',
  stepStateFailed: 'stoptha',
  newVersionTitle: 'Tá leagan {version} réidh',
  newVersionBody: 'Foilsigh é chun leagan {live} a athsholáthar.',
  newVersionBodyLive: 'Foilsigh é chun leagan {live} ar {domain} a athsholáthar.',
  previewSize: 'Méid an réamhamhairc',
  designWithChat: 'Oscail an dearadh',
  designReviewBody: 'Féach ar an dearadh reatha, ansin inis do Chat cad ba mhaith leat a athrú.',
  designWhileTitle: 'Dear é agus muid ag léamh do shiopa',
  designWhileBody:
    'Is féidir le Chat cuma, focail agus lógó an chúntóra a mhúnlú leat anois. Beidh an dearadh réidh nuair a bheidh an léamh críochnaithe.',
  designPreviewNote: 'Réamhamharc le táirgí samplacha go dtí go léifear do shiopa.',
  proposedDesign: 'Dearadh molta',
  proposedDesignNote:
    'An ghiúirléid leis an athrú seo, léirithe le táirgí samplacha. Sábhálfaidh ceadú é mar dhréacht nua; fanann foilsiú ar leathanach Storefront.',
  lineSources: 'Ó leathanaigh an tsiopa:',
  readAgainConfirm:
    'Léigh {name} arís? Tógann sé thart ar 10 go 30 nóiméad agus úsáideann sé creidmheasanna na cuideachta.',
  readAgainLive: 'Leanann an cúntóir foilsithe ag obair go dtí go bhfoilsíonn tú an leagan nua.',
  readAgainNow: 'Léigh arís',
  switchStore: 'Athraigh siopa nó cuir ceann leis',
  storesLabel: 'Do shiopaí',
  addStore: 'Cuir siopa eile leis',
  backToStores: 'Ar ais chuig do shiopaí',
  storeLive: 'Foilsithe',
  storeReady: 'Réidh',
  storeReading: 'Á léamh',
  storeStopped: 'Stoptha',
  actionDesignTitle: 'Athraigh dearadh an chúntóra siopadóireachta',
  actionKnowledgeTitle: 'Athraigh an méid atá ar eolas ag an gcúntóir',
  readTitleProducts: 'Léigh na táirgí arís',
  readTitleCart: 'Seiceáil an tralaí arís',
  readTitleTheirChat: 'Seiceáil an bealach chuig do chomhrá féin arís',
  readTitleAll: 'Léigh an siopa ar fad arís',
  readTitleStore: 'Léigh siopa nua',
  readTitleMarket: 'Cuir margadh leis',
  readShopLabel: 'Siopa',
  readScopeLabel: 'Léann sé',
  readTimeLabel: 'Tógann sé',
  readCostLabel: 'Costas',
  readAfterLabel: 'Ina dhiaidh',
  readScopeProducts: 'Cuardach agus sórtáil táirgí, grianghraif, dathanna agus méideanna táirgí',
  readScopeCart: 'Cur leis an tralaí agus baint as',
  readScopeTheirChat: 'Conas a shroicheann siopadóirí do chomhrá féin',
  readScopeAll: 'Táirgí, an tralaí, do chomhrá féin agus do mhargaí',
  readScopeStore:
    'Táirgí, praghsanna agus méideanna, an tralaí, do chomhrá féin, do chuma agus do mhargaí',
  readScopeMarket: '{market}: a tháirgí, praghsanna agus tralaí',
  readTime: 'De ghnáth {min}–{max} nóiméad',
  readCost: 'Úsáideann creidmheasanna na cuideachta',
  readCostLast:
    'Úsáideann creidmheasanna na cuideachta · d’úsáid an léamh deireanach den siopa seo {credits}',
  readCostLastPartial:
    'Úsáideann creidmheasanna na cuideachta · d’úsáid an léamh deireanach den siopa seo, a stopadh go luath, {credits} ar a laghad',
  readAfter: 'Sábhálann sé dréacht nua. Ní shroichfidh tada siopadóirí go dtí go bhfoilsíonn tú é.',
  readAfterLive:
    'Sábhálann sé dréacht nua. Leanann an cúntóir foilsithe ag obair, agus ní fheicfidh siopadóirí an t-athrú go dtí go bhfoilsíonn tú é.',
  readAfterNew: 'Sábhálann sé an chéad dréacht duit le triail a bhaint as. Ní fhoilsítear tada.',
  readCartNote:
    'Chun an tralaí a sheiceáil, cuireann sé mír thástála leis agus baineann sé arís í. Ní chríochnaíonn sé ceannach riamh.',
  readFocusLabel: 'An rud a d’iarr Chat ar an léamh a dheisiú',
  readFooter:
    'Ní thosaíonn aon rud go dtí go gceadaíonn tú é. Is féidir leat an léamh a stopadh sa chluaisín Socrú.',
  readStartedAt:
    'Tosaíodh {time} · de ghnáth {min}–{max} nóiméad. Nuair a bheidh an dréacht nua réidh, iarr ar Chat é a sheiceáil.',
  readFocusAll: 'Taispeáin gach rud',
  readFocusLess: 'Taispeáin níos lú',
  readStart: 'Tosaigh an léamh',
  addToSite: 'Cuir le do shuíomh',
  readTab: 'Iarratas',
  readStarted:
    'Tá an léamh tosaithe. Nuair a bheidh an dréacht nua réidh, iarr ar Chat é a sheiceáil.',
  readFollow: 'Lean é sa chluaisín Socrú',
  proposedDesignLive:
    'Fíor-réamhamharc ar do shiopa sa dearadh seo. Cuir ceist air: ní chuirtear aon rud i bhfíor-thralaí. Sábhálfaidh ceadú é mar dhréacht nua; déantar é a fhoilsiú ar leathanach Storefront.',
  designChanges: 'Cad a athraíonn',
  designReset: 'ar ais chuig an réamhshocrú',
  designGroupColors: 'Dathanna',
  designGroupType: 'Clónna',
  designGroupShape: 'Cruth',
  designGroupLauncher: 'Cnaipe ar an leathanach',
  designGroupPanel: 'Ceanntásc',
  designGroupProducts: 'Táirgí',
  designGroupCart: 'Cairt an tsiopa',
  designGroupCopy: 'Focail',
  designGroupLogo: 'Lógó',
  designGroupCss: 'CSS saincheaptha',
  designColorBrand: 'Branda',
  designColorActions: 'Cuir leis agus Seol',
  designColorPanel: 'Painéal',
  designColorSurface: 'Dromchlaí',
  designColorInk: 'Téacs',
  designColorMuted: 'Téacs tánaisteach',
  designColorLine: 'Línte',
  designColorBubble: 'Teachtaireachtaí an tsiopadóra',
  designColorBubbleInk: 'Téacs an tsiopadóra',
  designActionsBrand: 'Dath an bhranda',
  designActionsInk: 'Dath an téacs',
  designWordsName: 'Ainm',
  designWordsGreeting: 'Beannú',
  designWordsStarters: 'Ceisteanna tosaigh',
  designWordsLauncher: 'Lipéad an chnaipe',
  designWordsPlaceholder: 'Ionadchoinneálaí',
  designWordsAi: 'Lipéad AI',
  designWordsPerson: 'Nasc chuig duine',
  noteNew: 'Nua',
  noteChanged: 'Athraithe',
  noteRemoved: 'Bainte',
  noteSourceKnowledge: 'Leathanach Eolais: {title}',
  noteSourceFile: 'Comhad: {name}',
  noteSourceChat: 'Ón gcomhrá',
  knowledgeApprovalNote:
    'Is féidir le siopadóirí gach rud sna nótaí seo a léamh. Sábhálann ceadú dréacht nua: cuir ceisteanna air sa chluaisín Widget, ansin foilsigh é sa chluaisín Suiteáil.',
  profileTitle: 'Faoin siopa',
  profileChecked: 'Seiceáilte {date}',
  profileSources: 'Scríofa ó',
  profileSample: '{count} táirge sampláilte le haghaidh leibhéal praghsanna',
  profileSampleOne: '{count} táirge sampláilte le haghaidh leibhéal praghsanna',
  profileAssistant: 'An méid is féidir leis an gcúntóir a dhéanamh anseo',
  profileTeam: 'Scríofa ag d’fhoireann',
  profileEmpty:
    'Níl sé scríofa fós. Iarr ar Chat pas eolais a rith: scríobhann sé seo óna bhur leathanaigh féin agus ó na rudaí a chonaic sé ar bhur suíomh, agus is féidir libh línte de bhur gcuid féin a chur leis.',
  profileWithChat: 'Cuir leis nó athraigh le Chat',
  profilePrompt:
    'Cabhraigh liom feabhas a chur ar an méid a bhfuil a fhios ag an gcúntóir siopadóireachta do {name} ({domain}) faoinár siopa ina iomláine: cad a dhíolaimid, cad air a bhfuilimid aitheanta, cé dó a bhfuilimid agus cad is féidir le custaiméirí a dhéanamh anseo. Léigh próifíl Faoin siopa ar dtús agus inis dom cad atá ar iarraidh nó mícheart. Cuirtear isteach mar líne ónár bhfoireann gach rud a insím duit faoin siopa nach luann leathanach ar bith.',
  profilePartNavigation: 'Ranna',
  profilePartWelcome: 'Beannacht',
  profilePartMarkets: 'Margaí',
  profilePartFunctions: 'An méid is féidir leis an gcúntóir a dhéanamh',
  profilePartProducts: 'Táirgí',
  profilePartPages: 'Leathanaigh seirbhíse',
  profileCleared: 'Glanann sé an phróifíl scríofa',
  knowledgeTitle: 'A bhfuil ar eolas aige',
  knowledgeBody: 'Seachas do tháirgí, praghsanna agus stoc, a léann sé beo ó do shiopa i gcónaí.',
  knowledgeEmpty:
    'Do chatalóg amháin go dtí seo. Cuir eolas faoi sheachadadh, earraí a thabhairt ar ais nó íocaíocht leis trí Chat, ó do leathanaigh féin nó ó Eolas.',
  knowledgeWithChat: 'Cuir le Chat',
  knowledgeChangeWithChat: 'Athraigh le Chat',
  knowledgePrompt:
    'Cabhraigh liom a roghnú cad a insíonn an cúntóir siopadóireachta do {name} ({domain}) do shiopadóirí seachas ár dtáirgí, mar shampla faoi sheachadadh, earraí a thabhairt ar ais agus íocaíocht. Aimsigh an t-eolas ar ár leathanaigh féin nó in Eolas, agus mol nótaí is féidir liom a cheadú.',
  pagesTitle: 'Leathanaigh a léann sé',
  pagesBody:
    'Na leathanaigh as ar tháinig na nótaí. Seiceálann Poneva iad gach lá, agus insíonn Chat nuair a athraíonn ceann acu.',
  pagesEmpty:
    'Níl leathanaigh ann fós. Taispeánfar anseo na leathanaigh a aimsíonn léamh do shiopa, agus aon cheann a ainmníonn tú i Chat.',
  pageFound: 'aimsithe ag Poneva',
  pageLinkedFrom: 'nasctha ó {page}',
  pageAddedByYou: 'curtha leis de láimh',
  pageFromNote: 'luaite i nóta',
  pageNoteOne: '1 nóta',
  pageNotesMany: '{count} nótaí',
  pageStatusOk: 'Cothrom le dáta',
  pageStatusChanged: 'Athraithe',
  pageStatusUnreadable: 'Ní féidir é a léamh',
  pageStatusUnchecked: 'Níl seiceáilte fós',
  pagesChangeWithChat: 'Athraigh le Chat',
  pagesUpdateWithChat: 'Nuashonraigh nótaí le Chat',
  pagesPrompt:
    'Cabhraigh liom athrú cé na leathanaigh a léann cúntóir siopadóireachta {name} ({domain}) lena nótaí. Taispeáin dom na leathanaigh a úsáideann sé anois agus fiafraigh díom cé acu ba chóir a chur leis nó a bhaint.',
  pagesUpdatePrompt:
    'Tá roinnt leathanach de {name} ({domain}) athraithe nó ní féidir iad a léamh a thuilleadh ó rinneadh na nótaí. Taispeáin dom cé acu iad, agus déan na nótaí arís uathu mar dhréacht ar féidir liom breathnú air.',
  viewCompare: 'Comparáid',
  lookDeviceLabel: 'Déan comparáid ar',
  lookShop: 'Do shiopa',
  lookShopCaption: 'Leathanach baile, léite {when}',
  lookWidget: 'An cúntóir',
  lookWidgetCaption: 'Leagan {version}',
  lookLoading: 'An cúntóir á chur in aice le do shiopa…',
  lookFailed: 'Níorbh fhéidir an chomparáid a dhéanamh faoi láthair.',
  lookNoShopPicture: 'Níor choinnigh an léamh íomhá de do shiopa ag an leithead seo.',
  lookNoneTitle: 'Níor sábháladh cuma do shiopa féin',
  lookNoneBody:
    'Níor thaifead an léamh seo é. Léigh an stór arís ó Socrú, agus coinneofar a lógó, a dhathanna agus a leathanaigh le haghaidh comparáide.',
  lookColoursShop: 'Dathanna a úsáideann do shiopa',
  lookColoursAssistant: 'Dathanna an chúntóra',
  lookRoleHeader: 'Ceanntásc',
  lookRoleBrand: 'Branda',
  lookSame: 'Mar an gcéanna le {colour} do shiopa ({source})',
  lookOff: 'Gar do {colour} do shiopa ({source}) ach ní mar an gcéanna é, difríocht {delta}',
  lookOwn: 'A dath féin: ní úsáideann do shiopa é',
  lookNeutral:
    'Níor luchtaíodh do shiopa le haghaidh seo: ní thaispeántar ach an méid a choinnigh an léamh.',
  lookFromLogo: 'lógó',
  lookFromTheme: 'dath téama',
  lookFromTile: 'dath tíl',
  lookFromMask: 'deilbhín greamaithe',
  lookFromManifest: 'mainfeist aip',
  lookFromHeader: 'ceanntásc',
  lookFromButton: 'cnaipí',
  lookFromVariable: 'dath stíle',
  lookFix: 'Deisigh le Chat',
  lookFixPrompt:
    'Is é {colour} dath an chúntóra do {role}, gar do {exact} do shiopa ({source}) ach ní mar an gcéanna é. Úsáid dath cruinn do shiopa, {exact}, le do thoil.',
  lookSwatch: 'Dath {colour}',
  errorDesignChecks: 'Níor éirigh leis an leagan seo na seiceálacha dearaidh a rith.',
  designSamplesBlocked:
    'Ní féidir leaschláithe cárta a chruthú anseo: cuireann do shuíomh bac ar chuairteanna uathoibrithe. Oibríonn dathanna, cló agus an lógó fós.',
  designCheckLabel: 'Tá lipéad AI ró-fhann le léamh.',
  designCheckReach: 'Níl cnaipe inrochtana.',
  designCheckSize: 'Tá cuid den téacs ró-bheag.',
  designCheckOverflow: 'Scrollaíonn an painéal go cliathánach.',
  designCheckRender: 'Níorbh fhéidir na seiceálacha a rith. Bain triail eile as i gceann tamaill.',
  fixDesignPrompt:
    'Rinne mé iarracht an dréacht is déanaí de {name} ({domain}) a fhoilsiú, ach níor éirigh leis na seiceálacha dearaidh a rith: {facts}. Ceartaigh an dearadh sa chomhrá seo agus seiceáil é ar ghuthán agus ar ríomhaire. Ná foilsigh é.',
};

const it: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Assistente agli acquisti',
  designUnconfirmed:
    'Interrotto prima che Chat potesse confermare il salvataggio del design. Il canvas dell’assistente mostra la versione più recente.',
  errorNewerVersion:
    'Da quando hai aperto questa vista è stata salvata una nuova versione. Guardala prima di pubblicare.',
  canvasSelectHint: 'Fai clic su una parte dell’assistente per cambiarla in chat · Esc per uscire',
  canvasDraft: 'Versione {version} · non ancora pubblicata',
  canvasDraftOverLive: 'Versione {version} · la versione {live} è pubblicata',
  canvasLive: 'Versione {version} · pubblicata',
  canvasNoVersion: 'La prima versione arriva al termine della lettura del negozio',
  canvasPublish: 'Pubblica dalla pagina Storefront',
  canvasFrameTitle: 'Assistente agli acquisti di {name}',
  viewLabel: 'Vista',
  photosGood: '{count} foto dei prodotti: intere e nitide',
  photosGoodOne: 'La foto del prodotto è intera e nitida',
  canvasMissingTitle: 'Questo assistente agli acquisti non è in questo spazio di lavoro',
  canvasMissingBody: 'Appartiene a un altro spazio di lavoro o è stato rimosso.',
  canvasMissingLink: 'Vedi gli assistenti di questo spazio di lavoro',
  photosLabel: 'Foto dei prodotti',
  photosSmall: '{count} piccole nei riquadri',
  photosCut: '{count} tagliate',
  photosSoft: '{count} ingrandite e poco nitide',
  photosMissing: '{count} senza foto',
  photosFix: 'Sistema con Chat',
  photosPrompt:
    'Sistema le foto dei prodotti nelle schede: ognuna deve essere intera, nitida e abbastanza grande nel suo riquadro.',
  photosFacts:
    '{total} foto: {small} piccole nei riquadri, {cut} tagliate, {soft} ingrandite, {missing} senza foto. Dimensioni: {sizes}',
  viewDesign: 'Design',
  viewVersions: 'Versioni',
  versionsLoading: 'Caricamento delle versioni…',
  versionsFailed: 'Non è stato possibile caricare le versioni.',
  versionTitle: 'Versione {version}',
  versionLive: 'Pubblicata',
  versionChecked: 'Verificata',
  versionToday: 'Oggi',
  versionYesterday: 'Ieri',
  versionEarlier: 'In precedenza',
  versionByPerson: 'Richiesta da {name}',
  versionByChat: 'Richiesta in chat',
  versionFromReading: 'Dalla lettura di {domain}',
  versionPublishedBy: 'pubblicata da {name}, {at}',
  versionPublish: 'Pubblica',
  versionRestore: 'Ripristina…',
  versionRestoreTitle: 'Ripristinare la versione {version}?',
  versionRestoreBody:
    'Diventa la versione {next}, una nuova bozza. Il tuo sito non cambia finché non pubblichi.',
  versionRestoreConfirm: 'Ripristina',
  versionRestoreCancel: 'Annulla',
  versionRestored: 'Ripristinata come versione {version}',
  versionRestoreFailed: 'Non è stato possibile ripristinare la versione.',
  compareTitle: 'Confronta la versione {to} con la {from}',
  compareTitleLive: 'Confronta la versione {to} con la {from}, pubblicata',
  compareHint: 'Trascina per confrontare',
  compareLabel: 'Confronta le versioni {from} e {to}',
  compareTagLive: 'v{version} · Pubblicata',
  compareTag: 'v{version}',
  changesTitle: 'Cosa è cambiato dalla versione {from} alla {to}',
  changesNone: 'I design sono uguali. Sono cambiate solo le note o i dati del negozio.',
  changeAdded: 'Aggiunto',
  changeRemoved: 'Rimosso',
  changeChanged: 'Modificato',
  changeDrawn: 'un logo disegnato',
  changeCss: 'CSS dedicato',
  changeYes: 'sì',
  changeNo: 'no',
  'field.colors.brand': 'Colore del marchio',
  'field.colors.actions': 'Colore dei pulsanti',
  'field.colors.panel': 'Colore del pannello',
  'field.colors.surface': 'Colore delle superfici',
  'field.colors.ink': 'Colore del testo',
  'field.colors.muted': 'Colore del testo secondario',
  'field.colors.line': 'Colore delle linee',
  'field.colors.bubble': 'Colore del messaggio cliente',
  'field.colors.bubbleInk': 'Testo del messaggio cliente',
  'field.colors.header': 'Colore dell’intestazione',
  'field.colors.headerInk': 'Colore del testo nell’intestazione',
  'field.colors.tints': 'Tinte tenui',
  'field.copy.assistantName': 'Nome',
  'field.copy.greeting': 'Saluto',
  'field.copy.placeholder': 'Campo della domanda',
  'field.copy.starters': 'Domande iniziali',
  'field.copy.launcherLabel': 'Testo del pulsante di apertura',
  'field.copy.aiLabel': 'Etichetta IA',
  'field.copy.personLabel': 'Link a una persona',
  'field.type.body': 'Carattere del testo',
  'field.type.display': 'Carattere dei titoli',
  'field.type.fontCss': 'Origine del carattere',
  'field.type.scale': 'Dimensione del testo',
  'field.shape.radius': 'Angoli',
  'field.shape.shadow': 'Ombra',
  'field.shape.density': 'Spaziatura',
  'field.launcher.style': 'Pulsante sui computer',
  'field.launcher.phoneStyle': 'Pulsante sui telefoni',
  'field.launcher.side': 'Lato del pulsante',
  'field.launcher.icon': 'Icona del pulsante',
  'field.panel.header': 'Dimensione dell’intestazione',
  'field.panel.opening': 'Prima schermata',
  'field.products.layout': 'Prodotti',
  'field.products.imageFit': 'Foto dei prodotti',
  'field.products.imageRatio': 'Forma del riquadro foto',
  'field.products.imagePadding': 'Spazio attorno alle foto',
  'field.products.imageBackground': 'Colore del riquadro foto',
  'field.products.picker': 'Scelta della taglia',
  'field.products.showStock': 'Disponibilità visibile',
  'field.cart.landing': 'Animazione del carrello',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Forma del logo',
  'field.logo.plate': 'Sfondo del logo',
  'field.css': 'CSS personalizzato',
  'value.ink': 'colore del testo',
  'value.brand': 'colore del marchio',
  'value.small': 'piccolo',
  'value.medium': 'medio',
  'value.large': 'grande',
  'value.square': 'quadrato',
  'value.soft': 'morbido',
  'value.round': 'tondo',
  'value.flat': 'piatto',
  'value.deep': 'profondo',
  'value.compact': 'compatto',
  'value.comfortable': 'spazioso',
  'value.pill': 'capsula con testo',
  'value.circle': 'tondo',
  'value.right': 'destra',
  'value.left': 'sinistra',
  'value.logo': 'logo',
  'value.chat': 'fumetto della chat',
  'value.sparkle': 'scintilla',
  'value.bag': 'borsa',
  'value.full': 'completo',
  'value.middle': 'al centro',
  'value.bottom': 'in basso',
  'value.rows': 'righe',
  'value.grid': 'griglia',
  'value.carousel': 'riga da scorrere',
  'value.contain': 'foto intera',
  'value.cover': 'riquadro pieno',
  'value.portrait': 'verticale',
  'value.landscape': 'orizzontale',
  'value.none': 'nessuno',
  'value.pop': '+1 che sale',
  'value.ring': 'onda',
  'value.blink': 'lampeggio',
  'value.chips': 'pulsanti in fila',
  'value.menu': 'elenco',
  'value.rounded': 'arrotondato',
  designCardTitle: 'Assistente agli acquisti di {name}',
  designCardSaved: 'Versione {version} salvata · non ancora pubblicata',
  designCardSavedFirst: 'Design salvato · sarà usato nella prima versione',
  designSaving: 'Salvataggio del design…',
  selectedAssistantPart: 'Selezione: una parte dell’assistente',
  loading: 'Caricamento di Storefront…',
  disabledTitle: 'Storefront non è attivo in questo spazio di lavoro.',
  disabledBody:
    'È in accesso anticipato e arriva per fasi. Chiedi al tuo contatto Poneva quando sarà disponibile per voi.',
  loadFailed: 'Non è stato possibile caricare Storefront.',
  startTitle: 'Un assistente agli acquisti per il tuo negozio',
  startBody:
    'Poneva legge il negozio come lo usa un cliente: ricerca, prodotti, carrello e chat esistente. Richiede circa 10–30 minuti e usa i crediti aziendali. Nel frattempo puoi continuare a lavorare.',
  urlLabel: 'Indirizzo del tuo negozio',
  urlPlaceholder: 'https://www.tuonegozio.it',
  startButton: 'Leggi il mio negozio',
  starting: 'Avvio…',
  startFailed: 'Non è stato possibile leggere quel negozio. Controlla l’indirizzo e riprova.',
  tabConversations: 'Conversazioni',
  tabOverview: 'Panoramica',
  allAssistants: 'Tutti gli assistenti',
  allAssistantsNote: '{assistants} assistenti · {sites} siti',
  allAssistantsMenuNote: '{count} conversazioni questa settimana',
  marketLabel: 'Mercato',
  allMarkets: 'Tutti i mercati',
  allMarketsNote: '{count} siti',
  scopeIn: '{assistant} in {market}',
  askAbout: 'Chiedi a Chat di {scope}',
  overviewAssistantsTitle: 'Assistenti e mercati',
  overviewMarketsTitle: 'Mercati',
  overviewAssistantsOnly: 'Assistenti',
  overviewIdle: '{count} assistenti ancora senza conversazioni',
  allAssistantsCount: '{assistants} assistenti',
  overviewFailed: 'Non è stato possibile caricare la panoramica.',
  colConversations: 'Conversazioni',
  colCart: 'Aggiunto al carrello',
  colCheckout: 'Cassa',
  colPerson: 'Persona',
  colCost: 'Crediti',
  rowMarkets: '{count} mercati',
  rowVersion: 'versione {version}',
  rowAria:
    '{name}, {sub}: {conversations} conversazioni, {cart} aggiunte al carrello, {checkout} arrivi alla cassa, {person} richieste di una persona, crediti {cost}',
  versionPublishedAt: 'pubblicata {at}',
  designColorHeader: 'Intestazione',
  designColorHeaderInk: 'Testo dell’intestazione',
  designGroupTranslations: 'Testi in altre lingue',
  topicsTitle: 'Cosa chiedono i clienti',
  topicsNote: 'Temi trovati da Chat nelle prime domande',
  startPagesTitle: 'Dove iniziano le conversazioni',
  startPagesOne: 'Tutte le conversazioni sono iniziate su un solo tipo di pagina: {page}.',
  'startPage.product': 'Pagine dei prodotti',
  'startPage.search': 'Risultati di ricerca',
  'startPage.start': 'Pagina iniziale',
  'startPage.cart': 'Carrello',
  'startPage.other': 'Altre pagine',
  addMarketInChat: 'Aggiungi un mercato in chat',
  marketsTitle: 'Mercati',
  marketsBody:
    'Un assistente e un design. Ogni mercato ha lingua, valuta, prodotti, carrello e chat con una persona propri.',
  marketChecked: 'verificato {when}',
  'marketStatus.ready': 'Pronto',
  'marketStatus.found': 'Non aggiunto',
  'marketStatus.checking': 'Verifica in corso',
  'marketStatus.failed': 'Da sistemare',
  addMarketPrompt:
    'Aggiungi un altro mercato all’assistente agli acquisti di {name} ({domain}): trova gli altri paesi, lingue o siti del negozio e chiedimi quale aggiungere.',
  tabWidget: 'Widget',
  tabInstall: 'Installazione',
  tabSetup: 'Configurazione',
  statusReading: 'Lettura del negozio',
  readingProducts: 'Nuova lettura dei prodotti',
  readingCart: 'Nuova verifica del carrello',
  readingChat: 'Verifica dell’accesso alla tua chat',
  readingMarket: 'Aggiunta del mercato {market}',
  readingMarketAny: 'Aggiunta di un mercato',
  statusReady: 'Pronto da installare',
  statusFailed: 'Lettura interrotta',
  statusLive: 'Sul sito · versione {version} su {domain}',
  statusPublished: 'Pubblicato · versione {version} · non ancora sul sito',
  statusPublishedUnverified: 'Pubblicato · versione {version} · impossibile controllare il sito',
  stepOpen: 'Apertura del sito',
  stepSearch: 'Ricerca',
  stepProducts: 'Prodotti',
  stepCart: 'Carrello',
  stepChat: 'La tua chat esistente',
  stepLook: 'Il tuo stile e logo',
  stepChecks: 'Verifica del funzionamento',
  stepReady: 'Pronto',
  stepDetailOpen: 'Apertura di {domain}',
  stepDetailSearch: 'Ricerca dei prodotti del negozio',
  stepDetailProducts: 'Lettura di prodotti, prezzi, foto, colori e taglie',
  stepDetailCart: 'Prova del carrello: aggiunta, lettura, rimozione e svuotamento',
  stepDetailChat: 'Ricerca della tua chat esistente',
  stepDetailLook: 'Lettura del logo e dei colori del negozio',
  stepDetailChecks: 'Prova di ricerche dei clienti, prodotti, navigazione e carrello',
  stepDetailChecksProducts: 'Verifica di ricerche, navigazione, foto, colori e taglie',
  stepDetailChecksCart:
    'Verifica di aggiunta, lettura, rimozione e svuotamento del carrello, e dei prodotti',
  stepDetailChecksChat:
    'Verifica del passaggio alla tua chat esistente e della lettura dei prodotti',
  stepDetailChecksMarket: 'Verifica di prodotti, prezzi e carrello di questo mercato',
  nowRunning: 'Ora: {step}',
  nowProgress: 'Passaggio {n} di {total} · {minutes} min finora',
  nowReady: 'Il tuo negozio è pronto per un assistente',
  nowReadyBody: 'Ricerca, prodotti e carrello funzionano.',
  nowLastRead: 'Ultima lettura {when}',
  nowUsually: 'di solito {min}–{max}',
  stepsRead: 'Letto {when} · Mostra i passaggi',
  nowFailed: 'Lettura interrotta',
  factAnswers: 'Risponde ai clienti con',
  costCounting: 'Conteggio…',
  factPaid: 'Pagato con',
  paidCompany: 'Crediti aziendali',
  creditsAmount: '{amount} crediti',
  creditsOne: '{amount} credito',
  creditsUnder: 'meno di {amount} crediti',
  stopRun: 'Interrompi la lettura',
  stopping: 'Interruzione…',
  closeNote: 'Puoi lasciare questa pagina. La lettura continua.',
  readyNote: 'Poi: prova il Widget e installalo.',
  buildWidget: 'Prova il Widget',
  readAgain: 'Leggi di nuovo il negozio',
  foundTitle: 'Cosa ha trovato',
  foundProducts: 'Prodotti e prezzi letti',
  foundSearch: 'La ricerca funziona',
  foundCart: 'L’aggiunta al carrello funziona',
  foundChat: 'La tua chat {vendor}',
  foundNoChat: 'Nessuna chat propria',
  foundLook: 'I tuoi colori e logo',
  widgetTitle: 'Il tuo Widget',
  widgetBody:
    'Il tuo assistente, con i tuoi colori. Provalo qui: risponde con i tuoi veri prodotti. Non aggiunge nulla a un carrello reale.',
  widgetLoading: 'Caricamento dell’anteprima…',
  widgetFailed: 'L’anteprima non si è caricata.',
  widgetNotReady:
    'Il Widget sarà pronto quando saranno completati i passaggi della scheda Configurazione.',
  installLineTitle: 'Aggiungi questa riga al sito',
  installLineBody:
    'Funziona su ogni piattaforma: nel head delle pagine; su Shopify, in theme.liquid appena prima di </head>. Non mostra nulla finché non pubblichi.',
  installLineBodyLive:
    'Funziona su ogni piattaforma: nel head delle pagine; su Shopify, in theme.liquid appena prima di </head>. I clienti vedranno la versione {version}.',
  installPolicyAsk: 'Il tuo sito usa una Content Security Policy?',
  installPolicy:
    'Se il sito usa una Content Security Policy, consenti anche {origin} in {directives}, altrimenti l’assistente non può avviarsi.',
  installPolicyRefuses:
    'La Content Security Policy del sito blocca l’assistente: consenti {origin} in {directives}, altrimenti non si avvierà.',
  installPolicyNonce:
    'La policy esegue solo script attendibili: aggiungi la riga con il nonce delle tue pagine.',
  installPolicyEval: "L’assistente richiede anche 'unsafe-eval' in script-src.",
  copy: 'Copia',
  copied: 'Copiato',
  domainsTitle: 'Siti su cui risponde',
  domainsBody: 'Sugli altri siti resta in silenzio, anche se copiano la riga.',
  lineFound: 'Riga trovata',
  lineNotSeen: 'Non ancora sulla pagina',
  lineUnverified:
    'Controllo non riuscito: il sito blocca le visite automatiche. L’assistente funziona comunque per i clienti.',
  lineChecked: 'verificato {when}',
  lineCheckAgain: 'Verifica di nuovo',
  lineChecking: 'Verifica della pagina…',
  installCheckBusy: 'Il controllo è occupato in questo momento. Riprova tra poco.',
  publishTitle: 'Pubblica',
  publishBody: 'I clienti su {domain} lo vedono subito. Puoi ritirarlo in qualsiasi momento.',
  publishButton: 'Pubblica la versione {version}',
  publishConfirm: 'Pubblicare la versione {version} di {name}?',
  publishNow: 'Pubblica',
  notNow: 'Non ora',
  publishing: 'Pubblicazione…',
  liveTitle: 'Pubblicata: versione {version}',
  liveSince: 'Dal {since}',
  unpublish: 'Ritira dal sito',
  unpublishConfirm:
    'Ritirare la versione {version} dal sito? I clienti non vedranno l’assistente finché non pubblichi di nuovo.',
  publishedLine: 'Ora pubblicata: versione {version}, dal {since}',
  usageTitle: 'Utilizzo',
  usageBody: 'Usa i crediti della tua azienda, come il resto di Chat.',
  usageDiscovery: 'Lettura del negozio',
  usageChats: 'Chat con i clienti',
  usageChatsValue: '{count} chat · {cost}',
  last7: 'Ultimi 7 giorni',
  askWeek: 'Chiedi a Chat della settimana',
  kpiConversations: 'Conversazioni',
  kpiVsPrevious: '{change} rispetto alla settimana prima',
  kpiCart: 'Aggiunto al carrello',
  kpiCheckout: 'Arrivati alla cassa',
  kpiPerson: 'Chiesto di parlare con una persona',
  kpiCost: 'Crediti usati',
  ofConversations: '{percent} delle conversazioni',
  checkoutNote: '{percent} delle conversazioni',
  personNote: '{percent} ha aperto la tua chat',
  costNote: 'Circa {amount} a conversazione',
  costNoteUnder: 'Meno di {amount} a conversazione',
  sparkLabel: 'Conversazioni al giorno, ultimi {days} giorni',
  sparkDay: '{day}: {count} conversazioni',
  filterAll: 'Tutte',
  filterCart: 'Aggiunto al carrello',
  filterCheckout: 'Cassa',
  filterPerson: 'Persona',
  outcomeCart: 'Aggiunto al carrello',
  outcomeCheckout: 'Arrivato alla cassa',
  outcomePerson: 'Chiesto di parlare con una persona',
  outcomeAnswered: 'Risposta ricevuta',
  devicePhone: 'Telefono',
  deviceComputer: 'Computer',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Iniziata su {page}',
  anonymous: 'Il cliente è anonimo.',
  askConversation: 'Chiedi a Chat di questa conversazione',
  showOlderVersions: 'Mostra le versioni precedenti',
  showOlder: 'Mostra le conversazioni precedenti',
  filterCountsWeek: 'Conteggi: ultimi 7 giorni',
  readAlreadyHere:
    'Questo negozio è già presente, quindi non è stato avviato nulla di nuovo e non sono stati usati crediti.',
  readOpenShop: 'Aprilo',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Cosa possiamo imparare da questa conversazione?',
  askWeekMessage: 'Cosa possiamo imparare dalle conversazioni di questa settimana?',
  conversationsChipOne: '{name} · conversazione del {when}',
  conversationsChipWeek: '{name} · questa settimana',
  conversationsChipWeekIn: '{name} · {market} · questa settimana',
  removeConversations: 'Rimuovi le conversazioni',
  noConversations: 'Ancora nessuna conversazione',
  noConversationsBody: 'Quando i clienti usano l’assistente, le loro conversazioni compaiono qui.',
  pickConversation: 'Scegli una conversazione per leggerla.',
  eventCheckout: 'È andato alla cassa',
  eventPerson: 'Ha aperto la tua chat',
  showMore: 'Mostra altro',
  urlInvalid: 'Usa l’indirizzo https del negozio, per esempio https://www.tuonegozio.it.',
  errorNoCompany:
    'Storefront richiede uno spazio di lavoro aziendale. Chiedi all’amministratore di aggiungerti.',
  errorPublishPermission:
    'Può farlo solo chi ha il permesso di pubblicare per l’azienda. Chiedi all’amministratore.',
  actionFailed: 'Non ha funzionato. Riprova.',
  copyFailed: 'Impossibile copiare. Seleziona la riga e copiala.',
  reasonCancelled: 'Hai interrotto la lettura.',
  reasonFailed: 'La lettura del negozio non è terminata, quindi l’assistente non è pronto.',
  reasonFailedLive:
    'La lettura del negozio non è terminata. L’assistente pubblicato continua a funzionare come prima.',
  reasonFailedKept:
    'La lettura del negozio non è terminata, quindi nulla è cambiato: la versione {version} resta com’era.',
  statusReadStopped: 'Pronto · ultima lettura interrotta',
  reasonStepMarked: 'Il passaggio interrotto è indicato sotto.',
  reasonReadAgain: 'Una nuova lettura riparte da ciò che questa ha appreso.',
  reasonNotAStore:
    'Questo indirizzo non sembra un negozio online: non c’erano prodotti da cercare. Controlla l’indirizzo o prova il dominio principale del negozio.',
  reasonTooSlow:
    'La ricerca del negozio è troppo lenta per un assistente agli acquisti: ha impiegato oltre 12 secondi. Una ricerca più veloce o una nuova lettura più tardi potrebbero aiutare.',
  reasonOurSide:
    'La lettura si è fermata per un problema da parte nostra, non del tuo negozio, quindi l’assistente non è ancora pronto.',
  reasonOurSideLive:
    'La lettura si è fermata per un problema da parte nostra, non del tuo negozio. L’assistente pubblicato continua a funzionare come prima.',
  reasonOurSideKept:
    'La lettura si è fermata per un problema da parte nostra, non del tuo negozio, quindi nulla è cambiato: la versione {version} resta com’era.',
  reasonTryLater: 'Riprova a leggere il negozio tra un po’.',
  errorNoCredits:
    'Al momento non ci sono crediti per questa operazione. Un proprietario dell’azienda può controllare il piano e i limiti in Usage.',
  errorReadUnavailable:
    'Questo negozio non può essere letto in questo momento. Riprova tra qualche minuto.',
  reasonTimeout:
    'La lettura ha raggiunto il limite di tempo prima di terminare. Una nuova lettura riparte da ciò che è già stato verificato.',
  retrying: 'Riavvio…',
  readTitleChecks: 'Verifica di nuovo la lettura salvata',
  checksScopeLabel: 'Verifica',
  readScopeChecks:
    'Ricerche dei clienti, prodotti, navigazione e carrello, su quanto salvato dall’ultima lettura. Il negozio non viene riletto.',
  readScopeChecksProducts:
    'Ricerche di prodotti, navigazione, foto, colori e taglie, su quanto salvato dall’ultima lettura. Il negozio non viene riletto.',
  readScopeChecksCart:
    'Aggiunta, lettura, rimozione e svuotamento del carrello, e lettura dei prodotti, su quanto salvato dall’ultima lettura. Il negozio non viene riletto.',
  readScopeChecksChat:
    'Passaggio alla tua chat esistente e lettura dei prodotti, su quanto salvato dall’ultima lettura. Il negozio non viene riletto.',
  readScopeChecksMarket:
    'Prodotti, prezzi e carrello del mercato, su quanto salvato dall’ultima lettura. Il negozio non viene riletto.',
  checksCost: 'Solo le nuove verifiche usano crediti aziendali',
  checksAfter:
    'Salva una nuova bozza se le verifiche vanno a buon fine. I clienti non vedono nulla finché non pubblichi.',
  checksAfterLive:
    'Salva una nuova bozza se le verifiche vanno a buon fine. L’assistente pubblicato continua a funzionare e i clienti vedono la modifica solo dopo la pubblicazione.',
  checksStarted:
    'Le verifiche sono iniziate. Quando la nuova bozza è pronta, chiedi a Chat di verificarla.',
  checkingSavedWork: 'Nuova verifica del lavoro salvato',
  checkAgain: 'Verifica di nuovo',
  checkingAgain: 'Nuova verifica…',
  reasonCheckAgain:
    '«Verifica di nuovo» prova ancora una volta il lavoro salvato, senza rileggere il negozio. Solo le nuove verifiche usano crediti aziendali.',
  fixWithChat: 'Sistema con Chat',
  fixReadPrompt:
    'La lettura di {name} ({domain}) si è interrotta. Guarda cosa è successo e aiutami a farla funzionare: dimmi cosa trovi, suggerisci cosa potrei indicare alla lettura, poi verificala di nuovo o rifalla con quell’indicazione, leggendo il meno possibile del negozio.',
  fixReadPromptStep:
    'La lettura di {name} ({domain}) si è interrotta al passaggio «{step}». Guarda cosa è successo e aiutami a farla funzionare: dimmi cosa trovi, suggerisci cosa potrei indicare alla lettura, poi verificala di nuovo o rifalla con quell’indicazione, leggendo il meno possibile del negozio.',
  stepStateDone: 'completato',
  stepStateRunning: 'in corso',
  stepStateWaiting: 'non iniziato',
  stepStateFailed: 'interrotto',
  newVersionTitle: 'La versione {version} è pronta',
  newVersionBody: 'Pubblicala per sostituire la versione {live}.',
  newVersionBodyLive: 'Pubblicala per sostituire la versione {live} su {domain}.',
  previewSize: 'Dimensione dell’anteprima',
  designWithChat: 'Apri il design',
  designReviewBody: 'Esamina il design attuale, poi indica a Chat cosa vorresti modificare.',
  designWhileTitle: 'Disegnalo mentre leggiamo il negozio',
  designWhileBody:
    'Chat può già aiutarti a creare l’aspetto, i testi e il logo dell’assistente. Il design sarà pronto al termine della lettura.',
  designPreviewNote: 'Un’anteprima con prodotti di esempio finché il negozio non è stato letto.',
  proposedDesign: 'Design proposto',
  proposedDesignNote:
    'Il Widget con questa modifica e prodotti di esempio. Approvando salvi una nuova bozza; la pubblicazione resta nella pagina Storefront.',
  lineSources: 'Dalle pagine del negozio:',
  readAgainConfirm: 'Leggere di nuovo {name}? Richiede circa 10–30 minuti e usa crediti aziendali.',
  readAgainLive:
    'L’assistente pubblicato continua a funzionare finché non pubblichi la nuova versione.',
  readAgainNow: 'Leggi di nuovo',
  switchStore: 'Cambia negozio o aggiungine uno',
  storesLabel: 'I tuoi negozi',
  addStore: 'Aggiungi un altro negozio',
  backToStores: 'Torna ai tuoi negozi',
  storeLive: 'Pubblicato',
  storeReady: 'Pronto',
  storeReading: 'Lettura in corso',
  storeStopped: 'Interrotto',
  actionDesignTitle: 'Modifica il design dell’assistente agli acquisti',
  actionKnowledgeTitle: 'Modifica ciò che sa l’assistente agli acquisti',
  readTitleProducts: 'Leggi di nuovo i prodotti',
  readTitleCart: 'Verifica di nuovo il carrello',
  readTitleTheirChat: 'Verifica di nuovo l’accesso alla tua chat',
  readTitleAll: 'Leggi di nuovo tutto il negozio',
  readTitleStore: 'Leggi un nuovo negozio',
  readTitleMarket: 'Aggiungi un mercato',
  readShopLabel: 'Negozio',
  readScopeLabel: 'Legge',
  readTimeLabel: 'Durata',
  readCostLabel: 'Costo',
  readAfterLabel: 'Dopo',
  readScopeProducts: 'Ricerca e ordinamento dei prodotti, foto, colori e taglie dei prodotti',
  readScopeCart: 'Aggiunta al carrello e rimozione',
  readScopeTheirChat: 'Come i clienti raggiungono la tua chat',
  readScopeAll: 'Prodotti, carrello, la tua chat e i tuoi mercati',
  readScopeStore: 'Prodotti, prezzi e taglie, carrello, la tua chat, il tuo stile e i tuoi mercati',
  readScopeMarket: '{market}: prodotti, prezzi e carrello',
  readTime: 'Di solito {min}–{max} minuti',
  readCost: 'Usa crediti aziendali',
  readCostLast: 'Usa crediti aziendali · l’ultima lettura del negozio ha usato {credits}',
  readCostLastPartial:
    'Usa crediti aziendali · l’ultima lettura del negozio, interrotta prima del termine, ha usato almeno {credits}',
  readAfter: 'Salva una nuova bozza. I clienti non vedono nulla finché non pubblichi.',
  readAfterLive:
    'Salva una nuova bozza. L’assistente pubblicato continua a funzionare e i clienti vedono la modifica solo dopo la pubblicazione.',
  readAfterNew: 'Salva una prima bozza da provare. Non viene pubblicato nulla.',
  readCartNote:
    'Per verificare il carrello aggiunge un articolo di prova e poi lo rimuove. Non conclude mai l’acquisto.',
  readFocusLabel: 'Cosa Chat ha chiesto alla lettura di correggere',
  readFooter:
    'Non parte nulla finché non approvi. Puoi interrompere la lettura nella scheda Configurazione.',
  readStartedAt:
    'Iniziata {time} · di solito {min}–{max} minuti. Quando la nuova bozza è pronta, chiedi a Chat di verificarla.',
  readFocusAll: 'Mostra tutto',
  readFocusLess: 'Mostra meno',
  readStart: 'Avvia la lettura',
  addToSite: 'Aggiungi al sito',
  readTab: 'Richiesta',
  readStarted:
    'La lettura è iniziata. Quando la nuova bozza è pronta, chiedi a Chat di verificarla.',
  readFollow: 'Segui la lettura nella scheda Configurazione',
  proposedDesignLive:
    'L’anteprima reale del negozio con questo design. Fai una domanda: non aggiunge nulla a un carrello reale. Approvando salvi una nuova bozza; la pubblicazione resta nella pagina Storefront.',
  designChanges: 'Cosa cambia',
  designReset: 'torna al valore iniziale',
  designGroupColors: 'Colori',
  designGroupType: 'Caratteri',
  designGroupShape: 'Forma',
  designGroupLauncher: 'Pulsante sulla pagina',
  designGroupPanel: 'Intestazione',
  designGroupProducts: 'Prodotti',
  designGroupCart: 'Carrello del negozio',
  designGroupCopy: 'Testi',
  designGroupLogo: 'Logo',
  designGroupCss: 'CSS personalizzato',
  designColorBrand: 'Marchio',
  designColorActions: 'Aggiungi e Invia',
  designColorPanel: 'Pannello',
  designColorSurface: 'Superfici',
  designColorInk: 'Testo',
  designColorMuted: 'Testo secondario',
  designColorLine: 'Linee',
  designColorBubble: 'Messaggi del cliente',
  designColorBubbleInk: 'Testo del cliente',
  designActionsBrand: 'Colore del marchio',
  designActionsInk: 'Colore del testo',
  designWordsName: 'Nome',
  designWordsGreeting: 'Saluto',
  designWordsStarters: 'Domande iniziali',
  designWordsLauncher: 'Testo del pulsante',
  designWordsPlaceholder: 'Testo di esempio',
  designWordsAi: 'Etichetta IA',
  designWordsPerson: 'Link a una persona',
  noteNew: 'Nuova',
  noteChanged: 'Modificata',
  noteRemoved: 'Rimossa',
  noteSourceKnowledge: 'Pagina di conoscenza: {title}',
  noteSourceFile: 'File: {name}',
  noteSourceChat: 'Dalla chat',
  knowledgeApprovalNote:
    'I clienti possono leggere tutto ciò che contengono queste note. Approvando salvi una nuova bozza: fai domande nella scheda Widget e poi pubblicala nella scheda Installazione.',
  profileTitle: 'Informazioni sul negozio',
  profileChecked: 'Verificato il {date}',
  profileSources: 'Scritto a partire da',
  profileSample: '{count} prodotti campionati per la fascia di prezzo',
  profileSampleOne: '{count} prodotto campionato per la fascia di prezzo',
  profileAssistant: 'Cosa può fare qui l’assistente',
  profileTeam: 'Scritto dal vostro team',
  profileEmpty:
    'Non ancora scritto. Chiedi a Chat di avviare un passaggio di conoscenza: lo scrive dalle vostre pagine e da ciò che ha visto sul vostro sito, e potete aggiungere righe vostre.',
  profileWithChat: 'Aggiungi o modifica con Chat',
  profilePrompt:
    'Aiutami a migliorare ciò che l’assistente acquisti di {name} ({domain}) sa del nostro negozio nel suo insieme: cosa vendiamo, per cosa siamo conosciuti, per chi siamo e cosa possono fare qui i clienti. Leggi prima il profilo «Informazioni sul negozio» e dimmi cosa manca o è sbagliato. Tutto ciò che ti dico sul negozio e che nessuna pagina riporta entra come riga del nostro team.',
  profilePartNavigation: 'Reparti',
  profilePartWelcome: 'Saluto',
  profilePartMarkets: 'Mercati',
  profilePartFunctions: 'Cosa può fare l’assistente',
  profilePartProducts: 'Prodotti',
  profilePartPages: 'Pagine di servizio',
  profileCleared: 'Cancella il profilo scritto',
  knowledgeTitle: 'Cosa sa',
  knowledgeBody:
    'Oltre a prodotti, prezzi e disponibilità, che legge sempre in tempo reale dal negozio.',
  knowledgeEmpty:
    'Per ora solo il catalogo. Con Chat aggiungi consegne, resi o pagamenti dalle tue pagine o da Conoscenza.',
  knowledgeWithChat: 'Aggiungi con Chat',
  knowledgeChangeWithChat: 'Modifica con Chat',
  knowledgePrompt:
    'Aiutami a scegliere cosa l’assistente agli acquisti di {name} ({domain}) racconta ai clienti oltre ai prodotti, come consegne, resi e pagamenti. Cerca nelle nostre pagine o in Conoscenza e proponi note che possa approvare.',
  pagesTitle: 'Pagine che legge',
  pagesBody:
    'Le pagine da cui provengono le note. Poneva le controlla ogni giorno e Chat avvisa quando una cambia.',
  pagesEmpty:
    'Ancora nessuna pagina. Qui compaiono le pagine trovate dalla lettura del negozio e quelle indicate in Chat.',
  pageFound: 'trovata da Poneva',
  pageLinkedFrom: 'collegata da {page}',
  pageAddedByYou: 'aggiunta manualmente',
  pageFromNote: 'citata in una nota',
  pageNoteOne: '1 nota',
  pageNotesMany: '{count} note',
  pageStatusOk: 'Aggiornata',
  pageStatusChanged: 'Modificata',
  pageStatusUnreadable: 'Non leggibile',
  pageStatusUnchecked: 'Non ancora controllata',
  pagesChangeWithChat: 'Modifica con Chat',
  pagesUpdateWithChat: 'Aggiorna le note con Chat',
  pagesPrompt:
    'Aiutami a cambiare quali pagine legge l’assistente per gli acquisti di {name} ({domain}) per le sue note. Mostrami le pagine che usa adesso e chiedimi quali aggiungere o rimuovere.',
  pagesUpdatePrompt:
    'Alcune pagine di {name} ({domain}) sono cambiate o non si possono più leggere da quando sono state create le note. Mostrami quali sono e rifai le note partendo da queste, come bozza che posso controllare.',
  viewCompare: 'Confronto',
  lookDeviceLabel: 'Confronta su',
  lookShop: 'Il tuo negozio',
  lookShopCaption: 'Pagina iniziale, letta {when}',
  lookWidget: 'L’assistente',
  lookWidgetCaption: 'Versione {version}',
  lookLoading: 'Posizionamento dell’assistente accanto al tuo negozio…',
  lookFailed: 'Al momento non è stato possibile creare il confronto.',
  lookNoShopPicture: 'La lettura non ha conservato un’immagine del tuo negozio a questa larghezza.',
  lookNoneTitle: 'Lo stile proprio del tuo negozio non è stato salvato',
  lookNoneBody:
    'Questa lettura non lo ha registrato. Leggi di nuovo il negozio da Configurazione e logo, colori e pagine verranno conservati per il confronto.',
  lookColoursShop: 'Colori usati dal tuo negozio',
  lookColoursAssistant: 'Colori dell’assistente',
  lookRoleHeader: 'Intestazione',
  lookRoleBrand: 'Marchio',
  lookSame: 'Uguale al colore {colour} del tuo negozio ({source})',
  lookOff: 'Vicino al colore {colour} del tuo negozio ({source}) ma non uguale, differenza {delta}',
  lookOwn: 'Colore proprio: il tuo negozio non lo usa',
  lookNeutral:
    'Il tuo negozio non è stato caricato per questo: viene mostrato solo ciò che la lettura ha conservato.',
  lookFromLogo: 'logo',
  lookFromTheme: 'colore del tema',
  lookFromTile: 'colore del riquadro',
  lookFromMask: 'icona fissata',
  lookFromManifest: 'manifest dell’app',
  lookFromHeader: 'intestazione',
  lookFromButton: 'pulsanti',
  lookFromVariable: 'colore dello stile',
  lookFix: 'Sistema con Chat',
  lookFixPrompt:
    'Il colore dell’assistente per {role} è {colour}, vicino al colore {exact} del tuo negozio ({source}) ma non uguale. Usa il colore esatto del tuo negozio, {exact}.',
  lookSwatch: 'Colore {colour}',
  errorDesignChecks: 'Questa versione non ha superato i controlli di progettazione.',
  designSamplesBlocked:
    'I layout delle schede non si possono dimostrare qui: il tuo sito blocca le visite automatiche. Colori, caratteri e logo funzionano comunque.',
  designCheckLabel: 'Un’etichetta IA è troppo sbiadita per essere letta.',
  designCheckReach: 'Un pulsante non è raggiungibile.',
  designCheckSize: 'Una parte del testo è troppo piccola.',
  designCheckOverflow: 'Il pannello scorre lateralmente.',
  designCheckRender: 'I controlli non hanno potuto essere eseguiti. Riprova tra un attimo.',
  fixDesignPrompt:
    'Ho provato a pubblicare l’ultima bozza di {name} ({domain}), ma non ha superato i controlli di progettazione: {facts}. Correggi il design in questa chat e verificalo su telefono e computer. Non pubblicarlo.',
};

const lv: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Iepirkšanās asistents',
  designUnconfirmed:
    'Apturēts, pirms Chat varēja apstiprināt, ka dizains ir saglabāts. Asistenta kanvā redzama jaunākā versija.',
  errorNewerVersion:
    'Kopš šīs lapas atvēršanas ir saglabāta jaunāka versija. Vispirms to apskati un tad publicē.',
  canvasSelectHint: 'Noklikšķini uz asistenta daļas, lai to mainītu čatā · Esc, lai apturētu',
  canvasDraft: 'Versija {version} · vēl nav publicēta',
  canvasDraftOverLive: 'Versija {version} · publicēta ir versija {live}',
  canvasLive: 'Versija {version} · publicēta',
  canvasNoVersion: 'Pirmā versija būs pieejama, kad veikala nolasīšana būs pabeigta',
  canvasPublish: 'Publicēt Storefront lapā',
  canvasFrameTitle: '{name} iepirkšanās asistents',
  viewLabel: 'Skats',
  photosGood: '{count} produktu fotoattēli: veseli un asi',
  photosGoodOne: 'Produkta fotoattēls ir vesels un ass',
  canvasMissingTitle: 'Šajā darbvietā šāda iepirkšanās asistenta nav',
  canvasMissingBody: 'Tas pieder citai darbvietai vai ir noņemts.',
  canvasMissingLink: 'Skatīt šīs darbvietas asistentus',
  photosLabel: 'Produktu fotoattēli',
  photosSmall: '{count} ir mazi savos elementos',
  photosCut: '{count} ir nogriezti',
  photosSoft: '{count} ir palielināti, tāpēc izplūduši',
  photosMissing: '{count} nav fotoattēla',
  photosFix: 'Labot ar Chat',
  photosPrompt:
    'Sakārto produktu fotoattēlus kartītēs: lai katrs būtu pilnībā redzams, pietiekami liels savā elementā un ass.',
  photosFacts:
    '{total} fotoattēli: {small} mazi savos elementos, {cut} nogriezti, {soft} palielināti, {missing} bez fotoattēla. Izmēri: {sizes}',
  viewDesign: 'Dizains',
  viewVersions: 'Versijas',
  versionsLoading: 'Notiek versiju ielāde…',
  versionsFailed: 'Versijas neizdevās ielādēt.',
  versionTitle: 'Versija {version}',
  versionLive: 'Publicēta',
  versionChecked: 'Pārbaudīta',
  versionToday: 'Šodien',
  versionYesterday: 'Vakar',
  versionEarlier: 'Agrāk',
  versionByPerson: 'Pieprasīja {name}',
  versionByChat: 'Pieprasīts čatā',
  versionFromReading: 'No {domain} nolasīšanas',
  versionPublishedBy: 'publicēja {name}, {at}',
  versionPublish: 'Publicēt',
  versionRestore: 'Atjaunot…',
  versionRestoreTitle: 'Atjaunot versiju {version}?',
  versionRestoreBody:
    'Tā kļūs par jaunu melnrakstu, versiju {next}. Tava vietne nemainīsies, kamēr to nepublicēsi.',
  versionRestoreConfirm: 'Atjaunot',
  versionRestoreCancel: 'Atcelt',
  versionRestored: 'Atjaunota kā versija {version}',
  versionRestoreFailed: 'Versiju neizdevās atjaunot.',
  compareTitle: 'Salīdzināt versiju {to} ar versiju {from}',
  compareTitleLive: 'Salīdzināt versiju {to} ar publicēto versiju {from}',
  compareHint: 'Velc, lai salīdzinātu',
  compareLabel: 'Salīdzināt versiju {from} un versiju {to}',
  compareTagLive: 'v{version} · Publicēta',
  compareTag: 'v{version}',
  changesTitle: 'Kas mainījās no versijas {from} līdz {to}',
  changesNone: 'Dizaini ir vienādi. Mainījās tikai piezīmes vai veikala dati.',
  changeAdded: 'Pievienots',
  changeRemoved: 'Noņemts',
  changeChanged: 'Mainīts',
  changeDrawn: 'uzzīmēts logotips',
  changeCss: 'paša CSS',
  changeYes: 'jā',
  changeNo: 'nē',
  'field.colors.brand': 'Zīmola krāsa',
  'field.colors.actions': 'Pogu krāsa',
  'field.colors.panel': 'Paneļa krāsa',
  'field.colors.surface': 'Virsmas krāsa',
  'field.colors.ink': 'Teksta krāsa',
  'field.colors.muted': 'Sekundārā teksta krāsa',
  'field.colors.line': 'Līniju krāsa',
  'field.colors.bubble': 'Pircēja ziņas krāsa',
  'field.colors.bubbleInk': 'Pircēja ziņas teksts',
  'field.colors.header': 'Galvenes krāsa',
  'field.colors.headerInk': 'Galvenes teksta krāsa',
  'field.colors.tints': 'Maigas nokrāsas',
  'field.copy.assistantName': 'Nosaukums',
  'field.copy.greeting': 'Sveiciens',
  'field.copy.placeholder': 'Jautājuma lauks',
  'field.copy.starters': 'Sākuma jautājumi',
  'field.copy.launcherLabel': 'Atvērēja vārdi',
  'field.copy.aiLabel': 'MI apzīmējums',
  'field.copy.personLabel': 'Saite pie personāla',
  'field.type.body': 'Teksta fonts',
  'field.type.display': 'Virsrakstu fonts',
  'field.type.fontCss': 'Fonta avots',
  'field.type.scale': 'Teksta lielums',
  'field.shape.radius': 'Stūri',
  'field.shape.shadow': 'Ēna',
  'field.shape.density': 'Atstarpes',
  'field.launcher.style': 'Atvērējs datoros',
  'field.launcher.phoneStyle': 'Atvērējs tālruņos',
  'field.launcher.side': 'Atvērēja puse',
  'field.launcher.icon': 'Atvērēja ikona',
  'field.panel.header': 'Galvenes izmērs',
  'field.panel.opening': 'Pirmais ekrāns',
  'field.products.layout': 'Produkti',
  'field.products.imageFit': 'Produktu fotoattēli',
  'field.products.imageRatio': 'Fotoattēla elementa forma',
  'field.products.imagePadding': 'Vieta ap fotoattēliem',
  'field.products.imageBackground': 'Fotoattēla elementa krāsa',
  'field.products.picker': 'Izmēra izvēle',
  'field.products.showStock': 'Rādīt krājumus',
  'field.cart.landing': 'Groza animācija',
  'field.logo.svg': 'Logotips',
  'field.logo.shape': 'Logotipa forma',
  'field.logo.plate': 'Logotipa fons',
  'field.css': 'Pielāgots CSS',
  'value.ink': 'tinte',
  'value.brand': 'zīmola krāsa',
  'value.small': 'mazs',
  'value.medium': 'vidējs',
  'value.large': 'liels',
  'value.square': 'kvadrāts',
  'value.soft': 'maigs',
  'value.round': 'apaļš',
  'value.flat': 'plakans',
  'value.deep': 'dziļš',
  'value.compact': 'kompakts',
  'value.comfortable': 'ērts',
  'value.pill': 'tablete ar vārdiem',
  'value.circle': 'aplis',
  'value.right': 'labajā pusē',
  'value.left': 'kreisajā pusē',
  'value.logo': 'logotips',
  'value.chat': 'čata burbulis',
  'value.sparkle': 'dzirksts',
  'value.bag': 'soma',
  'value.full': 'pilns',
  'value.middle': 'vidū',
  'value.bottom': 'apakšā',
  'value.rows': 'rindas',
  'value.grid': 'režģis',
  'value.carousel': 'rinda, ko vari vilkt',
  'value.contain': 'viss fotoattēls',
  'value.cover': 'aizpildīts elements',
  'value.portrait': 'portrets',
  'value.landscape': 'ainava',
  'value.none': 'nekas',
  'value.pop': '+1, kas paceļas',
  'value.ring': 'vilnis',
  'value.blink': 'mirgošana',
  'value.chips': 'pogas rindā',
  'value.menu': 'saraksts',
  'value.rounded': 'noapaļots',
  designCardTitle: '{name} iepirkšanās asistents',
  designCardSaved: 'Versija {version} saglabāta · vēl nav publicēta',
  designCardSavedFirst: 'Dizains saglabāts · pirmā versija to ietvers',
  designSaving: 'Notiek dizaina saglabāšana…',
  selectedAssistantPart: 'Atlasīts: asistenta daļa',
  loading: 'Notiek Storefront ielāde…',
  disabledTitle: 'Storefront šajā darbvietā nav ieslēgts.',
  disabledBody:
    'Tas ir agrīnajā piekļuvē un tiek ieviests pakāpeniski. Pajautā savai Poneva kontaktpersonai, kad tas būs pieejams jums.',
  loadFailed: 'Storefront neizdevās ielādēt.',
  startTitle: 'Iepirkšanās asistents tavam veikalam',
  startBody:
    'Poneva nolasa tavu veikalu tā, kā to izmanto pircējs: meklēšanu, produktus, grozu un esošo čatu. Tas aizņem aptuveni 10–30 minūtes un izmanto uzņēmuma kredītus; tu vari turpināt darbu.',
  urlLabel: 'Tava veikala adrese',
  urlPlaceholder: 'https://www.tavaveikala.lv',
  startButton: 'Nolasīt manu veikalu',
  starting: 'Notiek sākšana…',
  startFailed: 'Veikalu neizdevās nolasīt. Pārbaudi adresi un mēģini vēlreiz.',
  tabConversations: 'Sarunas',
  tabOverview: 'Pārskats',
  allAssistants: 'Visi asistenti',
  allAssistantsNote: '{assistants} asistenti · {sites} vietnes',
  allAssistantsMenuNote: '{count} sarunas šonedēļ',
  marketLabel: 'Tirgus',
  allMarkets: 'Visi tirgi',
  allMarketsNote: '{count} vietnes',
  scopeIn: '{assistant} tirgū {market}',
  askAbout: 'Jautāt Chat par {scope}',
  overviewAssistantsTitle: 'Asistenti un tirgi',
  overviewMarketsTitle: 'Tirgi',
  overviewAssistantsOnly: 'Asistenti',
  overviewIdle: '{count} asistenti, ar kuriem vēl nav sarunu',
  allAssistantsCount: '{assistants} asistenti',
  overviewFailed: 'Pārskatu neizdevās ielādēt.',
  colConversations: 'Sarunas',
  colCart: 'Pievienots grozam',
  colCheckout: 'Norēķins',
  colPerson: 'Persona',
  colCost: 'Kredīti',
  rowMarkets: '{count} tirgi',
  rowVersion: 'versija {version}',
  rowAria:
    '{name}, {sub}: {conversations} sarunas, {cart} pievienoti grozam, {checkout} sasniegts norēķins, {person} lūdza personu, kredīti {cost}',
  versionPublishedAt: 'publicēta {at}',
  designColorHeader: 'Galvene',
  designColorHeaderInk: 'Galvenes teksts',
  designGroupTranslations: 'Vārdi citās valodās',
  topicsTitle: 'Ko jautā pircēji',
  topicsNote: 'Tēmas, ko Chat atrada pirmajos jautājumos',
  startPagesTitle: 'Kur sākas sarunas',
  startPagesOne: 'Katra saruna sākās viena veida lapā: {page}.',
  'startPage.product': 'Produktu lapas',
  'startPage.search': 'Meklēšanas rezultāti',
  'startPage.start': 'Sākumlapa',
  'startPage.cart': 'Grozs',
  'startPage.other': 'Citas lapas',
  addMarketInChat: 'Pievienot tirgu čatā',
  marketsTitle: 'Tirgi',
  marketsBody:
    'Viens asistents un viens dizains. Katram tirgum ir sava valoda, valūta, produkti, grozs un čats saziņai ar cilvēku.',
  marketChecked: 'pārbaudīts {when}',
  'marketStatus.ready': 'Gatavs',
  'marketStatus.found': 'Nav pievienots',
  'marketStatus.checking': 'Notiek pārbaude',
  'marketStatus.failed': 'Nepieciešams labojums',
  addMarketPrompt:
    'Pievieno iepirkšanās asistentam {name} vēl vienu tirgu ({domain}): atrodi veikala citas valstis, valodas vai vietnes un pajautā man, kuru pievienot.',
  tabWidget: 'Widget',
  tabInstall: 'Instalēšana',
  tabSetup: 'Iestatīšana',
  statusReading: 'Notiek veikala nolasīšana',
  readingProducts: 'Produktus nolasa vēlreiz',
  readingCart: 'Grozs tiek pārbaudīts vēlreiz',
  readingChat: 'Tiek pārbaudīts ceļš uz tavu čatu',
  readingMarket: 'Tiek pievienots tirgus {market}',
  readingMarketAny: 'Tiek pievienots tirgus',
  statusReady: 'Gatavs instalēšanai',
  statusFailed: 'Nolasīšana apturēta',
  statusLive: 'Aktīvs · versija {version} vietnē {domain}',
  statusPublished: 'Publicēts · versija {version} · vēl nav tavā vietnē',
  statusPublishedUnverified: 'Publicēts · versija {version} · tavu vietni neizdevās pārbaudīt',
  stepOpen: 'Vietnes atvēršana',
  stepSearch: 'Meklēšana',
  stepProducts: 'Produkti',
  stepCart: 'Grozs',
  stepChat: 'Tavs esošais čats',
  stepLook: 'Tavs izskats un logotips',
  stepChecks: 'Darbības pārbaude',
  stepReady: 'Gatavs',
  stepDetailOpen: 'Tiek atvērts {domain}',
  stepDetailSearch: 'Tiek atrasti veikala produkti',
  stepDetailProducts: 'Tiek nolasīti produkti, cenas, fotoattēli, krāsas un izmēri',
  stepDetailCart: 'Tiek izmēģināts grozs: pievienošana, nolasīšana, noņemšana un iztukšošana',
  stepDetailChat: 'Tiek meklēts tavs esošais čats',
  stepDetailLook: 'Tiek nolasīts veikala logotips un krāsas',
  stepDetailChecks: 'Tiek izmēģināta pircēju meklēšana, produkti, pārlūkošana un grozs',
  stepDetailChecksProducts:
    'Tiek pārbaudīta produktu meklēšana, pārlūkošana, fotoattēli, krāsas un izmēri',
  stepDetailChecksCart:
    'Tiek pārbaudīta groza pievienošana, nolasīšana, noņemšana un iztukšošana, kā arī produktu nolasīšana',
  stepDetailChecksChat: 'Tiek pārbaudīta pāreja uz tavu esošo čatu un produktu nolasīšana',
  stepDetailChecksMarket: 'Tiek pārbaudīti šī tirgus produkti, cenas un grozs',
  nowRunning: 'Tagad: {step}',
  nowProgress: 'Solis {n} no {total} · {minutes} minūtes līdz šim',
  nowReady: 'Tavs veikals ir gatavs asistentam',
  nowReadyBody: 'Meklēšana, produkti un grozs darbojas.',
  nowLastRead: 'Pēdējo reizi nolasīts {when}',
  nowUsually: 'parasti {min}–{max}',
  stepsRead: 'Nolasīts {when} · Rādīt soļus',
  nowFailed: 'Nolasīšana apturēta',
  factAnswers: 'Atbild pircējiem ar',
  costCounting: 'Notiek skaitīšana…',
  factPaid: 'Apmaksāts no',
  paidCompany: 'Uzņēmuma kredītiem',
  creditsAmount: '{amount} kredīti',
  creditsOne: '{amount} kredīts',
  creditsUnder: 'mazāk nekā {amount} kredītu',
  stopRun: 'Apturēt palaišanu',
  stopping: 'Notiek apturēšana…',
  closeNote: 'Šo lapu vari aizvērt. Palaišana turpinās.',
  readyNote: 'Nākamais: izmēģini savu logrīku un tad instalē to.',
  buildWidget: 'Izmēģināt logrīku',
  readAgain: 'Nolasīt veikalu vēlreiz',
  foundTitle: 'Kas tika atrasts',
  foundProducts: 'Produkti un cenas nolasīti',
  foundSearch: 'Meklēšana darbojas',
  foundCart: 'Pievienošana grozam darbojas',
  foundChat: 'Tavs {vendor} čats',
  foundNoChat: 'Tava čata nav',
  foundLook: 'Tavas krāsas un logotips',
  widgetTitle: 'Tavs logrīks',
  widgetBody:
    'Tavs asistents tavās krāsās. Izmēģini to šeit: tas atbild ar taviem īstajiem produktiem. Nekas netiek pievienots īstam grozam.',
  widgetLoading: 'Notiek priekšskatījuma ielāde…',
  widgetFailed: 'Priekšskatījumu neizdevās ielādēt.',
  widgetNotReady: 'Logrīks būs gatavs, kad būs pabeigtas cilnē “Iestatīšana” norādītās darbības.',
  installLineTitle: 'Pievieno šo rindu savai vietnei',
  installLineBody:
    'Tas darbojas jebkurā platformā: lapu head sadaļā, Shopify vietnē theme.liquid failā tieši pirms </head>. Līdz publicēšanai nekas netiek rādīts.',
  installLineBodyLive:
    'Tas darbojas jebkurā platformā: lapu head sadaļā, Shopify vietnē theme.liquid failā tieši pirms </head>. Pircēji pēc tam redzēs versiju {version}.',
  installPolicyAsk: 'Vai tava vietne iestata Content Security Policy?',
  installPolicy:
    'Ja tava vietne iestata Content Security Policy, atļauj {origin} arī {directives}; citādi asistents nevarēs sākt darbu.',
  installPolicyRefuses:
    'Tavas vietnes Content Security Policy atsaka asistentu: atļauj {origin} sadaļā {directives}, citādi nekas nesāksies.',
  installPolicyNonce:
    'Tava politika palaiž tikai uzticamus skriptus, tāpēc pievieno rindu ar savu lapu nonce.',
  installPolicyEval: "Asistentam script-src sadaļā nepieciešams arī 'unsafe-eval'.",
  copy: 'Kopēt',
  copied: 'Nokopēts',
  domainsTitle: 'Vietnes, kurās tas atbild',
  domainsBody: 'Citās vietnēs tas klusē, pat ja kāds nokopē šo rindu.',
  lineFound: 'Rinda atrasta',
  lineNotSeen: 'Lapā vēl nav redzama',
  lineUnverified:
    'Pārbaudīt neizdevās: vietne bloķē automatizētus apmeklējumus. Asistents tāpat darbojas pircējiem.',
  lineChecked: 'pārbaudīts {when}',
  lineCheckAgain: 'Pārbaudīt vēlreiz',
  lineChecking: 'Notiek lapas pārbaude…',
  installCheckBusy: 'Pārbaude pašlaik ir aizņemta. Mēģiniet vēlreiz pēc brīža.',
  publishTitle: 'Publicēt',
  publishBody: 'Pircēji vietnē {domain} to redzēs uzreiz. Publikāciju vari atcelt jebkurā laikā.',
  publishButton: 'Publicēt versiju {version}',
  publishConfirm: 'Publicēt {name} versiju {version}?',
  publishNow: 'Publicēt',
  notNow: 'Ne tagad',
  publishing: 'Notiek publicēšana…',
  liveTitle: 'Publicēta: versija {version}',
  liveSince: 'Kopš {since}',
  unpublish: 'Atcelt publikāciju',
  unpublishConfirm:
    'Noņemt versiju {version} no savas vietnes? Pircēji asistentu vairs neredzēs, kamēr to atkal nepublicēsi.',
  publishedLine: 'Tagad publicēta: versija {version}, kopš {since}',
  usageTitle: 'Lietojums',
  usageBody: 'Tas izmanto uzņēmuma kredītus tāpat kā pārējais Chat.',
  usageDiscovery: 'Veikala nolasīšana',
  usageChats: 'Pircēju čati',
  usageChatsValue: '{count} čati · {cost}',
  last7: 'Pēdējās 7 dienas',
  askWeek: 'Jautāt Chat par šo nedēļu',
  kpiConversations: 'Sarunas',
  kpiVsPrevious: '{change} salīdzinājumā ar iepriekšējo nedēļu',
  kpiCart: 'Pievienots grozam',
  kpiCheckout: 'Sasniegts norēķins',
  kpiPerson: 'Lūgta persona',
  kpiCost: 'Izmantotie kredīti',
  ofConversations: '{percent} sarunu',
  checkoutNote: '{percent} sarunu',
  personNote: '{percent} atvēra tavu čatu',
  costNote: 'Apmēram {amount} par sarunu',
  costNoteUnder: 'Mazāk nekā {amount} par sarunu',
  sparkLabel: 'Sarunas dienā pēdējās {days} dienās',
  sparkDay: '{day}: {count} sarunas',
  filterAll: 'Visas',
  filterCart: 'Pievienots grozam',
  filterCheckout: 'Norēķins',
  filterPerson: 'Persona',
  outcomeCart: 'Pievienots grozam',
  outcomeCheckout: 'Sasniegts norēķins',
  outcomePerson: 'Lūgta persona',
  outcomeAnswered: 'Atbildēts',
  devicePhone: 'Tālrunis',
  deviceComputer: 'Dators',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} minūtes · Sākts lapā {page}',
  anonymous: 'Pircējs ir anonīms.',
  askConversation: 'Jautāt Chat par šo sarunu',
  showOlderVersions: 'Rādīt agrākas versijas',
  showOlder: 'Rādīt senākas sarunas',
  filterCountsWeek: 'Skaits: pēdējās 7 dienas',
  readAlreadyHere:
    'Šis veikals jau ir šeit, tāpēc nekas jauns netika sākts un kredīti netika izmantoti.',
  readOpenShop: 'Atvērt veikalu',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Ko mēs varam mācīties no šīs sarunas?',
  askWeekMessage: 'Ko mēs varam mācīties no šīs nedēļas sarunām?',
  conversationsChipOne: '{name} · saruna, {when}',
  conversationsChipWeek: '{name} · šonedēļ',
  conversationsChipWeekIn: '{name} · {market} · šonedēļ',
  removeConversations: 'Noņemt sarunas',
  noConversations: 'Vēl nav sarunu',
  noConversationsBody: 'Kad pircēji izmantos asistentu, viņu sarunas būs redzamas šeit.',
  pickConversation: 'Izvēlies sarunu, ko lasīt.',
  eventCheckout: 'Devās uz norēķinu',
  eventPerson: 'Atvēra tavu čatu',
  showMore: 'Rādīt vairāk',
  urlInvalid: 'Izmanto sava veikala https adresi, piemēram, https://www.tavaveikala.lv.',
  errorNoCompany:
    'Storefront nepieciešama uzņēmuma darbvieta. Lūdz administratoram tevi pievienot.',
  errorPublishPermission:
    'To var darīt tikai cilvēki, kuri drīkst publicēt uzņēmuma vārdā. Lūdz administratoram.',
  actionFailed: 'Tas neizdevās. Mēģini vēlreiz.',
  copyFailed: 'Neizdevās nokopēt. Atlasi rindu un nokopē to pats.',
  reasonCancelled: 'Tu apturēji palaišanu.',
  reasonFailed: 'Tava veikala nolasīšanu neizdevās pabeigt, tāpēc asistents vēl nav gatavs.',
  reasonFailedLive:
    'Tava veikala nolasīšanu neizdevās pabeigt. Publicētais asistents turpina darboties kā iepriekš.',
  reasonFailedKept:
    'Tava veikala nolasīšanu neizdevās pabeigt, tāpēc nekas nemainījās: versija {version} paliek spēkā.',
  statusReadStopped: 'Gatavs · pēdējā nolasīšana apturēta',
  reasonStepMarked: 'Apturētais solis ir atzīmēts zemāk.',
  reasonReadAgain: 'Atkārtota nolasīšana sāksies ar to, ko šī nolasīšana uzzināja.',
  reasonNotAStore:
    'Šī adrese neizskatās pēc tīmekļa veikala: nebija produktu, ko meklēt. Pārbaudi adresi vai izmēģini veikala galveno domēnu.',
  reasonTooSlow:
    'Veikala meklēšana atbild pārāk lēni iepirkšanās asistentam: tā aizņēma vairāk nekā 12 sekundes. Var palīdzēt ātrāka meklēšana veikalā vai vēlāk atkārtota nolasīšana.',
  reasonOurSide:
    'Nolasīšana apstājās mūsu puses problēmas dēļ, nevis tava veikala dēļ, tāpēc asistents vēl nav gatavs.',
  reasonOurSideLive:
    'Nolasīšana apstājās mūsu puses problēmas dēļ, nevis tava veikala dēļ. Publicētais asistents turpina darboties kā iepriekš.',
  reasonOurSideKept:
    'Nolasīšana apstājās mūsu puses problēmas dēļ, nevis tava veikala dēļ, tāpēc nekas nemainījās: versija {version} paliek spēkā.',
  reasonTryLater: 'Mēģini veikalu nolasīt vēlreiz pēc brīža.',
  errorNoCredits:
    'Šim pašlaik nav pieejamu kredītu. Uzņēmuma īpašnieks var pārbaudīt plānu un limitus sadaļā Usage.',
  errorReadUnavailable: 'Šo veikalu pašlaik nevar nolasīt. Mēģini vēlreiz pēc dažām minūtēm.',
  reasonTimeout:
    'Nolasīšana sasniedza laika limitu, pirms tika pabeigta. Atkārtota nolasīšana turpinās no tā, ko tā jau pārbaudīja.',
  retrying: 'Notiek atkārtota sākšana…',
  readTitleChecks: 'Pārbaudīt saglabāto nolasīšanu vēlreiz',
  checksScopeLabel: 'Pārbauda',
  readScopeChecks:
    'Pircēju meklēšana, produkti, pārlūkošana un grozs – tam, ko saglabāja pēdējā nolasīšana. Veikals netiek nolasīts no jauna.',
  readScopeChecksProducts:
    'Produktu meklēšana, pārlūkošana, fotoattēli, krāsas un izmēri – tam, ko saglabāja pēdējā nolasīšana. Veikals netiek nolasīts no jauna.',
  readScopeChecksCart:
    'Groza pievienošana, nolasīšana, noņemšana un iztukšošana, kā arī produktu nolasīšana – tam, ko saglabāja pēdējā nolasīšana. Veikals netiek nolasīts no jauna.',
  readScopeChecksChat:
    'Pāreja uz tavu esošo čatu un produktu nolasīšana – tam, ko saglabāja pēdējā nolasīšana. Veikals netiek nolasīts no jauna.',
  readScopeChecksMarket:
    'Tirgus produkti, cenas un grozs – tam, ko saglabāja pēdējā nolasīšana. Veikals netiek nolasīts no jauna.',
  checksCost: 'Uzņēmuma kredītus izmanto tikai jaunās pārbaudes',
  checksAfter:
    'Saglabā jaunu melnrakstu, ja pārbaudes ir sekmīgas. Pircēji neko neredzēs, kamēr to nepublicēsi.',
  checksAfterLive:
    'Saglabā jaunu melnrakstu, ja pārbaudes ir sekmīgas. Publicētais asistents turpina darboties, un pircēji izmaiņas redzēs tikai pēc publicēšanas.',
  checksStarted: 'Pārbaudes ir sāktas. Kad jaunais melnraksts būs gatavs, lūdz Chat to pārbaudīt.',
  checkingSavedWork: 'Notiek saglabātā darba atkārtota pārbaude',
  checkAgain: 'Pārbaudīt vēlreiz',
  checkingAgain: 'Notiek atkārtota pārbaude…',
  reasonCheckAgain:
    '“Pārbaudīt vēlreiz” vēlreiz pārbauda saglabāto darbu, veikalu no jauna nenolasot. Uzņēmuma kredītus izmanto tikai jaunās pārbaudes.',
  fixWithChat: 'Labot ar Chat',
  fixReadPrompt:
    'Veikala {name} ({domain}) nolasīšana apstājās. Apskati, kas notika, un palīdzi man to salabot: pastāsti, ko atrodi, iesaki, ko es varētu pateikt nolasīšanai, un tad pārbaudi to vēlreiz vai nolasi no jauna ar šo padomu, nolasot pēc iespējas mazāku veikala daļu.',
  fixReadPromptStep:
    'Veikala {name} ({domain}) nolasīšana apstājās solī “{step}”. Apskati, kas notika, un palīdzi man to salabot: pastāsti, ko atrodi, iesaki, ko es varētu pateikt nolasīšanai, un tad pārbaudi to vēlreiz vai nolasi no jauna ar šo padomu, nolasot pēc iespējas mazāku veikala daļu.',
  stepStateDone: 'pabeigts',
  stepStateRunning: 'notiek',
  stepStateWaiting: 'nav sākts',
  stepStateFailed: 'apturēts',
  newVersionTitle: 'Versija {version} ir gatava',
  newVersionBody: 'Publicē to, lai aizstātu versiju {live}.',
  newVersionBodyLive: 'Publicē to, lai vietnē {domain} aizstātu versiju {live}.',
  previewSize: 'Priekšskatījuma izmērs',
  designWithChat: 'Atvērt dizainu',
  designReviewBody: 'Apskatiet pašreizējo dizainu un pastāstiet Chat, ko vēlaties pielāgot.',
  designWhileTitle: 'Izveido dizainu, kamēr mēs nolasām tavu veikalu',
  designWhileBody:
    'Chat tagad kopā ar tevi var veidot asistenta izskatu, vārdus un logotipu. Dizains būs gatavs, kad būs pabeigta nolasīšana.',
  designPreviewNote: 'Priekšskatījums ar piemēra produktiem, līdz tavs veikals ir nolasīts.',
  proposedDesign: 'Piedāvātais dizains',
  proposedDesignNote:
    'Logrīks ar šīm izmaiņām un piemēra produktiem. Apstiprināšana to saglabā kā jaunu melnrakstu; publicēšana paliek Storefront lapā.',
  lineSources: 'No veikala lapām:',
  readAgainConfirm:
    'Nolasīt {name} vēlreiz? Tas aizņem aptuveni 10–30 minūtes un izmanto uzņēmuma kredītus.',
  readAgainLive: 'Publicētais asistents turpina darboties, līdz publicēsi jauno versiju.',
  readAgainNow: 'Nolasīt vēlreiz',
  switchStore: 'Mainīt veikalu vai pievienot veikalu',
  storesLabel: 'Tavi veikali',
  addStore: 'Pievienot vēl vienu veikalu',
  backToStores: 'Atgriezties pie taviem veikaliem',
  storeLive: 'Publicēts',
  storeReady: 'Gatavs',
  storeReading: 'Notiek nolasīšana',
  storeStopped: 'Apturēts',
  actionDesignTitle: 'Mainīt iepirkšanās asistenta dizainu',
  actionKnowledgeTitle: 'Mainīt, ko iepirkšanās asistents zina',
  readTitleProducts: 'Nolasīt produktus vēlreiz',
  readTitleCart: 'Pārbaudīt grozu vēlreiz',
  readTitleTheirChat: 'Pārbaudīt ceļu uz tavu čatu vēlreiz',
  readTitleAll: 'Nolasīt visu veikalu vēlreiz',
  readTitleStore: 'Nolasīt jaunu veikalu',
  readTitleMarket: 'Pievienot tirgu',
  readShopLabel: 'Veikals',
  readScopeLabel: 'Nolasa',
  readTimeLabel: 'Ilgst',
  readCostLabel: 'Izmaksas',
  readAfterLabel: 'Pēc tam',
  readScopeProducts: 'Produktu meklēšana un kārtošana, produktu fotoattēli, krāsas un izmēri',
  readScopeCart: 'Pievienošana grozam un noņemšana no tā',
  readScopeTheirChat: 'Kā pircēji sasniedz tavu čatu',
  readScopeAll: 'Produkti, grozs, tavs čats un tavi tirgi',
  readScopeStore: 'Produkti, cenas un izmēri, grozs, tavs čats, tavs izskats un tavi tirgi',
  readScopeMarket: '{market}: tā produkti, cenas un grozs',
  readTime: 'Parasti {min}–{max} minūtes',
  readCost: 'Izmanto uzņēmuma kredītus',
  readCostLast: 'Izmanto uzņēmuma kredītus · veikala pēdējā nolasīšana izmantoja {credits}',
  readCostLastPartial:
    'Izmanto uzņēmuma kredītus · veikala pēdējā, agri apturētā nolasīšana izmantoja vismaz {credits}',
  readAfter: 'Saglabā jaunu melnrakstu. Pircēji neko neredzēs, kamēr to nepublicēsi.',
  readAfterLive:
    'Saglabā jaunu melnrakstu. Publicētais asistents turpina darboties, un pircēji izmaiņas redzēs tikai pēc publicēšanas.',
  readAfterNew: 'Saglabā pirmo melnrakstu izmēģināšanai. Nekas netiek publicēts.',
  readCartNote:
    'Lai pārbaudītu grozu, tas pievieno testa preci un pēc tam to noņem. Norēķins nekad netiek veikts.',
  readFocusLabel: 'Ko Chat lūdza nolasīšanai labot',
  readFooter:
    'Nekas nesāksies, kamēr to neapstiprināsi. Vari apturēt nolasīšanu cilnē “Iestatīšana”.',
  readStartedAt:
    'Sākts {time} · parasti {min}–{max} minūtes. Kad jaunais melnraksts būs gatavs, lūdz Chat to pārbaudīt.',
  readFocusAll: 'Rādīt visu',
  readFocusLess: 'Rādīt mazāk',
  readStart: 'Sākt nolasīšanu',
  addToSite: 'Pievienot vietnei',
  readTab: 'Pieprasījums',
  readStarted: 'Nolasīšana sākta. Kad jaunais melnraksts būs gatavs, lūdz Chat to pārbaudīt.',
  readFollow: 'Sekot cilnē “Iestatīšana”',
  proposedDesignLive:
    'Tavs veikala īstais priekšskatījums šajā dizainā. Uzdod tam jautājumu: nekas netiek pievienots īstam grozam. Apstiprināšana to saglabā kā jaunu melnrakstu; publicēšana paliek Storefront lapā.',
  designChanges: 'Kas mainās',
  designReset: 'atgriezties pie noklusējuma',
  designGroupColors: 'Krāsas',
  designGroupType: 'Fonti',
  designGroupShape: 'Forma',
  designGroupLauncher: 'Poga lapā',
  designGroupPanel: 'Galvene',
  designGroupProducts: 'Produkti',
  designGroupCart: 'Veikala grozs',
  designGroupCopy: 'Vārdi',
  designGroupLogo: 'Logotips',
  designGroupCss: 'Pielāgots CSS',
  designColorBrand: 'Zīmols',
  designColorActions: 'Pievienot un sūtīt',
  designColorPanel: 'Panelis',
  designColorSurface: 'Virsmas',
  designColorInk: 'Teksts',
  designColorMuted: 'Sekundārais teksts',
  designColorLine: 'Līnijas',
  designColorBubble: 'Pircēja ziņas',
  designColorBubbleInk: 'Pircēja teksts',
  designActionsBrand: 'Zīmola krāsa',
  designActionsInk: 'Teksta krāsa',
  designWordsName: 'Nosaukums',
  designWordsGreeting: 'Sveiciens',
  designWordsStarters: 'Sākuma jautājumi',
  designWordsLauncher: 'Pogas apzīmējums',
  designWordsPlaceholder: 'Vietturis',
  designWordsAi: 'MI apzīmējums',
  designWordsPerson: 'Saite pie personāla',
  noteNew: 'Jauns',
  noteChanged: 'Mainīts',
  noteRemoved: 'Noņemts',
  noteSourceKnowledge: 'Zināšanu lapa: {title}',
  noteSourceFile: 'Fails: {name}',
  noteSourceChat: 'No čata',
  knowledgeApprovalNote:
    'Pircēji var izlasīt visu šo piezīmju saturu. Apstiprināšana saglabā jaunu melnrakstu: uzdod tam jautājumus cilnē Widget un pēc tam publicē cilnē “Instalēšana”.',
  profileTitle: 'Par veikalu',
  profileChecked: 'Pārbaudīts {date}',
  profileSources: 'Uzrakstīts no',
  profileSample: '{count} produkti atlasīti cenu līmenim',
  profileSampleOne: '{count} produkts atlasīts cenu līmenim',
  profileAssistant: 'Ko asistents šeit var darīt',
  profileTeam: 'Uzrakstījusi jūsu komanda',
  profileEmpty:
    'Vēl nav uzrakstīts. Palūdziet Chat palaist zināšanu pārskatu: tas to uzraksta no jūsu pašu lapām un tā, ko redzēja jūsu vietnē, un jūs varat pievienot savas rindas.',
  profileWithChat: 'Pievienot vai mainīt ar Chat',
  profilePrompt:
    'Palīdzi man uzlabot to, ko iepirkšanās asistents veikalam {name} ({domain}) zina par mūsu veikalu kopumā: ko mēs pārdodam, ar ko esam pazīstami, kam mēs esam un ko pircēji šeit var darīt. Vispirms izlasi profilu „Par veikalu“ un pasaki, kā trūkst vai kas ir nepareizi. Viss, ko par veikalu pastāstu un ko neviena lapa nenorāda, tiek ierakstīts kā mūsu komandas rinda.',
  profilePartNavigation: 'Nodaļas',
  profilePartWelcome: 'Sveiciens',
  profilePartMarkets: 'Tirgi',
  profilePartFunctions: 'Ko asistents var darīt',
  profilePartProducts: 'Produkti',
  profilePartPages: 'Apkalpošanas lapas',
  profileCleared: 'Notīra uzrakstīto profilu',
  knowledgeTitle: 'Ko tas zina',
  knowledgeBody:
    'Papildus produktiem, cenām un krājumiem, ko tas vienmēr nolasa tieši no tava veikala.',
  knowledgeEmpty:
    'Pagaidām tikai tavs katalogs. Pievieno informāciju par piegādi, atgriešanu vai apmaksu ar Chat no savām lapām vai Zināšanām.',
  knowledgeWithChat: 'Pievienot ar Chat',
  knowledgeChangeWithChat: 'Mainīt ar Chat',
  knowledgePrompt:
    'Palīdzi man izvēlēties, ko {name} iepirkšanās asistents ({domain}) pircējiem stāsta papildus mūsu produktiem, piemēram, par piegādi, atgriešanu un apmaksu. Atrodi to mūsu lapās vai Zināšanās un piedāvā piezīmes, ko varu apstiprināt.',
  pagesTitle: 'Lapas, ko tas lasa',
  pagesBody:
    'Lapas, no kurām radušās piezīmes. Poneva tās pārbauda katru dienu, un Chat paziņo, kad kāda mainās.',
  pagesEmpty:
    'Vēl nav lapu. Šeit parādās lapas, ko atrod veikala lasīšana, un tās, ko nosauc Chat.',
  pageFound: 'Poneva atrasta',
  pageLinkedFrom: 'saistīts no {page}',
  pageAddedByYou: 'pievienota manuāli',
  pageFromNote: 'minēta piezīmē',
  pageNoteOne: '1 piezīme',
  pageNotesMany: '{count} piezīmes',
  pageStatusOk: 'Atjaunota',
  pageStatusChanged: 'Mainīta',
  pageStatusUnreadable: 'Nevar nolasīt',
  pageStatusUnchecked: 'Vēl nav pārbaudīta',
  pagesChangeWithChat: 'Mainīt ar Chat',
  pagesUpdateWithChat: 'Atjaunot piezīmes ar Chat',
  pagesPrompt:
    'Palīdzi man mainīt, kuras lapas veikala iepirkšanās asistents {name} ({domain}) lasa savām piezīmēm. Parādi man lapas, ko tas izmanto tagad, un pajautā, kuras pievienot vai noņemt.',
  pagesUpdatePrompt:
    'Dažas {name} ({domain}) lapas ir mainījušās vai vairs nevar tikt nolasītas kopš piezīmju izveides. Parādi man, kuras tās ir, un izveido no tām piezīmes no jauna kā melnrakstu, ko varu pārskatīt.',
  viewCompare: 'Salīdzinājums',
  lookDeviceLabel: 'Salīdzināt ierīcē',
  lookShop: 'Tavs veikals',
  lookShopCaption: 'Sākumlapa, nolasīta {when}',
  lookWidget: 'Asistents',
  lookWidgetCaption: 'Versija {version}',
  lookLoading: 'Asistents tiek novietots blakus tavam veikalam…',
  lookFailed: 'Salīdzinājumu šobrīd neizdevās izveidot.',
  lookNoShopPicture: 'Nolasīšanas laikā netika saglabāts tava veikala attēls šādā platumā.',
  lookNoneTitle: 'Tava veikala paša izskats netika saglabāts',
  lookNoneBody:
    'Šī nolasīšana to neierakstīja. Nolasi veikalu vēlreiz sadaļā Iestatīšana, un tā logotips, krāsas un lapas tiks saglabātas salīdzināšanai.',
  lookColoursShop: 'Krāsas, ko lieto tavs veikals',
  lookColoursAssistant: 'Asistenta krāsas',
  lookRoleHeader: 'Galvene',
  lookRoleBrand: 'Zīmols',
  lookSame: 'Tāda pati kā tava veikala krāsa {colour} ({source})',
  lookOff: 'Tuvu tava veikala krāsai {colour} ({source}), bet ne tādai pašai, atšķirība {delta}',
  lookOwn: 'Sava krāsa: tavs veikals to nelieto',
  lookNeutral: 'Tavs veikals šim netika ielādēts: tiek rādīts tikai tas, ko saglabāja nolasīšana.',
  lookFromLogo: 'logotips',
  lookFromTheme: 'motīva krāsa',
  lookFromTile: 'elementa krāsa',
  lookFromMask: 'piesprausta ikona',
  lookFromManifest: 'lietotnes manifests',
  lookFromHeader: 'galvene',
  lookFromButton: 'pogas',
  lookFromVariable: 'stila krāsa',
  lookFix: 'Labot ar Chat',
  lookFixPrompt:
    'Asistenta krāsa ({role}) ir {colour}, tuvu tava veikala krāsai {exact} ({source}), bet ne tādai pašai. Lūdzu, izmanto tava veikala precīzo krāsu {exact}.',
  lookSwatch: 'Krāsa {colour}',
  errorDesignChecks: 'Šī versija neizturēja dizaina pārbaudes.',
  designSamplesBlocked:
    'Kartīšu izkārtojumu šeit nevar pierādīt: tava vietne bloķē automatizētus apmeklējumus. Krāsas, fonti un logotips tāpat darbojas.',
  designCheckLabel: 'MI etiķete ir pārāk blāva, lai to varētu lasīt.',
  designCheckReach: 'Poga ir nesasniedzama.',
  designCheckSize: 'Daļa teksta ir pārāk maza.',
  designCheckOverflow: 'Panelis ritinās uz sāniem.',
  designCheckRender: 'Pārbaudes nevarēja palaist. Mēģiniet vēlreiz pēc brīža.',
  fixDesignPrompt:
    'Es mēģināju publicēt jaunāko {name} ({domain}) melnrakstu, taču tas neizturēja dizaina pārbaudes: {facts}. Izlabojiet dizainu šajā tērzēšanā un pārbaudiet to tālrunī un datorā. Nepublicējiet to.',
};

const lt: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Pirkinių asistentas',
  designUnconfirmed:
    'Sustabdyta, kol Chat nepatvirtino, kad dizainas išsaugotas. Asistento drobėje rodoma naujausia jo versija.',
  errorNewerVersion:
    'Nuo tada, kai atvėrėte šį puslapį, išsaugota naujesnė versija. Pirmiausia ją peržiūrėkite, tada publikuokite.',
  canvasSelectHint: 'Spustelėkite asistento dalį, kad pakeistumėte ją per Chat · Esc sustabdyti',
  canvasDraft: 'Versija {version} · dar nepublikuota',
  canvasDraftOverLive: 'Versija {version} · publikuota versija {live}',
  canvasLive: 'Versija {version} · publikuota',
  canvasNoVersion: 'Pirmoji versija atsiras baigus skaityti parduotuvę',
  canvasPublish: 'Publikuoti Storefront puslapyje',
  canvasFrameTitle: '{name} pirkinių asistentas',
  viewLabel: 'Peržiūra',
  photosGood: '{count} produktų nuotraukų: neapkirptos ir ryškios',
  photosGoodOne: 'Produkto nuotrauka neapkirpta ir ryški',
  canvasMissingTitle: 'Šio pirkinių asistento nėra šioje darbo srityje',
  canvasMissingBody: 'Jis priklauso kitai darbo sričiai arba buvo pašalintas.',
  canvasMissingLink: 'Peržiūrėti šios darbo srities asistentus',
  photosLabel: 'Produktų nuotraukos',
  photosSmall: '{count} per mažos savo langeliuose',
  photosCut: '{count} nukirptos',
  photosSoft: '{count} padidintos, todėl neryškios',
  photosMissing: '{count} be nuotraukos',
  photosFix: 'Taisyti per Chat',
  photosPrompt:
    'Sutvarkykite produktų nuotraukas kortelėse: kiekviena turi būti visa, pakankamai didelė savo langelyje ir ryški.',
  photosFacts:
    '{total} nuotraukos: {small} per mažos savo langeliuose, {cut} nukirptos, {soft} padidintos, {missing} be nuotraukos. Dydžiai: {sizes}',
  viewDesign: 'Dizainas',
  viewVersions: 'Versijos',
  versionsLoading: 'Įkeliamos versijos…',
  versionsFailed: 'Versijų įkelti nepavyko.',
  versionTitle: 'Versija {version}',
  versionLive: 'Publikuota',
  versionChecked: 'Patikrinta',
  versionToday: 'Šiandien',
  versionYesterday: 'Vakar',
  versionEarlier: 'Anksčiau',
  versionByPerson: 'Paprašė {name}',
  versionByChat: 'Paprašyta per Chat',
  versionFromReading: 'Iš {domain} skaitymo',
  versionPublishedBy: 'publikavo {name}, {at}',
  versionPublish: 'Publikuoti',
  versionRestore: 'Atkurti…',
  versionRestoreTitle: 'Atkurti versiją {version}?',
  versionRestoreBody:
    'Ji taps versija {next}, nauju juodraščiu. Jūsų svetainė nepasikeis, kol nepublikuosite.',
  versionRestoreConfirm: 'Atkurti',
  versionRestoreCancel: 'Atšaukti',
  versionRestored: 'Atkurta kaip versija {version}',
  versionRestoreFailed: 'Versijos atkurti nepavyko.',
  compareTitle: 'Palyginti versiją {to} su versija {from}',
  compareTitleLive: 'Palyginti versiją {to} su publikuota versija {from}',
  compareHint: 'Vilkite, kad palygintumėte',
  compareLabel: 'Palyginti versijas {from} ir {to}',
  compareTagLive: 'v{version} · Publikuota',
  compareTag: 'v{version}',
  changesTitle: 'Kas pasikeitė nuo versijos {from} iki {to}',
  changesNone: 'Dizainai vienodi. Pasikeitė tik pastabos arba parduotuvės duomenys.',
  changeAdded: 'Pridėta',
  changeRemoved: 'Pašalinta',
  changeChanged: 'Pakeista',
  changeDrawn: 'nupieštas logotipas',
  changeCss: 'savas CSS',
  changeYes: 'taip',
  changeNo: 'ne',
  'field.colors.brand': 'Prekės ženklo spalva',
  'field.colors.actions': 'Mygtuko spalva',
  'field.colors.panel': 'Skydelio spalva',
  'field.colors.surface': 'Fono spalva',
  'field.colors.ink': 'Teksto spalva',
  'field.colors.muted': 'Pritildyto teksto spalva',
  'field.colors.line': 'Linijos spalva',
  'field.colors.bubble': 'Pirkėjo žinutės spalva',
  'field.colors.bubbleInk': 'Pirkėjo žinutės tekstas',
  'field.colors.header': 'Antraštės spalva',
  'field.colors.headerInk': 'Antraštės teksto spalva',
  'field.colors.tints': 'Švelnūs atspalviai',
  'field.copy.assistantName': 'Pavadinimas',
  'field.copy.greeting': 'Pasisveikinimas',
  'field.copy.placeholder': 'Klausimo laukelis',
  'field.copy.starters': 'Pradžios klausimai',
  'field.copy.launcherLabel': 'Paleidimo mygtuko žodžiai',
  'field.copy.aiLabel': 'DI žyma',
  'field.copy.personLabel': 'Nuoroda į žmogų',
  'field.type.body': 'Teksto šriftas',
  'field.type.display': 'Antraščių šriftas',
  'field.type.fontCss': 'Šrifto šaltinis',
  'field.type.scale': 'Teksto dydis',
  'field.shape.radius': 'Kampai',
  'field.shape.shadow': 'Šešėlis',
  'field.shape.density': 'Tarpai',
  'field.launcher.style': 'Paleidimo mygtukas kompiuteriuose',
  'field.launcher.phoneStyle': 'Paleidimo mygtukas telefonuose',
  'field.launcher.side': 'Paleidimo mygtuko pusė',
  'field.launcher.icon': 'Paleidimo mygtuko piktograma',
  'field.panel.header': 'Antraštės dydis',
  'field.panel.opening': 'Pirmasis ekranas',
  'field.products.layout': 'Produktai',
  'field.products.imageFit': 'Produktų nuotraukos',
  'field.products.imageRatio': 'Nuotraukos langelio forma',
  'field.products.imagePadding': 'Vieta aplink nuotraukas',
  'field.products.imageBackground': 'Nuotraukos langelio spalva',
  'field.products.picker': 'Dydžio pasirinkimas',
  'field.products.showStock': 'Rodomas likutis',
  'field.cart.landing': 'Krepšelio animacija',
  'field.logo.svg': 'Logotipas',
  'field.logo.shape': 'Logotipo forma',
  'field.logo.plate': 'Logotipo fonas',
  'field.css': 'Pasirinktinis CSS',
  'value.ink': 'rašalas',
  'value.brand': 'prekės ženklo spalva',
  'value.small': 'mažas',
  'value.medium': 'vidutinis',
  'value.large': 'didelis',
  'value.square': 'kvadratinis',
  'value.soft': 'švelnus',
  'value.round': 'apvalus',
  'value.flat': 'plokščias',
  'value.deep': 'gilus',
  'value.compact': 'glaustas',
  'value.comfortable': 'erdvus',
  'value.pill': 'pailgas su žodžiais',
  'value.circle': 'apskritas',
  'value.right': 'dešinėje',
  'value.left': 'kairėje',
  'value.logo': 'logotipas',
  'value.chat': 'pokalbio burbulas',
  'value.sparkle': 'žėrėjimas',
  'value.bag': 'krepšys',
  'value.full': 'visa',
  'value.middle': 'viduryje',
  'value.bottom': 'apačioje',
  'value.rows': 'eilutės',
  'value.grid': 'tinklelis',
  'value.carousel': 'perbraukiama eilė',
  'value.contain': 'visa nuotrauka',
  'value.cover': 'užpildytas langelis',
  'value.portrait': 'portretinė',
  'value.landscape': 'gulsčia',
  'value.none': 'jokia',
  'value.pop': 'kylantis +1',
  'value.ring': 'banga',
  'value.blink': 'mirksėjimas',
  'value.chips': 'mygtukai eilėje',
  'value.menu': 'sąrašas',
  'value.rounded': 'suapvalintas',
  designCardTitle: '{name} pirkinių asistentas',
  designCardSaved: 'Versija {version} išsaugota · dar nepublikuota',
  designCardSavedFirst: 'Dizainas išsaugotas · pirmoji versija jį perims',
  designSaving: 'Dizainas išsaugomas…',
  selectedAssistantPart: 'Pasirinkta: asistento dalis',
  loading: 'Įkeliamas Storefront…',
  disabledTitle: 'Storefront šioje darbo srityje neįjungtas.',
  disabledBody:
    'Jis teikiamas ankstyvąja prieiga ir diegiamas etapais. Paklauskite savo Poneva kontaktinio asmens, kada jis bus prieinamas jums.',
  loadFailed: 'Storefront įkelti nepavyko.',
  startTitle: 'Pirkinių asistentas jūsų parduotuvei',
  startBody:
    'Poneva perskaito jūsų parduotuvę taip, kaip ja naudojasi pirkėjas: paiešką, produktus, krepšelį ir jau turimą pokalbį. Tai trunka apie 10–30 minučių, naudoja jūsų įmonės kreditus, o jūs galite toliau dirbti.',
  urlLabel: 'Jūsų parduotuvės adresas',
  urlPlaceholder: 'https://www.jusuparduotuve.lt',
  startButton: 'Skaityti mano parduotuvę',
  starting: 'Pradedama…',
  startFailed: 'Šios parduotuvės perskaityti nepavyko. Patikrinkite adresą ir bandykite dar kartą.',
  tabConversations: 'Pokalbiai',
  tabOverview: 'Apžvalga',
  allAssistants: 'Visi asistentai',
  allAssistantsNote: '{assistants} asistentai · {sites} svetainės',
  allAssistantsMenuNote: '{count} pokalbių šią savaitę',
  marketLabel: 'Rinka',
  allMarkets: 'Visos rinkos',
  allMarketsNote: '{count} svetainės',
  scopeIn: '{assistant} rinkoje {market}',
  askAbout: 'Paklausti Chat apie {scope}',
  overviewAssistantsTitle: 'Asistentai ir rinkos',
  overviewMarketsTitle: 'Rinkos',
  overviewAssistantsOnly: 'Asistentai',
  overviewIdle: '{count} asistentų dar neturi pokalbių',
  allAssistantsCount: '{assistants} asistentai',
  overviewFailed: 'Apžvalgos įkelti nepavyko.',
  colConversations: 'Pokalbiai',
  colCart: 'Įdėta į krepšelį',
  colCheckout: 'Atsiskaitymas',
  colPerson: 'Žmogus',
  colCost: 'Kreditai',
  rowMarkets: '{count} rinkos',
  rowVersion: 'versija {version}',
  rowAria:
    '{name}, {sub}: {conversations} pokalbių, {cart} įdėta į krepšelį, {checkout} pasiektas atsiskaitymas, {person} paprašė žmogaus, kreditai {cost}',
  versionPublishedAt: 'publikuota {at}',
  designColorHeader: 'Antraštė',
  designColorHeaderInk: 'Antraštės tekstas',
  designGroupTranslations: 'Žodžiai kitomis kalbomis',
  topicsTitle: 'Ko klausia pirkėjai',
  topicsNote: 'Temos, kurias Chat rado pirmuosiuose klausimuose',
  startPagesTitle: 'Kur prasideda pokalbiai',
  startPagesOne: 'Kiekvienas pokalbis prasidėjo vieno tipo puslapyje: {page}.',
  'startPage.product': 'Produktų puslapiai',
  'startPage.search': 'Paieškos rezultatai',
  'startPage.start': 'Pradinis puslapis',
  'startPage.cart': 'Krepšelis',
  'startPage.other': 'Kiti puslapiai',
  addMarketInChat: 'Pridėti rinką per Chat',
  marketsTitle: 'Rinkos',
  marketsBody:
    'Vienas asistentas ir vienas dizainas. Kiekviena rinka turi savo kalbą, valiutą, produktus, krepšelį ir pokalbį su žmogumi.',
  marketChecked: 'patikrinta {when}',
  'marketStatus.ready': 'Paruošta',
  'marketStatus.found': 'Nepridėta',
  'marketStatus.checking': 'Tikrinama',
  'marketStatus.failed': 'Reikia taisyti',
  addMarketPrompt:
    'Pridėkite dar vieną rinką {name} pirkinių asistentui ({domain}): raskite kitas parduotuvės šalis, kalbas ar svetaines ir paklauskite, kurią pridėti.',
  tabWidget: 'Widget',
  tabInstall: 'Diegimas',
  tabSetup: 'Nustatymas',
  statusReading: 'Skaitoma jūsų parduotuvė',
  readingProducts: 'Produktai skaitomi iš naujo',
  readingCart: 'Krepšelis tikrinamas iš naujo',
  readingChat: 'Tikrinamas kelias į jūsų pokalbį',
  readingMarket: 'Pridedama rinka {market}',
  readingMarketAny: 'Pridedama rinka',
  statusReady: 'Paruošta diegti',
  statusFailed: 'Skaitymas sustabdytas',
  statusLive: 'Veikia · versija {version} svetainėje {domain}',
  statusPublished: 'Publikuota · versija {version} · dar ne jūsų svetainėje',
  statusPublishedUnverified: 'Publikuota · versija {version} · jūsų svetainės patikrinti nepavyko',
  stepOpen: 'Atveriama svetainė',
  stepSearch: 'Paieška',
  stepProducts: 'Produktai',
  stepCart: 'Krepšelis',
  stepChat: 'Jūsų esamas pokalbis',
  stepLook: 'Jūsų išvaizda ir logotipas',
  stepChecks: 'Tikrinama, ar veikia',
  stepReady: 'Paruošta',
  stepDetailOpen: 'Atveriama {domain}',
  stepDetailSearch: 'Ieškomi parduotuvės produktai',
  stepDetailProducts: 'Skaitomi produktai, kainos, nuotraukos, spalvos ir dydžiai',
  stepDetailCart: 'Bandomas krepšelis: įdėti, perskaityti, pašalinti ir ištuštinti',
  stepDetailChat: 'Ieškomas jūsų esamas pokalbis',
  stepDetailLook: 'Skaitomi parduotuvės logotipas ir spalvos',
  stepDetailChecks: 'Bandomos pirkėjų paieškos, produktai, naršymas ir krepšelis',
  stepDetailChecksProducts: 'Tikrinama produktų paieška, naršymas, nuotraukos, spalvos ir dydžiai',
  stepDetailChecksCart:
    'Tikrinama, kaip krepšelyje įdėti, perskaityti, pašalinti ir ištuštinti, taip pat produktų skaitymas',
  stepDetailChecksChat: 'Tikrinamas perdavimas jūsų pokalbiui ir produktų skaitymas',
  stepDetailChecksMarket: 'Tikrinami šios rinkos produktai, kainos ir krepšelis',
  nowRunning: 'Dabar: {step}',
  nowProgress: 'Žingsnis {n} iš {total} · kol kas {minutes} min.',
  nowReady: 'Jūsų parduotuvė paruošta asistentui',
  nowReadyBody: 'Paieška, produktai ir krepšelis veikia.',
  nowLastRead: 'Paskutinį kartą skaityta {when}',
  nowUsually: 'paprastai {min}–{max}',
  stepsRead: 'Skaityta {when} · Rodyti žingsnius',
  nowFailed: 'Skaitymas sustabdytas',
  factAnswers: 'Atsako pirkėjams naudodamas',
  costCounting: 'Skaičiuojama…',
  factPaid: 'Apmokama iš',
  paidCompany: 'Įmonės kreditų',
  creditsAmount: '{amount} kreditų',
  creditsOne: '{amount} kreditas',
  creditsUnder: 'mažiau nei {amount} kreditų',
  stopRun: 'Sustabdyti vykdymą',
  stopping: 'Stabdoma…',
  closeNote: 'Galite išeiti iš šio puslapio. Vykdymas tęsis.',
  readyNote: 'Toliau išbandykite savo Widget, tada įdiekite jį.',
  buildWidget: 'Išbandyti Widget',
  readAgain: 'Skaityti parduotuvę iš naujo',
  foundTitle: 'Kas rasta',
  foundProducts: 'Perskaityti produktai ir kainos',
  foundSearch: 'Paieška veikia',
  foundCart: 'Įdėjimas į krepšelį veikia',
  foundChat: 'Jūsų {vendor} pokalbis',
  foundNoChat: 'Savo pokalbio nėra',
  foundLook: 'Jūsų spalvos ir logotipas',
  widgetTitle: 'Jūsų Widget',
  widgetBody:
    'Jūsų asistentas, jūsų spalvomis. Išbandykite čia: jis atsako naudodamas tikrus jūsų produktus. Į tikrą krepšelį niekas neįdedama.',
  widgetLoading: 'Įkeliama peržiūra…',
  widgetFailed: 'Peržiūros įkelti nepavyko.',
  widgetNotReady: 'Widget bus paruoštas atlikus veiksmus kortelėje „Nustatymas“.',
  installLineTitle: 'Pridėkite šią eilutę prie savo svetainės',
  installLineBody:
    'Ji veikia bet kurioje platformoje: puslapių head dalyje, o Shopify sistemoje theme.liquid faile, prieš </head>. Kol nepublikuosite, nieko nebus rodoma.',
  installLineBodyLive:
    'Ji veikia bet kurioje platformoje: puslapių head dalyje, o Shopify sistemoje theme.liquid faile, prieš </head>. Tada pirkėjai matys versiją {version}.',
  installPolicyAsk: 'Ar jūsų svetainė nustato Content Security Policy?',
  installPolicy:
    'Jei jūsų svetainė nustato Content Security Policy, taip pat leiskite {origin} direktyvoje {directives}, kitaip asistentas nepasileis.',
  installPolicyRefuses:
    'Jūsų svetainės Content Security Policy neleidžia paleisti asistento: leiskite {origin} direktyvoje {directives}, kitaip niekas nepasileis.',
  installPolicyNonce:
    'Jūsų politika paleidžia tik patikimus scenarijus, todėl pridėkite eilutę su puslapių nonce.',
  installPolicyEval: "Asistentui dar reikia 'unsafe-eval' direktyvoje script-src.",
  copy: 'Kopijuoti',
  copied: 'Nukopijuota',
  domainsTitle: 'Svetainės, kuriose jis atsako',
  domainsBody: 'Kitose svetainėse jis tyli, net jei kas nors nukopijuoja eilutę.',
  lineFound: 'Eilutė rasta',
  lineNotSeen: 'Puslapyje dar nėra',
  lineUnverified:
    'Patikrinti nepavyko: svetainė blokuoja automatinius apsilankymus. Asistentas vis tiek veikia pirkėjams.',
  lineChecked: 'patikrinta {when}',
  lineCheckAgain: 'Tikrinti dar kartą',
  lineChecking: 'Tikrinamas puslapis…',
  installCheckBusy: 'Patikra šiuo metu užimta. Bandykite dar kartą po akimirkos.',
  publishTitle: 'Publikuoti',
  publishBody:
    'Svetainėje {domain} esantys pirkėjai iškart jį matys. Galite bet kada atšaukti publikavimą.',
  publishButton: 'Publikuoti versiją {version}',
  publishConfirm: 'Publikuoti {name} versiją {version}?',
  publishNow: 'Publikuoti',
  notNow: 'Ne dabar',
  publishing: 'Publikuojama…',
  liveTitle: 'Publikuota: versija {version}',
  liveSince: 'Nuo {since}',
  unpublish: 'Atšaukti publikavimą',
  unpublishConfirm:
    'Pašalinti versiją {version} iš svetainės? Pirkėjai asistento nematys, kol vėl jo nepublikuosite.',
  publishedLine: 'Dabar publikuota: versija {version}, nuo {since}',
  usageTitle: 'Naudojimas',
  usageBody: 'Naudojami jūsų įmonės kreditai, kaip ir visame Chat.',
  usageDiscovery: 'Parduotuvės skaitymas',
  usageChats: 'Pirkėjų pokalbiai',
  usageChatsValue: '{count} pokalbiai · {cost}',
  last7: 'Paskutinės 7 dienos',
  askWeek: 'Paklausti Chat apie šią savaitę',
  kpiConversations: 'Pokalbiai',
  kpiVsPrevious: '{change}, palyginti su ankstesne savaite',
  kpiCart: 'Įdėta į krepšelį',
  kpiCheckout: 'Pasiektas atsiskaitymas',
  kpiPerson: 'Paprašyta žmogaus',
  kpiCost: 'Panaudoti kreditai',
  ofConversations: '{percent} pokalbių',
  checkoutNote: '{percent} pokalbių',
  personNote: '{percent} atidarė jūsų pokalbį',
  costNote: 'Maždaug {amount} vienam pokalbiui',
  costNoteUnder: 'Mažiau nei {amount} vienam pokalbiui',
  sparkLabel: 'Pokalbiai per dieną, paskutinės {days} dienos',
  sparkDay: '{day}: {count} pokalbių',
  filterAll: 'Visi',
  filterCart: 'Įdėta į krepšelį',
  filterCheckout: 'Atsiskaitymas',
  filterPerson: 'Žmogus',
  outcomeCart: 'Įdėta į krepšelį',
  outcomeCheckout: 'Pasiektas atsiskaitymas',
  outcomePerson: 'Paprašyta žmogaus',
  outcomeAnswered: 'Atsakyta',
  devicePhone: 'Telefonas',
  deviceComputer: 'Kompiuteris',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min. · Pradėta puslapyje {page}',
  anonymous: 'Pirkėjas yra anoniminis.',
  askConversation: 'Paklausti Chat apie šį pokalbį',
  showOlderVersions: 'Rodyti ankstesnes versijas',
  showOlder: 'Rodyti senesnius pokalbius',
  filterCountsWeek: 'Skaičiai: paskutinės 7 dienos',
  readAlreadyHere:
    'Ši parduotuvė jau yra čia, todėl nieko naujo nepradėta ir kreditai nepanaudoti.',
  readOpenShop: 'Atidaryti parduotuvę',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Ko galime pasimokyti iš šio pokalbio?',
  askWeekMessage: 'Ko galime pasimokyti iš šios savaitės pokalbių?',
  conversationsChipOne: '{name} · pokalbis, {when}',
  conversationsChipWeek: '{name} · ši savaitė',
  conversationsChipWeekIn: '{name} · {market} · ši savaitė',
  removeConversations: 'Pašalinti pokalbius',
  noConversations: 'Pokalbių dar nėra',
  noConversationsBody: 'Kai pirkėjai naudosis asistentu, jų pokalbiai bus rodomi čia.',
  pickConversation: 'Pasirinkite pokalbį, kurį norite perskaityti.',
  eventCheckout: 'Nuėjo į atsiskaitymą',
  eventPerson: 'Atidarė jūsų pokalbį',
  showMore: 'Rodyti daugiau',
  urlInvalid: 'Naudokite savo parduotuvės https adresą, pavyzdžiui, https://www.jusuparduotuve.lt.',
  errorNoCompany:
    'Storefront reikia įmonės darbo srities. Paprašykite administratoriaus jus pridėti.',
  errorPublishPermission:
    'Tai gali atlikti tik žmonės, turintys teisę publikuoti jūsų įmonėje. Paklauskite administratoriaus.',
  actionFailed: 'Nepavyko. Bandykite dar kartą.',
  copyFailed: 'Nukopijuoti nepavyko. Pažymėkite eilutę ir nukopijuokite ją patys.',
  reasonCancelled: 'Jūs sustabdėte vykdymą.',
  reasonFailed: 'Parduotuvės perskaityti nepavyko, todėl asistentas neparuoštas.',
  reasonFailedLive:
    'Parduotuvės perskaityti nepavyko. Publikuotas asistentas toliau veikia kaip anksčiau.',
  reasonFailedKept:
    'Parduotuvės perskaityti nepavyko, todėl niekas nepasikeitė: versija {version} liko tokia pati.',
  statusReadStopped: 'Paruošta · paskutinis skaitymas sustabdytas',
  reasonStepMarked: 'Žemiau pažymėtas sustojęs žingsnis.',
  reasonReadAgain: 'Skaitant iš naujo pradedama nuo to, ką šis skaitymas jau išmoko.',
  reasonNotAStore:
    'Šis adresas nepanašus į internetinę parduotuvę: nerasta produktų paieškai. Patikrinkite adresą arba išbandykite pagrindinį parduotuvės domeną.',
  reasonTooSlow:
    'Jūsų parduotuvės paieška pirkinių asistentui atsako per lėtai: užtruko daugiau nei 12 sekundžių. Gali padėti greitesnė paieška parduotuvėje arba vėlesnis skaitymas iš naujo.',
  reasonOurSide:
    'Skaitymas sustojo dėl problemos mūsų pusėje, ne dėl jūsų parduotuvės, todėl asistentas dar neparuoštas.',
  reasonOurSideLive:
    'Skaitymas sustojo dėl problemos mūsų pusėje, ne dėl jūsų parduotuvės. Publikuotas asistentas toliau veikia kaip anksčiau.',
  reasonOurSideKept:
    'Skaitymas sustojo dėl problemos mūsų pusėje, ne dėl jūsų parduotuvės, todėl niekas nepasikeitė: versija {version} liko tokia pati.',
  reasonTryLater: 'Pabandykite perskaityti parduotuvę dar kartą po kurio laiko.',
  errorNoCredits:
    'Šiam veiksmui šiuo metu nėra kreditų. Įmonės savininkas gali patikrinti planą ir limitus skiltyje Usage.',
  errorReadUnavailable:
    'Šios parduotuvės šiuo metu perskaityti negalima. Bandykite dar kartą po kelių minučių.',
  reasonTimeout:
    'Skaitymas pasiekė laiko limitą nebaigtas. Skaitant iš naujo tęsiama nuo to, kas jau patikrinta.',
  retrying: 'Pradedama iš naujo…',
  readTitleChecks: 'Dar kartą patikrinti išsaugotą skaitymą',
  checksScopeLabel: 'Tikrinama',
  readScopeChecks:
    'Pirkėjų paieškos, produktai, naršymas ir krepšelis – tai, ką išsaugojo paskutinis skaitymas. Parduotuvė iš naujo neskaitoma.',
  readScopeChecksProducts:
    'Produktų paieška, naršymas, nuotraukos, spalvos ir dydžiai – tai, ką išsaugojo paskutinis skaitymas. Parduotuvė iš naujo neskaitoma.',
  readScopeChecksCart:
    'Įdėjimas į krepšelį, perskaitymas, pašalinimas ir ištuštinimas, taip pat produktų skaitymas – tai, ką išsaugojo paskutinis skaitymas. Parduotuvė iš naujo neskaitoma.',
  readScopeChecksChat:
    'Perdavimas jūsų pokalbiui ir produktų skaitymas – tai, ką išsaugojo paskutinis skaitymas. Parduotuvė iš naujo neskaitoma.',
  readScopeChecksMarket:
    'Rinkos produktai, kainos ir krepšelis – tai, ką išsaugojo paskutinis skaitymas. Parduotuvė iš naujo neskaitoma.',
  checksCost: 'Įmonės kreditus naudoja tik nauji patikrinimai',
  checksAfter:
    'Išsaugomas naujas juodraštis, jei patikrinimai pavyksta. Pirkėjai nieko nepamatys, kol nepublikuosite.',
  checksAfterLive:
    'Išsaugomas naujas juodraštis, jei patikrinimai pavyksta. Publikuotas asistentas veiks toliau, o pirkėjai pakeitimą pamatys tik po publikavimo.',
  checksStarted:
    'Patikrinimai pradėti. Kai naujas juodraštis bus paruoštas, paprašykite Chat jį patikrinti.',
  checkingSavedWork: 'Dar kartą tikrinamas išsaugotas darbas',
  checkAgain: 'Patikrinti dar kartą',
  checkingAgain: 'Tikrinama dar kartą…',
  reasonCheckAgain:
    '„Patikrinti dar kartą“ dar kartą išbando išsaugotą darbą, iš naujo neskaitant parduotuvės. Įmonės kreditus naudoja tik nauji patikrinimai.',
  fixWithChat: 'Taisyti per Chat',
  fixReadPrompt:
    'Parduotuvės {name} ({domain}) skaitymas sustojo. Pažiūrėkite, kas nutiko, ir padėkite man tai sutaisyti: pasakykite, ką randate, pasiūlykite, ką galėčiau nurodyti skaitymui, ir tada patikrinkite jį dar kartą arba perskaitykite iš naujo su šia užuomina, skaitydami kuo mažesnę parduotuvės dalį.',
  fixReadPromptStep:
    'Parduotuvės {name} ({domain}) skaitymas sustojo ties žingsniu „{step}“. Pažiūrėkite, kas nutiko, ir padėkite man tai sutaisyti: pasakykite, ką randate, pasiūlykite, ką galėčiau nurodyti skaitymui, ir tada patikrinkite jį dar kartą arba perskaitykite iš naujo su šia užuomina, skaitydami kuo mažesnę parduotuvės dalį.',
  stepStateDone: 'baigta',
  stepStateRunning: 'vykdoma',
  stepStateWaiting: 'nepradėta',
  stepStateFailed: 'sustabdyta',
  newVersionTitle: 'Versija {version} paruošta',
  newVersionBody: 'Publikuokite ją, kad pakeistumėte versiją {live}.',
  newVersionBodyLive: 'Publikuokite ją, kad pakeistumėte versiją {live} svetainėje {domain}.',
  previewSize: 'Peržiūros dydis',
  designWithChat: 'Atverti dizainą',
  designReviewBody: 'Peržiūrėkite dabartinį dizainą ir pasakykite Chat, ką norėtumėte pakeisti.',
  designWhileTitle: 'Kurkite dizainą, kol skaitome parduotuvę',
  designWhileBody:
    'Chat jau dabar gali kartu su jumis formuoti asistento išvaizdą, žodžius ir logotipą. Dizainas bus paruoštas, kai baigsis skaitymas.',
  designPreviewNote: 'Peržiūra su pavyzdiniais produktais, kol parduotuvė dar neperskaityta.',
  proposedDesign: 'Siūlomas dizainas',
  proposedDesignNote:
    'Widget su šiuo pakeitimu, rodomas su pavyzdiniais produktais. Patvirtinus jis išsaugomas kaip naujas juodraštis; publikuojama Storefront puslapyje.',
  lineSources: 'Iš parduotuvės puslapių:',
  readAgainConfirm:
    'Dar kartą perskaityti {name}? Tai trunka apie 10–30 minučių ir naudoja įmonės kreditus.',
  readAgainLive: 'Publikuotas asistentas veiks toliau, kol publikuosite naują versiją.',
  readAgainNow: 'Skaityti iš naujo',
  switchStore: 'Keisti parduotuvę arba pridėti kitą',
  storesLabel: 'Jūsų parduotuvės',
  addStore: 'Pridėti kitą parduotuvę',
  backToStores: 'Grįžti į savo parduotuves',
  storeLive: 'Publikuota',
  storeReady: 'Paruošta',
  storeReading: 'Skaitoma',
  storeStopped: 'Sustabdyta',
  actionDesignTitle: 'Keisti pirkinių asistento dizainą',
  actionKnowledgeTitle: 'Keisti, ką pirkinių asistentas žino',
  readTitleProducts: 'Dar kartą perskaityti produktus',
  readTitleCart: 'Dar kartą patikrinti krepšelį',
  readTitleTheirChat: 'Dar kartą patikrinti kelią į jūsų pokalbį',
  readTitleAll: 'Dar kartą perskaityti visą parduotuvę',
  readTitleStore: 'Perskaityti naują parduotuvę',
  readTitleMarket: 'Pridėti rinką',
  readShopLabel: 'Parduotuvė',
  readScopeLabel: 'Skaitoma',
  readTimeLabel: 'Trukmė',
  readCostLabel: 'Kaina',
  readAfterLabel: 'Po to',
  readScopeProducts: 'Produktų paieška ir rūšiavimas, produktų nuotraukos, spalvos ir dydžiai',
  readScopeCart: 'Įdėjimas į krepšelį ir pašalinimas iš jo',
  readScopeTheirChat: 'Kaip pirkėjai pasiekia jūsų pokalbį',
  readScopeAll: 'Produktai, krepšelis, jūsų pokalbis ir rinkos',
  readScopeStore: 'Produktai, kainos ir dydžiai, krepšelis, jūsų pokalbis, išvaizda ir rinkos',
  readScopeMarket: '{market}: produktai, kainos ir krepšelis',
  readTime: 'Paprastai {min}–{max} minučių',
  readCost: 'Naudoja įmonės kreditus',
  readCostLast:
    'Naudoja įmonės kreditus · paskutinis šios parduotuvės skaitymas panaudojo {credits}',
  readCostLastPartial:
    'Naudoja įmonės kreditus · paskutinis šios parduotuvės skaitymas sustojo anksti ir panaudojo bent {credits}',
  readAfter: 'Išsaugomas naujas juodraštis. Pirkėjai nieko nepamatys, kol nepublikuosite.',
  readAfterLive:
    'Išsaugomas naujas juodraštis. Publikuotas asistentas veiks toliau, o pirkėjai pakeitimą pamatys tik po publikavimo.',
  readAfterNew: 'Išsaugomas pirmasis juodraštis, kurį galėsite išbandyti. Niekas nepublikuojama.',
  readCartNote:
    'Norėdamas patikrinti krepšelį, jis įdeda bandomą prekę ir vėl ją pašalina. Atsiskaitymas niekada neatliekamas.',
  readFocusLabel: 'Ką Chat paprašė ištaisyti skaitant',
  readFooter:
    'Nieko nepradėsime, kol nepatvirtinsite. Skaitymą galite sustabdyti kortelėje „Nustatymas“.',
  readStartedAt:
    'Pradėta {time} · paprastai trunka {min}–{max} minučių. Kai naujas juodraštis bus paruoštas, paprašykite Chat jį patikrinti.',
  readFocusAll: 'Rodyti viską',
  readFocusLess: 'Rodyti mažiau',
  readStart: 'Pradėti skaitymą',
  addToSite: 'Pridėti prie svetainės',
  readTab: 'Užklausa',
  readStarted:
    'Skaitymas pradėtas. Kai naujas juodraštis bus paruoštas, paprašykite Chat jį patikrinti.',
  readFollow: 'Stebėti kortelėje „Nustatymas“',
  proposedDesignLive:
    'Tikra jūsų parduotuvės peržiūra su šiuo dizainu. Užduokite jai klausimą: į tikrą krepšelį niekas neįdedama. Patvirtinus ji išsaugoma kaip naujas juodraštis; publikuojama Storefront puslapyje.',
  designChanges: 'Kas pasikeis',
  designReset: 'grįžti prie numatytojo',
  designGroupColors: 'Spalvos',
  designGroupType: 'Šriftai',
  designGroupShape: 'Forma',
  designGroupLauncher: 'Mygtukas puslapyje',
  designGroupPanel: 'Antraštė',
  designGroupProducts: 'Produktai',
  designGroupCart: 'Parduotuvės krepšelis',
  designGroupCopy: 'Tekstai',
  designGroupLogo: 'Logotipas',
  designGroupCss: 'Pasirinktinis CSS',
  designColorBrand: 'Prekės ženklas',
  designColorActions: 'Pridėti ir siųsti',
  designColorPanel: 'Skydelis',
  designColorSurface: 'Paviršiai',
  designColorInk: 'Tekstas',
  designColorMuted: 'Antrinis tekstas',
  designColorLine: 'Linijos',
  designColorBubble: 'Pirkėjo žinutės',
  designColorBubbleInk: 'Pirkėjo tekstas',
  designActionsBrand: 'Prekės ženklo spalva',
  designActionsInk: 'Teksto spalva',
  designWordsName: 'Pavadinimas',
  designWordsGreeting: 'Pasisveikinimas',
  designWordsStarters: 'Pradžios klausimai',
  designWordsLauncher: 'Mygtuko etiketė',
  designWordsPlaceholder: 'Vietos rezervavimo tekstas',
  designWordsAi: 'DI žyma',
  designWordsPerson: 'Nuoroda į žmogų',
  noteNew: 'Nauja',
  noteChanged: 'Pakeista',
  noteRemoved: 'Pašalinta',
  noteSourceKnowledge: 'Žinių puslapis: {title}',
  noteSourceFile: 'Failas: {name}',
  noteSourceChat: 'Iš pokalbio',
  knowledgeApprovalNote:
    'Pirkėjai gali perskaityti viską šiose pastabose. Patvirtinus išsaugomas naujas juodraštis: užduokite klausimų kortelėje Widget, tada publikuokite kortelėje „Diegimas“.',
  profileTitle: 'Apie parduotuvę',
  profileChecked: 'Patikrinta {date}',
  profileSources: 'Parašyta iš',
  profileSample: '{count} produktų paimta kainų lygiui',
  profileSampleOne: '{count} produktas paimtas kainų lygiui',
  profileAssistant: 'Ką asistentas čia gali daryti',
  profileTeam: 'Parašė jūsų komanda',
  profileEmpty:
    'Dar neparašyta. Paprašykite Chat paleisti žinių perėjimą: jis tai parašo iš jūsų pačių puslapių ir to, ką matė jūsų svetainėje, o jūs galite pridėti savo eilučių.',
  profileWithChat: 'Pridėti arba keisti per Chat',
  profilePrompt:
    'Padėk man pagerinti tai, ką apsipirkimo asistentas parduotuvei {name} ({domain}) žino apie mūsų parduotuvę apskritai: ką parduodame, kuo esame žinomi, kam skirti ir ką pirkėjai čia gali daryti. Pirmiausia perskaityk profilį „Apie parduotuvę“ ir pasakyk, ko trūksta ar kas neteisinga. Viskas, ką apie parduotuvę pasakysiu ir ko nemini jokie puslapiai, bus įrašyta kaip mūsų komandos eilutė.',
  profilePartNavigation: 'Skyriai',
  profilePartWelcome: 'Pasveikinimas',
  profilePartMarkets: 'Rinkos',
  profilePartFunctions: 'Ką asistentas gali daryti',
  profilePartProducts: 'Produktai',
  profilePartPages: 'Aptarnavimo puslapiai',
  profileCleared: 'Išvalo parašytą profilį',
  knowledgeTitle: 'Ką jis žino',
  knowledgeBody:
    'Be jūsų produktų, kainų ir likučio, kuriuos jis visada skaito tiesiogiai iš jūsų parduotuvės.',
  knowledgeEmpty:
    'Kol kas yra tik jūsų katalogas. Pridėkite pristatymo, grąžinimo ar mokėjimo informaciją per Chat, iš savo puslapių arba iš Žinių.',
  knowledgeWithChat: 'Pridėti per Chat',
  knowledgeChangeWithChat: 'Keisti per Chat',
  knowledgePrompt:
    'Padėkite pasirinkti, ką {name} pirkinių asistentas ({domain}) pasakos pirkėjams be mūsų produktų, pavyzdžiui, apie pristatymą, grąžinimą ir mokėjimą. Raskite tai mūsų puslapiuose arba Žiniose ir pasiūlykite pastabas, kurias galėčiau patvirtinti.',
  pagesTitle: 'Puslapiai, kuriuos skaito',
  pagesBody:
    'Puslapiai, iš kurių atsirado pastabos. Poneva juos tikrina kiekvieną dieną, o Chat praneša, kai kuris pasikeičia.',
  pagesEmpty:
    'Puslapių dar nėra. Čia rodomi puslapiai, kuriuos randa parduotuvės skaitymas, ir tie, kurie nurodyti Chat.',
  pageFound: 'Ponevos rasta',
  pageLinkedFrom: 'susieta iš {page}',
  pageAddedByYou: 'pridėta rankiniu būdu',
  pageFromNote: 'paminėtas pastaboje',
  pageNoteOne: '1 pastaba',
  pageNotesMany: '{count} pastabų',
  pageStatusOk: 'Atnaujinta',
  pageStatusChanged: 'Pakeista',
  pageStatusUnreadable: 'Neįmanoma perskaityti',
  pageStatusUnchecked: 'Dar netikrinta',
  pagesChangeWithChat: 'Keisti per Chat',
  pagesUpdateWithChat: 'Atnaujinti pastabas per Chat',
  pagesPrompt:
    'Padėkite man pakeisti, kuriuos puslapius {name} ({domain}) pirkimų asistentas skaito savo pastaboms. Parodykite man puslapius, kuriuos jis naudoja dabar, ir paklauskite, kuriuos pridėti ar pašalinti.',
  pagesUpdatePrompt:
    'Kai kurie {name} ({domain}) puslapiai pasikeitė arba nebegali būti perskaityti nuo pastabų sukūrimo. Parodykite, kurie tai yra, ir sukurkite pastabas iš jų iš naujo kaip juodraštį, kurį galiu peržiūrėti.',
  viewCompare: 'Palyginimas',
  lookDeviceLabel: 'Lyginti įrenginyje',
  lookShop: 'Jūsų parduotuvė',
  lookShopCaption: 'Pagrindinis puslapis, skaitytas {when}',
  lookWidget: 'Asistentas',
  lookWidgetCaption: 'Versija {version}',
  lookLoading: 'Asistentas statomas šalia jūsų parduotuvės…',
  lookFailed: 'Šiuo metu palyginimo sukurti nepavyko.',
  lookNoShopPicture: 'Skaitymo metu neišsaugotas jūsų parduotuvės vaizdas tokiu pločiu.',
  lookNoneTitle: 'Jūsų parduotuvės išvaizda neišsaugota',
  lookNoneBody:
    'Šis skaitymas jos neužfiksavo. Skaitykite parduotuvę iš naujo skiltyje Nustatymas, ir jos logotipas, spalvos bei puslapiai bus išsaugoti palyginimui.',
  lookColoursShop: 'Spalvos, kurias naudoja jūsų parduotuvė',
  lookColoursAssistant: 'Asistento spalvos',
  lookRoleHeader: 'Antraštė',
  lookRoleBrand: 'Prekės ženklas',
  lookSame: 'Tokia pati kaip jūsų parduotuvės spalva {colour} ({source})',
  lookOff:
    'Artima jūsų parduotuvės spalvai {colour} ({source}), bet ne tokia pati, skirtumas {delta}',
  lookOwn: 'Sava spalva: jūsų parduotuvė jos nenaudoja',
  lookNeutral: 'Jūsų parduotuvė tam nebuvo įkelta: rodoma tik tai, ką išsaugojo skaitymas.',
  lookFromLogo: 'logotipas',
  lookFromTheme: 'temos spalva',
  lookFromTile: 'langelio spalva',
  lookFromMask: 'prisegta piktograma',
  lookFromManifest: 'programėlės manifestas',
  lookFromHeader: 'antraštė',
  lookFromButton: 'mygtukai',
  lookFromVariable: 'stiliaus spalva',
  lookFix: 'Taisyti per Chat',
  lookFixPrompt:
    'Asistento spalva ({role}) yra {colour}, artima jūsų parduotuvės spalvai {exact} ({source}), bet ne tokia pati. Prašome naudoti tikslią jūsų parduotuvės spalvą {exact}.',
  lookSwatch: 'Spalva {colour}',
  errorDesignChecks: 'Ši versija neišlaikė dizaino patikrų.',
  designSamplesBlocked:
    'Kortelių išdėstymo čia įrodyti negalima: jūsų svetainė blokuoja automatinius apsilankymus. Spalvos, šriftai ir logotipas vis tiek veikia.',
  designCheckLabel: 'DI žymė per blanki, kad ją būtų galima perskaityti.',
  designCheckReach: 'Mygtuko nepavyksta pasiekti.',
  designCheckSize: 'Dalis teksto per maža.',
  designCheckOverflow: 'Skydelis slenka į šonus.',
  designCheckRender: 'Patikrų nepavyko atlikti. Pabandykite dar kartą po akimirkos.',
  fixDesignPrompt:
    'Bandžiau paskelbti naujausią {name} ({domain}) juodraštį, bet jis neišlaikė dizaino patikrų: {facts}. Ištaisykite dizainą šiame pokalbyje ir patikrinkite jį telefone ir kompiuteryje. Neskelbkite jo.',
};

const mt: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Assistent tax-xiri',
  designUnconfirmed:
    'Waqqafna qabel Chat seta’ jikkonferma li d-disinn ġie salvat. Il-kanvas tal-assistent juri l-aħħar verżjoni.',
  errorNewerVersion:
    'Ġiet salvata verżjoni aktar ġdida minn meta ftaħt din. Araha l-ewwel, imbagħad ippubblika.',
  canvasSelectHint: 'Ikklikkja parti mill-assistent biex tibdilha fiċ-chat · Esc biex tieqaf',
  canvasDraft: 'Verżjoni {version} · għadha mhux ippubblikata',
  canvasDraftOverLive: 'Verżjoni {version} · il-verżjoni {live} hija ppubblikata',
  canvasLive: 'Verżjoni {version} · ippubblikata',
  canvasNoVersion: 'L-ewwel verżjoni tidher meta jintemm il-qari tal-ħanut',
  canvasPublish: 'Ippubblika fil-paġna Storefront',
  canvasFrameTitle: 'Assistent tax-xiri ta’ {name}',
  viewLabel: 'Ara',
  photosGood: '{count} ritratt tal-prodotti: sħaħ u ċari',
  photosGoodOne: 'Ir-ritratt tal-prodott huwa sħiħ u ċar',
  canvasMissingTitle: 'Dan l-assistent tax-xiri mhuwiex f’dan il-workspace',
  canvasMissingBody: 'Jappartjeni għal workspace ieħor, jew tneħħa.',
  canvasMissingLink: 'Ara l-assistenti ta’ dan il-workspace',
  photosLabel: 'Ritratti tal-prodotti',
  photosSmall: '{count} żgħar fil-kaxxi tagħhom',
  photosCut: '{count} maqtugħin',
  photosSoft: '{count} mkabbrin u għalhekk imċajprin',
  photosMissing: '{count} mingħajr ritratt',
  photosFix: 'Irranġa ma’ Chat',
  photosPrompt:
    'Irranġa r-ritratti tal-prodotti fil-karti: kull wieħed sħiħ, kbir biżżejjed fil-kaxxa tiegħu u ċar.',
  photosFacts:
    '{total} ritratt: {small} żgħar fil-kaxxi, {cut} maqtugħin, {soft} mkabbrin, {missing} mingħajr ritratt. Daqsijiet: {sizes}',
  viewDesign: 'Disinn',
  viewVersions: 'Verżjonijiet',
  versionsLoading: 'Qed jitgħabbew il-verżjonijiet…',
  versionsFailed: 'Il-verżjonijiet ma setgħux jitgħabbew.',
  versionTitle: 'Verżjoni {version}',
  versionLive: 'Ippubblikata',
  versionChecked: 'Iċċekkjata',
  versionToday: 'Illum',
  versionYesterday: 'Ilbieraħ',
  versionEarlier: 'Aktar kmieni',
  versionByPerson: 'Talabha {name}',
  versionByChat: 'Talabha fiċ-chat',
  versionFromReading: 'Mill-qari ta’ {domain}',
  versionPublishedBy: 'ippubblikata minn {name}, {at}',
  versionPublish: 'Ippubblika',
  versionRestore: 'Irrestawra…',
  versionRestoreTitle: 'Irrestawra l-verżjoni {version}?',
  versionRestoreBody:
    'Issir il-verżjoni {next}, abbozz ġdid. Is-sit tiegħek ma jinbidilx qabel tippubblika.',
  versionRestoreConfirm: 'Irrestawra',
  versionRestoreCancel: 'Ikkanċella',
  versionRestored: 'Irrestawrata bħala verżjoni {version}',
  versionRestoreFailed: 'Il-verżjoni ma setgħetx tiġi rrestawrata.',
  compareTitle: 'Qabbel il-verżjoni {to} mal-verżjoni {from}',
  compareTitleLive: 'Qabbel il-verżjoni {to} mal-verżjoni ppubblikata {from}',
  compareHint: 'Iġbed biex tqabbel',
  compareLabel: 'Qabbel il-verżjoni {from} u l-verżjoni {to}',
  compareTagLive: 'v{version} · Ippubblikata',
  compareTag: 'v{version}',
  changesTitle: 'X’inbidel mill-verżjoni {from} għall-{to}',
  changesNone: 'Id-disinni huma l-istess. Inbidlu biss in-noti jew id-dejta tal-ħanut.',
  changeAdded: 'Miżjud',
  changeRemoved: 'Imneħħi',
  changeChanged: 'Inbidel',
  changeDrawn: 'logo mfassal',
  changeCss: 'CSS tiegħu stess',
  changeYes: 'iva',
  changeNo: 'le',
  'field.colors.brand': 'Kulur tal-marka',
  'field.colors.actions': 'Kulur tal-buttuni',
  'field.colors.panel': 'Kulur tal-pannell',
  'field.colors.surface': 'Kulur tal-wiċċ',
  'field.colors.ink': 'Kulur tat-test',
  'field.colors.muted': 'Kulur tat-test sekondarju',
  'field.colors.line': 'Kulur tal-linja',
  'field.colors.bubble': 'Kulur tal-messaġġ tax-xerrej',
  'field.colors.bubbleInk': 'Test tal-messaġġ tax-xerrej',
  'field.colors.header': 'Kulur tal-intestatura',
  'field.colors.headerInk': 'Kulur tat-test tal-intestatura',
  'field.colors.tints': 'Tinti ratba',
  'field.copy.assistantName': 'Isem',
  'field.copy.greeting': 'Tislima',
  'field.copy.placeholder': 'Kaxxa tal-mistoqsija',
  'field.copy.starters': 'Mistoqsijiet tal-bidu',
  'field.copy.launcherLabel': 'Kliem tal-buttuna',
  'field.copy.aiLabel': 'Tikketta AI',
  'field.copy.personLabel': 'Link għal persuna',
  'field.type.body': 'Font tat-test',
  'field.type.display': 'Font tal-intestaturi',
  'field.type.fontCss': 'Sors tal-font',
  'field.type.scale': 'Daqs tat-test',
  'field.shape.radius': 'Rkejjen',
  'field.shape.shadow': 'Dell',
  'field.shape.density': 'Spazjar',
  'field.launcher.style': 'Buttuna fuq il-kompjuters',
  'field.launcher.phoneStyle': 'Buttuna fuq it-telefowns',
  'field.launcher.side': 'Naħa tal-buttuna',
  'field.launcher.icon': 'Ikona tal-buttuna',
  'field.panel.header': 'Daqs tal-intestatura',
  'field.panel.opening': 'L-ewwel skrin',
  'field.products.layout': 'Prodotti',
  'field.products.imageFit': 'Ritratti tal-prodotti',
  'field.products.imageRatio': 'Għamla tal-kaxxa tar-ritratt',
  'field.products.imagePadding': 'Spazju madwar ir-ritratti',
  'field.products.imageBackground': 'Kulur tal-kaxxa tar-ritratt',
  'field.products.picker': 'Għażla tad-daqs',
  'field.products.showStock': 'Uri l-istokk',
  'field.cart.landing': 'Animazzjoni tal-karrettun',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Għamla tal-logo',
  'field.logo.plate': 'Sfond tal-logo',
  'field.css': 'CSS personalizzat',
  'value.ink': 'linka',
  'value.brand': 'kulur tal-marka',
  'value.small': 'żgħir',
  'value.medium': 'medju',
  'value.large': 'kbir',
  'value.square': 'kwadru',
  'value.soft': 'artab',
  'value.round': 'tond',
  'value.flat': 'ċatt',
  'value.deep': 'fond',
  'value.compact': 'kompatt',
  'value.comfortable': 'komdu',
  'value.pill': 'pillola bil-kliem',
  'value.circle': 'ċirku',
  'value.right': 'lemin',
  'value.left': 'xellug',
  'value.logo': 'logo',
  'value.chat': 'bużżieqa taċ-chat',
  'value.sparkle': 'tlellix',
  'value.bag': 'borża',
  'value.full': 'sħiħ',
  'value.middle': 'fin-nofs',
  'value.bottom': 'fil-qiegħ',
  'value.rows': 'ringieli',
  'value.grid': 'grilja',
  'value.carousel': 'ringiela li tiskrollja',
  'value.contain': 'ritratt sħiħ',
  'value.cover': 'kaxxa mimlija',
  'value.portrait': 'ritratt vertikali',
  'value.landscape': 'orizzontali',
  'value.none': 'xejn',
  'value.pop': "+1 li jitla'",
  'value.ring': 'mewġa',
  'value.blink': 'teptip',
  'value.chips': 'buttuni f’ringiela',
  'value.menu': 'lista',
  'value.rounded': 'imdawwar',
  designCardTitle: 'Assistent tax-xiri ta’ {name}',
  designCardSaved: 'Verżjoni {version} salvata · għadha mhux ippubblikata',
  designCardSavedFirst: 'Disinn salvat · l-ewwel verżjoni se ġġorru',
  designSaving: 'Qed jiġi salvat id-disinn…',
  selectedAssistantPart: 'Magħżula: parti mill-assistent',
  loading: 'Qed jitgħabba Storefront…',
  disabledTitle: 'Storefront mhux mixgħul f’dan il-workspace.',
  disabledBody:
    'Qiegħed f’aċċess bikri u qed jiddaħħal gradwalment. Staqsi lill-kuntatt tiegħek f’Poneva meta jasal għandkom.',
  loadFailed: 'Storefront ma setax jitgħabba.',
  startTitle: 'Assistent tax-xiri għall-ħanut tiegħek',
  startBody:
    'Poneva jaqra l-ħanut tiegħek kif jużah xerrej: it-tfittxija, il-prodotti, il-karrettun u ċ-chat li diġà għandek. Jieħu madwar 10 sa 30 minuta, juża l-krediti tal-kumpanija, u tista’ tkompli taħdem.',
  urlLabel: 'Indirizz tal-ħanut tiegħek',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Aqra l-ħanut tiegħi',
  starting: 'Qed jibda…',
  startFailed: 'Dak il-ħanut ma setax jinqara. Iċċekkja l-indirizz u erġa’ pprova.',
  tabConversations: 'Konversazzjonijiet',
  tabOverview: 'Ħarsa ġenerali',
  allAssistants: 'L-assistenti kollha',
  allAssistantsNote: '{assistants} assistenti · {sites} siti',
  allAssistantsMenuNote: '{count} konversazzjoni din il-ġimgħa',
  marketLabel: 'Suq',
  allMarkets: 'Is-swieq kollha',
  allMarketsNote: '{count} siti',
  scopeIn: '{assistant} f’{market}',
  askAbout: 'Staqsi lil Chat dwar {scope}',
  overviewAssistantsTitle: 'Assistenti u swieq',
  overviewMarketsTitle: 'Swieq',
  overviewAssistantsOnly: 'Assistenti',
  overviewIdle: '{count} assistenti mingħajr konversazzjonijiet s’issa',
  allAssistantsCount: '{assistants} assistenti',
  overviewFailed: 'Il-ħarsa ġenerali ma setgħetx titgħabba.',
  colConversations: 'Konversazzjonijiet',
  colCart: 'Miżjud fil-karrettun',
  colCheckout: 'Checkout',
  colPerson: 'Persuna',
  colCost: 'Krediti',
  rowMarkets: '{count} swieq',
  rowVersion: 'verżjoni {version}',
  rowAria:
    '{name}, {sub}: {conversations} konversazzjoni, {cart} miżjuda fil-karrettun, {checkout} waslu għal checkout, {person} talbu persuna, {cost} krediti',
  versionPublishedAt: 'ippubblikata {at}',
  designColorHeader: 'Intestatura',
  designColorHeaderInk: 'Test tal-intestatura',
  designGroupTranslations: 'Kliem f’lingwi oħra',
  topicsTitle: 'X’jistaqsu x-xerrejja',
  topicsNote: 'Suġġetti li Chat sab fl-ewwel mistoqsijiet',
  startPagesTitle: 'Fejn jibdew il-konversazzjonijiet',
  startPagesOne: 'Kull konversazzjoni bdiet f’tip wieħed ta’ paġna: {page}.',
  'startPage.product': 'Paġni tal-prodotti',
  'startPage.search': 'Riżultati tat-tfittxija',
  'startPage.start': 'Paġna tal-bidu',
  'startPage.cart': 'Karrettun',
  'startPage.other': 'Paġni oħra',
  addMarketInChat: 'Żid suq, fiċ-chat',
  marketsTitle: 'Swieq',
  marketsBody:
    'Assistent wieħed u disinn wieħed. Kull suq għandu l-lingwa, il-munita, il-prodotti, il-karrettun u ċ-chat tiegħu ma’ persuna.',
  marketChecked: 'iċċekkjat {when}',
  'marketStatus.ready': 'Lest',
  'marketStatus.found': 'Mhux miżjud',
  'marketStatus.checking': 'Qed jiġi ċċekkjat',
  'marketStatus.failed': 'Jeħtieġ tiswija',
  addMarketPrompt:
    'Żid suq ieħor mal-assistent tax-xiri għal {name} ({domain}): sib il-pajjiżi, il-lingwi jew is-siti l-oħra tal-ħanut u staqsini liema nżid.',
  tabWidget: 'Widget',
  tabInstall: 'Installazzjoni',
  tabSetup: 'Konfigurazzjoni',
  statusReading: 'Qed jinqara l-ħanut tiegħek',
  readingProducts: 'Qed jerġgħu jinqraw il-prodotti',
  readingCart: 'Qed jiġi ċċekkjat il-karrettun mill-ġdid',
  readingChat: 'Qed jiġi ċċekkjat il-mod lejn iċ-chat tiegħek',
  readingMarket: 'Qed jiżdied is-suq {market}',
  readingMarketAny: 'Qed jiżdied suq',
  statusReady: 'Lest għall-installazzjoni',
  statusFailed: 'Il-qari waqaf',
  statusLive: 'Attiv · verżjoni {version} fuq {domain}',
  statusPublished: 'Ippubblikat · verżjoni {version} · għadu mhux fuq is-sit tiegħek',
  statusPublishedUnverified:
    'Ippubblikat · verżjoni {version} · ma setgħax jiġi ċċekkjat is-sit tiegħek',
  stepOpen: 'Jinfetaħ is-sit',
  stepSearch: 'Tfittxija',
  stepProducts: 'Prodotti',
  stepCart: 'Karrettun',
  stepChat: 'Iċ-chat eżistenti tiegħek',
  stepLook: 'Id-dehra u l-logo tiegħek',
  stepChecks: 'Qed jiġi ċċekkjat li jaħdem',
  stepReady: 'Lest',
  stepDetailOpen: 'Jinfetaħ {domain}',
  stepDetailSearch: 'Qed jinstabu l-prodotti tal-ħanut',
  stepDetailProducts:
    'Qed jinqraw il-prodotti, il-prezzijiet, ir-ritratti, il-kuluri u d-daqsijiet',
  stepDetailCart: 'Qed jiġi ppruvat il-karrettun: żid, aqra lura, neħħi u żvojta',
  stepDetailChat: 'Qed jitfittex iċ-chat eżistenti tiegħek',
  stepDetailLook: 'Qed jinqraw il-logo u l-kuluri tal-ħanut',
  stepDetailChecks:
    'Qed jiġu ppruvati t-tfittxijiet tax-xerrejja, il-prodotti, in-navigazzjoni u l-karrettun',
  stepDetailChecksProducts:
    'Qed jiġu ċċekkjati t-tfittxijiet, in-navigazzjoni, ir-ritratti, il-kuluri u d-daqsijiet tal-prodotti',
  stepDetailChecksCart:
    'Qed jiġi ċċekkjat iż-żieda, il-qari lura, it-tneħħija u l-vojt tal-karrettun, kif ukoll il-prodotti',
  stepDetailChecksChat:
    'Qed jiġi ċċekkjat it-trasferiment lejn iċ-chat eżistenti tiegħek u l-prodotti',
  stepDetailChecksMarket:
    'Qed jiġu ċċekkjati l-prodotti, il-prezzijiet u l-karrettun ta’ dan is-suq',
  nowRunning: 'Issa: {step}',
  nowProgress: 'Pass {n} minn {total} · {minutes} min s’issa',
  nowReady: 'Il-ħanut tiegħek lest għal assistent',
  nowReadyBody: 'It-tfittxija, il-prodotti u l-karrettun jaħdmu.',
  nowLastRead: 'L-aħħar qari {when}',
  nowUsually: 'normalment {min}–{max}',
  stepsRead: 'Inqara {when} · Uri l-passi',
  nowFailed: 'Il-qari waqaf',
  factAnswers: 'Iwieġeb lix-xerrejja b’',
  costCounting: 'Qed jingħaddu…',
  factPaid: 'Imħallas minn',
  paidCompany: 'Krediti tal-kumpanija',
  creditsAmount: '{amount} krediti',
  creditsOne: '{amount} kreditu',
  creditsUnder: 'inqas minn {amount} krediti',
  stopRun: 'Waqqaf il-proċess',
  stopping: 'Qed jieqaf…',
  closeNote: 'Tista’ titlaq minn din il-paġna. Il-proċess jibqa’ għaddej.',
  readyNote: 'Li jmiss: ipprova l-widget tiegħek, imbagħad installah.',
  buildWidget: 'Ipprova l-widget',
  readAgain: 'Aqra l-ħanut mill-ġdid',
  foundTitle: 'Dak li nstab',
  foundProducts: 'Inqraw il-prodotti u l-prezzijiet',
  foundSearch: 'It-tfittxija taħdem',
  foundCart: 'Iż-żieda fil-karrettun taħdem',
  foundChat: 'Iċ-chat tiegħek ta’ {vendor}',
  foundNoChat: 'M’hemmx chat tiegħek',
  foundLook: 'Il-kuluri u l-logo tiegħek',
  widgetTitle: 'Il-widget tiegħek',
  widgetBody:
    'L-assistent tiegħek bil-kuluri tiegħek. Ipprovah hawn: iwieġeb bil-prodotti reali tiegħek. Xejn ma jiżdied ma’ karrettun reali.',
  widgetLoading: 'Qed jitgħabba l-preview…',
  widgetFailed: 'Il-preview ma tgħabbiex.',
  widgetNotReady: 'Il-widget ikun lest meta jitlestew il-passi fit-tab Konfigurazzjoni.',
  installLineTitle: 'Żid din il-linja mas-sit tiegħek',
  installLineBody:
    'Taħdem fuq kull pjattaforma: fl-intestatura tal-paġni tiegħek; fuq Shopify, f’theme.liquid eżatt qabel </head>. Ma turi xejn qabel tippubblika.',
  installLineBodyLive:
    'Taħdem fuq kull pjattaforma: fl-intestatura tal-paġni tiegħek; fuq Shopify, f’theme.liquid eżatt qabel </head>. Ix-xerrejja mbagħad jaraw il-verżjoni {version}.',
  installPolicyAsk: 'Is-sit tiegħek jistabbilixxi Content Security Policy?',
  installPolicy:
    'Jekk is-sit tiegħek jistabbilixxi Content Security Policy, ħalli wkoll {origin} f’{directives}, inkella l-assistent ma jistax jibda.',
  installPolicyRefuses:
    'Il-Content Security Policy tas-sit tirrifjuta lill-assistent: ħalli {origin} f’{directives}, inkella xejn ma jibda.',
  installPolicyNonce:
    'Il-policy tiegħek tħaddem biss scripts fdati, għalhekk żid il-linja bin-nonce tal-paġni tiegħek.',
  installPolicyEval: "L-assistent jeħtieġ ukoll 'unsafe-eval' f’script-src.",
  copy: 'Ikkopja',
  copied: 'Ikkupjat',
  domainsTitle: 'Siti fejn iwieġeb',
  domainsBody: 'Jibqa’ sieket fuq kull sit ieħor, anke jekk jikkopja l-linja.',
  lineFound: 'Instabet il-linja',
  lineNotSeen: 'Għadha mhix fuq il-paġna',
  lineUnverified:
    'Ma setgħetx tiġi ċċekkjata: is-sit jimblokka żjarat awtomatiċi. L-assistent xorta jaħdem għax-xerrejja.',
  lineChecked: 'iċċekkjata {when}',
  lineCheckAgain: 'Erġa’ ċċekkja',
  lineChecking: 'Qed tiġi ċċekkjata l-paġna…',
  installCheckBusy: "Il-verifika hija okkupata bħalissa. Erġa' pprova wara ftit.",
  publishTitle: 'Ippubblika',
  publishBody:
    'Ix-xerrejja fuq {domain} jarawh mill-ewwel. Tista’ tneħħi l-pubblikazzjoni kull meta trid.',
  publishButton: 'Ippubblika l-verżjoni {version}',
  publishConfirm: 'Tippubblika l-verżjoni {version} ta’ {name}?',
  publishNow: 'Ippubblika',
  notNow: 'Mhux issa',
  publishing: 'Qed jiġi ppubblikat…',
  liveTitle: 'Ippubblikata: verżjoni {version}',
  liveSince: 'Minn {since}',
  unpublish: 'Neħħi l-pubblikazzjoni',
  unpublishConfirm:
    'Tneħħi l-verżjoni {version} mis-sit tiegħek? Ix-xerrejja ma jibqgħux jaraw l-assistent sakemm terġa’ tippubblika.',
  publishedLine: 'Ippubblikat issa: verżjoni {version}, minn {since}',
  usageTitle: 'Użu',
  usageBody: 'Juża l-krediti tal-kumpanija tiegħek, bħall-bqija ta’ Chat.',
  usageDiscovery: 'Qari tal-ħanut',
  usageChats: 'Chats tax-xerrejja',
  usageChatsValue: '{count} chats · {cost}',
  last7: 'L-aħħar 7 ijiem',
  askWeek: 'Staqsi lil Chat dwar din il-ġimgħa',
  kpiConversations: 'Konversazzjonijiet',
  kpiVsPrevious: '{change} meta mqabbel mal-ġimgħa ta’ qabel',
  kpiCart: 'Miżjud fil-karrettun',
  kpiCheckout: 'Wasal għal checkout',
  kpiPerson: 'Talab persuna',
  kpiCost: 'Krediti użati',
  ofConversations: '{percent} tal-konversazzjonijiet',
  checkoutNote: '{percent} tal-konversazzjonijiet',
  personNote: '{percent} fetħu ċ-chat tiegħek',
  costNote: 'Madwar {amount} għal kull konversazzjoni',
  costNoteUnder: 'Inqas minn {amount} għal kull konversazzjoni',
  sparkLabel: 'Konversazzjonijiet kuljum, l-aħħar {days} ijiem',
  sparkDay: '{day}: {count} konversazzjoni',
  filterAll: 'Kollha',
  filterCart: 'Miżjuda fil-karrettun',
  filterCheckout: 'Checkout',
  filterPerson: 'Persuna',
  outcomeCart: 'Miżjuda fil-karrettun',
  outcomeCheckout: 'Waslet għal checkout',
  outcomePerson: 'Talbet persuna',
  outcomeAnswered: 'Imwieġba',
  devicePhone: 'Telefon',
  deviceComputer: 'Kompjuter',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Bdiet fuq {page}',
  anonymous: 'Ix-xerrej huwa anonimu.',
  askConversation: 'Staqsi lil Chat dwar din il-konversazzjoni',
  showOlderVersions: 'Uri verżjonijiet ta’ qabel',
  showOlder: 'Uri konversazzjonijiet eqdem',
  filterCountsWeek: 'Għadd: l-aħħar 7 ijiem',
  readAlreadyHere:
    'Dan il-ħanut diġà qiegħed hawn, għalhekk ma beda xejn ġdid u ma ntużaw l-ebda krediti.',
  readOpenShop: 'Iftħu',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'X’nistgħu nitgħallmu minn din il-konversazzjoni?',
  askWeekMessage: 'X’nistgħu nitgħallmu mill-konversazzjonijiet ta’ din il-ġimgħa?',
  conversationsChipOne: '{name} · konversazzjoni minn {when}',
  conversationsChipWeek: '{name} · din il-ġimgħa',
  conversationsChipWeekIn: '{name} · {market} · din il-ġimgħa',
  removeConversations: 'Neħħi l-konversazzjonijiet',
  noConversations: 'Għad m’hemmx konversazzjonijiet',
  noConversationsBody:
    'Meta x-xerrejja jużaw l-assistent, il-konversazzjonijiet tagħhom jidhru hawn.',
  pickConversation: 'Agħżel konversazzjoni biex taqraha.',
  eventCheckout: 'Mar għal checkout',
  eventPerson: 'Fetaħ iċ-chat tiegħek',
  showMore: 'Uri aktar',
  urlInvalid: 'Uża l-indirizz https tal-ħanut tiegħek, bħal https://www.yourstore.com.',
  errorNoCompany: 'Storefront jeħtieġ workspace ta’ kumpanija. Staqsi lill-amministratur iżidok.',
  errorPublishPermission:
    'Dawk biss li jistgħu jippubblikaw għall-kumpanija tiegħek jistgħu jagħmlu dan. Staqsi lill-amministratur.',
  actionFailed: 'Dan ma rnexxiex. Erġa’ pprova.',
  copyFailed: 'Ma setax jiġi kkupjat. Agħżel il-linja u kkopjaha int.',
  reasonCancelled: 'Int waqqaft il-proċess.',
  reasonFailed: 'Il-qari ma setax jintemm fuq il-ħanut tiegħek, għalhekk l-assistent mhux lest.',
  reasonFailedLive:
    'Il-qari ma setax jintemm fuq il-ħanut tiegħek. L-assistent ippubblikat jibqa’ jaħdem kif inhu.',
  reasonFailedKept:
    'Il-qari ma setax jintemm fuq il-ħanut tiegħek, għalhekk ma nbidel xejn: il-verżjoni {version} tibqa’ kif kienet.',
  statusReadStopped: 'Lest · l-aħħar qari waqaf',
  reasonStepMarked: 'Il-pass li waqaf huwa mmarkat hawn taħt.',
  reasonReadAgain: 'Il-qari mill-ġdid jibda minn dak li tgħallem dan il-qari.',
  reasonNotAStore:
    'Dan l-indirizz ma jidhirx ħanut fuq il-web: ma kien hemm l-ebda prodott x’tfittex. Iċċekkja l-indirizz jew ipprova d-dominju ewlieni tal-ħanut.',
  reasonTooSlow:
    'It-tfittxija tal-ħanut tiegħek twieġeb bil-mod wisq għal assistent tax-xiri: ħadet aktar minn 12-il sekonda. Tfittxija aktar mgħaġġla fuq il-ħanut, jew qari ieħor aktar tard, tista’ tgħin.',
  reasonOurSide:
    'Il-qari waqaf minħabba problema min-naħa tagħna, mhux fil-ħanut tiegħek, għalhekk l-assistent għadu mhux lest.',
  reasonOurSideLive:
    'Il-qari waqaf minħabba problema min-naħa tagħna, mhux fil-ħanut tiegħek. L-assistent ippubblikat jibqa’ jaħdem kif inhu.',
  reasonOurSideKept:
    'Il-qari waqaf minħabba problema min-naħa tagħna, mhux fil-ħanut tiegħek, għalhekk ma nbidel xejn: il-verżjoni {version} tibqa’ kif kienet.',
  reasonTryLater: 'Erġa’ pprova taqra l-ħanut wara ftit.',
  errorNoCredits:
    'Bħalissa m’hemmx krediti għal dan. Sid tal-kumpanija jista’ jiċċekkja l-pjan u l-limiti f’Usage.',
  errorReadUnavailable: 'Dan il-ħanut ma jistax jinqara bħalissa. Erġa’ pprova fi ftit minuti.',
  reasonTimeout:
    'Il-qari laħaq il-limitu taż-żmien qabel spiċċa. Il-qari mill-ġdid ikompli minn dak li diġà ġie vverifikat.',
  retrying: 'Qed jerġa’ jibda…',
  readTitleChecks: 'Iċċekkja l-qari ssejvjat mill-ġdid',
  checksScopeLabel: 'Jiċċekkja',
  readScopeChecks:
    'Tfittxijiet tax-xerrejja, prodotti, in-navigazzjoni u l-karrettun, fuq dak li ssejvja l-aħħar qari. Il-ħanut ma jerġax jinqara.',
  readScopeChecksProducts:
    'It-tfittxijiet, in-navigazzjoni, ir-ritratti, il-kuluri u d-daqsijiet tal-prodotti, fuq dak li ssejvja l-aħħar qari. Il-ħanut ma jerġax jinqara.',
  readScopeChecksCart:
    'Iż-żieda, il-qari lura, it-tneħħija u l-vojt tal-karrettun, kif ukoll il-prodotti, fuq dak li ssejvja l-aħħar qari. Il-ħanut ma jerġax jinqara.',
  readScopeChecksChat:
    'It-trasferiment lejn iċ-chat eżistenti tiegħek u l-prodotti, fuq dak li ssejvja l-aħħar qari. Il-ħanut ma jerġax jinqara.',
  readScopeChecksMarket:
    'Il-prodotti, il-prezzijiet u l-karrettun tas-suq, fuq dak li ssejvja l-aħħar qari. Il-ħanut ma jerġax jinqara.',
  checksCost: 'Il-kontrolli l-ġodda biss jużaw krediti tal-kumpanija',
  checksAfter:
    'Isalva abbozz ġdid jekk il-kontrolli jgħaddu. Xejn ma jasal għand ix-xerrejja qabel tippubblika.',
  checksAfterLive:
    'Isalva abbozz ġdid jekk il-kontrolli jgħaddu. L-assistent ippubblikat jibqa’ jaħdem, u x-xerrejja jaraw il-bidla biss wara li tippubblika.',
  checksStarted: 'Il-kontrolli bdew. Meta l-abbozz il-ġdid ikun lest, staqsi lil Chat jiċċekkjah.',
  checkingSavedWork: 'Qed jiġi ċċekkjat mill-ġdid ix-xogħol issejvjat',
  checkAgain: 'Iċċekkja mill-ġdid',
  checkingAgain: 'Qed jiġi ċċekkjat mill-ġdid…',
  reasonCheckAgain:
    'Iċċekkja mill-ġdid jittestja x-xogħol issejvjat darb’oħra, mingħajr ma jerġa’ jaqra l-ħanut. Il-kontrolli l-ġodda biss jużaw krediti tal-kumpanija.',
  fixWithChat: 'Irranġa ma’ Chat',
  fixReadPrompt:
    'Il-qari ta’ {name} ({domain}) waqaf. Ara x’ġara u għinni nħaddmu: għidli x’issib, issuġġerixxi x’nista’ ngħid lill-qari, imbagħad iċċekkjah mill-ġdid jew erġa’ aqrah b’dak il-ħjiel, l-inqas parti possibbli tal-ħanut.',
  fixReadPromptStep:
    'Il-qari ta’ {name} ({domain}) waqaf fil-pass “{step}”. Ara x’ġara u għinni nħaddmu: għidli x’issib, issuġġerixxi x’nista’ ngħid lill-qari, imbagħad iċċekkjah mill-ġdid jew erġa’ aqrah b’dak il-ħjiel, l-inqas parti possibbli tal-ħanut.',
  stepStateDone: 'lest',
  stepStateRunning: 'għaddej',
  stepStateWaiting: 'għadu ma bediex',
  stepStateFailed: 'waqaf',
  newVersionTitle: 'Il-verżjoni {version} lesta',
  newVersionBody: 'Ippubblikaha biex tissostitwixxi l-verżjoni {live}.',
  newVersionBodyLive: 'Ippubblikaha biex tissostitwixxi l-verżjoni {live} fuq {domain}.',
  previewSize: 'Daqs tal-preview',
  designWithChat: 'Iftaħ id-disinn',
  designReviewBody: 'Ħares lejn id-disinn attwali, imbagħad għid lil Chat x’tixtieq taġġusta.',
  designWhileTitle: 'Iddisinjah waqt li naqraw il-ħanut tiegħek',
  designWhileBody:
    'Chat jista’ jsawwar id-dehra, il-kliem u l-logo tal-assistent miegħek issa. Id-disinn ikun lest meta jintemm il-qari.',
  designPreviewNote: 'Preview bi prodotti ta’ eżempju sakemm jinqara l-ħanut tiegħek.',
  proposedDesign: 'Disinn propost',
  proposedDesignNote:
    'Il-widget b’din il-bidla, muri bi prodotti ta’ eżempju. L-approvazzjoni ssalvah bħala abbozz ġdid; il-pubblikazzjoni tibqa’ fil-paġna Storefront.',
  lineSources: 'Mill-paġni tal-ħanut:',
  readAgainConfirm:
    'Taqra {name} mill-ġdid? Jieħu madwar 10 sa 30 minuta u juża krediti tal-kumpanija.',
  readAgainLive: 'L-assistent ippubblikat jibqa’ jaħdem sakemm tippubblika l-verżjoni l-ġdida.',
  readAgainNow: 'Aqra mill-ġdid',
  switchStore: 'Ibdel il-ħanut jew żid ieħor',
  storesLabel: 'Il-ħwienet tiegħek',
  addStore: 'Żid ħanut ieħor',
  backToStores: 'Lura għall-ħwienet tiegħek',
  storeLive: 'Ippubblikat',
  storeReady: 'Lest',
  storeReading: 'Qed jinqara',
  storeStopped: 'Imwaqqaf',
  actionDesignTitle: 'Ibdel id-disinn tal-assistent tax-xiri',
  actionKnowledgeTitle: 'Ibdel dak li jaf l-assistent tax-xiri',
  readTitleProducts: 'Aqra l-prodotti mill-ġdid',
  readTitleCart: 'Iċċekkja l-karrettun mill-ġdid',
  readTitleTheirChat: 'Iċċekkja l-mod lejn iċ-chat tiegħek mill-ġdid',
  readTitleAll: 'Aqra l-ħanut kollu mill-ġdid',
  readTitleStore: 'Aqra ħanut ġdid',
  readTitleMarket: 'Żid suq',
  readShopLabel: 'Ħanut',
  readScopeLabel: 'Qari',
  readTimeLabel: 'Jieħu',
  readCostLabel: 'Spiża',
  readAfterLabel: 'Wara',
  readScopeProducts: 'Tiftix u issortjar tal-prodotti, ritratti, kuluri u daqsijiet tal-prodotti',
  readScopeCart: 'Żieda fil-karrettun u tneħħija minnu',
  readScopeTheirChat: 'Kif ix-xerrejja jaslu għaċ-chat tiegħek',
  readScopeAll: 'Prodotti, il-karrettun, iċ-chat tiegħek u s-swieq tiegħek',
  readScopeStore:
    'Prodotti, prezzijiet u daqsijiet, il-karrettun, iċ-chat tiegħek, id-dehra tiegħek u s-swieq tiegħek',
  readScopeMarket: '{market}: il-prodotti, il-prezzijiet u l-karrettun tiegħu',
  readTime: 'Normalment {min}–{max} minuta',
  readCost: 'Juża krediti tal-kumpanija',
  readCostLast: 'Juża krediti tal-kumpanija · l-aħħar qari ta’ dan il-ħanut uża {credits}',
  readCostLastPartial:
    'Juża krediti tal-kumpanija · l-aħħar qari ta’ dan il-ħanut, li waqaf kmieni, uża mill-inqas {credits}',
  readAfter: 'Isalva abbozz ġdid. Xejn ma jasal għand ix-xerrejja qabel tippubblika.',
  readAfterLive:
    'Isalva abbozz ġdid. L-assistent ippubblikat jibqa’ jaħdem, u x-xerrejja jaraw il-bidla biss wara li tippubblika.',
  readAfterNew: 'Isalva l-ewwel abbozz biex tipprovah. Xejn mhu ppubblikat.',
  readCartNote:
    'Biex jiċċekkja l-karrettun, iżid oġġett tat-test u jerġa’ jneħħih. Qatt ma jagħmel checkout.',
  readFocusLabel: 'Dak li Chat talab lill-qari jirranġa',
  readFooter: 'Xejn ma jibda qabel tapprova. Tista’ twaqqaf il-qari fit-tab Konfigurazzjoni.',
  readStartedAt:
    'Beda {time} · normalment {min}–{max} minuta. Meta l-abbozz il-ġdid ikun lest, staqsi lil Chat jiċċekkjah.',
  readFocusAll: 'Uri kollox',
  readFocusLess: 'Uri inqas',
  readStart: 'Ibda l-qari',
  addToSite: 'Żidu mas-sit tiegħek',
  readTab: 'Talba',
  readStarted: 'Il-qari beda. Meta l-abbozz il-ġdid ikun lest, staqsi lil Chat jiċċekkjah.',
  readFollow: 'Segwih fit-tab Konfigurazzjoni',
  proposedDesignLive:
    'Il-preview reali tal-ħanut tiegħek f’dan id-disinn. Staqsih xi ħaġa: xejn ma jiżdied ma’ karrettun reali. L-approvazzjoni ssalva abbozz ġdid; il-pubblikazzjoni tibqa’ fil-paġna Storefront.',
  designChanges: 'X’jinbidel',
  designReset: 'lura għall-valur predefinit',
  designGroupColors: 'Kuluri',
  designGroupType: 'Fonts',
  designGroupShape: 'Għamla',
  designGroupLauncher: 'Buttuna fuq il-paġna',
  designGroupPanel: 'Intestatura',
  designGroupProducts: 'Prodotti',
  designGroupCart: 'Il-karrettun tal-ħanut',
  designGroupCopy: 'Kliem',
  designGroupLogo: 'Logo',
  designGroupCss: 'CSS personalizzat',
  designColorBrand: 'Marka',
  designColorActions: 'Żid u Ibgħat',
  designColorPanel: 'Pannell',
  designColorSurface: 'Uċuħ',
  designColorInk: 'Test',
  designColorMuted: 'Test sekondarju',
  designColorLine: 'Linji',
  designColorBubble: 'Messaġġi tax-xerrej',
  designColorBubbleInk: 'Test tax-xerrej',
  designActionsBrand: 'Kulur tal-marka',
  designActionsInk: 'Kulur tat-test',
  designWordsName: 'Isem',
  designWordsGreeting: 'Tislima',
  designWordsStarters: 'Mistoqsijiet tal-bidu',
  designWordsLauncher: 'Tikketta tal-buttuna',
  designWordsPlaceholder: 'Test ta’ eżempju',
  designWordsAi: 'Tikketta AI',
  designWordsPerson: 'Link għal persuna',
  noteNew: 'Ġdid',
  noteChanged: 'Inbidel',
  noteRemoved: 'Imneħħi',
  noteSourceKnowledge: 'Paġna ta’ Għarfien: {title}',
  noteSourceFile: 'Fajl: {name}',
  noteSourceChat: 'Miċ-chat',
  knowledgeApprovalNote:
    'Ix-xerrejja jistgħu jaqraw kollox f’dawn in-noti. L-approvazzjoni ssalva abbozz ġdid: staqsih fit-tab Widget, imbagħad ippubblikah fit-tab Installazzjoni.',
  profileTitle: 'Dwar il-ħanut',
  profileChecked: 'Ivverifikat {date}',
  profileSources: 'Miktub minn',
  profileSample: '{count} prodotti meħuda bħala kampjun għal-livell tal-prezzijiet',
  profileSampleOne: '{count} prodott meħud bħala kampjun għal-livell tal-prezzijiet',
  profileAssistant: 'X’jista’ jagħmel l-assistent hawnhekk',
  profileTeam: 'Miktub mit-tim tagħkom',
  profileEmpty:
    'Għadu ma nkitibx. Itlob lil Chat iħaddem mixja tal-għarfien: jikteb dan mill-paġni tagħkom stess u minn dak li ra fuq is-sit tagħkom, u tistgħu żżidu linji tagħkom.',
  profileWithChat: 'Żid jew ibdel ma’ Chat',
  profilePrompt:
    'Għinni intejjeb dak li l-assistent tax-xiri għal {name} ({domain}) jaf dwar il-ħanut tagħna kollu kemm hu: x’ibigħu, għal xiex aħna magħrufin, għal min aħna u x’jistgħu jagħmlu x-xerrejja hawnhekk. L-ewwel aqra l-profil “Dwar il-ħanut” u għidli x’jonqos jew x’inhu ħażin. Kull ma ngħidlek dwar il-ħanut u ma jgħidu l-ebda paġna jidħol bħala linja mit-tim tagħna.',
  profilePartNavigation: 'Dipartimenti',
  profilePartWelcome: 'Tislima',
  profilePartMarkets: 'Swieq',
  profilePartFunctions: 'X’jista’ jagħmel l-assistent',
  profilePartProducts: 'Prodotti',
  profilePartPages: 'Paġni tas-servizz',
  profileCleared: 'Ineħħi l-profil miktub',
  knowledgeTitle: 'X’jaf',
  knowledgeBody:
    'Minbarra l-prodotti, il-prezzijiet u l-istokk tiegħek, li dejjem jaqra direttament mill-ħanut tiegħek.',
  knowledgeEmpty:
    'S’issa għandu biss il-katalgu tiegħek. Żid informazzjoni dwar il-kunsinna, ir-ritorni jew il-ħlas ma’ Chat, mill-paġni tiegħek jew minn Għarfien.',
  knowledgeWithChat: 'Żid ma’ Chat',
  knowledgeChangeWithChat: 'Ibdel ma’ Chat',
  knowledgePrompt:
    'Għinni nagħżel x’għandu jgħid l-assistent tax-xiri għal {name} ({domain}) lix-xerrejja minbarra l-prodotti tagħna, bħall-kunsinna, ir-ritorni u l-ħlas. Sibha fuq il-paġni tagħna jew f’Għarfien, u pproponi noti li nista’ napprova.',
  pagesTitle: 'Paġni li jaqra',
  pagesBody:
    'Il-paġni minn fejn ġejjin in-noti. Poneva jiċċekkjahom kull ġurnata, u Chat jgħarraf meta wieħed minnhom jinbidel.',
  pagesEmpty:
    'Għadu m’hemm l-ebda paġna. Hawn jidhru l-paġni li tiskopri l-qari tal-ħanut, u dawk li jissemmew f’Chat.',
  pageFound: 'instabet minn Poneva',
  pageLinkedFrom: 'marbut minn {page}',
  pageAddedByYou: 'miżjud manwalment',
  pageFromNote: 'imsemmija f’nota',
  pageNoteOne: '1 nota',
  pageNotesMany: '{count} noti',
  pageStatusOk: 'Aġġornat',
  pageStatusChanged: 'Inbidel',
  pageStatusUnreadable: 'Ma jistax jinqara',
  pageStatusUnchecked: 'Għadu ma ġiex iċċekkjat',
  pagesChangeWithChat: 'Ibdel ma’ Chat',
  pagesUpdateWithChat: 'Aġġorna n-noti ma’ Chat',
  pagesPrompt:
    'Għinni nibdel liema paġni l-assistent tax-xiri ta’ {name} ({domain}) jaqra għan-noti tiegħu. Urini l-paġni li juża issa u staqsini liema nżid jew neħħi.',
  pagesUpdatePrompt:
    'Xi paġni ta’ {name} ({domain}) inbidlu jew ma jistgħux jinqraw aktar mill-ħolqien tan-noti. Urini liema huma, u erġa’ agħmel in-noti minnhom bħala abbozz li nista’ nara.',
  viewCompare: 'Tqabbil',
  lookDeviceLabel: 'Qabbel fuq',
  lookShop: 'Il-ħanut tiegħek',
  lookShopCaption: 'Il-paġna ewlenija, inqrat {when}',
  lookWidget: 'L-assistent',
  lookWidgetCaption: 'Verżjoni {version}',
  lookLoading: 'L-assistent qed jitqiegħed ħdejn il-ħanut tiegħek…',
  lookFailed: 'It-tqabbil ma setax isir issa.',
  lookNoShopPicture: 'Il-qari ma żamm ebda immaġni tal-ħanut tiegħek f’din il-wisa’.',
  lookNoneTitle: 'Id-dehra tal-ħanut tiegħek stess ma nżammitx',
  lookNoneBody:
    'Dan il-qari ma rreġistrahiex. Aqra l-ħanut mill-ġdid minn Konfigurazzjoni, u l-logo, il-kuluri u l-paġni tiegħu jinżammu għat-tqabbil.',
  lookColoursShop: 'Kuluri li juża l-ħanut tiegħek',
  lookColoursAssistant: 'Kuluri tal-assistent',
  lookRoleHeader: 'Intestatura',
  lookRoleBrand: 'Marka',
  lookSame: 'L-istess bħal {colour} tal-ħanut tiegħek ({source})',
  lookOff: 'Qrib {colour} tal-ħanut tiegħek ({source}) imma mhux l-istess, differenza {delta}',
  lookOwn: 'Kulur tiegħu stess: il-ħanut tiegħek ma jużahx',
  lookNeutral: 'Il-ħanut tiegħek ma tgħabbiex għal dan: jintwera biss dak li żamm il-qari.',
  lookFromLogo: 'logo',
  lookFromTheme: 'kulur tat-tema',
  lookFromTile: 'kulur tal-kaxxa',
  lookFromMask: 'ikona mwaħħla',
  lookFromManifest: 'manifest tal-app',
  lookFromHeader: 'intestatura',
  lookFromButton: 'buttuni',
  lookFromVariable: 'kulur tal-istil',
  lookFix: 'Irranġa ma’ Chat',
  lookFixPrompt:
    'Il-kulur tal-assistent għal {role} huwa {colour}, qrib {exact} tal-ħanut tiegħek ({source}) imma mhux l-istess. Jekk jogħġbok uża l-kulur eżatt tal-ħanut tiegħek, {exact}.',
  lookSwatch: 'Kulur {colour}',
  errorDesignChecks: 'Din il-verżjoni ma għaddietx mill-kontrolli tad-disinn.',
  designSamplesBlocked:
    'Ma jistgħux jiġu ppruvati hawn l-iskemi tal-karti: is-sit tiegħek jimblokka żjarat awtomatiċi. Il-kuluri, it-tipi tal-ittri u l-logo xorta jaħdmu.',
  designCheckLabel: 'Tikketta tal-AI hija dgħajfa wisq biex tinqara.',
  designCheckReach: 'Buttuna ma tistax tintlaħaq.',
  designCheckSize: 'Parti mit-test hija żgħira wisq.',
  designCheckOverflow: 'Il-pannell jiskrollja lejn il-ġnub.',
  designCheckRender: 'Il-kontrolli ma setgħux jitwettqu. Erġa’ pprova f’mument.',
  fixDesignPrompt:
    'Ipprovajt nippubblika l-abbozz l-aktar riċenti ta’ {name} ({domain}), iżda ma għaddiex mill-kontrolli tad-disinn: {facts}. Irranġa d-disinn f’din iċ-chat u ċċekkjah fuq it-telefon u l-kompjuter. Tippubblikahx.',
};

const pl: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Asystent zakupowy',
  designUnconfirmed:
    'Zatrzymano przed potwierdzeniem zapisania projektu w Chat. Płótno asystenta pokazuje najnowszą wersję.',
  errorNewerVersion:
    'Od chwili otwarcia zapisano nowszą wersję. Najpierw ją obejrzyj, a potem opublikuj.',
  canvasSelectHint: 'Kliknij część asystenta, aby zmienić ją w Chat · Esc zatrzymuje',
  canvasDraft: 'Wersja {version} · jeszcze nieopublikowana',
  canvasDraftOverLive: 'Wersja {version} · opublikowana jest wersja {live}',
  canvasLive: 'Wersja {version} · opublikowana',
  canvasNoVersion: 'Pierwsza wersja powstanie po zakończeniu odczytywania sklepu',
  canvasPublish: 'Opublikuj na stronie Storefront',
  canvasFrameTitle: 'Asystent zakupowy {name}',
  viewLabel: 'Widok',
  photosGood: '{count} zdjęć produktów: całe i wyraźne',
  photosGoodOne: 'Zdjęcie produktu jest całe i wyraźne',
  canvasMissingTitle: 'Tego asystenta zakupowego nie ma w tym obszarze roboczym',
  canvasMissingBody: 'Należy do innego obszaru roboczego albo został usunięty.',
  canvasMissingLink: 'Zobacz asystentów w tym obszarze roboczym',
  photosLabel: 'Zdjęcia produktów',
  photosSmall: '{count} małych na kafelkach',
  photosCut: '{count} przyciętych',
  photosSoft: '{count} powiększonych i nieostrych',
  photosMissing: '{count} bez zdjęcia',
  photosFix: 'Napraw w Chat',
  photosPrompt:
    'Spraw, aby zdjęcia produktów na kartach wyglądały dobrze: każde ma być całe, wystarczająco duże na swoim kafelku i ostre.',
  photosFacts:
    '{total} zdjęć: {small} małych na kafelkach, {cut} przyciętych, {soft} powiększonych, {missing} bez zdjęcia. Rozmiary: {sizes}',
  viewDesign: 'Projekt',
  viewVersions: 'Wersje',
  versionsLoading: 'Ładowanie wersji…',
  versionsFailed: 'Nie udało się załadować wersji.',
  versionTitle: 'Wersja {version}',
  versionLive: 'Opublikowana',
  versionChecked: 'Sprawdzona',
  versionToday: 'Dzisiaj',
  versionYesterday: 'Wczoraj',
  versionEarlier: 'Wcześniej',
  versionByPerson: 'Na prośbę: {name}',
  versionByChat: 'Poproszono w Chat',
  versionFromReading: 'Z odczytu {domain}',
  versionPublishedBy: 'opublikował(a): {name}, {at}',
  versionPublish: 'Opublikuj',
  versionRestore: 'Przywróć…',
  versionRestoreTitle: 'Przywrócić wersję {version}?',
  versionRestoreBody:
    'Powstanie wersja {next} jako nowy szkic. Twoja strona nie zmieni się, dopóki jej nie opublikujesz.',
  versionRestoreConfirm: 'Przywróć',
  versionRestoreCancel: 'Anuluj',
  versionRestored: 'Przywrócono jako wersję {version}',
  versionRestoreFailed: 'Nie udało się przywrócić wersji.',
  compareTitle: 'Porównaj wersję {to} z wersją {from}',
  compareTitleLive: 'Porównaj wersję {to} z opublikowaną wersją {from}',
  compareHint: 'Przeciągnij, aby porównać',
  compareLabel: 'Porównaj wersję {from} i wersję {to}',
  compareTagLive: 'v{version} · Opublikowana',
  compareTag: 'v{version}',
  changesTitle: 'Co zmieniło się między wersją {from} a {to}',
  changesNone: 'Projekty są takie same. Zmieniły się tylko notatki lub dane sklepu.',
  changeAdded: 'Dodano',
  changeRemoved: 'Usunięto',
  changeChanged: 'Zmieniono',
  changeDrawn: 'rysowane logo',
  changeCss: 'własny CSS',
  changeYes: 'tak',
  changeNo: 'nie',
  'field.colors.brand': 'Kolor marki',
  'field.colors.actions': 'Kolor przycisków',
  'field.colors.panel': 'Kolor panelu',
  'field.colors.surface': 'Kolor powierzchni',
  'field.colors.ink': 'Kolor tekstu',
  'field.colors.muted': 'Kolor tekstu dodatkowego',
  'field.colors.line': 'Kolor linii',
  'field.colors.bubble': 'Kolor wiadomości kupującego',
  'field.colors.bubbleInk': 'Tekst wiadomości kupującego',
  'field.colors.header': 'Kolor nagłówka',
  'field.colors.headerInk': 'Kolor tekstu nagłówka',
  'field.colors.tints': 'Delikatne odcienie',
  'field.copy.assistantName': 'Nazwa',
  'field.copy.greeting': 'Powitanie',
  'field.copy.placeholder': 'Pole pytania',
  'field.copy.starters': 'Pytania startowe',
  'field.copy.launcherLabel': 'Słowa przycisku',
  'field.copy.aiLabel': 'Etykieta AI',
  'field.copy.personLabel': 'Link do osoby',
  'field.type.body': 'Czcionka tekstu',
  'field.type.display': 'Czcionka nagłówków',
  'field.type.fontCss': 'Źródło czcionki',
  'field.type.scale': 'Rozmiar tekstu',
  'field.shape.radius': 'Zaokrąglenie rogów',
  'field.shape.shadow': 'Cień',
  'field.shape.density': 'Odstępy',
  'field.launcher.style': 'Przycisk na komputerach',
  'field.launcher.phoneStyle': 'Przycisk na telefonach',
  'field.launcher.side': 'Strona przycisku',
  'field.launcher.icon': 'Ikona przycisku',
  'field.panel.header': 'Rozmiar nagłówka',
  'field.panel.opening': 'Pierwszy ekran',
  'field.products.layout': 'Produkty',
  'field.products.imageFit': 'Zdjęcia produktów',
  'field.products.imageRatio': 'Kształt kafelka zdjęcia',
  'field.products.imagePadding': 'Miejsce wokół zdjęć',
  'field.products.imageBackground': 'Kolor kafelka zdjęcia',
  'field.products.picker': 'Wybór rozmiaru',
  'field.products.showStock': 'Pokazuj stan magazynowy',
  'field.cart.landing': 'Animacja koszyka',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Kształt logo',
  'field.logo.plate': 'Tło logo',
  'field.css': 'Własny CSS',
  'value.ink': 'atramentowy',
  'value.brand': 'kolor marki',
  'value.small': 'mały',
  'value.medium': 'średni',
  'value.large': 'duży',
  'value.square': 'kwadratowy',
  'value.soft': 'delikatny',
  'value.round': 'okrągły',
  'value.flat': 'płaski',
  'value.deep': 'głęboki',
  'value.compact': 'zwarty',
  'value.comfortable': 'wygodny',
  'value.pill': 'pastylka ze słowami',
  'value.circle': 'okrąg',
  'value.right': 'prawa',
  'value.left': 'lewa',
  'value.logo': 'logo',
  'value.chat': 'dymek czatu',
  'value.sparkle': 'iskierka',
  'value.bag': 'torba',
  'value.full': 'pełne',
  'value.middle': 'pośrodku',
  'value.bottom': 'na dole',
  'value.rows': 'wiersze',
  'value.grid': 'siatka',
  'value.carousel': 'wiersz przewijany gestem',
  'value.contain': 'całe zdjęcie',
  'value.cover': 'wypełniony kafelek',
  'value.portrait': 'pionowy',
  'value.landscape': 'poziomy',
  'value.none': 'brak',
  'value.pop': 'unoszące się +1',
  'value.ring': 'fala',
  'value.blink': 'mrugnięcie',
  'value.chips': 'przyciski w wierszu',
  'value.menu': 'lista',
  'value.rounded': 'zaokrąglony',
  designCardTitle: 'Asystent zakupowy {name}',
  designCardSaved: 'Zapisano wersję {version} · jeszcze nieopublikowana',
  designCardSavedFirst: 'Zapisano projekt · pierwsza wersja go obejmie',
  designSaving: 'Zapisywanie projektu…',
  selectedAssistantPart: 'Wybrano: część asystenta',
  loading: 'Ładowanie Storefront…',
  disabledTitle: 'Storefront nie jest włączony w tym obszarze roboczym.',
  disabledBody:
    'Jest we wczesnym dostępie i udostępniamy go etapami. Zapytaj opiekuna Poneva, kiedy będzie dostępny dla Twojej firmy.',
  loadFailed: 'Nie udało się załadować Storefront.',
  startTitle: 'Asystent zakupowy dla Twojego sklepu',
  startBody:
    'Poneva odczytuje Twój sklep tak, jak korzysta z niego kupujący: wyszukiwanie, produkty, koszyk i używany już czat. Zajmuje to około 10–30 minut, wykorzystuje kredyty firmy, a Ty możesz dalej pracować.',
  urlLabel: 'Adres Twojego sklepu',
  urlPlaceholder: 'https://www.twojsklep.pl',
  startButton: 'Odczytaj mój sklep',
  starting: 'Uruchamianie…',
  startFailed: 'Nie udało się odczytać tego sklepu. Sprawdź adres i spróbuj ponownie.',
  tabConversations: 'Rozmowy',
  tabOverview: 'Przegląd',
  allAssistants: 'Wszyscy asystenci',
  allAssistantsNote: '{assistants} asystentów · {sites} witryn',
  allAssistantsMenuNote: '{count} rozmów w tym tygodniu',
  marketLabel: 'Rynek',
  allMarkets: 'Wszystkie rynki',
  allMarketsNote: '{count} witryn',
  scopeIn: '{assistant} w: {market}',
  askAbout: 'Zapytaj Chat o: {scope}',
  overviewAssistantsTitle: 'Asystenci i rynki',
  overviewMarketsTitle: 'Rynki',
  overviewAssistantsOnly: 'Asystenci',
  overviewIdle: '{count} asystentów bez rozmów',
  allAssistantsCount: '{assistants} asystentów',
  overviewFailed: 'Nie udało się załadować przeglądu.',
  colConversations: 'Rozmowy',
  colCart: 'Dodano do koszyka',
  colCheckout: 'Kasa',
  colPerson: 'Osoba',
  colCost: 'Kredyty',
  rowMarkets: '{count} rynków',
  rowVersion: 'wersja {version}',
  rowAria:
    '{name}, {sub}: {conversations} rozmów, {cart} dodanych do koszyka, {checkout} przejść do kasy, {person} próśb o osobę, {cost} kredytów',
  versionPublishedAt: 'opublikowano {at}',
  designColorHeader: 'Nagłówek',
  designColorHeaderInk: 'Tekst nagłówka',
  designGroupTranslations: 'Słowa w innych językach',
  topicsTitle: 'O co pytają kupujący',
  topicsNote: 'Tematy znalezione przez Chat w pierwszych pytaniach',
  startPagesTitle: 'Gdzie zaczynają się rozmowy',
  startPagesOne: 'Każda rozmowa zaczęła się na jednym rodzaju strony: {page}.',
  'startPage.product': 'Strony produktów',
  'startPage.search': 'Wyniki wyszukiwania',
  'startPage.start': 'Strona startowa',
  'startPage.cart': 'Koszyk',
  'startPage.other': 'Inne strony',
  addMarketInChat: 'Dodaj rynek w Chat',
  marketsTitle: 'Rynki',
  marketsBody:
    'Jeden asystent i jeden projekt. Każdy rynek ma własny język, walutę, produkty, koszyk i czat z obsługą.',
  marketChecked: 'sprawdzono {when}',
  'marketStatus.ready': 'Gotowy',
  'marketStatus.found': 'Nie dodano',
  'marketStatus.checking': 'Sprawdzanie',
  'marketStatus.failed': 'Wymaga poprawki',
  addMarketPrompt:
    'Dodaj kolejny rynek do asystenta zakupowego dla {name} ({domain}): znajdź inne kraje, języki lub witryny sklepu i zapytaj mnie, który dodać.',
  tabWidget: 'Widget',
  tabInstall: 'Instalacja',
  tabSetup: 'Konfiguracja',
  statusReading: 'Odczytywanie sklepu',
  readingProducts: 'Ponowne odczytywanie produktów',
  readingCart: 'Ponowne sprawdzanie koszyka',
  readingChat: 'Sprawdzanie drogi do własnego czatu',
  readingMarket: 'Dodawanie rynku {market}',
  readingMarketAny: 'Dodawanie rynku',
  statusReady: 'Gotowy do instalacji',
  statusFailed: 'Odczyt zatrzymany',
  statusLive: 'Aktywny · wersja {version} na {domain}',
  statusPublished: 'Opublikowany · wersja {version} · jeszcze nie w witrynie',
  statusPublishedUnverified: 'Opublikowany · wersja {version} · nie udało się sprawdzić witryny',
  stepOpen: 'Otwieranie witryny',
  stepSearch: 'Wyszukiwanie',
  stepProducts: 'Produkty',
  stepCart: 'Koszyk',
  stepChat: 'Twój obecny czat',
  stepLook: 'Wygląd i logo',
  stepChecks: 'Sprawdzanie działania',
  stepReady: 'Gotowe',
  stepDetailOpen: 'Otwieranie {domain}',
  stepDetailSearch: 'Znajdowanie produktów sklepu',
  stepDetailProducts: 'Odczytywanie produktów, cen, zdjęć, kolorów i rozmiarów',
  stepDetailCart: 'Testowanie koszyka: dodanie, odczyt, usunięcie i opróżnienie',
  stepDetailChat: 'Szukanie obecnego czatu',
  stepDetailLook: 'Odczytywanie logo i kolorów sklepu',
  stepDetailChecks: 'Testowanie wyszukiwania kupujących, produktów, przeglądania i koszyka',
  stepDetailChecksProducts:
    'Sprawdzanie wyszukiwania produktów, przeglądania, zdjęć, kolorów i rozmiarów',
  stepDetailChecksCart:
    'Sprawdzanie dodawania, odczytu, usuwania i opróżniania koszyka oraz odczytu produktów',
  stepDetailChecksChat: 'Sprawdzanie przekazania do obecnego czatu i odczytu produktów',
  stepDetailChecksMarket: 'Sprawdzanie produktów, cen i koszyka tego rynku',
  nowRunning: 'Teraz: {step}',
  nowProgress: 'Krok {n} z {total} · minęło {minutes} min',
  nowReady: 'Twój sklep jest gotowy na asystenta',
  nowReadyBody: 'Wyszukiwanie, produkty i koszyk działają.',
  nowLastRead: 'Ostatni odczyt: {when}',
  nowUsually: 'zwykle {min}–{max}',
  stepsRead: 'Odczytano {when} · Pokaż kroki',
  nowFailed: 'Odczyt zatrzymany',
  factAnswers: 'Odpowiada kupującym za pomocą',
  costCounting: 'Liczenie…',
  factPaid: 'Opłacane z',
  paidCompany: 'Kredytów firmy',
  creditsAmount: '{amount} kredytów',
  creditsOne: '{amount} kredyt',
  creditsUnder: 'poniżej {amount} kredytów',
  stopRun: 'Zatrzymaj odczyt',
  stopping: 'Zatrzymywanie…',
  closeNote: 'Możesz opuścić tę stronę. Odczyt będzie trwał.',
  readyNote: 'Dalej: wypróbuj widget, a potem go zainstaluj.',
  buildWidget: 'Wypróbuj widget',
  readAgain: 'Odczytaj sklep ponownie',
  foundTitle: 'Co znaleziono',
  foundProducts: 'Odczytano produkty i ceny',
  foundSearch: 'Wyszukiwanie działa',
  foundCart: 'Dodawanie do koszyka działa',
  foundChat: 'Twój czat: {vendor}',
  foundNoChat: 'Brak własnego czatu',
  foundLook: 'Twoje kolory i logo',
  widgetTitle: 'Twój widget',
  widgetBody:
    'Twój asystent w Twoich kolorach. Wypróbuj go tutaj: odpowiada na podstawie prawdziwych produktów. Nic nie zostanie dodane do prawdziwego koszyka.',
  widgetLoading: 'Ładowanie podglądu…',
  widgetFailed: 'Nie udało się załadować podglądu.',
  widgetNotReady: 'Widget będzie gotowy po ukończeniu kroków na karcie „Konfiguracja”.',
  installLineTitle: 'Dodaj tę linię do witryny',
  installLineBody:
    'Działa na każdej platformie: w sekcji head stron, a na Shopify w theme.liquid tuż przed </head>. Nic nie pokazuje, dopóki go nie opublikujesz.',
  installLineBodyLive:
    'Działa na każdej platformie: w sekcji head stron, a na Shopify w theme.liquid tuż przed </head>. Kupujący zobaczą wtedy wersję {version}.',
  installPolicyAsk: 'Czy witryna ustawia Content Security Policy?',
  installPolicy:
    'Jeśli witryna ustawia Content Security Policy, zezwól także na {origin} w {directives}, inaczej asystent nie wystartuje.',
  installPolicyRefuses:
    'Content Security Policy witryny odrzuca asystenta: zezwól na {origin} w {directives}, inaczej nic nie wystartuje.',
  installPolicyNonce:
    'Twoja polityka uruchamia tylko zaufane skrypty, więc dodaj linię z nonce stron.',
  installPolicyEval: "Asystent potrzebuje także 'unsafe-eval' w script-src.",
  copy: 'Kopiuj',
  copied: 'Skopiowano',
  domainsTitle: 'Witryny, na których odpowiada',
  domainsBody: 'Na każdej innej witrynie zachowuje ciszę, nawet jeśli ktoś skopiuje tę linię.',
  lineFound: 'Znaleziono linię',
  lineNotSeen: 'Jeszcze nie ma na stronie',
  lineUnverified:
    'Nie udało się sprawdzić: witryna blokuje automatyczne odwiedziny. Asystent nadal działa dla klientów.',
  lineChecked: 'sprawdzono {when}',
  lineCheckAgain: 'Sprawdź ponownie',
  lineChecking: 'Sprawdzanie strony…',
  installCheckBusy: 'Sprawdzanie jest teraz zajęte. Spróbuj ponownie za chwilę.',
  publishTitle: 'Publikowanie',
  publishBody:
    'Kupujący na {domain} zobaczą ją od razu. Możesz wycofać publikację w dowolnej chwili.',
  publishButton: 'Opublikuj wersję {version}',
  publishConfirm: 'Opublikować wersję {version} asystenta {name}?',
  publishNow: 'Opublikuj',
  notNow: 'Nie teraz',
  publishing: 'Publikowanie…',
  liveTitle: 'Opublikowano: wersja {version}',
  liveSince: 'Od {since}',
  unpublish: 'Wycofaj publikację',
  unpublishConfirm:
    'Usunąć wersję {version} z witryny? Kupujący nie zobaczą asystenta, dopóki nie opublikujesz go ponownie.',
  publishedLine: 'Opublikowano teraz: wersja {version}, od {since}',
  usageTitle: 'Wykorzystanie',
  usageBody: 'Wykorzystuje kredyty Twojej firmy, tak jak reszta Chat.',
  usageDiscovery: 'Odczytywanie sklepu',
  usageChats: 'Czaty kupujących',
  usageChatsValue: '{count} czatów · {cost}',
  last7: 'Ostatnie 7 dni',
  askWeek: 'Zapytaj Chat o ten tydzień',
  kpiConversations: 'Rozmowy',
  kpiVsPrevious: '{change} w porównaniu z poprzednim tygodniem',
  kpiCart: 'Dodano do koszyka',
  kpiCheckout: 'Dotarto do kasy',
  kpiPerson: 'Poproszono o osobę',
  kpiCost: 'Wykorzystane kredyty',
  ofConversations: '{percent} rozmów',
  checkoutNote: '{percent} rozmów',
  personNote: '{percent} otworzyło Twój czat',
  costNote: 'Około {amount} na rozmowę',
  costNoteUnder: 'Poniżej {amount} na rozmowę',
  sparkLabel: 'Rozmowy dziennie, ostatnie {days} dni',
  sparkDay: '{day}: {count} rozmów',
  filterAll: 'Wszystkie',
  filterCart: 'Dodano do koszyka',
  filterCheckout: 'Kasa',
  filterPerson: 'Osoba',
  outcomeCart: 'Dodano do koszyka',
  outcomeCheckout: 'Dotarto do kasy',
  outcomePerson: 'Poproszono o osobę',
  outcomeAnswered: 'Udzielono odpowiedzi',
  devicePhone: 'Telefon',
  deviceComputer: 'Komputer',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Rozpoczęto na stronie {page}',
  anonymous: 'Kupujący jest anonimowy.',
  askConversation: 'Zapytaj Chat o tę rozmowę',
  showOlderVersions: 'Pokaż wcześniejsze wersje',
  showOlder: 'Pokaż starsze rozmowy',
  filterCountsWeek: 'Liczby: ostatnie 7 dni',
  readAlreadyHere:
    'Ten sklep już tu jest, więc nic nowego się nie rozpoczęło i nie zużyto żadnych kredytów.',
  readOpenShop: 'Otwórz sklep',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Czego możemy się nauczyć z tej rozmowy?',
  askWeekMessage: 'Czego możemy się nauczyć z rozmów z tego tygodnia?',
  conversationsChipOne: '{name} · rozmowa z {when}',
  conversationsChipWeek: '{name} · ten tydzień',
  conversationsChipWeekIn: '{name} · {market} · ten tydzień',
  removeConversations: 'Usuń rozmowy',
  noConversations: 'Brak rozmów',
  noConversationsBody: 'Gdy kupujący użyją asystenta, ich rozmowy pojawią się tutaj.',
  pickConversation: 'Wybierz rozmowę, aby ją przeczytać.',
  eventCheckout: 'Przejście do kasy',
  eventPerson: 'Otwarto Twój czat',
  showMore: 'Pokaż więcej',
  urlInvalid: 'Użyj adresu sklepu https, na przykład https://www.twojsklep.pl.',
  errorNoCompany:
    'Storefront wymaga firmowego obszaru roboczego. Poproś administratora, aby Cię dodał.',
  errorPublishPermission:
    'Może to zrobić tylko osoba, która może publikować dla Twojej firmy. Poproś administratora.',
  actionFailed: 'To się nie udało. Spróbuj ponownie.',
  copyFailed: 'Nie udało się skopiować. Zaznacz linię i skopiuj ją samodzielnie.',
  reasonCancelled: 'Zatrzymano odczyt.',
  reasonFailed: 'Nie udało się dokończyć odczytu sklepu, więc asystent nie jest gotowy.',
  reasonFailedLive:
    'Nie udało się dokończyć odczytu sklepu. Opublikowany asystent nadal działa bez zmian.',
  reasonFailedKept:
    'Nie udało się dokończyć odczytu sklepu, więc nic się nie zmieniło: wersja {version} pozostaje bez zmian.',
  statusReadStopped: 'Gotowe · ostatni odczyt zatrzymano',
  reasonStepMarked: 'Zatrzymany krok jest zaznaczony poniżej.',
  reasonReadAgain: 'Ponowny odczyt rozpocznie się na podstawie tego, czego nauczył się ten odczyt.',
  reasonNotAStore:
    'Ten adres nie wygląda na sklep internetowy: nie znaleziono produktów do wyszukania. Sprawdź adres albo spróbuj głównej domeny sklepu.',
  reasonTooSlow:
    'Wyszukiwanie w Twoim sklepie odpowiada zbyt wolno jak na asystenta zakupowego: zajęło ponad 12 sekund. Pomóc może szybsze wyszukiwanie w sklepie lub późniejszy ponowny odczyt.',
  reasonOurSide:
    'Odczyt zatrzymał się z powodu problemu po naszej stronie, a nie w twoim sklepie, więc asystent nie jest jeszcze gotowy.',
  reasonOurSideLive:
    'Odczyt zatrzymał się z powodu problemu po naszej stronie, a nie w twoim sklepie. Opublikowany asystent nadal działa bez zmian.',
  reasonOurSideKept:
    'Odczyt zatrzymał się z powodu problemu po naszej stronie, a nie w twoim sklepie, więc nic się nie zmieniło: wersja {version} pozostaje bez zmian.',
  reasonTryLater: 'Spróbuj odczytać sklep ponownie za chwilę.',
  errorNoCredits:
    'Obecnie nie ma na to kredytów. Właściciel firmy może sprawdzić plan i limity w sekcji Usage.',
  errorReadUnavailable: 'Tego sklepu nie można teraz odczytać. Spróbuj ponownie za kilka minut.',
  reasonTimeout:
    'Odczyt osiągnął limit czasu, zanim się zakończył. Ponowny odczyt będzie kontynuowany na podstawie tego, co już sprawdzono.',
  retrying: 'Ponowne uruchamianie…',
  readTitleChecks: 'Sprawdź ponownie zapisany odczyt',
  checksScopeLabel: 'Zakres sprawdzenia',
  readScopeChecks:
    'Wyszukiwania kupujących, produkty, przeglądanie i koszyk – na tym, co zapisał ostatni odczyt. Sklep nie jest odczytywany ponownie.',
  readScopeChecksProducts:
    'Wyszukiwanie produktów, przeglądanie, zdjęcia, kolory i rozmiary – na tym, co zapisał ostatni odczyt. Sklep nie jest odczytywany ponownie.',
  readScopeChecksCart:
    'Dodawanie, odczyt, usuwanie i opróżnianie koszyka oraz odczyt produktów – na tym, co zapisał ostatni odczyt. Sklep nie jest odczytywany ponownie.',
  readScopeChecksChat:
    'Przekazanie do obecnego czatu i odczyt produktów – na tym, co zapisał ostatni odczyt. Sklep nie jest odczytywany ponownie.',
  readScopeChecksMarket:
    'Produkty, ceny i koszyk rynku – na tym, co zapisał ostatni odczyt. Sklep nie jest odczytywany ponownie.',
  checksCost: 'Tylko nowe sprawdzenia wykorzystują kredyty firmy',
  checksAfter:
    'Zapisuje nowy szkic, jeśli sprawdzenia przejdą. Kupujący niczego nie zobaczą, dopóki go nie opublikujesz.',
  checksAfterLive:
    'Zapisuje nowy szkic, jeśli sprawdzenia przejdą. Opublikowany asystent nadal działa, a kupujący zobaczą zmianę dopiero po publikacji.',
  checksStarted:
    'Sprawdzanie się rozpoczęło. Gdy nowy szkic będzie gotowy, poproś Chat o sprawdzenie.',
  checkingSavedWork: 'Ponowne sprawdzanie zapisanej pracy',
  checkAgain: 'Sprawdź ponownie',
  checkingAgain: 'Ponowne sprawdzanie…',
  reasonCheckAgain:
    '„Sprawdź ponownie” jeszcze raz testuje zapisaną pracę, bez ponownego odczytywania sklepu. Tylko nowe sprawdzenia wykorzystują kredyty firmy.',
  fixWithChat: 'Napraw w Chat',
  fixReadPrompt:
    'Odczyt sklepu {name} ({domain}) został zatrzymany. Sprawdź, co się stało, i pomóż mi to naprawić: powiedz, co znajdujesz, zaproponuj, co mogę przekazać odczytowi, a potem sprawdź go ponownie lub odczytaj go ponownie z tą wskazówką, obejmując jak najmniejszą część sklepu.',
  fixReadPromptStep:
    'Odczyt sklepu {name} ({domain}) został zatrzymany na kroku „{step}”. Sprawdź, co się stało, i pomóż mi to naprawić: powiedz, co znajdujesz, zaproponuj, co mogę przekazać odczytowi, a potem sprawdź go ponownie lub odczytaj go ponownie z tą wskazówką, obejmując jak najmniejszą część sklepu.',
  stepStateDone: 'ukończono',
  stepStateRunning: 'w toku',
  stepStateWaiting: 'nie rozpoczęto',
  stepStateFailed: 'zatrzymano',
  newVersionTitle: 'Wersja {version} jest gotowa',
  newVersionBody: 'Opublikuj ją, aby zastąpić wersję {live}.',
  newVersionBodyLive: 'Opublikuj ją, aby zastąpić wersję {live} na {domain}.',
  previewSize: 'Rozmiar podglądu',
  designWithChat: 'Otwórz wygląd',
  designReviewBody: 'Obejrzyj obecny wygląd i powiedz Chatowi, co chcesz dostosować.',
  designWhileTitle: 'Zaprojektuj go podczas odczytywania sklepu',
  designWhileBody:
    'Chat może już teraz pomóc Ci ułożyć wygląd, słowa i logo asystenta. Projekt będzie gotowy po zakończeniu odczytu.',
  designPreviewNote: 'Podgląd z przykładowymi produktami do czasu odczytania sklepu.',
  proposedDesign: 'Proponowany projekt',
  proposedDesignNote:
    'Widget z tą zmianą i przykładowymi produktami. Zatwierdzenie zapisze ją jako nowy szkic; publikowanie odbywa się na stronie Storefront.',
  lineSources: 'Ze stron sklepu:',
  readAgainConfirm:
    'Odczytać {name} ponownie? Zajmuje to około 10–30 minut i wykorzystuje kredyty firmy.',
  readAgainLive: 'Opublikowany asystent będzie działać, dopóki nie opublikujesz nowej wersji.',
  readAgainNow: 'Odczytaj ponownie',
  switchStore: 'Przełącz sklep lub dodaj sklep',
  storesLabel: 'Twoje sklepy',
  addStore: 'Dodaj kolejny sklep',
  backToStores: 'Wróć do swoich sklepów',
  storeLive: 'Opublikowany',
  storeReady: 'Gotowy',
  storeReading: 'Odczytywanie',
  storeStopped: 'Zatrzymany',
  actionDesignTitle: 'Zmień projekt asystenta zakupowego',
  actionKnowledgeTitle: 'Zmień wiedzę asystenta zakupowego',
  readTitleProducts: 'Odczytaj produkty ponownie',
  readTitleCart: 'Sprawdź koszyk ponownie',
  readTitleTheirChat: 'Sprawdź ponownie drogę do własnego czatu',
  readTitleAll: 'Odczytaj cały sklep ponownie',
  readTitleStore: 'Odczytaj nowy sklep',
  readTitleMarket: 'Dodaj rynek',
  readShopLabel: 'Sklep',
  readScopeLabel: 'Zakres odczytu',
  readTimeLabel: 'Czas',
  readCostLabel: 'Koszt',
  readAfterLabel: 'Po zakończeniu',
  readScopeProducts: 'Wyszukiwanie i sortowanie produktów, zdjęcia produktów, kolory i rozmiary',
  readScopeCart: 'Dodawanie do koszyka i usuwanie z niego',
  readScopeTheirChat: 'Jak kupujący docierają do Twojego czatu',
  readScopeAll: 'Produkty, koszyk, własny czat i rynki',
  readScopeStore: 'Produkty, ceny i rozmiary, koszyk, własny czat, wygląd i rynki',
  readScopeMarket: '{market}: jego produkty, ceny i koszyk',
  readTime: 'Zwykle {min}–{max} minut',
  readCost: 'Wykorzystuje kredyty firmy',
  readCostLast: 'Wykorzystuje kredyty firmy · ostatni odczyt tego sklepu wykorzystał {credits}',
  readCostLastPartial:
    'Wykorzystuje kredyty firmy · ostatni, przedwcześnie zatrzymany odczyt tego sklepu wykorzystał co najmniej {credits}',
  readAfter: 'Zapisuje nowy szkic. Kupujący niczego nie zobaczą, dopóki go nie opublikujesz.',
  readAfterLive:
    'Zapisuje nowy szkic. Opublikowany asystent nadal działa, a kupujący zobaczą zmianę dopiero po publikacji.',
  readAfterNew: 'Zapisuje pierwszy szkic do wypróbowania. Nic nie zostanie opublikowane.',
  readCartNote:
    'Aby sprawdzić koszyk, dodaje testowy produkt i ponownie go usuwa. Nigdy nie przechodzi do kasy.',
  readFocusLabel: 'Co Chat poprosił naprawić podczas odczytu',
  readFooter:
    'Nic się nie rozpocznie, dopóki nie zatwierdzisz. Możesz zatrzymać odczyt na karcie „Konfiguracja”.',
  readStartedAt:
    'Rozpoczęto {time} · zwykle {min}–{max} minut. Gdy nowy szkic będzie gotowy, poproś Chat o sprawdzenie.',
  readFocusAll: 'Pokaż wszystko',
  readFocusLess: 'Pokaż mniej',
  readStart: 'Rozpocznij odczyt',
  addToSite: 'Dodaj do witryny',
  readTab: 'Żądanie',
  readStarted: 'Odczyt się rozpoczął. Gdy nowy szkic będzie gotowy, poproś Chat o sprawdzenie.',
  readFollow: 'Śledź na karcie „Konfiguracja”',
  proposedDesignLive:
    'Rzeczywisty podgląd Twojego sklepu w tym projekcie. Zadaj mu pytanie: nic nie zostanie dodane do prawdziwego koszyka. Zatwierdzenie zapisze nowy szkic; publikowanie odbywa się na stronie Storefront.',
  designChanges: 'Co się zmienia',
  designReset: 'przywróć ustawienia domyślne',
  designGroupColors: 'Kolory',
  designGroupType: 'Czcionki',
  designGroupShape: 'Kształt',
  designGroupLauncher: 'Przycisk na stronie',
  designGroupPanel: 'Nagłówek',
  designGroupProducts: 'Produkty',
  designGroupCart: 'Koszyk sklepu',
  designGroupCopy: 'Słowa',
  designGroupLogo: 'Logo',
  designGroupCss: 'Własny CSS',
  designColorBrand: 'Marka',
  designColorActions: 'Dodaj i wyślij',
  designColorPanel: 'Panel',
  designColorSurface: 'Powierzchnie',
  designColorInk: 'Tekst',
  designColorMuted: 'Tekst dodatkowy',
  designColorLine: 'Linie',
  designColorBubble: 'Wiadomości kupującego',
  designColorBubbleInk: 'Tekst kupującego',
  designActionsBrand: 'Kolor marki',
  designActionsInk: 'Kolor tekstu',
  designWordsName: 'Nazwa',
  designWordsGreeting: 'Powitanie',
  designWordsStarters: 'Pytania startowe',
  designWordsLauncher: 'Etykieta przycisku',
  designWordsPlaceholder: 'Tekst zastępczy',
  designWordsAi: 'Etykieta AI',
  designWordsPerson: 'Link do osoby',
  noteNew: 'Nowe',
  noteChanged: 'Zmienione',
  noteRemoved: 'Usunięte',
  noteSourceKnowledge: 'Wiedza: {title}',
  noteSourceFile: 'Plik: {name}',
  noteSourceChat: 'Z czatu',
  knowledgeApprovalNote:
    'Kupujący mogą przeczytać wszystko w tych notatkach. Zatwierdzenie zapisuje nowy szkic: zadaj pytania na karcie Widget, a potem opublikuj go na karcie „Instalacja”.',
  profileTitle: 'O sklepie',
  profileChecked: 'Sprawdzono {date}',
  profileSources: 'Napisano na podstawie',
  profileSample: '{count} produktów pobrano jako próbkę poziomu cen',
  profileSampleOne: '{count} produkt pobrano jako próbkę poziomu cen',
  profileAssistant: 'Co asystent potrafi tutaj',
  profileTeam: 'Napisane przez Wasz zespół',
  profileEmpty:
    'Jeszcze nie napisano. Poproś Chat o przebieg wiedzy: napisze to z Waszych własnych stron i tego, co zobaczył w Waszej witrynie, a Wy możecie dodać własne wiersze.',
  profileWithChat: 'Dodaj lub zmień w Chat',
  profilePrompt:
    'Pomóż mi ulepszyć to, co asystent zakupowy sklepu {name} ({domain}) wie o naszym sklepie jako całości: co sprzedajemy, z czego jesteśmy znani, dla kogo jesteśmy i co klienci mogą tu zrobić. Najpierw przeczytaj profil „O sklepie“ i powiedz mi, czego brakuje lub co jest błędne. Wszystko, co powiem o sklepie, a czego nie podaje żadna strona, trafi jako wiersz od naszego zespołu.',
  profilePartNavigation: 'Działy',
  profilePartWelcome: 'Powitanie',
  profilePartMarkets: 'Rynki',
  profilePartFunctions: 'Co asystent potrafi',
  profilePartProducts: 'Produkty',
  profilePartPages: 'Strony obsługi',
  profileCleared: 'Czyści napisany profil',
  knowledgeTitle: 'Co wie',
  knowledgeBody:
    'Oprócz produktów, cen i stanów magazynowych, które zawsze odczytuje na żywo z Twojego sklepu.',
  knowledgeEmpty:
    'Na razie zna tylko katalog. Dodaj informacje o dostawie, zwrotach lub płatnościach za pomocą Chat, z własnych stron albo z Wiedzy.',
  knowledgeWithChat: 'Dodaj w Chat',
  knowledgeChangeWithChat: 'Zmień w Chat',
  knowledgePrompt:
    'Pomóż mi wybrać, co asystent zakupowy dla {name} ({domain}) ma mówić kupującym oprócz informacji o naszych produktach, na przykład o dostawie, zwrotach i płatnościach. Znajdź to na naszych stronach albo w naszej Wiedzy i zaproponuj notatki, które mogę zatwierdzić.',
  pagesTitle: 'Strony, które czyta',
  pagesBody:
    'Strony, z których pochodzą notatki. Poneva sprawdza je codziennie, a Chat informuje, gdy któraś się zmieni.',
  pagesEmpty:
    'Brak stron. Pojawią się tu strony znalezione przez odczyt sklepu oraz te wskazane w Chat.',
  pageFound: 'znaleziona przez Poneva',
  pageLinkedFrom: 'połączona z {page}',
  pageAddedByYou: 'dodana ręcznie',
  pageFromNote: 'wymieniona w notatce',
  pageNoteOne: '1 notatka',
  pageNotesMany: '{count} notatek',
  pageStatusOk: 'Aktualna',
  pageStatusChanged: 'Zmieniona',
  pageStatusUnreadable: 'Nie można odczytać',
  pageStatusUnchecked: 'Jeszcze nie sprawdzona',
  pagesChangeWithChat: 'Zmień w Chat',
  pagesUpdateWithChat: 'Zaktualizuj notatki w Chat',
  pagesPrompt:
    'Pomóż mi zmienić, które strony asystent zakupowy {name} ({domain}) czyta dla swoich notatek. Pokaż mi strony, których używa teraz, i zapytaj, które dodać lub usunąć.',
  pagesUpdatePrompt:
    'Niektóre strony {name} ({domain}) zmieniły się albo nie można ich już odczytać od czasu utworzenia notatek. Pokaż mi, które to są, i utwórz notatki z nich na nowo jako szkic, który mogę przejrzeć.',
  viewCompare: 'Porównanie',
  lookDeviceLabel: 'Porównaj na',
  lookShop: 'Twój sklep',
  lookShopCaption: 'Strona główna, odczytana {when}',
  lookWidget: 'Asystent',
  lookWidgetCaption: 'Wersja {version}',
  lookLoading: 'Ustawianie asystenta obok Twojego sklepu…',
  lookFailed: 'Nie udało się teraz wykonać porównania.',
  lookNoShopPicture: 'Odczyt nie zachował obrazu Twojego sklepu przy tej szerokości.',
  lookNoneTitle: 'Własny wygląd Twojego sklepu nie został zapisany',
  lookNoneBody:
    'Ten odczyt go nie zapisał. Odczytaj sklep ponownie w Konfiguracji, a jego logo, kolory i strony zostaną zachowane do porównania.',
  lookColoursShop: 'Kolory używane przez Twój sklep',
  lookColoursAssistant: 'Kolory asystenta',
  lookRoleHeader: 'Nagłówek',
  lookRoleBrand: 'Marka',
  lookSame: 'Taki sam jak kolor {colour} Twojego sklepu ({source})',
  lookOff:
    'Zbliżony do koloru {colour} Twojego sklepu ({source}), ale nie taki sam, różnica {delta}',
  lookOwn: 'Własny kolor: Twój sklep go nie używa',
  lookNeutral: 'Twój sklep nie został do tego wczytany: widać tylko to, co zachował odczyt.',
  lookFromLogo: 'logo',
  lookFromTheme: 'kolor motywu',
  lookFromTile: 'kolor kafelka',
  lookFromMask: 'przypięta ikona',
  lookFromManifest: 'manifest aplikacji',
  lookFromHeader: 'nagłówek',
  lookFromButton: 'przyciski',
  lookFromVariable: 'kolor stylu',
  lookFix: 'Napraw w Chat',
  lookFixPrompt:
    'Kolor asystenta ({role}) to {colour}, zbliżony do koloru {exact} Twojego sklepu ({source}), ale nie taki sam. Użyj dokładnego koloru Twojego sklepu, {exact}.',
  lookSwatch: 'Kolor {colour}',
  errorDesignChecks: 'Ta wersja nie przeszła kontroli projektu.',
  designSamplesBlocked:
    'Układów kart nie da się tu sprawdzić: Twoja witryna blokuje automatyczne odwiedziny. Kolory, czcionki i logo nadal działają.',
  designCheckLabel: 'Etykieta AI jest zbyt blada, by ją odczytać.',
  designCheckReach: 'Przycisk jest nieosiągalny.',
  designCheckSize: 'Część tekstu jest zbyt mała.',
  designCheckOverflow: 'Panel przewija się na boki.',
  designCheckRender: 'Nie udało się uruchomić kontroli. Spróbuj ponownie za chwilę.',
  fixDesignPrompt:
    'Próbowałem opublikować najnowszy szkic {name} ({domain}), ale nie przeszedł kontroli projektu: {facts}. Popraw projekt w tym czacie i sprawdź go na telefonie i komputerze. Nie publikuj go.',
};

const pt: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Assistente de compras',
  designUnconfirmed:
    'Parou antes de o Chat confirmar que o design foi guardado. A tela do assistente mostra a versão mais recente.',
  errorNewerVersion:
    'Foi guardada uma versão mais recente desde que abriu esta. Veja-a antes de publicar.',
  canvasSelectHint: 'Clique numa parte do assistente para a alterar no chat · Esc para sair',
  canvasDraft: 'Versão {version} · ainda não publicada',
  canvasDraftOverLive: 'Versão {version} · a versão {live} está publicada',
  canvasLive: 'Versão {version} · publicada',
  canvasNoVersion: 'A primeira versão chega quando a leitura da loja terminar',
  canvasPublish: 'Publicar na página Storefront',
  canvasFrameTitle: 'Assistente de compras de {name}',
  viewLabel: 'Vista',
  photosGood: '{count} fotos de produtos: inteiras e nítidas',
  photosGoodOne: 'A foto do produto está inteira e nítida',
  canvasMissingTitle: 'Este assistente de compras não está neste espaço de trabalho',
  canvasMissingBody: 'Pertence a outro espaço de trabalho ou foi removido.',
  canvasMissingLink: 'Ver os assistentes deste espaço de trabalho',
  photosLabel: 'Fotos dos produtos',
  photosSmall: '{count} pequenas nos seus espaços',
  photosCut: '{count} cortadas',
  photosSoft: '{count} ampliadas e pouco nítidas',
  photosMissing: '{count} sem foto',
  photosFix: 'Corrigir com o Chat',
  photosPrompt:
    'Faça com que as fotos dos produtos nos cartões fiquem bem: inteiras, nítidas e com tamanho suficiente no seu espaço.',
  photosFacts:
    '{total} fotos: {small} pequenas nos seus espaços, {cut} cortadas, {soft} ampliadas, {missing} sem foto. Tamanhos: {sizes}',
  viewDesign: 'Design',
  viewVersions: 'Versões',
  versionsLoading: 'A carregar versões…',
  versionsFailed: 'Não foi possível carregar as versões.',
  versionTitle: 'Versão {version}',
  versionLive: 'Publicada',
  versionChecked: 'Verificada',
  versionToday: 'Hoje',
  versionYesterday: 'Ontem',
  versionEarlier: 'Anteriormente',
  versionByPerson: 'Pedida por {name}',
  versionByChat: 'Pedida no chat',
  versionFromReading: 'Da leitura de {domain}',
  versionPublishedBy: 'publicada por {name}, {at}',
  versionPublish: 'Publicar',
  versionRestore: 'Restaurar…',
  versionRestoreTitle: 'Restaurar a versão {version}?',
  versionRestoreBody:
    'Passa a ser a versão {next}, um novo rascunho. O site só muda quando publicar.',
  versionRestoreConfirm: 'Restaurar',
  versionRestoreCancel: 'Cancelar',
  versionRestored: 'Restaurada como versão {version}',
  versionRestoreFailed: 'Não foi possível restaurar a versão.',
  compareTitle: 'Comparar a versão {to} com a {from}',
  compareTitleLive: 'Comparar a versão {to} com a {from}, publicada',
  compareHint: 'Arraste para comparar',
  compareLabel: 'Comparar as versões {from} e {to}',
  compareTagLive: 'v{version} · Publicada',
  compareTag: 'v{version}',
  changesTitle: 'O que mudou da versão {from} para a {to}',
  changesNone: 'Os designs são iguais. Só mudaram as notas ou os dados da loja.',
  changeAdded: 'Adicionado',
  changeRemoved: 'Removido',
  changeChanged: 'Alterado',
  changeDrawn: 'um logótipo desenhado',
  changeCss: 'CSS próprio',
  changeYes: 'sim',
  changeNo: 'não',
  'field.colors.brand': 'Cor da marca',
  'field.colors.actions': 'Cor dos botões',
  'field.colors.panel': 'Cor do painel',
  'field.colors.surface': 'Cor das superfícies',
  'field.colors.ink': 'Cor do texto',
  'field.colors.muted': 'Cor do texto secundário',
  'field.colors.line': 'Cor das linhas',
  'field.colors.bubble': 'Cor da mensagem do cliente',
  'field.colors.bubbleInk': 'Texto da mensagem do cliente',
  'field.colors.header': 'Cor do cabeçalho',
  'field.colors.headerInk': 'Cor do texto do cabeçalho',
  'field.colors.tints': 'Tons suaves',
  'field.copy.assistantName': 'Nome',
  'field.copy.greeting': 'Saudação',
  'field.copy.placeholder': 'Campo de pergunta',
  'field.copy.starters': 'Perguntas iniciais',
  'field.copy.launcherLabel': 'Texto do botão de abertura',
  'field.copy.aiLabel': 'Etiqueta de IA',
  'field.copy.personLabel': 'Ligação a uma pessoa',
  'field.type.body': 'Tipo de letra do texto',
  'field.type.display': 'Tipo de letra dos títulos',
  'field.type.fontCss': 'Origem do tipo de letra',
  'field.type.scale': 'Tamanho do texto',
  'field.shape.radius': 'Cantos',
  'field.shape.shadow': 'Sombra',
  'field.shape.density': 'Espaçamento',
  'field.launcher.style': 'Botão nos computadores',
  'field.launcher.phoneStyle': 'Botão nos telemóveis',
  'field.launcher.side': 'Lado do botão',
  'field.launcher.icon': 'Ícone do botão',
  'field.panel.header': 'Tamanho do cabeçalho',
  'field.panel.opening': 'Primeiro ecrã',
  'field.products.layout': 'Produtos',
  'field.products.imageFit': 'Fotos dos produtos',
  'field.products.imageRatio': 'Formato do espaço da foto',
  'field.products.imagePadding': 'Espaço à volta das fotos',
  'field.products.imageBackground': 'Cor de fundo da foto',
  'field.products.picker': 'Escolha do tamanho',
  'field.products.showStock': 'Stock visível',
  'field.cart.landing': 'Animação do carrinho',
  'field.logo.svg': 'Logótipo',
  'field.logo.shape': 'Formato do logótipo',
  'field.logo.plate': 'Fundo do logótipo',
  'field.css': 'CSS personalizado',
  'value.ink': 'cor do texto',
  'value.brand': 'cor da marca',
  'value.small': 'pequeno',
  'value.medium': 'médio',
  'value.large': 'grande',
  'value.square': 'quadrado',
  'value.soft': 'suave',
  'value.round': 'redondo',
  'value.flat': 'plano',
  'value.deep': 'profundo',
  'value.compact': 'compacto',
  'value.comfortable': 'espaçoso',
  'value.pill': 'cápsula com texto',
  'value.circle': 'redondo',
  'value.right': 'direita',
  'value.left': 'esquerda',
  'value.logo': 'logótipo',
  'value.chat': 'balão de conversa',
  'value.sparkle': 'brilho',
  'value.bag': 'saco',
  'value.full': 'completo',
  'value.middle': 'ao centro',
  'value.bottom': 'em baixo',
  'value.rows': 'linhas',
  'value.grid': 'grelha',
  'value.carousel': 'fila deslizante',
  'value.contain': 'foto inteira',
  'value.cover': 'espaço preenchido',
  'value.portrait': 'vertical',
  'value.landscape': 'horizontal',
  'value.none': 'nenhum',
  'value.pop': '+1 que sobe',
  'value.ring': 'onda',
  'value.blink': 'piscar',
  'value.chips': 'botões em fila',
  'value.menu': 'lista',
  'value.rounded': 'arredondado',
  designCardTitle: 'Assistente de compras de {name}',
  designCardSaved: 'Versão {version} guardada · ainda não publicada',
  designCardSavedFirst: 'Design guardado · será usado na primeira versão',
  designSaving: 'A guardar o design…',
  selectedAssistantPart: 'Selecionado: uma parte do assistente',
  loading: 'A carregar Storefront…',
  disabledTitle: 'O Storefront não está ativo neste espaço de trabalho.',
  disabledBody:
    'Está em acesso antecipado e chega por fases. Pergunte ao seu contacto na Poneva quando estará disponível para si.',
  loadFailed: 'Não foi possível carregar o Storefront.',
  startTitle: 'Um assistente de compras para a sua loja',
  startBody:
    'A Poneva lê a loja como um cliente a usa: pesquisa, produtos, carrinho e o chat que já tem. Demora cerca de 10 a 30 minutos e usa os créditos da empresa. Pode continuar a trabalhar.',
  urlLabel: 'Endereço da loja',
  urlPlaceholder: 'https://www.sualoja.pt',
  startButton: 'Ler a minha loja',
  starting: 'A iniciar…',
  startFailed: 'Não foi possível ler essa loja. Verifique o endereço e tente novamente.',
  tabConversations: 'Conversas',
  tabOverview: 'Resumo',
  allAssistants: 'Todos os assistentes',
  allAssistantsNote: '{assistants} assistentes · {sites} sites',
  allAssistantsMenuNote: '{count} conversas esta semana',
  marketLabel: 'Mercado',
  allMarkets: 'Todos os mercados',
  allMarketsNote: '{count} sites',
  scopeIn: '{assistant} em {market}',
  askAbout: 'Perguntar ao Chat sobre {scope}',
  overviewAssistantsTitle: 'Assistentes e mercados',
  overviewMarketsTitle: 'Mercados',
  overviewAssistantsOnly: 'Assistentes',
  overviewIdle: '{count} assistentes ainda sem conversas',
  allAssistantsCount: '{assistants} assistentes',
  overviewFailed: 'Não foi possível carregar o resumo.',
  colConversations: 'Conversas',
  colCart: 'Adicionado ao carrinho',
  colCheckout: 'Pagamento',
  colPerson: 'Pessoa',
  colCost: 'Créditos',
  rowMarkets: '{count} mercados',
  rowVersion: 'versão {version}',
  rowAria:
    '{name}, {sub}: {conversations} conversas, {cart} adições ao carrinho, {checkout} chegaram ao pagamento, {person} pediram uma pessoa, créditos {cost}',
  versionPublishedAt: 'publicada {at}',
  designColorHeader: 'Cabeçalho',
  designColorHeaderInk: 'Texto do cabeçalho',
  designGroupTranslations: 'Textos noutras línguas',
  topicsTitle: 'O que os clientes perguntam',
  topicsNote: 'Temas que o Chat encontrou nas primeiras perguntas',
  startPagesTitle: 'Onde começam as conversas',
  startPagesOne: 'Todas as conversas começaram num tipo de página: {page}.',
  'startPage.product': 'Páginas de produtos',
  'startPage.search': 'Resultados de pesquisa',
  'startPage.start': 'Página inicial',
  'startPage.cart': 'Carrinho',
  'startPage.other': 'Outras páginas',
  addMarketInChat: 'Adicionar um mercado no chat',
  marketsTitle: 'Mercados',
  marketsBody:
    'Um assistente e um design. Cada mercado tem a sua língua, moeda, produtos, carrinho e chat com uma pessoa.',
  marketChecked: 'verificado {when}',
  'marketStatus.ready': 'Pronto',
  'marketStatus.found': 'Por adicionar',
  'marketStatus.checking': 'A verificar',
  'marketStatus.failed': 'Precisa de um ajuste',
  addMarketPrompt:
    'Adicione outro mercado ao assistente de compras de {name} ({domain}): encontre os outros países, línguas ou sites da loja e pergunte-me qual adicionar.',
  tabWidget: 'Widget',
  tabInstall: 'Instalação',
  tabSetup: 'Configuração',
  statusReading: 'A ler a loja',
  readingProducts: 'A ler os produtos novamente',
  readingCart: 'A verificar o carrinho novamente',
  readingChat: 'A verificar o acesso ao seu chat',
  readingMarket: 'A adicionar o mercado {market}',
  readingMarketAny: 'A adicionar um mercado',
  statusReady: 'Pronto a instalar',
  statusFailed: 'Leitura interrompida',
  statusLive: 'No site · versão {version} em {domain}',
  statusPublished: 'Publicado · versão {version} · ainda não está no site',
  statusPublishedUnverified: 'Publicado · versão {version} · não foi possível verificar o site',
  stepOpen: 'A abrir o site',
  stepSearch: 'Pesquisa',
  stepProducts: 'Produtos',
  stepCart: 'Carrinho',
  stepChat: 'O seu chat atual',
  stepLook: 'O seu estilo e logótipo',
  stepChecks: 'A verificar se funciona',
  stepReady: 'Pronto',
  stepDetailOpen: 'A abrir {domain}',
  stepDetailSearch: 'A procurar os produtos da loja',
  stepDetailProducts: 'A ler produtos, preços, fotos, cores e tamanhos',
  stepDetailCart: 'A testar o carrinho: adicionar, consultar, remover e esvaziar',
  stepDetailChat: 'A procurar o seu chat atual',
  stepDetailLook: 'A ler o logótipo e as cores da loja',
  stepDetailChecks: 'A testar pesquisas dos clientes, produtos, navegação e carrinho',
  stepDetailChecksProducts: 'A verificar pesquisas, navegação, fotos, cores e tamanhos',
  stepDetailChecksCart:
    'A verificar adição, consulta, remoção e esvaziamento do carrinho, e leitura de produtos',
  stepDetailChecksChat: 'A verificar a passagem para o seu chat atual e a leitura de produtos',
  stepDetailChecksMarket: 'A verificar os produtos, preços e carrinho deste mercado',
  nowRunning: 'Agora: {step}',
  nowProgress: 'Passo {n} de {total} · {minutes} min até agora',
  nowReady: 'A loja está pronta para um assistente',
  nowReadyBody: 'A pesquisa, os produtos e o carrinho funcionam.',
  nowLastRead: 'Última leitura {when}',
  nowUsually: 'normalmente {min}–{max}',
  stepsRead: 'Leitura {when} · Ver os passos',
  nowFailed: 'Leitura interrompida',
  factAnswers: 'Responde aos clientes com',
  costCounting: 'A contar…',
  factPaid: 'Pago com',
  paidCompany: 'Créditos da empresa',
  creditsAmount: '{amount} créditos',
  creditsOne: '{amount} crédito',
  creditsUnder: 'menos de {amount} créditos',
  stopRun: 'Parar a leitura',
  stopping: 'A parar…',
  closeNote: 'Pode sair desta página. A leitura continua.',
  readyNote: 'A seguir: experimente o Widget e instale-o.',
  buildWidget: 'Experimentar o Widget',
  readAgain: 'Ler a loja novamente',
  foundTitle: 'O que encontrou',
  foundProducts: 'Produtos e preços lidos',
  foundSearch: 'A pesquisa funciona',
  foundCart: 'A adição ao carrinho funciona',
  foundChat: 'O seu chat {vendor}',
  foundNoChat: 'Sem chat próprio',
  foundLook: 'As suas cores e logótipo',
  widgetTitle: 'O seu Widget',
  widgetBody:
    'O seu assistente, com as suas cores. Experimente aqui: responde com os seus produtos reais. Não adiciona nada a um carrinho real.',
  widgetLoading: 'A carregar a pré-visualização…',
  widgetFailed: 'A pré-visualização não carregou.',
  widgetNotReady:
    'O Widget fica pronto quando os passos no separador Configuração estiverem concluídos.',
  installLineTitle: 'Adicione esta linha ao site',
  installLineBody:
    'Funciona em qualquer plataforma: no head das páginas; no Shopify, em theme.liquid, antes de </head>. Não mostra nada até publicar.',
  installLineBodyLive:
    'Funciona em qualquer plataforma: no head das páginas; no Shopify, em theme.liquid, antes de </head>. Os clientes passam a ver a versão {version}.',
  installPolicyAsk: 'O site usa uma Content Security Policy?',
  installPolicy:
    'Se o site usa uma Content Security Policy, permita também {origin} em {directives}, ou o assistente não pode iniciar.',
  installPolicyRefuses:
    'A Content Security Policy do site bloqueia o assistente: permita {origin} em {directives}, ou não poderá iniciar.',
  installPolicyNonce:
    'A política só executa scripts de confiança. Adicione a linha com o nonce das páginas.',
  installPolicyEval: "O assistente também precisa de 'unsafe-eval' em script-src.",
  copy: 'Copiar',
  copied: 'Copiado',
  domainsTitle: 'Sites onde responde',
  domainsBody: 'Fica em silêncio nos outros sites, mesmo que copiem a linha.',
  lineFound: 'Linha encontrada',
  lineNotSeen: 'Ainda não está na página',
  lineUnverified:
    'Não foi possível verificar: o site bloqueia visitas automáticas. O assistente continua a funcionar para os clientes.',
  lineChecked: 'verificado {when}',
  lineCheckAgain: 'Verificar novamente',
  lineChecking: 'A verificar a página…',
  installCheckBusy: 'A verificação está ocupada neste momento. Tente novamente daqui a pouco.',
  publishTitle: 'Publicar',
  publishBody: 'Os clientes em {domain} veem-no de imediato. Pode retirá-lo a qualquer momento.',
  publishButton: 'Publicar a versão {version}',
  publishConfirm: 'Publicar a versão {version} de {name}?',
  publishNow: 'Publicar',
  notNow: 'Agora não',
  publishing: 'A publicar…',
  liveTitle: 'Publicada: versão {version}',
  liveSince: 'Desde {since}',
  unpublish: 'Retirar publicação',
  unpublishConfirm:
    'Retirar a versão {version} do site? Os clientes deixam de ver o assistente até publicar novamente.',
  publishedLine: 'Publicada agora: versão {version}, desde {since}',
  usageTitle: 'Utilização',
  usageBody: 'Usa os créditos da empresa, como o resto do Chat.',
  usageDiscovery: 'Leitura da loja',
  usageChats: 'Conversas com clientes',
  usageChatsValue: '{count} conversas · {cost}',
  last7: 'Últimos 7 dias',
  askWeek: 'Perguntar ao Chat sobre esta semana',
  kpiConversations: 'Conversas',
  kpiVsPrevious: '{change} face à semana anterior',
  kpiCart: 'Adicionado ao carrinho',
  kpiCheckout: 'Chegaram ao pagamento',
  kpiPerson: 'Pediram uma pessoa',
  kpiCost: 'Créditos usados',
  ofConversations: '{percent} das conversas',
  checkoutNote: '{percent} das conversas',
  personNote: '{percent} abriram o seu chat',
  costNote: 'Cerca de {amount} por conversa',
  costNoteUnder: 'Menos de {amount} por conversa',
  sparkLabel: 'Conversas por dia, últimos {days} dias',
  sparkDay: '{day}: {count} conversas',
  filterAll: 'Todas',
  filterCart: 'Adicionado ao carrinho',
  filterCheckout: 'Pagamento',
  filterPerson: 'Pessoa',
  outcomeCart: 'Adicionado ao carrinho',
  outcomeCheckout: 'Chegou ao pagamento',
  outcomePerson: 'Pediu uma pessoa',
  outcomeAnswered: 'Respondida',
  devicePhone: 'Telemóvel',
  deviceComputer: 'Computador',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Começou em {page}',
  anonymous: 'O cliente é anónimo.',
  askConversation: 'Perguntar ao Chat sobre esta conversa',
  showOlderVersions: 'Mostrar versões anteriores',
  showOlder: 'Mostrar conversas mais antigas',
  filterCountsWeek: 'Contagens: últimos 7 dias',
  readAlreadyHere:
    'Esta loja já está aqui, por isso não foi iniciada nenhuma leitura nova nem foram usados créditos.',
  readOpenShop: 'Abrir loja',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'O que podemos aprender com esta conversa?',
  askWeekMessage: 'O que podemos aprender com as conversas desta semana?',
  conversationsChipOne: '{name} · conversa de {when}',
  conversationsChipWeek: '{name} · esta semana',
  conversationsChipWeekIn: '{name} · {market} · esta semana',
  removeConversations: 'Remover as conversas',
  noConversations: 'Ainda não há conversas',
  noConversationsBody: 'Quando os clientes usarem o assistente, as conversas aparecem aqui.',
  pickConversation: 'Escolha uma conversa para a ler.',
  eventCheckout: 'Foi para o pagamento',
  eventPerson: 'Abriu o seu chat',
  showMore: 'Mostrar mais',
  urlInvalid: 'Use o endereço https da loja, como https://www.sualoja.pt.',
  errorNoCompany:
    'O Storefront precisa de um espaço de trabalho da empresa. Peça ao administrador para o adicionar.',
  errorPublishPermission:
    'Só quem tem permissão para publicar pela empresa o pode fazer. Fale com o administrador.',
  actionFailed: 'Não funcionou. Tente novamente.',
  copyFailed: 'Não foi possível copiar. Selecione a linha e copie-a.',
  reasonCancelled: 'Parou a leitura.',
  reasonFailed: 'A leitura da loja não terminou, por isso o assistente não está pronto.',
  reasonFailedLive:
    'A leitura da loja não terminou. O assistente publicado continua a funcionar como antes.',
  reasonFailedKept:
    'A leitura da loja não terminou, por isso nada mudou: a versão {version} mantém-se como estava.',
  statusReadStopped: 'Pronto · a última leitura parou',
  reasonStepMarked: 'O passo que parou está assinalado abaixo.',
  reasonReadAgain: 'Uma nova leitura parte do que esta aprendeu.',
  reasonNotAStore:
    'Este endereço não parece uma loja online: não havia produtos para pesquisar. Verifique o endereço ou experimente o domínio principal da loja.',
  reasonTooSlow:
    'A pesquisa da loja é demasiado lenta para um assistente de compras: demorou mais de 12 segundos. Uma pesquisa mais rápida ou uma nova leitura mais tarde pode ajudar.',
  reasonOurSide:
    'A leitura parou devido a um problema do nosso lado, não na sua loja, por isso o assistente ainda não está pronto.',
  reasonOurSideLive:
    'A leitura parou devido a um problema do nosso lado, não na sua loja. O assistente publicado continua a funcionar como antes.',
  reasonOurSideKept:
    'A leitura parou devido a um problema do nosso lado, não na sua loja, por isso nada mudou: a versão {version} mantém-se como estava.',
  reasonTryLater: 'Tente ler a loja novamente daqui a pouco.',
  errorNoCredits:
    'Neste momento não há créditos para isto. Um proprietário da empresa pode verificar o plano e os limites em Usage.',
  errorReadUnavailable:
    'Não é possível ler esta loja neste momento. Tente novamente dentro de alguns minutos.',
  reasonTimeout:
    'A leitura atingiu o limite de tempo antes de terminar. Uma nova leitura continua a partir do que já foi verificado.',
  retrying: 'A iniciar novamente…',
  readTitleChecks: 'Verificar novamente a leitura guardada',
  checksScopeLabel: 'Verifica',
  readScopeChecks:
    'Pesquisas dos clientes, produtos, navegação e carrinho, sobre o que a última leitura guardou. A loja não é lida de novo.',
  readScopeChecksProducts:
    'Pesquisas, navegação, fotos, cores e tamanhos, sobre o que a última leitura guardou. A loja não é lida de novo.',
  readScopeChecksCart:
    'Adição, consulta, remoção e esvaziamento do carrinho, e leitura de produtos, sobre o que a última leitura guardou. A loja não é lida de novo.',
  readScopeChecksChat:
    'A passagem para o seu chat atual e a leitura de produtos, sobre o que a última leitura guardou. A loja não é lida de novo.',
  readScopeChecksMarket:
    'Os produtos, preços e carrinho do mercado, sobre o que a última leitura guardou. A loja não é lida de novo.',
  checksCost: 'Só as novas verificações usam créditos da empresa',
  checksAfter:
    'Guarda um novo rascunho se as verificações passarem. Os clientes não veem nada até publicar.',
  checksAfterLive:
    'Guarda um novo rascunho se as verificações passarem. O assistente publicado continua a funcionar e os clientes só veem a alteração quando publicar.',
  checksStarted:
    'As verificações começaram. Quando o novo rascunho estiver pronto, peça ao Chat para o verificar.',
  checkingSavedWork: 'A verificar novamente o trabalho guardado',
  checkAgain: 'Verificar novamente',
  checkingAgain: 'A verificar novamente…',
  reasonCheckAgain:
    '“Verificar novamente” testa outra vez o trabalho guardado, sem ler a loja de novo. Só as novas verificações usam créditos da empresa.',
  fixWithChat: 'Corrigir com o Chat',
  fixReadPrompt:
    'A leitura de {name} ({domain}) parou. Veja o que aconteceu e ajude-me a pô-la a funcionar: diga-me o que encontra, sugira o que posso indicar à leitura e depois verifique-a novamente ou leia de novo com essa indicação, lendo o mínimo possível da loja.',
  fixReadPromptStep:
    'A leitura de {name} ({domain}) parou no passo “{step}”. Veja o que aconteceu e ajude-me a pô-la a funcionar: diga-me o que encontra, sugira o que posso indicar à leitura e depois verifique-a novamente ou leia de novo com essa indicação, lendo o mínimo possível da loja.',
  stepStateDone: 'concluído',
  stepStateRunning: 'em curso',
  stepStateWaiting: 'por iniciar',
  stepStateFailed: 'interrompido',
  newVersionTitle: 'A versão {version} está pronta',
  newVersionBody: 'Publique-a para substituir a versão {live}.',
  newVersionBodyLive: 'Publique-a para substituir a versão {live} em {domain}.',
  previewSize: 'Tamanho da pré-visualização',
  designWithChat: 'Abrir design',
  designReviewBody: 'Veja o design atual e diga ao Chat o que gostaria de ajustar.',
  designWhileTitle: 'Desenhe-o enquanto lemos a loja',
  designWhileBody:
    'O Chat já pode ajudar a criar o aspeto, os textos e o logótipo do assistente. O design fica pronto quando a leitura terminar.',
  designPreviewNote: 'Uma pré-visualização com produtos de exemplo até a loja ser lida.',
  proposedDesign: 'Design proposto',
  proposedDesignNote:
    'O Widget com esta alteração e produtos de exemplo. Ao aprovar, guarda um novo rascunho; a publicação faz-se na página Storefront.',
  lineSources: 'Das páginas da loja:',
  readAgainConfirm:
    'Ler {name} novamente? Demora cerca de 10 a 30 minutos e usa créditos da empresa.',
  readAgainLive: 'O assistente publicado continua a funcionar até publicar a nova versão.',
  readAgainNow: 'Ler novamente',
  switchStore: 'Mudar de loja ou adicionar uma',
  storesLabel: 'As suas lojas',
  addStore: 'Adicionar outra loja',
  backToStores: 'Voltar às suas lojas',
  storeLive: 'Publicado',
  storeReady: 'Pronto',
  storeReading: 'A ler',
  storeStopped: 'Interrompido',
  actionDesignTitle: 'Alterar o design do assistente de compras',
  actionKnowledgeTitle: 'Alterar o que o assistente de compras sabe',
  readTitleProducts: 'Ler os produtos novamente',
  readTitleCart: 'Verificar o carrinho novamente',
  readTitleTheirChat: 'Verificar novamente o acesso ao seu chat',
  readTitleAll: 'Ler toda a loja novamente',
  readTitleStore: 'Ler uma nova loja',
  readTitleMarket: 'Adicionar um mercado',
  readShopLabel: 'Loja',
  readScopeLabel: 'Lê',
  readTimeLabel: 'Demora',
  readCostLabel: 'Custo',
  readAfterLabel: 'Depois',
  readScopeProducts: 'Pesquisa e ordenação de produtos, fotos, cores e tamanhos dos produtos',
  readScopeCart: 'Adicionar ao carrinho e remover dele',
  readScopeTheirChat: 'Como os clientes chegam ao seu chat',
  readScopeAll: 'Produtos, carrinho, o seu chat e os seus mercados',
  readScopeStore:
    'Produtos, preços e tamanhos, carrinho, o seu chat, o seu estilo e os seus mercados',
  readScopeMarket: '{market}: produtos, preços e carrinho',
  readTime: 'Normalmente {min}–{max} minutos',
  readCost: 'Usa créditos da empresa',
  readCostLast: 'Usa créditos da empresa · a última leitura desta loja usou {credits}',
  readCostLastPartial:
    'Usa créditos da empresa · a última leitura desta loja, interrompida antes do fim, usou pelo menos {credits}',
  readAfter: 'Guarda um novo rascunho. Os clientes não veem nada até publicar.',
  readAfterLive:
    'Guarda um novo rascunho. O assistente publicado continua a funcionar e os clientes só veem a alteração quando publicar.',
  readAfterNew: 'Guarda um primeiro rascunho para experimentar. Nada é publicado.',
  readCartNote:
    'Para verificar o carrinho, adiciona um artigo de teste e remove-o de seguida. Nunca conclui uma compra.',
  readFocusLabel: 'O que o Chat pediu à leitura para corrigir',
  readFooter: 'Nada começa sem a sua aprovação. Pode parar a leitura no separador Configuração.',
  readStartedAt:
    'Início: {time} · normalmente {min}–{max} minutos. Quando o novo rascunho estiver pronto, peça ao Chat para o verificar.',
  readFocusAll: 'Mostrar tudo',
  readFocusLess: 'Mostrar menos',
  readStart: 'Iniciar a leitura',
  addToSite: 'Adicionar ao site',
  readTab: 'Pedido',
  readStarted:
    'A leitura começou. Quando o novo rascunho estiver pronto, peça ao Chat para o verificar.',
  readFollow: 'Acompanhar no separador Configuração',
  proposedDesignLive:
    'A pré-visualização real da loja com este design. Faça uma pergunta: não adiciona nada a um carrinho real. Ao aprovar, guarda um novo rascunho; a publicação faz-se na página Storefront.',
  designChanges: 'O que muda',
  designReset: 'voltar ao padrão',
  designGroupColors: 'Cores',
  designGroupType: 'Tipos de letra',
  designGroupShape: 'Forma',
  designGroupLauncher: 'Botão na página',
  designGroupPanel: 'Cabeçalho',
  designGroupProducts: 'Produtos',
  designGroupCart: 'Carrinho da loja',
  designGroupCopy: 'Textos',
  designGroupLogo: 'Logótipo',
  designGroupCss: 'CSS personalizado',
  designColorBrand: 'Marca',
  designColorActions: 'Adicionar e Enviar',
  designColorPanel: 'Painel',
  designColorSurface: 'Superfícies',
  designColorInk: 'Texto',
  designColorMuted: 'Texto secundário',
  designColorLine: 'Linhas',
  designColorBubble: 'Mensagens do cliente',
  designColorBubbleInk: 'Texto do cliente',
  designActionsBrand: 'Cor da marca',
  designActionsInk: 'Cor do texto',
  designWordsName: 'Nome',
  designWordsGreeting: 'Saudação',
  designWordsStarters: 'Perguntas iniciais',
  designWordsLauncher: 'Texto do botão',
  designWordsPlaceholder: 'Texto de exemplo',
  designWordsAi: 'Etiqueta de IA',
  designWordsPerson: 'Ligação a uma pessoa',
  noteNew: 'Nova',
  noteChanged: 'Alterada',
  noteRemoved: 'Removida',
  noteSourceKnowledge: 'Página de conhecimento: {title}',
  noteSourceFile: 'Ficheiro: {name}',
  noteSourceChat: 'Do chat',
  knowledgeApprovalNote:
    'Os clientes podem ler tudo o que consta destas notas. Ao aprovar, guarda um novo rascunho: faça perguntas no separador Widget e publique-o no separador Instalação.',
  profileTitle: 'Sobre a loja',
  profileChecked: 'Verificado em {date}',
  profileSources: 'Escrito a partir de',
  profileSample: '{count} produtos amostrados para o nível de preços',
  profileSampleOne: '{count} produto amostrado para o nível de preços',
  profileAssistant: 'O que o assistente pode fazer aqui',
  profileTeam: 'Escrito pela vossa equipa',
  profileEmpty:
    'Ainda não escrito. Peça ao Chat para fazer uma passagem de conhecimento: escreve isto a partir das vossas páginas e do que viu no vosso site, e podem acrescentar linhas vossas.',
  profileWithChat: 'Acrescentar ou alterar com o Chat',
  profilePrompt:
    'Ajuda-me a melhorar o que o assistente de compras de {name} ({domain}) sabe sobre a nossa loja no seu conjunto: o que vendemos, pelo que somos conhecidos, para quem somos e o que os clientes podem fazer aqui. Lê primeiro o perfil «Sobre a loja» e diz-me o que falta ou está errado. Tudo o que eu te disser sobre a loja e que nenhuma página indique entra como linha da nossa equipa.',
  profilePartNavigation: 'Departamentos',
  profilePartWelcome: 'Saudação',
  profilePartMarkets: 'Mercados',
  profilePartFunctions: 'O que o assistente pode fazer',
  profilePartProducts: 'Produtos',
  profilePartPages: 'Páginas de serviço',
  profileCleared: 'Limpa o perfil escrito',
  knowledgeTitle: 'O que sabe',
  knowledgeBody: 'Além dos produtos, preços e stock, que lê sempre em direto da loja.',
  knowledgeEmpty:
    'Por agora, só o catálogo. Acrescente entregas, devoluções ou pagamentos com o Chat, a partir das suas páginas ou de Conhecimento.',
  knowledgeWithChat: 'Adicionar com o Chat',
  knowledgeChangeWithChat: 'Alterar com o Chat',
  knowledgePrompt:
    'Ajude-me a escolher o que o assistente de compras de {name} ({domain}) diz aos clientes além dos nossos produtos, como entregas, devoluções e pagamentos. Encontre a informação nas nossas páginas ou em Conhecimento e proponha notas que eu possa aprovar.',
  pagesTitle: 'Páginas que lê',
  pagesBody:
    'As páginas de onde vêm as notas. A Poneva verifica-as todos os dias, e o Chat avisa quando alguma muda.',
  pagesEmpty:
    'Ainda não há páginas. Aqui aparecem as páginas encontradas pela leitura da loja e as que forem indicadas no Chat.',
  pageFound: 'encontrada pela Poneva',
  pageLinkedFrom: 'ligada a partir de {page}',
  pageAddedByYou: 'adicionada manualmente',
  pageFromNote: 'citada numa nota',
  pageNoteOne: '1 nota',
  pageNotesMany: '{count} notas',
  pageStatusOk: 'Atualizada',
  pageStatusChanged: 'Alterada',
  pageStatusUnreadable: 'Não é possível ler',
  pageStatusUnchecked: 'Ainda não verificada',
  pagesChangeWithChat: 'Alterar com o Chat',
  pagesUpdateWithChat: 'Atualizar notas com o Chat',
  pagesPrompt:
    'Ajude-me a alterar as páginas que o assistente de compras de {name} ({domain}) lê para as suas notas. Mostre-me as páginas que usa agora e pergunte-me quais devo acrescentar ou remover.',
  pagesUpdatePrompt:
    'Algumas páginas de {name} ({domain}) mudaram ou já não podem ser lidas desde que as notas foram criadas. Mostre-me quais são e refaça as notas a partir delas, como rascunho que possa rever.',
  viewCompare: 'Comparação',
  lookDeviceLabel: 'Comparar em',
  lookShop: 'A sua loja',
  lookShopCaption: 'Página inicial, lida {when}',
  lookWidget: 'O assistente',
  lookWidgetCaption: 'Versão {version}',
  lookLoading: 'A colocar o assistente ao lado da sua loja…',
  lookFailed: 'Não foi possível fazer a comparação neste momento.',
  lookNoShopPicture: 'A leitura não guardou uma imagem da sua loja com esta largura.',
  lookNoneTitle: 'O estilo próprio da sua loja não foi guardado',
  lookNoneBody:
    'Esta leitura não o registou. Leia a loja novamente a partir de Configuração, e o seu logótipo, cores e páginas ficam guardados para comparação.',
  lookColoursShop: 'Cores que a sua loja usa',
  lookColoursAssistant: 'Cores do assistente',
  lookRoleHeader: 'Cabeçalho',
  lookRoleBrand: 'Marca',
  lookSame: 'Igual à cor {colour} da sua loja ({source})',
  lookOff: 'Próxima da cor {colour} da sua loja ({source}), mas não igual, diferença {delta}',
  lookOwn: 'Cor própria: a sua loja não a usa',
  lookNeutral: 'A sua loja não foi carregada para isto: só se mostra o que a leitura guardou.',
  lookFromLogo: 'logótipo',
  lookFromTheme: 'cor do tema',
  lookFromTile: 'cor do mosaico',
  lookFromMask: 'ícone afixado',
  lookFromManifest: 'manifesto da aplicação',
  lookFromHeader: 'cabeçalho',
  lookFromButton: 'botões',
  lookFromVariable: 'cor do estilo',
  lookFix: 'Corrigir com o Chat',
  lookFixPrompt:
    'A cor do assistente para {role} é {colour}, próxima da cor {exact} da sua loja ({source}), mas não igual. Use a cor exata da sua loja, {exact}.',
  lookSwatch: 'Cor {colour}',
  errorDesignChecks: 'Esta versão não passou nas verificações de design.',
  designSamplesBlocked:
    'Os layouts dos cartões não podem ser comprovados aqui: o seu site bloqueia visitas automáticas. As cores, os tipos de letra e o logótipo continuam a funcionar.',
  designCheckLabel: 'Uma etiqueta de IA está demasiado esbatida para ser lida.',
  designCheckReach: 'Um botão está inacessível.',
  designCheckSize: 'Parte do texto é demasiado pequena.',
  designCheckOverflow: 'O painel desloca-se para os lados.',
  designCheckRender: 'Não foi possível executar as verificações. Tente novamente daqui a pouco.',
  fixDesignPrompt:
    'Tentei publicar o rascunho mais recente de {name} ({domain}), mas não passou nas verificações de design: {facts}. Corrija o design neste chat e verifique-o no telemóvel e no computador. Não o publique.',
};

const ro: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Asistent pentru cumpărături',
  designUnconfirmed:
    'Oprit înainte ca Chat să confirme că designul a fost salvat. Panoul asistentului afișează cea mai nouă versiune.',
  errorNewerVersion:
    'O versiune mai nouă a fost salvată de când ai deschis această pagină. Consult-o mai întâi, apoi publică.',
  canvasSelectHint:
    'Fă clic pe o parte a asistentului pentru a o schimba în chat · Esc pentru oprire',
  canvasDraft: 'Versiunea {version} · încă nepublicată',
  canvasDraftOverLive: 'Versiunea {version} · versiunea {live} este publicată',
  canvasLive: 'Versiunea {version} · publicată',
  canvasNoVersion: 'Prima versiune apare după terminarea citirii magazinului',
  canvasPublish: 'Publică pe pagina Storefront',
  canvasFrameTitle: 'Asistentul pentru cumpărături al {name}',
  viewLabel: 'Vizualizare',
  photosGood: '{count} fotografii de produs: complete și clare',
  photosGoodOne: 'Fotografia produsului este completă și clară',
  canvasMissingTitle: 'Acest asistent pentru cumpărături nu se află în acest spațiu de lucru',
  canvasMissingBody: 'Îi aparține altui spațiu de lucru sau a fost eliminat.',
  canvasMissingLink: 'Vezi asistenții acestui spațiu de lucru',
  photosLabel: 'Fotografii de produs',
  photosSmall: '{count} sunt mici în casetele lor',
  photosCut: '{count} sunt tăiate',
  photosSoft: '{count} sunt mărite și neclare',
  photosMissing: '{count} nu au fotografie',
  photosFix: 'Repară cu Chat',
  photosPrompt:
    'Fă fotografiile produselor din carduri să arate bine: fiecare să fie completă, suficient de mare în casetă și clară.',
  photosFacts:
    '{total} fotografii: {small} mici în casetele lor, {cut} tăiate, {soft} mărite, {missing} fără fotografie. Dimensiuni: {sizes}',
  viewDesign: 'Design',
  viewVersions: 'Versiuni',
  versionsLoading: 'Se încarcă versiunile…',
  versionsFailed: 'Versiunile nu au putut fi încărcate.',
  versionTitle: 'Versiunea {version}',
  versionLive: 'Publicată',
  versionChecked: 'Verificată',
  versionToday: 'Astăzi',
  versionYesterday: 'Ieri',
  versionEarlier: 'Mai devreme',
  versionByPerson: 'Solicitată de {name}',
  versionByChat: 'Solicitată în chat',
  versionFromReading: 'Din citirea domeniului {domain}',
  versionPublishedBy: 'publicată de {name}, {at}',
  versionPublish: 'Publică',
  versionRestore: 'Restaurează…',
  versionRestoreTitle: 'Restaurezi versiunea {version}?',
  versionRestoreBody:
    'Va deveni versiunea {next}, o schiță nouă. Site-ul nu se schimbă până când nu publici.',
  versionRestoreConfirm: 'Restaurează',
  versionRestoreCancel: 'Anulează',
  versionRestored: 'Restaurată ca versiunea {version}',
  versionRestoreFailed: 'Versiunea nu a putut fi restaurată.',
  compareTitle: 'Compară versiunea {to} cu versiunea {from}',
  compareTitleLive: 'Compară versiunea {to} cu versiunea publicată {from}',
  compareHint: 'Trage pentru a compara',
  compareLabel: 'Compară versiunea {from} cu versiunea {to}',
  compareTagLive: 'v{version} · Publicată',
  compareTag: 'v{version}',
  changesTitle: 'Ce s-a schimbat de la versiunea {from} la {to}',
  changesNone: 'Designurile sunt identice. S-au schimbat doar notițele sau datele magazinului.',
  changeAdded: 'Adăugat',
  changeRemoved: 'Eliminat',
  changeChanged: 'Schimbat',
  changeDrawn: 'un logo desenat',
  changeCss: 'propriul CSS',
  changeYes: 'da',
  changeNo: 'nu',
  'field.colors.brand': 'Culoarea brandului',
  'field.colors.actions': 'Culoarea butoanelor',
  'field.colors.panel': 'Culoarea panoului',
  'field.colors.surface': 'Culoarea suprafeței',
  'field.colors.ink': 'Culoarea textului',
  'field.colors.muted': 'Culoarea textului discret',
  'field.colors.line': 'Culoarea liniilor',
  'field.colors.bubble': 'Culoarea mesajelor cumpărătorului',
  'field.colors.bubbleInk': 'Textul mesajelor cumpărătorului',
  'field.colors.header': 'Culoarea antetului',
  'field.colors.headerInk': 'Culoarea textului antetului',
  'field.colors.tints': 'Nuanțe discrete',
  'field.copy.assistantName': 'Nume',
  'field.copy.greeting': 'Salut',
  'field.copy.placeholder': 'Caseta întrebării',
  'field.copy.starters': 'Întrebări de început',
  'field.copy.launcherLabel': 'Textul butonului',
  'field.copy.aiLabel': 'Eticheta AI',
  'field.copy.personLabel': 'Link către o persoană',
  'field.type.body': 'Fontul textului',
  'field.type.display': 'Fontul titlurilor',
  'field.type.fontCss': 'Sursa fontului',
  'field.type.scale': 'Dimensiunea textului',
  'field.shape.radius': 'Colțuri',
  'field.shape.shadow': 'Umbră',
  'field.shape.density': 'Spațiere',
  'field.launcher.style': 'Buton pe computere',
  'field.launcher.phoneStyle': 'Buton pe telefoane',
  'field.launcher.side': 'Latura butonului',
  'field.launcher.icon': 'Pictograma butonului',
  'field.panel.header': 'Dimensiunea antetului',
  'field.panel.opening': 'Primul ecran',
  'field.products.layout': 'Produse',
  'field.products.imageFit': 'Fotografii de produs',
  'field.products.imageRatio': 'Forma casetei foto',
  'field.products.imagePadding': 'Spațiul din jurul fotografiilor',
  'field.products.imageBackground': 'Culoarea casetei foto',
  'field.products.picker': 'Alegerea mărimii',
  'field.products.showStock': 'Stoc afișat',
  'field.cart.landing': 'Animația coșului',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Forma logo-ului',
  'field.logo.plate': 'Fundalul logo-ului',
  'field.css': 'CSS personalizat',
  'value.ink': 'cerneală',
  'value.brand': 'culoarea brandului',
  'value.small': 'mic',
  'value.medium': 'mediu',
  'value.large': 'mare',
  'value.square': 'pătrat',
  'value.soft': 'difuz',
  'value.round': 'rotund',
  'value.flat': 'plat',
  'value.deep': 'profund',
  'value.compact': 'compact',
  'value.comfortable': 'aerisit',
  'value.pill': 'capsulă cu text',
  'value.circle': 'cerc',
  'value.right': 'dreapta',
  'value.left': 'stânga',
  'value.logo': 'logo',
  'value.chat': 'bulă de chat',
  'value.sparkle': 'sclipire',
  'value.bag': 'geantă',
  'value.full': 'complet',
  'value.middle': 'la mijloc',
  'value.bottom': 'jos',
  'value.rows': 'rânduri',
  'value.grid': 'grilă',
  'value.carousel': 'un rând pe care îl glisezi',
  'value.contain': 'fotografie completă',
  'value.cover': 'casetă umplută',
  'value.portrait': 'portret',
  'value.landscape': 'peisaj',
  'value.none': 'niciuna',
  'value.pop': '+1 care urcă',
  'value.ring': 'undă',
  'value.blink': 'clipire',
  'value.chips': 'butoane într-un rând',
  'value.menu': 'o listă',
  'value.rounded': 'rotunjit',
  designCardTitle: 'Asistentul pentru cumpărături al {name}',
  designCardSaved: 'Versiunea {version} salvată · încă nepublicată',
  designCardSavedFirst: 'Design salvat · prima versiune îl va conține',
  designSaving: 'Se salvează designul…',
  selectedAssistantPart: 'Selectat: o parte a asistentului',
  loading: 'Se încarcă Storefront…',
  disabledTitle: 'Storefront nu este activat în acest spațiu de lucru.',
  disabledBody:
    'Este în acces timpuriu și este introdus treptat. Întreabă persoana ta de contact Poneva când va fi disponibil pentru voi.',
  loadFailed: 'Storefront nu a putut fi încărcat.',
  startTitle: 'Un asistent pentru cumpărături pentru magazinul tău',
  startBody:
    'Poneva îți citește magazinul așa cum îl folosește un cumpărător: căutarea, produsele, coșul și chatul pe care îl ai deja. Durează aproximativ 10–30 de minute, folosește creditele companiei și poți continua să lucrezi.',
  urlLabel: 'Adresa magazinului tău',
  urlPlaceholder: 'https://www.magazinul-tau.ro',
  startButton: 'Citește-mi magazinul',
  starting: 'Se pornește…',
  startFailed: 'Magazinul nu a putut fi citit. Verifică adresa și încearcă din nou.',
  tabConversations: 'Conversații',
  tabOverview: 'Prezentare generală',
  allAssistants: 'Toți asistenții',
  allAssistantsNote: '{assistants} asistenți · {sites} site-uri',
  allAssistantsMenuNote: '{count} conversații în această săptămână',
  marketLabel: 'Piață',
  allMarkets: 'Toate piețele',
  allMarketsNote: '{count} site-uri',
  scopeIn: '{assistant} în {market}',
  askAbout: 'Întreabă Chat despre {scope}',
  overviewAssistantsTitle: 'Asistenți și piețe',
  overviewMarketsTitle: 'Piețe',
  overviewAssistantsOnly: 'Asistenți',
  overviewIdle: '{count} asistenți fără conversații încă',
  allAssistantsCount: '{assistants} asistenți',
  overviewFailed: 'Prezentarea generală nu a putut fi încărcată.',
  colConversations: 'Conversații',
  colCart: 'Adăugate în coș',
  colCheckout: 'Finalizare comandă',
  colPerson: 'Persoană',
  colCost: 'Credite',
  rowMarkets: '{count} piețe',
  rowVersion: 'versiunea {version}',
  rowAria:
    '{name}, {sub}: {conversations} conversații, {cart} adăugate în coș, {checkout} au ajuns la finalizarea comenzii, {person} au cerut o persoană, {cost} credite',
  versionPublishedAt: 'publicată {at}',
  designColorHeader: 'Antet',
  designColorHeaderInk: 'Textul antetului',
  designGroupTranslations: 'Cuvinte în alte limbi',
  topicsTitle: 'Ce întreabă cumpărătorii',
  topicsNote: 'Subiecte găsite de Chat în primele întrebări',
  startPagesTitle: 'Unde încep conversațiile',
  startPagesOne: 'Fiecare conversație a început pe un tip de pagină: {page}.',
  'startPage.product': 'Pagini de produs',
  'startPage.search': 'Rezultate de căutare',
  'startPage.start': 'Pagina de pornire',
  'startPage.cart': 'Coș',
  'startPage.other': 'Alte pagini',
  addMarketInChat: 'Adaugă o piață, în chat',
  marketsTitle: 'Piețe',
  marketsBody:
    'Un asistent și un design. Fiecare piață are propria limbă, monedă, produse, coș și chat cu o persoană.',
  marketChecked: 'verificată {when}',
  'marketStatus.ready': 'Gata',
  'marketStatus.found': 'Nu este adăugată',
  'marketStatus.checking': 'Se verifică',
  'marketStatus.failed': 'Necesită o remediere',
  addMarketPrompt:
    'Adaugă o altă piață asistentului pentru cumpărături al {name} ({domain}): găsește celelalte țări, limbi sau site-uri ale magazinului și întreabă-mă pe care să o adăugăm.',
  tabWidget: 'Widget',
  tabInstall: 'Instalare',
  tabSetup: 'Configurare',
  statusReading: 'Se citește magazinul tău',
  readingProducts: 'Se citesc din nou produsele',
  readingCart: 'Se verifică din nou coșul',
  readingChat: 'Se verifică drumul către chatul tău',
  readingMarket: 'Se adaugă piața {market}',
  readingMarketAny: 'Se adaugă o piață',
  statusReady: 'Gata de instalare',
  statusFailed: 'Citirea s-a oprit',
  statusLive: 'Activ · versiunea {version} pe {domain}',
  statusPublished: 'Publicată · versiunea {version} · încă nu este pe site-ul tău',
  statusPublishedUnverified:
    'Publicată · versiunea {version} · site-ul tău nu a putut fi verificat',
  stepOpen: 'Se deschide site-ul',
  stepSearch: 'Căutare',
  stepProducts: 'Produse',
  stepCart: 'Coș',
  stepChat: 'Chatul tău existent',
  stepLook: 'Aspectul și logo-ul tău',
  stepChecks: 'Se verifică funcționarea',
  stepReady: 'Gata',
  stepDetailOpen: 'Se deschide {domain}',
  stepDetailSearch: 'Se găsesc produsele magazinului',
  stepDetailProducts: 'Se citesc produsele, prețurile, fotografiile, culorile și mărimile',
  stepDetailCart: 'Se încearcă coșul: adăugare, citire, eliminare și golire',
  stepDetailChat: 'Se caută chatul tău existent',
  stepDetailLook: 'Se citesc logo-ul și culorile magazinului',
  stepDetailChecks: 'Se încearcă căutări de cumpărător, produse, navigare și coșul',
  stepDetailChecksProducts:
    'Se verifică căutările de produse, navigarea, fotografiile, culorile și mărimile',
  stepDetailChecksCart:
    'Se verifică adăugarea, citirea, eliminarea și golirea coșului, precum și citirea produselor',
  stepDetailChecksChat: 'Se verifică transferul către chatul tău existent și citirea produselor',
  stepDetailChecksMarket: 'Se verifică produsele, prețurile și coșul acestei piețe',
  nowRunning: 'Acum: {step}',
  nowProgress: 'Pasul {n} din {total} · {minutes} min până acum',
  nowReady: 'Magazinul tău este gata pentru un asistent',
  nowReadyBody: 'Căutarea, produsele și coșul funcționează.',
  nowLastRead: 'Ultima citire {when}',
  nowUsually: 'de obicei {min}–{max}',
  stepsRead: 'Citit {when} · Arată pașii',
  nowFailed: 'Citirea s-a oprit',
  factAnswers: 'Răspunde cumpărătorilor cu',
  costCounting: 'Se numără…',
  factPaid: 'Plătit din',
  paidCompany: 'Creditele companiei',
  creditsAmount: '{amount} credite',
  creditsOne: '{amount} credit',
  creditsUnder: 'sub {amount} credite',
  stopRun: 'Oprește rularea',
  stopping: 'Se oprește…',
  closeNote: 'Poți părăsi această pagină. Rularea continuă.',
  readyNote: 'Urmează: încearcă Widget, apoi instalează-l.',
  buildWidget: 'Încearcă Widget',
  readAgain: 'Citește din nou magazinul',
  foundTitle: 'Ce a găsit',
  foundProducts: 'Produse și prețuri citite',
  foundSearch: 'Căutarea funcționează',
  foundCart: 'Adăugarea în coș funcționează',
  foundChat: 'Chatul tău {vendor}',
  foundNoChat: 'Nu există un chat propriu',
  foundLook: 'Culorile și logo-ul tău',
  widgetTitle: 'Widget',
  widgetBody:
    'Asistentul tău, în culorile tale. Încearcă-l aici: răspunde cu produsele tale reale. Nimic nu este adăugat într-un coș real.',
  widgetLoading: 'Se încarcă previzualizarea…',
  widgetFailed: 'Previzualizarea nu s-a încărcat.',
  widgetNotReady: 'Widget este gata după finalizarea pașilor din fila Configurare.',
  installLineTitle: 'Adaugă această linie pe site-ul tău',
  installLineBody:
    'Funcționează pe orice platformă: în head-ul paginilor tale, iar pe Shopify în theme.liquid, chiar înainte de </head>. Nu afișează nimic până când nu publici.',
  installLineBodyLive:
    'Funcționează pe orice platformă: în head-ul paginilor tale, iar pe Shopify în theme.liquid, chiar înainte de </head>. Cumpărătorii vor întâlni apoi versiunea {version}.',
  installPolicyAsk: 'Site-ul tău setează o politică Content Security Policy?',
  installPolicy:
    'Dacă site-ul tău setează o politică Content Security Policy, permite și {origin} în {directives}, altfel asistentul nu poate porni.',
  installPolicyRefuses:
    'Politica Content Security Policy a site-ului refuză asistentul: permite {origin} în {directives}, altfel nimic nu va porni.',
  installPolicyNonce:
    'Politica ta rulează doar scripturile în care are încredere, așa că adaugă linia cu nonce-ul paginilor tale.',
  installPolicyEval: "Asistentul are nevoie și de 'unsafe-eval' în script-src.",
  copy: 'Copiază',
  copied: 'Copiat',
  domainsTitle: 'Site-uri pe care răspunde',
  domainsBody: 'Rămâne tăcut pe orice alt site, chiar și pe unul care copiază linia.',
  lineFound: 'Linie găsită',
  lineNotSeen: 'Încă nu este pe pagină',
  lineUnverified:
    'Nu s-a putut verifica: site-ul blochează vizitele automate. Asistentul funcționează în continuare pentru clienți.',
  lineChecked: 'verificată {when}',
  lineCheckAgain: 'Verifică din nou',
  lineChecking: 'Se verifică pagina…',
  installCheckBusy: 'Verificarea este ocupată acum. Încercați din nou în scurt timp.',
  publishTitle: 'Publică',
  publishBody: 'Cumpărătorii de pe {domain} îl văd imediat. Îl poți retrage oricând.',
  publishButton: 'Publică versiunea {version}',
  publishConfirm: 'Publici versiunea {version} a asistentului {name}?',
  publishNow: 'Publică',
  notNow: 'Nu acum',
  publishing: 'Se publică…',
  liveTitle: 'Publicat: versiunea {version}',
  liveSince: 'Din {since}',
  unpublish: 'Retrage publicarea',
  unpublishConfirm:
    'Elimini versiunea {version} de pe site? Cumpărătorii nu vor mai vedea asistentul până când publici din nou.',
  publishedLine: 'Publicată acum: versiunea {version}, din {since}',
  usageTitle: 'Utilizare',
  usageBody: 'Folosește creditele companiei, ca restul Chatului.',
  usageDiscovery: 'Citirea magazinului',
  usageChats: 'Chat-uri cu cumpărătorii',
  usageChatsValue: '{count} chat-uri · {cost}',
  last7: 'Ultimele 7 zile',
  askWeek: 'Întreabă Chat despre această săptămână',
  kpiConversations: 'Conversații',
  kpiVsPrevious: '{change} față de săptămâna anterioară',
  kpiCart: 'Adăugate în coș',
  kpiCheckout: 'Au ajuns la finalizarea comenzii',
  kpiPerson: 'Au cerut o persoană',
  kpiCost: 'Credite folosite',
  ofConversations: '{percent} din conversații',
  checkoutNote: '{percent} din conversații',
  personNote: '{percent} au deschis chatul tău',
  costNote: 'Aproximativ {amount} per conversație',
  costNoteUnder: 'Sub {amount} per conversație',
  sparkLabel: 'Conversații pe zi, ultimele {days} zile',
  sparkDay: '{day}: {count} conversații',
  filterAll: 'Toate',
  filterCart: 'Adăugate în coș',
  filterCheckout: 'Finalizare comandă',
  filterPerson: 'Persoană',
  outcomeCart: 'Adăugate în coș',
  outcomeCheckout: 'Au ajuns la finalizarea comenzii',
  outcomePerson: 'Au cerut o persoană',
  outcomeAnswered: 'Răspuns',
  devicePhone: 'Telefon',
  deviceComputer: 'Computer',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · A început pe {page}',
  anonymous: 'Cumpărătorul este anonim.',
  askConversation: 'Întreabă Chat despre această conversație',
  showOlderVersions: 'Arată versiunile anterioare',
  showOlder: 'Arată conversațiile mai vechi',
  filterCountsWeek: 'Număr: ultimele 7 zile',
  readAlreadyHere:
    'Acest magazin este deja aici, așa că nu a început nimic nou și nu s-au folosit credite.',
  readOpenShop: 'Deschide-l',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Ce putem învăța din această conversație?',
  askWeekMessage: 'Ce putem învăța din conversațiile din această săptămână?',
  conversationsChipOne: '{name} · conversație din {when}',
  conversationsChipWeek: '{name} · săptămâna aceasta',
  conversationsChipWeekIn: '{name} · {market} · săptămâna aceasta',
  removeConversations: 'Elimină conversațiile',
  noConversations: 'Încă nu există conversații',
  noConversationsBody: 'Când cumpărătorii folosesc asistentul, conversațiile lor apar aici.',
  pickConversation: 'Alege o conversație pentru a o citi.',
  eventCheckout: 'A mers la finalizarea comenzii',
  eventPerson: 'Ți-a deschis chatul',
  showMore: 'Arată mai mult',
  urlInvalid: 'Folosește adresa https a magazinului tău, de exemplu https://www.magazinul-tau.ro.',
  errorNoCompany:
    'Storefront are nevoie de un spațiu de lucru al companiei. Cere-i administratorului să te adauge.',
  errorPublishPermission:
    'Doar persoanele care pot publica pentru compania ta pot face acest lucru. Cere-i administratorului.',
  actionFailed: 'Nu a funcționat. Încearcă din nou.',
  copyFailed: 'Nu s-a putut copia. Selectează linia și copiaz-o manual.',
  reasonCancelled: 'Ai oprit rularea.',
  reasonFailed: 'Citirea nu s-a putut încheia pe magazinul tău, așa că asistentul nu este gata.',
  reasonFailedLive:
    'Citirea nu s-a putut încheia pe magazinul tău. Asistentul publicat continuă să funcționeze ca înainte.',
  reasonFailedKept:
    'Citirea nu s-a putut încheia pe magazinul tău, așa că nimic nu s-a schimbat: versiunea {version} rămâne neschimbată.',
  statusReadStopped: 'Gata · ultima citire s-a oprit',
  reasonStepMarked: 'Pasul la care s-a oprit este marcat mai jos.',
  reasonReadAgain: 'O nouă citire pornește de la ce a învățat această citire.',
  reasonNotAStore:
    'Această adresă nu pare să fie un magazin online: nu au existat produse de căutat. Verifică adresa sau încearcă domeniul principal al magazinului.',
  reasonTooSlow:
    'Căutarea magazinului tău răspunde prea lent pentru un asistent de cumpărături: a durat peste 12 secunde. O căutare mai rapidă pe magazin sau o nouă citire mai târziu poate ajuta.',
  reasonOurSide:
    'Citirea s-a oprit din cauza unei probleme la noi, nu la magazinul tău, așa că asistentul nu este încă gata.',
  reasonOurSideLive:
    'Citirea s-a oprit din cauza unei probleme la noi, nu la magazinul tău. Asistentul publicat continuă să funcționeze ca înainte.',
  reasonOurSideKept:
    'Citirea s-a oprit din cauza unei probleme la noi, nu la magazinul tău, așa că nimic nu s-a schimbat: versiunea {version} rămâne neschimbată.',
  reasonTryLater: 'Încearcă să citești magazinul din nou peste puțin timp.',
  errorNoCredits:
    'Momentan nu există credite pentru asta. Un proprietar al companiei poate verifica planul și limitele în Usage.',
  errorReadUnavailable: 'Magazinul nu poate fi citit acum. Încearcă din nou peste câteva minute.',
  reasonTimeout:
    'Citirea și-a atins limita de timp înainte să se termine. O nouă citire continuă de la ce a verificat deja.',
  retrying: 'Se pornește din nou…',
  readTitleChecks: 'Verifică din nou citirea salvată',
  checksScopeLabel: 'Verifică',
  readScopeChecks:
    'Căutările cumpărătorilor, produsele, navigarea și coșul, pe ce a salvat ultima citire. Magazinul nu este citit din nou.',
  readScopeChecksProducts:
    'Căutările de produse, navigarea, fotografiile, culorile și mărimile, pe ce a salvat ultima citire. Magazinul nu este citit din nou.',
  readScopeChecksCart:
    'Adăugarea, citirea, eliminarea și golirea coșului, precum și citirea produselor, pe ce a salvat ultima citire. Magazinul nu este citit din nou.',
  readScopeChecksChat:
    'Transferul către chatul tău existent și citirea produselor, pe ce a salvat ultima citire. Magazinul nu este citit din nou.',
  readScopeChecksMarket:
    'Produsele, prețurile și coșul pieței, pe ce a salvat ultima citire. Magazinul nu este citit din nou.',
  checksCost: 'Doar verificările noi folosesc creditele companiei',
  checksAfter:
    'Salvează o schiță nouă dacă verificările trec. Nimic nu ajunge la cumpărători până când nu publici.',
  checksAfterLive:
    'Salvează o schiță nouă dacă verificările trec. Asistentul publicat continuă să funcționeze, iar cumpărătorii văd schimbarea doar după publicare.',
  checksStarted:
    'Verificările au început. Când noua schiță este gata, cere-i lui Chat să o verifice.',
  checkingSavedWork: 'Se verifică din nou munca salvată',
  checkAgain: 'Verifică din nou',
  checkingAgain: 'Se verifică din nou…',
  reasonCheckAgain:
    '„Verifică din nou” testează încă o dată munca salvată, fără a citi din nou magazinul. Doar verificările noi folosesc creditele companiei.',
  fixWithChat: 'Repară cu Chat',
  fixReadPrompt:
    'Citirea magazinului {name} ({domain}) s-a oprit. Uită-te ce s-a întâmplat și ajută-mă să funcționeze: spune-mi ce găsești, sugerează ce i-aș putea spune citirii, apoi verifică din nou sau citește din nou cu acest indiciu, cât mai puțin din magazin.',
  fixReadPromptStep:
    'Citirea magazinului {name} ({domain}) s-a oprit la pasul „{step}”. Uită-te ce s-a întâmplat și ajută-mă să funcționeze: spune-mi ce găsești, sugerează ce i-aș putea spune citirii, apoi verifică din nou sau citește din nou cu acest indiciu, cât mai puțin din magazin.',
  stepStateDone: 'terminat',
  stepStateRunning: 'în desfășurare',
  stepStateWaiting: 'neînceput',
  stepStateFailed: 'oprit',
  newVersionTitle: 'Versiunea {version} este gata',
  newVersionBody: 'Public-o pentru a înlocui versiunea {live}.',
  newVersionBodyLive: 'Public-o pentru a înlocui versiunea {live} pe {domain}.',
  previewSize: 'Dimensiunea previzualizării',
  designWithChat: 'Deschide designul',
  designReviewBody: 'Vezi designul actual, apoi spune-i lui Chat ce ai dori să ajustezi.',
  designWhileTitle: 'Proiectează-l în timp ce citim magazinul',
  designWhileBody:
    'Chat poate modela împreună cu tine aspectul, textele și logo-ul asistentului. Designul este gata când se termină citirea.',
  designPreviewNote: 'O previzualizare cu produse exemplu, până când magazinul tău este citit.',
  proposedDesign: 'Design propus',
  proposedDesignNote:
    'Widgetul cu această schimbare, afișat cu produse exemplu. Aprobarea îl salvează ca schiță nouă; publicarea se face pe pagina Storefront.',
  lineSources: 'Din paginile magazinului:',
  readAgainConfirm:
    'Citești din nou {name}? Durează aproximativ 10–30 de minute și folosește creditele companiei.',
  readAgainLive: 'Asistentul publicat continuă să funcționeze până când publici noua versiune.',
  readAgainNow: 'Citește din nou',
  switchStore: 'Schimbă magazinul sau adaugă unul',
  storesLabel: 'Magazinele tale',
  addStore: 'Adaugă alt magazin',
  backToStores: 'Înapoi la magazinele tale',
  storeLive: 'Publicat',
  storeReady: 'Gata',
  storeReading: 'Se citește',
  storeStopped: 'Oprit',
  actionDesignTitle: 'Schimbă designul asistentului pentru cumpărături',
  actionKnowledgeTitle: 'Schimbă ce știe asistentul pentru cumpărături',
  readTitleProducts: 'Citește din nou produsele',
  readTitleCart: 'Verifică din nou coșul',
  readTitleTheirChat: 'Verifică din nou drumul către chatul tău',
  readTitleAll: 'Citește din nou întregul magazin',
  readTitleStore: 'Citește un magazin nou',
  readTitleMarket: 'Adaugă o piață',
  readShopLabel: 'Magazin',
  readScopeLabel: 'Citește',
  readTimeLabel: 'Durată',
  readCostLabel: 'Cost',
  readAfterLabel: 'După',
  readScopeProducts: 'Căutarea și sortarea produselor, fotografii de produs, culori și mărimi',
  readScopeCart: 'Adăugarea în coș și eliminarea din coș',
  readScopeTheirChat: 'Cum ajung cumpărătorii la chatul tău',
  readScopeAll: 'Produse, coșul, chatul tău și piețele tale',
  readScopeStore: 'Produse, prețuri și mărimi, coșul, chatul tău, aspectul tău și piețele tale',
  readScopeMarket: '{market}: produsele, prețurile și coșul său',
  readTime: 'De obicei {min}–{max} minute',
  readCost: 'Folosește creditele companiei',
  readCostLast:
    'Folosește creditele companiei · ultima citire a acestui magazin a folosit {credits}',
  readCostLastPartial:
    'Folosește creditele companiei · ultima citire a acestui magazin, oprită devreme, a folosit cel puțin {credits}',
  readAfter: 'Salvează o schiță nouă. Nimic nu ajunge la cumpărători până când nu publici.',
  readAfterLive:
    'Salvează o schiță nouă. Asistentul publicat continuă să funcționeze, iar cumpărătorii văd schimbarea doar după publicare.',
  readAfterNew: 'Salvează o primă schiță pe care să o încerci. Nimic nu este publicat.',
  readCartNote:
    'Pentru a verifica coșul, adaugă un articol de test și îl elimină imediat. Nu ajunge niciodată la finalizarea comenzii.',
  readFocusLabel: 'Ce a cerut Chat să fie reparat prin citire',
  readFooter: 'Nimic nu pornește până când nu aprobi. Poți opri citirea din fila Configurare.',
  readStartedAt:
    'Pornită {time} · de obicei {min}–{max} minute. Când noua schiță este gata, cere-i lui Chat să o verifice.',
  readFocusAll: 'Arată tot',
  readFocusLess: 'Arată mai puțin',
  readStart: 'Pornește citirea',
  addToSite: 'Adaugă pe site-ul tău',
  readTab: 'Solicitare',
  readStarted: 'Citirea a început. Când noua schiță este gata, cere-i lui Chat să o verifice.',
  readFollow: 'Urmărește citirea în fila Configurare',
  proposedDesignLive:
    'Previzualizarea reală a magazinului tău în acest design. Întreabă-l ceva: nimic nu este adăugat într-un coș real. Aprobarea salvează o schiță nouă; publicarea se face pe pagina Storefront.',
  designChanges: 'Ce se schimbă',
  designReset: 'revenire la setarea implicită',
  designGroupColors: 'Culori',
  designGroupType: 'Fonturi',
  designGroupShape: 'Formă',
  designGroupLauncher: 'Butonul de pe pagină',
  designGroupPanel: 'Antet',
  designGroupProducts: 'Produse',
  designGroupCart: 'Coșul magazinului',
  designGroupCopy: 'Texte',
  designGroupLogo: 'Logo',
  designGroupCss: 'CSS personalizat',
  designColorBrand: 'Brand',
  designColorActions: 'Adaugă și trimite',
  designColorPanel: 'Panou',
  designColorSurface: 'Suprafețe',
  designColorInk: 'Text',
  designColorMuted: 'Text secundar',
  designColorLine: 'Linii',
  designColorBubble: 'Mesajele cumpărătorului',
  designColorBubbleInk: 'Textul cumpărătorului',
  designActionsBrand: 'Culoarea brandului',
  designActionsInk: 'Culoarea textului',
  designWordsName: 'Nume',
  designWordsGreeting: 'Salut',
  designWordsStarters: 'Întrebări de început',
  designWordsLauncher: 'Eticheta butonului',
  designWordsPlaceholder: 'Text de înlocuire',
  designWordsAi: 'Eticheta AI',
  designWordsPerson: 'Link către o persoană',
  noteNew: 'Nou',
  noteChanged: 'Schimbat',
  noteRemoved: 'Eliminat',
  noteSourceKnowledge: 'Pagină de Cunoștințe: {title}',
  noteSourceFile: 'Fișier: {name}',
  noteSourceChat: 'Din chat',
  knowledgeApprovalNote:
    'Cumpărătorii pot citi tot ce se află în aceste notițe. Aprobarea salvează o schiță nouă: pune întrebări în fila Widget, apoi public-o din fila Instalare.',
  profileTitle: 'Despre magazin',
  profileChecked: 'Verificat {date}',
  profileSources: 'Scris pe baza',
  profileSample: '{count} produse eșantionate pentru nivelul prețurilor',
  profileSampleOne: '{count} produs eșantionat pentru nivelul prețurilor',
  profileAssistant: 'Ce poate face asistentul aici',
  profileTeam: 'Scris de echipa voastră',
  profileEmpty:
    'Încă nescris. Cereți-i lui Chat să facă o trecere de cunoștințe: scrie asta din paginile voastre și din ceea ce a văzut pe site-ul vostru, iar voi puteți adăuga rânduri proprii.',
  profileWithChat: 'Adaugă sau schimbă prin Chat',
  profilePrompt:
    'Ajută-mă să îmbunătățesc ce știe asistentul de cumpărături pentru {name} ({domain}) despre magazinul nostru în ansamblu: ce vindem, cu ce suntem cunoscuți, pentru cine suntem și ce pot face clienții aici. Citește mai întâi profilul „Despre magazin“ și spune-mi ce lipsește sau este greșit. Tot ce îți spun despre magazin și nu apare pe nicio pagină intră ca rând din partea echipei noastre.',
  profilePartNavigation: 'Departamente',
  profilePartWelcome: 'Salut',
  profilePartMarkets: 'Piețe',
  profilePartFunctions: 'Ce poate face asistentul',
  profilePartProducts: 'Produse',
  profilePartPages: 'Pagini de servicii',
  profileCleared: 'Șterge profilul scris',
  knowledgeTitle: 'Ce știe',
  knowledgeBody:
    'Pe lângă produsele, prețurile și stocul tău, pe care le citește mereu în timp real din magazinul tău.',
  knowledgeEmpty:
    'Deocamdată doar catalogul tău. Adaugă informații despre livrare, retururi sau plată cu Chat, din paginile tale sau din Cunoștințe.',
  knowledgeWithChat: 'Adaugă prin Chat',
  knowledgeChangeWithChat: 'Schimbă prin Chat',
  knowledgePrompt:
    'Ajută-mă să aleg ce le spune asistentul pentru cumpărături al {name} ({domain}) cumpărătorilor, pe lângă produsele noastre, cum ar fi informații despre livrare, retururi și plată. Găsește informațiile pe paginile noastre sau în Cunoștințe și propune notițe pe care să le pot aproba.',
  pagesTitle: 'Pagini pe care le citește',
  pagesBody:
    'Paginile din care provin notele. Poneva le verifică în fiecare zi, iar Chat anunță când se schimbă una dintre ele.',
  pagesEmpty:
    'Nu există încă pagini. Aici apar paginile găsite la citirea magazinului și cele indicate în Chat.',
  pageFound: 'găsită de Poneva',
  pageLinkedFrom: 'legată de la {page}',
  pageAddedByYou: 'adăugată manual',
  pageFromNote: 'menționată într-o notă',
  pageNoteOne: '1 notă',
  pageNotesMany: '{count} note',
  pageStatusOk: 'La zi',
  pageStatusChanged: 'Modificată',
  pageStatusUnreadable: 'Nu poate fi citită',
  pageStatusUnchecked: 'Nu a fost verificată încă',
  pagesChangeWithChat: 'Schimbă prin Chat',
  pagesUpdateWithChat: 'Actualizează notele prin Chat',
  pagesPrompt:
    'Ajută-mă să schimb ce pagini citește asistentul de cumpărături pentru {name} ({domain}) pentru notele sale. Arată-mi paginile pe care le folosește acum și întreabă-mă pe care să le adaug sau să le elimin.',
  pagesUpdatePrompt:
    'Unele pagini pentru {name} ({domain}) s-au schimbat sau nu mai pot fi citite de la crearea notelor. Arată-mi care sunt și refă notele din ele ca ciornă pe care o pot verifica.',
  viewCompare: 'Comparare',
  lookDeviceLabel: 'Compară pe',
  lookShop: 'Magazinul tău',
  lookShopCaption: 'Pagina principală, citită {when}',
  lookWidget: 'Asistentul',
  lookWidgetCaption: 'Versiunea {version}',
  lookLoading: 'Se plasează asistentul lângă magazinul tău…',
  lookFailed: 'Comparația nu a putut fi realizată acum.',
  lookNoShopPicture: 'Citirea nu a păstrat o imagine a magazinului tău la această lățime.',
  lookNoneTitle: 'Aspectul propriu al magazinului tău nu a fost păstrat',
  lookNoneBody:
    'Această citire nu l-a înregistrat. Citește din nou magazinul din Configurare, iar logo-ul, culorile și paginile lui vor fi păstrate pentru comparare.',
  lookColoursShop: 'Culorile folosite de magazinul tău',
  lookColoursAssistant: 'Culorile asistentului',
  lookRoleHeader: 'Antet',
  lookRoleBrand: 'Brand',
  lookSame: 'La fel ca culoarea {colour} a magazinului tău ({source})',
  lookOff:
    'Aproape de culoarea {colour} a magazinului tău ({source}), dar nu identică, diferență {delta}',
  lookOwn: 'Culoare proprie: magazinul tău nu o folosește',
  lookNeutral: 'Magazinul tău nu a fost încărcat pentru asta: se arată doar ce a păstrat citirea.',
  lookFromLogo: 'logo',
  lookFromTheme: 'culoarea temei',
  lookFromTile: 'culoarea casetei',
  lookFromMask: 'pictogramă fixată',
  lookFromManifest: 'manifestul aplicației',
  lookFromHeader: 'antet',
  lookFromButton: 'butoane',
  lookFromVariable: 'culoarea stilului',
  lookFix: 'Repară cu Chat',
  lookFixPrompt:
    'Culoarea asistentului pentru {role} este {colour}, aproape de culoarea {exact} a magazinului tău ({source}), dar nu identică. Folosește culoarea exactă a magazinului tău, {exact}.',
  lookSwatch: 'Culoarea {colour}',
  errorDesignChecks: 'Această versiune nu a trecut verificările de design.',
  designSamplesBlocked:
    'Aspectul cardurilor nu poate fi dovedit aici: site-ul tău blochează vizitele automate. Culorile, fonturile și logoul funcționează în continuare.',
  designCheckLabel: 'O etichetă IA este prea ștearsă pentru a fi citită.',
  designCheckReach: 'Un buton nu poate fi atins.',
  designCheckSize: 'O parte din text este prea mică.',
  designCheckOverflow: 'Panoul derulează lateral.',
  designCheckRender: 'Verificările nu au putut rula. Încercați din nou într-o clipă.',
  fixDesignPrompt:
    'Am încercat să public cea mai recentă ciornă pentru {name} ({domain}), dar nu a trecut verificările de design: {facts}. Corectați designul în acest chat și verificați-l pe telefon și pe computer. Nu îl publicați.',
};

const sk: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Nákupný asistent',
  designUnconfirmed:
    'Zastavené skôr, než Chat mohol potvrdiť uloženie dizajnu. Plátno asistenta zobrazuje najnovšiu verziu.',
  errorNewerVersion:
    'Od otvorenia tejto stránky bola uložená novšia verzia. Najprv si ju pozrite a potom ju publikujte.',
  canvasSelectHint: 'Kliknite na časť asistenta a zmeňte ju v chate · Esc na zastavenie',
  canvasDraft: 'Verzia {version} · zatiaľ nepublikovaná',
  canvasDraftOverLive: 'Verzia {version} · verzia {live} je publikovaná',
  canvasLive: 'Verzia {version} · publikovaná',
  canvasNoVersion: 'Prvá verzia vznikne po dokončení čítania obchodu',
  canvasPublish: 'Publikovať na stránke Storefront',
  canvasFrameTitle: 'Nákupný asistent {name}',
  viewLabel: 'Zobraziť',
  photosGood: '{count} produktových fotografií: celé a ostré',
  photosGoodOne: 'Produktová fotografia je celá a ostrá',
  canvasMissingTitle: 'Tento nákupný asistent nie je v tomto pracovnom priestore',
  canvasMissingBody: 'Patrí do iného pracovného priestoru alebo bol odstránený.',
  canvasMissingLink: 'Zobraziť asistentov tohto pracovného priestoru',
  photosLabel: 'Produktové fotografie',
  photosSmall: '{count} malých vo svojich dlaždiciach',
  photosCut: '{count} orezaných',
  photosSoft: '{count} zväčšených, preto neostrých',
  photosMissing: '{count} bez fotografie',
  photosFix: 'Opraviť pomocou Chat',
  photosPrompt:
    'Uprav produktové fotografie na kartách tak, aby vyzerali správne: každá celá, dostatočne veľká vo svojej dlaždici a ostrá.',
  photosFacts:
    '{total} fotografií: {small} malých vo svojich dlaždiciach, {cut} orezaných, {soft} zväčšených, {missing} bez fotografie. Veľkosti: {sizes}',
  viewDesign: 'Dizajn',
  viewVersions: 'Verzie',
  versionsLoading: 'Načítavajú sa verzie…',
  versionsFailed: 'Verzie sa nepodarilo načítať.',
  versionTitle: 'Verzia {version}',
  versionLive: 'Publikovaná',
  versionChecked: 'Skontrolovaná',
  versionToday: 'Dnes',
  versionYesterday: 'Včera',
  versionEarlier: 'Skôr',
  versionByPerson: 'Vyžiadal(a) {name}',
  versionByChat: 'Vyžiadaná v chate',
  versionFromReading: 'Z čítania {domain}',
  versionPublishedBy: 'publikoval(a) {name}, {at}',
  versionPublish: 'Publikovať',
  versionRestore: 'Obnoviť…',
  versionRestoreTitle: 'Obnoviť verziu {version}?',
  versionRestoreBody:
    'Stane sa z nej verzia {next}, nový koncept. Vaša stránka sa nezmení, kým ju nepublikujete.',
  versionRestoreConfirm: 'Obnoviť',
  versionRestoreCancel: 'Zrušiť',
  versionRestored: 'Obnovená ako verzia {version}',
  versionRestoreFailed: 'Verziu sa nepodarilo obnoviť.',
  compareTitle: 'Porovnať verziu {to} s verziou {from}',
  compareTitleLive: 'Porovnať verziu {to} s publikovanou verziou {from}',
  compareHint: 'Potiahnutím porovnajte',
  compareLabel: 'Porovnať verziu {from} a verziu {to}',
  compareTagLive: 'v{version} · Publikovaná',
  compareTag: 'v{version}',
  changesTitle: 'Čo sa zmenilo z verzie {from} na {to}',
  changesNone: 'Dizajny sú rovnaké. Zmenili sa iba poznámky alebo údaje obchodu.',
  changeAdded: 'Pridané',
  changeRemoved: 'Odstránené',
  changeChanged: 'Zmenené',
  changeDrawn: 'nakreslené logo',
  changeCss: 'vlastné CSS',
  changeYes: 'áno',
  changeNo: 'nie',
  'field.colors.brand': 'Farba značky',
  'field.colors.actions': 'Farba tlačidla',
  'field.colors.panel': 'Farba panela',
  'field.colors.surface': 'Farba plochy',
  'field.colors.ink': 'Farba textu',
  'field.colors.muted': 'Farba tlmeného textu',
  'field.colors.line': 'Farba čiary',
  'field.colors.bubble': 'Farba správy nakupujúceho',
  'field.colors.bubbleInk': 'Text správy nakupujúceho',
  'field.colors.header': 'Farba hlavičky',
  'field.colors.headerInk': 'Farba textu hlavičky',
  'field.colors.tints': 'Jemné odtiene',
  'field.copy.assistantName': 'Názov',
  'field.copy.greeting': 'Pozdrav',
  'field.copy.placeholder': 'Pole otázky',
  'field.copy.starters': 'Úvodné otázky',
  'field.copy.launcherLabel': 'Text spúšťača',
  'field.copy.aiLabel': 'Označenie AI',
  'field.copy.personLabel': 'Odkaz na človeka',
  'field.type.body': 'Písmo textu',
  'field.type.display': 'Písmo nadpisov',
  'field.type.fontCss': 'Zdroj písma',
  'field.type.scale': 'Veľkosť textu',
  'field.shape.radius': 'Rohy',
  'field.shape.shadow': 'Tieň',
  'field.shape.density': 'Rozostupy',
  'field.launcher.style': 'Spúšťač na počítačoch',
  'field.launcher.phoneStyle': 'Spúšťač na telefónoch',
  'field.launcher.side': 'Strana spúšťača',
  'field.launcher.icon': 'Ikona spúšťača',
  'field.panel.header': 'Veľkosť hlavičky',
  'field.panel.opening': 'Prvá obrazovka',
  'field.products.layout': 'Produkty',
  'field.products.imageFit': 'Produktové fotografie',
  'field.products.imageRatio': 'Tvar dlaždice fotografie',
  'field.products.imagePadding': 'Priestor okolo fotografií',
  'field.products.imageBackground': 'Farba dlaždice fotografie',
  'field.products.picker': 'Výber veľkosti',
  'field.products.showStock': 'Zobrazené skladové zásoby',
  'field.cart.landing': 'Animácia košíka',
  'field.logo.svg': 'Logo',
  'field.logo.shape': 'Tvar loga',
  'field.logo.plate': 'Podklad loga',
  'field.css': 'Vlastné CSS',
  'value.ink': 'atramentová',
  'value.brand': 'farba značky',
  'value.small': 'malá',
  'value.medium': 'stredná',
  'value.large': 'veľká',
  'value.square': 'štvorcová',
  'value.soft': 'mäkká',
  'value.round': 'okrúhla',
  'value.flat': 'plochá',
  'value.deep': 'hlboká',
  'value.compact': 'kompaktná',
  'value.comfortable': 'pohodlná',
  'value.pill': 'pilulka so slovami',
  'value.circle': 'kruh',
  'value.right': 'vpravo',
  'value.left': 'vľavo',
  'value.logo': 'logo',
  'value.chat': 'bublina chatu',
  'value.sparkle': 'iskra',
  'value.bag': 'taška',
  'value.full': 'plné',
  'value.middle': 'v strede',
  'value.bottom': 'dole',
  'value.rows': 'riadky',
  'value.grid': 'mriežka',
  'value.carousel': 'riadok posúvaný prstom',
  'value.contain': 'celá fotografia',
  'value.cover': 'vyplnená dlaždica',
  'value.portrait': 'na výšku',
  'value.landscape': 'na šírku',
  'value.none': 'žiadne',
  'value.pop': 'stúpajúce +1',
  'value.ring': 'vlnka',
  'value.blink': 'bliknutie',
  'value.chips': 'tlačidlá v riadku',
  'value.menu': 'zoznam',
  'value.rounded': 'zaoblené',
  designCardTitle: 'Nákupný asistent {name}',
  designCardSaved: 'Verzia {version} uložená · zatiaľ nepublikovaná',
  designCardSavedFirst: 'Dizajn uložený · prvá verzia ho prevezme',
  designSaving: 'Ukladá sa dizajn…',
  selectedAssistantPart: 'Vybrané: časť asistenta',
  loading: 'Načítava sa Storefront…',
  disabledTitle: 'Storefront nie je v tomto pracovnom priestore zapnutý.',
  disabledBody:
    'Je v predbežnom prístupe a zavádza sa postupne. Opýtajte sa svojej kontaktnej osoby v Poneva, kedy bude dostupný aj pre vás.',
  loadFailed: 'Storefront sa nepodarilo načítať.',
  startTitle: 'Nákupný asistent pre váš obchod',
  startBody:
    'Poneva číta váš obchod tak, ako ho používa nakupujúci: vyhľadávanie, produkty, košík a existujúci chat. Trvá to približne 10 až 30 minút, využíva kredity vašej spoločnosti a vy môžete ďalej pracovať.',
  urlLabel: 'Adresa vášho obchodu',
  urlPlaceholder: 'https://www.yourstore.com',
  startButton: 'Prečítať môj obchod',
  starting: 'Spúšťa sa…',
  startFailed: 'Obchod sa nepodarilo prečítať. Skontrolujte adresu a skúste to znova.',
  tabConversations: 'Konverzácie',
  tabOverview: 'Prehľad',
  allAssistants: 'Všetci asistenti',
  allAssistantsNote: '{assistants} asistentov · {sites} stránok',
  allAssistantsMenuNote: '{count} konverzácií tento týždeň',
  marketLabel: 'Trh',
  allMarkets: 'Všetky trhy',
  allMarketsNote: '{count} stránok',
  scopeIn: '{assistant} v {market}',
  askAbout: 'Opýtať sa Chat na {scope}',
  overviewAssistantsTitle: 'Asistenti a trhy',
  overviewMarketsTitle: 'Trhy',
  overviewAssistantsOnly: 'Asistenti',
  overviewIdle: '{count} asistentov zatiaľ bez konverzácií',
  allAssistantsCount: '{assistants} asistentov',
  overviewFailed: 'Prehľad sa nepodarilo načítať.',
  colConversations: 'Konverzácie',
  colCart: 'Pridané do košíka',
  colCheckout: 'Pokladňa',
  colPerson: 'Človek',
  colCost: 'Kredity',
  rowMarkets: '{count} trhov',
  rowVersion: 'verzia {version}',
  rowAria:
    '{name}, {sub}: {conversations} konverzácií, {cart} pridaných do košíka, {checkout} dosiahlo pokladňu, {person} požiadalo o človeka, kredity {cost}',
  versionPublishedAt: 'publikovaná {at}',
  designColorHeader: 'Hlavička',
  designColorHeaderInk: 'Text hlavičky',
  designGroupTranslations: 'Slová v iných jazykoch',
  topicsTitle: 'Na čo sa nakupujúci pýtajú',
  topicsNote: 'Témy, ktoré Chat našiel v prvých otázkach',
  startPagesTitle: 'Kde sa konverzácie začínajú',
  startPagesOne: 'Každá konverzácia sa začala na jednom type stránky: {page}.',
  'startPage.product': 'Stránky produktov',
  'startPage.search': 'Výsledky vyhľadávania',
  'startPage.start': 'Úvodná stránka',
  'startPage.cart': 'Košík',
  'startPage.other': 'Iné stránky',
  addMarketInChat: 'Pridať trh v chate',
  marketsTitle: 'Trhy',
  marketsBody:
    'Jeden asistent a jeden dizajn. Každý trh má vlastný jazyk, menu, produkty, košík a chat s človekom.',
  marketChecked: 'skontrolované {when}',
  'marketStatus.ready': 'Pripravené',
  'marketStatus.found': 'Nepridané',
  'marketStatus.checking': 'Kontroluje sa',
  'marketStatus.failed': 'Vyžaduje opravu',
  addMarketPrompt:
    'Pridaj k nákupnému asistentovi pre {name} ({domain}) ďalší trh: nájdi ostatné krajiny, jazyky alebo stránky obchodu a opýtaj sa ma, ktorý pridať.',
  tabWidget: 'Widget',
  tabInstall: 'Inštalácia',
  tabSetup: 'Nastavenie',
  statusReading: 'Číta sa váš obchod',
  readingProducts: 'Produkty sa čítajú znova',
  readingCart: 'Košík sa kontroluje znova',
  readingChat: 'Kontroluje sa cesta k vášmu chatu',
  readingMarket: 'Pridáva sa trh {market}',
  readingMarketAny: 'Pridáva sa trh',
  statusReady: 'Pripravené na inštaláciu',
  statusFailed: 'Čítanie zastavené',
  statusLive: 'Živé · verzia {version} na {domain}',
  statusPublished: 'Publikované · verzia {version} · zatiaľ nie na vašej stránke',
  statusPublishedUnverified: 'Publikované · verzia {version} · váš web sa nepodarilo overiť',
  stepOpen: 'Otváranie stránky',
  stepSearch: 'Vyhľadávanie',
  stepProducts: 'Produkty',
  stepCart: 'Košík',
  stepChat: 'Váš existujúci chat',
  stepLook: 'Váš vzhľad a logo',
  stepChecks: 'Kontrola funkčnosti',
  stepReady: 'Pripravené',
  stepDetailOpen: 'Otváranie {domain}',
  stepDetailSearch: 'Hľadajú sa produkty obchodu',
  stepDetailProducts: 'Čítajú sa produkty, ceny, fotografie, farby a veľkosti',
  stepDetailCart: 'Skúša sa košík: pridať, načítať, odstrániť a vyprázdniť',
  stepDetailChat: 'Hľadá sa váš existujúci chat',
  stepDetailLook: 'Číta sa logo a farby obchodu',
  stepDetailChecks: 'Skúšajú sa vyhľadávania nakupujúcich, produkty, prehliadanie a košík',
  stepDetailChecksProducts:
    'Kontrolujú sa vyhľadávania produktov, prehliadanie, fotografie, farby a veľkosti',
  stepDetailChecksCart:
    'Kontroluje sa pridanie do košíka, načítanie, odstránenie a vyprázdnenie, ako aj čítanie produktov',
  stepDetailChecksChat: 'Kontroluje sa odovzdanie do vášho existujúceho chatu a čítanie produktov',
  stepDetailChecksMarket: 'Kontrolujú sa produkty, ceny a košík tohto trhu',
  nowRunning: 'Teraz: {step}',
  nowProgress: 'Krok {n} z {total} · zatiaľ {minutes} min',
  nowReady: 'Váš obchod je pripravený na asistenta',
  nowReadyBody: 'Vyhľadávanie, produkty a košík fungujú.',
  nowLastRead: 'Naposledy prečítané {when}',
  nowUsually: 'zvyčajne {min}–{max}',
  stepsRead: 'Prečítané {when} · Zobraziť kroky',
  nowFailed: 'Čítanie zastavené',
  factAnswers: 'Odpovedá nakupujúcim pomocou',
  costCounting: 'Počítanie…',
  factPaid: 'Hradené z',
  paidCompany: 'Kreditov spoločnosti',
  creditsAmount: '{amount} kreditov',
  creditsOne: '{amount} kredit',
  creditsUnder: 'menej než {amount} kreditov',
  stopRun: 'Zastaviť čítanie',
  stopping: 'Zastavuje sa…',
  closeNote: 'Túto stránku môžete opustiť. Čítanie pokračuje.',
  readyNote: 'Ďalej: vyskúšajte Widget a potom ho nainštalujte.',
  buildWidget: 'Vyskúšať Widget',
  readAgain: 'Prečítať obchod znova',
  foundTitle: 'Čo sa našlo',
  foundProducts: 'Prečítané produkty a ceny',
  foundSearch: 'Vyhľadávanie funguje',
  foundCart: 'Pridanie do košíka funguje',
  foundChat: 'Váš chat {vendor}',
  foundNoChat: 'Žiadny vlastný chat',
  foundLook: 'Vaše farby a logo',
  widgetTitle: 'Váš Widget',
  widgetBody:
    'Váš asistent vo vašich farbách. Vyskúšajte ho tu: odpovedá pomocou skutočných produktov. Do skutočného košíka sa nič nepridá.',
  widgetLoading: 'Načítava sa ukážka…',
  widgetFailed: 'Ukážku sa nepodarilo načítať.',
  widgetNotReady: 'Widget bude pripravený po dokončení krokov na karte Nastavenie.',
  installLineTitle: 'Pridajte tento riadok na svoju stránku',
  installLineBody:
    'Funguje na každej platforme: do hlavičky stránok, na Shopify v theme.liquid tesne pred </head>. Kým ho nepublikujete, nič nezobrazuje.',
  installLineBodyLive:
    'Funguje na každej platforme: do hlavičky stránok, na Shopify v theme.liquid tesne pred </head>. Nakupujúci potom uvidia verziu {version}.',
  installPolicyAsk: 'Nastavuje vaša stránka Content Security Policy?',
  installPolicy:
    'Ak stránka nastavuje Content Security Policy, povoľte tiež {origin} v {directives}, inak sa asistent nespustí.',
  installPolicyRefuses:
    'Content Security Policy vašej stránky asistenta odmieta: povoľte {origin} v {directives}, inak sa nič nespustí.',
  installPolicyNonce:
    'Vaša politika spúšťa iba dôveryhodné skripty, preto pridajte riadok s nonce vašich stránok.',
  installPolicyEval: "Asistent potrebuje v script-src aj 'unsafe-eval'.",
  copy: 'Kopírovať',
  copied: 'Skopírované',
  domainsTitle: 'Stránky, na ktorých odpovedá',
  domainsBody: 'Na iných stránkach zostane ticho, aj keby niekto skopíroval tento riadok.',
  lineFound: 'Riadok nájdený',
  lineNotSeen: 'Na stránke zatiaľ nie je',
  lineUnverified:
    'Nepodarilo sa overiť: web blokuje automatické návštevy. Asistent napriek tomu funguje pre zákazníkov.',
  lineChecked: 'skontrolované {when}',
  lineCheckAgain: 'Skontrolovať znova',
  lineChecking: 'Kontroluje sa stránka…',
  installCheckBusy: 'Kontrola je teraz zaneprázdnená. Skúste to znova o chvíľu.',
  publishTitle: 'Publikovať',
  publishBody: 'Nakupujúci na {domain} ho uvidia okamžite. Publikovanie môžete kedykoľvek zrušiť.',
  publishButton: 'Publikovať verziu {version}',
  publishConfirm: 'Publikovať verziu {version} pre {name}?',
  publishNow: 'Publikovať',
  notNow: 'Teraz nie',
  publishing: 'Publikuje sa…',
  liveTitle: 'Publikované: verzia {version}',
  liveSince: 'Od {since}',
  unpublish: 'Zrušiť publikovanie',
  unpublishConfirm:
    'Odobrať verziu {version} zo stránky? Nakupujúci asistenta neuvidia, kým ho znova nepublikujete.',
  publishedLine: 'Teraz publikované: verzia {version}, od {since}',
  usageTitle: 'Využitie',
  usageBody: 'Využíva kredity vašej spoločnosti, rovnako ako zvyšok Chat.',
  usageDiscovery: 'Čítanie obchodu',
  usageChats: 'Chaty nakupujúcich',
  usageChatsValue: '{count} chatov · {cost}',
  last7: 'Posledných 7 dní',
  askWeek: 'Opýtať sa Chat na tento týždeň',
  kpiConversations: 'Konverzácie',
  kpiVsPrevious: '{change} oproti predchádzajúcemu týždňu',
  kpiCart: 'Pridané do košíka',
  kpiCheckout: 'Dosiahnutá pokladňa',
  kpiPerson: 'Vyžiadaný človek',
  kpiCost: 'Použité kredity',
  ofConversations: '{percent} konverzácií',
  checkoutNote: '{percent} konverzácií',
  personNote: '{percent} otvorilo váš chat',
  costNote: 'Približne {amount} na konverzáciu',
  costNoteUnder: 'Menej než {amount} na konverzáciu',
  sparkLabel: 'Konverzácie za deň, posledných {days} dní',
  sparkDay: '{day}: {count} konverzácií',
  filterAll: 'Všetky',
  filterCart: 'Pridané do košíka',
  filterCheckout: 'Pokladňa',
  filterPerson: 'Človek',
  outcomeCart: 'Pridané do košíka',
  outcomeCheckout: 'Dosiahnutá pokladňa',
  outcomePerson: 'Vyžiadaný človek',
  outcomeAnswered: 'Zodpovedané',
  devicePhone: 'Telefón',
  deviceComputer: 'Počítač',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Začaté na {page}',
  anonymous: 'Nakupujúci je anonymný.',
  askConversation: 'Opýtať sa Chat na túto konverzáciu',
  showOlderVersions: 'Zobraziť staršie verzie',
  showOlder: 'Zobraziť staršie konverzácie',
  filterCountsWeek: 'Počty: posledných 7 dní',
  readAlreadyHere:
    'Tento obchod už je tu, takže sa nič nové nespustilo a nepoužili sa žiadne kredity.',
  readOpenShop: 'Otvoriť obchod',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Čo sa môžeme naučiť z tejto konverzácie?',
  askWeekMessage: 'Čo sa môžeme naučiť z konverzácií tohto týždňa?',
  conversationsChipOne: '{name} · konverzácia z {when}',
  conversationsChipWeek: '{name} · tento týždeň',
  conversationsChipWeekIn: '{name} · {market} · tento týždeň',
  removeConversations: 'Odstrániť konverzácie',
  noConversations: 'Zatiaľ žiadne konverzácie',
  noConversationsBody: 'Keď nakupujúci použijú asistenta, ich konverzácie sa zobrazia tu.',
  pickConversation: 'Vyberte konverzáciu na prečítanie.',
  eventCheckout: 'Prešiel na pokladňu',
  eventPerson: 'Otvoril váš chat',
  showMore: 'Zobraziť viac',
  urlInvalid: 'Použite https adresu svojho obchodu, napríklad https://www.yourstore.com.',
  errorNoCompany:
    'Storefront potrebuje pracovný priestor spoločnosti. Požiadajte správcu, aby vás pridal.',
  errorPublishPermission:
    'Toto môžu urobiť iba ľudia, ktorí môžu publikovať za vašu spoločnosť. Požiadajte správcu.',
  actionFailed: 'To sa nepodarilo. Skúste to znova.',
  copyFailed: 'Kopírovanie sa nepodarilo. Vyberte riadok a skopírujte ho sami.',
  reasonCancelled: 'Čítanie ste zastavili.',
  reasonFailed: 'Čítanie vášho obchodu sa nedokončilo, takže asistent nie je pripravený.',
  reasonFailedLive:
    'Čítanie vášho obchodu sa nedokončilo. Publikovaný asistent ďalej funguje bez zmeny.',
  reasonFailedKept:
    'Čítanie vášho obchodu sa nedokončilo, takže sa nič nezmenilo: verzia {version} zostáva taká, aká bola.',
  statusReadStopped: 'Pripravené · posledné čítanie sa zastavilo',
  reasonStepMarked: 'Krok, pri ktorom sa čítanie zastavilo, je označený nižšie.',
  reasonReadAgain: 'Opätovné čítanie začne od toho, čo sa toto čítanie naučilo.',
  reasonNotAStore:
    'Táto adresa nevyzerá ako internetový obchod: neboli nájdené žiadne produkty na vyhľadávanie. Skontrolujte adresu alebo skúste hlavnú doménu obchodu.',
  reasonTooSlow:
    'Vyhľadávanie vo vašom obchode je pre nákupného asistenta príliš pomalé: trvalo viac než 12 sekúnd. Pomôcť môže rýchlejšie vyhľadávanie v obchode alebo neskoršie opätovné čítanie.',
  reasonOurSide:
    'Čítanie sa zastavilo pre problém na našej strane, nie vo vašom obchode, takže asistent ešte nie je pripravený.',
  reasonOurSideLive:
    'Čítanie sa zastavilo pre problém na našej strane, nie vo vašom obchode. Publikovaný asistent ďalej funguje bez zmeny.',
  reasonOurSideKept:
    'Čítanie sa zastavilo pre problém na našej strane, nie vo vašom obchode, takže sa nič nezmenilo: verzia {version} zostáva taká, aká bola.',
  reasonTryLater: 'Skúste obchod prečítať znova o chvíľu.',
  errorNoCredits:
    'Na toto momentálne nie sú k dispozícii žiadne kredity. Vlastník spoločnosti môže skontrolovať plán a limity v časti Usage.',
  errorReadUnavailable: 'Obchod sa teraz nedá prečítať. Skúste to znova o pár minút.',
  reasonTimeout:
    'Čítanie dosiahlo časový limit skôr, než sa dokončilo. Opätovné čítanie pokračuje od toho, čo už overilo.',
  retrying: 'Spúšťa sa znova…',
  readTitleChecks: 'Znova skontrolovať uložené čítanie',
  checksScopeLabel: 'Kontroluje sa',
  readScopeChecks:
    'Vyhľadávanie nakupujúcich, produkty, prehliadanie a košík – na tom, čo uložilo posledné čítanie. Obchod sa znova nečíta.',
  readScopeChecksProducts:
    'Vyhľadávania produktov, prehliadanie, fotografie, farby a veľkosti – na tom, čo uložilo posledné čítanie. Obchod sa znova nečíta.',
  readScopeChecksCart:
    'Pridanie do košíka, načítanie, odstránenie a vyprázdnenie, ako aj čítanie produktov – na tom, čo uložilo posledné čítanie. Obchod sa znova nečíta.',
  readScopeChecksChat:
    'Odovzdanie do vášho existujúceho chatu a čítanie produktov – na tom, čo uložilo posledné čítanie. Obchod sa znova nečíta.',
  readScopeChecksMarket:
    'Produkty, ceny a košík trhu – na tom, čo uložilo posledné čítanie. Obchod sa znova nečíta.',
  checksCost: 'Len nové kontroly využijú kredity spoločnosti',
  checksAfter:
    'Uloží nový koncept, ak kontroly prejdú. Kým ho nepublikujete, nakupujúci nič neuvidia.',
  checksAfterLive:
    'Uloží nový koncept, ak kontroly prejdú. Publikovaný asistent ďalej funguje a nakupujúci uvidia zmenu až po publikovaní.',
  checksStarted:
    'Kontroly sa spustili. Keď bude nový koncept pripravený, požiadajte Chat o jeho kontrolu.',
  checkingSavedWork: 'Znova sa kontroluje uložená práca',
  checkAgain: 'Skontrolovať znova',
  checkingAgain: 'Kontroluje sa znova…',
  reasonCheckAgain:
    'Skontrolovať znova otestuje uloženú prácu ešte raz bez opätovného čítania obchodu. Len nové kontroly využijú kredity spoločnosti.',
  fixWithChat: 'Opraviť pomocou Chat',
  fixReadPrompt:
    'Čítanie obchodu {name} ({domain}) sa zastavilo. Pozri sa, čo sa stalo, a pomôž mi to sprevádzkovať: povedz mi, čo nájdeš, navrhni, čo môžem čítaniu povedať, a potom ho skontroluj znova alebo ho s touto radou spusti znova a prečítaj pritom čo najmenšiu časť obchodu.',
  fixReadPromptStep:
    'Čítanie obchodu {name} ({domain}) sa zastavilo pri kroku „{step}“. Pozri sa, čo sa stalo, a pomôž mi to sprevádzkovať: povedz mi, čo nájdeš, navrhni, čo môžem čítaniu povedať, a potom ho skontroluj znova alebo ho s touto radou spusti znova a prečítaj pritom čo najmenšiu časť obchodu.',
  stepStateDone: 'hotovo',
  stepStateRunning: 'prebieha',
  stepStateWaiting: 'nespustené',
  stepStateFailed: 'zastavené',
  newVersionTitle: 'Verzia {version} je pripravená',
  newVersionBody: 'Publikujte ju a nahraďte verziu {live}.',
  newVersionBodyLive: 'Publikujte ju a nahraďte verziu {live} na {domain}.',
  previewSize: 'Veľkosť ukážky',
  designWithChat: 'Otvoriť dizajn',
  designReviewBody: 'Prezrite si aktuálny dizajn a povedzte Chatu, čo chcete upraviť.',
  designWhileTitle: 'Navrhnite ho počas čítania obchodu',
  designWhileBody:
    'Chat s vami už teraz môže vytvoriť vzhľad, texty a logo asistenta. Dizajn bude pripravený, keď sa čítanie dokončí.',
  designPreviewNote: 'Ukážka s príkladovými produktmi, kým sa váš obchod neprečíta.',
  proposedDesign: 'Navrhovaný dizajn',
  proposedDesignNote:
    'Widget s touto zmenou zobrazený s príkladovými produktmi. Schválením sa uloží ako nový koncept; publikovanie zostáva na stránke Storefront.',
  lineSources: 'Zo stránok obchodu:',
  readAgainConfirm:
    'Prečítať {name} znova? Trvá to približne 10 až 30 minút a využíva kredity spoločnosti.',
  readAgainLive: 'Publikovaný asistent ďalej funguje, kým nepublikujete novú verziu.',
  readAgainNow: 'Prečítať znova',
  switchStore: 'Prepnúť obchod alebo pridať obchod',
  storesLabel: 'Vaše obchody',
  addStore: 'Pridať ďalší obchod',
  backToStores: 'Späť na vaše obchody',
  storeLive: 'Publikovaný',
  storeReady: 'Pripravený',
  storeReading: 'Číta sa',
  storeStopped: 'Zastavený',
  actionDesignTitle: 'Zmeniť dizajn nákupného asistenta',
  actionKnowledgeTitle: 'Zmeniť, čo nákupný asistent vie',
  readTitleProducts: 'Znova prečítať produkty',
  readTitleCart: 'Znova skontrolovať košík',
  readTitleTheirChat: 'Znova skontrolovať cestu k vášmu chatu',
  readTitleAll: 'Znova prečítať celý obchod',
  readTitleStore: 'Prečítať nový obchod',
  readTitleMarket: 'Pridať trh',
  readShopLabel: 'Obchod',
  readScopeLabel: 'Číta sa',
  readTimeLabel: 'Trvanie',
  readCostLabel: 'Cena',
  readAfterLabel: 'Potom',
  readScopeProducts: 'Vyhľadávanie a radenie produktov, produktové fotografie, farby a veľkosti',
  readScopeCart: 'Pridanie do košíka a odstránenie z neho',
  readScopeTheirChat: 'Ako sa nakupujúci dostanú k vášmu chatu',
  readScopeAll: 'Produkty, košík, váš chat a vaše trhy',
  readScopeStore: 'Produkty, ceny a veľkosti, košík, váš chat, váš vzhľad a vaše trhy',
  readScopeMarket: '{market}: jeho produkty, ceny a košík',
  readTime: 'Zvyčajne {min}–{max} minút',
  readCost: 'Využíva kredity spoločnosti',
  readCostLast: 'Využíva kredity spoločnosti · posledné čítanie tohto obchodu využilo {credits}',
  readCostLastPartial:
    'Využíva kredity spoločnosti · posledné čítanie tohto obchodu sa zastavilo skôr a využilo aspoň {credits}',
  readAfter: 'Uloží nový koncept. Kým ho nepublikujete, nakupujúci nič neuvidia.',
  readAfterLive:
    'Uloží nový koncept. Publikovaný asistent ďalej funguje a nakupujúci uvidia zmenu až po publikovaní.',
  readAfterNew: 'Uloží prvý koncept na vyskúšanie. Nič sa nepublikuje.',
  readCartNote:
    'Na kontrolu košíka pridá testovaciu položku a znova ju odstráni. Nikdy neprejde k platbe.',
  readFocusLabel: 'Čo Chat požiadal opraviť pri čítaní',
  readFooter: 'Nič sa nespustí, kým to neschválite. Čítanie môžete zastaviť na karte Nastavenie.',
  readStartedAt:
    'Spustené {time} · zvyčajne {min}–{max} minút. Keď bude nový koncept pripravený, požiadajte Chat o jeho kontrolu.',
  readFocusAll: 'Zobraziť všetko',
  readFocusLess: 'Zobraziť menej',
  readStart: 'Spustiť čítanie',
  addToSite: 'Pridať na svoju stránku',
  readTab: 'Požiadavka',
  readStarted:
    'Čítanie sa spustilo. Keď bude nový koncept pripravený, požiadajte Chat o jeho kontrolu.',
  readFollow: 'Sledovať na karte Nastavenie',
  proposedDesignLive:
    'Skutočná ukážka vášho obchodu v tomto dizajne. Niečo sa opýtajte: do skutočného košíka sa nič nepridá. Schválením sa uloží ako nový koncept; publikovanie zostáva na stránke Storefront.',
  designChanges: 'Čo sa zmení',
  designReset: 'späť na predvolené',
  designGroupColors: 'Farby',
  designGroupType: 'Písma',
  designGroupShape: 'Tvar',
  designGroupLauncher: 'Tlačidlo na stránke',
  designGroupPanel: 'Hlavička',
  designGroupProducts: 'Produkty',
  designGroupCart: 'Košík obchodu',
  designGroupCopy: 'Slová',
  designGroupLogo: 'Logo',
  designGroupCss: 'Vlastné CSS',
  designColorBrand: 'Značka',
  designColorActions: 'Pridať a odoslať',
  designColorPanel: 'Panel',
  designColorSurface: 'Plochy',
  designColorInk: 'Text',
  designColorMuted: 'Sekundárny text',
  designColorLine: 'Čiary',
  designColorBubble: 'Správy nakupujúceho',
  designColorBubbleInk: 'Text nakupujúceho',
  designActionsBrand: 'Farba značky',
  designActionsInk: 'Farba textu',
  designWordsName: 'Názov',
  designWordsGreeting: 'Pozdrav',
  designWordsStarters: 'Úvodné otázky',
  designWordsLauncher: 'Označenie tlačidla',
  designWordsPlaceholder: 'Zástupný text',
  designWordsAi: 'Označenie AI',
  designWordsPerson: 'Odkaz na človeka',
  noteNew: 'Nové',
  noteChanged: 'Zmenené',
  noteRemoved: 'Odstránené',
  noteSourceKnowledge: 'Stránka Znalostí: {title}',
  noteSourceFile: 'Súbor: {name}',
  noteSourceChat: 'Z chatu',
  knowledgeApprovalNote:
    'Nakupujúci si môžu prečítať všetko v týchto poznámkach. Schválením sa uloží nový koncept: položte mu otázky na karte Widget a potom ho publikujte na karte Inštalácia.',
  profileTitle: 'O obchode',
  profileChecked: 'Skontrolované {date}',
  profileSources: 'Napísané z',
  profileSample: '{count} produktov vzorkovaných pre cenovú úroveň',
  profileSampleOne: '{count} produkt vzorkovaný pre cenovú úroveň',
  profileAssistant: 'Čo tu asistent vie',
  profileTeam: 'Napísal váš tím',
  profileEmpty:
    'Ešte nenapísané. Požiadajte Chat, aby spustil prechod znalostí: napíše to z vašich vlastných stránok a z toho, čo videl na vašom webe, a môžete pridať vlastné riadky.',
  profileWithChat: 'Pridať alebo zmeniť cez Chat',
  profilePrompt:
    'Pomôž mi zlepšiť, čo nákupný asistent pre {name} ({domain}) vie o našom obchode ako celku: čo predávame, čím sme známi, pre koho sme a čo tu môžu zákazníci robiť. Najprv si prečítaj profil „O obchode“ a povedz mi, čo chýba alebo je nesprávne. Všetko, čo ti o obchode poviem a žiadna stránka to neuvádza, sa vloží ako riadok od nášho tímu.',
  profilePartNavigation: 'Oddelenia',
  profilePartWelcome: 'Pozdrav',
  profilePartMarkets: 'Trhy',
  profilePartFunctions: 'Čo asistent vie',
  profilePartProducts: 'Produkty',
  profilePartPages: 'Servisné stránky',
  profileCleared: 'Vymaže napísaný profil',
  knowledgeTitle: 'Čo vie',
  knowledgeBody:
    'Okrem produktov, cien a skladových zásob, ktoré vždy číta priamo z vášho obchodu.',
  knowledgeEmpty:
    'Zatiaľ iba váš katalóg. Pomocou Chat pridajte informácie o doručení, vrátení alebo platbe z vlastných stránok alebo zo Znalostí.',
  knowledgeWithChat: 'Pridať pomocou Chat',
  knowledgeChangeWithChat: 'Zmeniť pomocou Chat',
  knowledgePrompt:
    'Pomôž mi vybrať, čo má nákupný asistent pre {name} ({domain}) hovoriť nakupujúcim okrem našich produktov, napríklad o doručení, vrátení a platbe. Nájdi to na našich stránkach alebo v Znalostiach a navrhni poznámky, ktoré môžem schváliť.',
  pagesTitle: 'Stránky, ktoré číta',
  pagesBody:
    'Stránky, z ktorých pochádzajú poznámky. Poneva ich kontroluje každý deň a Chat upozorní, keď sa niektorá zmení.',
  pagesEmpty:
    'Zatiaľ žiadne stránky. Zobrazia sa tu stránky, ktoré nájde čítanie obchodu, a tie, ktoré uvediete pomocou Chat.',
  pageFound: 'Ponevou nájdené',
  pageLinkedFrom: 'prepojené z {page}',
  pageAddedByYou: 'pridané ručne',
  pageFromNote: 'uvedená v poznámke',
  pageNoteOne: '1 poznámka',
  pageNotesMany: '{count} poznámok',
  pageStatusOk: 'Aktuálne',
  pageStatusChanged: 'Zmenené',
  pageStatusUnreadable: 'Nedá sa prečítať',
  pageStatusUnchecked: 'Zatiaľ neskontrolované',
  pagesChangeWithChat: 'Zmeniť pomocou Chat',
  pagesUpdateWithChat: 'Aktualizovať poznámky pomocou Chat',
  pagesPrompt:
    'Pomôžte mi zmeniť, ktoré stránky číta nákupný asistent pre {name} ({domain}) pre svoje poznámky. Ukážte mi stránky, ktoré teraz používa, a opýtajte sa ma, ktoré pridať alebo odstrániť.',
  pagesUpdatePrompt:
    'Niektoré stránky {name} ({domain}) sa zmenili alebo sa od vytvorenia poznámok nedajú prečítať. Ukážte mi, ktoré to sú, a vytvorte z nich poznámky znova ako koncept, ktorý si môžem prezrieť.',
  viewCompare: 'Porovnanie',
  lookDeviceLabel: 'Porovnať na',
  lookShop: 'Váš obchod',
  lookShopCaption: 'Domovská stránka, prečítaná {when}',
  lookWidget: 'Asistent',
  lookWidgetCaption: 'Verzia {version}',
  lookLoading: 'Asistent sa umiestňuje vedľa vášho obchodu…',
  lookFailed: 'Porovnanie sa teraz nepodarilo vytvoriť.',
  lookNoShopPicture: 'Čítanie neuložilo obrázok vášho obchodu v tejto šírke.',
  lookNoneTitle: 'Vlastný vzhľad vášho obchodu sa neuložil',
  lookNoneBody:
    'Toto čítanie ho nezaznamenalo. Prečítajte obchod znova v časti Nastavenie a jeho logo, farby a stránky sa uložia na porovnanie.',
  lookColoursShop: 'Farby, ktoré váš obchod používa',
  lookColoursAssistant: 'Farby asistenta',
  lookRoleHeader: 'Hlavička',
  lookRoleBrand: 'Značka',
  lookSame: 'Rovnaká ako farba {colour} vášho obchodu ({source})',
  lookOff: 'Blízka farbe {colour} vášho obchodu ({source}), ale nie rovnaká, rozdiel {delta}',
  lookOwn: 'Vlastná farba: váš obchod ju nepoužíva',
  lookNeutral: 'Váš obchod sa na to nenačítal: zobrazuje sa len to, čo si čítanie uložilo.',
  lookFromLogo: 'logo',
  lookFromTheme: 'farba motívu',
  lookFromTile: 'farba dlaždice',
  lookFromMask: 'pripnutá ikona',
  lookFromManifest: 'manifest aplikácie',
  lookFromHeader: 'hlavička',
  lookFromButton: 'tlačidlá',
  lookFromVariable: 'farba štýlu',
  lookFix: 'Opraviť pomocou Chat',
  lookFixPrompt:
    'Farba asistenta ({role}) je {colour}, blízka farbe {exact} vášho obchodu ({source}), ale nie rovnaká. Použite presnú farbu vášho obchodu, {exact}.',
  lookSwatch: 'Farba {colour}',
  errorDesignChecks: 'Táto verzia nevyhovela kontrolám dizajnu.',
  designSamplesBlocked:
    'Rozloženie kariet tu nemožno overiť: váš web blokuje automatické návštevy. Farby, písma a logo fungujú ďalej.',
  designCheckLabel: 'Označenie AI je príliš nevýrazné na čítanie.',
  designCheckReach: 'Tlačidlo je nedostupné.',
  designCheckSize: 'Časť textu je príliš malá.',
  designCheckOverflow: 'Panel sa posúva do strán.',
  designCheckRender: 'Kontroly sa nepodarilo spustiť. Skúste to o chvíľu znova.',
  fixDesignPrompt:
    'Pokúsil som sa zverejniť najnovší koncept {name} ({domain}), ale nevyhovel kontrolám dizajnu: {facts}. Opravte dizajn v tomto chate a skontrolujte ho na telefóne aj počítači. Nezverejňujte ho.',
};

const sl: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Nakupovalni pomočnik',
  designUnconfirmed:
    'Chat se je ustavil, preden je lahko potrdil, da je bil videz shranjen. Platno pomočnika prikazuje najnovejšo različico.',
  errorNewerVersion:
    'Odkar ste to odprli, je bila shranjena novejša različica. Najprej si jo oglejte, nato jo objavite.',
  canvasSelectHint: 'Kliknite del pomočnika, ki ga želite spremeniti v Chat · Esc za ustavitev',
  canvasDraft: 'Različica {version} · še ni objavljena',
  canvasDraftOverLive: 'Različica {version} · objavljena je različica {live}',
  canvasLive: 'Različica {version} · objavljeno',
  canvasNoVersion: 'Prva različica bo ustvarjena, ko bo branje trgovine končano',
  canvasPublish: 'Objavite na strani Storefront',
  canvasFrameTitle: 'Nakupovalni pomočnik {name}',
  viewLabel: 'Pogled',
  photosGood: '{count} fotografij izdelkov: cele in ostre',
  photosGoodOne: 'Fotografija izdelka je cela in ostra',
  canvasMissingTitle: 'Ta nakupovalni pomočnik ni v tem delovnem prostoru',
  canvasMissingBody: 'Pripada drugemu delovnemu prostoru ali pa je bil odstranjen.',
  canvasMissingLink: 'Oglejte si pomočnike tega delovnega prostora',
  photosLabel: 'Fotografije izdelkov',
  photosSmall: '{count} majhnih v svojih ploščicah',
  photosCut: '{count} odrezanih',
  photosSoft: '{count} povečanih in zato neostrih',
  photosMissing: '{count} brez fotografije',
  photosFix: 'Popravite s Chat',
  photosPrompt:
    'Uredite fotografije izdelkov na karticah: vsaka naj bo cela, dovolj velika za svojo ploščico in ostra.',
  photosFacts:
    '{total} fotografij: {small} majhnih v ploščicah, {cut} odrezanih, {soft} povečanih, {missing} brez fotografije. Velikosti: {sizes}',
  viewDesign: 'Videz',
  viewVersions: 'Različice',
  versionsLoading: 'Nalaganje različic …',
  versionsFailed: 'Različic ni bilo mogoče naložiti.',
  versionTitle: 'Različica {version}',
  versionLive: 'Objavljeno',
  versionChecked: 'Preverjeno',
  versionToday: 'Danes',
  versionYesterday: 'Včeraj',
  versionEarlier: 'Prej',
  versionByPerson: 'Zahteval/-a {name}',
  versionByChat: 'Zahtevano v Chat',
  versionFromReading: 'Iz branja {domain}',
  versionPublishedBy: 'objavil/-a {name}, {at}',
  versionPublish: 'Objavi',
  versionRestore: 'Obnovi …',
  versionRestoreTitle: 'Obnovim različico {version}?',
  versionRestoreBody:
    'Postala bo različica {next}, nov osnutek. Vaše spletno mesto se ne spremeni, dokler je ne objavite.',
  versionRestoreConfirm: 'Obnovi',
  versionRestoreCancel: 'Prekliči',
  versionRestored: 'Obnovljeno kot različica {version}',
  versionRestoreFailed: 'Različice ni bilo mogoče obnoviti.',
  compareTitle: 'Primerjaj različico {to} z različico {from}',
  compareTitleLive: 'Primerjaj različico {to} z objavljeno različico {from}',
  compareHint: 'Povlecite za primerjavo',
  compareLabel: 'Primerjava različic {from} in {to}',
  compareTagLive: 'v{version} · Objavljeno',
  compareTag: 'v{version}',
  changesTitle: 'Kaj se je spremenilo od različice {from} do {to}',
  changesNone: 'Videza sta enaka. Spremenile so se samo opombe ali podatki trgovine.',
  changeAdded: 'Dodano',
  changeRemoved: 'Odstranjeno',
  changeChanged: 'Spremenjeno',
  changeDrawn: 'narisan logotip',
  changeCss: 'lasten CSS',
  changeYes: 'da',
  changeNo: 'ne',
  'field.colors.brand': 'Barva znamke',
  'field.colors.actions': 'Barva gumbov',
  'field.colors.panel': 'Barva plošče',
  'field.colors.surface': 'Barva površine',
  'field.colors.ink': 'Barva besedila',
  'field.colors.muted': 'Barva umirjenega besedila',
  'field.colors.line': 'Barva črte',
  'field.colors.bubble': 'Barva sporočila kupca',
  'field.colors.bubbleInk': 'Besedilo sporočila kupca',
  'field.colors.header': 'Barva glave',
  'field.colors.headerInk': 'Barva besedila glave',
  'field.colors.tints': 'Nežni odtenki',
  'field.copy.assistantName': 'Ime',
  'field.copy.greeting': 'Pozdrav',
  'field.copy.placeholder': 'Polje za vprašanje',
  'field.copy.starters': 'Začetna vprašanja',
  'field.copy.launcherLabel': 'Besedilo zaganjalnika',
  'field.copy.aiLabel': 'Oznaka UI',
  'field.copy.personLabel': 'Povezava do osebe',
  'field.type.body': 'Pisava besedila',
  'field.type.display': 'Pisava naslovov',
  'field.type.fontCss': 'Vir pisave',
  'field.type.scale': 'Velikost besedila',
  'field.shape.radius': 'Robovi',
  'field.shape.shadow': 'Senca',
  'field.shape.density': 'Razmik',
  'field.launcher.style': 'Zaganjalnik na računalnikih',
  'field.launcher.phoneStyle': 'Zaganjalnik na telefonih',
  'field.launcher.side': 'Stran zaganjalnika',
  'field.launcher.icon': 'Ikona zaganjalnika',
  'field.panel.header': 'Velikost glave',
  'field.panel.opening': 'Prvi zaslon',
  'field.products.layout': 'Izdelki',
  'field.products.imageFit': 'Fotografije izdelkov',
  'field.products.imageRatio': 'Oblika ploščice fotografije',
  'field.products.imagePadding': 'Prostor okoli fotografij',
  'field.products.imageBackground': 'Barva ploščice fotografije',
  'field.products.picker': 'Izbira velikosti',
  'field.products.showStock': 'Prikazana zaloga',
  'field.cart.landing': 'Animacija košarice',
  'field.logo.svg': 'Logotip',
  'field.logo.shape': 'Oblika logotipa',
  'field.logo.plate': 'Podlaga logotipa',
  'field.css': 'Poljuben CSS',
  'value.ink': 'črnilo',
  'value.brand': 'barva znamke',
  'value.small': 'majhno',
  'value.medium': 'srednje',
  'value.large': 'veliko',
  'value.square': 'kvadratno',
  'value.soft': 'mehko',
  'value.round': 'okroglo',
  'value.flat': 'ravno',
  'value.deep': 'globoko',
  'value.compact': 'strnjeno',
  'value.comfortable': 'udobno',
  'value.pill': 'gumb v obliki kapsule z besedilom',
  'value.circle': 'krog',
  'value.right': 'desno',
  'value.left': 'levo',
  'value.logo': 'logotip',
  'value.chat': 'oblaček klepeta',
  'value.sparkle': 'iskrice',
  'value.bag': 'vrečka',
  'value.full': 'polno',
  'value.middle': 'na sredini',
  'value.bottom': 'spodaj',
  'value.rows': 'vrstice',
  'value.grid': 'mreža',
  'value.carousel': 'vrstica, po kateri podrsate',
  'value.contain': 'cela fotografija',
  'value.cover': 'zapolnjena ploščica',
  'value.portrait': 'pokončno',
  'value.landscape': 'ležeče',
  'value.none': 'brez',
  'value.pop': '+1, ki se dvigne',
  'value.ring': 'val',
  'value.blink': 'utripanje',
  'value.chips': 'gumbi v vrstici',
  'value.menu': 'seznam',
  'value.rounded': 'zaobljeno',
  designCardTitle: 'Nakupovalni pomočnik {name}',
  designCardSaved: 'Različica {version} shranjena · še ni objavljena',
  designCardSavedFirst: 'Videz shranjen · prenesla ga bo prva različica',
  designSaving: 'Shranjevanje videza …',
  selectedAssistantPart: 'Izbrano: del pomočnika',
  loading: 'Nalaganje Storefront …',
  disabledTitle: 'Storefront v tem delovnem prostoru ni vklopljen.',
  disabledBody:
    'Je v zgodnjem dostopu in se uvaja postopoma. Vprašajte svojo kontaktno osebo pri Ponevi, kdaj bo na voljo za vas.',
  loadFailed: 'Storefronta ni bilo mogoče naložiti.',
  startTitle: 'Nakupovalni pomočnik za vašo trgovino',
  startBody:
    'Poneva prebere vašo trgovino tako, kot jo uporablja kupec: iskanje, izdelke, košarico in obstoječi klepet. To traja približno 10–30 minut, uporablja dobroimetje vašega podjetja, vi pa lahko nadaljujete z delom.',
  urlLabel: 'Naslov vaše trgovine',
  urlPlaceholder: 'https://www.vasatrgovina.si',
  startButton: 'Preberi mojo trgovino',
  starting: 'Začenjanje …',
  startFailed: 'Te trgovine ni bilo mogoče prebrati. Preverite naslov in poskusite znova.',
  tabConversations: 'Pogovori',
  tabOverview: 'Pregled',
  allAssistants: 'Vsi pomočniki',
  allAssistantsNote: '{assistants} pomočnikov · {sites} spletnih mest',
  allAssistantsMenuNote: '{count} pogovorov ta teden',
  marketLabel: 'Trg',
  allMarkets: 'Vsi trgi',
  allMarketsNote: '{count} spletnih mest',
  scopeIn: '{assistant} v {market}',
  askAbout: 'Vprašaj Chat o {scope}',
  overviewAssistantsTitle: 'Pomočniki in trgi',
  overviewMarketsTitle: 'Trgi',
  overviewAssistantsOnly: 'Pomočniki',
  overviewIdle: '{count} pomočnikov še nima pogovorov',
  allAssistantsCount: '{assistants} pomočnikov',
  overviewFailed: 'Pregleda ni bilo mogoče naložiti.',
  colConversations: 'Pogovori',
  colCart: 'Dodano v košarico',
  colCheckout: 'Blagajna',
  colPerson: 'Oseba',
  colCost: 'Dobroimetje',
  rowMarkets: '{count} trgov',
  rowVersion: 'različica {version}',
  rowAria:
    '{name}, {sub}: {conversations} pogovorov, {cart} dodanih v košarico, {checkout} doseženih blagajn, {person} prošenj za osebo, dobroimetje {cost}',
  versionPublishedAt: 'objavljeno {at}',
  designColorHeader: 'Glava',
  designColorHeaderInk: 'Besedilo glave',
  designGroupTranslations: 'Besede v drugih jezikih',
  topicsTitle: 'O čem sprašujejo kupci',
  topicsNote: 'Teme, ki jih je Chat našel v prvih vprašanjih',
  startPagesTitle: 'Kje se pogovori začnejo',
  startPagesOne: 'Vsak pogovor se je začel na eni vrsti strani: {page}.',
  'startPage.product': 'Strani izdelkov',
  'startPage.search': 'Rezultati iskanja',
  'startPage.start': 'Začetna stran',
  'startPage.cart': 'Košarica',
  'startPage.other': 'Druge strani',
  addMarketInChat: 'Dodaj trg v Chat',
  marketsTitle: 'Trgi',
  marketsBody:
    'En pomočnik in en videz. Vsak trg ima svoj jezik, valuto, izdelke, košarico in klepet s človekom.',
  marketChecked: 'preverjeno {when}',
  'marketStatus.ready': 'Pripravljeno',
  'marketStatus.found': 'Ni dodano',
  'marketStatus.checking': 'Preverjanje',
  'marketStatus.failed': 'Potrebna je odprava težave',
  addMarketPrompt:
    'Nakupovalnemu pomočniku za {name} ({domain}) dodaj še en trg: poišči druge države, jezike ali spletna mesta trgovine in me vprašaj, katerega naj dodam.',
  tabWidget: 'Widget',
  tabInstall: 'Namestitev',
  tabSetup: 'Nastavitev',
  statusReading: 'Branje vaše trgovine',
  readingProducts: 'Ponovno branje izdelkov',
  readingCart: 'Ponovno preverjanje košarice',
  readingChat: 'Preverjanje poti do vašega klepeta',
  readingMarket: 'Dodajanje trga {market}',
  readingMarketAny: 'Dodajanje trga',
  statusReady: 'Pripravljeno za namestitev',
  statusFailed: 'Branje ustavljeno',
  statusLive: 'Aktivno · različica {version} na {domain}',
  statusPublished: 'Objavljeno · različica {version} · še ni na vašem spletnem mestu',
  statusPublishedUnverified:
    'Objavljeno · različica {version} · vašega spletnega mesta ni bilo mogoče preveriti',
  stepOpen: 'Odpiranje spletnega mesta',
  stepSearch: 'Iskanje',
  stepProducts: 'Izdelki',
  stepCart: 'Košarica',
  stepChat: 'Vaš obstoječi klepet',
  stepLook: 'Vaš videz in logotip',
  stepChecks: 'Preverjanje delovanja',
  stepReady: 'Pripravljeno',
  stepDetailOpen: 'Odpiranje {domain}',
  stepDetailSearch: 'Iskanje izdelkov trgovine',
  stepDetailProducts: 'Branje izdelkov, cen, fotografij, barv in velikosti',
  stepDetailCart: 'Preizkušanje košarice: dodajanje, ponovno branje, odstranjevanje in praznjenje',
  stepDetailChat: 'Iskanje vašega obstoječega klepeta',
  stepDetailLook: 'Branje logotipa in barv trgovine',
  stepDetailChecks: 'Preizkušanje iskanja kupcev, izdelkov, brskanja in košarice',
  stepDetailChecksProducts: 'Preverjanje iskanja izdelkov, brskanja, fotografij, barv in velikosti',
  stepDetailChecksCart:
    'Preverjanje dodajanja, ponovnega branja, odstranjevanja in praznjenja košarice ter branja izdelkov',
  stepDetailChecksChat: 'Preverjanje predaje vašemu obstoječemu klepetu in branja izdelkov',
  stepDetailChecksMarket: 'Preverjanje izdelkov, cen in košarice tega trga',
  nowRunning: 'Zdaj: {step}',
  nowProgress: 'Korak {n} od {total} · {minutes} min doslej',
  nowReady: 'Vaša trgovina je pripravljena na pomočnika',
  nowReadyBody: 'Iskanje, izdelki in košarica delujejo.',
  nowLastRead: 'Nazadnje prebrano {when}',
  nowUsually: 'običajno {min}–{max}',
  stepsRead: 'Prebrano {when} · Prikaži korake',
  nowFailed: 'Branje ustavljeno',
  factAnswers: 'Kupcem odgovarja z',
  costCounting: 'Štetje …',
  factPaid: 'Plačano iz',
  paidCompany: 'Dobroimetja podjetja',
  creditsAmount: '{amount} enot dobroimetja',
  creditsOne: '{amount} enota dobroimetja',
  creditsUnder: 'manj kot {amount} enot dobroimetja',
  stopRun: 'Ustavi izvajanje',
  stopping: 'Ustavljanje …',
  closeNote: 'To stran lahko zapustite. Izvajanje se nadaljuje.',
  readyNote: 'Naprej: preizkusite Widget in ga nato namestite.',
  buildWidget: 'Preizkusi Widget',
  readAgain: 'Ponovno preberi trgovino',
  foundTitle: 'Kaj je našel',
  foundProducts: 'Prebrani izdelki in cene',
  foundSearch: 'Iskanje deluje',
  foundCart: 'Dodajanje v košarico deluje',
  foundChat: 'Vaš klepet {vendor}',
  foundNoChat: 'Nimate lastnega klepeta',
  foundLook: 'Vaše barve in logotip',
  widgetTitle: 'Vaš Widget',
  widgetBody:
    'Vaš pomočnik v vaših barvah. Preizkusite ga tukaj: odgovarja z vašimi resničnimi izdelki. V pravo košarico se nič ne doda.',
  widgetLoading: 'Nalaganje predogleda …',
  widgetFailed: 'Predogleda ni bilo mogoče naložiti.',
  widgetNotReady: 'Widget bo pripravljen, ko bodo zaključeni koraki na zavihku Nastavitev.',
  installLineTitle: 'Dodajte to vrstico na svoje spletno mesto',
  installLineBody:
    'Deluje na kateri koli platformi: v glavi strani, v Shopifyju v theme.liquid tik pred </head>. Ničesar ne prikaže, dokler ga ne objavite.',
  installLineBodyLive:
    'Deluje na kateri koli platformi: v glavi strani, v Shopifyju v theme.liquid tik pred </head>. Kupci bodo nato videli različico {version}.',
  installPolicyAsk: 'Ali vaše spletno mesto nastavlja pravilnik Content Security Policy?',
  installPolicy:
    'Če vaše spletno mesto nastavlja pravilnik Content Security Policy, dovolite tudi {origin} v {directives}, sicer se pomočnik ne more zagnati.',
  installPolicyRefuses:
    'Pravilnik Content Security Policy vašega spletnega mesta zavrača pomočnika: dovolite {origin} v {directives}, sicer se nič ne bo zagnalo.',
  installPolicyNonce:
    'Vaš pravilnik izvaja samo zaupanja vredne skripte, zato dodajte vrstico z nonce vaše strani.',
  installPolicyEval: "Pomočnik potrebuje tudi 'unsafe-eval' v script-src.",
  copy: 'Kopiraj',
  copied: 'Kopirano',
  domainsTitle: 'Spletna mesta, na katerih odgovarja',
  domainsBody: 'Na drugih spletnih mestih ostane tiho, tudi če kdo kopira vrstico.',
  lineFound: 'Vrstica najdena',
  lineNotSeen: 'Na strani še ni',
  lineUnverified:
    'Preverjanje ni uspelo: spletno mesto blokira samodejne obiske. Pomočnik kljub temu deluje za kupce.',
  lineChecked: 'preverjeno {when}',
  lineCheckAgain: 'Preveri znova',
  lineChecking: 'Preverjanje strani …',
  installCheckBusy: 'Preverjanje je trenutno zasedeno. Poskusite znova čez trenutek.',
  publishTitle: 'Objava',
  publishBody: 'Kupci na {domain} ga bodo videli takoj. Kadar koli ga lahko umaknete iz objave.',
  publishButton: 'Objavi različico {version}',
  publishConfirm: 'Objavim različico {version} za {name}?',
  publishNow: 'Objavi',
  notNow: 'Ne zdaj',
  publishing: 'Objavljanje …',
  liveTitle: 'Objavljeno: različica {version}',
  liveSince: 'Od {since}',
  unpublish: 'Umakni iz objave',
  unpublishConfirm:
    'Odstranim različico {version} s spletnega mesta? Kupci pomočnika ne bodo več videli, dokler ga znova ne objavite.',
  publishedLine: 'Zdaj objavljeno: različica {version}, od {since}',
  usageTitle: 'Uporaba',
  usageBody: 'Uporablja dobroimetje vašega podjetja, tako kot preostali Chat.',
  usageDiscovery: 'Branje trgovine',
  usageChats: 'Klepeti kupcev',
  usageChatsValue: '{count} klepetov · {cost}',
  last7: 'Zadnjih 7 dni',
  askWeek: 'Vprašaj Chat o tem tednu',
  kpiConversations: 'Pogovori',
  kpiVsPrevious: '{change} v primerjavi s prejšnjim tednom',
  kpiCart: 'Dodano v košarico',
  kpiCheckout: 'Dosežena blagajna',
  kpiPerson: 'Prošnja za osebo',
  kpiCost: 'Porabljeno dobroimetje',
  ofConversations: '{percent} pogovorov',
  checkoutNote: '{percent} pogovorov',
  personNote: '{percent} jih je odprlo vaš klepet',
  costNote: 'Približno {amount} na pogovor',
  costNoteUnder: 'Manj kot {amount} na pogovor',
  sparkLabel: 'Pogovori na dan, zadnjih {days} dni',
  sparkDay: '{day}: {count} pogovorov',
  filterAll: 'Vse',
  filterCart: 'Dodano v košarico',
  filterCheckout: 'Blagajna',
  filterPerson: 'Oseba',
  outcomeCart: 'Dodano v košarico',
  outcomeCheckout: 'Dosežena blagajna',
  outcomePerson: 'Prošnja za osebo',
  outcomeAnswered: 'Odgovorjeno',
  devicePhone: 'Telefon',
  deviceComputer: 'Računalnik',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Začeto na strani {page}',
  anonymous: 'Kupec je anonimen.',
  askConversation: 'Vprašaj Chat o tem pogovoru',
  showOlderVersions: 'Prikaži starejše različice',
  showOlder: 'Prikaži starejše pogovore',
  filterCountsWeek: 'Število: zadnjih 7 dni',
  readAlreadyHere:
    'Ta trgovina je že tukaj, zato se ni začelo nič novega in dobroimetje ni bilo porabljeno.',
  readOpenShop: 'Odpri jo',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: 'Kaj se lahko naučimo iz tega pogovora?',
  askWeekMessage: 'Kaj se lahko naučimo iz pogovorov tega tedna?',
  conversationsChipOne: '{name} · pogovor z dne {when}',
  conversationsChipWeek: '{name} · ta teden',
  conversationsChipWeekIn: '{name} · {market} · ta teden',
  removeConversations: 'Odstrani pogovore',
  noConversations: 'Še ni pogovorov',
  noConversationsBody: 'Ko kupci uporabljajo pomočnika, se njihovi pogovori prikažejo tukaj.',
  pickConversation: 'Izberite pogovor za branje.',
  eventCheckout: 'Odšel/-la na blagajno',
  eventPerson: 'Odprl/-a vaš klepet',
  showMore: 'Prikaži več',
  urlInvalid: 'Uporabite https-naslov svoje trgovine, na primer https://www.vasatrgovina.si.',
  errorNoCompany: 'Storefront potrebuje delovni prostor podjetja. Prosite skrbnika, naj vas doda.',
  errorPublishPermission:
    'To lahko storijo samo osebe, ki lahko objavljajo za vaše podjetje. Prosite skrbnika.',
  actionFailed: 'To ni uspelo. Poskusite znova.',
  copyFailed: 'Kopiranje ni uspelo. Izberite vrstico in jo kopirajte sami.',
  reasonCancelled: 'Ustavili ste izvajanje.',
  reasonFailed: 'Branja vaše trgovine ni bilo mogoče dokončati, zato pomočnik še ni pripravljen.',
  reasonFailedLive:
    'Branja vaše trgovine ni bilo mogoče dokončati. Objavljeni pomočnik še naprej deluje kot prej.',
  reasonFailedKept:
    'Branja vaše trgovine ni bilo mogoče dokončati, zato se ni nič spremenilo: različica {version} ostaja enaka.',
  statusReadStopped: 'Pripravljeno · zadnje branje je bilo ustavljeno',
  reasonStepMarked: 'Korak, pri katerem se je ustavilo, je označen spodaj.',
  reasonReadAgain: 'Ponovno branje se začne s tem, kar je izvedelo prejšnje branje.',
  reasonNotAStore:
    'Ta naslov ni videti kot spletna trgovina: ni bilo izdelkov za iskanje. Preverite naslov ali poskusite z glavno domeno trgovine.',
  reasonTooSlow:
    'Iskanje vaše trgovine odgovarja prepočasi za nakupovalnega pomočnika: trajalo je več kot 12 sekund. Hitrejše iskanje v trgovini ali poznejše ponovno branje lahko pomaga.',
  reasonOurSide:
    'Branje se je ustavilo zaradi težave na naši strani, ne v vaši trgovini, zato pomočnik še ni pripravljen.',
  reasonOurSideLive:
    'Branje se je ustavilo zaradi težave na naši strani, ne v vaši trgovini. Objavljeni pomočnik še naprej deluje kot prej.',
  reasonOurSideKept:
    'Branje se je ustavilo zaradi težave na naši strani, ne v vaši trgovini, zato se ni nič spremenilo: različica {version} ostaja enaka.',
  reasonTryLater: 'Poskusite trgovino znova prebrati čez nekaj časa.',
  errorNoCredits:
    'Za to trenutno ni na voljo enot dobroimetja. Lastnik podjetja lahko preveri paket in omejitve v razdelku Usage.',
  errorReadUnavailable: 'Te trgovine trenutno ni mogoče prebrati. Poskusite znova čez nekaj minut.',
  reasonTimeout:
    'Branje je doseglo časovno omejitev, preden se je končalo. Ponovno branje nadaljuje s tem, kar je bilo že preverjeno.',
  retrying: 'Ponovni začetek …',
  readTitleChecks: 'Ponovno preveri shranjeno branje',
  checksScopeLabel: 'Kaj preveri',
  readScopeChecks:
    'Iskanja kupcev, izdelki, brskanje in košarica, na tem, kar je shranilo zadnje branje. Trgovina se ne prebere znova.',
  readScopeChecksProducts:
    'Iskanje izdelkov, brskanje, fotografije, barve in velikosti, na tem, kar je shranilo zadnje branje. Trgovina se ne prebere znova.',
  readScopeChecksCart:
    'Dodajanje, ponovno branje, odstranjevanje in praznjenje košarice ter branje izdelkov, na tem, kar je shranilo zadnje branje. Trgovina se ne prebere znova.',
  readScopeChecksChat:
    'Predaja vašemu obstoječemu klepetu in branje izdelkov, na tem, kar je shranilo zadnje branje. Trgovina se ne prebere znova.',
  readScopeChecksMarket:
    'Izdelki, cene in košarica trga, na tem, kar je shranilo zadnje branje. Trgovina se ne prebere znova.',
  checksCost: 'Dobroimetje podjetja porabijo samo nova preverjanja',
  checksAfter:
    'Shrani nov osnutek, če preverjanja uspejo. Kupci ničesar ne vidijo, dokler ga ne objavite.',
  checksAfterLive:
    'Shrani nov osnutek, če preverjanja uspejo. Objavljeni pomočnik deluje naprej, kupci pa spremembo vidijo šele po objavi.',
  checksStarted:
    'Preverjanja so se začela. Ko bo novi osnutek pripravljen, prosite Chat, naj ga preveri.',
  checkingSavedWork: 'Ponovno preverjanje shranjenega dela',
  checkAgain: 'Ponovno preveri',
  checkingAgain: 'Ponovno preverjanje …',
  reasonCheckAgain:
    '»Ponovno preveri« še enkrat preizkusi shranjeno delo, ne da bi trgovino znova prebrali. Dobroimetje podjetja porabijo samo nova preverjanja.',
  fixWithChat: 'Popravi s Chat',
  fixReadPrompt:
    'Branje trgovine {name} ({domain}) se je ustavilo. Poglej, kaj se je zgodilo, in mi pomagaj, da bo delovalo: povej mi, kaj najdeš, predlagaj, kaj lahko povem branju, nato pa ga ponovno preveri ali ga s tem namigom ponovi in pri tem preberi čim manjši del trgovine.',
  fixReadPromptStep:
    'Branje trgovine {name} ({domain}) se je ustavilo pri koraku »{step}«. Poglej, kaj se je zgodilo, in mi pomagaj, da bo delovalo: povej mi, kaj najdeš, predlagaj, kaj lahko povem branju, nato pa ga ponovno preveri ali ga s tem namigom ponovi in pri tem preberi čim manjši del trgovine.',
  stepStateDone: 'končano',
  stepStateRunning: 'v teku',
  stepStateWaiting: 'še ni začeto',
  stepStateFailed: 'ustavljeno',
  newVersionTitle: 'Različica {version} je pripravljena',
  newVersionBody: 'Objavite jo, da zamenja različico {live}.',
  newVersionBodyLive: 'Objavite jo, da zamenja različico {live} na {domain}.',
  previewSize: 'Velikost predogleda',
  designWithChat: 'Odpri oblikovanje',
  designReviewBody: 'Oglejte si trenutno oblikovanje in povejte Chatu, kaj želite prilagoditi.',
  designWhileTitle: 'Oblikujte ga med branjem trgovine',
  designWhileBody:
    'Chat lahko zdaj skupaj z vami oblikuje videz, besede in logotip pomočnika. Videz bo pripravljen, ko bo branje končano.',
  designPreviewNote: 'Predogled z vzorčnimi izdelki, dokler trgovina ni prebrana.',
  proposedDesign: 'Predlagani videz',
  proposedDesignNote:
    'Gradnik s to spremembo, prikazan z vzorčnimi izdelki. Z odobritvijo se shrani kot nov osnutek; objava ostane na strani Storefront.',
  lineSources: 'Iz strani trgovine:',
  readAgainConfirm:
    'Ponovno preberem {name}? To traja približno 10–30 minut in uporablja dobroimetje podjetja.',
  readAgainLive: 'Objavljeni pomočnik deluje naprej, dokler ne objavite nove različice.',
  readAgainNow: 'Preberi znova',
  switchStore: 'Zamenjaj trgovino ali dodaj novo',
  storesLabel: 'Vaše trgovine',
  addStore: 'Dodaj drugo trgovino',
  backToStores: 'Nazaj k vašim trgovinam',
  storeLive: 'Objavljeno',
  storeReady: 'Pripravljeno',
  storeReading: 'Branje',
  storeStopped: 'Ustavljeno',
  actionDesignTitle: 'Spremeni videz nakupovalnega pomočnika',
  actionKnowledgeTitle: 'Spremeni, kaj nakupovalni pomočnik ve',
  readTitleProducts: 'Ponovno preberi izdelke',
  readTitleCart: 'Ponovno preveri košarico',
  readTitleTheirChat: 'Ponovno preveri pot do vašega klepeta',
  readTitleAll: 'Ponovno preberi celotno trgovino',
  readTitleStore: 'Preberi novo trgovino',
  readTitleMarket: 'Dodaj trg',
  readShopLabel: 'Trgovina',
  readScopeLabel: 'Kaj prebere',
  readTimeLabel: 'Trajanje',
  readCostLabel: 'Cena',
  readAfterLabel: 'Po koncu',
  readScopeProducts: 'Iskanje in razvrščanje izdelkov, fotografije izdelkov, barve in velikosti',
  readScopeCart: 'Dodajanje v košarico in odstranjevanje iz nje',
  readScopeTheirChat: 'Kako kupci pridejo do vašega klepeta',
  readScopeAll: 'Izdelki, košarica, vaš klepet in vaši trgi',
  readScopeStore: 'Izdelki, cene in velikosti, košarica, vaš klepet, vaš videz in vaši trgi',
  readScopeMarket: '{market}: njegovi izdelki, cene in košarica',
  readTime: 'Običajno {min}–{max} minut',
  readCost: 'Uporablja dobroimetje podjetja',
  readCostLast: 'Uporablja dobroimetje podjetja · zadnje branje te trgovine je porabilo {credits}',
  readCostLastPartial:
    'Uporablja dobroimetje podjetja · zadnje branje te trgovine, ki se je ustavilo prezgodaj, je porabilo vsaj {credits}',
  readAfter: 'Shrani nov osnutek. Kupci ničesar ne vidijo, dokler ga ne objavite.',
  readAfterLive:
    'Shrani nov osnutek. Objavljeni pomočnik deluje naprej, kupci pa spremembo vidijo šele po objavi.',
  readAfterNew: 'Shrani prvi osnutek za preizkus. Nič ni objavljeno.',
  readCartNote:
    'Za preverjanje košarice doda preizkusni izdelek in ga nato odstrani. Nakupa nikoli ne dokonča.',
  readFocusLabel: 'Kaj je Chat prosil, naj branje popravi',
  readFooter: 'Nič se ne začne, dokler ne odobrite. Branje lahko ustavite na zavihku Nastavitev.',
  readStartedAt:
    'Začeto {time} · običajno {min}–{max} minut. Ko bo novi osnutek pripravljen, prosite Chat, naj ga preveri.',
  readFocusAll: 'Prikaži vse',
  readFocusLess: 'Prikaži manj',
  readStart: 'Začni branje',
  addToSite: 'Dodaj na svoje spletno mesto',
  readTab: 'Zahteva',
  readStarted: 'Branje se je začelo. Ko bo novi osnutek pripravljen, prosite Chat, naj ga preveri.',
  readFollow: 'Spremljaj na zavihku Nastavitev',
  proposedDesignLive:
    'Resnični predogled vaše trgovine v tem videzu. Nekaj vprašajte: v pravo košarico se nič ne doda. Z odobritvijo se shrani kot nov osnutek; objava ostane na strani Storefront.',
  designChanges: 'Kaj se spreminja',
  designReset: 'nazaj na privzeto',
  designGroupColors: 'Barve',
  designGroupType: 'Pisave',
  designGroupShape: 'Oblika',
  designGroupLauncher: 'Gumb na strani',
  designGroupPanel: 'Glava',
  designGroupProducts: 'Izdelki',
  designGroupCart: 'Košarica trgovine',
  designGroupCopy: 'Besedilo',
  designGroupLogo: 'Logotip',
  designGroupCss: 'Poljuben CSS',
  designColorBrand: 'Znamka',
  designColorActions: 'Dodaj in pošlji',
  designColorPanel: 'Plošča',
  designColorSurface: 'Površine',
  designColorInk: 'Besedilo',
  designColorMuted: 'Drugotno besedilo',
  designColorLine: 'Črte',
  designColorBubble: 'Sporočila kupcev',
  designColorBubbleInk: 'Besedilo kupcev',
  designActionsBrand: 'Barva znamke',
  designActionsInk: 'Barva besedila',
  designWordsName: 'Ime',
  designWordsGreeting: 'Pozdrav',
  designWordsStarters: 'Začetna vprašanja',
  designWordsLauncher: 'Oznaka gumba',
  designWordsPlaceholder: 'Nadomestno besedilo',
  designWordsAi: 'Oznaka UI',
  designWordsPerson: 'Povezava do osebe',
  noteNew: 'Novo',
  noteChanged: 'Spremenjeno',
  noteRemoved: 'Odstranjeno',
  noteSourceKnowledge: 'Stran v razdelku Znanje: {title}',
  noteSourceFile: 'Datoteka: {name}',
  noteSourceChat: 'Iz Chat',
  knowledgeApprovalNote:
    'Kupci lahko preberejo vse v teh opombah. Z odobritvijo se shrani nov osnutek: postavite mu vprašanja na zavihku Widget, nato ga objavite na zavihku Namestitev.',
  profileTitle: 'O trgovini',
  profileChecked: 'Preverjeno {date}',
  profileSources: 'Napisano iz',
  profileSample: '{count} izdelkov je vzorčenih za cenovni nivo',
  profileSampleOne: '{count} izdelek je vzorčen za cenovni nivo',
  profileAssistant: 'Kaj lahko asistent tukaj naredi',
  profileTeam: 'Napisala vaša ekipa',
  profileEmpty:
    'Še ni napisano. Prosite Chat, naj zažene prehod znanja: to napiše iz vaših lastnih strani in tega, kar je videl na vašem spletnem mestu, vi pa lahko dodate svoje vrstice.',
  profileWithChat: 'Dodaj ali spremeni s Chat',
  profilePrompt:
    'Pomagaj mi izboljšati, kar nakupovalni asistent za {name} ({domain}) ve o naši trgovini kot celoti: kaj prodajamo, po čem smo znani, za koga smo in kaj lahko kupci tukaj naredijo. Najprej preberi profil „O trgovini“ in mi povej, kaj manjka ali je napačno. Vse, kar ti povem o trgovini in česar ne navaja nobena stran, se vnese kot vrstica naše ekipe.',
  profilePartNavigation: 'Oddelki',
  profilePartWelcome: 'Pozdrav',
  profilePartMarkets: 'Trgi',
  profilePartFunctions: 'Kaj asistent zna',
  profilePartProducts: 'Izdelki',
  profilePartPages: 'Strani s storitvami',
  profileCleared: 'Počisti napisani profil',
  knowledgeTitle: 'Kaj ve',
  knowledgeBody: 'Poleg izdelkov, cen in zaloge, ki jih vedno bere neposredno iz vaše trgovine.',
  knowledgeEmpty:
    'Za zdaj samo vaš katalog. S Chat dodajte informacije o dostavi, vračilih ali plačilu s svojih strani ali iz razdelka Znanje.',
  knowledgeWithChat: 'Dodaj s Chat',
  knowledgeChangeWithChat: 'Spremeni s Chat',
  knowledgePrompt:
    'Pomagaj mi izbrati, kaj nakupovalni pomočnik za {name} ({domain}) pove kupcem poleg naših izdelkov, na primer o dostavi, vračilih in plačilu. Poišči to na naših straneh ali v razdelku Znanje in predlagaj opombe, ki jih lahko odobrim.',
  pagesTitle: 'Strani, ki jih bere',
  pagesBody:
    'Strani, iz katerih prihajajo zapiski. Poneva jih preverja vsak dan, Chat pa sporoči, ko se katera spremeni.',
  pagesEmpty:
    'Še ni strani. Tukaj se prikažejo strani, ki jih najde branje trgovine, in tiste, ki jih navedete prek Chat.',
  pageFound: 'Ponevina najdba',
  pageLinkedFrom: 'povezano z {page}',
  pageAddedByYou: 'dodano ročno',
  pageFromNote: 'omenjena v opombi',
  pageNoteOne: '1 zapis',
  pageNotesMany: '{count} zapisov',
  pageStatusOk: 'Posodobljeno',
  pageStatusChanged: 'Spremenjeno',
  pageStatusUnreadable: 'Ni mogoče prebrati',
  pageStatusUnchecked: 'Še ni preverjeno',
  pagesChangeWithChat: 'Spremeni s Chat',
  pagesUpdateWithChat: 'Posodobi zapise s Chat',
  pagesPrompt:
    'Pomagaj mi spremeniti, katere strani nakupovalni pomočnik za {name} ({domain}) bere za svoje zapiske. Pokaži mi strani, ki jih zdaj uporablja, in me vprašaj, katere naj dodam ali odstranim.',
  pagesUpdatePrompt:
    'Nekatere strani za {name} ({domain}) so se spremenile ali jih od nastanka zapiskov ni več mogoče prebrati. Pokaži mi, katere so, in iz njih ponovno ustvari zapiske kot osnutek, ki ga lahko pregledam.',
  viewCompare: 'Primerjava',
  lookDeviceLabel: 'Primerjaj na',
  lookShop: 'Vaša trgovina',
  lookShopCaption: 'Domača stran, prebrana {when}',
  lookWidget: 'Pomočnik',
  lookWidgetCaption: 'Različica {version}',
  lookLoading: 'Nameščanje pomočnika ob vašo trgovino …',
  lookFailed: 'Primerjave trenutno ni bilo mogoče narediti.',
  lookNoShopPicture: 'Branje ni shranilo slike vaše trgovine pri tej širini.',
  lookNoneTitle: 'Lastni videz vaše trgovine ni bil shranjen',
  lookNoneBody:
    'To branje ga ni zabeležilo. Ponovno preberite trgovino v razdelku Nastavitev in njen logotip, barve in strani bodo shranjeni za primerjavo.',
  lookColoursShop: 'Barve, ki jih uporablja vaša trgovina',
  lookColoursAssistant: 'Barve pomočnika',
  lookRoleHeader: 'Glava',
  lookRoleBrand: 'Znamka',
  lookSame: 'Enaka kot barva {colour} vaše trgovine ({source})',
  lookOff: 'Blizu barvi {colour} vaše trgovine ({source}), vendar ni enaka, razlika {delta}',
  lookOwn: 'Lastna barva: vaša trgovina te barve ne uporablja',
  lookNeutral: 'Vaša trgovina za to ni bila naložena: prikazano je le to, kar je shranilo branje.',
  lookFromLogo: 'logotip',
  lookFromTheme: 'barva teme',
  lookFromTile: 'barva ploščice',
  lookFromMask: 'pripeta ikona',
  lookFromManifest: 'manifest aplikacije',
  lookFromHeader: 'glava',
  lookFromButton: 'gumbi',
  lookFromVariable: 'barva sloga',
  lookFix: 'Popravite s Chat',
  lookFixPrompt:
    'Barva pomočnika ({role}) je {colour}, blizu barvi {exact} vaše trgovine ({source}), vendar ni enaka. Uporabite natančno barvo vaše trgovine, {exact}.',
  lookSwatch: 'Barva {colour}',
  errorDesignChecks: 'Ta različica ni prestala preverjanj zasnove.',
  designSamplesBlocked:
    'Postavitev kartic tu ni mogoče dokazati: vaše spletno mesto blokira samodejne obiske. Barve, pisave in logotip še vedno delujejo.',
  designCheckLabel: 'Oznaka UI je prešibka za branje.',
  designCheckReach: 'Gumba ni mogoče doseči.',
  designCheckSize: 'Del besedila je premajhen.',
  designCheckOverflow: 'Plošča se premika vstran.',
  designCheckRender: 'Preverjanj ni bilo mogoče izvesti. Poskusite znova čez trenutek.',
  fixDesignPrompt:
    'Poskusil sem objaviti najnovejši osnutek za {name} ({domain}), a ni prestal preverjanj zasnove: {facts}. Popravite zasnovo v tem klepetu in jo preverite na telefonu in računalniku. Ne objavite je.',
};

const es: Record<StorefrontCopyKey, string> = {
  pageTitle: 'Storefront',
  assistantTitle: 'Asistente de compras',
  designUnconfirmed:
    'Se detuvo antes de que Chat confirmara que el diseño se guardó. El lienzo del asistente muestra su última versión.',
  errorNewerVersion:
    'Se ha guardado una versión más reciente desde que abriste esta. Revísala antes de publicar.',
  canvasSelectHint:
    'Haz clic en una parte del asistente para cambiarla en el chat · Esc para salir',
  canvasDraft: 'Versión {version} · aún sin publicar',
  canvasDraftOverLive: 'Versión {version} · la versión {live} está publicada',
  canvasLive: 'Versión {version} · publicada',
  canvasNoVersion: 'La primera versión llega cuando termina la lectura de la tienda',
  canvasPublish: 'Publicar en la página Storefront',
  canvasFrameTitle: 'Asistente de compras de {name}',
  viewLabel: 'Vista',
  photosGood: '{count} fotos de productos: completas y nítidas',
  photosGoodOne: 'La foto del producto se ve completa y nítida',
  canvasMissingTitle: 'Este asistente de compras no está en este espacio de trabajo',
  canvasMissingBody: 'Pertenece a otro espacio de trabajo o se ha eliminado.',
  canvasMissingLink: 'Ver los asistentes de este espacio de trabajo',
  photosLabel: 'Fotos de productos',
  photosSmall: '{count} pequeñas en sus recuadros',
  photosCut: '{count} recortadas',
  photosSoft: '{count} ampliadas y poco nítidas',
  photosMissing: '{count} sin foto',
  photosFix: 'Corregir con Chat',
  photosPrompt:
    'Haz que las fotos de las tarjetas se vean bien: completas, nítidas y con un tamaño adecuado en su recuadro.',
  photosFacts:
    '{total} fotos: {small} pequeñas en sus recuadros, {cut} recortadas, {soft} ampliadas, {missing} sin foto. Tamaños: {sizes}',
  viewDesign: 'Diseño',
  viewVersions: 'Versiones',
  versionsLoading: 'Cargando versiones…',
  versionsFailed: 'No se han podido cargar las versiones.',
  versionTitle: 'Versión {version}',
  versionLive: 'Publicada',
  versionChecked: 'Comprobada',
  versionToday: 'Hoy',
  versionYesterday: 'Ayer',
  versionEarlier: 'Anteriormente',
  versionByPerson: 'Solicitada por {name}',
  versionByChat: 'Solicitada en el chat',
  versionFromReading: 'De la lectura de {domain}',
  versionPublishedBy: 'publicada por {name}, {at}',
  versionPublish: 'Publicar',
  versionRestore: 'Restaurar…',
  versionRestoreTitle: '¿Restaurar la versión {version}?',
  versionRestoreBody:
    'Será la versión {next}, un nuevo borrador. Tu web no cambia hasta que publiques.',
  versionRestoreConfirm: 'Restaurar',
  versionRestoreCancel: 'Cancelar',
  versionRestored: 'Restaurada como versión {version}',
  versionRestoreFailed: 'No se ha podido restaurar la versión.',
  compareTitle: 'Comparar la versión {to} con la {from}',
  compareTitleLive: 'Comparar la versión {to} con la {from}, publicada',
  compareHint: 'Arrastra para comparar',
  compareLabel: 'Comparar las versiones {from} y {to}',
  compareTagLive: 'v{version} · Publicada',
  compareTag: 'v{version}',
  changesTitle: 'Qué cambió de la versión {from} a la {to}',
  changesNone: 'Los diseños son iguales. Solo cambiaron las notas o los datos de la tienda.',
  changeAdded: 'Añadido',
  changeRemoved: 'Eliminado',
  changeChanged: 'Modificado',
  changeDrawn: 'un logotipo dibujado',
  changeCss: 'CSS propio',
  changeYes: 'sí',
  changeNo: 'no',
  'field.colors.brand': 'Color de marca',
  'field.colors.actions': 'Color de botones',
  'field.colors.panel': 'Color del panel',
  'field.colors.surface': 'Color de superficies',
  'field.colors.ink': 'Color del texto',
  'field.colors.muted': 'Color del texto secundario',
  'field.colors.line': 'Color de líneas',
  'field.colors.bubble': 'Color del mensaje del cliente',
  'field.colors.bubbleInk': 'Texto del mensaje del cliente',
  'field.colors.header': 'Color de cabecera',
  'field.colors.headerInk': 'Color del texto de cabecera',
  'field.colors.tints': 'Tonos suaves',
  'field.copy.assistantName': 'Nombre',
  'field.copy.greeting': 'Saludo',
  'field.copy.placeholder': 'Campo de preguntas',
  'field.copy.starters': 'Preguntas iniciales',
  'field.copy.launcherLabel': 'Texto del botón de apertura',
  'field.copy.aiLabel': 'Etiqueta de IA',
  'field.copy.personLabel': 'Enlace a una persona',
  'field.type.body': 'Fuente del texto',
  'field.type.display': 'Fuente de los títulos',
  'field.type.fontCss': 'Origen de la fuente',
  'field.type.scale': 'Tamaño del texto',
  'field.shape.radius': 'Esquinas',
  'field.shape.shadow': 'Sombra',
  'field.shape.density': 'Espaciado',
  'field.launcher.style': 'Botón en ordenadores',
  'field.launcher.phoneStyle': 'Botón en móviles',
  'field.launcher.side': 'Lado del botón',
  'field.launcher.icon': 'Icono del botón',
  'field.panel.header': 'Tamaño de cabecera',
  'field.panel.opening': 'Primera pantalla',
  'field.products.layout': 'Productos',
  'field.products.imageFit': 'Fotos de productos',
  'field.products.imageRatio': 'Forma del recuadro de foto',
  'field.products.imagePadding': 'Espacio alrededor de las fotos',
  'field.products.imageBackground': 'Color del recuadro de foto',
  'field.products.picker': 'Elección de talla',
  'field.products.showStock': 'Disponibilidad visible',
  'field.cart.landing': 'Animación del carrito',
  'field.logo.svg': 'Logotipo',
  'field.logo.shape': 'Forma del logotipo',
  'field.logo.plate': 'Fondo del logotipo',
  'field.css': 'CSS personalizado',
  'value.ink': 'color del texto',
  'value.brand': 'color de marca',
  'value.small': 'pequeño',
  'value.medium': 'mediano',
  'value.large': 'grande',
  'value.square': 'cuadrado',
  'value.soft': 'suave',
  'value.round': 'redondo',
  'value.flat': 'plano',
  'value.deep': 'profundo',
  'value.compact': 'compacto',
  'value.comfortable': 'holgado',
  'value.pill': 'cápsula con texto',
  'value.circle': 'redondo',
  'value.right': 'derecha',
  'value.left': 'izquierda',
  'value.logo': 'logotipo',
  'value.chat': 'burbuja de chat',
  'value.sparkle': 'destello',
  'value.bag': 'bolsa',
  'value.full': 'completo',
  'value.middle': 'en el centro',
  'value.bottom': 'abajo',
  'value.rows': 'filas',
  'value.grid': 'cuadrícula',
  'value.carousel': 'fila deslizable',
  'value.contain': 'foto completa',
  'value.cover': 'recuadro lleno',
  'value.portrait': 'vertical',
  'value.landscape': 'horizontal',
  'value.none': 'ninguno',
  'value.pop': '+1 que sube',
  'value.ring': 'onda',
  'value.blink': 'parpadeo',
  'value.chips': 'botones en fila',
  'value.menu': 'lista',
  'value.rounded': 'redondeado',
  designCardTitle: 'Asistente de compras de {name}',
  designCardSaved: 'Versión {version} guardada · aún sin publicar',
  designCardSavedFirst: 'Diseño guardado · se usará en la primera versión',
  designSaving: 'Guardando el diseño…',
  selectedAssistantPart: 'Selección: una parte del asistente',
  loading: 'Cargando Storefront…',
  disabledTitle: 'Storefront no está activado en este espacio de trabajo.',
  disabledBody:
    'Está en acceso anticipado y llega por fases. Pregunta a tu contacto de Poneva cuándo estará disponible para tu empresa.',
  loadFailed: 'No se ha podido cargar Storefront.',
  startTitle: 'Un asistente de compras para tu tienda',
  startBody:
    'Poneva lee tu tienda como la usa un cliente: búsquedas, productos, carrito y el chat que ya tienes. Tarda entre 10 y 30 minutos y usa los créditos de tu empresa. Puedes seguir trabajando.',
  urlLabel: 'Dirección de tu tienda',
  urlPlaceholder: 'https://www.tutienda.es',
  startButton: 'Leer mi tienda',
  starting: 'Iniciando…',
  startFailed: 'No se ha podido leer esa tienda. Comprueba la dirección e inténtalo de nuevo.',
  tabConversations: 'Conversaciones',
  tabOverview: 'Resumen',
  allAssistants: 'Todos los asistentes',
  allAssistantsNote: '{assistants} asistentes · {sites} sitios',
  allAssistantsMenuNote: '{count} conversaciones esta semana',
  marketLabel: 'Mercado',
  allMarkets: 'Todos los mercados',
  allMarketsNote: '{count} sitios',
  scopeIn: '{assistant} en {market}',
  askAbout: 'Preguntar a Chat sobre {scope}',
  overviewAssistantsTitle: 'Asistentes y mercados',
  overviewMarketsTitle: 'Mercados',
  overviewAssistantsOnly: 'Asistentes',
  overviewIdle: '{count} asistentes aún sin conversaciones',
  allAssistantsCount: '{assistants} asistentes',
  overviewFailed: 'No se ha podido cargar el resumen.',
  colConversations: 'Conversaciones',
  colCart: 'Añadido al carrito',
  colCheckout: 'Pago',
  colPerson: 'Persona',
  colCost: 'Créditos',
  rowMarkets: '{count} mercados',
  rowVersion: 'versión {version}',
  rowAria:
    '{name}, {sub}: {conversations} conversaciones, {cart} añadieron al carrito, {checkout} llegaron al pago, {person} pidieron una persona, créditos {cost}',
  versionPublishedAt: 'publicada {at}',
  designColorHeader: 'Cabecera',
  designColorHeaderInk: 'Texto de cabecera',
  designGroupTranslations: 'Textos en otros idiomas',
  topicsTitle: 'Qué preguntan los clientes',
  topicsNote: 'Temas que Chat encontró en las primeras preguntas',
  startPagesTitle: 'Dónde empiezan las conversaciones',
  startPagesOne: 'Todas las conversaciones empezaron en un tipo de página: {page}.',
  'startPage.product': 'Páginas de productos',
  'startPage.search': 'Resultados de búsqueda',
  'startPage.start': 'Página de inicio',
  'startPage.cart': 'Carrito',
  'startPage.other': 'Otras páginas',
  addMarketInChat: 'Añadir un mercado en el chat',
  marketsTitle: 'Mercados',
  marketsBody:
    'Un asistente y un diseño. Cada mercado tiene su idioma, moneda, productos, carrito y chat con una persona.',
  marketChecked: 'comprobado {when}',
  'marketStatus.ready': 'Listo',
  'marketStatus.found': 'Sin añadir',
  'marketStatus.checking': 'Comprobando',
  'marketStatus.failed': 'Necesita un ajuste',
  addMarketPrompt:
    'Añade otro mercado al asistente de compras de {name} ({domain}): busca los demás países, idiomas o sitios de la tienda y pregúntame cuál añadir.',
  tabWidget: 'Widget',
  tabInstall: 'Instalación',
  tabSetup: 'Configuración',
  statusReading: 'Leyendo tu tienda',
  readingProducts: 'Leyendo los productos de nuevo',
  readingCart: 'Comprobando el carrito de nuevo',
  readingChat: 'Comprobando el acceso a tu chat',
  readingMarket: 'Añadiendo el mercado {market}',
  readingMarketAny: 'Añadiendo un mercado',
  statusReady: 'Listo para instalar',
  statusFailed: 'Lectura detenida',
  statusLive: 'En tu web · versión {version} en {domain}',
  statusPublished: 'Publicado · versión {version} · aún no está en tu web',
  statusPublishedUnverified: 'Publicado · versión {version} · no se pudo comprobar tu web',
  stepOpen: 'Abriendo la web',
  stepSearch: 'Búsqueda',
  stepProducts: 'Productos',
  stepCart: 'Carrito',
  stepChat: 'Tu chat actual',
  stepLook: 'Tu estilo y logotipo',
  stepChecks: 'Comprobando que funciona',
  stepReady: 'Listo',
  stepDetailOpen: 'Abriendo {domain}',
  stepDetailSearch: 'Buscando los productos de la tienda',
  stepDetailProducts: 'Leyendo productos, precios, fotos, colores y tallas',
  stepDetailCart: 'Probando el carrito: añadir, comprobar, quitar y vaciar',
  stepDetailChat: 'Buscando tu chat actual',
  stepDetailLook: 'Leyendo el logotipo y los colores de la tienda',
  stepDetailChecks: 'Probando búsquedas de clientes, productos, navegación y carrito',
  stepDetailChecksProducts: 'Comprobando búsquedas, navegación, fotos, colores y tallas',
  stepDetailChecksCart:
    'Comprobando añadir, consultar, quitar y vaciar el carrito, y leer productos',
  stepDetailChecksChat: 'Comprobando el paso a tu chat actual y la lectura de productos',
  stepDetailChecksMarket: 'Comprobando productos, precios y carrito de este mercado',
  nowRunning: 'Ahora: {step}',
  nowProgress: 'Paso {n} de {total} · {minutes} min hasta ahora',
  nowReady: 'Tu tienda está lista para un asistente',
  nowReadyBody: 'La búsqueda, los productos y el carrito funcionan.',
  nowLastRead: 'Última lectura {when}',
  nowUsually: 'normalmente {min}–{max}',
  stepsRead: 'Lectura {when} · Ver los pasos',
  nowFailed: 'Lectura detenida',
  factAnswers: 'Responde a los clientes con',
  costCounting: 'Contando…',
  factPaid: 'Se paga con',
  paidCompany: 'Créditos de la empresa',
  creditsAmount: '{amount} créditos',
  creditsOne: '{amount} crédito',
  creditsUnder: 'menos de {amount} créditos',
  stopRun: 'Detener la lectura',
  stopping: 'Deteniendo…',
  closeNote: 'Puedes salir de esta página. La lectura continúa.',
  readyNote: 'Siguiente: prueba tu Widget e instálalo.',
  buildWidget: 'Probar el Widget',
  readAgain: 'Leer la tienda de nuevo',
  foundTitle: 'Qué encontró',
  foundProducts: 'Productos y precios leídos',
  foundSearch: 'La búsqueda funciona',
  foundCart: 'Añadir al carrito funciona',
  foundChat: 'Tu chat de {vendor}',
  foundNoChat: 'Sin chat propio',
  foundLook: 'Tus colores y logotipo',
  widgetTitle: 'Tu Widget',
  widgetBody:
    'Tu asistente, con tus colores. Pruébalo aquí: responde con tus productos reales. No se añade nada a un carrito real.',
  widgetLoading: 'Cargando la vista previa…',
  widgetFailed: 'La vista previa no se ha cargado.',
  widgetNotReady:
    'El Widget estará listo cuando se completen los pasos de la pestaña Configuración.',
  installLineTitle: 'Añade esta línea a tu web',
  installLineBody:
    'Funciona en cualquier plataforma: en el head de tus páginas; en Shopify, en theme.liquid justo antes de </head>. No muestra nada hasta que publiques.',
  installLineBodyLive:
    'Funciona en cualquier plataforma: en el head de tus páginas; en Shopify, en theme.liquid justo antes de </head>. Los clientes verán la versión {version}.',
  installPolicyAsk: '¿Tu web tiene una Content Security Policy?',
  installPolicy:
    'Si tu web tiene una Content Security Policy, permite también {origin} en {directives} o el asistente no podrá iniciarse.',
  installPolicyRefuses:
    'La Content Security Policy de tu web bloquea el asistente: permite {origin} en {directives} o no se iniciará.',
  installPolicyNonce:
    'Tu política solo ejecuta scripts de confianza. Añade la línea con el nonce de tus páginas.',
  installPolicyEval: "El asistente también necesita 'unsafe-eval' en script-src.",
  copy: 'Copiar',
  copied: 'Copiado',
  domainsTitle: 'Sitios en los que responde',
  domainsBody: 'No responde en otros sitios, aunque copien la línea.',
  lineFound: 'Línea encontrada',
  lineNotSeen: 'Aún no está en la página',
  lineUnverified:
    'No se pudo comprobar: el sitio bloquea las visitas automáticas. El asistente sigue funcionando para los clientes.',
  lineChecked: 'comprobado {when}',
  lineCheckAgain: 'Comprobar de nuevo',
  lineChecking: 'Comprobando la página…',
  installCheckBusy:
    'La comprobación está ocupada en este momento. Inténtalo de nuevo en un momento.',
  publishTitle: 'Publicar',
  publishBody: 'Los clientes de {domain} lo ven al instante. Puedes retirarlo cuando quieras.',
  publishButton: 'Publicar versión {version}',
  publishConfirm: '¿Publicar la versión {version} de {name}?',
  publishNow: 'Publicar',
  notNow: 'Ahora no',
  publishing: 'Publicando…',
  liveTitle: 'Publicada: versión {version}',
  liveSince: 'Desde {since}',
  unpublish: 'Retirar publicación',
  unpublishConfirm:
    '¿Retirar la versión {version} de tu web? Los clientes dejarán de ver el asistente hasta que publiques de nuevo.',
  publishedLine: 'Publicada ahora: versión {version}, desde {since}',
  usageTitle: 'Uso',
  usageBody: 'Usa los créditos de tu empresa, como el resto de Chat.',
  usageDiscovery: 'Lectura de la tienda',
  usageChats: 'Chats con clientes',
  usageChatsValue: '{count} chats · {cost}',
  last7: 'Últimos 7 días',
  askWeek: 'Preguntar a Chat por esta semana',
  kpiConversations: 'Conversaciones',
  kpiVsPrevious: '{change} frente a la semana anterior',
  kpiCart: 'Añadido al carrito',
  kpiCheckout: 'Llegaron al pago',
  kpiPerson: 'Pidieron una persona',
  kpiCost: 'Créditos usados',
  ofConversations: '{percent} de las conversaciones',
  checkoutNote: '{percent} de las conversaciones',
  personNote: '{percent} abrieron tu chat',
  costNote: 'Unos {amount} por conversación',
  costNoteUnder: 'Menos de {amount} por conversación',
  sparkLabel: 'Conversaciones por día, últimos {days} días',
  sparkDay: '{day}: {count} conversaciones',
  filterAll: 'Todas',
  filterCart: 'Añadido al carrito',
  filterCheckout: 'Pago',
  filterPerson: 'Persona',
  outcomeCart: 'Añadido al carrito',
  outcomeCheckout: 'Llegó al pago',
  outcomePerson: 'Pidió una persona',
  outcomeAnswered: 'Respondida',
  devicePhone: 'Móvil',
  deviceComputer: 'Ordenador',
  rowMeta: '{when} · {device}',
  detailMeta: '{when} · {device} · {minutes} min · Empezó en {page}',
  anonymous: 'El cliente es anónimo.',
  askConversation: 'Preguntar a Chat sobre esta conversación',
  showOlderVersions: 'Mostrar versiones anteriores',
  showOlder: 'Mostrar conversaciones anteriores',
  filterCountsWeek: 'Recuentos: últimos 7 días',
  readAlreadyHere:
    'Esta tienda ya está aquí, así que no se inició nada nuevo ni se usaron créditos.',
  readOpenShop: 'Abrirla',
  fieldInLanguage: '{field} ({language})',
  askConversationMessage: '¿Qué podemos aprender de esta conversación?',
  askWeekMessage: '¿Qué podemos aprender de las conversaciones de esta semana?',
  conversationsChipOne: '{name} · conversación del {when}',
  conversationsChipWeek: '{name} · esta semana',
  conversationsChipWeekIn: '{name} · {market} · esta semana',
  removeConversations: 'Quitar las conversaciones',
  noConversations: 'Aún no hay conversaciones',
  noConversationsBody: 'Cuando los clientes usen el asistente, sus conversaciones aparecerán aquí.',
  pickConversation: 'Elige una conversación para leerla.',
  eventCheckout: 'Fue al pago',
  eventPerson: 'Abrió tu chat',
  showMore: 'Ver más',
  urlInvalid: 'Usa la dirección https de tu tienda, como https://www.tutienda.es.',
  errorNoCompany:
    'Storefront necesita un espacio de trabajo de empresa. Pide a tu administrador que te añada.',
  errorPublishPermission:
    'Solo pueden hacerlo quienes tengan permiso para publicar en tu empresa. Pregunta a tu administrador.',
  actionFailed: 'No ha funcionado. Inténtalo de nuevo.',
  copyFailed: 'No se ha podido copiar. Selecciona la línea y cópiala.',
  reasonCancelled: 'Has detenido la lectura.',
  reasonFailed:
    'La lectura de tu tienda no ha podido terminar, así que el asistente aún no está listo.',
  reasonFailedLive:
    'La lectura de tu tienda no ha podido terminar. El asistente publicado sigue funcionando como hasta ahora.',
  reasonFailedKept:
    'La lectura de tu tienda no ha podido terminar. Nada ha cambiado: la versión {version} sigue como estaba.',
  statusReadStopped: 'Listo · la última lectura se detuvo',
  reasonStepMarked: 'El paso que se detuvo está marcado abajo.',
  reasonReadAgain: 'La próxima lectura parte de lo que esta ha aprendido.',
  reasonNotAStore:
    'Esta dirección no parece una tienda online: no había productos que buscar. Comprueba la dirección o prueba el dominio principal de la tienda.',
  reasonTooSlow:
    'La búsqueda de tu tienda es demasiado lenta para un asistente de compras: tardó más de 12 segundos. Acelerar la búsqueda o leer la tienda más tarde puede ayudar.',
  reasonOurSide:
    'La lectura se detuvo por un problema de nuestra parte, no de tu tienda, así que el asistente aún no está listo.',
  reasonOurSideLive:
    'La lectura se detuvo por un problema de nuestra parte, no de tu tienda. El asistente publicado sigue funcionando como hasta ahora.',
  reasonOurSideKept:
    'La lectura se detuvo por un problema de nuestra parte, no de tu tienda. Nada ha cambiado: la versión {version} sigue como estaba.',
  reasonTryLater: 'Vuelve a intentar leer la tienda dentro de un rato.',
  errorNoCredits:
    'Ahora mismo no hay créditos para esto. Un propietario de la empresa puede revisar el plan y los límites en Usage.',
  errorReadUnavailable: 'Ahora no se puede leer esta tienda. Inténtalo de nuevo en unos minutos.',
  reasonTimeout:
    'La lectura alcanzó su límite de tiempo antes de terminar. Al leer de nuevo, continúa desde lo ya comprobado.',
  retrying: 'Iniciando de nuevo…',
  readTitleChecks: 'Comprobar de nuevo la lectura guardada',
  checksScopeLabel: 'Comprueba',
  readScopeChecks:
    'Búsquedas de los clientes, productos, navegación y carrito, sobre lo que guardó la última lectura. La tienda no se vuelve a leer.',
  readScopeChecksProducts:
    'Búsquedas, navegación, fotos, colores y tallas, sobre lo que guardó la última lectura. La tienda no se vuelve a leer.',
  readScopeChecksCart:
    'Añadir, consultar, quitar y vaciar el carrito, y leer productos, sobre lo que guardó la última lectura. La tienda no se vuelve a leer.',
  readScopeChecksChat:
    'El paso a tu chat actual y la lectura de productos, sobre lo que guardó la última lectura. La tienda no se vuelve a leer.',
  readScopeChecksMarket:
    'Productos, precios y carrito del mercado, sobre lo que guardó la última lectura. La tienda no se vuelve a leer.',
  checksCost: 'Solo las nuevas comprobaciones usan créditos de la empresa',
  checksAfter:
    'Guarda un nuevo borrador si las comprobaciones salen bien. Los clientes no ven nada hasta que publiques.',
  checksAfterLive:
    'Guarda un nuevo borrador si las comprobaciones salen bien. El asistente publicado sigue funcionando y los clientes solo ven el cambio cuando publiques.',
  checksStarted:
    'Las comprobaciones han empezado. Cuando el nuevo borrador esté listo, pide a Chat que lo revise.',
  checkingSavedWork: 'Comprobando de nuevo el trabajo guardado',
  checkAgain: 'Comprobar de nuevo',
  checkingAgain: 'Comprobando de nuevo…',
  reasonCheckAgain:
    '«Comprobar de nuevo» prueba otra vez el trabajo guardado, sin leer la tienda de nuevo. Solo las nuevas comprobaciones usan créditos de la empresa.',
  fixWithChat: 'Corregir con Chat',
  fixReadPrompt:
    'La lectura de {name} ({domain}) se detuvo. Mira qué pasó y ayúdame a que funcione: dime qué encuentras, sugiere qué podría indicarle a la lectura y luego compruébala de nuevo o vuelve a leerla con esa indicación, leyendo lo menos posible de la tienda.',
  fixReadPromptStep:
    'La lectura de {name} ({domain}) se detuvo en el paso «{step}». Mira qué pasó y ayúdame a que funcione: dime qué encuentras, sugiere qué podría indicarle a la lectura y luego compruébala de nuevo o vuelve a leerla con esa indicación, leyendo lo menos posible de la tienda.',
  stepStateDone: 'terminado',
  stepStateRunning: 'en curso',
  stepStateWaiting: 'sin empezar',
  stepStateFailed: 'detenido',
  newVersionTitle: 'La versión {version} está lista',
  newVersionBody: 'Publícala para sustituir la versión {live}.',
  newVersionBodyLive: 'Publícala para sustituir la versión {live} en {domain}.',
  previewSize: 'Tamaño de vista previa',
  designWithChat: 'Abrir diseño',
  designReviewBody: 'Revisa el diseño actual y dile a Chat qué te gustaría ajustar.',
  designWhileTitle: 'Diséñalo mientras leemos tu tienda',
  designWhileBody:
    'Chat puede ayudarte a dar forma al estilo, los textos y el logotipo del asistente ahora. El diseño estará listo cuando termine la lectura.',
  designPreviewNote: 'Una vista previa con productos de ejemplo hasta que se lea tu tienda.',
  proposedDesign: 'Diseño propuesto',
  proposedDesignNote:
    'El Widget con este cambio y productos de ejemplo. Al aprobarlo se guarda un nuevo borrador; se publica desde Storefront.',
  lineSources: 'De las páginas de la tienda:',
  readAgainConfirm:
    '¿Leer {name} de nuevo? Tarda entre 10 y 30 minutos y usa créditos de la empresa.',
  readAgainLive: 'El asistente publicado sigue funcionando hasta que publiques la nueva versión.',
  readAgainNow: 'Leer de nuevo',
  switchStore: 'Cambiar de tienda o añadir una',
  storesLabel: 'Tus tiendas',
  addStore: 'Añadir otra tienda',
  backToStores: 'Volver a tus tiendas',
  storeLive: 'Publicado',
  storeReady: 'Listo',
  storeReading: 'Leyendo',
  storeStopped: 'Detenido',
  actionDesignTitle: 'Cambiar el diseño del asistente de compras',
  actionKnowledgeTitle: 'Cambiar lo que sabe el asistente de compras',
  readTitleProducts: 'Leer los productos de nuevo',
  readTitleCart: 'Comprobar el carrito de nuevo',
  readTitleTheirChat: 'Comprobar de nuevo el acceso a tu chat',
  readTitleAll: 'Leer toda la tienda de nuevo',
  readTitleStore: 'Leer una tienda nueva',
  readTitleMarket: 'Añadir un mercado',
  readShopLabel: 'Tienda',
  readScopeLabel: 'Lee',
  readTimeLabel: 'Duración',
  readCostLabel: 'Coste',
  readAfterLabel: 'Después',
  readScopeProducts: 'Búsqueda y ordenación de productos, fotos, colores y tallas de productos',
  readScopeCart: 'Añadir al carrito y quitar de él',
  readScopeTheirChat: 'Cómo llegan los clientes a tu chat',
  readScopeAll: 'Productos, carrito, tu chat y tus mercados',
  readScopeStore: 'Productos, precios y tallas, carrito, tu chat, tu estilo y tus mercados',
  readScopeMarket: '{market}: sus productos, precios y carrito',
  readTime: 'Normalmente {min}–{max} minutos',
  readCost: 'Usa créditos de la empresa',
  readCostLast: 'Usa créditos de la empresa · la última lectura de esta tienda usó {credits}',
  readCostLastPartial:
    'Usa créditos de la empresa · la última lectura, detenida antes de terminar, usó al menos {credits}',
  readAfter: 'Guarda un nuevo borrador. Los clientes no ven nada hasta que publiques.',
  readAfterLive:
    'Guarda un nuevo borrador. El asistente publicado sigue funcionando y los clientes solo ven el cambio cuando publiques.',
  readAfterNew: 'Guarda un primer borrador para que lo pruebes. No se publica nada.',
  readCartNote:
    'Para comprobar el carrito, añade un artículo de prueba y lo quita. Nunca completa la compra.',
  readFocusLabel: 'Lo que Chat pidió corregir en la lectura',
  readFooter:
    'Nada empieza hasta que lo apruebes. Puedes detener la lectura en la pestaña Configuración.',
  readStartedAt:
    'Inicio: {time} · normalmente {min}–{max} minutos. Cuando el nuevo borrador esté listo, pide a Chat que lo revise.',
  readFocusAll: 'Ver todo',
  readFocusLess: 'Ver menos',
  readStart: 'Iniciar la lectura',
  addToSite: 'Añadir a tu web',
  readTab: 'Solicitud',
  readStarted:
    'La lectura ha empezado. Cuando el nuevo borrador esté listo, pide a Chat que lo revise.',
  readFollow: 'Seguir en la pestaña Configuración',
  proposedDesignLive:
    'La vista previa real de tu tienda con este diseño. Pregunta algo: no se añade nada a un carrito real. Al aprobarlo se guarda un nuevo borrador; se publica desde Storefront.',
  designChanges: 'Qué cambia',
  designReset: 'volver al valor inicial',
  designGroupColors: 'Colores',
  designGroupType: 'Fuentes',
  designGroupShape: 'Forma',
  designGroupLauncher: 'Botón de la página',
  designGroupPanel: 'Cabecera',
  designGroupProducts: 'Productos',
  designGroupCart: 'Carrito de la tienda',
  designGroupCopy: 'Textos',
  designGroupLogo: 'Logotipo',
  designGroupCss: 'CSS personalizado',
  designColorBrand: 'Marca',
  designColorActions: 'Añadir y Enviar',
  designColorPanel: 'Panel',
  designColorSurface: 'Superficies',
  designColorInk: 'Texto',
  designColorMuted: 'Texto secundario',
  designColorLine: 'Líneas',
  designColorBubble: 'Mensajes del cliente',
  designColorBubbleInk: 'Texto del cliente',
  designActionsBrand: 'Color de marca',
  designActionsInk: 'Color del texto',
  designWordsName: 'Nombre',
  designWordsGreeting: 'Saludo',
  designWordsStarters: 'Preguntas iniciales',
  designWordsLauncher: 'Texto del botón',
  designWordsPlaceholder: 'Texto de ejemplo',
  designWordsAi: 'Etiqueta de IA',
  designWordsPerson: 'Enlace a una persona',
  noteNew: 'Nueva',
  noteChanged: 'Modificada',
  noteRemoved: 'Eliminada',
  noteSourceKnowledge: 'Página de conocimiento: {title}',
  noteSourceFile: 'Archivo: {name}',
  noteSourceChat: 'Del chat',
  knowledgeApprovalNote:
    'Los clientes pueden leer todo lo que dicen estas notas. Al aprobarlas se guarda un nuevo borrador: hazle preguntas en la pestaña Widget y publícalo en la pestaña Instalación.',
  profileTitle: 'Sobre la tienda',
  profileChecked: 'Comprobado el {date}',
  profileSources: 'Escrito a partir de',
  profileSample: '{count} productos muestreados para el nivel de precios',
  profileSampleOne: '{count} producto muestreado para el nivel de precios',
  profileAssistant: 'Qué puede hacer aquí el asistente',
  profileTeam: 'Escrito por vuestro equipo',
  profileEmpty:
    'Aún no escrito. Pide a Chat que haga un recorrido de conocimiento: lo escribe a partir de vuestras propias páginas y de lo que vio en vuestro sitio, y podéis añadir líneas propias.',
  profileWithChat: 'Añadir o cambiar con Chat',
  profilePrompt:
    'Ayúdame a mejorar lo que el asistente de compras de {name} ({domain}) sabe de nuestra tienda en conjunto: qué vendemos, por qué somos conocidos, para quién somos y qué pueden hacer aquí los clientes. Lee primero el perfil «Sobre la tienda» y dime qué falta o está mal. Todo lo que te cuente de la tienda y que ninguna página diga entra como línea de nuestro equipo.',
  profilePartNavigation: 'Departamentos',
  profilePartWelcome: 'Saludo',
  profilePartMarkets: 'Mercados',
  profilePartFunctions: 'Qué puede hacer el asistente',
  profilePartProducts: 'Productos',
  profilePartPages: 'Páginas de servicio',
  profileCleared: 'Borra el perfil escrito',
  knowledgeTitle: 'Qué sabe',
  knowledgeBody:
    'Además de tus productos, precios y existencias, que siempre lee en directo de tu tienda.',
  knowledgeEmpty:
    'De momento, solo tu catálogo. Añade información de envíos, devoluciones o pagos con Chat, desde tus páginas o desde Conocimiento.',
  knowledgeWithChat: 'Añadir con Chat',
  knowledgeChangeWithChat: 'Cambiar con Chat',
  knowledgePrompt:
    'Ayúdame a elegir qué cuenta a los clientes el asistente de compras de {name} ({domain}), además de nuestros productos: envíos, devoluciones y pagos, por ejemplo. Búscalo en nuestras páginas o en Conocimiento y propón notas que pueda aprobar.',
  pagesTitle: 'Páginas que lee',
  pagesBody:
    'Las páginas de las que salen las notas. Poneva las comprueba cada día y Chat avisa cuando cambia alguna.',
  pagesEmpty:
    'Todavía no hay páginas. Aquí aparecen las páginas que encuentra la lectura de la tienda y las que se nombren en Chat.',
  pageFound: 'encontrada por Poneva',
  pageLinkedFrom: 'enlazada desde {page}',
  pageAddedByYou: 'añadida manualmente',
  pageFromNote: 'mencionada en una nota',
  pageNoteOne: '1 nota',
  pageNotesMany: '{count} notas',
  pageStatusOk: 'Actualizada',
  pageStatusChanged: 'Cambiada',
  pageStatusUnreadable: 'No se puede leer',
  pageStatusUnchecked: 'Aún no comprobada',
  pagesChangeWithChat: 'Cambiar con Chat',
  pagesUpdateWithChat: 'Actualizar notas con Chat',
  pagesPrompt:
    'Ayúdame a cambiar qué páginas lee el asistente de compras de {name} ({domain}) para sus notas. Muéstrame las páginas que usa ahora y pregúntame cuáles añadir o quitar.',
  pagesUpdatePrompt:
    'Algunas páginas de {name} ({domain}) han cambiado o ya no se pueden leer desde que se crearon las notas. Muéstrame cuáles son y vuelve a hacer las notas a partir de ellas como borrador que pueda revisar.',
  viewCompare: 'Comparación',
  lookDeviceLabel: 'Comparar en',
  lookShop: 'Tu tienda',
  lookShopCaption: 'Página de inicio, leída {when}',
  lookWidget: 'El asistente',
  lookWidgetCaption: 'Versión {version}',
  lookLoading: 'Colocando el asistente junto a tu tienda…',
  lookFailed: 'No se ha podido hacer la comparación ahora mismo.',
  lookNoShopPicture: 'La lectura no guardó una imagen de tu tienda con este ancho.',
  lookNoneTitle: 'El estilo propio de tu tienda no se guardó',
  lookNoneBody:
    'Esta lectura no lo registró. Vuelve a leer la tienda desde Configuración y su logotipo, colores y páginas se guardarán para comparar.',
  lookColoursShop: 'Colores que usa tu tienda',
  lookColoursAssistant: 'Colores del asistente',
  lookRoleHeader: 'Cabecera',
  lookRoleBrand: 'Marca',
  lookSame: 'Igual que el color {colour} de tu tienda ({source})',
  lookOff: 'Cercano al color {colour} de tu tienda ({source}) pero no igual, diferencia {delta}',
  lookOwn: 'Color propio: tu tienda no lo usa',
  lookNeutral: 'Tu tienda no se cargó para esto: solo se muestra lo que guardó la lectura.',
  lookFromLogo: 'logotipo',
  lookFromTheme: 'color del tema',
  lookFromTile: 'color del recuadro',
  lookFromMask: 'icono fijado',
  lookFromManifest: 'manifiesto de la aplicación',
  lookFromHeader: 'cabecera',
  lookFromButton: 'botones',
  lookFromVariable: 'color del estilo',
  lookFix: 'Corregir con Chat',
  lookFixPrompt:
    'El color del asistente para {role} es {colour}, cercano al color {exact} de tu tienda ({source}) pero no igual. Usa el color exacto de tu tienda, {exact}.',
  lookSwatch: 'Color {colour}',
  errorDesignChecks: 'Esta versión no superó las comprobaciones de diseño.',
  designSamplesBlocked:
    'Los diseños de las tarjetas no se pueden demostrar aquí: tu web bloquea las visitas automáticas. Los colores, las fuentes y el logotipo siguen funcionando.',
  designCheckLabel: 'Una etiqueta de IA es demasiado tenue para leerse.',
  designCheckReach: 'Un botón no se puede alcanzar.',
  designCheckSize: 'Parte del texto es demasiado pequeña.',
  designCheckOverflow: 'El panel se desplaza hacia los lados.',
  designCheckRender:
    'No se pudieron ejecutar las comprobaciones. Inténtalo de nuevo en un momento.',
  fixDesignPrompt:
    'Intenté publicar el último borrador de {name} ({domain}), pero no superó las comprobaciones de diseño: {facts}. Corrige el diseño en este chat y compruébalo en el móvil y el ordenador. No lo publiques.',
};

const copy: NamespaceCatalog<StorefrontCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};

export default copy;
